import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun, faMoon, faPen, faCheck, faTimes, faPlus, faTrash,
  faCopy, faChevronDown, faChevronUp, faBolt, faEnvelope,
  faUser, faUsers, faBuilding, faReceipt, faCalendar, faDumbbell, faTrophy, faTableTennis, faLock,
  faGear, faChartLine, faBullseye, faClipboardList, faSchool, faGraduationCap, faMedal, faHandshake,
  faCreditCard, faCoins, faRotateRight, faMagnifyingGlass, faTriangleExclamation, faCamera,
  faClock, faFire, faLocationDot, faRepeat, faList, faUtensils,
  faGamepad, faCircleCheck, faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Circles } from 'react-loader-spinner';
import { Country, State } from 'country-state-city';
import logo from '../assets/logo.png';
import { supabase } from '../supabaseClient';
import { workspaceNameSlug } from '../institutionWorkspaceSlug';
import { withRequestedStripeMode } from '../stripeMode';
import { loadWorkoutGroupAccess, loadWorkoutGroupOwnerAccessForGroup, getDefaultWorkoutGroupAccess, WORKOUT_GROUP_PLAN_ORDER, hasWorkoutGroupTier } from '../workoutGroupAccess';
import { getCurrentBillingPlans, getEmptyBillingOverview, loadBillingOverview } from '../billingOverview';
import { loadInstitutionBillingRows, mergeInstitutionPlansWithBillingRows } from '../services/institutionBillingPlans';
import { loadInstitutionBusinessDemoConfig } from '../services/institutionDemoMode';
import InstitutionLiveDashboardSettings from './InstitutionLiveDashboardSettings';
import InstituteVisibilityControl from './InstituteVisibilityControl';
import { WorkoutGroupAnalyticsAthleteSelector } from './WorkoutGroupAnalyticsShared';
import { uploadFileToR2 } from '../services/r2Upload';
import { DEMO_COMPANY_LEADERBOARD_EMAIL, buildDemoCompanyLeaderboard, isDemoCompanyInstituteRow, isDemoCompanyLeaderboard } from '../demoCompanyLeaderboard';
import { buildDemoSchoolLeaderboard, isDemoSchoolLeaderboard } from '../demoSchoolLeaderboard';
import { buildDemoCollegeLeaderboard, isDemoCollegeLeaderboard } from '../demoCollegeLeaderboard';
import { isPaddedUcdLogo } from '../institutionLogoPresentation';
import { buildDemoClubLeaderboard, isAfriRunDemoInstitute, isJustUsDemoInstitute, isDemoClubInstituteRow, isDemoClubLeaderboard, mergeAfriRunDemoCommunity } from '../demoClubExperience';
import { fetchDemoInstituteMemberRows, mergeDemoInstituteMembers } from '../demoInstituteMembers';
import { publishInstitutesAndLoadRosters } from '../institutionLoadStages';
import { buildInstitutionWorkspaceGroups, mergeManagedInstituteRows, shouldShowAllInstituteGroups, visibleInstituteGroups } from '../institutionWorkspaceGroups';
import { buildInstitutionAdminCandidates, institutionAdminDisplayName } from '../institutionAdminManagement';
import {
  formatCoins as formatTxCoins,
  formatMoney,
  formatTransactionAmount,
  formatTransactionDate,
  formatTransactionDiscountSummary,
  formatTransactionStatus,
  formatTransactionTitle,
  getStoreOrderPrimaryItem,
  getPrimaryTransactionAction,
  getTransactionIcon,
  getTransactionTone,
  getTransactionDiscount,
  humanizeToken,
  isTransactionIssue,
  sanitizeTransactionDescription,
  shortenTransactionId,
} from '../utils/transactionFormatting';

/* ═══════════════════ BYPASS CODE ═══════════════════ */
const BYPASS_CODE = 'BYpass2026';
const EMAIL_CHANGE_REDIRECT_URL = 'https://snowsproam.com/auth/callback';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHALLENGE_MEMBER_DEBUG_KEY = 'snowsproam.challengeMemberDebugLogs';
const safeDebugPayload = (payload) => {
  try { return JSON.parse(JSON.stringify(payload)); } catch { return String(payload); }
};
const recordChallengeMemberDebug = (scope, event, payload, level = 'log') => {
  const entry = { at: new Date().toISOString(), scope, event, payload: safeDebugPayload(payload) };
  const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
  console[consoleMethod](`[ChallengeMembers][${scope}] ${event}`, payload);
  try {
    const existing = JSON.parse(window.localStorage.getItem(CHALLENGE_MEMBER_DEBUG_KEY) || '[]');
    const next = [...existing, entry].slice(-100);
    window.localStorage.setItem(CHALLENGE_MEMBER_DEBUG_KEY, JSON.stringify(next));
    window.__challengeMemberDebugLogs = next;
  } catch {}
  return entry;
};
const copyChallengeMemberDebugLogs = async () => {
  const logs = JSON.parse(window.localStorage.getItem(CHALLENGE_MEMBER_DEBUG_KEY) || '[]');
  await navigator.clipboard.writeText(JSON.stringify(logs, null, 2));
  return logs.length;
};
const DEFAULT_APP_LEADERBOARD_SETTINGS = {
  primaryColor: '#0C69C8',
  accentColor: '#D5A419',
  supportColor: '#14B8A6',
  highlightColor: '#F97316',
  backgroundColor: '#FFFFFF',
  applyBranding: true,
};
const WORKOUT_GROUP_THEME_SURFACES = [
  {
    key: 'workout_groups',
    label: 'Workout Groups',
    enabledKey: 'theme_workout_groups_enabled',
    clubIdKey: 'theme_workout_groups_club_id',
    clubToggleKey: 'theme_workout_groups',
    toggleLabel: 'Use colours in Workout Groups',
  },
  {
    key: 'workouts',
    label: 'Workouts',
    enabledKey: 'theme_workouts_enabled',
    clubIdKey: 'theme_workouts_club_id',
    clubToggleKey: 'theme_workouts',
    toggleLabel: 'Use colours in Workouts',
  },
  {
    key: 'nutrition',
    label: 'Calorie Tracker',
    enabledKey: 'theme_nutrition_enabled',
    clubIdKey: 'theme_nutrition_club_id',
    clubToggleKey: 'theme_nutrition',
    toggleLabel: 'Use colours in Calorie Tracker',
  },
  {
    key: 'sessions',
    label: 'Workout Sessions',
    enabledKey: 'theme_sessions_enabled',
    clubIdKey: 'theme_sessions_club_id',
    clubToggleKey: 'theme_sessions',
    toggleLabel: 'Use colours in Workout Sessions',
  },
];
const USER_WORKOUT_GROUP_THEME_SURFACES = WORKOUT_GROUP_THEME_SURFACES;
const DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES = {
  theme_workout_groups_enabled: false,
  theme_workout_groups_club_id: null,
  theme_workouts_enabled: false,
  theme_workouts_club_id: null,
  theme_nutrition_enabled: false,
  theme_nutrition_club_id: null,
  theme_sessions_enabled: false,
  theme_sessions_club_id: null,
};
const ASSIGNED_WORKOUT_DAYS = [
  { index: 0, short: 'Mon', label: 'Monday' },
  { index: 1, short: 'Tue', label: 'Tuesday' },
  { index: 2, short: 'Wed', label: 'Wednesday' },
  { index: 3, short: 'Thu', label: 'Thursday' },
  { index: 4, short: 'Fri', label: 'Friday' },
  { index: 5, short: 'Sat', label: 'Saturday' },
  { index: 6, short: 'Sun', label: 'Sunday' },
];
const normalizeAssignedWorkoutDays = (value) => (
  Array.isArray(value)
    ? Array.from(new Set(value.map(day => Number(day)).filter(day => Number.isInteger(day) && day >= 0 && day <= 6))).sort((a, b) => a - b)
    : []
);
const formatAssignedWorkoutDays = (pair) => {
  const days = normalizeAssignedWorkoutDays(pair?.scheduled_days || pair?.days);
  if (!days.length) return 'Any training day';
  return days.map(day => ASSIGNED_WORKOUT_DAYS.find(item => item.index === day)?.short).filter(Boolean).join(', ');
};
const parseThemeClubId = (value) => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
};
const parseWorkoutGroupThemePreferences = (row) => ({
  ...DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES,
  ...(row || {}),
  theme_workout_groups_enabled: row?.theme_workout_groups_enabled === true,
  theme_workouts_enabled: row?.theme_workouts_enabled === true,
  theme_nutrition_enabled: row?.theme_nutrition_enabled === true,
  theme_sessions_enabled: row?.theme_sessions_enabled === true,
  theme_workout_groups_club_id: parseThemeClubId(row?.theme_workout_groups_club_id),
  theme_workouts_club_id: parseThemeClubId(row?.theme_workouts_club_id),
  theme_nutrition_club_id: parseThemeClubId(row?.theme_nutrition_club_id),
  theme_sessions_club_id: parseThemeClubId(row?.theme_sessions_club_id),
});
const isValidHexColor = (value) => /^#[0-9A-Fa-f]{6}$/.test(String(value || '').trim());
const normalizeHexInput = (value, fallback) => {
  const next = String(value || '').trim().toUpperCase();
  return isValidHexColor(next) ? next : fallback;
};
const hexToRgb = (hex = '') => {
  const clean = normalizeHexInput(hex, '#0F172A').replace('#', '');
  const value = parseInt(clean, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
};
const rgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};
const isLightHexColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return ((r * 299 + g * 587 + b * 114) / 1000) > 185;
};
const getReadableClubPalette = (club = {}, C = { accent: '#0C69C8' }) => {
  const fallbackPrimary = C?.accent || '#0C69C8';
  const rawPrimary = normalizeHexInput(club?.primary_color || fallbackPrimary, fallbackPrimary);
  const rawSecondary = normalizeHexInput(club?.secondary_color || '#FFFFFF', '#FFFFFF');
  const rawAccent = normalizeHexInput(club?.accent_color || '#111827', '#111827');
  const primary = isLightHexColor(rawPrimary) ? '#0F5132' : rawPrimary;
  const surfaceSecondary = isLightHexColor(rawSecondary) ? '#111827' : rawSecondary;
  const accent = isLightHexColor(rawAccent) ? '#111827' : rawAccent;
  const onPrimary = isLightHexColor(primary) ? '#111827' : '#FFFFFF';
  return {
    primary,
    secondary: surfaceSecondary,
    surfaceSecondary,
    accent,
    rawPrimary,
    rawSecondary,
    rawAccent,
    onPrimary,
    mutedOnPrimary: onPrimary === '#FFFFFF' ? 'rgba(255,255,255,0.78)' : 'rgba(17,24,39,0.72)',
    subtleOnPrimary: onPrimary === '#FFFFFF' ? 'rgba(255,255,255,0.13)' : 'rgba(17,24,39,0.09)',
    borderOnPrimary: onPrimary === '#FFFFFF' ? 'rgba(255,255,255,0.24)' : 'rgba(17,24,39,0.18)',
  };
};
const LEADERBOARD_MEDAL_COLORS = {
  1: '#D4AF37',
  2: '#8F98A8',
  3: '#CD7F32',
};
const mixHex = (fromHex, toHex, amount) => {
  const from = hexToRgb(fromHex);
  const to = hexToRgb(toHex);
  const ratio = Math.max(0, Math.min(1, amount));
  const next = {
    r: Math.round(from.r + (to.r - from.r) * ratio),
    g: Math.round(from.g + (to.g - from.g) * ratio),
    b: Math.round(from.b + (to.b - from.b) * ratio),
  };
  return `#${[next.r, next.g, next.b].map(value => value.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
};
const tintHex = (hex, amount = 0.94) => mixHex(hex, '#FFFFFF', amount);

const getWorkoutGroupAssignedWorkoutCount = (group = {}) => {
  const seen = new Set();
  const add = (name) => {
    const key = String(name || '').trim().toLowerCase();
    if (key) seen.add(key);
  };
  (Array.isArray(group.allowed_workout_pairs) ? group.allowed_workout_pairs : []).forEach(pair => add(pair?.name));
  (Array.isArray(group.allowed_workout_names) ? group.allowed_workout_names : []).forEach(add);
  return seen.size;
};
const firstRelationRow = (value) => Array.isArray(value) ? value[0] : value;
const DEMO_WORKOUT_GROUP_MEMBERS_SELECT = 'id,demo_profile_id,group_id,squad_role,position,jersey_number,counts_as_athlete,metadata,demo_profiles(id,username,name,surname,profile_img,level,metadata)';
const mapDemoWorkoutMemberRow = (row = {}) => {
  const profile = firstRelationRow(row.demo_profiles) || {};
  const id = row.demo_profile_id || profile.id;
  return {
    ...row,
    id,
    athlete_id: id,
    demo_profile_id: id,
    is_demo_profile: true,
    profiles: {
      id,
      username: profile.username || '',
      name: profile.name || '',
      surname: profile.surname || '',
      profile_img: profile.profile_img || '',
      level: profile.level || 1,
      metadata: profile.metadata || {},
      is_demo_profile: true,
      demo_profile_id: id,
      squad_role: row.squad_role || 'Athlete',
      position: row.position || '',
      jersey_number: row.jersey_number || null,
    },
  };
};
const isCountingWorkoutAthlete = (member = {}) => (
  member.counts_as_athlete !== false &&
  String(member.squad_role || member.profiles?.squad_role || 'Athlete').toLowerCase() === 'athlete'
);
const getWorkoutGroupMemberPosition = (member = {}) =>
  String(member.position || member.sport_position || member.metadata?.position || '').trim();
const normalizeDemoWorkoutSession = (row = {}) => ({
  ...row,
  profile_id: row.demo_profile_id,
  user_workout_feedback: row.demo_user_workout_feedback || [],
  workout_exercise_summary: (row.demo_exercise_progress || []).map((entry) => ({
    ...entry,
    total_sets: entry.set === false || String(entry.logged_as || 'set').toLowerCase() === 'cardio' ? 0 : 1,
    total_reps: Number(entry.reps || 0),
    avg_weight: Number(entry.weight || 0),
    total_volume: Number(entry.volume ?? (Number(entry.weight || 0) * Number(entry.reps || 0))),
    total_xp: Number(entry.xp_gained || 0),
  })),
});
const mapDemoFoodLogRow = (row = {}) => ({ ...row, user_id: row.demo_profile_id });
const getWorkoutGroupPlanOwnerId = (group = {}) => {
  const club = firstRelationRow(group?.workout_clubs);
  return club?.owner_profile_id || group?.created_by || group?.coach_id || '';
};

/* ═══════════════════ MOBILE HOOK ═══════════════════ */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
};

const orderItems = (order = {}) => {
  if (!order || typeof order !== 'object') return [];
  return Array.isArray(order.order_items) ? order.order_items : Array.isArray(order.items) ? order.items : [];
};
const isMerchandiseTransaction = (tx = {}) => {
  const haystack = `${tx.transaction_type || ''} ${tx.source || ''} ${tx.description || ''}`.toLowerCase();
  return haystack.includes('merchandise') || haystack.includes('proam store') || haystack.includes('store');
};
const orderTransactionAmount = (order = {}) => Number(order.total_price || 0);
const isLikelySameOrderTransaction = (tx = {}, order = {}) => {
  if (!isMerchandiseTransaction(tx)) return false;
  const txTime = new Date(tx.created_at || 0).getTime();
  const orderTime = new Date(order.created_at || 0).getTime();
  const closeInTime = txTime && orderTime && Math.abs(txTime - orderTime) <= 10 * 60 * 1000;
  const txAmount = Math.abs(Number(tx.amount || tx.amount_cents / 100 || 0));
  const closeInAmount = Math.abs(txAmount - orderTransactionAmount(order)) < 0.01;
  return closeInTime && (closeInAmount || txAmount === 0);
};

const recordedStoreOrderCoinAmount = (order = {}) => {
  const metadata = order.payment_metadata || {};
  const raw = metadata.coins_amount ?? metadata.charged_coins ?? metadata.selected_coins ?? metadata.coin_amount;
  const coins = Number(raw);
  if (Number.isFinite(coins) && coins > 0) return Math.round(coins);
  return Math.round(Number(order.total_price || 0) * 100);
};

const transactionFromStoreOrder = (order = {}, profileId = '') => ({
  id: `order:${order.id}`,
  profile_id: order.profile_id || profileId,
  amount: String(order.total_price || 0),
  proam_coins: String(order.payment_provider || order.payment_method || '').toLowerCase() === 'coins'
    ? recordedStoreOrderCoinAmount(order)
    : 0,
  transaction_type: 'ProAm Store',
  payment_intent_id: null,
  status: String(order.order_status || 'succeeded').toLowerCase() === 'cancelled' ? 'cancelled' : 'succeeded',
  source: 'Purchase',
  withdrawable: false,
  created_at: order.created_at || new Date().toISOString(),
  target_id: order.id || null,
  synthetic: true,
  store_order: { ...order, items: orderItems(order) },
});
const billingPaymentCoinCharge = (payment = {}) => {
  if (typeof payment?.metadata?.charged_coins === 'number') return Number(payment.metadata.charged_coins || 0);
  if (typeof payment?.coins_amount === 'number') return Number(payment.coins_amount || 0);
  return 0;
};
const billingPaymentAmount = (payment = {}) => (
  typeof payment?.amount_cents === 'number' ? Number(payment.amount_cents || 0) / 100 : 0
);
const transactionTypeForBillingPayment = (payment = {}) => {
  const line = payment?.billing_products?.product_line;
  if (line === 'workout_groups') return 'Workout Groups';
  if (line === 'tournaments') return 'Tournament';
  if (line === 'institutions') return 'Institution';
  return payment?.billing_products?.name || 'ProAm Store';
};
const transactionFromBillingPayment = (payment = {}, profileId = '') => ({
  id: `billing:${payment.id}`,
  profile_id: payment.profile_id || profileId,
  amount: String(billingPaymentAmount(payment)),
  amount_cents: payment.amount_cents,
  currency: payment.currency || 'eur',
  proam_coins: billingPaymentCoinCharge(payment),
  transaction_type: transactionTypeForBillingPayment(payment),
  payment_intent_id: payment.provider === 'stripe' ? (payment.provider_payment_id || payment.provider_checkout_session_id || null) : null,
  status: payment.status === 'refunded' ? 'refunded' : 'succeeded',
  source: payment.status === 'refunded' ? 'Refund' : payment.provider === 'stripe' && payment?.metadata?.coin_top_up ? 'Deposit' : 'Purchase',
  withdrawable: false,
  created_at: payment.created_at || new Date().toISOString(),
  target_id: payment.product_id || payment.target_id || null,
  synthetic: true,
  billing_payment_id: payment.id,
  discount_code: payment.discount_code || payment?.metadata?.discount_code || payment?.metadata?.discount?.code || null,
  discount_amount_cents: payment.discount_amount_cents || payment?.metadata?.discount_amount_cents || payment?.metadata?.discount?.discount_amount_cents || null,
  discount_coins: payment.discount_coins || payment?.metadata?.discount_coins || payment?.metadata?.discount?.discount_coins || null,
  discount_metadata: payment.discount_metadata || payment?.metadata?.discount || payment?.metadata || null,
});
const mergeTransactionsWithBillingPayments = (transactions = [], payments = [], profileId = '') => {
  const transactionIds = new Set(transactions.map(tx => tx.id).filter(Boolean));
  const paymentIds = new Set();
  const merged = transactions.map((tx) => {
    const payment = payments.find(row => row.transaction_id === tx.id || row.id === tx.billing_payment_id);
    if (!payment?.id) return tx;
    paymentIds.add(payment.id);
    return {
      ...tx,
      billing_payment_id: payment.id,
      discount_code: tx.discount_code || payment.discount_code || payment?.metadata?.discount_code || payment?.metadata?.discount?.code || null,
      discount_amount_cents: tx.discount_amount_cents || payment.discount_amount_cents || payment?.metadata?.discount_amount_cents || payment?.metadata?.discount?.discount_amount_cents || null,
      discount_coins: tx.discount_coins || payment.discount_coins || payment?.metadata?.discount_coins || payment?.metadata?.discount?.discount_coins || null,
      discount_metadata: tx.discount_metadata || payment.discount_metadata || payment?.metadata?.discount || payment?.metadata || null,
    };
  });

  payments.forEach((payment) => {
    if (!payment?.id || paymentIds.has(payment.id)) return;
    if (payment.transaction_id && transactionIds.has(payment.transaction_id)) return;
    if (!['succeeded', 'refunded'].includes(String(payment.status || 'succeeded'))) return;
    merged.push(transactionFromBillingPayment(payment, profileId));
  });

  return merged;
};
const mergeTransactionsWithStoreOrders = (transactions = [], orders = [], profileId = '') => {
  const usedOrderIds = new Set();
  const merged = transactions.map((tx) => {
    const exactOrder = orders.find((order) => order.id && tx.target_id === order.id);
    if (exactOrder) {
      usedOrderIds.add(exactOrder.id);
      return { ...tx, store_order: { ...exactOrder, items: orderItems(exactOrder) } };
    }
    const likelyOrder = orders.find((order) => order.id && !usedOrderIds.has(order.id) && isLikelySameOrderTransaction(tx, order));
    if (likelyOrder) {
      usedOrderIds.add(likelyOrder.id);
      return { ...tx, target_id: tx.target_id || likelyOrder.id, store_order: { ...likelyOrder, items: orderItems(likelyOrder) } };
    }
    return tx;
  });

  orders.forEach((order) => {
    if (!order?.id || usedOrderIds.has(order.id)) return;
    merged.push(transactionFromStoreOrder(order, profileId));
  });

  return merged.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
};

const readablePayload = (payload) => {
  if (!payload) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload === 'object') {
    const candidate = payload.error || payload.message || payload.details || payload.hint;
    if (typeof candidate === 'string') return candidate;
    if (candidate) return JSON.stringify(candidate, null, 2);
    return JSON.stringify(payload, null, 2);
  }
  return String(payload);
};

const readSupabaseFunctionError = async (error, fallback) => {
  if (!error) return { message: fallback };
  const response = error.context;
  let payload = null;
  let rawBody = '';

  try {
    if (response?.clone && typeof response.clone === 'function') {
      const copy = response.clone();
      const contentType = copy.headers?.get?.('content-type') || '';
      if (contentType.includes('application/json')) {
        payload = await copy.json();
      } else {
        rawBody = await copy.text();
      }
    }
  } catch (readError) {
    rawBody = readError?.message ? `Could not read Edge Function response: ${readError.message}` : '';
  }

  const payloadText = readablePayload(payload) || rawBody;
  console.warn('Stripe Connect setup error:', {
    status: response?.status || null,
    error: error.message || error,
    response: payloadText || null,
  });
  return { message: fallback };
};

/* ═══════════════════ THEME ═══════════════════ */
const DARK = {
  mode: 'dark', accent: '#5BA8F7', accentDark: '#0C69C8', accentLight: '#9CCBFF',
  accentGrad: 'linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)',
  accentGlow: 'rgba(12,105,200,0.35)', accentBgSubtle: 'rgba(12,105,200,0.08)',
  accentBgLight: 'rgba(12,105,200,0.18)', accentBorder: 'rgba(91,168,247,0.42)',
  bg: '#0A0A0A', bgAlt: '#111111', bgCard: '#141414', bgElevated: '#1E1E1E',
  border: 'rgba(255,255,255,0.14)', borderMedium: 'rgba(255,255,255,0.22)',
  shadowSm: '0 1px 3px rgba(0,0,0,0.4)', shadowMd: '0 4px 16px rgba(0,0,0,0.5)',
  shadowLg: '0 12px 40px rgba(0,0,0,0.6)',
  text: '#FAFAFA', textSecondary: 'rgba(255,255,255,0.84)', textMuted: 'rgba(255,255,255,0.66)',
  headerBg: 'rgba(10,10,10,0.92)', inputBg: 'rgba(255,255,255,0.08)',
  inputBorder: 'rgba(255,255,255,0.22)', inputText: '#FFFFFF',
  gold: '#D4A017', silver: '#A0AEC0', bronze: '#C97B3A',
  success: '#34D399', danger: '#EF4444', warn: '#F59E0B',
  scrollbarThumb: 'rgba(255,255,255,0.12)',
};
const LIGHT = {
  mode: 'light', accent: '#0C69C8', accentDark: '#0A1D44', accentLight: '#0C69C8',
  accentGrad: 'linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)',
  accentGlow: 'rgba(12,105,200,0.25)', accentBgSubtle: 'rgba(12,105,200,0.05)',
  accentBgLight: 'rgba(12,105,200,0.08)', accentBorder: 'rgba(12,105,200,0.18)',
  bg: '#F5F7FA', bgAlt: '#EDF0F5', bgCard: '#FFFFFF', bgElevated: '#FFFFFF',
  border: 'rgba(10,29,68,0.08)', borderMedium: 'rgba(10,29,68,0.14)',
  shadowSm: '0 1px 3px rgba(10,29,68,0.06)', shadowMd: '0 4px 16px rgba(10,29,68,0.09)',
  shadowLg: '0 12px 40px rgba(10,29,68,0.12)',
  text: '#0F1629', textSecondary: '#3D4A6B', textMuted: '#7A87A8',
  headerBg: 'rgba(245,247,250,0.95)', inputBg: '#F8F9FC',
  inputBorder: 'rgba(10,29,68,0.12)', inputText: '#0F1629',
  gold: '#B8860B', silver: '#718096', bronze: '#A0522D',
  success: '#059669', danger: '#DC2626', warn: '#D97706',
  scrollbarThumb: 'rgba(10,29,68,0.12)',
};

/* ═══════════════════ CONSTANTS ═══════════════════ */
const noImage = 'https://wcislnchclarifyudagt.supabase.co/storage/v1/object/public/images/app-Images/no-image.jpg';
const COUNTRIES = Country.getAllCountries();
const findCountry = (value) => (
  COUNTRIES.find((item) => item.isoCode === value || item.name.toLowerCase() === String(value || '').toLowerCase())
);
const countryFlagEmoji = (countryCode) => {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🏳️';
  return code.split('').map(letter => String.fromCodePoint(127397 + letter.charCodeAt(0))).join('');
};
const dateInputValue = (date = new Date()) => {
  const dateOnly = String(date || '').match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) return dateOnly[1];
  const next = new Date(date);
  if (!Number.isFinite(next.getTime())) return '';
  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, '0');
  const day = String(next.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const utcDateBoundary = (dateValue, endOfDay = false) => {
  const match = String(dateValue || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return new Date(NaN);
  const [, year, month, day] = match;
  return new Date(Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  ));
};

const platformChallengeOfferDateInput = (offer, keys = []) => {
  for (const key of keys) {
    const value = offer?.[key] ?? offer?.metadata?.[key] ?? offer?.brand_settings?.[key];
    if (!value) continue;
    const formatted = dateInputValue(value);
    if (formatted) return formatted;
  }
  return '';
};
const platformChallengeOfferStartsOn = (offer) => platformChallengeOfferDateInput(offer, ['starts_at', 'startsAt', 'starts_on', 'startsOn', 'start_date', 'startDate']);
const platformChallengeOfferEndsOn = (offer) => platformChallengeOfferDateInput(offer, ['ends_at', 'endsAt', 'ends_on', 'endsOn', 'finish_date', 'finishDate', 'end_date', 'endDate']);
const platformChallengeOfferFinished = (offer) => {
  const endsOn = platformChallengeOfferEndsOn(offer);
  return !!endsOn && endsOn < dateInputValue(new Date());
};
const challengeDurationDays = (startsOn, endsOn) => {
  const start = utcDateBoundary(startsOn);
  const end = utcDateBoundary(endsOn);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};
const challengeDurationLabel = (startsOn, endsOn) => {
  const days = challengeDurationDays(startsOn, endsOn);
  return days > 0 ? `${days} day challenge` : 'Choose dates to see the challenge length.';
};

const ALL_INSTITUTE_TYPES = [
  { value: 'Collective Group', label: 'Collective Group' },
  { value: 'School', label: 'School' },
  { value: 'College', label: 'College / University' },
  { value: 'Company', label: 'Company' },
  { value: 'Club', label: 'Sports Club' },
  { value: 'Gym', label: 'Gym' },
  { value: 'Organisation', label: 'Organisation' },
  { value: 'Challenge', label: 'Challenge' },
];

const statusConfig = {
  upcoming:  { label: 'Upcoming',  bg: 'rgba(12,105,200,0.10)',  border: 'rgba(12,105,200,0.25)',  color: '#0C69C8' },
  completed: { label: 'Completed', bg: 'rgba(5,150,105,0.10)',   border: 'rgba(5,150,105,0.25)',   color: '#059669' },
  cancelled: { label: 'Cancelled', bg: 'rgba(220,38,38,0.10)',   border: 'rgba(220,38,38,0.25)',   color: '#DC2626' },
};

const getAgeFromDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) return '';
  const birthDate = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) return '';

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1;

  return age >= 0 ? String(age) : '';
};

const TABS = [
  { id: 'profile',      label: 'Profile',      icon: faUser },
  { id: 'bookings',     label: 'Bookings',     icon: faCalendar },
  { id: 'transactions', label: 'Transactions', icon: faReceipt },
  { id: 'institutes',   label: 'Institutes',   icon: faBuilding },
  { id: 'workouts',     label: 'Workouts',     icon: faDumbbell },
  { id: 'achievements', label: 'Achievements', icon: faTrophy },
  { id: 'matches',      label: 'Matches',      icon: faTableTennis },
];

const WORKOUT_GROUP_WEB_PLANS = [
  { tier: 'free', title: 'Free', monthlyPrice: '€0', yearlyPrice: '€0', monthlyPriceKey: null, yearlyPriceKey: null, cta: 'Current Free Access', bullets: ['1 group', '2 athletes per group', '1 assigned workout per group', 'Basic dashboard', '1W range'] },
  { tier: 'coach_starter', title: 'Coach Starter', monthlyPrice: '€4.99/month', yearlyPrice: '€50.90/year', monthlyPriceKey: 'workout_groups_coach_starter_monthly_eur', yearlyPriceKey: 'workout_groups_coach_starter_yearly_eur', yearlySaving: 'Save €8.98/year', cta: 'Subscribe', note: 'Best for new coaches.', bullets: ['Up to 2 groups', 'Up to 6 athletes per group', '2 assigned workouts per group', 'Workout share codes', 'Basic dashboard', 'Athlete session feedback', 'Athlete + group 1W / 2W / 4W ranges'] },
  { tier: 'coach', title: 'Coach', monthlyPrice: '€9.99/month', yearlyPrice: '€101.90/year', monthlyPriceKey: 'workout_groups_coach_monthly_eur', yearlyPriceKey: 'workout_groups_coach_yearly_eur', yearlySaving: 'Save €17.98/year', cta: 'Subscribe', badge: 'Most Popular', bullets: ['Up to 3 groups', 'Up to 12 athletes per group', '5 assigned workouts per group', 'Workout share codes', 'Coach attention statuses, notes, reminders', 'Athlete session feedback', 'Rest time analysis', '1W / 2W / 4W ranges'] },
  { tier: 'pro_coach', title: 'Pro Coach', monthlyPrice: '€24.99/month', yearlyPrice: '€254.90/year', monthlyPriceKey: 'workout_groups_pro_coach_monthly_eur', yearlyPriceKey: 'workout_groups_pro_coach_yearly_eur', yearlySaving: 'Save €44.98/year', cta: 'Subscribe', bullets: ['Unlimited groups', 'Unlimited athletes', 'Unlimited assigned workouts', 'Calendar sessions and attendance', 'Nutrition stats and calorie reminders'] },
  { tier: 'team_club', title: 'Club Coaching', monthlyPrice: '€59.99/month', yearlyPrice: '€611.90/year', monthlyPriceKey: 'workout_groups_team_club_monthly_eur', yearlyPriceKey: 'workout_groups_team_club_yearly_eur', yearlySaving: 'Save €107.98/year', cta: 'Start 1-Month Free Trial', badge: '1st month free online', note: 'Online subscription includes the free trial. Mobile coin unlocks are 30-day, non-renewing access with no trial.', bullets: ['Web club dashboard', 'Club branding', 'Preset workout packs', 'Multiple coaches', 'Calendar sessions and attendance', 'Season management', 'Club analytics'] },
];

const WORKOUT_GROUP_CALENDAR_FILTERS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WORKOUT_GROUP_CALENDAR_STATUS = {
  accepted: { label: 'Available', bg: '#ECFDF3', border: '#BBF7D0', text: '#166534' },
  pending: { label: 'Pending', bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
  declined: { label: 'Unavailable', bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' },
  cancelled: { label: 'Cancelled', bg: '#F1F5F9', border: '#CBD5E1', text: '#475569' },
};

const WORKOUT_GROUP_SESSION_TARGET_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map(value => ({ value, label: String(value) }));
const WORKOUT_GROUP_SESSION_GOAL_OPTIONS = [
  { key: 'stay_active', label: 'Stay active', detail: 'Keep the group moving', suggestedTarget: 3 },
  { key: 'weight_loss', label: 'Weight loss', detail: 'More consistent sessions', suggestedTarget: 4 },
  { key: 'build_strength', label: 'Build strength', detail: 'Focused weekly work', suggestedTarget: 4 },
  { key: 'intense_training', label: 'Intense training', detail: 'Push the group harder', suggestedTarget: 5 },
  { key: 'other', label: 'Other', detail: 'Pick your own number', suggestedTarget: 3 },
];
const WORKOUT_GROUP_SESSION_TRACKING_OPTIONS = [
  { key: 'attendance', label: 'Attendance', detail: 'Track who shows up' },
  { key: 'consistency', label: 'Consistency', detail: 'Focus on weekly habits' },
  { key: 'xp', label: 'XP progress', detail: 'Use effort and volume' },
  { key: 'team_average', label: 'Group average', detail: 'Keep it team-based' },
];
const WORKOUT_GROUP_SESSION_INTENSITY_OPTIONS = [
  { key: 'light', label: 'Light', detail: 'Low-pressure week' },
  { key: 'steady', label: 'Steady', detail: 'Balanced effort' },
  { key: 'challenging', label: 'Challenging', detail: 'A clear push' },
  { key: 'high', label: 'High', detail: 'Hard training' },
];
const WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY = {
  ownOnly: 'own_only',
  group: 'group',
};

const WG_CALENDAR_PROFILE_SELECT = 'id,username,name,surname,profile_img,push_token';

const uniqValues = (values = []) => Array.from(new Set(values.filter(Boolean).map(value => String(value))));
const isExpoPushToken = (token) =>
  /^ExponentPushToken\[[^\]]+\]$/.test(String(token || '')) ||
  /^ExpoPushToken\[[^\]]+\]$/.test(String(token || ''));

const isMissingWorkoutGroupColumnError = (error) => {
  const text = [error?.code, error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return error?.code === '42703' ||
    (text.includes('workout_groups') && text.includes('schema cache')) ||
    (text.includes('workout_groups') && text.includes('column') && text.includes('does not exist')) ||
    (text.includes('workout_groups') && text.includes('could not find'));
};

const normalizePositiveTarget = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.round(numericValue) : fallback;
};

const getWorkoutGroupWeeklySessionTarget = (group = {}) => normalizePositiveTarget(group?.weekly_session_target, 3);
const getWorkoutGroupSessionGoalForTarget = (target) => {
  if (target >= 5) return 'intense_training';
  if (target === 4) return 'weight_loss';
  return 'stay_active';
};
const getWorkoutGroupSessionIntensityForTarget = (target) => {
  if (target >= 5) return 'high';
  if (target === 4) return 'challenging';
  if (target <= 2) return 'light';
  return 'steady';
};
const getWorkoutGroupSessionTrackingForTarget = (target) => {
  if (target >= 5) return 'xp';
  if (target >= 3) return 'consistency';
  return 'attendance';
};
const findWorkoutGroupSessionGoalOption = (key) => (
  WORKOUT_GROUP_SESSION_GOAL_OPTIONS.find(option => option.key === String(key || ''))
);
const findWorkoutGroupSessionTrackingOption = (key) => (
  WORKOUT_GROUP_SESSION_TRACKING_OPTIONS.find(option => option.key === String(key || ''))
);
const findWorkoutGroupSessionIntensityOption = (key) => (
  WORKOUT_GROUP_SESSION_INTENSITY_OPTIONS.find(option => option.key === String(key || ''))
);
const getWorkoutGroupSessionGoalOption = (group = {}) => {
  const target = getWorkoutGroupWeeklySessionTarget(group);
  return findWorkoutGroupSessionGoalOption(group?.session_target_goal_key) ||
    findWorkoutGroupSessionGoalOption(getWorkoutGroupSessionGoalForTarget(target)) ||
    WORKOUT_GROUP_SESSION_GOAL_OPTIONS[0];
};
const getWorkoutGroupSessionTrackingOption = (group = {}) => {
  const target = getWorkoutGroupWeeklySessionTarget(group);
  return findWorkoutGroupSessionTrackingOption(group?.session_target_tracking_key) ||
    findWorkoutGroupSessionTrackingOption(getWorkoutGroupSessionTrackingForTarget(target)) ||
    WORKOUT_GROUP_SESSION_TRACKING_OPTIONS[0];
};
const getWorkoutGroupSessionIntensityOption = (group = {}) => {
  const target = getWorkoutGroupWeeklySessionTarget(group);
  return findWorkoutGroupSessionIntensityOption(group?.session_target_intensity_key) ||
    findWorkoutGroupSessionIntensityOption(getWorkoutGroupSessionIntensityForTarget(target)) ||
    WORKOUT_GROUP_SESSION_INTENSITY_OPTIONS[1];
};
const getWorkoutGroupMemberAnalyticsVisibility = (group = {}) => (
  group?.member_analytics_visibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
    ? WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
    : WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.ownOnly
);

const getWorkoutGroupSessionProgressStatus = (sessions, sessionTarget) => {
  const target = Number(sessionTarget || 0);
  const count = Number(sessions || 0);
  const ratio = target > 0 ? count / target : count > 0 ? 1 : 0;

  if (count <= 0) return { label: 'Needs Activity', bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' };
  if (ratio >= 1.2) return { label: '120%+ complete', bg: '#E6F0FF', text: '#0C69C8', border: '#CFE2FF' };
  if (ratio >= 1) return { label: '100% complete', bg: '#ECFDF3', text: '#166534', border: '#BBF7D0' };
  if (ratio >= 0.75) return { label: '75%+ complete', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' };
  return { label: 'Needs Activity', bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' };
};

const localDateInputValue = (date = new Date()) => {
  const next = new Date(date);
  if (!Number.isFinite(next.getTime())) return '';
  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, '0');
  const day = String(next.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const localTimeInputValue = (date = new Date()) => {
  const next = new Date(date);
  if (!Number.isFinite(next.getTime())) return '';
  return `${String(next.getHours()).padStart(2, '0')}:${String(next.getMinutes()).padStart(2, '0')}`;
};

const getWorkoutSessionDateKey = (session = {}) => {
  if (session?.local_date) return String(session.local_date).slice(0, 10);
  return localDateInputValue(session?.started_at ? new Date(session.started_at) : new Date());
};

const getWorkoutSessionTotals = (session = {}) => {
  const exerciseRows = Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : [];
  const exerciseReps = exerciseRows.reduce((sum, row) => sum + Number(row?.total_reps || row?.reps || 0), 0);
  const exerciseVolume = exerciseRows.reduce((sum, row) => {
    const reps = Number(row?.total_reps || row?.reps || 0);
    const savedVolume = Number(row?.total_volume ?? row?.volume);
    if (Number.isFinite(savedVolume)) return sum + savedVolume;
    return sum + (Number(row?.avg_weight || row?.weight || 0) * reps);
  }, 0);
  const exerciseSets = exerciseRows.reduce((sum, row) => {
    if (String(row?.logged_as || 'set').toLowerCase() === 'cardio') return sum;
    if (row?.set === false) return sum;
    return sum + 1;
  }, 0);
  const workoutContext = Array.isArray(session.workout_context) ? session.workout_context[0] : session.workout_context;
  const sessionReps = Number(session.total_reps || 0);
  const sessionVolume = Number(session.total_volume || 0);
  const sessionSets = Number(session.total_sets || 0);
  const totalReps = sessionReps > 0 ? sessionReps : exerciseReps;
  const totalVolume = sessionVolume > 0 ? sessionVolume : exerciseVolume;
  const totalSets = sessionSets > 0 ? sessionSets : exerciseSets;
  const totalRestSeconds = Number(session.total_rest_seconds ?? workoutContext?.rest_timer_usage ?? 0);
  return {
    totalXp: Number(session.total_xp || 0),
    totalSets,
    totalReps,
    totalVolume,
    durationSeconds: Number(session.duration_seconds || 0),
    totalRestSeconds,
    effort: Number(session.session_effort || 0),
  };
};

const getWorkoutGroupEndOfToday = () => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end;
};

const minWorkoutGroupDate = (...dates) => {
  const validDates = dates.filter(date => date instanceof Date && Number.isFinite(date.getTime()));
  if (!validDates.length) return null;
  return new Date(Math.min(...validDates.map(date => date.getTime())));
};

const getWorkoutSessionsTotals = (sessions = []) => (sessions || []).reduce((acc, session) => {
  const totals = getWorkoutSessionTotals(session);
  acc.totalXp += totals.totalXp;
  acc.totalSets += totals.totalSets;
  acc.totalReps += totals.totalReps;
  acc.totalVolume += totals.totalVolume;
  acc.totalDurationSeconds += totals.durationSeconds;
  acc.totalRestSeconds += totals.totalRestSeconds;
  acc.effortSum += totals.effort;
  acc.effortCount += totals.effort > 0 ? 1 : 0;
  return acc;
}, { totalXp: 0, totalSets: 0, totalReps: 0, totalVolume: 0, totalDurationSeconds: 0, totalRestSeconds: 0, effortSum: 0, effortCount: 0 });

const formatWorkoutGroupVolume = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '0 kg';
  if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(numericValue >= 10000 ? 0 : 1)}k kg`;
  return `${Math.round(numericValue).toLocaleString()} kg`;
};

const compactWorkoutGroupNumber = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return '0';
  if (Math.abs(numericValue) >= 1000000) return `${(numericValue / 1000000).toFixed(1)}m`;
  if (Math.abs(numericValue) >= 1000) return `${(numericValue / 1000).toFixed(numericValue >= 10000 ? 0 : 1)}k`;
  return Math.round(numericValue).toLocaleString();
};

const safeWorkoutGroupNutritionNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const formatWorkoutGroupNutritionNumber = (value, options = {}) => {
  const numericValue = safeWorkoutGroupNutritionNumber(value);
  if (options.compact) return compactWorkoutGroupNumber(numericValue);
  return Math.round(numericValue).toLocaleString();
};

const calculateWorkoutGroupProteinTargetFromWeight = (weightKg) => {
  const weight = safeWorkoutGroupNutritionNumber(weightKg, NaN);
  if (!Number.isFinite(weight) || weight <= 0) return null;
  return Math.round(weight * 1.8);
};

const getWorkoutGroupAthleteProteinTarget = (group = {}, nutritionProfile = {}) => {
  const groupTarget = safeWorkoutGroupNutritionNumber(group?.daily_protein_target_g, NaN);
  if (Number.isFinite(groupTarget) && groupTarget > 0) return Math.round(groupTarget);

  const profileTarget = safeWorkoutGroupNutritionNumber(nutritionProfile?.protein_g, NaN);
  if (Number.isFinite(profileTarget) && profileTarget > 0) return Math.round(profileTarget);

  return calculateWorkoutGroupProteinTargetFromWeight(nutritionProfile?.weight_kg);
};

const buildWorkoutGroupNutritionSummary = (rows = [], options = {}) => {
  const expectedRangeDays = Math.max(1, Math.round(safeWorkoutGroupNutritionNumber(options.expectedDays, 1)));
  const athleteCount = Math.max(1, Math.round(safeWorkoutGroupNutritionNumber(options.athleteCount, 1)));
  const expectedDays = expectedRangeDays * athleteCount;
  const targetCalories = Math.max(0, safeWorkoutGroupNutritionNumber(options.dailyCalorieTarget, 2500));
  const proteinTarget = safeWorkoutGroupNutritionNumber(options.dailyProteinTarget, NaN);
  const grouped = new Map();

  (rows || []).forEach(row => {
    const logDate = String(row?.log_date || '').slice(0, 10);
    if (!logDate) return;
    const athleteKey = String(row?.user_id || 'athlete');
    const key = `${athleteKey}:${logDate}`;
    if (!grouped.has(key)) {
      const date = parseWorkoutGroupLocalDateKey(logDate);
      grouped.set(key, {
        key,
        user_id: athleteKey,
        log_date: logDate,
        label: date ? date.toLocaleDateString([], { weekday: 'short' }) : logDate,
        calories: 0,
        protein_g: 0,
        carbs_g: 0,
        fat_g: 0,
        fiber_g: 0,
        sugar_g: 0,
        sodium_mg: 0,
        items_count: 0,
      });
    }
    const bucket = grouped.get(key);
    bucket.calories += safeWorkoutGroupNutritionNumber(row?.calories);
    bucket.protein_g += safeWorkoutGroupNutritionNumber(row?.protein_g);
    bucket.carbs_g += safeWorkoutGroupNutritionNumber(row?.carbs_g);
    bucket.fat_g += safeWorkoutGroupNutritionNumber(row?.fat_g);
    bucket.fiber_g += safeWorkoutGroupNutritionNumber(row?.fiber_g);
    bucket.sugar_g += safeWorkoutGroupNutritionNumber(row?.sugar_g);
    bucket.sodium_mg += safeWorkoutGroupNutritionNumber(row?.sodium_mg);
    bucket.items_count += 1;
  });

  const days = Array.from(grouped.values()).sort((a, b) => `${a.log_date}:${a.user_id}`.localeCompare(`${b.log_date}:${b.user_id}`));
  const loggedDays = days.length;
  const totalCalories = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.calories), 0);
  const totalProtein = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.protein_g), 0);
  const totalCarbs = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.carbs_g), 0);
  const totalFat = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.fat_g), 0);
  const totalFiber = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.fiber_g), 0);
  const totalSugar = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.sugar_g), 0);
  const totalSodium = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.sodium_mg), 0);
  const totalItems = days.reduce((sum, day) => sum + safeWorkoutGroupNutritionNumber(day.items_count), 0);
  const calorieTargetHitDays = days.filter(day => safeWorkoutGroupNutritionNumber(day.calories) >= targetCalories).length;
  const proteinTargetHitDays = Number.isFinite(proteinTarget) && proteinTarget > 0
    ? days.filter(day => safeWorkoutGroupNutritionNumber(day.protein_g) >= proteinTarget * 0.9).length
    : 0;
  const calorieTargetTotal = targetCalories * expectedDays;

  return {
    days,
    logged_days: loggedDays,
    calendar_days: new Set(days.map(day => day.log_date)).size,
    expected_days: expectedDays,
    range_days: expectedRangeDays,
    athlete_count: athleteCount,
    total_calories: totalCalories,
    total_protein: totalProtein,
    total_carbs: totalCarbs,
    total_fat: totalFat,
    total_fiber: totalFiber,
    total_sugar: totalSugar,
    total_sodium: totalSodium,
    total_items: totalItems,
    average_calories: loggedDays ? totalCalories / loggedDays : 0,
    average_protein: loggedDays ? totalProtein / loggedDays : 0,
    average_carbs: loggedDays ? totalCarbs / loggedDays : 0,
    average_fat: loggedDays ? totalFat / loggedDays : 0,
    average_fiber: loggedDays ? totalFiber / loggedDays : 0,
    average_sugar: loggedDays ? totalSugar / loggedDays : 0,
    average_sodium: loggedDays ? totalSodium / loggedDays : 0,
    average_items: loggedDays ? totalItems / loggedDays : 0,
    calorie_target_total: calorieTargetTotal,
    calorie_target_hit_days: calorieTargetHitDays,
    protein_target_hit_days: proteinTargetHitDays,
    logging_consistency_pct: expectedDays ? (loggedDays / expectedDays) * 100 : 0,
    calorie_target_progress_pct: calorieTargetTotal ? Math.min(100, (totalCalories / calorieTargetTotal) * 100) : 0,
    calorie_target_hit_pct: loggedDays ? (calorieTargetHitDays / loggedDays) * 100 : 0,
    protein_target_hit_pct: loggedDays && Number.isFinite(proteinTarget) && proteinTarget > 0 ? (proteinTargetHitDays / loggedDays) * 100 : 0,
    last_logged_at: days[days.length - 1]?.log_date || null,
  };
};

const buildWorkoutGroupNutritionInsight = (summary, dailyCalorieTarget = 2500, label = 'this range', dailyProteinTarget = null, scope = 'athlete') => {
  const loggedDays = safeWorkoutGroupNutritionNumber(summary?.logged_days);
  const consistency = safeWorkoutGroupNutritionNumber(summary?.logging_consistency_pct);
  const avgCalories = safeWorkoutGroupNutritionNumber(summary?.average_calories);
  const avgProtein = safeWorkoutGroupNutritionNumber(summary?.average_protein);
  const targetProgress = safeWorkoutGroupNutritionNumber(summary?.calorie_target_progress_pct);
  const subject = scope === 'group' ? 'athletes' : 'the athlete';
  const isPluralSubject = scope === 'group';

  if (loggedDays <= 0) return `No nutrition logs ${label}.`;
  if (consistency >= 80 && targetProgress >= 60) return `Nutrition logging is consistent and ${subject} ${isPluralSubject ? 'are' : 'is'} close to the calorie window.`;
  if (consistency < 40) return `Food logging is inconsistent. Coach ${subject} to log more days before judging intake.`;
  if (avgCalories > dailyCalorieTarget * 1.15) return `Average calories are above target at ${formatWorkoutGroupNutritionNumber(avgCalories)} kcal/day.`;
  if (avgCalories > 0 && avgCalories < dailyCalorieTarget * 0.85) return `Average calories are below target at ${formatWorkoutGroupNutritionNumber(avgCalories)} kcal/day.`;
  if (dailyProteinTarget && avgProtein > 0 && avgProtein < dailyProteinTarget * 0.85) return `Protein is below target at ${formatWorkoutGroupNutritionNumber(avgProtein)}g/day.`;
  if (avgProtein >= 120) return `Protein intake looks solid at ${formatWorkoutGroupNutritionNumber(avgProtein)}g/day.`;
  return 'Nutrition data is limited. Watch consistency before making strong changes.';
};

const WORKOUT_GROUP_MEAL_META = {
  Breakfast: { color: '#D97706', bg: '#FFF7ED', border: '#FED7AA', icon: faFire },
  Lunch: { color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0', icon: faUtensils },
  Dinner: { color: '#0C69C8', bg: '#E6F0FF', border: '#BFDBFE', icon: faClock },
  Snacks: { color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', icon: faBolt },
};

const normalizeWorkoutGroupMealSection = (value = '') => {
  const normalized = String(value || '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (normalized === 'breakfast') return 'Breakfast';
  if (normalized === 'lunch') return 'Lunch';
  if (normalized === 'dinner') return 'Dinner';
  if (normalized === 'snack' || normalized === 'snacks') return 'Snacks';
  return 'Meals';
};

const getWorkoutGroupNutritionStatus = (value, target, lower = 0.85, upper = 1.15) => {
  const numericValue = safeWorkoutGroupNutritionNumber(value);
  const numericTarget = safeWorkoutGroupNutritionNumber(target, NaN);
  if (!Number.isFinite(numericTarget) || numericTarget <= 0) return { label: 'Track', color: '#64748B', bg: '#F8FAFC', pct: 0 };
  const ratio = numericValue / numericTarget;
  if (ratio < lower) return { label: 'Low', color: '#D97706', bg: '#FFF7ED', pct: Math.min(100, Math.round(ratio * 100)) };
  if (ratio > upper) return { label: 'High', color: '#DC2626', bg: '#FEF2F2', pct: Math.min(140, Math.round(ratio * 100)) };
  return { label: 'On target', color: '#16A34A', bg: '#F0FDF4', pct: Math.min(100, Math.round(ratio * 100)) };
};

const buildWorkoutGroupNutritionStory = (rows = [], members = [], summary = {}, options = {}) => {
  const memberMap = new Map((members || []).filter(member => member?.id).map(member => [
    member.id,
    (`${member.name || ''} ${member.surname || ''}`).trim() || member.username || 'Athlete',
  ]));
  const memberIds = new Set(memberMap.keys());
  const athleteMap = new Map(Array.from(memberMap.entries()).map(([id, name]) => [id, {
    id,
    name,
    dates: new Set(),
    calories: 0,
    protein_g: 0,
    items: 0,
  }]));
  const mealMap = new Map(['Breakfast', 'Lunch', 'Dinner', 'Snacks'].map(meal => [meal, {
    meal,
    count: 0,
    calories: 0,
    protein_g: 0,
    carbs_g: 0,
    fat_g: 0,
    athletes: new Set(),
  }]));
  const foodMap = new Map();

  (rows || []).forEach(row => {
    const athleteId = row?.user_id;
    if (memberIds.size && !memberIds.has(athleteId)) return;
    const logDate = String(row?.log_date || '').slice(0, 10);
    const calories = safeWorkoutGroupNutritionNumber(row?.calories);
    const protein = safeWorkoutGroupNutritionNumber(row?.protein_g);
    const carbs = safeWorkoutGroupNutritionNumber(row?.carbs_g);
    const fat = safeWorkoutGroupNutritionNumber(row?.fat_g);
    const meal = normalizeWorkoutGroupMealSection(row?.meal_section);
    const athlete = athleteMap.get(athleteId) || {
      id: athleteId,
      name: memberMap.get(athleteId) || 'Athlete',
      dates: new Set(),
      calories: 0,
      protein_g: 0,
      items: 0,
    };
    if (logDate) athlete.dates.add(logDate);
    athlete.calories += calories;
    athlete.protein_g += protein;
    athlete.items += 1;
    athleteMap.set(athleteId, athlete);

    const mealBucket = mealMap.get(meal) || { meal, count: 0, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, athletes: new Set() };
    mealBucket.count += 1;
    mealBucket.calories += calories;
    mealBucket.protein_g += protein;
    mealBucket.carbs_g += carbs;
    mealBucket.fat_g += fat;
    if (athleteId) mealBucket.athletes.add(athleteId);
    mealMap.set(meal, mealBucket);

    const foodName = String(row?.food_name || '').replace(/\s+/g, ' ').trim();
    if (foodName) {
      const key = foodName.toLowerCase();
      const foodBucket = foodMap.get(key) || { name: foodName, count: 0, calories: 0, protein_g: 0, meals: new Map(), athletes: new Set() };
      foodBucket.count += 1;
      foodBucket.calories += calories;
      foodBucket.protein_g += protein;
      foodBucket.meals.set(meal, (foodBucket.meals.get(meal) || 0) + 1);
      if (athleteId) foodBucket.athletes.add(athleteId);
      foodMap.set(key, foodBucket);
    }
  });

  const expectedDays = Math.max(1, safeWorkoutGroupNutritionNumber(summary?.expected_days, Math.max(1, memberMap.size)));
  const loggedDays = safeWorkoutGroupNutritionNumber(summary?.logged_days);
  const consistency = safeWorkoutGroupNutritionNumber(summary?.logging_consistency_pct);
  const coverageStatus = consistency >= 75 ? 'Reliable sample' : consistency >= 40 ? 'Partial sample' : 'Not enough logs';
  const macroRows = [
    { key: 'calories', label: 'Calories', icon: faFire, value: safeWorkoutGroupNutritionNumber(summary?.average_calories), target: options.dailyCalorieTarget || 2500, unit: 'kcal', color: '#D97706' },
    { key: 'protein', label: 'Protein', icon: faDumbbell, value: safeWorkoutGroupNutritionNumber(summary?.average_protein), target: options.dailyProteinTarget, unit: 'g', color: '#16A34A' },
    { key: 'carbs', label: 'Carbs', icon: faBolt, value: safeWorkoutGroupNutritionNumber(summary?.average_carbs), target: options.dailyCarbsTarget, unit: 'g', color: '#0C69C8' },
    { key: 'fat', label: 'Fat', icon: faUtensils, value: safeWorkoutGroupNutritionNumber(summary?.average_fat), target: options.dailyFatTarget, unit: 'g', color: '#7C3AED' },
    { key: 'fibre', label: 'Fibre', icon: faUtensils, value: safeWorkoutGroupNutritionNumber(summary?.average_fiber), target: options.dailyFibreTarget, unit: 'g', color: '#0F766E' },
  ].map(row => ({ ...row, status: getWorkoutGroupNutritionStatus(row.value, row.target) }));
  const mealRows = Array.from(mealMap.values())
    .map(row => ({
      ...row,
      athletes: row.athletes.size,
      avgCalories: row.count ? row.calories / row.count : 0,
      meta: WORKOUT_GROUP_MEAL_META[row.meal] || WORKOUT_GROUP_MEAL_META.Lunch,
    }))
    .sort((a, b) => ['Breakfast', 'Lunch', 'Dinner', 'Snacks'].indexOf(a.meal) - ['Breakfast', 'Lunch', 'Dinner', 'Snacks'].indexOf(b.meal));
  const topFoods = Array.from(foodMap.values())
    .map(food => ({
      ...food,
      athletes: food.athletes.size,
      topMeal: Array.from(food.meals.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Meals',
      avgCalories: food.count ? food.calories / food.count : 0,
    }))
    .sort((a, b) => b.count - a.count || b.calories - a.calories)
    .slice(0, 5);
  const athleteRows = Array.from(athleteMap.values())
    .map(athlete => {
      const logged = athlete.dates.size;
      const avgCalories = logged ? athlete.calories / logged : 0;
      const status = logged <= 0
        ? { label: 'No logs', color: '#DC2626', bg: '#FEF2F2' }
        : logged < Math.max(2, Math.ceil(safeWorkoutGroupNutritionNumber(options.expectedDays, 1) * 0.35))
        ? { label: 'Needs logs', color: '#D97706', bg: '#FFF7ED' }
        : { label: 'Logging', color: '#16A34A', bg: '#F0FDF4' };
      return { ...athlete, loggedDays: logged, avgCalories, status };
    })
    .sort((a, b) => a.loggedDays - b.loggedDays || a.name.localeCompare(b.name));

  return {
    expectedDays,
    loggedDays,
    consistency,
    coverageStatus,
    macroRows,
    mealRows,
    topFoods,
    athleteRows,
  };
};

const parseWorkoutGroupLocalDateKey = (key = '') => {
  const [year, month, day] = String(key || '').slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isFinite(date.getTime()) ? date : null;
};

const startOfWorkoutGroupDay = (date = new Date()) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const startOfWorkoutGroupWeek = (date = new Date()) => {
  const next = startOfWorkoutGroupDay(date);
  const day = next.getDay();
  const offset = day === 0 ? 6 : day - 1;
  next.setDate(next.getDate() - offset);
  return next;
};

const addWorkoutGroupDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatWorkoutGroupTrendDate = (date) =>
  date?.toLocaleDateString([], { month: 'short', day: 'numeric' }) || '';

const makeWorkoutGroupTrendBucket = (key, rangeDays) => {
  const date = parseWorkoutGroupLocalDateKey(key);
  return {
    key,
    label: date
      ? Number(rangeDays || 7) <= 7
        ? date.toLocaleDateString([], { weekday: 'short' })
        : formatWorkoutGroupTrendDate(date)
      : key,
    sessions: 0,
    totalXp: 0,
    totalSets: 0,
    totalReps: 0,
    totalVolume: 0,
    durationSeconds: 0,
    totalRestSeconds: 0,
    sessionIds: new Set(),
  };
};

const getWorkoutGroupTrendBucketKey = (date, rangeDays = 7) =>
  Number(rangeDays || 7) > 31
    ? localDateInputValue(startOfWorkoutGroupWeek(date))
    : localDateInputValue(startOfWorkoutGroupDay(date));

const addWorkoutGroupSessionToTrendBucket = (bucket, session) => {
  const totals = getWorkoutSessionTotals(session);
  const sessionId = String(session?.workout_session_id || session?.id || '').trim();
  if (sessionId) {
    if (!bucket.sessionIds.has(sessionId)) {
      bucket.sessionIds.add(sessionId);
      bucket.sessions += 1;
    }
  } else {
    bucket.sessions += 1;
  }
  bucket.totalXp += totals.totalXp;
  bucket.totalSets += totals.totalSets;
  bucket.totalReps += totals.totalReps;
  bucket.totalVolume += totals.totalVolume;
  bucket.durationSeconds += totals.durationSeconds;
  bucket.totalRestSeconds += totals.totalRestSeconds;
};

const buildWorkoutGroupTrendRows = (sessions = [], rangeDays = 7) => {
  const numericRange = Number(rangeDays || 7);
  const today = startOfWorkoutGroupDay(new Date());
  const byKey = new Map();

  if (rangeDays === 0) {
    const datedSessions = (sessions || [])
      .map(session => ({ session, date: parseWorkoutGroupLocalDateKey(getWorkoutSessionDateKey(session)) }))
      .filter(item => item.date);
    if (!datedSessions.length) return [];

    const sortedDates = datedSessions.map(item => item.date).sort((a, b) => a.getTime() - b.getTime());
    const spanDays = Math.max(1, Math.round((sortedDates[sortedDates.length - 1].getTime() - sortedDates[0].getTime()) / 86400000) + 1);
    const bucketRange = spanDays > 31 ? 90 : Math.max(7, spanDays);

    datedSessions.forEach(({ session, date }) => {
      const key = getWorkoutGroupTrendBucketKey(date, bucketRange);
      if (!byKey.has(key)) byKey.set(key, makeWorkoutGroupTrendBucket(key, bucketRange));
      addWorkoutGroupSessionToTrendBucket(byKey.get(key), session);
    });

    return Array.from(byKey.values()).sort((a, b) => String(a.key).localeCompare(String(b.key)));
  }

  const days = Math.max(numericRange || 7, 1);
  const start = addWorkoutGroupDays(today, -(days - 1));
  const weekly = days > 31;
  const cursorStart = weekly ? startOfWorkoutGroupWeek(start) : startOfWorkoutGroupDay(start);
  const cursorEnd = weekly ? startOfWorkoutGroupWeek(today) : today;
  const stepDays = weekly ? 7 : 1;
  const buckets = [];

  for (let cursor = cursorStart; cursor.getTime() <= cursorEnd.getTime(); cursor = addWorkoutGroupDays(cursor, stepDays)) {
    const key = localDateInputValue(cursor);
    const row = makeWorkoutGroupTrendBucket(key, days);
    buckets.push(row);
    byKey.set(key, row);
  }

  (sessions || []).forEach(session => {
    const date = parseWorkoutGroupLocalDateKey(getWorkoutSessionDateKey(session));
    if (!date || date.getTime() < start.getTime() || date.getTime() > today.getTime()) return;
    const key = getWorkoutGroupTrendBucketKey(date, days);
    const bucket = byKey.get(key);
    if (!bucket) return;
    addWorkoutGroupSessionToTrendBucket(bucket, session);
  });

  return buckets;
};

const normalizeWorkoutGroupAnalyticsKey = (value = '') => String(value || '').trim().toLowerCase();

const getWorkoutGroupSessionWorkoutMeta = (session = {}) => {
  const customWorkout = Array.isArray(session.user_workouts) ? session.user_workouts[0] : session.user_workouts;
  const presetWorkout = Array.isArray(session.workouts) ? session.workouts[0] : session.workouts;
  const name = customWorkout?.name || presetWorkout?.name || session.workout_name || 'Unnamed workout';
  const key = session.workout_plan_id
    ? `custom:${session.workout_plan_id}`
    : session.preset_plan_id
      ? `preset:${session.preset_plan_id}`
      : `name:${normalizeWorkoutGroupAnalyticsKey(name) || 'unknown'}`;
  return { key, label: name };
};

const getWorkoutGroupExerciseMeta = (row = {}, fallbackIndex = 0) => {
  const exercise = Array.isArray(row.exercises) ? row.exercises[0] : row.exercises;
  const name = exercise?.name || `Exercise ${fallbackIndex + 1}`;
  return {
    id: String(exercise?.id || row.exercise_id || name).trim(),
    name,
    image_url: exercise?.image_url || null,
    gif_url: exercise?.gif_url || null,
  };
};

const buildWorkoutGroupWorkoutTargets = (sessions = []) => {
  const workoutMap = new Map();

  const ensureWorkout = (session) => {
    const meta = getWorkoutGroupSessionWorkoutMeta(session);
    if (!workoutMap.has(meta.key)) {
      workoutMap.set(meta.key, {
        key: meta.key,
        label: meta.label,
        total_sets: 0,
        total_reps: 0,
        total_volume: 0,
        total_xp: 0,
        sessions_count: 0,
        sessionIds: new Set(),
        trendSessions: [],
        exerciseMap: new Map(),
      });
    }
    return workoutMap.get(meta.key);
  };

  (sessions || []).forEach(session => {
    const sessionId = String(session?.id || session?.workout_session_id || '').trim();
    const workout = ensureWorkout(session);
    const totals = getWorkoutSessionTotals(session);
    workout.total_sets += totals.totalSets;
    workout.total_reps += totals.totalReps;
    workout.total_volume += totals.totalVolume;
    workout.total_xp += totals.totalXp;
    workout.trendSessions.push(session);
    if (sessionId) workout.sessionIds.add(sessionId);
    workout.sessions_count = workout.sessionIds.size || workout.trendSessions.length;

    (Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : []).forEach((row, index) => {
      const meta = getWorkoutGroupExerciseMeta(row, index);
      const exerciseKey = meta.id || `${workout.key}:${meta.name}`;
      if (!workout.exerciseMap.has(exerciseKey)) {
        workout.exerciseMap.set(exerciseKey, {
          id: exerciseKey,
          name: meta.name,
          image_url: meta.image_url,
          gif_url: meta.gif_url,
          total_sets: 0,
          total_reps: 0,
          total_volume: 0,
          total_xp: 0,
          sessions_count: 0,
          sessionIds: new Set(),
          trendSessions: [],
        });
      }
      const exercise = workout.exerciseMap.get(exerciseKey);
      const reps = Number(row.total_reps || 0);
      const avgWeight = Number(row.avg_weight || 0);
      const sets = Number(row.total_sets || 0);
      const volume = Number(row.total_volume || 0) || (avgWeight * reps);
      const xp = Number(row.total_xp || 0);
      exercise.total_sets += sets;
      exercise.total_reps += reps;
      exercise.total_volume += volume;
      exercise.total_xp += xp;
      if (sessionId) exercise.sessionIds.add(sessionId);
      exercise.sessions_count = exercise.sessionIds.size || exercise.sessions_count + 1;
      exercise.trendSessions.push({
        ...session,
        total_sets: sets,
        total_reps: reps,
        total_volume: volume,
        total_xp: xp,
        workout_exercise_summary: [],
      });
    });
  });

  return Array.from(workoutMap.values())
    .map(workout => ({
      ...workout,
      exercises: Array.from(workout.exerciseMap.values())
        .map(exercise => ({
          ...exercise,
          sessionIds: undefined,
        }))
        .sort((a, b) => (b.total_xp - a.total_xp) || (b.total_reps - a.total_reps) || String(a.name).localeCompare(String(b.name))),
      sessionIds: undefined,
      exerciseMap: undefined,
    }))
    .filter(workout => workout.exercises.length > 0 || workout.total_reps > 0)
    .sort((a, b) => (b.total_xp - a.total_xp) || (b.total_reps - a.total_reps) || String(a.label).localeCompare(String(b.label)));
};

const buildWorkoutGroupCalendarDateTime = (dateText, timeText) => {
  const [year, month, day] = String(dateText || '').split('-').map(Number);
  const [hour, minute] = String(timeText || '').split(':').map(Number);
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) return null;
  const value = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isFinite(value.getTime()) ? value : null;
};

const getWorkoutGroupCalendarDefaultForm = (profileId = '') => {
  const start = new Date();
  start.setHours(start.getHours() + 1, 0, 0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    title: 'Group training',
    date: localDateInputValue(start),
    startTime: localTimeInputValue(start),
    hasEndTime: true,
    endTime: localTimeInputValue(end),
    location: '',
    notes: '',
    inviteAll: true,
    selectedAthleteIds: [],
    selectedCoachIds: profileId ? [profileId] : [],
  };
};

const getPreferredWorkoutGroupCalendarCoachIds = (group, coachOptions = [], profileId = '') => {
  const coachOptionIds = new Set((coachOptions || []).map(coach => coach?.id).filter(Boolean).map(String));
  const hasCoachOptions = coachOptionIds.size > 0;
  const leadCoachId = group?.coach_id && (!hasCoachOptions || coachOptionIds.has(String(group.coach_id)))
    ? String(group.coach_id)
    : '';
  const currentCoachId = profileId && (!hasCoachOptions || coachOptionIds.has(String(profileId)))
    ? String(profileId)
    : '';
  const fallbackCoachId = coachOptions?.[0]?.id ? String(coachOptions[0].id) : '';
  const preferredCoachId = leadCoachId || currentCoachId || fallbackCoachId;
  return preferredCoachId ? [preferredCoachId] : [];
};

const getWorkoutGroupCalendarEditForm = (session) => {
  const start = new Date(session?.starts_at || Date.now());
  const end = session?.ends_at ? new Date(session.ends_at) : new Date(start.getTime() + 60 * 60 * 1000);
  return {
    title: session?.title || '',
    date: localDateInputValue(start),
    startTime: localTimeInputValue(start),
    hasEndTime: !!session?.ends_at,
    endTime: localTimeInputValue(end),
    location: session?.location || '',
    notes: session?.notes || '',
    inviteAll: false,
    selectedAthleteIds: (session?.athletes || []).map(row => row.athlete_id).filter(Boolean),
    selectedCoachIds: (session?.coaches || []).map(row => row.coach_id).filter(Boolean),
  };
};

const getWorkoutGroupCalendarDateKey = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return localDateInputValue(date);
};

const formatWorkoutGroupCalendarSectionLabel = (dateKey) => {
  if (!dateKey) return 'Unscheduled';
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const current = new Date(date);
  current.setHours(0, 0, 0, 0);
  const diffDays = Math.round((current.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatWorkoutGroupCalendarRange = (startsAt, endsAt) => {
  if (!startsAt) return 'Date not set';
  const start = new Date(startsAt);
  if (!Number.isFinite(start.getTime())) return 'Date not set';
  const date = start.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (!endsAt) return `${date} at ${startTime}`;
  const end = new Date(endsAt);
  if (!Number.isFinite(end.getTime())) return `${date} at ${startTime}`;
  return `${date} at ${startTime}-${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

const formatWorkoutGroupCalendarParts = (startsAt, endsAt) => {
  if (!startsAt) return { date: 'Date not set', time: 'Time not set' };
  const start = new Date(startsAt);
  if (!Number.isFinite(start.getTime())) return { date: 'Date not set', time: 'Time not set' };
  const date = start.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });
  const startTime = start.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' });
  const end = endsAt ? new Date(endsAt) : null;
  const endTime = end && Number.isFinite(end.getTime())
    ? end.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })
    : '';
  return { date, time: endTime ? `${startTime}-${endTime}` : startTime };
};

const getWorkoutGroupCalendarProfileName = (profile) => (
  `${profile?.name || ''} ${profile?.surname || ''}`.trim() || (profile?.username ? `@${profile.username}` : 'Unknown')
);

const mapDemoProfileToWorkoutGroupCalendarProfile = (profile = {}, fallbackId = '') => ({
  id: profile?.id || fallbackId,
  username: profile?.username || '',
  name: profile?.name || '',
  surname: profile?.surname || '',
  profile_img: profile?.profile_img || '',
  level: profile?.level || 1,
  metadata: profile?.metadata || {},
  is_demo_profile: true,
});

const getWorkoutGroupCalendarAttendanceSummary = (session) => (
  (session?.athletes || []).reduce((acc, athlete) => {
    if (athlete.status === 'accepted') acc.accepted += 1;
    else if (athlete.status === 'declined') acc.declined += 1;
    else acc.pending += 1;
    return acc;
  }, { accepted: 0, pending: 0, declined: 0 })
);

const hasWorkoutGroupCalendarAccess = (access) => !!(
  hasWorkoutGroupTier(access, 'pro_coach') ||
  hasWorkoutGroupTier(access, 'team_club') ||
  access?.entitlements?.has?.('workout_groups.calendar_sessions')
);

const INSTITUTION_WEB_PLANS = [
  { tier: 'friends', title: 'Friends', segment: 'Social', size: 'Up to 5 people', monthlyPrice: '€4.99/month', yearlyPrice: '€50.90/year', memberLimit: 5, monthlyProductKey: 'institutions_friends_monthly', yearlyProductKey: 'institutions_friends_yearly', cta: 'Create Leaderboard', bullets: ['Step leaderboard', 'App dashboard', 'No team institutes'] },
  { tier: 'crew', title: 'Crew', segment: 'Social', size: 'Up to 15 people', monthlyPrice: '€14.99/month', yearlyPrice: '€152.90/year', memberLimit: 15, monthlyProductKey: 'institutions_crew_monthly', yearlyProductKey: 'institutions_crew_yearly', cta: 'Create Leaderboard', bullets: ['Step leaderboard', 'App dashboard', 'No team institutes'] },
  { tier: 'club', title: 'Club', segment: 'Social', size: 'Up to 30 people', monthlyPrice: '€29/month', yearlyPrice: '€295.80/year', memberLimit: 30, monthlyProductKey: 'institutions_club_monthly', yearlyProductKey: 'institutions_club_yearly', cta: 'Create Leaderboard', bullets: ['Step leaderboard', 'App dashboard', 'No team institutes'] },
  { tier: 'club_starter', title: 'Club Starter', segment: 'Club', size: 'Up to 50 members', monthlyPrice: '€49/month', yearlyPrice: '€499.80/year', baseAmountCents: 4900, yearlyAmountCents: 49980, memberLimit: 50, monthlyProductKey: 'institutions_club_starter_monthly', yearlyProductKey: 'institutions_club_starter_yearly', cta: 'Start Access', bullets: ['Step leaderboard', 'App dashboard', 'Custom branding', 'Team institutes', 'Priority support', 'Onboarding'] },
  { tier: 'club_plus', title: 'Club Plus', segment: 'Club', size: 'Up to 150 members', monthlyPrice: '€99/month', yearlyPrice: '€1,009.80/year', baseAmountCents: 9900, yearlyAmountCents: 100980, memberLimit: 150, monthlyProductKey: 'institutions_club_plus_monthly', yearlyProductKey: 'institutions_club_plus_yearly', cta: 'Start Access', bullets: ['Step leaderboard', 'App dashboard', 'Custom branding', 'Team institutes', 'Priority support', 'Onboarding'] },
  { tier: 'club_pro', title: 'Club Pro', segment: 'Club', size: 'Up to 300 members', monthlyPrice: '€199/month', yearlyPrice: '€2,029.80/year', baseAmountCents: 19900, yearlyAmountCents: 202980, memberLimit: 300, monthlyProductKey: 'institutions_club_pro_monthly', yearlyProductKey: 'institutions_club_pro_yearly', cta: 'Start Access', bullets: ['Step leaderboard', 'App dashboard', 'Custom branding', 'Analytics and exporting', 'Team institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
  { tier: 'club_elite', title: 'Club Elite', segment: 'Club', size: '301+ members', monthlyPrice: 'From €449/month', yearlyPrice: 'From €4,579.80/year', baseAmountCents: 44900, yearlyAmountCents: 457980, metadata: { usage_based: true, included_members: 300, overage_bands: [{ from: 301, to: null, amount_cents: 125 }] }, memberLimit: null, monthlyProductKey: 'institutions_club_elite_monthly', yearlyProductKey: 'institutions_club_elite_yearly', cta: 'Estimate your price', perMember: '300 included, then €1.25/member/month', bullets: ['Step leaderboard', 'App dashboard', 'Custom branding', 'Analytics and exporting', 'Team institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
  { tier: 'team', title: 'Team', segment: 'Business', size: 'Up to 20 people', monthlyPrice: '€149/month', yearlyPrice: '€1,519.80/year', memberLimit: 20, monthlyProductKey: 'institutions_team_monthly', yearlyProductKey: 'institutions_team_yearly', cta: 'Start Access', badge: 'Full dashboard starts here', featured: true, bullets: ['Full web dashboard', 'Custom branding', 'Analytics and exporting', 'Member and department institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
  { tier: 'startup', title: 'Startup', segment: 'Business', size: 'Up to 50 people', monthlyPrice: '€299/month', yearlyPrice: '€3,049.80/year', memberLimit: 50, monthlyProductKey: 'institutions_startup_monthly', yearlyProductKey: 'institutions_startup_yearly', cta: 'Start Access', bullets: ['Full web dashboard', 'Custom branding', 'Analytics and exporting', 'Member and department institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
  { tier: 'growth', title: 'Growth', segment: 'Business', size: 'Up to 150 people', monthlyPrice: '€1,299/month', yearlyPrice: '€13,249.80/year', memberLimit: 150, monthlyProductKey: 'institutions_growth_monthly', yearlyProductKey: 'institutions_growth_yearly', cta: 'Start Access', bullets: ['Full web dashboard', 'Custom branding', 'Analytics and exporting', 'Member and department institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
  { tier: 'scale', title: 'Scale', segment: 'Business', size: 'Up to 300 people', monthlyPrice: '€1,999/month', yearlyPrice: '€20,389.80/year', memberLimit: 300, monthlyProductKey: 'institutions_scale_monthly', yearlyProductKey: 'institutions_scale_yearly', cta: 'Start Access', bullets: ['Full web dashboard', 'Custom branding', 'Analytics and exporting', 'Member and department institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
  { tier: 'enterprise', title: 'Enterprise', segment: 'Business', size: 'Custom size', monthlyPrice: 'Custom pricing', yearlyPrice: 'Custom pricing', memberLimit: null, monthlyProductKey: 'institutions_enterprise_monthly', yearlyProductKey: 'institutions_enterprise_yearly', cta: 'Request rollout', badge: 'Custom rollout', bullets: ['Full web dashboard', 'Custom branding', 'Analytics and exporting', 'Member and department institutes', 'Priority support', 'Onboarding', 'Live dashboard'] },
];
const institutionPlanDisplayName = (plan) => plan?.segment === 'Club' ? plan.title : [plan?.segment, plan?.title].filter(Boolean).join(' ');
const isBusinessInstitutionPlan = (plan) => String(plan?.segment || '').toLowerCase() === 'business';
const createBusinessDemoInstitutionPlan = (basePlan = {}) => ({
  ...basePlan,
  tier: `${basePlan.tier || 'business'}_demo_card`,
  title: basePlan.title || 'Enterprise',
  segment: 'Business',
  size: basePlan.size || 'Custom size',
  badge: '',
  featured: false,
  monthlyPrice: basePlan.monthlyPrice || 'Custom pricing',
  yearlyPrice: basePlan.yearlyPrice || 'Custom pricing',
  perMember: '',
  cta: basePlan.cta || 'Book a Demo',
  bullets: [
    'Member and department institutes',
    'Live web dashboards',
    'Analytics and exporting',
    'Custom branding',
    'Onboarding support',
  ],
  isBusinessDemoCard: true,
  sourcePlan: basePlan,
});
const collapseBusinessDemoInstitutionPlans = (plans = [], demoMode = false) => {
  if (!demoMode) return plans;
  const businessPlan = plans.find((plan) => String(plan?.tier || '').toLowerCase() === 'enterprise') || plans.find(isBusinessInstitutionPlan);
  if (!businessPlan) return plans;
  return [
    ...plans.filter((plan) => !isBusinessInstitutionPlan(plan)),
    createBusinessDemoInstitutionPlan(businessPlan),
  ];
};
const INSTITUTION_WEB_PLAN_ORDER = Object.fromEntries(INSTITUTION_WEB_PLANS.map((plan, index) => [plan.tier, index + 1]));
const institutionPlanRank = (tier) => INSTITUTION_WEB_PLAN_ORDER[String(tier || '').toLowerCase()] || 0;
const institutionSubgroupLabel = (inst = {}) => {
  const settings = inst.brand_settings || {};
  const explicit = String(settings.subgroupLabel || settings.subgroup_label || '').toLowerCase();
  if (explicit.includes('academic group')) return 'Academic Group';
  if (explicit.includes('department')) return 'Department';
  if (explicit.includes('team')) return 'Team';
  if ((inst.type || inst.institute_type) === 'College') return 'Academic Group';
  const plan = INSTITUTION_WEB_PLANS.find(item => item.tier === String(inst.plan_tier || settings.planTier || '').toLowerCase());
  if (plan?.segment === 'Business') return 'Department';
  if (plan?.segment === 'Club' || plan?.segment === 'Social') return 'Team';
  return ['School', 'Company'].includes(inst.type || inst.institute_type) ? 'Department' : 'Team';
};
const businessInstitutionPlanTiers = new Set(INSTITUTION_WEB_PLANS.filter(plan => plan.segment === 'Business').map(plan => plan.tier));
const institutionPlanSegment = (inst = {}) => {
  const settings = inst.brand_settings || {};
  const explicitSegment = String(settings.segment || settings.planSegment || '').toLowerCase();
  if (['business', 'club', 'social'].includes(explicitSegment)) return explicitSegment;
  const plan = INSTITUTION_WEB_PLANS.find(item => item.tier === String(inst.plan_tier || settings.planTier || '').toLowerCase());
  return plan ? String(plan.segment || '').toLowerCase() : '';
};
const defaultAssociationTypeForInstitute = (inst = {}) => {
  const type = String(inst.type || inst.institute_type || '');
  if (type === 'Challenge') return 'Member';
  if (type === 'School' || type === 'College') return 'Student';
  const segment = institutionPlanSegment(inst);
  if (segment === 'business') return 'Employee';
  if (segment === 'social' || segment === 'club') return 'Member';
  const settings = inst.brand_settings || {};
  const tier = String(inst.plan_tier || settings.planTier || '').toLowerCase();
  if (businessInstitutionPlanTiers.has(tier) || type === 'Company') return 'Employee';
  return 'Member';
};

const planItemState = (item) => {
  const unavailable = /^no\s+/i.test(item);
  const addon = /add-on/i.test(item);
  return {
    unavailable,
    addon,
    label: unavailable ? item.replace(/^no\s+/i, '') : item,
  };
};

const WORKOUT_GROUP_PROFILE_RANGE_OPTIONS = [
  { value: 'all', label: 'All', days: 0 },
  { value: 'date', label: 'Date', days: 1 },
  { value: 'last_7', label: '1W', days: 7 },
  { value: 'last_14', label: '2W', days: 14 },
  { value: 'last_28', label: '4W', days: 28 },
  { value: 'last_365', label: 'Year', days: 365 },
  { value: 'custom', label: 'Custom', days: null },
];
const WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS = [
  { value: 'date', label: 'Date', days: 1 },
  { value: 'last_7', label: '7D', days: 7 },
  { value: 'last_14', label: '14D', days: 14 },
  { value: 'last_30', label: '30D', days: 30 },
  { value: 'last_90', label: '90D', days: 90 },
  { value: 'all', label: 'All', days: 0 },
];

const FEEDBACK_EMOJI_OPTIONS = {
  rating: [
    { value: 1, emoji: '😖', label: 'Hated' },
    { value: 2, emoji: '😕', label: 'Meh' },
    { value: 3, emoji: '🙂', label: 'Good' },
    { value: 4, emoji: '😄', label: 'Loved' },
    { value: 5, emoji: '🤩', label: 'Amazing' },
  ],
  difficulty: [
    { value: 1, emoji: '😌', label: 'Easy' },
    { value: 2, emoji: '🙂', label: 'Light' },
    { value: 3, emoji: '😅', label: 'Moderate' },
    { value: 4, emoji: '😤', label: 'Hard' },
    { value: 5, emoji: '🥵', label: 'Brutal' },
  ],
  focus: [
    { value: 1, emoji: '❌', label: 'Missed' },
    { value: 2, emoji: '👎', label: 'Poor' },
    { value: 3, emoji: '🤷', label: 'Unsure' },
    { value: 4, emoji: '👍', label: 'Good' },
    { value: 5, emoji: '🎯', label: 'Perfect' },
  ],
};

const MOOD_OPTIONS = [
  { value: 'energized', emoji: '⚡', label: 'Energized' },
  { value: 'strong', emoji: '💪', label: 'Strong' },
  { value: 'relaxed', emoji: '😌', label: 'Relaxed' },
  { value: 'stressed', emoji: '😤', label: 'Stressed' },
  { value: 'tired', emoji: '😴', label: 'Tired' },
];

/* ═══════════════════ HELPERS ═══════════════════ */
const uid = (p = 'id') => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
const randomDigits = (len) => Array.from({ length: len }, () => Math.floor(Math.random() * 10)).join('');
const normalizeAccessCode = (value = '') => String(value || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
const codeFromName = (name = '', fallback = 'SNOW') => {
  const prefix = normalizeAccessCode(name).slice(0, 4) || fallback;
  return `${prefix}${randomDigits(4)}`.slice(0, 8);
};
const capitalise = (s = '') => s.charAt(0).toUpperCase() + s.slice(1);
const fmtDate = (v) => { try { return new Date(v).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return v; } };
const workoutAccessTimingSummary = (access = {}) => {
  if (access.renewsAt) return { label: 'Renewal', value: fmtDate(access.renewsAt) };
  if (access.expiresAt) return { label: 'Access ends', value: fmtDate(access.expiresAt) };
  const isFree = String(access.tier || '').toLowerCase() === 'free' || String(access.source || '').toLowerCase() === 'free';
  return { label: 'Access', value: isFree ? 'Free tier' : 'Ongoing' };
};
const fmtAmt = (v) => Number(v || 0).toLocaleString('en-IE', { style: 'currency', currency: 'EUR' });
const numberFmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');
const inactiveInstitutionStatuses = ['expired', 'cancelled', 'revoked', 'past_due', 'inactive'];
const isInstitutionInactive = (status) => inactiveInstitutionStatuses.includes(String(status || '').toLowerCase());
const isInstitutionAccessLocked = (institution) => Boolean(institution && (institution.access_active === false || isInstitutionInactive(institution.billing_status)));
const institutionStepText = (locked, value) => locked ? 'Locked' : numberFmt(Number(value || 0));
const ASSOCIATION_TYPE_OPTIONS_BY_INSTITUTE = {
  School: ['Student', 'Teacher', 'Employee', 'Member'],
  College: ['Student', 'Lecturer', 'Employee', 'Member'],
  Company: ['Employee', 'Manager', 'Member', 'Student'],
  Club: ['Member', 'Coach', 'Employee', 'Student'],
  Gym: ['Member', 'Coach', 'Employee'],
  Organisation: ['Member', 'Employee', 'Student'],
  Challenge: ['Member'],
  'Collective Group': ['Member', 'Employee', 'Student'],
};
const roleOptionsForMember = (type = '', current = '') => {
  const defaults = type === 'Challenge'
    ? ASSOCIATION_TYPE_OPTIONS_BY_INSTITUTE[type] || ['Member']
    : Array.from(new Set([...(ASSOCIATION_TYPE_OPTIONS_BY_INSTITUTE[type] || ['Member']), 'Employee', 'Manager', 'Member', 'Student']));
  return current && !defaults.includes(current) ? [current, ...defaults] : defaults;
};
const fmtCoins = (n) => Number(n || 0).toLocaleString('en-IE');
const pct = (v, m) => (m ? Math.min(100, Math.round((v / m) * 100)) : 0);
const dateOnlyTime = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};
const inclusiveDays = (start, end) => {
  if (!start || !end) return 1;
  return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000) + 1);
};
const planStatusLabel = (plan) => {
  if (plan.cancelAtPeriodEnd || plan.status === 'cancels') return 'Cancels';
  if (plan.status === 'trialing') return 'Trial';
  if (plan.status === 'past_due') return 'Past due';
  if (plan.status === 'pending') return 'Pending';
  return 'Active';
};
const planStatusTone = (plan) => {
  if (plan.cancelAtPeriodEnd || plan.status === 'cancels') return 'warning';
  if (plan.status === 'trialing') return 'info';
  if (plan.status === 'past_due' || plan.status === 'pending') return 'warning';
  if (plan.expiresAt) {
    const expires = new Date(plan.expiresAt).getTime();
    if (Number.isFinite(expires) && expires - Date.now() <= 14 * 86400000) return 'warning';
  }
  return 'success';
};
const isCoinAccessPlan = (plan) => (
  String(plan?.source || '').toLowerCase().includes('coin') ||
  Number(plan?.coinAmount || plan?.chargedCoins || 0) > 0
);
const planDateLabel = (plan) => {
  if (isCoinAccessPlan(plan) && plan.expiresAt) return `30-day access · Ends ${fmtDate(plan.expiresAt)}`;
  if (plan.renewsAt) return `Renews ${fmtDate(plan.renewsAt)}`;
  if (plan.expiresAt) return `Ends ${fmtDate(plan.expiresAt)}`;
  return plan.kind === 'subscription' ? 'Subscription active' : 'Access active';
};
const planSourceLabel = (plan) => {
  const source = String(plan?.source || '').toLowerCase();
  const interval = String(plan?.interval || '').toLowerCase();
  if (source.includes('stripe')) return 'Card payment';
  if (source.includes('coin')) return 'Coin unlock';
  if (source.includes('manual')) return 'Manual access';
  if (source.includes('trial')) return 'Trial access';
  if (interval === 'one_time') return 'One-time access';
  if (plan?.kind === 'subscription') return 'Subscription';
  if (plan?.amountCents === 0 || plan?.amountCents === null) return 'No card charge';
  return humanizeToken(plan?.source, 'Access');
};
const canCancelBillingSubscription = (subscription) => (
  subscription?.provider === 'stripe' &&
  !!subscription?.provider_subscription_id &&
  !subscription?.cancel_at_period_end &&
  ['active', 'trialing', 'past_due'].includes(String(subscription?.status || ''))
);
const subscriptionMatchesPayment = (subscription = {}, payment = {}, tx = {}) => {
  if (!subscription?.id || !payment?.id) return false;
  if (subscription.product_id && payment.product_id && subscription.product_id === payment.product_id) return true;
  if (subscription.price_id && payment.price_id && subscription.price_id === payment.price_id) return true;
  if (subscription.target_id && payment.target_id && subscription.target_id === payment.target_id) return true;
  if (subscription.target_id && tx.target_id && subscription.target_id === tx.target_id) return true;
  return false;
};
const paymentAmountLabel = (payment) => {
  if (typeof payment?.amount_cents === 'number') {
    if (payment.amount_cents === 0) return payment.status === 'refunded' ? 'Refunded' : 'Free';
    return formatMoney(payment.amount_cents / 100, payment.currency || 'eur');
  }
  if (typeof payment?.coins_amount === 'number') return `${fmtCoins(payment.coins_amount)} coins`;
  return 'Recorded';
};
const paymentStatusLabel = (payment) => {
  const status = String(payment?.status || '').toLowerCase();
  if (paymentAmountLabel(payment) === 'Free') return 'Free';
  if (status === 'succeeded') return 'Paid';
  return humanizeToken(status, 'Recorded');
};
const paymentTone = (payment) => {
  const status = String(payment?.status || '').toLowerCase();
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'pending') return 'warning';
  if (status === 'refunded') return 'success';
  return 'success';
};
const accessImportance = (plan) => {
  const source = String(plan?.source || '').toLowerCase();
  if (plan.kind === 'subscription' && plan.status === 'active') return 1;
  if (plan.renewsAt) return 2;
  if (plan.expiresAt) return 3;
  if (plan.status === 'trialing') return 4;
  if (source.includes('manual') || !plan.amountCents) return 5;
  return 6;
};
const sortAccessPlans = (plans = []) => [...plans].sort((a, b) => accessImportance(a) - accessImportance(b));

const templateIconFor = (t) => ({
  School: faSchool,
  College: faGraduationCap,
  Company: faBuilding,
  Club: faMedal,
  Gym: faDumbbell,
  Organisation: faBuilding,
  Challenge: faTrophy,
  'Collective Group': faHandshake,
}[t] || faBuilding);
const TypeIcon = ({ type, style = {} }) => <FontAwesomeIcon icon={templateIconFor(type)} style={{ fontSize: 12, marginRight: 5, ...style }} />;
const instituteLogoUrl = (institute = {}) => institute.brand_logo_url || institute.workspace_logo_url || institute.image_url || institute.display_logo_url || null;
const InstituteBrandLogo = ({ src, alt, style = {} }) => (
  <span style={{ ...style, display: 'inline-grid', placeItems: 'center', overflow: 'hidden', padding: isPaddedUcdLogo(src) ? 0 : (style.padding || 0) }}>
    <img src={src} alt={alt} loading="lazy" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', transform: isPaddedUcdLogo(src) ? 'scale(1.24)' : undefined }} />
  </span>
);
const instituteImageUrl = (institute = {}) => institute.image_url || institute.display_logo_url || institute.brand_logo_url || null;
const isCompanyInstitute = (institute = {}) => String(institute.type || institute.institute_type || '').toLowerCase() === 'company';
const instituteImageIsLogo = (institute = {}) => {
  const imageUrl = instituteImageUrl(institute);
  if (!imageUrl) return false;
  return [institute.workspace_logo_url, institute.brand_logo_url]
    .filter(Boolean)
    .some(logoUrl => logoUrl === imageUrl);
};
const InstituteImage = ({ C, institute, style = {}, iconSize = 20, fit = 'cover' }) => {
  const src = instituteImageUrl(institute);
  const compactLogo = fit === 'contain' && isPaddedUcdLogo(src);
  return src ? (
    compactLogo
      ? <span style={{ background: C.bgCard, ...style, display: 'inline-grid', placeItems: 'center', overflow: 'hidden', padding: 0 }}><img src={src} alt={institute.name} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', transform: 'scale(1.24)' }} loading="lazy" /></span>
      : <img src={src} alt={institute.name} style={{ objectFit: fit, background: C.bgCard, ...style }} loading="lazy" />
  ) : (
    <div role="img" aria-label={`${institute.name} image placeholder`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.accentBgSubtle, color: C.accent, ...style }}>
      <FontAwesomeIcon icon={templateIconFor(institute.type)} style={{ fontSize: iconSize }} />
    </div>
  );
};
const GroupImage = ({ C, group, label = 'Team', style = {}, iconSize = 20 }) => group?.image_url ? (
  <img src={group.image_url} alt={group.name} style={{ objectFit: 'cover', ...style }} loading="lazy" />
) : (
  <div role="img" aria-label={`${group?.name || label} image placeholder`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.accentBgSubtle, color: C.accent, ...style }}>
    <FontAwesomeIcon icon={label === 'Department' ? faBuilding : faUsers} style={{ fontSize: iconSize }} />
  </div>
);
const WorkoutGroupLogo = ({ C, group, club, themed = false, size = 44, radius = 14, icon = faDumbbell, iconSize = 16, style = {} }) => {
  const [failed, setFailed] = useState(false);
  const logoUrl = club?.logo_url || group?.logo_url || group?.image_url || null;
  const label = group?.name || club?.name || 'Workout group';
  const baseStyle = {
    width: size,
    height: size,
    borderRadius: radius,
    flexShrink: 0,
    ...style,
  };

  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt=""
        style={{ ...baseStyle, objectFit: 'cover', objectPosition: 'center', border: '1px solid rgba(255,255,255,0.35)', background: '#FFFFFF' }}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div
      role="img"
      aria-label={`${label} logo placeholder`}
      style={{
        ...baseStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: themed ? 'rgba(255,255,255,0.14)' : C.accentBgSubtle,
        color: themed ? '#fff' : C.accent,
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: iconSize }} />
    </div>
  );
};
const capitaliseType = (t = '') => { if (!t) return ''; const k = ['Collective Group', 'School', 'College', 'Company', 'Club', 'Gym', 'Organisation', 'Challenge']; const m = k.find(x => x.toLowerCase() === t.toLowerCase()); return m || t.charAt(0).toUpperCase() + t.slice(1).toLowerCase(); };
const typePluralLower = (t = 'School') => { const s = (t || '').toLowerCase(); if (s.startsWith('school')) return 'schools'; if (s.startsWith('college')) return 'colleges'; if (s.startsWith('company')) return 'companies'; if (s.startsWith('organisation') || s.startsWith('organization')) return 'organisations'; if (s.startsWith('club')) return 'clubs'; if (s.startsWith('challenge')) return 'challenges'; return `${s}s`; };
const instituteStructureLabel = (inst = {}) => {
  if (inst.type === 'Challenge') return inst.teams ? 'Team challenge' : 'Member challenge';
  if (inst.teams) return `${institutionSubgroupLabel(inst)}s`;
  return 'Members';
};
const instituteCardOrder = (a = {}, b = {}) => {
  const rank = inst => inst.type === 'Challenge' ? 2 : inst.teams ? 1 : 0;
  const rankOrder = rank(a) - rank(b);
  if (rankOrder) return rankOrder;
  const workspaceOrder = String(a.workspace_name || '').localeCompare(String(b.workspace_name || ''), undefined, { sensitivity: 'base' });
  if (workspaceOrder) return workspaceOrder;
  return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
};
const isCodeTaken = async (code, excludeInstId = null, excludeTeamId = null) => {
  let iq = supabase.from('institutes').select('id').eq('access_code', code);
  if (excludeInstId) iq = iq.neq('id', excludeInstId);
  const { data: ir } = await iq;
  if (ir?.length > 0) return true;
  let tq = supabase.from('institute_teams').select('id').eq('access_code', code).is('archived_at', null);
  if (excludeTeamId) tq = tq.neq('id', excludeTeamId);
  const { data: tr } = await tq;
  return tr?.length > 0;
};
const generateUniqueCode = async (len, exI = null, exT = null, name = '') => {
  for (let i = 0; i < 10; i++) { const c = name && i === 0 ? codeFromName(name).slice(0, len) : randomDigits(len); if (!(await isCodeTaken(c, exI, exT))) return c; }
  throw new Error('Could not generate unique code');
};

/* ═══════════════════ CSS ═══════════════════ */
const makeCSS = (C) => `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;-webkit-font-smoothing:antialiased;}
html{scroll-behavior:smooth;}
body{font-family:'DM Sans',sans-serif;background:${C.bg};color:${C.text};}
a{color:inherit;text-decoration:none;}
::-webkit-scrollbar{width:5px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:${C.scrollbarThumb};border-radius:99px;}
.heading{font-family:'Sora',sans-serif;}
.grad-text{background:linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.card-lift{transition:transform 0.25s cubic-bezier(0.16,1,0.3,1),box-shadow 0.25s ease;}
.card-lift:hover{transform:translateY(-2px);box-shadow:${C.shadowLg};}
.tab-btn{transition:all 0.15s ease;}
.tab-btn:hover{color:${C.accent}!important;}
input:focus,select:focus,textarea:focus{outline:none;border-color:rgba(12,105,200,0.5)!important;box-shadow:0 0 0 3px rgba(12,105,200,0.10)!important;}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
.fade-in{animation:fadeIn 0.3s cubic-bezier(0.16,1,0.3,1);}
@keyframes modalIn{from{opacity:0;transform:scale(0.96) translateY(8px);}to{opacity:1;transform:scale(1) translateY(0);}}
.modal-animate{animation:modalIn 0.28s cubic-bezier(0.16,1,0.3,1);}
@keyframes shimmer{0%{opacity:0.6;}50%{opacity:1;}100%{opacity:0.6;}}
.achiev-locked{filter:grayscale(1);opacity:0.38;}
.achiev-earned{filter:drop-shadow(0 0 8px rgba(212,160,23,0.5));}

/* ── MOBILE RESPONSIVE ── */
@media (max-width:767px){
  /* Prevent horizontal scroll on body */
  body{overflow-x:hidden;}

  /* Tab bar: icon-only on very small, compact labels on larger mobile */
  .tab-label{display:none;}
  
  /* Touch-friendly tap targets */
  button,a,[role="button"]{-webkit-tap-highlight-color:transparent;}

  /* Hero stats wrap nicely */
  .hero-stats{flex-wrap:wrap;gap:6px!important;}

  /* Achievement grid: 2 columns on mobile */
  .achiev-grid{grid-template-columns:repeat(2,1fr)!important;}

  /* Stats 4-grid: 2x2 on mobile */
  .stats-4{grid-template-columns:repeat(2,1fr)!important;}

  /* Match grid: 1 column */
  .match-grid{grid-template-columns:1fr!important;}

  /* Info card grid: 1 column */
  .info-grid{grid-template-columns:1fr!important;}

  /* Sport + team grid */
  .sport-grid{grid-template-columns:1fr!important;}

  /* Modal full-screen on mobile */
  .modal-wrap{padding:0!important;align-items:flex-end!important;}
  .modal-inner{border-radius:20px 20px 0 0!important;max-height:92svh!important;width:100%!important;}
  
  /* Leaderboard table: hide sessions col */
  .wg-sessions-col{display:none!important;}
  .wg-grid{grid-template-columns:36px 1.6fr 1fr!important;}

  /* Score display responsive */
  .score-num{font-size:28px!important;}

  /* Compact match location banner */
  .match-banner{height:120px!important;}

  /* Dept card grid: 1 col on mobile */
  .dept-grid{grid-template-columns:1fr!important;}

  /* Scrollable filter chips */
  .filter-row{overflow-x:auto;flex-wrap:nowrap!important;padding-bottom:6px;}
  .filter-row::-webkit-scrollbar{height:0;}

  /* Institute switcher cards */
  .inst-card{min-width:160px!important;max-width:180px!important;}

  /* Workout group cards */
  .wg-card{min-width:180px!important;max-width:200px!important;}
}

@media (min-width:768px){
  .tab-label{display:inline;}
}
`;

/* ═══════════════════ STYLE HELPERS ═══════════════════ */
const card = (C, extra = {}) => ({ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, boxShadow: C.shadowMd, ...extra });
const inputSt = (C) => ({ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${C.inputBorder}`, background: C.inputBg, color: C.inputText, fontSize: 14, fontFamily: "'DM Sans',sans-serif", WebkitAppearance: 'none' });
const readableAccentText = (C) => C.mode === 'dark' ? '#D8ECFF' : C.accent;
const dangerIconButtonSt = (C, size = 34, extra = {}) => ({
  width: size,
  height: size,
  borderRadius: size >= 34 ? 8 : 7,
  background: C.mode === 'dark' ? 'rgba(239,68,68,0.16)' : 'rgba(220,38,38,0.06)',
  border: `1px solid ${C.mode === 'dark' ? 'rgba(248,113,113,0.48)' : 'rgba(220,38,38,0.24)'}`,
  color: C.mode === 'dark' ? '#FCA5A5' : C.danger,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  ...extra,
});
const dangerActionButtonSt = (C, extra = {}) => ({
  border: `1px solid ${C.mode === 'dark' ? 'rgba(248,113,113,0.48)' : 'rgba(220,38,38,0.24)'}`,
  background: C.mode === 'dark' ? 'rgba(239,68,68,0.16)' : 'rgba(220,38,38,0.06)',
  color: C.mode === 'dark' ? '#FCA5A5' : C.danger,
  ...extra,
});
const imageOverlayIconButtonSt = (C, tone = 'default') => ({
  width: 30,
  height: 30,
  borderRadius: 9,
  background: C.mode === 'dark' ? 'rgba(20,20,20,0.92)' : 'rgba(255,255,255,0.92)',
  border: `1px solid ${C.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.68)'}`,
  color: tone === 'danger' ? (C.mode === 'dark' ? '#FCA5A5' : C.danger) : (C.mode === 'dark' ? '#FFFFFF' : C.text),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
const selectSt = (C) => {
  const arrow = C.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(10,29,68,0.45)';
  return {
    ...inputSt(C),
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    cursor: 'pointer',
    backgroundImage: `linear-gradient(45deg, transparent 50%, ${arrow} 50%), linear-gradient(135deg, ${arrow} 50%, transparent 50%)`,
    backgroundPosition: 'calc(100% - 18px) 50%, calc(100% - 13px) 50%',
    backgroundSize: '5px 5px, 5px 5px',
    backgroundRepeat: 'no-repeat',
    paddingRight: 36,
  };
};
const tagSt = (C, active = false) => ({ padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: active ? C.accentBgLight : C.bgAlt, border: `1px solid ${active ? C.accentBorder : C.border}`, color: active ? readableAccentText(C) : (C.mode === 'dark' ? C.textSecondary : C.textMuted), display: 'inline-flex', alignItems: 'center' });
const joinWorkoutGroupButtonSt = (C) => ({ ...tagSt(C), background: C.accentBgSubtle, borderColor: C.accentBorder, color: C.accent, boxShadow: C.mode === 'dark' ? 'none' : '0 1px 0 rgba(12,105,200,0.08)' });
const getScheduledInterestProfile = (row = {}) => Array.isArray(row.profiles) ? row.profiles[0] : row.profiles;
const getScheduledInterestName = (row = {}) => {
  const profile = getScheduledInterestProfile(row) || {};
  return [profile.name, profile.surname].filter(Boolean).join(' ').trim() || profile.username || 'Player';
};
const getScheduledInterestAvatar = (row = {}) => getScheduledInterestProfile(row)?.profile_img || '';
const scheduledDateKey = (value) => String(value || '').slice(0, 10);
const isRecurringScheduledSession = (session = {}) =>
  Boolean(session?.series_id && session?.scheduled_session_series?.status !== 'cancelled');
const scheduledSessionFrequencyLabel = (session = {}) =>
  isRecurringScheduledSession(session) ? 'Weekly recurring' : 'One-off';
const ScheduledSessionAppPreview = ({ C, session, instituteName, audience, imageUrl, onClose, formatDate, formatTime }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [selectionOrder, setSelectionOrder] = useState('fair');
  const [expandedSelectionId, setExpandedSelectionId] = useState(null);
  const [registered, setRegistered] = useState(false);
  useEffect(() => {
    setInfoOpen(false);
    setSelectionOpen(false);
    setSelectionOrder('fair');
    setExpandedSelectionId(null);
    setRegistered(false);
  }, [session?.id]);
  if (!session) return null;
  const title = session.location_name || `${session.sport_id || 'Scheduled'} session`;
  const dateLabel = session.date ? new Date(session.date).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Date pending';
  const startLabel = formatTime(session.start_time);
  const location = session.location_name || 'Location to be confirmed';
  const spotsLeft = Number(session.spots_left);
  const playerLimit = Number(session.player_limit);
  const isFairSession = session.session_join_mode === 'interest' || !!session.courts_count || String(session.session_info || '').toLowerCase().includes('interest');
  const joinedStatus = registered ? 'Interested' : isFairSession ? 'Interest Open' : 'Open';
  const interestedCount = Number(session.interest_count || session.interestCount || 6) + (registered ? 1 : 0);
  const spots = isFairSession
    ? `${interestedCount} interested · ${Number.isFinite(playerLimit) && playerLimit > 0 ? playerLimit : 4} spots`
    : Number.isFinite(spotsLeft) && Number.isFinite(playerLimit) && playerLimit > 0
      ? spotsLeft === 1 ? '1 spot left' : `${spotsLeft} spots left`
      : 'Spots updating';
  const entryLabel = session.is_free === false ? 'Entry: Paid venue' : 'Entry: Free';
  const appBlue = '#0D275B';
  const selectionRows = [
    { id: 'you', name: 'You', status: registered ? 'Interested' : 'Interested', plays: 0, rank: 1, joined: '#1', last: 'Never', isCurrentUser: true },
    { id: 'alisha', name: 'Alisha Morgan', status: 'Interested', plays: 0, rank: 2, joined: '#2', last: 'Never' },
    { id: 'daniel', name: 'Daniel Byrne', status: 'Interested', plays: 1, rank: 3, joined: '#3', last: '21 Jul 2026' },
    { id: 'maya', name: 'Maya Walsh', status: 'Interested', plays: 2, rank: 4, joined: '#4', last: '14 Jul 2026' },
    { id: 'conor', name: 'Conor Kelly', status: 'Interested', plays: 3, rank: 5, joined: '#5', last: '07 Jul 2026' },
  ].filter(row => registered || !row.isCurrentUser);
  const orderedSelectionRows = selectionOrder === 'joined'
    ? selectionRows
    : [...selectionRows].sort((a, b) => a.rank - b.rank);
  const closeSelection = () => {
    setSelectionOpen(false);
    setSelectionOrder('fair');
    setExpandedSelectionId(null);
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`iPhone app session preview for ${title}`} style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(10,29,68,0.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <button type="button" onClick={onClose} aria-label="Close iPhone preview" style={{ position: 'fixed', top: 16, right: 16, zIndex: 3, width: 40, height: 40, borderRadius: 20, border: '1px solid rgba(255,255,255,0.36)', background: 'rgba(10,29,68,0.82)', color: '#fff', cursor: 'pointer' }}>
        <FontAwesomeIcon icon={faTimes} />
      </button>
      <div style={{ width: 'min(390px, 96vw)', borderRadius: 44, padding: 10, background: '#0B1020', boxShadow: '0 28px 90px rgba(0,0,0,0.38)' }}>
        <div style={{ position: 'relative', height: 'min(812px, 92vh)', minHeight: 680, borderRadius: 34, overflow: 'hidden', background: appBlue, color: '#fff', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)' }}>
          <div style={{ position: 'absolute', top: 9, left: '50%', transform: 'translateX(-50%)', width: 118, height: 28, borderRadius: 18, background: '#0B1020', zIndex: 2 }} />
          <div style={{ padding: '14px 16px 10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 900, marginBottom: 26 }}>
              <span>9:41</span>
              <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>5G <span style={{ width: 21, height: 10, borderRadius: 3, border: '1px solid rgba(255,255,255,0.9)', display: 'inline-block' }} /></span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 21, fontWeight: 800, letterSpacing: 0.3 }}>Schedule Sessions</h3>
              <div style={{ display: 'flex', gap: 7 }}>
                <button type="button" aria-label="Mute notifications" style={{ width: 34, height: 34, border: 'none', borderRadius: 17, background: 'rgba(255,255,255,0.14)', color: '#fff', cursor: 'pointer' }}><FontAwesomeIcon icon={faBolt} /></button>
                <button type="button" aria-label="Create session" style={{ width: 34, height: 34, border: 'none', borderRadius: 17, background: '#fff', color: '#0C69C8', cursor: 'pointer' }}><FontAwesomeIcon icon={faPlus} /></button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 3, borderRadius: 12, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.14)' }}>
              <span style={{ minHeight: 30, borderRadius: 9, color: 'rgba(255,255,255,0.82)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 900 }}>Venues</span>
              <span style={{ minHeight: 30, borderRadius: 9, background: '#fff', color: '#0A1D44', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 900 }}>Join</span>
            </div>
          </div>

          <div style={{ overflowY: 'auto', padding: '4px 16px 18px' }}>
            <div style={{ background: '#FFFFFF', border: '1px solid #E1ECF8', borderRadius: 18, overflow: 'hidden', paddingBottom: 14, boxShadow: '0 3px 10px rgba(10,29,68,0.13)' }}>
              <button type="button" onClick={() => setInfoOpen(true)} aria-label={`Open details for ${title}`} style={{ position: 'relative', width: '100%', height: 150, border: 'none', padding: 0, background: '#E6F0FF', cursor: 'pointer', display: 'block' }}>
                <img src={imageUrl || noImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <span style={{ position: 'absolute', right: 10, top: 10, display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, border: '1px solid rgba(255,255,255,0.32)', background: 'rgba(10,29,68,0.72)', color: '#fff', padding: '6px 9px', fontSize: 10, fontWeight: 900 }}>
                  <FontAwesomeIcon icon={faClipboardList} /> Details
                </span>
              </button>
              <div style={{ padding: '16px 16px 0' }}>
                <h4 style={{ margin: '0 0 6px', color: '#0A1D44', fontSize: 17, lineHeight: 1.25, fontWeight: 900 }}>{title}</h4>
                {audience && (
                  <div style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 5, maxWidth: '100%', border: '1px solid #B9D8FF', borderRadius: 12, background: '#EAF3FF', color: '#0A4B91', padding: '5px 9px', marginBottom: 8 }}>
                    <FontAwesomeIcon icon={faBuilding} style={{ fontSize: 12, marginTop: 1, flexShrink: 0 }} />
                    <span style={{ minWidth: 0, fontSize: 11, fontWeight: 900, lineHeight: 1.3, overflowWrap: 'anywhere' }}>Institution: {audience}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#40546E', fontSize: 12, fontWeight: 800, lineHeight: 1.35, marginBottom: 8 }}>
                  <FontAwesomeIcon icon={isRecurringScheduledSession(session) ? faRepeat : faCalendar} style={{ color: '#0C69C8', flexShrink: 0 }} />
                  <span>{scheduledSessionFrequencyLabel(session)}{isFairSession ? ` · Fair rotation · ${Number.isFinite(playerLimit) && playerLimit > 0 ? playerLimit : 4} spots` : ''}</span>
                </div>
                <div style={{ color: '#40546E', fontSize: 13, fontWeight: 700, lineHeight: 1.35, marginBottom: 8 }}>{dateLabel}</div>
                <div style={{ display: 'inline-flex', border: '1px solid #C8DEFF', borderRadius: 999, background: '#F4F8FF', padding: '5px 10px', marginBottom: 10 }}>
                  <span style={{ color: '#0C69C8', fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>{joinedStatus}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 3 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#40546E', fontSize: 13, fontWeight: 700 }}><FontAwesomeIcon icon={faUsers} style={{ color: '#667085' }} />{spots}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#40546E', fontSize: 13, fontWeight: 700 }}><FontAwesomeIcon icon={faClock} style={{ color: '#667085' }} />{session.duration || 'Pending'}</span>
                </div>
                {startLabel && <div style={{ color: '#40546E', fontSize: 13, fontWeight: 700, lineHeight: 1.35, marginTop: 4 }}>Start: {startLabel}</div>}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  <span style={{ border: '1px solid #BBF7D0', background: '#F6FEF9', color: '#047857', borderRadius: 10, padding: '6px 10px', fontSize: 12, fontWeight: 900 }}>{entryLabel}</span>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 10, padding: '14px 14px 0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button type="button" onClick={() => setRegistered(current => !current)} style={{ minHeight: 44, border: registered ? '1px solid #FECDD3' : 'none', borderRadius: 14, background: registered ? '#FFF1F2' : '#0C69C8', color: registered ? '#E03131' : '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}>
                    {registered ? 'Cancel Interest' : isFairSession ? 'Register Interest' : 'Join'}
                  </button>
                  <button type="button" onClick={() => isFairSession ? setSelectionOpen(true) : setInfoOpen(true)} style={{ minHeight: 44, border: '1px solid #C8DEFF', borderRadius: 14, background: '#F4F8FF', color: '#0C69C8', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}>
                    {isFairSession ? 'Selection' : 'Players'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setInfoOpen(true)} style={{ minWidth: 46, height: 50, border: '1px solid #C8DEFF', borderRadius: 13, background: '#F4F8FF', color: '#0C69C8', cursor: 'pointer', fontSize: 8, fontWeight: 900, display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faClipboardList} style={{ fontSize: 18 }} />Info</button>
                  <button type="button" style={{ minWidth: 46, height: 50, border: '1px solid #C8DEFF', borderRadius: 13, background: '#F4F8FF', color: '#0C69C8', cursor: 'pointer', fontSize: 8, fontWeight: 900, display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faCopy} style={{ fontSize: 18 }} />Share</button>
                  {isRecurringScheduledSession(session) && <button type="button" style={{ minWidth: 64, height: 50, border: '1px solid #C8DEFF', borderRadius: 13, background: '#F4F8FF', color: '#0C69C8', cursor: 'pointer', fontSize: 8, fontWeight: 900, display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faRepeat} style={{ fontSize: 18 }} />Dates</button>}
                </div>
              </div>
            </div>

            {registered && (
              <div style={{ marginTop: 12, border: '1px solid #BBF7D0', borderRadius: 16, background: '#ECFDF3', color: '#047857', padding: 12, fontSize: 12, fontWeight: 900, lineHeight: 1.4 }}>
                Interest registered. Selection will prioritize people who have not played recently.
              </div>
            )}
          </div>

          {selectionOpen && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 4, background: 'rgba(10,29,68,0.50)', display: 'grid', placeItems: 'center', padding: 14 }}>
              <div style={{ width: '100%', height: 'min(700px, 86%)', background: '#fff', borderRadius: 20, border: '1px solid #E0E7EF', padding: 16, color: '#0A1D44', display: 'grid', gridTemplateRows: 'auto auto auto minmax(0, 1fr) auto' }}>
                <div style={{ width: 40, height: 4, borderRadius: 3, background: '#ddd', justifySelf: 'center', marginBottom: 12 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 10, background: '#F4F8FF', border: '1px solid #C8DEFF', color: '#0C69C8', display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faRotateRight} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', color: '#0A1D44', fontSize: 16, lineHeight: 1.25, fontWeight: 900 }}>Fair Selection</span>
                    <span style={{ display: 'block', color: '#40546E', fontSize: 11, fontWeight: 700, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
                  {[
                    ['0', 'Picked'],
                    ['0', 'Bench'],
                    [selectionRows.length, 'Pool'],
                  ].map(([value, label]) => (
                    <div key={label} style={{ border: '1px solid #E0E7EF', borderRadius: 14, background: '#F7F9FC', padding: 10, textAlign: 'center' }}>
                      <div style={{ color: '#0C69C8', fontSize: 18, lineHeight: 1, fontWeight: 900 }}>{value}</div>
                      <div style={{ color: '#64748B', fontSize: 9, fontWeight: 900, textTransform: 'uppercase', marginTop: 4 }}>{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, padding: 4, borderRadius: 12, border: '1px solid #D8E7FA', background: '#F4F8FF', marginBottom: 10 }}>
                  {[
                    ['fair', 'Fair', faRotateRight],
                    ['joined', 'Joined', faClock],
                  ].map(([mode, label, icon]) => {
                    const active = selectionOrder === mode;
                    return (
                      <button key={mode} type="button" onClick={() => { setSelectionOrder(mode); setExpandedSelectionId(null); }} aria-pressed={active} style={{ minHeight: 36, border: 'none', borderRadius: 9, background: active ? '#0C69C8' : 'transparent', color: active ? '#fff' : '#0C69C8', cursor: 'pointer', fontSize: 12, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <FontAwesomeIcon icon={icon} /> {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ overflowY: 'auto', display: 'grid', gap: 8, alignContent: 'start', paddingRight: 2 }}>
                  {orderedSelectionRows.length ? orderedSelectionRows.map(row => {
                    const expanded = expandedSelectionId === row.id;
                    const score = selectionOrder === 'joined' ? row.joined : row.plays;
                    const scoreLabel = selectionOrder === 'joined' ? 'joined' : 'plays';
                    const best = row.plays <= 0;
                    return (
                      <button key={row.id} type="button" onClick={() => setExpandedSelectionId(current => current === row.id ? null : row.id)} aria-expanded={expanded} style={{ textAlign: 'left', border: `1px solid ${row.isCurrentUser ? '#BBF7D0' : '#E0E7EF'}`, borderRadius: 14, background: row.isCurrentUser ? '#ECFDF3' : '#FFFFFF', padding: 10, cursor: 'pointer', color: '#0A1D44' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 10, alignItems: 'center' }}>
                          <span style={{ minHeight: 46, borderRadius: 12, border: `1px solid ${best ? '#BBF7D0' : '#D8E7FA'}`, background: best ? '#F6FEF9' : '#F4F8FF', display: 'grid', placeItems: 'center' }}>
                            <strong style={{ color: '#0A1D44', fontSize: 15, lineHeight: 1 }}>{score}</strong>
                            <small style={{ color: '#64748B', fontSize: 8, fontWeight: 900, textTransform: 'uppercase' }}>{scoreLabel}</small>
                          </span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'grid', gridTemplateColumns: '30px 1fr', gap: 8, alignItems: 'center' }}>
                              <span style={{ width: 30, height: 30, borderRadius: 15, background: row.isCurrentUser ? '#0C69C8' : '#EAF3FF', color: row.isCurrentUser ? '#fff' : '#0C69C8', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 900 }}>{row.name.slice(0, 1)}</span>
                              <strong style={{ minWidth: 0, color: '#0A1D44', fontSize: 13, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{row.name}</strong>
                            </span>
                            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                              <span style={{ border: '1px solid #B9D8FF', borderRadius: 999, background: '#EAF3FF', color: '#0C69C8', padding: '4px 8px', fontSize: 10, fontWeight: 900 }}>{row.status}</span>
                              <span style={{ border: '1px solid #E0E7EF', borderRadius: 999, background: '#F8FAFC', color: '#40546E', padding: '4px 8px', fontSize: 10, fontWeight: 900 }}>{selectionOrder === 'joined' ? `${row.plays} plays` : `#${row.rank}`}</span>
                              <span style={{ border: '1px solid #E0E7EF', borderRadius: 999, background: '#F8FAFC', color: '#40546E', padding: '4px 8px', fontSize: 10, fontWeight: 900 }}>{selectionOrder === 'joined' ? row.joined : row.last}</span>
                            </span>
                          </span>
                          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ color: '#64748B' }} />
                        </div>
                        {expanded && (
                          <div style={{ marginTop: 9, border: '1px solid #BBF7D0', borderRadius: 12, background: '#F6FEF9', color: '#047857', padding: 9, fontSize: 12, fontWeight: 900 }}>
                            {row.plays <= 0 ? 'No attended sessions yet' : `Last matching session: ${row.last}.`}
                          </div>
                        )}
                      </button>
                    );
                  }) : (
                    <div style={{ border: '1px dashed #D8E7FA', borderRadius: 14, background: '#F8FAFC', padding: 18, textAlign: 'center', color: '#64748B', fontSize: 12, fontWeight: 800 }}>No interest yet</div>
                  )}
                </div>
                <button type="button" onClick={closeSelection} style={{ width: '100%', marginTop: 12, minHeight: 44, border: '1px solid #C8DEFF', borderRadius: 14, background: '#F4F8FF', color: '#0C69C8', cursor: 'pointer', fontSize: 13, fontWeight: 900 }}>Close</button>
              </div>
            </div>
          )}

          {infoOpen && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 4, background: 'rgba(10,29,68,0.55)', display: 'grid', placeItems: 'center', padding: 14 }}>
              <div style={{ width: '100%', background: '#fff', borderRadius: 20, border: '1px solid #E0E7EF', padding: 16, color: '#0A1D44' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 16, lineHeight: 1.25, fontWeight: 900, textAlign: 'center' }}>{title}</h4>
                {[
                  ['Institution', audience, faBuilding],
                  ['Frequency', scheduledSessionFrequencyLabel(session), isRecurringScheduledSession(session) ? faRepeat : faCalendar],
                  ['Fair Selection', isFairSession ? `${interestedCount} interested. Admins pick up to ${Number.isFinite(playerLimit) && playerLimit > 0 ? playerLimit : 4} players by prioritizing people who have not played recently.` : '', faRotateRight],
                ].filter(([, value]) => value).map(([label, value, icon]) => (
                  <div key={label} style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 10, alignItems: 'center', border: '1px solid #C8DEFF', borderRadius: 14, background: '#F4F8FF', padding: '10px 12px', marginBottom: 12 }}>
                    <span style={{ width: 32, height: 32, borderRadius: 10, background: '#fff', border: '1px solid #E0E7EF', color: '#0C69C8', display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={icon} /></span>
                    <span>
                      <span style={{ display: 'block', color: '#64748B', fontSize: 9, fontWeight: 900, textTransform: 'uppercase' }}>{label}</span>
                      <span style={{ display: 'block', color: '#0A1D44', fontSize: 12, fontWeight: 900, lineHeight: 1.35, marginTop: 1, overflowWrap: 'anywhere' }}>{value}</span>
                    </span>
                  </div>
                ))}
                <img src={imageUrl || noImage} alt="" style={{ width: '100%', height: 160, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 10, alignItems: 'center', border: '1px solid #E0E7EF', borderRadius: 14, background: '#F0F4FA', padding: '10px 12px', marginBottom: 12 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 10, background: '#fff', border: '1px solid #E0E7EF', color: '#0C69C8', display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faLocationDot} /></span>
                  <span>
                    <span style={{ display: 'block', color: '#0A1D44', opacity: 0.75, fontSize: 11, fontWeight: 800 }}>Address</span>
                    <span style={{ display: 'block', color: '#0C69C8', fontSize: 12, fontWeight: 700, lineHeight: 1.35 }}>{location}</span>
                  </span>
                </div>
                <div style={{ color: '#0A1D44', fontSize: 11, fontWeight: 900, marginBottom: 6 }}>Session Info</div>
                <div style={{ maxHeight: 150, overflowY: 'auto', color: '#0A1D44', fontSize: 12, lineHeight: 1.5 }}>
                  {session.session_info || 'No extra info provided.'}
                </div>
                <button type="button" onClick={() => setInfoOpen(false)} style={{ width: '100%', marginTop: 14, minHeight: 44, border: 'none', borderRadius: 15, background: 'linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 900 }}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const leaderboardMockRankColor = (rank) => LEADERBOARD_MEDAL_COLORS[rank] || '#0C69C8';
const leaderboardMockTextOnColor = (color) => isLightHexColor(color) ? '#0A1D44' : '#FFFFFF';
const leaderboardMockName = (row = {}) => String(row.name || row.teamName || row.username || 'Member').trim();
const leaderboardMockSteps = (row = {}, locked = false) => institutionStepText(locked || row.accessLocked, row.totalSteps ?? row.total ?? row.steps);
const LeaderboardAppPreview = ({ C, mode = 'members', activeInst, members = [], departments = [], overallRows = [], branding = {}, locked = false, deptLabel = 'Department', memberLabel = 'Member', onClose }) => {
  const [drillDepartment, setDrillDepartment] = useState(null);
  useEffect(() => setDrillDepartment(null), [mode, activeInst?.id]);
  const brand = { ...DEFAULT_APP_LEADERBOARD_SETTINGS, ...(branding || {}) };
  const primary = normalizeHexInput(brand.primaryColor, DEFAULT_APP_LEADERBOARD_SETTINGS.primaryColor);
  const secondary = normalizeHexInput(brand.secondaryColor, '#0D275B');
  const background = normalizeHexInput(brand.backgroundColor, DEFAULT_APP_LEADERBOARD_SETTINGS.backgroundColor);
  const surfaceOnBrand = isLightHexColor(background) ? '#FFFFFF' : 'rgba(255,255,255,0.10)';
  const textOnBrand = isLightHexColor(background) ? '#0A1D44' : '#FFFFFF';
  const mutedOnBrand = isLightHexColor(background) ? '#64748B' : 'rgba(255,255,255,0.72)';
  const sortedMembers = [...(members || [])].sort((a, b) => Number(b.steps || b.totalSteps || 0) - Number(a.steps || a.totalSteps || 0));
  const sortedDepartments = [...(departments || [])].sort((a, b) => Number(b.total || b.totalSteps || 0) - Number(a.total || a.totalSteps || 0));
  const fallbackOverallRows = [
    { id: 'overall-ey', name: activeInst?.workspace_name || activeInst?.name || 'EY Members', state_province: 'Ireland', country: 'Company league', totalSteps: Math.max(Number(activeInst?.totalSteps || 0), 7562106), image: activeInst?.workspace_logo_url || activeInst?.brand_logo_url || '' },
    { id: 'overall-apple', name: 'Apple', state_province: 'United States', country: 'Company league', totalSteps: 6892140 },
    { id: 'overall-google', name: 'Google', state_province: 'United States', country: 'Company league', totalSteps: 6312040 },
    { id: 'overall-amazon', name: 'Amazon', state_province: 'United States', country: 'Company league', totalSteps: 5986415 },
  ];
  const normalizedOverallRows = (overallRows?.length ? overallRows : fallbackOverallRows)
    .map((row, index) => ({ ...row, totalSteps: Number(row.totalSteps || row.total || row.steps || 0), rank: row.rank || index + 1 }));
  const departmentMembers = drillDepartment
    ? [...(drillDepartment.members || [])].sort((a, b) => Number(b.steps || 0) - Number(a.steps || 0))
    : [];
  const viewRows = drillDepartment
    ? departmentMembers.map(row => ({ id: row.id, name: row.name, sub: row.association_type || memberLabel, totalSteps: row.steps, image: row.profile_img, kind: 'member' }))
    : mode === 'departments'
      ? sortedDepartments.map(row => ({ id: row.id, name: row.name, sub: `${(row.members || []).length} ${memberLabel.toLowerCase()}s`, totalSteps: row.total, image: row.image_url, members: row.members || [], kind: 'department' }))
      : mode === 'overall'
        ? normalizedOverallRows.map(row => ({ id: row.id, name: row.name, sub: [row.state_province || row.region, row.country].filter(Boolean).join(', ') || 'Overall league', totalSteps: row.totalSteps, image: row.image || row.image_url || row.brand_logo_url, kind: 'overall' }))
        : sortedMembers.map(row => ({ id: row.id, name: row.name, sub: row.association_type || memberLabel, totalSteps: row.steps, image: row.profile_img, kind: 'member' }));
  const rows = viewRows.slice(0, 8);
  const topRows = rows.slice(0, 3);
  const podiumRows = [topRows[1], topRows[0], topRows[2]];
  const title = drillDepartment ? drillDepartment.name : mode === 'departments' ? `${deptLabel}s` : mode === 'overall' ? 'Overall League' : `${memberLabel}s`;
  const subtitle = drillDepartment ? `${memberLabel} places` : mode === 'departments' ? 'Tap a department' : mode === 'overall' ? 'Company league' : activeInst?.name || 'Institute';
  const maxSteps = Math.max(1, ...rows.map(row => Number(row.totalSteps || 0)));

  const renderAvatar = (row, rank, size = 34) => {
    const color = leaderboardMockRankColor(rank);
    const initials = leaderboardMockName(row).slice(0, 1).toUpperCase();
    if (row?.image) {
      return <img src={row.image} alt="" style={{ width: size, height: size, borderRadius: row.kind === 'overall' || row.kind === 'department' ? 10 : 999, objectFit: row.kind === 'overall' ? 'contain' : 'cover', background: '#FFFFFF', border: `2px solid ${rgba(color, 0.35)}`, padding: row.kind === 'overall' ? 3 : 0 }} />;
    }
    return <span style={{ width: size, height: size, borderRadius: row?.kind === 'department' ? 10 : 999, background: color, color: leaderboardMockTextOnColor(color), display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 950, flexShrink: 0 }}>{initials}</span>;
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`${title} app leaderboard preview`} style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(10,29,68,0.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ width: 390, maxWidth: '96vw', height: 760, maxHeight: '94vh', borderRadius: 40, background: '#07172F', padding: 10, boxShadow: '0 28px 80px rgba(0,0,0,0.40)', position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label="Close leaderboard app preview" style={{ position: 'absolute', top: -10, right: -10, width: 38, height: 38, borderRadius: 19, border: '1px solid rgba(255,255,255,0.36)', background: '#fff', color: '#0A1D44', cursor: 'pointer', display: 'grid', placeItems: 'center', zIndex: 2 }}><FontAwesomeIcon icon={faTimes} /></button>
        <div style={{ height: '100%', borderRadius: 32, overflow: 'hidden', background: isLightHexColor(background) ? background : `linear-gradient(180deg, ${background} 0%, ${secondary} 100%)`, color: textOnBrand, display: 'grid', gridTemplateRows: 'auto auto minmax(0, 1fr)' }}>
          <div style={{ padding: '18px 16px 10px', display: 'grid', gridTemplateColumns: '36px 1fr 36px', alignItems: 'center', gap: 8 }}>
            <button type="button" onClick={() => drillDepartment ? setDrillDepartment(null) : onClose()} aria-label={drillDepartment ? `Back to ${deptLabel}s` : 'Close app preview'} style={{ width: 36, height: 36, borderRadius: 18, border: '1px solid rgba(255,255,255,0.18)', background: isLightHexColor(background) ? '#EEF4FF' : 'rgba(255,255,255,0.14)', color: isLightHexColor(background) ? primary : '#fff', cursor: 'pointer' }}>{drillDepartment ? '‹' : '×'}</button>
            <div style={{ minWidth: 0, textAlign: 'center' }}>
              <div style={{ color: textOnBrand, fontSize: 18, fontWeight: 950, lineHeight: 1.15 }}>{title}</div>
              <div style={{ color: mutedOnBrand, fontSize: 10.5, fontWeight: 800, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
            </div>
            <span style={{ width: 36, height: 36, borderRadius: 18, border: '1px solid rgba(255,255,255,0.18)', background: isLightHexColor(background) ? '#FFF7E0' : 'rgba(255,255,255,0.14)', color: LEADERBOARD_MEDAL_COLORS[1], display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faTrophy} /></span>
          </div>
          <div style={{ padding: '0 16px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 }}>
              {['Regional', 'National', 'Global'].map((label, index) => (
                <span key={label} style={{ minHeight: 34, borderRadius: 11, border: `1px solid ${index === 0 ? primary : 'rgba(255,255,255,0.16)'}`, background: index === 0 ? rgba(primary, isLightHexColor(background) ? 0.10 : 0.28) : 'rgba(255,255,255,0.08)', color: index === 0 ? (isLightHexColor(background) ? primary : '#fff') : mutedOnBrand, display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 950 }}>{label}</span>
              ))}
            </div>
            <div style={{ border: '1px solid rgba(255,255,255,0.16)', borderRadius: 20, background: surfaceOnBrand, padding: '14px 8px 12px', display: 'grid', gridTemplateColumns: '1fr 1.08fr 1fr', alignItems: 'end', gap: 4 }}>
              {podiumRows.map((row, index) => {
                const rank = [2, 1, 3][index];
                const color = leaderboardMockRankColor(rank);
                return row ? (
                  <div key={`${row.id || rank}-podium`} style={{ display: 'grid', justifyItems: 'center', gap: 5, transform: rank === 1 ? 'translateY(-8px)' : 'none', minWidth: 0 }}>
                    <div style={{ position: 'relative' }}>
                      {renderAvatar(row, rank, rank === 1 ? 58 : 48)}
                      <span style={{ position: 'absolute', right: -3, bottom: -3, width: 20, height: 20, borderRadius: 10, background: '#FFFFFF', border: `1px solid ${rgba(color, 0.55)}`, color, display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 950 }}>{rank}</span>
                    </div>
                    <div style={{ color: textOnBrand, maxWidth: '100%', fontSize: 10.5, fontWeight: 900, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leaderboardMockName(row)}</div>
                    <div style={{ color, fontSize: 11, fontWeight: 950 }}>{leaderboardMockSteps(row, locked)}</div>
                  </div>
                ) : <span key={`empty-${rank}`} />;
              })}
            </div>
          </div>
          <div style={{ overflowY: 'auto', padding: '0 16px 18px', display: 'grid', alignContent: 'start', gap: 8 }}>
            {rows.length ? rows.map((row, index) => {
              const rank = index + 1;
              const rankColor = leaderboardMockRankColor(rank);
              const progress = Math.round((Number(row.totalSteps || 0) / maxSteps) * 100);
              const clickable = mode === 'departments' && !drillDepartment && row.members?.length;
              return (
                <button key={`${row.id || row.name}-${rank}`} type="button" disabled={!clickable} onClick={() => clickable && setDrillDepartment(row)} style={{ width: '100%', minHeight: 64, borderRadius: 16, border: `1px solid ${rank <= 3 ? rgba(rankColor, 0.38) : 'rgba(255,255,255,0.14)'}`, background: rank <= 3 ? rgba(rankColor, isLightHexColor(background) ? 0.10 : 0.18) : surfaceOnBrand, color: textOnBrand, display: 'grid', gridTemplateColumns: '30px 38px minmax(0,1fr) auto', alignItems: 'center', gap: 9, padding: 10, cursor: clickable ? 'pointer' : 'default', textAlign: 'left' }}>
                  <span style={{ width: 28, height: 28, borderRadius: 14, background: rank <= 3 ? rgba(rankColor, 0.18) : rgba(primary, 0.16), border: `1px solid ${rank <= 3 ? rgba(rankColor, 0.46) : rgba(primary, 0.28)}`, color: rank <= 3 ? rankColor : (isLightHexColor(background) ? primary : '#FFFFFF'), display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 950 }}>{rank}</span>
                  {renderAvatar(row, rank, 38)}
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', color: textOnBrand, fontSize: 12.5, lineHeight: 1.2, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leaderboardMockName(row)}</span>
                    <span style={{ display: 'block', color: mutedOnBrand, fontSize: 9.5, fontWeight: 800, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.sub || ''}</span>
                    <span style={{ display: 'block', marginTop: 6, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.14)', overflow: 'hidden' }}><span style={{ display: 'block', width: `${progress}%`, height: '100%', borderRadius: 999, background: rank <= 3 ? rankColor : primary }} /></span>
                  </span>
                  <span style={{ display: 'grid', justifyItems: 'end', gap: 2 }}>
                    <strong style={{ color: rank <= 3 ? rankColor : (isLightHexColor(background) ? primary : '#FFFFFF'), fontSize: 12, fontWeight: 950, whiteSpace: 'nowrap' }}>{leaderboardMockSteps(row, locked)}</strong>
                    <small style={{ color: mutedOnBrand, fontSize: 8, fontWeight: 900 }}>{locked ? 'hidden' : 'steps'}</small>
                  </span>
                </button>
              );
            }) : (
              <div style={{ border: '1px dashed rgba(255,255,255,0.22)', borderRadius: 16, padding: 18, textAlign: 'center', color: mutedOnBrand, fontSize: 12, fontWeight: 850 }}>No leaderboard rows yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const formatScheduledHistoryDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
};
const scheduledInterestJoinedStamp = (row = {}) => String(row.created_at || row.updated_at || '');
const formatScheduledInterestJoined = (row = {}) => {
  const stamp = scheduledInterestJoinedStamp(row);
  if (!stamp) return 'Joined';
  const date = new Date(stamp);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Joined';
};
const sortScheduledInterestRows = (rows = [], mode = 'fair') => {
  const statusOrder = { selected: 0, waitlisted: 1, interested: 2, cancelled: 3 };
  return [...rows].sort((a, b) => {
    const statusDiff = (statusOrder[a?.status] ?? 9) - (statusOrder[b?.status] ?? 9);
    if (statusDiff) return statusDiff;
    if (mode === 'joined') {
      return scheduledInterestJoinedStamp(a).localeCompare(scheduledInterestJoinedStamp(b))
        || getScheduledInterestName(a).localeCompare(getScheduledInterestName(b));
    }
    return Number(a?.selection_rank ?? 9999) - Number(b?.selection_rank ?? 9999)
      || Number(a?.preview_rank ?? 9999) - Number(b?.preview_rank ?? 9999)
      || getScheduledInterestName(a).localeCompare(getScheduledInterestName(b));
  });
};
const getScheduledJoinedOrderMap = (rows = []) => new Map(
  [...rows]
    .sort((a, b) => scheduledInterestJoinedStamp(a).localeCompare(scheduledInterestJoinedStamp(b)) || getScheduledInterestName(a).localeCompare(getScheduledInterestName(b)))
    .map((row, index) => [String(row.profile_id), index + 1])
);
const SelectShell = ({ C, children, style }) => (
  <div style={{ position: 'relative', width: style?.width || '100%' }}>
    {React.cloneElement(children, { style: { ...selectSt(C), ...style, width: '100%', backgroundImage: 'none' } })}
    <FontAwesomeIcon
      icon={faChevronDown}
      aria-hidden="true"
      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: C.textMuted, pointerEvents: 'none' }}
    />
  </div>
);

const InstitutionContentPanel = ({ C, activeInst, institutes = [], isMobile, profileData, showAppDemoPreview = false }) => {
  // Keep the preview implementation available for demo work, but remove its floating
  // simulator shortcut from the institute dashboard until it is ready to expose again.
  const showAppSimulatorShortcut = false;
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [contentTeamNames, setContentTeamNames] = useState({});
  const [error, setError] = useState('');
  const [communityFormOpen, setCommunityFormOpen] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [communityName, setCommunityName] = useState('');
  const [communityPrivate, setCommunityPrivate] = useState(false);
  const [communitySportId, setCommunitySportId] = useState('');
  const [communityTeamId, setCommunityTeamId] = useState('');
  const [communityImageFile, setCommunityImageFile] = useState(null);
  const [communityImagePreview, setCommunityImagePreview] = useState('');
  const [savingCommunity, setSavingCommunity] = useState(false);
  const [deletingCommunityId, setDeletingCommunityId] = useState(null);
  const [communityFeedback, setCommunityFeedback] = useState(null);
  const [activityAudienceEditor, setActivityAudienceEditor] = useState(null);
  const [activityAudienceDepartments, setActivityAudienceDepartments] = useState([]);
  const [activityAudienceLoading, setActivityAudienceLoading] = useState(false);
  const [activityAudienceSaving, setActivityAudienceSaving] = useState(false);
  const [activityAudienceFeedback, setActivityAudienceFeedback] = useState(null);
  const [expandedContentGroups, setExpandedContentGroups] = useState({});
  const [sessionManager, setSessionManager] = useState(null);
  const [sessionManagerLoading, setSessionManagerLoading] = useState(false);
  const [sessionManagerSaving, setSessionManagerSaving] = useState(false);
  const [sessionManagerError, setSessionManagerError] = useState('');
  const [sessionOrderMode, setSessionOrderMode] = useState('fair');
  const [sessionManualMode, setSessionManualMode] = useState(false);
  const [sessionManualIds, setSessionManualIds] = useState([]);
  const [sessionExpandedProfileId, setSessionExpandedProfileId] = useState(null);
  const [sessionEditOpen, setSessionEditOpen] = useState(false);
  const [sessionEditDraft, setSessionEditDraft] = useState({ date: '', start_time: '', player_limit: '', session_info: '' });
  const [appPreviewSession, setAppPreviewSession] = useState(null);
  const communityImageInputRef = useRef(null);
  const afriRunDemoCommunity = isAfriRunDemoInstitute(activeInst, profileData?.email);

  const hydrateScheduledSessions = useCallback(async (nextContent = {}) => {
    const sessionRows = nextContent?.scheduled_sessions || [];
    const ids = sessionRows.map(row => row?.id).filter(Boolean);
    if (!ids.length) return nextContent;
    try {
      const { data, error: sessionError } = await supabase
        .from('scheduled_sessions')
        .select('id,creator_id,location_id,custom_location,session_join_mode,selection_status,selection_run_at,courts_count,players_per_court,series_id,series_occurrence_date')
        .in('id', ids);
      if (sessionError) throw sessionError;
      const byId = Object.fromEntries((data || []).map(row => [row.id, row]));
      const enrichedRows = sessionRows.map(row => ({ ...row, ...(byId[row.id] || {}) }));
      const seriesIds = Array.from(new Set(enrichedRows.map(row => row?.series_id).filter(Boolean).map(String)));
      if (!seriesIds.length) {
        return { ...nextContent, scheduled_sessions: enrichedRows };
      }
      try {
        const { data: seriesRows, error: seriesError } = await supabase
          .from('scheduled_session_series')
          .select('id,status,start_date,end_date,weekdays,start_time,duration')
          .in('id', seriesIds);
        if (seriesError) throw seriesError;
        const bySeriesId = new Map((seriesRows || []).map(row => [String(row.id), row]));
        return {
          ...nextContent,
          scheduled_sessions: enrichedRows.map(row => ({
            ...row,
            scheduled_session_series: row?.series_id ? bySeriesId.get(String(row.series_id)) || null : null,
          })),
        };
      } catch {
        return { ...nextContent, scheduled_sessions: enrichedRows };
      }
    } catch {
      return nextContent;
    }
  }, []);

  const loadContent = useCallback(() => {
    let alive = true;
    if (!activeInst?.id) {
      setContent(null);
      setExpandedContentGroups({});
      return () => {};
    }
    setLoading(true);
    setError('');
    setExpandedContentGroups({});
    supabase.rpc('get_my_institution_content')
      .then(async ({ data, error: rpcError }) => {
        if (!alive) return;
        if (rpcError) throw rpcError;
        const hydrated = await hydrateScheduledSessions(data || {});
        if (!alive) return;
        setContent(mergeAfriRunDemoCommunity(hydrated || {}, activeInst, profileData?.email));
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || 'Could not load member activities.');
        setContent(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [activeInst, hydrateScheduledSessions, profileData?.email]);

  useEffect(() => loadContent(), [loadContent]);

  const activeContentInstituteIds = useMemo(() => {
    const ids = new Set();
    if (activeInst?.id) ids.add(activeInst.id);
    if (activeInst?.workspace_id) {
      (institutes || []).forEach(inst => {
        if (inst?.id && inst.workspace_id === activeInst.workspace_id) ids.add(inst.id);
      });
    }
    return ids;
  }, [activeInst?.id, activeInst?.workspace_id, institutes]);

  useEffect(() => {
    let alive = true;
    const rows = ['scheduled_sessions', 'events', 'tournaments', 'communities']
      .flatMap(key => content?.[key] || []);
    const ids = Array.from(new Set(rows.flatMap(row => [
      row?.institute_team_id,
      ...(Array.isArray(row?.institute_team_ids) ? row.institute_team_ids : []),
    ]).filter(Boolean)));
    if (!ids.length) {
      setContentTeamNames({});
      return undefined;
    }
    supabase
      .from('institute_teams')
      .select('id,name')
      .in('id', ids)
      .then(({ data }) => {
        if (!alive) return;
        setContentTeamNames(Object.fromEntries((data || []).map(team => [team.id, team.name])));
      })
      .catch(() => {
        if (alive) setContentTeamNames({});
      });
    return () => { alive = false; };
  }, [content]);

  useEffect(() => {
    setCommunityFormOpen(false);
    setEditingCommunity(null);
    setCommunityName('');
    setCommunityPrivate(false);
    setCommunitySportId('');
    setCommunityTeamId('');
    setCommunityImageFile(null);
    setCommunityImagePreview('');
    setCommunityFeedback(null);
    setActivityAudienceEditor(null);
    setActivityAudienceDepartments([]);
    setActivityAudienceFeedback(null);
    setSessionManager(null);
    setSessionManagerError('');
    setSessionManualMode(false);
    setSessionManualIds([]);
    setSessionExpandedProfileId(null);
    setSessionEditOpen(false);
  }, [activeInst?.id]);

  const communityImageInputId = `institution-community-image-${activeInst?.id || 'current'}`;
  const uniqueIds = (values = []) => Array.from(new Set(values.map(value => String(value || '').trim()).filter(Boolean)));
  const getRowAudienceIds = (row = {}) => uniqueIds([
    ...(Array.isArray(row.institute_team_ids) ? row.institute_team_ids : []),
    row.institute_team_id,
  ]);
  const localDateKey = (value) => {
    if (!value) return '';
    const raw = String(value);
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  const todayKey = localDateKey(new Date());
  const formatDate = (value) => {
    if (!value) return '';
    const raw = String(value);
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
    return Number.isFinite(date.getTime()) ? date.toLocaleDateString() : raw;
  };
  const formatTime = (value) => value ? String(value).slice(0, 5) : '';
  const activitySubgroupLabel = institutionSubgroupLabel(activeInst);
  const audienceLabel = (row) => {
    if (Array.isArray(row?.institute_team_names) && row.institute_team_names.length) {
      const names = row.institute_team_names.filter(Boolean);
      const label = names.length === 1 ? activitySubgroupLabel : `${activitySubgroupLabel}s`;
      return `${label}: ${names.join(', ')}`;
    }
    const teamIds = [row?.institute_team_id, ...(Array.isArray(row?.institute_team_ids) ? row.institute_team_ids : [])].filter(Boolean);
    const departmentNames = teamIds.map(id => contentTeamNames[id]).filter(Boolean);
    if (departmentNames.length) {
      const names = Array.from(new Set(departmentNames));
      const label = names.length === 1 ? activitySubgroupLabel : `${activitySubgroupLabel}s`;
      return `${label}: ${names.join(', ')}`;
    }
    if (row?.institute_team_name) return `${activitySubgroupLabel}: ${row.institute_team_name}`;
    if (teamIds.length) return teamIds.length === 1 ? `Selected ${activitySubgroupLabel.toLowerCase()}` : `${teamIds.length} selected ${activitySubgroupLabel.toLowerCase()}s`;
    return 'All members';
  };
  const activitySpotsLabel = (row = {}) => {
    if (row.spots_left == null) return '';
    const spotsLeft = Number(row.spots_left);
    const playerLimit = Number(row.player_limit);
    if (Number.isFinite(spotsLeft) && Number.isFinite(playerLimit) && playerLimit > 0) {
      return `${spotsLeft} of ${playerLimit} spots left`;
    }
    return `${row.spots_left} spots left`;
  };
  const activityMetaRows = (key, row) => {
    const audience = audienceLabel(row);
    if (key === 'communities') {
      return [
        ['Audience', audience],
        ['Sport', row?.sport_id],
        ['Visibility', row?.private ? 'Private' : 'Public'],
      ].filter(([, value]) => value);
    }
    if (key === 'events') {
      return [
        ['Audience', audience],
        ['Location', row?.location_name],
        ['Date', formatDate(row?.event_date)],
        ['Sport', row?.sport_id],
      ].filter(([, value]) => value);
    }
    if (key === 'tournaments') {
      return [
        ['Audience', audience],
        ['Location', row?.location],
        ['Schedule', [formatDate(row?.date), formatTime(row?.start_time)].filter(Boolean).join(' at ')],
        ['Sport', row?.sport],
      ].filter(([, value]) => value);
    }
    return [
      ['Audience', audience],
      ['Frequency', scheduledSessionFrequencyLabel(row)],
      ['Location', row?.location_name],
      ['Schedule', [formatDate(row?.date), formatTime(row?.start_time)].filter(Boolean).join(' at ')],
      ['Availability', activitySpotsLabel(row)],
    ].filter(([, value]) => value);
  };
  const isCurrentRow = (key, row) => {
    if (key === 'communities') return true;
    const keyDate = localDateKey(key === 'events' ? row?.event_date : row?.date);
    return !keyDate || keyDate >= todayKey;
  };
  const filterRows = (key) => (content?.[key] || [])
    .filter(row => row.institute_id && activeContentInstituteIds.has(row.institute_id))
    .filter(row => isCurrentRow(key, row));
  const groups = [
    { key: 'scheduled_sessions', title: 'Sessions', rows: filterRows('scheduled_sessions'), icon: faCalendar, empty: 'No upcoming sessions', getTitle: row => row.location_name || `${row.sport_id || 'Scheduled'} session` },
    { key: 'events', title: 'Events', rows: filterRows('events'), icon: faCalendar, empty: 'No upcoming events', getTitle: row => row.title || 'Event' },
    { key: 'tournaments', title: 'Tournaments', rows: filterRows('tournaments'), icon: faTrophy, empty: 'No upcoming tournaments', getTitle: row => row.tournament_name || 'Tournament' },
    { key: 'communities', title: 'Communities', rows: filterRows('communities'), icon: faUsers, empty: 'No communities', getTitle: row => row.name || 'Community' },
  ];
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const demoPreviewSessions = groups.find(group => group.key === 'scheduled_sessions')?.rows || [];
  const openAppPreview = (session) => {
    if (!session) return;
    setAppPreviewSession(session);
  };
  const sports = profileData?.sports || [];
  const getActivityImage = (groupKey, row) => {
    if (groupKey === 'tournaments') return row?.image || row?.image_url || noImage;
    return row?.image_url || row?.location_image_url || noImage;
  };
  const activityAudienceConfig = {
    scheduled_sessions: { table: 'scheduled_sessions', joinTable: 'scheduled_session_institute_teams', targetColumn: 'session_id', label: 'session' },
    events: { table: 'events', joinTable: 'event_institute_teams', targetColumn: 'event_id', label: 'event' },
    tournaments: { table: 'tournaments', joinTable: 'tournament_institute_teams', targetColumn: 'tournament_id', label: 'tournament' },
    communities: { table: 'communities', joinTable: 'community_institute_teams', targetColumn: 'community_id', label: 'community' },
  };
  const getAudienceSourceInstitute = (row = {}) => {
    const rowInstitute = institutes.find(inst => inst.id === row.institute_id);
    if (rowInstitute?.teams) return rowInstitute;
    if (rowInstitute?.workspace_id) {
      const grouped = institutes.find(inst =>
        inst.id !== rowInstitute.id &&
        inst.workspace_id === rowInstitute.workspace_id &&
        inst.type !== 'Challenge' &&
        inst.teams
      );
      if (grouped) return grouped;
    }
    if (activeInst?.teams) return activeInst;
    if (activeInst?.workspace_id) {
      const grouped = institutes.find(inst =>
        inst.id !== activeInst.id &&
        inst.workspace_id === activeInst.workspace_id &&
        inst.type !== 'Challenge' &&
        inst.teams
      );
      if (grouped) return grouped;
    }
    return rowInstitute || activeInst || null;
  };
  const syncActivityAudienceTeams = async (config, targetId, teamIds) => {
    const nextIds = uniqueIds(teamIds);
    const { error: deleteError } = await supabase.from(config.joinTable).delete().eq(config.targetColumn, targetId);
    if (deleteError) throw deleteError;
    if (!nextIds.length) return;
    const { error: insertError } = await supabase
      .from(config.joinTable)
      .insert(nextIds.map(institute_team_id => ({ [config.targetColumn]: targetId, institute_team_id })));
    if (insertError) throw insertError;
  };
  const startEditActivityAudience = async (groupKey, row, title) => {
    const config = activityAudienceConfig[groupKey];
    if (!config || !row?.id) return;
    const sourceInstitute = getAudienceSourceInstitute(row);
    setCommunityFeedback(null);
    setActivityAudienceFeedback(null);
    setActivityAudienceDepartments([]);
    setActivityAudienceEditor({
      groupKey,
      row,
      title,
      config,
      sourceInstitute,
      teamIds: getRowAudienceIds(row),
    });
    if (!sourceInstitute?.id || !sourceInstitute.teams) return;
    const cachedDepartments = (sourceInstitute.departments || []).filter(dept => !dept.archived_at);
    if (cachedDepartments.length) {
      setActivityAudienceDepartments(cachedDepartments);
      return;
    }
    setActivityAudienceLoading(true);
    try {
      const { data, error: deptError } = await supabase
        .from('institute_teams')
        .select('id,name,image_url,type')
        .eq('institute_id', sourceInstitute.id)
        .is('archived_at', null)
        .order('name', { ascending: true });
      if (deptError) throw deptError;
      setActivityAudienceDepartments(data || []);
    } catch (err) {
      setActivityAudienceFeedback({ tone: 'error', text: err?.message || `Could not load ${activitySubgroupLabel.toLowerCase()}s.` });
    } finally {
      setActivityAudienceLoading(false);
    }
  };
  const setActivityAudienceMode = (mode) => {
    setActivityAudienceEditor(current => current ? ({ ...current, teamIds: mode === 'members' ? [] : current.teamIds }) : current);
  };
  const toggleActivityAudienceDepartment = (deptId) => {
    setActivityAudienceEditor(current => {
      if (!current) return current;
      const next = new Set(current.teamIds || []);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return { ...current, teamIds: Array.from(next) };
    });
  };
  const saveActivityAudience = async () => {
    if (!activityAudienceEditor?.row?.id || activityAudienceSaving) return;
    const selectedIds = uniqueIds(activityAudienceEditor.teamIds || []);
    const sourceInstitute = activityAudienceEditor.sourceInstitute;
    const validIds = selectedIds.filter(id => activityAudienceDepartments.some(dept => dept.id === id));
    if (selectedIds.length !== validIds.length) {
      setActivityAudienceFeedback({ tone: 'error', text: `Choose ${activitySubgroupLabel.toLowerCase()}s from ${sourceInstitute?.name || 'this institute'}.` });
      return;
    }
    setActivityAudienceSaving(true);
    setActivityAudienceFeedback(null);
    try {
      const firstTeamId = validIds[0] || null;
      const { error: updateError } = await supabase
        .from(activityAudienceEditor.config.table)
        .update({ institute_team_id: firstTeamId })
        .eq('id', activityAudienceEditor.row.id);
      if (updateError) throw updateError;
      await syncActivityAudienceTeams(activityAudienceEditor.config, activityAudienceEditor.row.id, validIds);
      setActivityAudienceFeedback({ tone: 'success', text: 'Audience updated.' });
      setActivityAudienceEditor(null);
      setActivityAudienceDepartments([]);
      loadContent();
    } catch (err) {
      setActivityAudienceFeedback({ tone: 'error', text: err?.message || 'Could not update this audience.' });
    } finally {
      setActivityAudienceSaving(false);
    }
  };

  const loadSessionManagerRows = useCallback(async (session) => {
    if (!session?.id) return { interestRows: [], playerRows: [] };
    const [{ data: playersData, error: playersError }, interestResult] = await Promise.all([
      supabase
        .from('scheduled_user_sessions')
        .select('profile_id,creator,profiles(username,name,surname,profile_img)')
        .eq('session_id', session.id)
        .order('creator', { ascending: false }),
      supabase
        .from('scheduled_session_interest')
        .select('session_id,profile_id,status,created_at,updated_at,selection_rank,selection_reason,last_selected_session_at,selected_at,profiles!scheduled_session_interest_profile_id_fkey(username,name,surname,profile_img)')
        .eq('session_id', session.id)
        .in('status', ['interested', 'selected', 'waitlisted'])
        .order('selection_rank', { ascending: true, nullsFirst: false }),
    ]);
    if (playersError) throw playersError;
    if (interestResult.error && !/scheduled_session_interest|schema cache|does not exist|PGRST/i.test(String(interestResult.error.message || ''))) {
      throw interestResult.error;
    }

    let interestRows = interestResult.data || [];
    const profileIds = interestRows.map(row => row.profile_id).filter(Boolean);
    const sessionDate = scheduledDateKey(session.date);
    if (interestRows.length && profileIds.length && session.institute_id && session.sport_id && sessionDate) {
      const { data: historyRows, error: historyError } = await supabase
        .from('scheduled_user_sessions')
        .select('profile_id,scheduled_sessions!inner(id,date,start_time,duration,institute_id,sport_id,deleted_session)')
        .in('profile_id', profileIds)
        .eq('creator', false)
        .eq('scheduled_sessions.institute_id', session.institute_id)
        .eq('scheduled_sessions.sport_id', session.sport_id)
        .eq('scheduled_sessions.deleted_session', false)
        .lt('scheduled_sessions.date', sessionDate);
      if (!historyError) {
        const historyByProfile = new Map();
        (historyRows || []).forEach(row => {
          const historySession = Array.isArray(row.scheduled_sessions) ? row.scheduled_sessions[0] : row.scheduled_sessions;
          if (!row.profile_id || !historySession?.date) return;
          const stamp = `${historySession.date}T${historySession.start_time || '00:00:00'}`;
          const current = historyByProfile.get(String(row.profile_id)) || { played: 0, last: '', sessions: [] };
          historyByProfile.set(String(row.profile_id), {
            played: current.played + 1,
            last: !current.last || stamp > current.last ? stamp : current.last,
            sessions: [...current.sessions, {
              id: historySession.id,
              date: historySession.date,
              start_time: historySession.start_time,
              duration: historySession.duration,
            }],
          });
        });
        interestRows = [...interestRows]
          .sort((a, b) => {
            const aHistory = historyByProfile.get(String(a.profile_id));
            const bHistory = historyByProfile.get(String(b.profile_id));
            return (aHistory?.played || 0) - (bHistory?.played || 0)
              || String(aHistory?.last || '').localeCompare(String(bHistory?.last || ''))
              || getScheduledInterestName(a).localeCompare(getScheduledInterestName(b));
          })
          .map((row, index) => {
            const history = historyByProfile.get(String(row.profile_id));
            return {
              ...row,
              preview_rank: index + 1,
              preview_played_count: history?.played || 0,
              preview_last_selected_session_at: history?.last || null,
              preview_history_sessions: [...(history?.sessions || [])].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
            };
          });
      }
    }

    return { interestRows, playerRows: playersData || [] };
  }, []);

  const refreshSessionManager = useCallback(async (session = sessionManager?.session) => {
    if (!session?.id) return;
    setSessionManagerLoading(true);
    setSessionManagerError('');
    try {
      const rows = await loadSessionManagerRows(session);
      setSessionManager(current => current && current.session?.id === session.id ? ({ ...current, ...rows }) : ({ session, ...rows }));
    } catch (err) {
      setSessionManagerError(err?.message || 'Could not load this session.');
    } finally {
      setSessionManagerLoading(false);
    }
  }, [loadSessionManagerRows, sessionManager?.session]);

  const notifySelectedScheduledSessionPlayers = async (session, selectedRows = []) => {
    const recipientIds = uniqValues(
      selectedRows
        .map(row => row?.profile_id)
        .filter(id => id && String(id) !== String(profileData?.id || '')),
    );
    if (!profileData?.id || !session?.id || recipientIds.length === 0) {
      return { notificationCount: 0, pushCount: 0 };
    }

    const location = String(session.location_name || session.location || session.custom_location?.name || 'your scheduled session').trim();
    const dateKey = scheduledDateKey(session.date || session.series_occurrence_date);
    const dateLabel = dateKey ? new Date(`${dateKey}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) : 'the selected date';
    const startLabel = String(session.start_time || '').slice(0, 5);
    const message = startLabel
      ? `You were selected for ${location} on ${dateLabel} at ${startLabel}.`
      : `You were selected for ${location} on ${dateLabel}.`;
    const metadata = {
      notification_type: 'scheduled_session_selected',
      session_id: session.id,
      location,
      date: dateKey || null,
      start_time: startLabel || null,
      sport_id: session.sport_id || null,
    };

    try {
      const { error: notificationError } = await supabase.from('notifications').insert(recipientIds.map(receiverId => ({
        sender_id: profileData.id,
        receiver_id: receiverId,
        type: 'Scheduled Session',
        target_id: session.id,
        sport_id: session.sport_id || null,
        image_url: profileData.profile_img || null,
        message,
        metadata,
      })));
      if (notificationError) throw notificationError;

      const { data: recipientProfiles, error: profileError } = await supabase
        .from('profiles')
        .select('id,push_token')
        .in('id', recipientIds);
      if (profileError) throw profileError;

      const pushMessages = (recipientProfiles || [])
        .map(recipient => recipient?.push_token)
        .filter(isExpoPushToken)
        .map(token => ({
          to: token,
          title: 'You were selected',
          body: message,
          sound: 'default',
          data: {
            screen: 'ScheduleSessions',
            sessionId: session.id,
            notificationType: 'scheduled_session_selected',
            type: 'scheduled_session_selected',
            selectedSport: session.sport_id || undefined,
          },
        }));

      if (pushMessages.length > 0) {
        const { data, error } = await supabase.functions.invoke('send_expo_push', { body: { messages: pushMessages } });
        if (error) throw error;
        return { notificationCount: recipientIds.length, pushCount: Number(data?.attempted || 0) };
      }

      return { notificationCount: recipientIds.length, pushCount: 0 };
    } catch (err) {
      console.error('scheduled session selected notification error:', err);
      return { notificationCount: 0, pushCount: 0 };
    }
  };

  const openSessionManager = async (session) => {
    setSessionManager({ session, interestRows: [], playerRows: [] });
    setSessionManagerError('');
    setSessionManualMode(false);
    setSessionManualIds([]);
    setSessionExpandedProfileId(null);
    setSessionEditOpen(false);
    setSessionOrderMode('fair');
    await refreshSessionManager(session);
  };

  const openSessionEditor = async (session) => {
    setSessionManager({ session, interestRows: [], playerRows: [] });
    startEditSession(session);
    await refreshSessionManager(session);
  };

  const closeSessionManager = () => {
    setSessionManager(null);
    setSessionManagerError('');
    setSessionManualMode(false);
    setSessionManualIds([]);
    setSessionExpandedProfileId(null);
    setSessionEditOpen(false);
    setSessionOrderMode('fair');
  };

  const ensureSessionCreatorRow = async (session) => {
    const creatorId = session?.creator_id;
    if (!session?.id || !creatorId) return;
    const { data: existing, error: lookupError } = await supabase
      .from('scheduled_user_sessions')
      .select('id,creator')
      .eq('session_id', session.id)
      .eq('profile_id', creatorId)
      .maybeSingle();
    if (lookupError) throw lookupError;
    if (existing?.id) {
      if (existing.creator !== true) {
        const { error } = await supabase.from('scheduled_user_sessions').update({ creator: true }).eq('id', existing.id);
        if (error) throw error;
      }
      return;
    }
    const { error: insertError } = await supabase
      .from('scheduled_user_sessions')
      .insert({ session_id: session.id, profile_id: creatorId, creator: true });
    if (insertError) throw insertError;
  };

  const toggleManualSessionPlayer = (row, capacity) => {
    const profileId = String(row?.profile_id || '');
    if (!profileId || row?.status !== 'interested') return;
    setSessionManualIds(current => {
      if (current.includes(profileId)) return current.filter(id => id !== profileId);
      if (current.length >= capacity) {
        setSessionManagerError(`Max ${capacity}.`);
        return current;
      }
      setSessionManagerError('');
      return [...current, profileId];
    });
  };

  const confirmManualSessionSelection = async () => {
    const session = sessionManager?.session;
    if (!session?.id || sessionManagerSaving) return;
    const capacity = Math.max(1, Number(session.player_limit || 1));
    const selectedSet = new Set(sessionManualIds.map(String));
    const rows = sortScheduledInterestRows((sessionManager.interestRows || []).filter(row => row.status !== 'cancelled'), 'fair');
    const selectedRows = rows.filter(row => selectedSet.has(String(row.profile_id)));
    if (!selectedRows.length) {
      setSessionManagerError('Choose players first.');
      return;
    }
    setSessionManagerSaving(true);
    setSessionManagerError('');
    try {
      await ensureSessionCreatorRow(session);
      const { error: deleteError } = await supabase
        .from('scheduled_user_sessions')
        .delete()
        .eq('session_id', session.id)
        .eq('creator', false);
      if (deleteError) throw deleteError;
      const selectedAt = new Date().toISOString();
      for (const [index, row] of selectedRows.entries()) {
        const { error } = await supabase.from('scheduled_session_interest').update({
          status: 'selected',
          selected_at: selectedAt,
          selected_by: profileData?.id || null,
          selection_rank: index + 1,
          selection_reason: 'Selected manually by host',
          last_selected_session_at: row.preview_last_selected_session_at || row.last_selected_session_at || null,
        }).eq('session_id', session.id).eq('profile_id', row.profile_id);
        if (error) throw error;
      }
      const waitlistedRows = rows.filter(row => !selectedSet.has(String(row.profile_id)) && ['interested', 'waitlisted'].includes(row.status));
      for (const [index, row] of waitlistedRows.entries()) {
        const { error } = await supabase.from('scheduled_session_interest').update({
          status: 'waitlisted',
          selected_at: null,
          selected_by: null,
          selection_rank: selectedRows.length + index + 1,
          selection_reason: 'Not selected manually by host',
          last_selected_session_at: row.preview_last_selected_session_at || row.last_selected_session_at || null,
        }).eq('session_id', session.id).eq('profile_id', row.profile_id);
        if (error) throw error;
      }
      const { error: insertError } = await supabase
        .from('scheduled_user_sessions')
        .insert(selectedRows.map(row => ({ session_id: session.id, profile_id: row.profile_id, creator: false })));
      if (insertError) throw insertError;
      const patch = { selection_status: 'selected', selection_run_at: selectedAt, spots_left: Math.max(capacity - selectedRows.length, 0) };
      const { error: sessionError } = await supabase.from('scheduled_sessions').update(patch).eq('id', session.id);
      if (sessionError) throw sessionError;
      setSessionManualMode(false);
      setSessionManualIds([]);
      const nextSession = { ...session, ...patch };
      await notifySelectedScheduledSessionPlayers(nextSession, selectedRows);
      setSessionManager(current => current ? ({ ...current, session: nextSession }) : current);
      await refreshSessionManager(nextSession);
      loadContent();
    } catch (err) {
      setSessionManagerError(err?.message || 'Selection failed.');
    } finally {
      setSessionManagerSaving(false);
    }
  };

  const automateSessionSelection = async () => {
    const session = sessionManager?.session;
    if (!session?.id || sessionManagerSaving) return;
    setSessionManagerSaving(true);
    setSessionManagerError('');
    try {
      await ensureSessionCreatorRow(session);
      const { data, error } = await supabase.rpc('select_scheduled_session_participants', {
        p_session_id: session.id,
        p_limit: Number(session.player_limit || 1),
      });
      if (error) throw error;
      const patch = { selection_status: 'selected', spots_left: Math.max(Number(session.player_limit || 1) - Number(data?.selected_count || 0), 0) };
      const nextSession = { ...session, ...patch };
      if (Number(data?.selected_count || 0) > 0 && data?.already_selected !== true) {
        const rows = await loadSessionManagerRows(nextSession);
        await notifySelectedScheduledSessionPlayers(nextSession, rows.interestRows.filter(row => row.status === 'selected'));
      }
      setSessionManualMode(false);
      setSessionManualIds([]);
      setSessionManager(current => current ? ({ ...current, session: nextSession }) : current);
      await refreshSessionManager(nextSession);
      loadContent();
    } catch (err) {
      setSessionManagerError(err?.message || 'Automate failed.');
    } finally {
      setSessionManagerSaving(false);
    }
  };

  const undoSessionSelection = async () => {
    const session = sessionManager?.session;
    if (!session?.id || sessionManagerSaving) return;
    const confirmed = window.confirm('Undo selection and keep everyone interested?');
    if (!confirmed) return;
    setSessionManagerSaving(true);
    setSessionManagerError('');
    try {
      await ensureSessionCreatorRow(session);
      const selectedProfileIds = (sessionManager.interestRows || []).filter(row => row.status === 'selected').map(row => row.profile_id).filter(Boolean);
      if (selectedProfileIds.length) {
        const { error: deleteError } = await supabase
          .from('scheduled_user_sessions')
          .delete()
          .eq('session_id', session.id)
          .eq('creator', false)
          .in('profile_id', selectedProfileIds);
        if (deleteError) throw deleteError;
      }
      const { error: interestError } = await supabase.from('scheduled_session_interest').update({
        status: 'interested',
        selected_at: null,
        selected_by: null,
        selection_rank: null,
        selection_reason: null,
        last_selected_session_at: null,
      }).eq('session_id', session.id).in('status', ['selected', 'waitlisted']);
      if (interestError) throw interestError;
      const patch = { selection_status: 'interest_open', selection_run_at: null, spots_left: Number(session.player_limit || 0) };
      const { error: sessionError } = await supabase.from('scheduled_sessions').update(patch).eq('id', session.id);
      if (sessionError) throw sessionError;
      const nextSession = { ...session, ...patch };
      setSessionManager(current => current ? ({ ...current, session: nextSession }) : current);
      await refreshSessionManager(nextSession);
      loadContent();
    } catch (err) {
      setSessionManagerError(err?.message || 'Undo failed.');
    } finally {
      setSessionManagerSaving(false);
    }
  };

  const startEditSession = (session) => {
    setSessionEditDraft({
      date: scheduledDateKey(session.date),
      start_time: String(session.start_time || '').slice(0, 5),
      player_limit: String(session.player_limit || ''),
      session_info: session.session_info || '',
    });
    setSessionEditOpen(true);
    setSessionManagerError('');
  };

  const saveSessionEdit = async () => {
    const session = sessionManager?.session;
    if (!session?.id || sessionManagerSaving) return;
    const nextLimit = Number(sessionEditDraft.player_limit);
    if (!sessionEditDraft.date || !sessionEditDraft.start_time || !Number.isFinite(nextLimit) || nextLimit < 1) {
      setSessionManagerError('Add date, time and valid spots.');
      return;
    }
    const nonCreatorPlayers = (sessionManager.playerRows || []).filter(row => row.creator !== true).length;
    const selectedFairPlayers = (sessionManager.interestRows || []).filter(row => row.status === 'selected').length;
    const joinedCount = session.session_join_mode === 'interest' ? selectedFairPlayers : nonCreatorPlayers;
    if (nextLimit < joinedCount) {
      setSessionManagerError(`Minimum ${joinedCount} spots.`);
      return;
    }
    setSessionManagerSaving(true);
    setSessionManagerError('');
    try {
      const patch = {
        date: sessionEditDraft.date,
        start_time: sessionEditDraft.start_time,
        player_limit: nextLimit,
        session_info: sessionEditDraft.session_info.trim() || null,
        spots_left: Math.max(nextLimit - joinedCount, 0),
      };
      const { error } = await supabase.from('scheduled_sessions').update(patch).eq('id', session.id);
      if (error) throw error;
      const nextSession = { ...session, ...patch };
      setSessionManager(current => current ? ({ ...current, session: nextSession }) : current);
      setSessionEditOpen(false);
      await refreshSessionManager(nextSession);
      loadContent();
    } catch (err) {
      setSessionManagerError(err?.message || 'Could not save session.');
    } finally {
      setSessionManagerSaving(false);
    }
  };

  const stopSessionRecurrence = async (session) => {
    if (!session?.series_id || sessionManagerSaving) return;
    const confirmed = window.confirm('Stop weekly recurrence? This keeps visible sessions available, but no future weekly dates will be created from this series.');
    if (!confirmed) return;
    setSessionManagerSaving(true);
    setSessionManagerError('');
    try {
      const seriesId = session.series_id;
      const today = dateInputValue(new Date());
      const { error: seriesError } = await supabase
        .from('scheduled_session_series')
        .update({ status: 'cancelled' })
        .eq('id', seriesId);
      if (seriesError) throw seriesError;
      const { error: sessionsError } = await supabase
        .from('scheduled_sessions')
        .update({ series_id: null, series_occurrence_date: null })
        .eq('series_id', seriesId)
        .gte('date', today);
      if (sessionsError) throw sessionsError;
      const nextSession = {
        ...session,
        series_id: null,
        series_occurrence_date: null,
        scheduled_session_series: null,
      };
      setSessionManager(current => current ? ({ ...current, session: nextSession }) : current);
      setSessionEditOpen(false);
      setActivityAudienceFeedback({ tone: 'success', text: 'Weekly recurrence stopped. Visible sessions are now one-off.' });
      loadContent();
    } catch (err) {
      setSessionManagerError(err?.message || 'Could not stop recurrence.');
    } finally {
      setSessionManagerSaving(false);
    }
  };

  const deleteScheduledSession = async (session) => {
    if (!session?.id) return;
    const confirmed = window.confirm(`Delete "${session.location_name || 'this session'}"? Members will no longer see it.`);
    if (!confirmed) return;
    setSessionManagerSaving(true);
    setSessionManagerError('');
    try {
      const { error } = await supabase.from('scheduled_sessions').update({ deleted_session: true }).eq('id', session.id);
      if (error) throw error;
      closeSessionManager();
      setActivityAudienceFeedback({ tone: 'success', text: 'Session deleted.' });
      loadContent();
    } catch (err) {
      setSessionManagerError(err?.message || 'Could not delete session.');
    } finally {
      setSessionManagerSaving(false);
    }
  };

  const handleCommunityImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setCommunityImageFile(file);
    setCommunityImagePreview(file ? URL.createObjectURL(file) : editingCommunity?.image_url || '');
  };

  const resetCommunityForm = () => {
    setCommunityFormOpen(false);
    setEditingCommunity(null);
    setCommunityName('');
    setCommunityPrivate(false);
    setCommunitySportId('');
    setCommunityTeamId('');
    setCommunityImageFile(null);
    setCommunityImagePreview('');
  };

  const startEditCommunity = (community) => {
    setCommunityFeedback(null);
    setEditingCommunity(community);
    setCommunityName(community.name || '');
    setCommunityPrivate(!!community.private);
    setCommunitySportId(community.sport_id || '');
    setCommunityTeamId(community.institute_team_id || '');
    setCommunityImageFile(null);
    setCommunityImagePreview(community.image_url || '');
    setCommunityFormOpen(true);
  };

  const saveCommunity = async () => {
    const name = communityName.trim().toLowerCase();
    if (!name) {
      setCommunityFeedback({ tone: 'error', text: 'Enter a community name before saving.' });
      return;
    }
    if (!activeInst?.id || !profileData?.id) {
      setCommunityFeedback({ tone: 'error', text: 'This institution is not ready for community management yet.' });
      return;
    }
    if (!editingCommunity?.id) {
      setCommunityFeedback({ tone: 'error', text: 'Create new communities in the app, then link them to this institution.' });
      return;
    }
    setSavingCommunity(true);
    setCommunityFeedback(null);
    try {
      let imageUrl = editingCommunity?.image_url || communityImagePreview || noImage;
      if (communityImageFile) {
        const ext = communityImageFile.name?.split('.').pop() || 'jpg';
        const path = `${activeInst.id}-${Date.now()}-${name}.${ext}`;
        imageUrl = await uploadFileToR2({ bucket: 'media', folder: 'communities', filename: path.replace(/[/\\]+/g, '-'), file: communityImageFile });
      }

      const { error: updateError } = await supabase
        .from('communities')
        .update({
          name,
          private: communityPrivate,
          sport_id: communitySportId || null,
          image_url: imageUrl || noImage,
          institute_id: activeInst.id,
          institute_team_id: communityTeamId || null,
        })
        .eq('id', editingCommunity.id);
      if (updateError) throw updateError;
      setCommunityFeedback({ tone: 'success', text: 'Community updated.' });
      resetCommunityForm();
      loadContent();
    } catch (err) {
      setCommunityFeedback({ tone: 'error', text: err?.message || 'Could not save this community.' });
    } finally {
      setSavingCommunity(false);
    }
  };

  const deleteCommunity = async (community) => {
    if (!community?.id) return;
    const confirmed = window.confirm(`Delete "${community.name || 'this community'}"? This removes the institution community for members.`);
    if (!confirmed) return;
    setDeletingCommunityId(community.id);
    setCommunityFeedback(null);
    try {
      const { error: deleteError } = await supabase.from('communities').delete().eq('id', community.id);
      if (deleteError) throw deleteError;
      if (editingCommunity?.id === community.id) resetCommunityForm();
      setCommunityFeedback({ tone: 'success', text: 'Community deleted.' });
      loadContent();
    } catch (err) {
      setCommunityFeedback({ tone: 'error', text: err?.message || 'Could not delete this community.' });
    } finally {
      setDeletingCommunityId(null);
    }
  };

  const managedSession = sessionManager?.session || null;
  const managedIsFairSession = managedSession?.session_join_mode === 'interest';
  const managedCapacity = Math.max(1, Number(managedSession?.player_limit || 1));
  const managedBaseInterestRows = (sessionManager?.interestRows || []).filter(row => row.status !== 'cancelled');
  const managedJoinedOrder = getScheduledJoinedOrderMap(managedBaseInterestRows);
  const managedInterestRows = sortScheduledInterestRows(managedBaseInterestRows, sessionOrderMode);
  const managedSelectedCount = managedInterestRows.filter(row => row.status === 'selected').length;
  const managedWaitlistedCount = managedInterestRows.filter(row => row.status === 'waitlisted').length;
  const managedInterestedCount = managedInterestRows.filter(row => row.status === 'interested').length;
  const managedPlayerRows = sessionManager?.playerRows || [];

  return (
    <section aria-label="Member activities" style={{ ...card(C, { padding: isMobile ? 14 : 20, marginBottom: 16, borderRadius: 18 }), display: 'grid', gap: isMobile ? 12 : 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h3 className="heading" style={{ margin: 0, color: C.text, fontSize: 15, fontWeight: 900 }}>Member activities</h3>
          <p style={{ margin: '4px 0 0', color: C.textMuted, fontSize: 12, lineHeight: 1.45 }}>
            Sessions, events, tournaments and communities available to selected members.
          </p>
        </div>
        <div style={{ display: 'grid', justifyItems: 'end', gap: 4 }}>
          <span style={{ padding: '5px 9px', borderRadius: 999, background: C.bgAlt, color: C.textSecondary, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 900 }}>
            {loading ? 'Loading' : `${total} linked`}
          </span>
          <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>
            {afriRunDemoCommunity ? 'AfriRun is a safe demo-only community preview.' : 'Create communities in the app.'}
          </span>
        </div>
      </div>
      {communityFeedback && (
        <div role={communityFeedback.tone === 'error' ? 'alert' : 'status'} style={{ padding: 11, borderRadius: 10, background: communityFeedback.tone === 'error' ? 'rgba(220,38,38,0.10)' : 'rgba(5,150,105,0.10)', color: communityFeedback.tone === 'error' ? C.danger : C.success, fontSize: 12, fontWeight: 800 }}>
          {communityFeedback.text}
        </div>
      )}
      {activityAudienceFeedback && (
        <div role={activityAudienceFeedback.tone === 'error' ? 'alert' : 'status'} style={{ padding: 11, borderRadius: 10, background: activityAudienceFeedback.tone === 'error' ? 'rgba(220,38,38,0.10)' : 'rgba(5,150,105,0.10)', color: activityAudienceFeedback.tone === 'error' ? C.danger : C.success, fontSize: 12, fontWeight: 800 }}>
          {activityAudienceFeedback.text}
        </div>
      )}
      {showAppSimulatorShortcut && showAppDemoPreview && demoPreviewSessions.length > 0 && (
        <button
          type="button"
          onClick={() => openAppPreview(demoPreviewSessions[0])}
          aria-label="Open demo app session preview"
          title="Open demo app session preview"
          style={{
            position: 'fixed',
            right: isMobile ? 16 : 24,
            bottom: isMobile ? 78 : 24,
            zIndex: 1400,
            width: 58,
            height: 58,
            borderRadius: 18,
            border: '1px solid rgba(255,255,255,0.28)',
            background: 'linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)',
            color: '#fff',
            boxShadow: '0 14px 36px rgba(10,29,68,0.32)',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <FontAwesomeIcon icon={faCalendar} style={{ fontSize: 22 }} />
        </button>
      )}
      {appPreviewSession && (
        <ScheduledSessionAppPreview
          C={C}
          session={appPreviewSession}
          instituteName={activeInst?.name}
          audience={audienceLabel(appPreviewSession)}
          imageUrl={getActivityImage('scheduled_sessions', appPreviewSession)}
          onClose={() => setAppPreviewSession(null)}
          formatDate={formatDate}
          formatTime={formatTime}
        />
      )}
      {managedSession && (
        <div role="dialog" aria-modal="true" aria-label={`Manage ${managedSession.location_name || 'scheduled session'}`} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(10,29,68,0.56)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 20 }}>
          <div style={{ width: 'min(920px, 94vw)', maxHeight: '92vh', overflow: 'hidden', borderRadius: 18, background: C.bgCard, border: `1px solid ${C.border}`, boxShadow: '0 24px 70px rgba(10,29,68,0.28)', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto' }}>
            <div style={{ padding: 16, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>{managedIsFairSession ? 'Fair selection session' : 'Session management'}</div>
	                <h3 style={{ margin: '4px 0 0', color: C.text, fontSize: 19, lineHeight: 1.15 }}>{managedSession.location_name || managedSession.sport_id || 'Scheduled session'}</h3>
	                <div style={{ marginTop: 5, color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>
	                  {[formatDate(managedSession.date), formatTime(managedSession.start_time), managedSession.duration].filter(Boolean).join(' · ')}
	                </div>
	                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, border: `1px solid ${isRecurringScheduledSession(managedSession) ? 'rgba(12,105,200,0.28)' : C.border}`, background: isRecurringScheduledSession(managedSession) ? C.accentBgSubtle : C.bgAlt, color: isRecurringScheduledSession(managedSession) ? C.accent : C.textSecondary, borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 900 }}>
	                  <FontAwesomeIcon icon={isRecurringScheduledSession(managedSession) ? faRepeat : faCalendar} />
	                  {scheduledSessionFrequencyLabel(managedSession)}
	                </div>
	              </div>
              <button type="button" onClick={closeSessionManager} disabled={sessionManagerSaving} aria-label="Close session manager" style={{ width: 36, height: 36, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, cursor: sessionManagerSaving ? 'not-allowed' : 'pointer' }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', padding: 16, display: 'grid', gap: 14 }}>
              {sessionManagerError && (
                <div role="alert" style={{ padding: 11, borderRadius: 10, background: 'rgba(220,38,38,0.10)', color: C.danger, fontSize: 12, fontWeight: 850 }}>{sessionManagerError}</div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 9 }}>
                {(managedIsFairSession ? [
                  ['Picked', sessionManualMode ? sessionManualIds.length : managedSelectedCount],
                  ['Bench', managedWaitlistedCount],
                  ['Pool', managedInterestedCount],
                  ['Spots', managedCapacity],
                ] : [
                  ['Spots', managedCapacity],
                  ['Players', managedPlayerRows.length],
                  ['Joined', managedPlayerRows.filter(row => row.creator !== true).length],
                  ['Left', Math.max(Number(managedSession.spots_left || 0), 0)],
                ]).map(([label, value]) => (
                  <div key={label} style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 12, padding: 10, minHeight: 58 }}>
                    <div style={{ color: C.accent, fontSize: 18, fontWeight: 950 }}>{value}</div>
                    <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>

              {sessionEditOpen && (
                <div style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 14, padding: 12, display: 'grid', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <strong style={{ color: C.text, fontSize: 13 }}>Edit session</strong>
                    <button type="button" onClick={() => setSessionEditOpen(false)} disabled={sessionManagerSaving} style={{ border: 'none', background: 'transparent', color: C.textMuted, cursor: sessionManagerSaving ? 'not-allowed' : 'pointer' }}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
                    <label style={{ display: 'grid', gap: 5, color: C.text, fontSize: 12, fontWeight: 850 }}>
                      Date
                      <input type="date" value={sessionEditDraft.date} onChange={event => setSessionEditDraft(current => ({ ...current, date: event.target.value }))} style={inputSt(C)} disabled={sessionManagerSaving} />
                    </label>
                    <label style={{ display: 'grid', gap: 5, color: C.text, fontSize: 12, fontWeight: 850 }}>
                      Time
                      <input type="time" value={sessionEditDraft.start_time} onChange={event => setSessionEditDraft(current => ({ ...current, start_time: event.target.value }))} style={inputSt(C)} disabled={sessionManagerSaving} />
                    </label>
                    <label style={{ display: 'grid', gap: 5, color: C.text, fontSize: 12, fontWeight: 850 }}>
                      Spots
                      <input type="number" min="1" value={sessionEditDraft.player_limit} onChange={event => setSessionEditDraft(current => ({ ...current, player_limit: event.target.value }))} style={inputSt(C)} disabled={sessionManagerSaving} />
                    </label>
                  </div>
	                  <label style={{ display: 'grid', gap: 5, color: C.text, fontSize: 12, fontWeight: 850 }}>
	                    Session details
	                    <textarea value={sessionEditDraft.session_info} onChange={event => setSessionEditDraft(current => ({ ...current, session_info: event.target.value }))} rows={4} style={{ ...inputSt(C), resize: 'vertical', lineHeight: 1.45 }} disabled={sessionManagerSaving} />
	                  </label>
	                  <div style={{ border: `1px solid ${isRecurringScheduledSession(managedSession) ? 'rgba(217,119,6,0.30)' : C.border}`, background: isRecurringScheduledSession(managedSession) ? 'rgba(245,158,11,0.10)' : C.bgCard, color: C.textSecondary, borderRadius: 12, padding: 10, display: 'grid', gap: 7 }}>
	                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: isRecurringScheduledSession(managedSession) ? '#92400E' : C.textSecondary, fontSize: 12, fontWeight: 950 }}>
	                      <FontAwesomeIcon icon={isRecurringScheduledSession(managedSession) ? faRepeat : faCalendar} />
	                      {scheduledSessionFrequencyLabel(managedSession)}
	                    </div>
	                    <div style={{ color: C.textMuted, fontSize: 11.5, lineHeight: 1.4, fontWeight: 750 }}>
	                      {isRecurringScheduledSession(managedSession)
	                        ? 'Editing changes this visible session. Stop recurring to prevent future weekly dates while keeping current sessions visible.'
	                        : 'This session will not automatically create future dates.'}
	                    </div>
	                  </div>
	                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
	                    {isRecurringScheduledSession(managedSession) && (
	                      <button type="button" onClick={() => stopSessionRecurrence(managedSession)} disabled={sessionManagerSaving} style={{ minHeight: 38, borderRadius: 10, border: '1px solid rgba(217,119,6,0.35)', background: 'rgba(245,158,11,0.10)', color: '#92400E', padding: '8px 12px', fontSize: 12, fontWeight: 900, cursor: sessionManagerSaving ? 'wait' : 'pointer' }}><FontAwesomeIcon icon={faRepeat} /> Stop recurring</button>
	                    )}
	                    <button type="button" onClick={() => setSessionEditOpen(false)} disabled={sessionManagerSaving} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', fontSize: 12, fontWeight: 850, cursor: sessionManagerSaving ? 'not-allowed' : 'pointer' }}>Cancel</button>
	                    <button type="button" onClick={saveSessionEdit} disabled={sessionManagerSaving} style={{ minHeight: 38, border: 'none', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '8px 12px', fontSize: 12, fontWeight: 950, cursor: sessionManagerSaving ? 'wait' : 'pointer' }}>{sessionManagerSaving ? 'Saving...' : 'Save session'}</button>
                  </div>
                </div>
              )}

              {managedIsFairSession ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, padding: 4, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                    {[
                      ['fair', 'Fair', faRotateRight],
                      ['joined', 'Joined', faClock],
                    ].map(([mode, label, icon]) => (
                      <button key={mode} type="button" onClick={() => { setSessionOrderMode(mode); setSessionExpandedProfileId(null); }} aria-pressed={sessionOrderMode === mode} style={{ minHeight: 36, borderRadius: 9, border: 'none', background: sessionOrderMode === mode ? C.accentGrad : 'transparent', color: sessionOrderMode === mode ? '#fff' : C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 950, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                        <FontAwesomeIcon icon={icon} /> {label}
                      </button>
                    ))}
                  </div>

                  {sessionManagerLoading ? (
                    <div style={{ color: C.textMuted, fontSize: 13 }}>Loading players...</div>
                  ) : managedInterestRows.length ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {managedInterestRows.map(row => {
                        const profileId = String(row.profile_id || '');
                        const avatar = getScheduledInterestAvatar(row);
                        const played = Number(row.preview_played_count || 0);
                        const rank = Number(row.selection_rank ?? row.preview_rank);
                        const joinedOrder = managedJoinedOrder.get(profileId);
                        const chosen = sessionManualMode && sessionManualIds.includes(profileId);
                        const expanded = sessionExpandedProfileId === profileId;
                        const status = chosen ? 'Chosen' : row.status === 'selected' ? 'Selected' : row.status === 'waitlisted' ? 'Waitlisted' : 'Interested';
                        return (
                          <button key={`${profileId}-${row.status}-${row.selection_rank || 'open'}`} type="button" onClick={() => sessionManualMode ? toggleManualSessionPlayer(row, managedCapacity) : setSessionExpandedProfileId(current => current === profileId ? null : profileId)} aria-expanded={!sessionManualMode && expanded} aria-pressed={sessionManualMode ? chosen : undefined} style={{ textAlign: 'left', border: `1px solid ${chosen ? C.success : C.border}`, background: chosen ? 'rgba(5,150,105,0.10)' : C.bgCard, color: C.text, borderRadius: 13, padding: 10, cursor: sessionManagerSaving ? 'wait' : 'pointer', display: 'grid', gap: 9 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '54px minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
                              <div style={{ minHeight: 50, borderRadius: 12, border: `1px solid ${played <= 0 ? 'rgba(5,150,105,0.35)' : C.border}`, background: played <= 0 ? 'rgba(5,150,105,0.10)' : C.bgAlt, display: 'grid', placeItems: 'center', alignContent: 'center' }}>
                                <strong style={{ color: C.text, fontSize: 17 }}>{sessionOrderMode === 'joined' ? `#${joinedOrder || '-'}` : played}</strong>
                                <span style={{ color: C.textMuted, fontSize: 8, fontWeight: 950, textTransform: 'uppercase' }}>{sessionOrderMode === 'joined' ? 'joined' : 'plays'}</span>
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                  <img src={avatar || noImage} alt="" style={{ width: 34, height: 34, borderRadius: 17, objectFit: 'cover', border: `1px solid ${C.border}`, flexShrink: 0 }} />
                                  <strong style={{ color: C.text, fontSize: 13.5, lineHeight: 1.25, minWidth: 0, whiteSpace: 'normal' }}>{getScheduledInterestName(row)}</strong>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                                  <span style={tagSt(C, row.status === 'selected' || chosen)}>{status}</span>
                                  <span style={tagSt(C)}>{sessionOrderMode === 'joined' ? formatScheduledInterestJoined(row) : Number.isFinite(rank) ? `#${rank}` : 'No rank'}</span>
                                  <span style={tagSt(C)}>{sessionOrderMode === 'joined' ? `${played} plays` : (formatScheduledHistoryDate(row.preview_last_selected_session_at || row.last_selected_session_at) || 'Never')}</span>
                                </div>
                              </div>
                              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ color: C.textMuted }} />
                            </div>
                            {expanded && (
                              <div style={{ border: `1px solid rgba(5,150,105,0.22)`, background: 'rgba(5,150,105,0.08)', borderRadius: 12, padding: 9, display: 'grid', gap: 7 }}>
                                {(row.preview_history_sessions || []).length ? row.preview_history_sessions.map((history, index) => (
                                  <div key={`${history.id || index}-${history.date}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: `1px solid ${C.border}`, background: C.bgCard, borderRadius: 10, padding: 8 }}>
                                    <span style={{ color: C.text, fontSize: 12, fontWeight: 900 }}>{formatDate(history.date)}</span>
                                    <span style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>{[formatTime(history.start_time), history.duration].filter(Boolean).join(' · ') || 'Played'}</span>
                                  </div>
                                )) : (
                                  <div style={{ color: C.success, fontSize: 12, fontWeight: 900 }}>No attended sessions yet</div>
                                )}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ border: `1px dashed ${C.border}`, background: C.bgAlt, color: C.textMuted, borderRadius: 12, padding: 14, fontSize: 13, fontWeight: 850 }}>No players registered interest yet.</div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {sessionManagerLoading ? (
                    <div style={{ color: C.textMuted, fontSize: 13 }}>Loading players...</div>
                  ) : managedPlayerRows.length ? managedPlayerRows.map(row => {
                    const profile = Array.isArray(row.profiles) ? row.profiles[0] : row.profiles;
                    const name = [profile?.name, profile?.surname].filter(Boolean).join(' ').trim() || profile?.username || 'Player';
                    return (
                      <div key={`${row.profile_id}-${row.creator}`} style={{ border: `1px solid ${C.border}`, background: C.bgCard, borderRadius: 12, padding: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img src={profile?.profile_img || noImage} alt="" style={{ width: 38, height: 38, borderRadius: 19, objectFit: 'cover', border: `1px solid ${C.border}` }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <strong style={{ color: C.text, fontSize: 13 }}>{name}</strong>
                          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800 }}>{profile?.username ? `@${profile.username}` : row.profile_id}</div>
                        </div>
                        {row.creator && <span style={tagSt(C, true)}>Host</span>}
                      </div>
                    );
                  }) : (
                    <div style={{ border: `1px dashed ${C.border}`, background: C.bgAlt, color: C.textMuted, borderRadius: 12, padding: 14, fontSize: 13, fontWeight: 850 }}>No players joined yet.</div>
                  )}
                </div>
              )}
            </div>

	            <div style={{ padding: 14, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', background: C.bgAlt }}>
	              {isRecurringScheduledSession(managedSession) && (
	                <button type="button" onClick={() => stopSessionRecurrence(managedSession)} disabled={sessionManagerSaving} style={{ minHeight: 38, borderRadius: 10, border: '1px solid rgba(217,119,6,0.35)', background: 'rgba(245,158,11,0.10)', color: '#92400E', padding: '8px 12px', cursor: sessionManagerSaving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 900 }}><FontAwesomeIcon icon={faRepeat} /> Stop recurring</button>
	              )}
	              <button type="button" onClick={() => startEditSession(managedSession)} disabled={sessionManagerSaving} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', cursor: sessionManagerSaving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 900 }}><FontAwesomeIcon icon={faPen} /> Edit</button>
              <button type="button" onClick={() => deleteScheduledSession(managedSession)} disabled={sessionManagerSaving} style={dangerActionButtonSt(C, { minHeight: 38, borderRadius: 10, padding: '8px 12px', cursor: sessionManagerSaving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 900 })}><FontAwesomeIcon icon={faTrash} /> Delete</button>
              {managedIsFairSession && managedSession.selection_status === 'selected' ? (
                <button type="button" onClick={undoSessionSelection} disabled={sessionManagerSaving} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', cursor: sessionManagerSaving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 900 }}>Undo</button>
              ) : managedIsFairSession ? (
                <>
                  <button type="button" onClick={() => sessionManualMode ? confirmManualSessionSelection() : (setSessionManualMode(true), setSessionManualIds([]), setSessionExpandedProfileId(null))} disabled={sessionManagerSaving || !managedBaseInterestRows.length} style={{ minHeight: 38, border: 'none', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '8px 12px', cursor: sessionManagerSaving || !managedBaseInterestRows.length ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 950 }}><FontAwesomeIcon icon={faCheck} /> {sessionManualMode ? 'Save' : 'Select'}</button>
                  <button type="button" onClick={automateSessionSelection} disabled={sessionManagerSaving || !managedBaseInterestRows.length} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '8px 12px', cursor: sessionManagerSaving || !managedBaseInterestRows.length ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 950 }}><FontAwesomeIcon icon={faRotateRight} /> Automate</button>
                </>
              ) : null}
              <button type="button" onClick={closeSessionManager} disabled={sessionManagerSaving} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', cursor: sessionManagerSaving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 900 }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {activityAudienceEditor && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgAlt, padding: 12, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <strong style={{ color: C.text, fontSize: 13 }}>Edit audience</strong>
              <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>
                {activityAudienceEditor.title} · {activityAudienceEditor.sourceInstitute?.name || activeInst?.name || 'Institution'}
              </div>
            </div>
            <button type="button" onClick={() => { setActivityAudienceEditor(null); setActivityAudienceDepartments([]); }} disabled={activityAudienceSaving} style={{ border: 'none', background: 'transparent', color: C.textMuted, cursor: activityAudienceSaving ? 'not-allowed' : 'pointer', padding: 4 }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
            <button type="button" onClick={() => setActivityAudienceMode('members')} aria-pressed={(activityAudienceEditor.teamIds || []).length === 0} disabled={activityAudienceSaving} style={{ minHeight: 42, borderRadius: 10, border: `1px solid ${(activityAudienceEditor.teamIds || []).length === 0 ? C.accent : C.border}`, background: (activityAudienceEditor.teamIds || []).length === 0 ? C.accentBgLight : C.bgCard, color: (activityAudienceEditor.teamIds || []).length === 0 ? C.accent : C.textSecondary, padding: '9px 10px', cursor: activityAudienceSaving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 900 }}>
              All members
            </button>
            <button type="button" onClick={() => setActivityAudienceEditor(current => current ? ({ ...current, teamIds: current.teamIds?.length ? current.teamIds : [] }) : current)} aria-pressed={(activityAudienceEditor.teamIds || []).length > 0} disabled={activityAudienceSaving || activityAudienceLoading || !activityAudienceDepartments.length} style={{ minHeight: 42, borderRadius: 10, border: `1px solid ${(activityAudienceEditor.teamIds || []).length > 0 ? C.accent : C.border}`, background: (activityAudienceEditor.teamIds || []).length > 0 ? C.accentBgLight : C.bgCard, color: (activityAudienceEditor.teamIds || []).length > 0 ? C.accent : C.textSecondary, padding: '9px 10px', cursor: activityAudienceSaving || activityAudienceLoading || !activityAudienceDepartments.length ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 900 }}>
              Selected {activitySubgroupLabel.toLowerCase()}s
            </button>
          </div>
          {activityAudienceLoading ? (
            <div style={{ color: C.textMuted, fontSize: 12 }}>Loading {activitySubgroupLabel.toLowerCase()}s...</div>
          ) : activityAudienceDepartments.length > 0 ? (
            <div style={{ display: 'grid', gap: 9 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ color: C.textSecondary, fontSize: 12, fontWeight: 850 }}>
                  {(activityAudienceEditor.teamIds || []).length} of {activityAudienceDepartments.length} selected
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setActivityAudienceEditor(current => current ? ({ ...current, teamIds: activityAudienceDepartments.map(dept => dept.id).filter(Boolean) }) : current)} disabled={activityAudienceSaving} style={{ border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: activityAudienceSaving ? 'not-allowed' : 'pointer' }}>Select all</button>
                  <button type="button" onClick={() => setActivityAudienceMode('members')} disabled={activityAudienceSaving} style={{ border: `1px solid ${C.border}`, background: C.bgElevated, color: C.textSecondary, borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: activityAudienceSaving ? 'not-allowed' : 'pointer' }}>Clear</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, maxHeight: 220, overflowY: 'auto', paddingRight: 2 }}>
                {activityAudienceDepartments.map(dept => {
                  const checked = (activityAudienceEditor.teamIds || []).includes(dept.id);
                  return (
                    <label key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '8px 10px', borderRadius: 10, border: `1px solid ${checked ? C.accent : C.border}`, background: checked ? C.accentBgLight : C.bgCard, cursor: activityAudienceSaving ? 'not-allowed' : 'pointer', color: C.text }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleActivityAudienceDepartment(dept.id)} disabled={activityAudienceSaving} style={{ width: 16, height: 16, accentColor: C.accent, flexShrink: 0 }} />
                      {dept.image_url && <img src={dept.image_url} alt="" style={{ width: 40, height: 24, borderRadius: 6, objectFit: 'cover', border: `1px solid ${C.border}`, flexShrink: 0 }} />}
                      <span style={{ minWidth: 0, fontSize: 13, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dept.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div role="status" style={{ padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, fontSize: 13, lineHeight: 1.45 }}>
              No {activitySubgroupLabel.toLowerCase()}s are available. Use all members or create {activitySubgroupLabel.toLowerCase()}s first.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => { setActivityAudienceEditor(null); setActivityAudienceDepartments([]); }} disabled={activityAudienceSaving} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', cursor: activityAudienceSaving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 850 }}>Cancel</button>
            <button type="button" onClick={saveActivityAudience} disabled={activityAudienceSaving} style={{ minHeight: 38, border: 'none', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '8px 12px', cursor: activityAudienceSaving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 900 }}>
              {activityAudienceSaving ? 'Saving...' : 'Save audience'}
            </button>
          </div>
        </div>
      )}
      {communityFormOpen && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgAlt, padding: 12, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <strong style={{ color: C.text, fontSize: 13 }}>Edit community</strong>
            <button type="button" onClick={resetCommunityForm} disabled={savingCommunity} style={{ border: 'none', background: 'transparent', color: C.textMuted, cursor: 'pointer', padding: 4 }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr .8fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 5, color: C.text, fontSize: 12, fontWeight: 800 }}>
              Community name
              <input value={communityName} onChange={e => setCommunityName(e.target.value)} placeholder="e.g. google runners" style={inputSt(C)} disabled={savingCommunity} />
            </label>
            <label style={{ display: 'grid', gap: 5, color: C.text, fontSize: 12, fontWeight: 800 }}>
              Sport
              <select value={communitySportId} onChange={e => setCommunitySportId(e.target.value)} style={selectSt(C)} disabled={savingCommunity}>
                <option value="">No sport selected</option>
                {sports.map(sport => {
                  const sportId = sport.sport_id || sport.id;
                  return <option key={sportId} value={sportId}>{sportId}</option>;
                })}
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px minmax(0, 1fr)', gap: 12, alignItems: 'stretch' }}>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bgCard }}>
              <img src={communityImagePreview || noImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <button type="button" onClick={() => communityImageInputRef.current?.click()} disabled={savingCommunity} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8, padding: 10, border: 'none', background: 'linear-gradient(180deg, rgba(10,29,68,0.04) 35%, rgba(10,29,68,0.70) 100%)', color: '#fff', cursor: savingCommunity ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 900, textAlign: 'left' }} aria-label={communityImagePreview ? 'Change community image' : 'Choose community image'}>
                <span>{communityImagePreview ? 'Change image' : 'Choose image'}</span>
                <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 14, background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.45)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faCamera} />
                </span>
              </button>
              <input ref={communityImageInputRef} id={communityImageInputId} type="file" accept="image/*" onChange={handleCommunityImageChange} disabled={savingCommunity} tabIndex={-1} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
            </div>
            <div style={{ display: 'grid', gap: 10, alignContent: 'center', minWidth: 0 }}>
              <div>
                <strong style={{ color: C.text, fontSize: 13 }}>Community image</strong>
                <p style={{ margin: '4px 0 0', color: C.textMuted, fontSize: 12, lineHeight: 1.45 }}>
                  Use a photo members will recognise in the institution activity list.
                </p>
                {communityImageFile?.name && (
                  <div style={{ marginTop: 6, color: C.textSecondary, fontSize: 11, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Selected: {communityImageFile.name}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={resetCommunityForm} disabled={savingCommunity} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 850 }}>Cancel</button>
            <button type="button" onClick={saveCommunity} disabled={savingCommunity || !communityName.trim()} style={{ minHeight: 38, border: 'none', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '8px 12px', cursor: savingCommunity || !communityName.trim() ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 900 }}>
              {savingCommunity ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
      {error ? (
        <div role="alert" style={{ padding: 12, borderRadius: 10, background: 'rgba(220,38,38,0.10)', color: C.danger, fontSize: 12, fontWeight: 800 }}>{error}</div>
      ) : loading ? (
        <div style={{ color: C.textMuted, fontSize: 13, padding: '4px 0' }}>Loading member activities...</div>
      ) : total === 0 ? (
        <div style={{ padding: 13, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, fontSize: 13 }}>
          No member activities are linked to {activeInst?.name || 'this institution'} yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? 12 : 16, alignItems: 'start' }}>
          {groups.map(group => {
            const expanded = !!expandedContentGroups[group.key];
            const visibleRows = expanded ? group.rows : group.rows.slice(0, 3);
            const remaining = Math.max(0, group.rows.length - 3);
            return (
            <div key={group.key} style={{ border: `1px solid ${C.border}`, borderRadius: 16, padding: isMobile ? 11 : 14, background: C.bgAlt, minWidth: 0, display: 'grid', gap: 12, alignContent: 'start', alignSelf: 'start' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 10, background: C.bgCard, color: C.textSecondary, border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FontAwesomeIcon icon={group.icon} style={{ fontSize: 12 }} />
                  </span>
                  <strong style={{ color: C.text, fontSize: 13 }}>{group.title}</strong>
                </span>
                <span style={{ color: C.textSecondary, fontSize: 12, fontWeight: 900, padding: '3px 8px', borderRadius: 999, background: C.bgCard, border: `1px solid ${C.border}` }}>
                  {group.rows.length}
                </span>
              </div>
              {!group.rows.length ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 54, borderRadius: 12, border: `1px dashed ${C.border}`, padding: 10, color: C.textMuted, fontSize: 12, fontWeight: 800 }}>
                  <FontAwesomeIcon icon={group.icon} aria-hidden="true" />
                  {group.empty}
                </div>
              ) : visibleRows.map(row => (
                <div key={row.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(150px, 176px) minmax(0, 1fr)', gap: isMobile ? 11 : 14, alignItems: 'start', padding: isMobile ? 11 : 14, border: `1px solid ${C.border}`, background: C.bgCard, borderRadius: 14, boxShadow: C.shadowSm }}>
                  <img src={getActivityImage(group.key, row)} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 11, objectFit: 'cover', border: `1px solid ${C.border}`, background: C.bgCard, display: 'block' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: C.text, fontSize: isMobile ? 13.5 : 15, lineHeight: 1.25, fontWeight: 950, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{group.getTitle(row)}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(112px, 1fr))', gap: 8, marginTop: 10 }}>
                      {activityMetaRows(group.key, row).map(([label, value]) => (
                        <div key={`${row.id}-${label}`} style={{ minWidth: 0, minHeight: 53, border: `1px solid ${label === 'Availability' ? 'rgba(217,179,0,0.36)' : C.border}`, borderRadius: 10, background: label === 'Availability' ? 'rgba(255,230,0,0.08)' : C.bgElevated, padding: '8px 9px' }}>
                          <div style={{ color: C.textMuted, fontSize: 9.5, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
                          <div style={{ color: label === 'Availability' ? '#6B5600' : C.textSecondary, fontSize: 12, lineHeight: 1.4, fontWeight: 850, marginTop: 3, whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    {group.key === 'scheduled_sessions' && row.session_info && (
                      <div style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 10, padding: '9px 11px', marginTop: 10 }}>
                        <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>Session details</div>
                        <div style={{ color: C.textSecondary, fontSize: 12, lineHeight: 1.5, fontWeight: 750, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{row.session_info}</div>
                      </div>
                    )}
                    {group.key === 'communities' && row.description && (
                      <div style={{ border: `1px solid ${row.demo_only ? C.accentBorder : C.border}`, background: row.demo_only ? C.accentBgSubtle : C.bgAlt, borderRadius: 10, padding: '9px 11px', marginTop: 10 }}>
                        <div style={{ color: row.demo_only ? C.accent : C.textMuted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{row.demo_only ? 'Demo community preview' : 'About'}</div>
                        <div style={{ color: C.textSecondary, fontSize: 12, lineHeight: 1.5, fontWeight: 750 }}>{row.description}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 7, justifyContent: isMobile ? 'stretch' : 'flex-end', flexWrap: 'wrap', borderTop: `1px solid ${C.border}`, paddingTop: 11 }}>
                    {row.demo_only ? (
                      <span role="status" style={{ minHeight: isMobile ? 40 : 34, display: 'inline-flex', alignItems: 'center', border: `1px solid ${C.accentBorder}`, borderRadius: 999, background: C.accentBgSubtle, color: C.accent, padding: '7px 11px', fontSize: 11, fontWeight: 900 }}>
                        Demo-only · no community record created
                      </span>
                    ) : (<>
                    {group.key === 'scheduled_sessions' && (
                      <>
                        <button type="button" aria-label={`Manage players for ${group.getTitle(row)}`} onClick={() => openSessionManager(row)} style={{ minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, border: 'none', borderRadius: 9, background: C.accentGrad, color: '#fff', padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 900 }}>
                          Manage
                        </button>
                        <button type="button" aria-label={`Edit ${group.getTitle(row)}`} onClick={() => openSessionEditor(row)} style={{ minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, border: `1px solid ${C.border}`, borderRadius: 9, background: C.bgCard, color: C.textSecondary, padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 850 }}>
                          Edit
                        </button>
                        {showAppDemoPreview && (
                          <button type="button" aria-label={`Open app preview for ${group.getTitle(row)}`} onClick={() => openAppPreview(row)} style={{ minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, border: `1px solid ${C.accentBorder}`, borderRadius: 9, background: C.accentBgSubtle, color: C.accent, padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 900 }}>
                            App
                          </button>
                        )}
                      </>
                    )}
                    <button type="button" aria-label={`Edit audience for ${group.getTitle(row)}`} onClick={() => startEditActivityAudience(group.key, row, group.getTitle(row))} style={{ minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, border: `1px solid ${C.border}`, borderRadius: 9, background: C.bgElevated, color: C.textSecondary, padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 850 }}>
                      Audience
                    </button>
                    {group.key === 'scheduled_sessions' && (
                      <button type="button" aria-label={`Delete ${group.getTitle(row)}`} onClick={() => deleteScheduledSession(row)} disabled={sessionManagerSaving} style={dangerActionButtonSt(C, { minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, borderRadius: 9, padding: '7px 10px', cursor: sessionManagerSaving ? 'wait' : 'pointer', fontSize: 11, fontWeight: 850 })}>
                        Delete
                      </button>
                    )}
                    {group.key === 'communities' && (
                      <>
                        <button type="button" aria-label={`Edit ${group.getTitle(row)}`} onClick={() => startEditCommunity(row)} style={{ minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, border: `1px solid ${C.border}`, borderRadius: 9, background: C.bgCard, color: C.textSecondary, padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 850 }}>
                          Edit
                        </button>
                      <button type="button" aria-label={`Delete ${group.getTitle(row)}`} onClick={() => deleteCommunity(row)} disabled={deletingCommunityId === row.id} style={dangerActionButtonSt(C, { minHeight: isMobile ? 40 : 34, flex: isMobile ? 1 : undefined, borderRadius: 9, padding: '7px 10px', cursor: deletingCommunityId === row.id ? 'wait' : 'pointer', fontSize: 11, fontWeight: 850 })}>
                        {deletingCommunityId === row.id ? 'Deleting...' : 'Delete'}
                      </button>
                      </>
                    )}
                    </>)}
                  </div>
                </div>
              ))}
              {remaining > 0 && (
                <button type="button" onClick={() => setExpandedContentGroups(current => ({ ...current, [group.key]: !expanded }))} aria-expanded={expanded} style={{ justifySelf: 'start', border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, borderRadius: 9, minHeight: 34, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 850 }}>
                  {expanded ? 'Show less' : `Show ${remaining} more`} <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ marginLeft: 5, fontSize: 10 }} />
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

/* ═══════════════════ SHARED COMPONENTS ═══════════════════ */
const pillColors = (C, tone = 'default') => ({
  success: { bg: 'rgba(5,150,105,0.10)', border: 'rgba(5,150,105,0.24)', color: C.success },
  danger: { bg: 'rgba(220,38,38,0.10)', border: 'rgba(220,38,38,0.24)', color: C.danger },
  warning: { bg: 'rgba(245,158,11,0.11)', border: 'rgba(245,158,11,0.28)', color: C.warn },
  issue: { bg: 'rgba(245,158,11,0.11)', border: 'rgba(245,158,11,0.28)', color: C.warn },
  info: { bg: C.accentBgSubtle, border: C.accentBorder, color: C.accent },
  neutral: { bg: C.bgAlt, border: C.border, color: C.textSecondary },
  default: { bg: C.bgAlt, border: C.border, color: C.textSecondary },
}[tone] || { bg: C.bgAlt, border: C.border, color: C.textSecondary });

const StatusPill = ({ C, children, tone = 'default', style = {} }) => {
  const p = pillColors(C, tone);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 24, borderRadius: 999, padding: '3px 9px', background: p.bg, border: `1px solid ${p.border}`, color: p.color, fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap', ...style }}>
      {children}
    </span>
  );
};

const TxIcon = ({ C, iconKey, tone = 'default', size = 40 }) => {
  const iconMap = { card: faCreditCard, coins: faCoins, refund: faRotateRight, trophy: faTrophy, workout: faDumbbell, calendar: faCalendar, issue: faTriangleExclamation, receipt: faReceipt };
  const p = pillColors(C, tone);
  return (
    <span aria-hidden="true" style={{ width: size, height: size, minWidth: size, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: p.bg, border: `1px solid ${p.border}`, color: p.color }}>
      <FontAwesomeIcon icon={iconMap[iconKey] || faReceipt} style={{ fontSize: Math.round(size * 0.42) }} />
    </span>
  );
};

const SectionFrame = ({ C, id, title, subtitle, action, children, style = {} }) => (
  <section style={card(C, { padding: 0, overflow: 'hidden', boxShadow: C.shadowSm, ...style })} aria-labelledby={id}>
    <div style={{ padding: '16px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <h3 id={id} className="heading" style={{ color: C.text, fontSize: 17, fontWeight: 900, margin: 0 }}>{title}</h3>
        {subtitle && <p style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, lineHeight: 1.45, marginTop: 4 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const EmptyState = ({ C, icon, title, body, action }) => (
  <div style={{ padding: '28px 18px', textAlign: 'center', display: 'grid', justifyItems: 'center', gap: 8 }}>
    <div style={{ width: 44, height: 44, borderRadius: 14, display: 'grid', placeItems: 'center', background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.accent }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: 18 }} />
    </div>
    <div className="heading" style={{ color: C.text, fontSize: 15, fontWeight: 900 }}>{title}</div>
    {body && <p style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.5, maxWidth: 340, margin: 0 }}>{body}</p>}
    {action}
  </div>
);

const SkeletonLine = ({ C, width = '100%', height = 12 }) => (
  <span aria-hidden="true" style={{ display: 'block', width, height, borderRadius: 999, background: C.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(10,29,68,0.08)', animation: 'shimmer 1.2s ease-in-out infinite' }} />
);

const TransactionSkeletons = ({ C, rows = 4 }) => (
  <div aria-hidden="true" style={{ display: 'grid' }}>
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 88px', gap: 12, alignItems: 'center', padding: '14px 18px', borderTop: index ? `1px solid ${C.border}` : 'none' }}>
        <SkeletonLine C={C} width={40} height={40} />
        <div style={{ display: 'grid', gap: 8 }}><SkeletonLine C={C} width="62%" /><SkeletonLine C={C} width="44%" height={10} /></div>
        <SkeletonLine C={C} width={72} />
      </div>
    ))}
  </div>
);

const AccessCard = ({ C, plan, isMobile, subscription, onCancelSubscription }) => {
  const tone = planStatusTone(plan);
  const sourceLabel = planSourceLabel(plan);
  const canCancel = canCancelBillingSubscription(subscription);
  const action = plan.status === 'trialing'
    ? 'Upgrade'
    : tone === 'warning' && plan.expiresAt
      ? 'Renew'
      : plan.productLine === 'Institutions'
        ? 'Manage'
        : 'View details';
  return (
    <article style={{ border: `1px solid ${tone === 'success' ? C.accentBorder : C.border}`, background: tone === 'success' ? C.accentBgSubtle : C.bgAlt, borderRadius: 14, padding: 14, minWidth: 0, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <h4 style={{ color: C.text, fontSize: isMobile ? 14 : 15, fontWeight: 950, lineHeight: 1.2, margin: 0, overflowWrap: 'anywhere' }}>{plan.name}</h4>
          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 850, marginTop: 4 }}>{plan.productLine || 'Access'}</div>
        </div>
        <StatusPill C={C} tone={tone}>{planStatusLabel(plan)}</StatusPill>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ color: C.textSecondary, fontSize: 13, fontWeight: 850 }}>{planDateLabel(plan)}</div>
        <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750 }}>{sourceLabel}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={{ justifySelf: 'start', minHeight: 36, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 11px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>
          {action}
        </button>
        {canCancel && (
          <button
            type="button"
            onClick={() => onCancelSubscription?.(subscription)}
            style={{ justifySelf: 'start', minHeight: 36, borderRadius: 9, border: '1px solid rgba(220,38,38,0.24)', background: 'rgba(220,38,38,0.08)', color: C.danger, padding: '8px 11px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}
          >
            Cancel subscription
          </button>
        )}
      </div>
    </article>
  );
};

const BillingOverviewPanel = ({ C, overview, error, isMobile, loading, profileData, onRetry, onCancelSubscription }) => {
  const [showAllAccess, setShowAllAccess] = useState(false);
  const plans = sortAccessPlans(getCurrentBillingPlans(overview));
  const visiblePlans = showAllAccess ? plans : plans.slice(0, 3);
  const extraPlans = Math.max(0, plans.length - visiblePlans.length);
  const recentPayments = (overview.payments || []).filter((payment) => !payment.transaction_id).slice(0, 5);
  const recentCoins = (overview.coinLedger || []).filter((entry) => !entry.transaction_id).slice(0, 5);
  const hasCoinPurchases = (overview.payments || []).some(payment => Number(payment?.coins_amount || 0) > 0);
  const trialPlan = plans.find(plan => plan.status === 'trialing');
  const expiringPlan = plans.find(plan => plan.expiresAt && new Date(plan.expiresAt).getTime() - Date.now() <= 14 * 86400000);
  const hasInstitution = plans.some(plan => plan.productLine === 'Institutions');
  const accessCta = trialPlan ? 'Upgrade before trial ends' : expiringPlan ? 'Renew access' : hasInstitution ? 'Manage team' : null;
  const coinCta = hasCoinPurchases || Number(profileData?.proam_coins || 0) === 0 ? 'Buy coins' : null;
  const subscriptionByPlanId = new Map((overview.subscriptions || []).map(row => [`sub:${row.id}`, row]));

  if (loading) {
    return (
      <div aria-busy="true" style={{ display: 'grid', gap: 14, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,1fr)', gap: 10 }}>
          {Array.from({ length: isMobile ? 3 : 4 }).map((_, i) => <div key={i} style={card(C, { padding: 14, boxShadow: C.shadowSm })}><SkeletonLine C={C} width="48%" /><div style={{ marginTop: 14 }}><SkeletonLine C={C} width="72%" height={18} /></div></div>)}
        </div>
        <SectionFrame C={C} id="access-loading" title="Current access"><TransactionSkeletons C={C} rows={3} /></SectionFrame>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
          <SectionFrame C={C} id="billing-loading" title="Billing summary"><TransactionSkeletons C={C} rows={3} /></SectionFrame>
          <SectionFrame C={C} id="coins-loading" title="Coin activity"><TransactionSkeletons C={C} rows={3} /></SectionFrame>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14, marginBottom: 16 }}>
      {error && (
        <div role="alert" style={{ border: `1px solid ${C.warn}55`, background: 'rgba(245,158,11,0.10)', color: C.text, borderRadius: 14, padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TxIcon C={C} iconKey="issue" tone="warning" size={36} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="heading" style={{ fontSize: 15, fontWeight: 900 }}>Transactions could not load</div>
            <div style={{ color: C.textSecondary, fontSize: 13, fontWeight: 750, marginTop: 3 }}>Refresh the page or contact support if this keeps happening.</div>
          </div>
          <button type="button" onClick={onRetry} style={{ minHeight: 40, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.text, padding: '9px 12px', fontWeight: 900, cursor: 'pointer' }}>Try again</button>
          <a href={`mailto:support@snowsproam.com?subject=Transaction%20support%20request&body=Username%3A%20%40${encodeURIComponent(profileData?.username || '')}%0AEmail%3A%20${encodeURIComponent(profileData?.email || '')}%0AIssue%3A`} style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '9px 12px', fontWeight: 900, fontSize: 13 }}>Contact support</a>
        </div>
      )}

      <SectionFrame
        C={C}
        id="current-access-heading"
        title="Current access"
        subtitle="Your active subscriptions, unlocks, trials, and manual access."
        action={<StatusPill C={C} tone={plans.length ? 'success' : 'neutral'}>{plans.length} active</StatusPill>}
      >
        <div style={{ padding: 18 }}>
          {plans.length === 0 ? (
            <EmptyState C={C} icon={faLock} title="No active access" />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(230px,1fr))', gap: 10 }}>
                {visiblePlans.map((plan) => (
                  <AccessCard
                    key={plan.id}
                    C={C}
                    plan={plan}
                    isMobile={isMobile}
                    subscription={subscriptionByPlanId.get(plan.id)}
                    onCancelSubscription={onCancelSubscription}
                  />
                ))}
              </div>
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {extraPlans > 0 || showAllAccess ? (
                  <button type="button" onClick={() => setShowAllAccess(open => !open)} style={{ minHeight: 40, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, padding: '9px 12px', fontWeight: 900, cursor: 'pointer' }}>
                    {showAllAccess ? 'Show less access' : `Show all access (${plans.length})`}
                  </button>
                ) : <span />}
                {accessCta && <button type="button" style={{ minHeight: 40, border: 'none', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '9px 13px', fontWeight: 900, cursor: 'pointer' }}>{accessCta}</button>}
              </div>
            </>
          )}
        </div>
      </SectionFrame>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
        <SectionFrame C={C} id="billing-summary-heading" title="Billing summary" subtitle="Latest card payments and coin purchases." action={recentPayments.length > 5 ? <button type="button" style={tagSt(C, true)}>View all billing</button> : null}>
          {recentPayments.length === 0 ? (
            <EmptyState C={C} icon={faCreditCard} title="No billing yet" body="Card payments and coin purchases will appear here." action={<button type="button" style={{ ...tagSt(C, true), minHeight: 40, cursor: 'pointer', marginTop: 6 }}>View plans</button>} />
          ) : (
            <div style={{ display: 'grid' }}>
              {recentPayments.map((payment, index) => (
                <div key={payment.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '13px 18px', borderTop: index ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{payment?.billing_products?.name || 'Payment'}</div>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, marginTop: 3 }}>{planSourceLabel({ source: payment.provider, interval: payment?.billing_prices?.interval })} · {fmtDate(payment.created_at)}</div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'grid', justifyItems: 'end', gap: 5 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 950 }}>{paymentAmountLabel(payment)}</div>
                    <StatusPill C={C} tone={paymentTone(payment)}>{paymentStatusLabel(payment)}</StatusPill>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionFrame>

        <SectionFrame C={C} id="coin-activity-heading" title="Coin activity" subtitle="Unlocks, refunds, redemptions, and purchases." action={coinCta ? <button type="button" style={{ ...tagSt(C, true), minHeight: 40, cursor: 'pointer' }}>{coinCta}</button> : null}>
          {recentCoins.length === 0 ? (
            <EmptyState C={C} icon={faCoins} title="No coin activity yet" body="Coin unlocks, refunds, and redemptions will appear here." action={<button type="button" style={{ ...tagSt(C, true), minHeight: 40, cursor: 'pointer', marginTop: 6 }}>Buy coins</button>} />
          ) : (
            <div style={{ display: 'grid' }}>
              {recentCoins.map((entry, index) => {
                const issue = isTransactionIssue(entry);
                const delta = Number(entry.delta || 0);
                return (
                  <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '13px 18px', borderTop: index ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue ? 'Transaction issue - contact support' : sanitizeTransactionDescription(entry.reason) || entry?.billing_products?.name || humanizeToken(entry.source, 'Coin activity')}</div>
                      <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, marginTop: 3 }}>{humanizeToken(entry.source, 'Coins')} · {fmtDate(entry.created_at)}</div>
                    </div>
                    <div style={{ textAlign: 'right', display: 'grid', justifyItems: 'end', gap: 5 }}>
                      <div style={{ color: delta > 0 ? C.success : C.text, fontSize: 13, fontWeight: 950 }}>{delta > 0 ? '+' : ''}{formatTxCoins(delta)}</div>
                      {issue && <StatusPill C={C} tone="issue">Issue</StatusPill>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionFrame>
      </div>
    </div>
  );
};

const Bar = ({ C, p, height = 5, fill = null, track = null, border = null }) => (
  <div style={{ height, borderRadius: 99, background: track || C.bgAlt, border: border === null ? `1px solid ${C.border}` : border, overflow: 'hidden' }}>
    <div style={{ height: '100%', width: `${p}%`, background: fill || C.accentGrad, borderRadius: 99, transition: 'width 0.6s ease' }} />
  </div>
);

const SportIcon = ({ C, iconUrl, sportId, size = 26, style = {} }) => (
  <span style={{ width: size, minWidth: size, maxWidth: size, height: size, minHeight: size, maxHeight: size, borderRadius: Math.max(6, Math.round(size * 0.28)), background: 'rgba(255,255,255,0.14)', border: `1px solid ${C?.border || 'rgba(255,255,255,0.18)'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flex: '0 0 auto', color: C?.text || '#fff', fontSize: Math.max(10, Math.round(size * 0.42)), fontWeight: 900, lineHeight: 1, ...style }}>
    {iconUrl ? (
      <img src={iconUrl} alt={sportId || 'Sport'} style={{ width: '100%', minWidth: '100%', height: '100%', minHeight: '100%', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block' }} loading="lazy" />
    ) : (
      <span>{String(sportId || '?').slice(0, 1).toUpperCase()}</span>
    )}
  </span>
);

const TeamImageStack = ({ C, team, size = 44 }) => {
  const members = team.team_members || team.user_teams?.map(row => row.profiles).filter(Boolean) || [];
  if (team.team_type === 'individual' && members.length) {
    const visibleMembers = members.slice(0, 4);
    const stackWidth = size + Math.max(0, visibleMembers.length - 1) * Math.round(size * 0.55);
    return (
      <div style={{ display: 'flex', alignItems: 'center', width: stackWidth, minWidth: stackWidth, height: size, minHeight: size, flex: '0 0 auto', overflow: 'visible' }}>
        {visibleMembers.map((member, index) => (
          <img
            key={member.id || index}
            src={member.profile_img || noImage}
            alt={member.username || member.name || 'Team member'}
            style={{ width: size, minWidth: size, maxWidth: size, height: size, minHeight: size, maxHeight: size, aspectRatio: '1 / 1', borderRadius: 999, objectFit: 'cover', display: 'block', flex: '0 0 auto', border: `2px solid ${C.bgCard}`, marginLeft: index === 0 ? 0 : -Math.round(size * 0.45), zIndex: 10 - index, background: C.bgAlt }}
            loading="lazy"
          />
        ))}
      </div>
    );
  }

  return (
    <img
      src={team.image_url || noImage}
      alt={team.team_name || 'Team'}
      style={{ width: size, minWidth: size, maxWidth: size, height: size, minHeight: size, maxHeight: size, aspectRatio: '1 / 1', borderRadius: 12, objectFit: 'cover', display: 'block', flex: '0 0 auto', border: `1px solid ${C.border}`, background: C.bgAlt }}
      loading="lazy"
    />
  );
};

const getFeedbackOption = (options = [], value) => options.find(option => Number(option.value) === Number(value));

const WGFeedbackSummaryRow = ({ label, value, options, color = '#0C69C8' }) => {
  const selected = getFeedbackOption(options, value);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 10, border: '1px solid #E0E7EF', background: '#FFFFFF', borderRadius: 14, padding: '10px 12px' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: '#64748B', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
        <div style={{ color: '#0D275B', fontSize: 13, fontWeight: 950, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected?.label || 'Not logged'}</div>
      </div>
      <span style={{ minWidth: 42, height: 42, borderRadius: 16, background: selected ? color : '#F1F5F9', color: selected ? '#fff' : '#94A3B8', display: 'inline-grid', placeItems: 'center', fontSize: 20, border: `1px solid ${selected ? color : '#E2E8F0'}` }}>
        {selected?.emoji || '-'}
      </span>
    </div>
  );
};

const WGAppIcon = ({ icon, color = '#0C69C8', bg = '#E6F0FF', size = 38 }) => (
  <span style={{ width: size, height: size, borderRadius: Math.round(size * 0.38), background: bg, color, display: 'inline-grid', placeItems: 'center', flexShrink: 0, border: '1px solid rgba(12,105,200,0.08)' }}>
    <FontAwesomeIcon icon={icon} style={{ fontSize: Math.max(12, Math.round(size * 0.42)) }} />
  </span>
);

const WGAppDecor = ({ color = '#0C69C8', icon = null }) => (
  <>
    <span aria-hidden="true" style={{ position: 'absolute', right: -20, top: -24, width: 116, height: 88, borderRadius: 34, background: color, opacity: 0.08, transform: 'rotate(-18deg)' }} />
    {icon && (
      <FontAwesomeIcon aria-hidden="true" icon={icon} style={{ position: 'absolute', right: -12, bottom: -14, color, opacity: 0.055, fontSize: 58 }} />
    )}
  </>
);

const WGAppMetricCard = ({ icon, label, value, color = '#0C69C8', bg = '#E6F0FF', wide = false }) => (
  <div style={{ position: 'relative', minHeight: wide ? 132 : 122, border: '1px solid #E0E7EF', background: '#FFFFFF', borderRadius: 20, padding: 14, overflow: 'hidden', display: 'grid', alignContent: 'space-between' }}>
    <WGAppDecor color={color} icon={icon} />
    <WGAppIcon icon={icon} color={color} bg={bg} size={42} />
    <div style={{ position: 'relative' }}>
      <div style={{ color: '#0D275B', fontSize: 28, lineHeight: 1, fontWeight: 950 }}>{value}</div>
      <div style={{ color: '#64748B', fontSize: 12, lineHeight: 1.2, fontWeight: 950, textTransform: 'uppercase', marginTop: 7 }}>{label}</div>
    </div>
  </div>
);

const WGNutritionTile = ({ icon, label, value, sub, color = '#D97706', bg = '#FFF7ED', emphasis = false }) => (
  <div style={{ position: 'relative', minHeight: emphasis ? 132 : 118, border: `1px solid ${emphasis ? '#FED7AA' : '#E0E7EF'}`, background: emphasis ? '#FFFBEB' : '#FFFFFF', borderRadius: 18, padding: 13, overflow: 'hidden', display: 'grid', alignContent: 'space-between', gap: 10 }}>
    <WGAppDecor color={color} icon={icon} />
    <WGAppIcon icon={icon} color={color} bg={bg} size={38} />
    <div style={{ position: 'relative', minWidth: 0 }}>
      <div style={{ color: '#0D275B', fontSize: 20, lineHeight: 1.05, fontWeight: 950, overflowWrap: 'anywhere' }}>{value}</div>
      <div style={{ color: '#64748B', fontSize: 10, lineHeight: 1.2, fontWeight: 950, textTransform: 'uppercase', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ color: '#64748B', fontSize: 11, lineHeight: 1.35, fontWeight: 800, marginTop: 5 }}>{sub}</div>}
    </div>
  </div>
);

const WGNutritionMacroRow = ({ row }) => {
  const target = safeWorkoutGroupNutritionNumber(row?.target, NaN);
  const hasTarget = Number.isFinite(target) && target > 0;
  const pct = hasTarget ? Math.min(100, Math.round((safeWorkoutGroupNutritionNumber(row?.value) / target) * 100)) : 0;
  return (
    <div style={{ border: '1px solid #E0E7EF', background: '#FFFFFF', borderRadius: 14, padding: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <WGAppIcon icon={row.icon} color={row.color} bg={`${row.color}14`} size={30} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#0D275B', fontSize: 12, fontWeight: 950 }}>{row.label}</div>
            <div style={{ color: '#64748B', fontSize: 11, fontWeight: 800, marginTop: 2 }}>
              {formatWorkoutGroupNutritionNumber(row.value)}{row.unit === 'kcal' ? ' kcal' : row.unit}
              {hasTarget ? ` / ${formatWorkoutGroupNutritionNumber(target)}${row.unit === 'kcal' ? ' kcal' : row.unit}` : ' average'}
            </div>
          </div>
        </div>
        <span style={{ color: row.status.color, background: row.status.bg, borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 950, whiteSpace: 'nowrap' }}>{row.status.label}</span>
      </div>
      {hasTarget && (
        <div style={{ height: 7, background: '#E2E8F0', borderRadius: 999, overflow: 'hidden', marginTop: 10 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: row.status.color, borderRadius: 999 }} />
        </div>
      )}
    </div>
  );
};

const WGNutritionMealRow = ({ meal }) => (
  <div style={{ border: `1px solid ${meal.meta.border}`, background: meal.count ? meal.meta.bg : '#FFFFFF', borderRadius: 14, padding: 11, display: 'grid', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: meal.meta.color, fontSize: 12, fontWeight: 950 }}>
        <WGAppIcon icon={meal.meta.icon} color={meal.meta.color} bg="#FFFFFF" size={28} />
        {meal.meal}
      </span>
      <span style={{ color: '#0D275B', fontSize: 12, fontWeight: 950 }}>{meal.count} log{meal.count === 1 ? '' : 's'}</span>
    </div>
    <div style={{ color: '#64748B', fontSize: 11, fontWeight: 800 }}>
      {meal.athletes} athlete{meal.athletes === 1 ? '' : 's'} · avg {formatWorkoutGroupNutritionNumber(meal.avgCalories)} kcal
    </div>
  </div>
);

const getWGTrendMetricUnit = (metric, value) => {
  const count = Math.round(Number(value || 0));
  if (metric === 'sessions') return count === 1 ? 'session' : 'sessions';
  if (metric === 'totalReps') return count === 1 ? 'rep' : 'reps';
  if (metric === 'totalSets') return count === 1 ? 'set' : 'sets';
  if (metric === 'totalVolume') return 'volume';
  return '';
};

const formatWGTrendRangeLabel = (rows = []) => {
  const first = parseWorkoutGroupLocalDateKey(rows[0]?.key);
  const last = parseWorkoutGroupLocalDateKey(rows[rows.length - 1]?.key);
  const firstLabel = formatWorkoutGroupTrendDate(first);
  const lastLabel = formatWorkoutGroupTrendDate(last);
  if (!firstLabel && !lastLabel) return '';
  if (!lastLabel || firstLabel === lastLabel) return firstLabel;
  return `${firstLabel} - ${lastLabel}`;
};

const WGTrendBars = ({ rows = [], metric = 'sessions', color = '#0C69C8', height = 126 }) => {
  const values = rows.map(row => Number(row?.[metric] || 0));
  const max = Math.max(...values, 1);
  const hasValues = values.some(value => value > 0);
  const useLineTrend = rows.length > 7;
  if (useLineTrend) {
    const chartHeight = Math.max(72, height - 44);
    const chartWidth = 320;
    const padX = 10;
    const padY = 10;
    const usableWidth = chartWidth - padX * 2;
    const usableHeight = chartHeight - padY * 2;
    const points = values.map((value, index) => ({
      value,
      x: values.length <= 1 ? chartWidth / 2 : padX + (index / (values.length - 1)) * usableWidth,
      y: chartHeight - padY - (value / max) * usableHeight,
    }));
    const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
    const peak = points.reduce((best, point) => point.value > best.value ? point : best, { x: 0, y: 0, value: 0 });
    const latest = points[points.length - 1] || null;
    const metricUnit = getWGTrendMetricUnit(metric, peak.value);
    return (
      <div role="img" aria-label={`${metric} trend for ${formatWGTrendRangeLabel(rows) || 'selected range'}`} style={{ minHeight: height, display: 'grid', alignContent: 'center', gap: 7, paddingTop: 10 }}>
        {!hasValues ? (
          <>
            <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none" aria-hidden="true">
              <path d={`M ${padX} ${chartHeight - padY} L ${chartWidth - padX} ${chartHeight - padY}`} stroke="#DDE7F6" strokeWidth="2" strokeLinecap="round" fill="none" />
            </svg>
            <div style={{ textAlign: 'center', color: '#64748B', fontSize: 11, fontWeight: 850 }}>No completed sessions on chart dates.</div>
          </>
        ) : (
          <>
            <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none" aria-hidden="true">
              <path d={`M ${padX} ${chartHeight - padY} L ${chartWidth - padX} ${chartHeight - padY}`} stroke="#DDE7F6" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d={path} stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" vectorEffect="non-scaling-stroke" />
              {peak.value > 0 && <circle cx={peak.x} cy={peak.y} r="4.5" fill="#FFFFFF" stroke={color} strokeWidth="3" vectorEffect="non-scaling-stroke" />}
              {latest?.value > 0 && <circle cx={latest.x} cy={latest.y} r="4" fill={color} />}
            </svg>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', color: '#64748B', fontSize: 10, fontWeight: 900 }}>
              <span>{formatWGTrendRangeLabel(rows)}</span>
              <span style={{ color }}>{`Peak ${compactWorkoutGroupNumber(peak.value)}${metricUnit ? ` ${metricUnit}` : ''}`}</span>
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <div style={{ minHeight: height, display: 'grid', gap: 7, paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7, minHeight: Math.max(70, height - (hasValues ? 12 : 30)) }}>
        {rows.map((row, index) => {
        const value = Number(row?.[metric] || 0);
        const barHeight = value > 0 ? Math.max(12, Math.round((value / max) * (height - 52))) : 5;
        return (
          <div key={row.key || index} style={{ flex: 1, minWidth: 0, display: 'grid', justifyItems: 'center', alignItems: 'end', gap: 5 }}>
            <div style={{ color: value > 0 ? '#0D275B' : '#94A3B8', fontSize: 10, fontWeight: 950, minHeight: 12, width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {compactWorkoutGroupNumber(value)}
            </div>
            <div style={{ width: '66%', maxWidth: 34, height: barHeight, borderRadius: 10, background: value > 0 ? color : '#E8EEF8' }} />
            <div style={{ color: '#64748B', fontSize: 10, fontWeight: 900, width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.label}</div>
          </div>
        );
        })}
      </div>
      {!hasValues && <div style={{ textAlign: 'center', color: '#64748B', fontSize: 11, fontWeight: 850 }}>No completed sessions on chart dates.</div>}
    </div>
  );
};

const clubTheme = (group, C, themePreferences = null) => {
  const club = group?.workout_clubs || null;
  const palette = getReadableClubPalette(club || {}, C);
  if (themePreferences) {
    const selectedClubId = themePreferences.theme_workout_groups_club_id;
    const enabled = themePreferences.theme_workout_groups_enabled === true;
    if (club?.id && enabled && selectedClubId && String(club.id) === String(selectedClubId) && club?.brand_settings?.theme_workout_groups === true) {
      return {
        club,
        ...palette,
        primary: palette.primary,
        secondary: palette.secondary,
        accent: palette.accent,
        enabled: true,
      };
    }
    return {
      club: null,
      primary: C.accent,
      secondary: '#0A1D44',
      accent: C.accent,
      enabled: false,
    };
  }
  return {
    club,
    ...palette,
    primary: palette.primary,
    secondary: palette.secondary,
    accent: palette.accent,
    enabled: !!club,
  };
};
const workoutGroupModalTheme = (group, C, themePreferences = null) => {
  const t = clubTheme(group, C, themePreferences);
  if (!t.enabled) return C;
  const primary = t.primary || C.accent;
  const secondary = t.surfaceSecondary || t.secondary || C.accentDark || '#0A1D44';
  const accent = t.accent || primary;
  return {
    ...C,
    accent: primary,
    accentDark: secondary,
    accentLight: primary,
    accentGrad: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
    accentGlow: rgba(primary, C.mode === 'dark' ? 0.35 : 0.25),
    accentBgSubtle: rgba(primary, C.mode === 'dark' ? 0.16 : 0.06),
    accentBgLight: rgba(primary, C.mode === 'dark' ? 0.22 : 0.10),
    accentBorder: rgba(accent, C.mode === 'dark' ? 0.42 : 0.24),
  };
};
const leaderboardRankColor = (branding, rank) => {
  if (LEADERBOARD_MEDAL_COLORS[rank]) return LEADERBOARD_MEDAL_COLORS[rank];
  const settings = { ...DEFAULT_APP_LEADERBOARD_SETTINGS, ...(branding || {}) };
  return normalizeHexInput(settings.primaryColor, DEFAULT_APP_LEADERBOARD_SETTINGS.primaryColor);
};

const RankBadge = ({ C, rank, branding = null }) => {
  const cols = {
    1: { bg: 'rgba(212,175,55,0.14)', border: 'rgba(212,175,55,0.42)', c: LEADERBOARD_MEDAL_COLORS[1] },
    2: { bg: 'rgba(143,152,168,0.18)', border: 'rgba(143,152,168,0.52)', c: LEADERBOARD_MEDAL_COLORS[2] },
    3: { bg: 'rgba(205,127,50,0.14)', border: 'rgba(205,127,50,0.42)', c: LEADERBOARD_MEDAL_COLORS[3] },
  };
  const brandedColor = branding ? leaderboardRankColor(branding, rank) : null;
  const s = brandedColor
    ? { bg: rgba(brandedColor, rank <= 3 ? 0.12 : 0.06), border: rgba(brandedColor, rank <= 3 ? 0.35 : 0.18), c: brandedColor }
    : cols[rank] || { bg: C.bgAlt, border: C.border, c: C.textMuted };
  return (
    <div style={{ width: 30, height: 30, borderRadius: 8, background: s.bg, border: `1px solid ${s.border}`, color: s.c, fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'Sora',sans-serif" }}>{rank}</div>
  );
};

const BtnPrimary = ({ C, onClick, disabled, children, style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{ background: disabled ? C.textMuted : C.accentGrad, border: 'none', color: '#fff', padding: '12px 20px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", opacity: disabled ? 0.5 : 1, minHeight: 44, ...style }}>{children}</button>
);

const BtnSec = ({ C, onClick, children }) => (
  <button onClick={onClick} style={{ background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textSecondary, padding: '12px 20px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", minHeight: 44 }}>{children}</button>
);

const FormRow = ({ C, label, hint, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary }}>{label}</label>
    {children}
    {hint ? <div style={{ color: C.textMuted, fontSize: 11.5, lineHeight: 1.4 }}>{hint}</div> : null}
  </div>
);

/* ═══════════════════ SPORT PROFILE CARD ═══════════════════ */
const SportProfileCard = ({ C, sport }) => {
  const sportIconUrl = sport.sport_icon_url || sport.sports?.icon_url || null;
  const rankColors = {
    1: { bg: 'rgba(212,160,23,0.10)', border: 'rgba(212,160,23,0.30)', text: '#D4A017' },
    2: { bg: 'rgba(160,174,192,0.10)', border: 'rgba(160,174,192,0.30)', text: '#A0AEC0' },
  };
  const getRankStyle = (rank) => (rank <= 2 && rank > 0) ? rankColors[rank] : { bg: C.accentBgSubtle, border: C.accentBorder, text: C.accent };

  const seasonSteps = (() => {
    if (!sport.season_steps) return [];
    try {
      const obj = typeof sport.season_steps === 'string' ? JSON.parse(sport.season_steps) : sport.season_steps;
      return Object.entries(obj).map(([k, v]) => {
        const value = v && typeof v === 'object' ? v : { steps: v };
        return {
          season: k,
          rank: value.display_rank ?? value.dense_rank ?? value.rank,
          steps: value.steps,
          tied: Boolean(value.tied),
          tieGroupSize: Number(value.tie_group_size || 0),
        };
      });
    } catch { return []; }
  })();

  const statItems = [
    { label: 'League', val: sport.league ? `League ${sport.league}` : '—', icon: '🏆' },
    { label: 'Rating', val: sport.rating ?? '—', icon: '⭐' },
    { label: 'Trophies', val: sport.trophies?.toLocaleString() ?? '—', icon: '🥇' },
    { label: 'Regional Rank', val: sport.regional_rank > 0 ? `#${sport.regional_rank}` : '—', icon: '📍' },
    { label: 'National Rank', val: sport.national_rank > 0 ? `#${sport.national_rank}` : '—', icon: '🏳️' },
    { label: 'Global Rank', val: sport.global_rank > 0 ? `#${sport.global_rank}` : '—', icon: '🌍' },
    String(sport.sport_id || '').toLowerCase() === 'boxing' && sport.stance ? { label: 'Stance', val: sport.stance, icon: '🥊' } : null,
    sport.position ? { label: 'Position', val: sport.position, icon: '📌' } : null,
    sport.club ? { label: 'Club', val: sport.club, icon: '🏟️' } : null,
  ].filter(Boolean);

  return (
    <div className="card-lift" style={card(C, { overflow: 'hidden', display: 'flex', flexDirection: 'column' })}>
      <div style={{ background: C.accentGrad, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, position: 'relative', overflow: 'hidden' }}>
        {sportIconUrl && <img src={sportIconUrl} alt="" aria-hidden="true" style={{ position: 'absolute', right: -18, top: -18, width: 92, height: 92, objectFit: 'contain', opacity: 0.12, filter: 'grayscale(1) brightness(2)' }} loading="lazy" />}
        <SportIcon C={C} iconUrl={sportIconUrl} sportId={sport.sport_id} size={52} style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff', backdropFilter: 'blur(8px)' }} />
        <div>
          <div className="heading" style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: -0.3 }}>{sport.sport_id}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Sport Profile</div>
        </div>
        {sport.coach_access && (
          <div style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 99, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', fontSize: 11, fontWeight: 700, color: '#fff' }}>Coach Access ✓</div>
        )}
      </div>

      <div style={{ padding: '16px 18px', flex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
          {statItems.slice(0, 3).map(s => (
            <div key={s.label} style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
              <div className="heading" style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{s.val}</div>
              <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 14 }}>
          {statItems.slice(3, 6).map(s => {
            const rankNum = s.val !== '—' ? parseInt(s.val.replace('#', '')) : null;
            const rs = rankNum ? getRankStyle(rankNum) : null;
            return (
              <div key={s.label} style={{ padding: '8px 10px', borderRadius: 8, background: rs ? rs.bg : C.bgAlt, border: `1px solid ${rs ? rs.border : C.border}`, textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: rs ? rs.text : C.textMuted, fontFamily: "'Sora',sans-serif" }}>{s.val}</div>
                <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>{s.icon} {s.label.replace(' Rank', '')}</div>
              </div>
            );
          })}
        </div>

        {statItems.slice(6).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {statItems.slice(6).map(s => (
              <div key={s.label} style={{ ...tagSt(C), gap: 5, padding: '5px 10px' }}>
                <span>{s.icon}</span>
                <span style={{ color: C.textSecondary }}>{s.label}:</span>
                <span style={{ color: C.text, fontWeight: 700 }}>{s.val}</span>
              </div>
            ))}
          </div>
        )}

        {seasonSteps.length > 0 && (
          <div style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>📅 Season History</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {seasonSteps.map(s => (
                <div key={s.season} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: C.textSecondary, fontWeight: 600 }}>{s.season}</span>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.textMuted }}>{(s.steps || 0).toLocaleString()} steps</span>
                    {s.rank && (
                      <span style={{ ...tagSt(C, true), padding: '2px 8px', fontSize: 11 }}>
                        {s.tied ? `T-${s.rank}` : `#${s.rank}`}{s.tied && s.tieGroupSize > 1 ? ` · ${s.tieGroupSize} tied` : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════ TEAM CARD ═══════════════════ */
const TeamCard = ({ C, team }) => {
  const sportIconUrl = team.sport_icon_url || team.sports?.icon_url || null;
  const leagueBg = 'linear-gradient(135deg,#0A1D44 0%,#1a3a6e 100%)';
  return (
    <div className="card-lift" style={card(C, { overflow: 'hidden' })}>
      <div style={{ height: 70, background: leagueBg, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Ccircle cx='30' cy='30' r='20'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")", backgroundSize: 60 }} />
        {team.team_type === 'group' && team.image_url && <img src={team.image_url} alt={team.team_name} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }} />}
        <div style={{ position: 'absolute', top: 8, right: 10, opacity: 0.75 }}>
          <SportIcon C={C} iconUrl={sportIconUrl} sportId={team.sport_id} size={28} style={{ background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.24)', color: '#fff' }} />
        </div>
        {team.league != null && (
          <div style={{ position: 'absolute', top: 8, left: 10, padding: '3px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', fontSize: 11, fontWeight: 700, color: '#fff' }}>League {team.league}</div>
        )}
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <TeamImageStack C={C} team={team} size={46} />
            <div style={{ minWidth: 0 }}>
              <div className="heading" style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.team_name}</div>
              <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}><SportIcon C={C} iconUrl={sportIconUrl} sportId={team.sport_id} size={18} style={{ background: C.bgAlt }} /> {team.sport_id} · {team.team_type === 'individual' ? 'Individual' : 'Team'}</div>
            </div>
          </div>
          {team.trophies != null && (
            <div style={{ textAlign: 'right' }}>
              <div className="heading" style={{ fontSize: 18, fontWeight: 800, color: C.gold || '#D4A017' }}>🏆 {team.trophies.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>trophies</div>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
          {[
            { label: 'Regional', val: team.regional_rank > 0 ? `#${team.regional_rank}` : '—', icon: '📍' },
            { label: 'National', val: team.national_rank > 0 ? `#${team.national_rank}` : '—', icon: '🏳️' },
            { label: 'Global',   val: team.global_rank > 0   ? `#${team.global_rank}`   : '—', icon: '🌍' },
          ].map(r => {
            const num = r.val !== '—' ? parseInt(r.val.replace('#', '')) : null;
            const isTop = num && num <= 3;
            return (
              <div key={r.label} style={{ textAlign: 'center', padding: '8px 6px', borderRadius: 8, background: isTop ? 'rgba(212,160,23,0.08)' : C.bgAlt, border: `1px solid ${isTop ? 'rgba(212,160,23,0.25)' : C.border}` }}>
                <div className="heading" style={{ fontSize: 14, fontWeight: 800, color: isTop ? '#D4A017' : C.text }}>{r.val}</div>
                <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 2 }}>{r.icon} {r.label}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {team.rating != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 12, color: C.textMuted }}>Rating</span>
              <span className="heading" style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>⭐ {team.rating}</span>
            </div>
          )}
          <div style={{ fontSize: 11, color: C.textMuted }}>{fmtDate(team.created_at)}</div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ ACHIEVEMENT CARD ═══════════════════ */
const AchievementCard = ({ C, achievement, earned, completedAt }) => {
  const isEarned = !!earned;
  return (
    <div className={`card-lift ${isEarned ? 'achiev-earned' : 'achiev-locked'}`}
      style={card(C, { padding: 0, overflow: 'hidden', borderColor: isEarned ? 'rgba(212,160,23,0.4)' : C.border, position: 'relative' })}>
      {isEarned && (
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, width: 22, height: 22, borderRadius: 99, background: 'linear-gradient(135deg,#D4A017,#f0c040)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>✓</div>
      )}
      <div style={{ padding: '16px 16px 12px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: 14, overflow: 'hidden', margin: '0 auto 10px', border: `2px solid ${isEarned ? 'rgba(212,160,23,0.5)' : C.border}`, background: C.bgAlt }}>
          <img src={achievement.image_url} alt={achievement.key} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        </div>
        <div className="heading" style={{ fontSize: 12, fontWeight: 700, color: isEarned ? C.text : C.textMuted, marginBottom: 4, lineHeight: 1.3 }}>
          {achievement.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.4, marginBottom: 8 }}>{achievement.description}</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ ...tagSt(C, isEarned), fontSize: 10, padding: '3px 8px' }}>{achievement.sport_id}</span>
          <span style={{ ...tagSt(C), fontSize: 10, padding: '3px 8px' }}>🎯 {achievement.target_value?.toLocaleString()}</span>
        </div>
        {isEarned && completedAt && (
          <div style={{ marginTop: 8, fontSize: 10, color: C.gold || '#D4A017', fontWeight: 600 }}>✨ Earned {fmtDate(completedAt)}</div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════ MATCH CARD ═══════════════════ */
const MatchCard = ({ C, match, myId, location, sportIconMap = {} }) => {
  const isP1 = match.profile1_id === myId;
  const myPoints = isP1 ? match.profile1_points : match.profile2_points;
  const oppPoints = isP1 ? match.profile2_points : match.profile1_points;
  const myPrevT = isP1 ? match.profile1_prev_trophies : match.profile2_prev_trophies;
  const myNewT  = isP1 ? match.profile1_new_trophies  : match.profile2_new_trophies;
  const isCompetitive = match.mode2 === 'competitive';
  const won = myPoints > oppPoints;
  const draw = myPoints === oppPoints;
  const result = draw ? 'draw' : won ? 'win' : 'loss';
  const trophyDiff = (myNewT != null && myPrevT != null) ? myNewT - myPrevT : null;

  const resultConfig = {
    win:  { label: 'WIN',  bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.30)', color: '#34D399', bar: '#34D399' },
    loss: { label: 'LOSS', bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.30)',  color: '#EF4444', bar: '#EF4444' },
    draw: { label: 'DRAW', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', color: '#F59E0B', bar: '#F59E0B' },
  };
  const rc = resultConfig[result];

  const duration = (() => {
    if (!match.match_started_at || !match.match_finished_at) return null;
    const diff = Math.round((new Date(match.match_finished_at) - new Date(match.match_started_at)) / 60000);
    return diff > 0 ? `${diff}m` : null;
  })();

  return (
    <div className="card-lift" style={{ background: C.bgCard, border: `1px solid ${rc.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: C.shadowMd, display: 'flex', flexDirection: 'column' }}>
      <div className="match-banner" style={{ position: 'relative', height: 160, overflow: 'hidden', flexShrink: 0 }}>
        <img src={location?.image_url || noImage} alt={location?.name || 'Location'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.65) 100%)' }} />
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 800, background: rc.bg, border: `1px solid ${rc.border}`, color: rc.color, fontFamily: "'Sora',sans-serif", letterSpacing: 0.5, backdropFilter: 'blur(6px)' }}>{rc.label}</span>
        </div>
        <div style={{ position: 'absolute', top: 8, left: 8 }}>
          <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: isCompetitive ? 'rgba(139,92,246,0.75)' : 'rgba(12,105,200,0.75)', border: `1px solid ${isCompetitive ? 'rgba(139,92,246,0.5)' : 'rgba(12,105,200,0.5)'}`, color: '#fff', backdropFilter: 'blur(6px)' }}>
            {isCompetitive ? 'Competitive' : 'Casual'}
          </span>
        </div>
        {location?.name && (
          <div style={{ position: 'absolute', bottom: 8, left: 10, right: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>📍</span> {location.name}
            </div>
          </div>
        )}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: rc.bar }} />
      </div>
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <SportIcon C={C} iconUrl={sportIconMap[match.sport_id]?.icon_url} sportId={match.sport_id} size={22} style={{ background: C.bgAlt }} />
            <span className="heading" style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{match.sport_id}</span>
          </div>
          <span style={{ fontSize: 11, color: C.textMuted }}>{fmtDate(match.match_finished_at)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: '10px 0 0 10px', background: won ? 'rgba(52,211,153,0.07)' : C.bgAlt, border: `1px solid ${won ? 'rgba(52,211,153,0.2)' : C.border}`, borderRight: 'none' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>You</div>
            <div className="heading score-num" style={{ fontSize: 38, fontWeight: 900, color: won ? '#34D399' : C.text, lineHeight: 1, letterSpacing: -2 }}>{myPoints}</div>
          </div>
          <div style={{ width: 38, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bgAlt, border: `1px solid ${C.border}`, flexShrink: 0 }}>
            <span className="heading" style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, letterSpacing: 1 }}>VS</span>
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: '0 10px 10px 0', background: !won && !draw ? 'rgba(239,68,68,0.07)' : C.bgAlt, border: `1px solid ${!won && !draw ? 'rgba(239,68,68,0.2)' : C.border}`, borderLeft: 'none' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Opp.</div>
            <div className="heading score-num" style={{ fontSize: 38, fontWeight: 900, color: !won && !draw ? '#EF4444' : C.text, lineHeight: 1, letterSpacing: -2 }}>{oppPoints}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {trophyDiff !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, background: trophyDiff >= 0 ? 'rgba(52,211,153,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${trophyDiff >= 0 ? 'rgba(52,211,153,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                <span style={{ fontSize: 12 }}>🏆</span>
                <span className="heading" style={{ fontSize: 12, fontWeight: 800, color: trophyDiff >= 0 ? '#34D399' : '#EF4444' }}>{trophyDiff >= 0 ? '+' : ''}{trophyDiff}</span>
                {myNewT != null && <span style={{ fontSize: 10, color: C.textMuted }}>({myNewT.toLocaleString()})</span>}
              </div>
            )}
            {match.forfeit_occurred && (
              <span style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 11, color: '#F59E0B', fontWeight: 600 }}>⚡ Forfeit</span>
            )}
          </div>
          {duration && <span style={{ fontSize: 11, color: C.textMuted, flexShrink: 0 }}>⏱ {duration}</span>}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ TEAM MATCH CARD ═══════════════════ */
const TeamMatchCard = ({ C, match, myTeamIds, location, sportIconMap = {} }) => {
  const myTeamNum = myTeamIds.has(match.team1_id) ? 1 : 2;
  const myPoints  = myTeamNum === 1 ? match.team1_points : match.team2_points;
  const oppPoints = myTeamNum === 1 ? match.team2_points : match.team1_points;
  const myPrevT   = myTeamNum === 1 ? match.team1_prev_trophies : match.team2_prev_trophies;
  const myNewT    = myTeamNum === 1 ? match.team1_new_trophies  : match.team2_new_trophies;
  const isCompetitive = match.mode2 === 'competitive';
  const won  = myPoints > oppPoints;
  const draw = myPoints === oppPoints;
  const result = draw ? 'draw' : won ? 'win' : 'loss';
  const trophyDiff = (myNewT != null && myPrevT != null) ? myNewT - myPrevT : null;

  const rc = {
    win:  { label: 'WIN',  bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.30)', color: '#34D399', bar: '#34D399' },
    loss: { label: 'LOSS', bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.30)',  color: '#EF4444', bar: '#EF4444' },
    draw: { label: 'DRAW', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', color: '#F59E0B', bar: '#F59E0B' },
  }[result];

  const duration = (() => {
    if (!match.match_started_at || !match.match_finished_at) return null;
    const diff = Math.round((new Date(match.match_finished_at) - new Date(match.match_started_at)) / 60000);
    return diff > 0 ? `${diff}m` : null;
  })();

  return (
    <div className="card-lift" style={{ background: C.bgCard, border: `1px solid ${rc.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: C.shadowMd, display: 'flex', flexDirection: 'column' }}>
      <div className="match-banner" style={{ position: 'relative', height: 160, overflow: 'hidden', flexShrink: 0 }}>
        <img src={location?.image_url || noImage} alt={location?.name || 'Location'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.65) 100%)' }} />
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 800, background: rc.bg, border: `1px solid ${rc.border}`, color: rc.color, backdropFilter: 'blur(6px)', letterSpacing: 0.5 }}>{rc.label}</span>
        </div>
        <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 5 }}>
          <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: 'rgba(139,92,246,0.75)', border: '1px solid rgba(139,92,246,0.5)', color: '#fff', backdropFilter: 'blur(6px)' }}>Team</span>
          <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: isCompetitive ? 'rgba(139,92,246,0.75)' : 'rgba(12,105,200,0.75)', border: `1px solid ${isCompetitive ? 'rgba(139,92,246,0.5)' : 'rgba(12,105,200,0.5)'}`, color: '#fff', backdropFilter: 'blur(6px)' }}>
            {isCompetitive ? 'Competitive' : 'Casual'}
          </span>
        </div>
        {location?.name && (
          <div style={{ position: 'absolute', bottom: 8, left: 10, right: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>📍</span> {location.name}
            </div>
          </div>
        )}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: rc.bar }} />
      </div>
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <SportIcon C={C} iconUrl={sportIconMap[match.sport_id]?.icon_url} sportId={match.sport_id} size={22} style={{ background: C.bgAlt }} />
            <span className="heading" style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{match.sport_id}</span>
          </div>
          <span style={{ fontSize: 11, color: C.textMuted }}>{fmtDate(match.match_finished_at)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: '10px 0 0 10px', background: won ? 'rgba(52,211,153,0.07)' : C.bgAlt, border: `1px solid ${won ? 'rgba(52,211,153,0.2)' : C.border}`, borderRight: 'none' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>My Team</div>
            <div className="heading score-num" style={{ fontSize: 38, fontWeight: 900, color: won ? '#34D399' : C.text, lineHeight: 1, letterSpacing: -2 }}>{myPoints}</div>
          </div>
          <div style={{ width: 38, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bgAlt, border: `1px solid ${C.border}`, flexShrink: 0 }}>
            <span className="heading" style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, letterSpacing: 1 }}>VS</span>
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: '0 10px 10px 0', background: !won && !draw ? 'rgba(239,68,68,0.07)' : C.bgAlt, border: `1px solid ${!won && !draw ? 'rgba(239,68,68,0.2)' : C.border}`, borderLeft: 'none' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Opp.</div>
            <div className="heading score-num" style={{ fontSize: 38, fontWeight: 900, color: !won && !draw ? '#EF4444' : C.text, lineHeight: 1, letterSpacing: -2 }}>{oppPoints}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {trophyDiff !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, background: trophyDiff >= 0 ? 'rgba(52,211,153,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${trophyDiff >= 0 ? 'rgba(52,211,153,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                <span style={{ fontSize: 12 }}>🏆</span>
                <span className="heading" style={{ fontSize: 12, fontWeight: 800, color: trophyDiff >= 0 ? '#34D399' : '#EF4444' }}>{trophyDiff >= 0 ? '+' : ''}{trophyDiff}</span>
                {myNewT != null && <span style={{ fontSize: 10, color: C.textMuted }}>({myNewT.toLocaleString()})</span>}
              </div>
            )}
            {match.forfeit_occurred && (
              <span style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 11, color: '#F59E0B', fontWeight: 600 }}>⚡ Forfeit</span>
            )}
          </div>
          {duration && <span style={{ fontSize: 11, color: C.textMuted, flexShrink: 0 }}>⏱ {duration}</span>}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ MODAL ═══════════════════ */
const Modal = ({ C, onClose, children, wide = false, extraWide = false }) => (
  <div onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    className="modal-wrap"
    style={{ position: 'fixed', inset: 0, zIndex: 2000, background: C.mode === 'dark' ? 'rgba(0,0,0,0.75)' : 'rgba(10,29,68,0.35)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div className="modal-animate modal-inner" style={{ ...card(C, { width: extraWide ? 'min(1040px,100%)' : wide ? 'min(680px,100%)' : 'min(480px,100%)', maxHeight: '90svh', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 20, border: C.mode === 'dark' ? '0' : `1px solid ${C.border}` }), boxShadow: C.shadowLg }}>
      {children}
    </div>
  </div>
);
const MHead = ({ C, title, onClose }) => (
  <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div className="heading" style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{title}</div>
    <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
    </button>
  </div>
);
const MBody = ({ children, className = '' }) => (<div className={className} style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>{children}</div>);
const MFoot = ({ C, children }) => (<div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>{children}</div>);

const WGManageLabel = ({ C, children }) => (
  <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, margin: '14px 0 8px' }}>{children}</div>
);

const WGManageOption = ({ C, icon, title, subtitle, onClick, muted = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ width: '100%', minHeight: 72, border: `1px solid ${C.border}`, background: muted ? C.bgAlt : C.bgCard, borderRadius: 18, padding: 12, cursor: 'pointer', display: 'grid', gridTemplateColumns: '46px minmax(0, 1fr) 34px', gap: 12, alignItems: 'center', textAlign: 'left' }}
  >
    <span style={{ width: 46, height: 46, borderRadius: 17, background: muted ? C.bgAlt : C.accentBgLight, border: `1px solid ${muted ? C.border : C.accentBorder}`, color: muted ? C.textMuted : C.accent, display: 'grid', placeItems: 'center' }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: 17 }} />
    </span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', color: C.text, fontSize: 15, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
      {subtitle && <span style={{ display: 'block', color: C.textMuted, fontSize: 12, fontWeight: 800, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>}
    </span>
    <span style={{ width: 34, height: 34, borderRadius: 17, background: C.bgAlt, color: muted ? C.textMuted : C.accent, display: 'grid', placeItems: 'center' }}>
      <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 12, transform: 'rotate(-90deg)' }} />
    </span>
  </button>
);

const WGManageTileButton = ({ C, icon, title, onClick, muted = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ minHeight: 62, border: `1px solid ${C.border}`, background: muted ? C.bgAlt : C.bgCard, color: muted ? C.textMuted : C.text, borderRadius: 17, padding: '10px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 950, textAlign: 'center' }}
  >
    <FontAwesomeIcon icon={icon} style={{ color: muted ? C.textMuted : C.accent, fontSize: 15 }} />
    <span>{title}</span>
  </button>
);

const WGSessionTargetField = ({ C, label, title, detail, open, onToggle, children }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      style={{ width: '100%', minHeight: 68, border: `1px solid ${C.border}`, background: C.bgCard, borderRadius: 16, padding: '12px 13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left' }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', color: C.textMuted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
        <span style={{ display: 'block', color: C.text, fontSize: 17, fontWeight: 950, marginTop: 3 }}>{title}</span>
        {detail && <span style={{ display: 'block', color: C.textMuted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>{detail}</span>}
      </span>
      <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ color: C.accent, fontSize: 14, flexShrink: 0 }} />
    </button>
    {open && <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>{children}</div>}
  </div>
);

/* ═══════════════════ MEMBER BUILDER ═══════════════════ */
const MemberBuilder = ({ C, rows, setRows }) => {
  const add = () => setRows(r => [...r, { id: uid('m'), username: '' }]);
  const upd = (id, v) => setRows(r => r.map(x => x.id === id ? { ...x, username: v } : x));
  const rem = (id) => setRows(r => r.length <= 1 ? r : r.filter(x => x.id !== id));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ padding: '12px 14px', borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 13, color: C.textSecondary, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>How to add members</div>
        Set a passcode via <strong>Profile → Settings → One-Time Passcode</strong><br />
        Enter <strong>username + 4-digit passcode</strong> with no spaces.{' '}
        <span style={{ display: 'inline-block', marginTop: 4, padding: '3px 8px', borderRadius: 6, background: C.bgCard, border: `1px solid ${C.border}`, fontWeight: 600, color: C.accent, fontSize: 12 }}>e.g. johndoe5291</span>
      </div>
      {rows.map((r, i) => (
        <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
          <input value={r.username} onChange={e => upd(r.id, e.target.value)} placeholder="e.g. johndoe5291" style={{ ...inputSt(C), flex: 1 }} autoCapitalize="none" autoCorrect="off" />
          <button onClick={() => rem(r.id)} style={dangerIconButtonSt(C, 40)}>
            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} />
          </button>
        </div>
      ))}
      <button onClick={add} style={{ alignSelf: 'flex-start', padding: '9px 14px', borderRadius: 9, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minHeight: 44 }}>
        <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} /> Add Row
      </button>
    </div>
  );
};

const ChallengeMemberPicker = ({ C, candidates, currentMembers, loading, addingId, search, setSearch, onAdd, parentName }) => {
  const q = search.trim().toLowerCase();
  const currentIds = new Set((currentMembers || []).map(member => member.id || member.profile_id).filter(Boolean));
  const filtered = (candidates || []).filter(member => {
    const profile = member.profiles || {};
    const display = [profile.name, profile.surname].filter(Boolean).join(' ') || profile.username || '';
    return !q || `${display} ${profile.username || ''}`.toLowerCase().includes(q);
  });
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ padding: '12px 14px', borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.textSecondary, fontSize: 13, lineHeight: 1.5 }}>
        Search members from <strong>{parentName || 'the parent institute'}</strong> and add them to this challenge.
      </div>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search members"
        style={inputSt(C)}
        autoCapitalize="none"
        autoCorrect="off"
        aria-label="Search parent institute members"
      />
      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: C.textMuted, fontSize: 13, fontWeight: 700 }}>Loading members...</div>
      ) : filtered.length ? (
        <div style={{ display: 'grid', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
          {filtered.map(member => {
            const profile = member.profiles || {};
            const display = [profile.name, profile.surname].filter(Boolean).join(' ') || profile.username || 'Member';
            const alreadyAdded = currentIds.has(member.profile_id);
            const adding = addingId === member.profile_id;
            return (
              <div key={member.profile_id} style={{ display: 'grid', gridTemplateColumns: '38px minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: 10, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                <img src={profile.profile_img || noImage} alt="" style={{ width: 38, height: 38, borderRadius: 999, objectFit: 'cover', border: `1px solid ${C.border}` }} loading="lazy" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</div>
                  <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, marginTop: 2 }}>{profile.username ? `@${profile.username}` : member.association_type || 'Member'}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onAdd(member)}
                  disabled={alreadyAdded || adding}
                  style={{ minHeight: 36, border: 'none', borderRadius: 9, background: alreadyAdded ? C.bgCard : C.accentGrad, color: alreadyAdded ? C.textMuted : '#fff', padding: '8px 12px', cursor: alreadyAdded || adding ? 'default' : 'pointer', fontSize: 12, fontWeight: 850 }}
                >
                  {adding ? 'Adding...' : alreadyAdded ? 'Added' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ padding: 18, textAlign: 'center', borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 850 }}>No members found</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>Add members to the parent institute first, then return to this challenge.</div>
        </div>
      )}
    </div>
  );
};

const ExistingMemberPicker = ({ C, candidates, currentMembers, loading, addingId, search, setSearch, onAdd, sourceName, emptyText = 'Add members to the member institute first, then return here.' }) => {
  const q = search.trim().toLowerCase();
  const currentIds = new Set((currentMembers || []).map(member => member.id || member.profile_id).filter(Boolean));
  const filtered = (candidates || []).filter(member => {
    const profile = member.profiles || {};
    const display = [profile.name, profile.surname].filter(Boolean).join(' ') || profile.username || '';
    return !q || `${display} ${profile.username || ''}`.toLowerCase().includes(q);
  });
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ padding: '12px 14px', borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.textSecondary, fontSize: 13, lineHeight: 1.5 }}>
        Search existing members from <strong>{sourceName || 'the member institute'}</strong> and add them here.
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members" style={inputSt(C)} autoCapitalize="none" autoCorrect="off" aria-label="Search institution members" />
      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: C.textMuted, fontSize: 13, fontWeight: 700 }}>Loading members...</div>
      ) : filtered.length ? (
        <div style={{ display: 'grid', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
          {filtered.map(member => {
            const profile = member.profiles || {};
            const display = [profile.name, profile.surname].filter(Boolean).join(' ') || profile.username || 'Member';
            const alreadyAdded = currentIds.has(member.profile_id);
            const adding = addingId === member.profile_id;
            return (
              <div key={member.profile_id} style={{ display: 'grid', gridTemplateColumns: '38px minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: 10, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                <img src={profile.profile_img || noImage} alt="" style={{ width: 38, height: 38, borderRadius: 999, objectFit: 'cover', border: `1px solid ${C.border}` }} loading="lazy" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</div>
                  <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, marginTop: 2 }}>{profile.username ? `@${profile.username}` : member.association_type || 'Member'}</div>
                </div>
                <button type="button" onClick={() => onAdd(member)} disabled={alreadyAdded || adding} style={{ minHeight: 36, border: 'none', borderRadius: 9, background: alreadyAdded ? C.bgCard : C.accentGrad, color: alreadyAdded ? C.textMuted : '#fff', padding: '8px 12px', cursor: alreadyAdded || adding ? 'default' : 'pointer', fontSize: 12, fontWeight: 850 }}>
                  {adding ? 'Adding...' : alreadyAdded ? 'Added' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ padding: 18, textAlign: 'center', borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 850 }}>No members found</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>{emptyText}</div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ EDITABLE FIELD ═══════════════════ */
const EditField = ({ C, label, field, value, onSave, readOnly = false, displayValue, inputType = 'text' }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  useEffect(() => setVal(value || ''), [value]);
  const save = async () => { await onSave(field, val); setEditing(false); };
  const shownValue = displayValue ?? val;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
        {editing ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <input type={inputType} value={val} onChange={e => setVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(value || ''); } }}
              autoFocus style={{ ...inputSt(C), padding: '10px 11px', fontSize: 14, flex: 1 }} />
            <button onClick={save} style={{ width: 40, height: 40, borderRadius: 8, background: C.accentGrad, border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FontAwesomeIcon icon={faCheck} style={{ fontSize: 13 }} />
            </button>
            <button onClick={() => { setEditing(false); setVal(value || ''); }} style={{ width: 40, height: 40, borderRadius: 8, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 15, fontWeight: 500, color: shownValue ? C.text : C.textMuted }}>{shownValue || '—'}</div>
        )}
      </div>
      {!editing && !readOnly && (
        <button onClick={() => setEditing(true)} style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 12 }}>
          <FontAwesomeIcon icon={faPen} style={{ fontSize: 12 }} />
        </button>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [isDark, setIsDark] = useState(() => { try { return localStorage.getItem('spa-theme') !== 'light'; } catch { return true; } });
  const C = isDark ? DARK : LIGHT;
  const toggleTheme = useCallback(() => { setIsDark(v => { const n = !v; try { localStorage.setItem('spa-theme', n ? 'dark' : 'light'); } catch {} return n; }); }, []);
  useEffect(() => {
    const syncTheme = () => {
      try { setIsDark(localStorage.getItem('spa-theme') !== 'light'); } catch {}
    };
    window.addEventListener('storage', syncTheme);
    window.addEventListener('spa-theme-change', syncTheme);
    return () => {
      window.removeEventListener('storage', syncTheme);
      window.removeEventListener('spa-theme-change', syncTheme);
    };
  }, []);
  const workoutDashboardRef = useRef(null);
  const workoutGroupAnalyticsRef = useRef(null);
  const workoutGroupCalendarRef = useRef(null);
  const workoutGroupMembersRef = useRef(null);

  const [profileData, setProfileData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [storeOrders, setStoreOrders] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionSearch, setTransactionSearch] = useState('');
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [transactionSort, setTransactionSort] = useState('newest');
  const [visibleTransactionCount, setVisibleTransactionCount] = useState(20);
  const [billingOverview, setBillingOverview] = useState(getEmptyBillingOverview());
  const [billingOverviewError, setBillingOverviewError] = useState('');
  const [payoutSetupVisible, setPayoutSetupVisible] = useState(false);
  const [payoutSetupLoading, setPayoutSetupLoading] = useState(false);
  const [payoutSetupError, setPayoutSetupError] = useState('');
  const [payoutAccount, setPayoutAccount] = useState(null);
  const payoutSetupReturned = searchParams.get('connect_return') === '1';
  const payoutRequirementCount = [
    ...(Array.isArray(payoutAccount?.requirements_due) ? payoutAccount.requirements_due : []),
    ...(Array.isArray(payoutAccount?.requirements_past_due) ? payoutAccount.requirements_past_due : []),
  ].filter(Boolean).length;
  const payoutSetupNeedsVerification = !!payoutAccount?.payouts_enabled && payoutRequirementCount > 0;
  const payoutSetupReady = !!payoutAccount?.payouts_enabled && !payoutSetupNeedsVerification;
  const [institutionBillingRows, setInstitutionBillingRows] = useState([]);
  const [institutionBusinessDemoMode, setInstitutionBusinessDemoMode] = useState(false);
  const [institutionPublicPricingHidden, setInstitutionPublicPricingHidden] = useState(true);
  const [institutionBusinessDemoCalendlyUrl, setInstitutionBusinessDemoCalendlyUrl] = useState('');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [bookingDetails, setBookingDetails] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [loadingImg, setLoadingImg] = useState(false);

  const updateProfileSearch = useCallback((update, options = {}) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      update(next);
      return next;
    }, options);
  }, [setSearchParams]);

  const selectProfileTab = useCallback((tabId) => {
    if (!TABS.some(tab => tab.id === tabId)) return;
    setActiveTab(tabId);
    updateProfileSearch((next) => {
      if (tabId === 'profile') next.delete('tab');
      else next.set('tab', tabId);
      next.delete('plans');
      if (tabId !== 'institutes') next.delete('institution_plans');
      if (tabId !== 'workouts') next.delete('group_id');
    }, { replace: true });
  }, [updateProfileSearch]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (TABS.some(tab => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    } else if (searchParams.get('plans') === '1') {
      setActiveTab('workouts');
    } else {
      setActiveTab('profile');
    }
  }, [searchParams]);

  const closePayoutSetupModal = useCallback(() => {
    setPayoutSetupVisible(false);
    setPayoutSetupError('');
    updateProfileSearch((next) => {
      next.delete('payout_setup');
      next.delete('connect_return');
    }, { replace: true });
  }, [updateProfileSearch]);

  useEffect(() => {
    let mounted = true;
    loadInstitutionBillingRows()
      .then(rows => { if (mounted) setInstitutionBillingRows(rows); })
      .catch(() => { if (mounted) setInstitutionBillingRows([]); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    loadInstitutionBusinessDemoConfig()
      .then(config => {
        if (mounted) {
          setInstitutionBusinessDemoMode(config.demoMode);
          setInstitutionBusinessDemoCalendlyUrl(config.calendlyUrl);
          setInstitutionPublicPricingHidden(config.publicPricingHidden);
        }
      })
      .catch(() => {
        if (mounted) {
          setInstitutionBusinessDemoMode(false);
          setInstitutionBusinessDemoCalendlyUrl('');
          setInstitutionPublicPricingHidden(true);
        }
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (searchParams.get('plans') === '1') {
      setShowWorkoutPlans(true);
      setActiveTab('workouts');
    }
  }, [searchParams]);

  const [allAchievements, setAllAchievements] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [achivFilter, setAchivFilter] = useState('all');
  const [achivSport, setAchivSport] = useState('all');

  const [matches, setMatches] = useState([]);
  const [teamMatches, setTeamMatches] = useState([]);
  const [matchLocations, setMatchLocations] = useState({});
  const [matchTab, setMatchTab] = useState('solo');
  const [matchFilter, setMatchFilter] = useState('all');
  const [matchSport, setMatchSport] = useState('all');
  const [matchMode, setMatchMode] = useState('all');

  const [institutes, setInstitutes] = useState([]);
  const [institutesLoading, setInstitutesLoading] = useState(true);
  const [institutesError, setInstitutesError] = useState('');
  const [loadingInstituteMemberIds, setLoadingInstituteMemberIds] = useState([]);
  const [archivedInstitutes, setArchivedInstitutes] = useState([]);
  const [archivedInstitutesOpen, setArchivedInstitutesOpen] = useState(false);
  const [restoringInstituteId, setRestoringInstituteId] = useState(null);
  const [instituteRefresh, setInstituteRefresh] = useState(0);
  const [activeInstIdx, setActiveInstIdx] = useState(0);
  const [workspaceFilterId, setWorkspaceFilterId] = useState(null);
  const activeInst = institutes[activeInstIdx];
  const instituteMembersLoading = loadingInstituteMemberIds.includes(activeInst?.id);
  const requestedInstituteId = searchParams.get('institute_id') || location.state?.institutionReturn?.instituteId || '';
  const requestedWorkspaceId = searchParams.get('workspace_id') || location.state?.institutionReturn?.workspaceId || '';
  const activeInstituteIdRef = useRef(null);
  const restoredInstitutionContextRef = useRef('');
  const realtimeInstituteIds = useMemo(
    () => Array.from(new Set(institutes.map(inst => inst.id).filter(Boolean))).sort(),
    [institutes],
  );
  const realtimeWorkspaceIds = useMemo(
    () => Array.from(new Set(institutes.map(inst => inst.workspace_id).filter(Boolean))).sort(),
    [institutes],
  );
  const [appLeaderboardForm, setAppLeaderboardForm] = useState(DEFAULT_APP_LEADERBOARD_SETTINGS);
  const [appLeaderboardOpen, setAppLeaderboardOpen] = useState(false);
  const [appLeaderboardPreviewMode, setAppLeaderboardPreviewMode] = useState(null);
  const [savingAppLeaderboardBranding, setSavingAppLeaderboardBranding] = useState(false);
  const [showWorkspaceRename, setShowWorkspaceRename] = useState(false);
  const [workspaceRenameValue, setWorkspaceRenameValue] = useState('');
  const [workspaceLogoFile, setWorkspaceLogoFile] = useState(null);
  const [workspaceLogoPreview, setWorkspaceLogoPreview] = useState(null);
  const [savingWorkspaceRename, setSavingWorkspaceRename] = useState(false);
  useEffect(() => {
    activeInstituteIdRef.current = activeInst?.id || null;
  }, [activeInst?.id]);
  useEffect(() => {
    if (!institutes.length || (!requestedInstituteId && !requestedWorkspaceId)) return;
    const contextKey = `${requestedWorkspaceId}:${requestedInstituteId}`;
    if (restoredInstitutionContextRef.current === contextKey) return;
    const requestedIndex = requestedInstituteId
      ? institutes.findIndex(inst => inst.id === requestedInstituteId)
      : institutes.findIndex(inst => inst.workspace_id === requestedWorkspaceId);
    if (requestedIndex < 0) return;
    restoredInstitutionContextRef.current = contextKey;
    const requestedInstitute = institutes[requestedIndex];
    if (activeInstIdx !== requestedIndex) setActiveInstIdx(requestedIndex);
    const nextWorkspaceId = requestedInstitute.workspace_id || '__unassigned__';
    if (workspaceFilterId !== nextWorkspaceId) setWorkspaceFilterId(nextWorkspaceId);
  }, [activeInstIdx, institutes, requestedInstituteId, requestedWorkspaceId, workspaceFilterId]);
  useEffect(() => {
    setAppLeaderboardForm({
      ...DEFAULT_APP_LEADERBOARD_SETTINGS,
      ...(activeInst?.brand_settings || {}),
    });
    setAppLeaderboardOpen(false);
    setAppLeaderboardPreviewMode(null);
    setActiveInstituteView('people');
  }, [activeInst?.brand_settings, activeInst?.id]);
  const [openingLiveDashboard, setOpeningLiveDashboard] = useState(false);
  const [instQuery, setInstQuery] = useState('');
  const [institutionMemberSearch, setInstitutionMemberSearch] = useState('');
  const [institutionDepartmentMemberSearch, setInstitutionDepartmentMemberSearch] = useState('');
  const [editingInstitutionMemberRole, setEditingInstitutionMemberRole] = useState(null);
  const [institutionCustomRole, setInstitutionCustomRole] = useState('');
  const [updatingInstitutionMemberRoleId, setUpdatingInstitutionMemberRoleId] = useState(null);
  const [expandedDept, setExpandedDept] = useState(null);
  const [activeInstituteView, setActiveInstituteView] = useState('people');
  const [institutionAdmins, setInstitutionAdmins] = useState([]);
  const [institutionAdminsLoading, setInstitutionAdminsLoading] = useState(false);
  const [institutionAdminsError, setInstitutionAdminsError] = useState('');
  const [institutionAdminSearch, setInstitutionAdminSearch] = useState('');
  const [changingInstitutionAdminId, setChangingInstitutionAdminId] = useState(null);
  useEffect(() => {
    setInstitutionMemberSearch('');
    setInstitutionDepartmentMemberSearch('');
    setEditingInstitutionMemberRole(null);
    setInstitutionCustomRole('');
    setInstitutionAdminSearch('');
  }, [activeInst?.id]);
  const [scope, setScope] = useState('national');
  const [rankCountry, setRankCountry] = useState('Ireland');
  const [rankCountryCode, setRankCountryCode] = useState(() => findCountry('Ireland')?.isoCode || '');
  const [rankRegion, setRankRegion] = useState('Dublin');
  const rankRegionOptions = useMemo(() => State.getStatesOfCountry(rankCountryCode), [rankCountryCode]);
  const chooseRankCountry = useCallback((nextCode, nextRegion = '') => {
    const nextCountry = findCountry(nextCode);
    setRankCountryCode(nextCode);
    setRankCountry(nextCountry?.name || '');
    setRankRegion(nextRegion);
  }, []);
  const applyInstituteRankLocation = useCallback((inst) => {
    if (!inst || scope === 'global') return;
    const nextCountry = findCountry(inst.country || rankCountry);
    if (nextCountry) {
      setRankCountryCode(nextCountry.isoCode);
      setRankCountry(nextCountry.name);
    } else if (inst.country) {
      setRankCountry(inst.country);
      setRankCountryCode('');
    }
    if (scope === 'regional' && inst.region) setRankRegion(inst.region);
  }, [rankCountry, scope]);
  const [showAllDepts, setShowAllDepts] = useState(false);
  const [peerBoard, setPeerBoard] = useState([]);
  const [loadingRanks, setLoadingRanks] = useState(false);
  const [instType, setInstType] = useState('Collective Group');
  const [showAddInstitute, setShowAddInstitute] = useState(false);
  const [instName, setInstName] = useState('');
  const [instNewType, setInstNewType] = useState('Collective Group');
  const [instTeams, setInstTeams] = useState(false);
  const [instInitialDepartments, setInstInitialDepartments] = useState('');
  const [instChallengeStartsOn, setInstChallengeStartsOn] = useState(() => dateInputValue(new Date()));
  const [instChallengeEndsOn, setInstChallengeEndsOn] = useState(() => dateInputValue(addDays(new Date(), 1)));
  const [instChallengeMemberLimit, setInstChallengeMemberLimit] = useState('');
  const [instChallengeDescription, setInstChallengeDescription] = useState('');
  const [instChallengeGoalSteps, setInstChallengeGoalSteps] = useState('50000');
  const [instChallengeDepartmentIds, setInstChallengeDepartmentIds] = useState([]);
  const [selectedPlatformChallengeOffer, setSelectedPlatformChallengeOffer] = useState(null);
  const [instImageFile, setInstImageFile] = useState(null);
  const [instImagePreview, setInstImagePreview] = useState(null);
  const [editInstImageFile, setEditInstImageFile] = useState(null);
  const [editInstImagePreview, setEditInstImagePreview] = useState(null);
  const [editDeptImageFile, setEditDeptImageFile] = useState(null);
  const [editDeptImagePreview, setEditDeptImagePreview] = useState(null);
  const [instInitMembers, setInstInitMembers] = useState([{ id: uid('m'), username: '' }]);
  const [showAddDept, setShowAddDept] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptType, setNewDeptType] = useState('');
  const [deptImageFile, setDeptImageFile] = useState(null);
  const [deptImagePreview, setDeptImagePreview] = useState(null);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [newMembers, setNewMembers] = useState([{ id: uid('m'), username: '' }]);
  const [challengeMemberSearch, setChallengeMemberSearch] = useState('');
  const [challengeMemberCandidates, setChallengeMemberCandidates] = useState([]);
  const [loadingChallengeMemberCandidates, setLoadingChallengeMemberCandidates] = useState(false);
  const [addingChallengeMemberId, setAddingChallengeMemberId] = useState(null);
  const [platformChallengeOffers, setPlatformChallengeOffers] = useState([]);
  const [loadingPlatformChallengeOffers, setLoadingPlatformChallengeOffers] = useState(false);
  const [platformChallengeOffersError, setPlatformChallengeOffersError] = useState('');
  const [optingIntoPlatformChallengeId, setOptingIntoPlatformChallengeId] = useState(null);
  const [challengeResults, setChallengeResults] = useState([]);
  const [loadingChallengeResults, setLoadingChallengeResults] = useState(false);
  const [challengeResultsError, setChallengeResultsError] = useState('');
  const [showAddDeptMembers, setShowAddDeptMembers] = useState({ open: false, dept: null });
  const [departmentMemberSearch, setDepartmentMemberSearch] = useState('');
  const [departmentMemberCandidates, setDepartmentMemberCandidates] = useState([]);
  const [loadingDepartmentMemberCandidates, setLoadingDepartmentMemberCandidates] = useState(false);
  const [addingDepartmentMemberId, setAddingDepartmentMemberId] = useState(null);
  const [moveDeptMember, setMoveDeptMember] = useState({ open: false, member: null, fromDeptId: null, targetDeptId: '' });
  const [editingInstitute, setEditingInstitute] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ website: '', phone_number: '', email: '', address: '', country: '', region: '' });
  const [editInstForm, setEditInstForm] = useState(null);
  const [editChallengeDepartments, setEditChallengeDepartments] = useState([]);
  const [loadingEditChallengeAudience, setLoadingEditChallengeAudience] = useState(false);
  const [editDeptForm, setEditDeptForm] = useState(null);

  const openInstitutionCreatePage = useCallback((options = {}) => {
    const next = new URLSearchParams();
    if (options.restart) next.set('restart', '1');
    if (options.instituteId) next.set('institute_id', options.instituteId);
    if (options.workspaceId) next.set('workspace_id', options.workspaceId);
    if (options.planTier) next.set('plan_tier', options.planTier);
    if (options.billingCycle) next.set('billing_cycle', options.billingCycle);
    if (options.challenge) {
      next.set('setupMode', 'challenge');
      next.set('type', 'challenge');
    }
    const query = next.toString();
    navigate(`/institutions/create${query ? `?${query}` : ''}`);
  }, [navigate]);

  const openInstitutionDemoRequest = useCallback((plan = {}) => {
    if (institutionBusinessDemoCalendlyUrl) {
      window.open(institutionBusinessDemoCalendlyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const next = new URLSearchParams();
    next.set('segment', 'business');
    if (plan.tier) next.set('plan_tier', plan.tier);
    const displayName = institutionPlanDisplayName(plan);
    if (displayName) next.set('plan', displayName);
    navigate(`/wellness?${next.toString()}#book-demo`);
  }, [institutionBusinessDemoCalendlyUrl, navigate]);

  const openAddInstituteModal = useCallback((type = 'Collective Group', platformOffer = null) => {
    if (type !== 'Challenge') {
      openInstitutionCreatePage();
      return;
    }
    const templateStartsOn = platformChallengeOfferStartsOn(platformOffer);
    const templateEndsOn = platformChallengeOfferEndsOn(platformOffer);
    setSelectedPlatformChallengeOffer(platformOffer || null);
    setInstName(platformOffer?.name || '');
    setInstNewType(type);
    setInstTeams(false);
    setInstInitialDepartments('');
    setInstChallengeStartsOn(templateStartsOn || dateInputValue(new Date()));
    setInstChallengeEndsOn(templateEndsOn || dateInputValue(addDays(templateStartsOn ? new Date(`${templateStartsOn}T00:00:00`) : new Date(), 1)));
    setInstChallengeMemberLimit('');
    setInstChallengeDescription(platformOffer?.description || '');
    setInstChallengeGoalSteps(platformOffer?.goal_steps ? String(platformOffer.goal_steps) : '50000');
    setInstChallengeDepartmentIds([]);
    setInstImageFile(null);
    setInstImagePreview(null);
    setInstInitMembers([{ id: uid('m'), username: '' }]);
    setShowAddInstitute(true);
  }, [openInstitutionCreatePage]);

  const openEditInstituteModal = useCallback(async (inst) => {
    if (!inst?.id) return;
    const logoUrl = instituteLogoUrl(inst);
    const settings = inst.brand_settings || {};
    const challengeGoalValue = inst.goal_steps ?? settings.goalSteps ?? '';
    const isChallenge = inst.type === 'Challenge';
    const fallbackAudienceSource = isChallenge && inst.workspace_id
      ? institutes.find(item => item.id !== inst.id && item.workspace_id === inst.workspace_id && item.type !== 'Challenge' && item.teams)
      : null;
    const audienceSourceId = settings.parentInstituteId || settings.departmentSourceInstituteId || inst.auto_join_institute_id || fallbackAudienceSource?.id || null;
    const initialForm = {
      id: inst.id,
      name: inst.name,
      code: inst.code || '',
      type: inst.type,
      image_url: logoUrl,
      remove_image: false,
      workspace_id: inst.workspace_id || null,
      starts_on: inst.starts_at ? String(inst.starts_at).slice(0, 10) : '',
      ends_on: inst.ends_at ? String(inst.ends_at).slice(0, 10) : '',
      challenge_member_limit: inst.challenge_member_limit == null ? '' : String(inst.challenge_member_limit),
      goal_steps: challengeGoalValue === '' || challengeGoalValue == null ? '' : String(challengeGoalValue),
      description: settings.description || settings.challengeDescription || '',
      audience_mode: inst.teams ? 'groups' : 'members',
      audience_source_id: audienceSourceId,
      audience_source_name: settings.parentInstituteName || settings.departmentSourceInstituteName || fallbackAudienceSource?.name || '',
      audience_department_ids: Array.isArray(settings.departmentIds) ? settings.departmentIds.filter(Boolean) : [],
      brand_settings: settings,
    };
    setEditingInstitute(inst);
    setEditInstForm(initialForm);
    setEditChallengeDepartments([]);
    setEditInstImageFile(null);
    setEditInstImagePreview(logoUrl);
    if (!isChallenge || !audienceSourceId) return;
    setLoadingEditChallengeAudience(true);
    try {
      const [{ data: sourceDepartments }, { data: challengeDepartments }] = await Promise.all([
        supabase.from('institute_teams').select('id,name,image_url,type').eq('institute_id', audienceSourceId).is('archived_at', null).order('name', { ascending: true }),
        supabase.from('institute_teams').select('id,name,image_url,type').eq('institute_id', inst.id).is('archived_at', null).order('name', { ascending: true }),
      ]);
      const sourceRows = sourceDepartments || [];
      const challengeNames = new Set((challengeDepartments || []).map(dept => dept.name).filter(Boolean));
      const inferredIds = sourceRows.filter(dept => challengeNames.has(dept.name)).map(dept => dept.id);
      setEditChallengeDepartments(sourceRows);
      setEditInstForm(current => current?.id === inst.id ? {
        ...current,
        audience_department_ids: current.audience_department_ids?.length ? current.audience_department_ids : inferredIds,
      } : current);
    } finally {
      setLoadingEditChallengeAudience(false);
    }
  }, [institutes]);

  const [workoutGroups, setWorkoutGroups] = useState([]);
  const [workoutAccess, setWorkoutAccess] = useState(null);
  const [workoutGroupOwnerAccessMap, setWorkoutGroupOwnerAccessMap] = useState({});
  const [activeWGOwnerAccess, setActiveWGOwnerAccess] = useState(null);
  const [showWorkoutPlans, setShowWorkoutPlans] = useState(false);
  const [workoutBillingCycle, setWorkoutBillingCycle] = useState('yearly');
  const [showInstitutionPlans, setShowInstitutionPlans] = useState(false);
  const [institutionBillingCycle, setInstitutionBillingCycle] = useState('monthly');
  const [institutionPlanSegment, setInstitutionPlanSegment] = useState('Social');
  const [institutionCheckoutLoading, setInstitutionCheckoutLoading] = useState(null);
  const [institutionCheckoutMessage, setInstitutionCheckoutMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [openingWorkoutBillingPortal, setOpeningWorkoutBillingPortal] = useState(false);
  const handledWorkoutCheckoutReturnRef = useRef('');
  const handledInstitutionCheckoutReturnRef = useRef('');
  const [loadingWG, setLoadingWG] = useState(false);
  const workoutPlansRef = useRef(null);
  const [showCreateWG, setShowCreateWG] = useState(false);
  const [showJoinWG, setShowJoinWG] = useState(false);
  const [joinWGCode, setJoinWGCode] = useState('');
  const [joinCodeShare, setJoinCodeShare] = useState(null);
  const [wgName, setWgName] = useState('');
  const [creatingWG, setCreatingWG] = useState(false);
  const [editingWG, setEditingWG] = useState(null);
  const [editingWGName, setEditingWGName] = useState('');
  const [activeWGId, setActiveWGId] = useState(null);
  const [activeWG, setActiveWG] = useState(null);
  const [expandedWorkoutClubIds, setExpandedWorkoutClubIds] = useState(() => new Set());
  const [timeRange, setTimeRange] = useState(7);
  const [wgRangeMode, setWgRangeMode] = useState('last_7');
  const [wgCustomDays, setWgCustomDays] = useState('90');
  const [wgSelectedDate, setWgSelectedDate] = useState(() => localDateInputValue(new Date()));
  const [wgNutritionDays, setWgNutritionDays] = useState(1);
  const [wgNutritionSummary, setWgNutritionSummary] = useState(null);
  const [wgNutritionRows, setWgNutritionRows] = useState([]);
  const [wgNutritionByAthlete, setWgNutritionByAthlete] = useState({});
  const [wgNutritionProfilesByAthlete, setWgNutritionProfilesByAthlete] = useState({});
  const [wgNutritionError, setWgNutritionError] = useState('');
  const [wgClubSeasons, setWgClubSeasons] = useState([]);
  const [wgMembers, setWgMembers] = useState([]);
  const [groupStats, setGroupStats] = useState(null);
  const [perfMap, setPerfMap] = useState({});
  const [wgAnalyticsSessions, setWgAnalyticsSessions] = useState([]);
  const [selectedWGAthlete, setSelectedWGAthlete] = useState(null);
  const [athleteSessions, setAthleteSessions] = useState([]);
  const [wgTrendMetric, setWgTrendMetric] = useState('sessions');
  const [wgWorkoutTrendMetric, setWgWorkoutTrendMetric] = useState('totalReps');
  const [selectedWGWorkoutKey, setSelectedWGWorkoutKey] = useState('');
  const [selectedWGExerciseId, setSelectedWGExerciseId] = useState('');
  const [wgAnalyticsAthleteId, setWgAnalyticsAthleteId] = useState('all');
  const [athleteTrendMetric, setAthleteTrendMetric] = useState('totalVolume');
  const [workoutGroupAnalyticsOpen, setWorkoutGroupAnalyticsOpen] = useState(false);
  const [showAllWGAnalyticsAthletes, setShowAllWGAnalyticsAthletes] = useState(false);
  const [expandedAthleteSessionId, setExpandedAthleteSessionId] = useState(null);
  const [athleteNote, setAthleteNote] = useState(null);
  const [athleteNoteText, setAthleteNoteText] = useState('');
  const [athleteLoading, setAthleteLoading] = useState(false);
  const [athleteNoteSaving, setAthleteNoteSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [refundDialog, setRefundDialog] = useState(null);
  const wgOwnerAccessCacheRef = useRef(new Map());
  const [workoutGroupManageOpen, setWorkoutGroupManageOpen] = useState(false);
  const [workoutGroupSessionTargetOpen, setWorkoutGroupSessionTargetOpen] = useState(false);
  const [workoutGroupSessionTargetSaving, setWorkoutGroupSessionTargetSaving] = useState(false);
  const [workoutGroupSessionTargetPicker, setWorkoutGroupSessionTargetPicker] = useState(null);
  const [workoutGroupSessionTargetDraft, setWorkoutGroupSessionTargetDraft] = useState({
    weeklySessionTarget: '3',
    weeklyXpTarget: '1000',
    goalKey: 'stay_active',
    trackingKey: 'consistency',
    intensityKey: 'steady',
  });
  const [workoutGroupAnalyticsVisibilitySaving, setWorkoutGroupAnalyticsVisibilitySaving] = useState(false);
  const [targetModal, setTargetModal] = useState(null);
  const [allowedWorkoutsModal, setAllowedWorkoutsModal] = useState(false);
  const [allowedWorkoutSearch, setAllowedWorkoutSearch] = useState('');
  const [coachWorkouts, setCoachWorkouts] = useState([]);
  const [coachWorkoutsLoading, setCoachWorkoutsLoading] = useState(false);
  const [allowedWorkoutsSaving, setAllowedWorkoutsSaving] = useState(false);
  const [workoutGroupThemePreferences, setWorkoutGroupThemePreferences] = useState(null);
  const [workoutGroupThemePrefsDraft, setWorkoutGroupThemePrefsDraft] = useState({ ...DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES });
  const [workoutGroupThemePrefsLoaded, setWorkoutGroupThemePrefsLoaded] = useState(false);
  const [workoutGroupThemePrefsSaving, setWorkoutGroupThemePrefsSaving] = useState(false);
  const [wgCalendarSessions, setWgCalendarSessions] = useState([]);
  const [wgCalendarLoading, setWgCalendarLoading] = useState(false);
  const [wgCalendarError, setWgCalendarError] = useState('');
  const [wgCalendarFilter, setWgCalendarFilter] = useState('upcoming');
  const [wgCalendarSelectedDateKey, setWgCalendarSelectedDateKey] = useState(null);
  const [wgCalendarSelectedSessionId, setWgCalendarSelectedSessionId] = useState(null);
  const [wgCalendarFormOpen, setWgCalendarFormOpen] = useState(false);
  const [wgCalendarEditingSession, setWgCalendarEditingSession] = useState(null);
  const [wgCalendarForm, setWgCalendarForm] = useState(() => getWorkoutGroupCalendarDefaultForm());
  const [wgCalendarSaving, setWgCalendarSaving] = useState(false);
  const [wgCalendarResponseSaving, setWgCalendarResponseSaving] = useState(null);
  const [wgCalendarNotifySaving, setWgCalendarNotifySaving] = useState(false);
  const [wgCalendarCoachOptions, setWgCalendarCoachOptions] = useState([]);
  const [wgCalendarCoachesLoading, setWgCalendarCoachesLoading] = useState(false);
  const [openingInstitutionBilling, setOpeningInstitutionBilling] = useState(false);
  const [resettingInstitutionSteps, setResettingInstitutionSteps] = useState(false);

  const toggleInstitutionPlans = useCallback(() => {
    setShowInstitutionPlans(v => !v);
    updateProfileSearch((next) => {
      next.set('tab', 'institutes');
      next.delete('institution_plans');
    });
  }, [showInstitutionPlans, updateProfileSearch]);

  const openInstitutionPaymentOptions = useCallback(() => {
    setShowInstitutionPlans(true);
    updateProfileSearch((next) => {
      next.set('tab', 'institutes');
    });
    window.setTimeout(() => {
      document.getElementById('institution-access-payment-options')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [updateProfileSearch]);

  const openInstitutionBillingPortal = useCallback(async () => {
    const activeStatuses = new Set(['active', 'trialing', 'past_due', 'incomplete']);
    const activeId = activeInst?.id || null;
    const workspaceInstituteIds = new Set((institutes || [])
      .filter(inst => activeInst?.workspace_id && inst?.workspace_id === activeInst.workspace_id)
      .map(inst => inst.id)
      .filter(Boolean));
    const matchingStripeSubscription = (billingOverview?.subscriptions || []).find(row =>
      row?.billing_products?.product_line === 'institutions' &&
      String(row?.provider || '').toLowerCase() === 'stripe' &&
      activeStatuses.has(String(row?.status || '').toLowerCase()) &&
      row?.target_id &&
      (row.target_id === activeId || workspaceInstituteIds.has(row.target_id)));
    const stripeSubscription = matchingStripeSubscription || (!activeId
      ? (billingOverview?.subscriptions || []).find(row =>
        row?.billing_products?.product_line === 'institutions' &&
        String(row?.provider || '').toLowerCase() === 'stripe' &&
        activeStatuses.has(String(row?.status || '').toLowerCase()) &&
        row?.target_id)
      : null);
    const instituteId = stripeSubscription?.target_id || null;
    if (!instituteId || openingInstitutionBilling) {
      if (!instituteId && !openingInstitutionBilling) {
        setToast({ id: Date.now(), message: 'No card subscription was found for this institution.', tone: 'warning' });
      }
      return;
    }
    setOpeningInstitutionBilling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) throw new Error('Sign in on the website before managing institution billing.');
      const { data, error } = await supabase.functions.invoke('institutions_create_billing_portal', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          institute_id: instituteId,
          return_url: `${window.location.origin}/profile?tab=institutes&institutions=billing`,
        },
      });
      if (error || data?.error || !data?.url) throw new Error(data?.error || error?.message || 'Billing portal URL was not returned.');
      window.location.href = data.url;
    } catch (err) {
      setToast({ id: Date.now(), message: err?.message || 'Could not open billing portal.', tone: 'error' });
    } finally {
      setOpeningInstitutionBilling(false);
    }
  }, [activeInst?.id, activeInst?.workspace_id, billingOverview?.subscriptions, institutes, openingInstitutionBilling]);

  const notify = useCallback((message, tone = 'info') => {
    setToast({ id: Date.now(), message, tone });
  }, []);

  const loadInstitutionAdmins = useCallback(async (institute) => {
    if (!institute?.id) {
      setInstitutionAdmins([]);
      setInstitutionAdminsError('');
      return;
    }
    setInstitutionAdminsLoading(true);
    setInstitutionAdminsError('');
    try {
      const { data: adminRows, error: adminError } = await supabase
        .from('institute_admins')
        .select('id,institute_id,profile_id,role,created_at')
        .eq('institute_id', institute.id)
        .order('created_at', { ascending: true });
      if (adminError) throw adminError;

      const profileIds = Array.from(new Set([
        institute.owner_profile_id,
        ...(adminRows || []).map(row => row.profile_id),
      ].filter(Boolean)));
      let profileById = new Map();
      if (profileIds.length) {
        const { data: profileRows, error: profileError } = await supabase
          .from('profiles')
          .select('id,name,surname,username,profile_img')
          .in('id', profileIds);
        if (profileError) throw profileError;
        profileById = new Map((profileRows || []).map(row => [row.id, row]));
      }

      const ownerRow = institute.owner_profile_id ? {
        id: `owner-${institute.owner_profile_id}`,
        institute_id: institute.id,
        profile_id: institute.owner_profile_id,
        role: 'owner',
        profiles: profileById.get(institute.owner_profile_id) || (institute.owner_profile_id === profileData?.id ? profileData : null),
      } : null;
      const assignedRows = (adminRows || [])
        .filter(row => row.profile_id !== institute.owner_profile_id)
        .map(row => ({ ...row, profiles: profileById.get(row.profile_id) || null }));
      setInstitutionAdmins([ownerRow, ...assignedRows].filter(Boolean));
    } catch (error) {
      setInstitutionAdmins([]);
      setInstitutionAdminsError(error?.message || 'Could not load institution admins.');
    } finally {
      setInstitutionAdminsLoading(false);
    }
  }, [profileData]);

  useEffect(() => {
    loadInstitutionAdmins(activeInst);
  }, [activeInst, instituteRefresh, loadInstitutionAdmins]);

  const grantInstitutionAdmin = useCallback(async (member) => {
    if (!activeInst?.id || activeInst.access_role !== 'owner' || !profileData?.id || !member?.id || changingInstitutionAdminId) return;
    setChangingInstitutionAdminId(member.id);
    try {
      const { error } = await supabase.from('institute_admins').insert({
        institute_id: activeInst.id,
        profile_id: member.id,
        role: 'admin',
        created_by: profileData.id,
      });
      if (error) throw error;
      await loadInstitutionAdmins(activeInst);
      notify(`${member.name || member.username || 'Member'} can now manage this institute.`, 'success');
    } catch (error) {
      notify(error?.message || 'Could not add institution admin.', 'error');
    } finally {
      setChangingInstitutionAdminId(null);
    }
  }, [activeInst, changingInstitutionAdminId, loadInstitutionAdmins, notify, profileData?.id]);

  const resetInstituteSteps = useCallback(async (targetInst = activeInst) => {
    const targetId = targetInst?.id || null;
    if (!targetId || resettingInstitutionSteps) return;
    setResettingInstitutionSteps(true);
    try {
      const { data, error } = await supabase.rpc('reset_institute_steps', {
        p_institute_id: targetId,
        p_mode: 'all',
      });
      if (error) throw error;

      setInstitutes(prev => prev.map(inst => {
        if (inst.id !== targetId) return inst;
        return {
          ...inst,
          members: (inst.members || []).map(member => ({ ...member, steps: 0 })),
          departments: (inst.departments || []).map(dept => ({
            ...dept,
            totalSteps: 0,
            total_steps: 0,
            members: (dept.members || []).map(member => ({ ...member, steps: 0 })),
          })),
        };
      }));
      setInstituteRefresh(value => value + 1);
      const cleared = Number(data || 0);
      notify(`Reset current steps for ${cleared.toLocaleString()} member${cleared === 1 ? '' : 's'}. Lifetime steps were not changed.`, 'success');
    } catch (err) {
      notify(err?.message || 'Could not reset steps.', 'error');
    } finally {
      setResettingInstitutionSteps(false);
    }
  }, [activeInst, notify, resettingInstitutionSteps]);

  const focusLiveDashboardSetup = useCallback(() => {
    const panel = document.getElementById('institution-live-dashboard-profile-panel');
    if (panel) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      panel.focus({ preventScroll: true });
    }
  }, []);

  const repairInstitutionWorkspace = useCallback(async () => {
    if (!activeInst?.id || !profileData?.id) throw new Error('No institute workspace found for this account.');
    const workspaceName = activeInst.name || profileData.username || 'Workspace';
    let workspace = null;

    if (activeInst.workspace_id) {
      const { data: currentWorkspace, error: currentError } = await supabase
        .from('institution_workspaces')
        .select('id,name,archived_at')
        .eq('id', activeInst.workspace_id)
        .maybeSingle();
      if (currentError) throw currentError;
      if (currentWorkspace?.id) {
        if (currentWorkspace.archived_at) {
          const { data: restored, error: restoreError } = await supabase
            .from('institution_workspaces')
            .update({ archived_at: null, updated_at: new Date().toISOString() })
            .eq('id', currentWorkspace.id)
            .select('id,name')
            .single();
          if (restoreError) throw restoreError;
          workspace = restored;
        } else {
          workspace = currentWorkspace;
        }
      }
    }

    if (!workspace?.id) {
      const { data: existingRows, error: lookupError } = await supabase
        .from('institution_workspaces')
        .select('id,name')
        .eq('owner_profile_id', profileData.id)
        .is('archived_at', null)
        .order('created_at', { ascending: true })
        .limit(1);
      if (lookupError) throw lookupError;

      workspace = existingRows?.[0] || null;
      if (!workspace?.id) {
        const { data: created, error: createError } = await supabase
          .from('institution_workspaces')
          .insert({ owner_profile_id: profileData.id, name: workspaceName })
          .select('id,name')
          .single();
        if (createError) throw createError;
        workspace = created;
      }
    }

    const { error: updateError } = await supabase
      .from('institutes')
      .update({ workspace_id: workspace.id })
      .eq('id', activeInst.id);
    if (updateError) throw updateError;

    setInstitutes(prev => prev.map(inst => inst.id === activeInst.id ? { ...inst, workspace_id: workspace.id, workspace_archived: false } : inst));
    return workspace.id;
  }, [activeInst?.id, activeInst?.name, activeInst?.workspace_id, profileData?.id, profileData?.username]);

  const openLiveDashboard = useCallback(async () => {
    if (!activeInst?.id || openingLiveDashboard) return;
    setOpeningLiveDashboard(true);
    try {
      let workspaceId = activeInst.workspace_id;
      if (workspaceId && activeInst.workspace_archived) {
        workspaceId = await repairInstitutionWorkspace();
      }

      let response = workspaceId
        ? await supabase.rpc('get_institution_live_dashboard_admin', { p_workspace_id: workspaceId })
        : { data: null, error: new Error('Workspace not found') };

      if (response.error && /workspace not found/i.test(response.error.message || '')) {
        workspaceId = await repairInstitutionWorkspace();
        response = await supabase.rpc('get_institution_live_dashboard_admin', { p_workspace_id: workspaceId });
      }

      if (response.error) throw response.error;
      const data = response.data;
      if (!data?.has_access) {
        const inactiveAccess = activeInst?.accessLocked || isInstitutionInactive(activeInst?.billing_status);
        const params = new URLSearchParams();
        if (inactiveAccess) params.set('restart', '1');
        else params.set('feature', 'live-dashboard');
        if (activeInst?.id) params.set('institute_id', activeInst.id);
        if (workspaceId || activeInst?.workspace_id) params.set('workspace_id', workspaceId || activeInst.workspace_id);
        const storedTier = activeInst?.plan_tier || activeInst?.brand_settings?.planTier || '';
        if (storedTier) params.set('plan_tier', storedTier);
        notify(inactiveAccess ? 'Institution access is inactive. Renew membership to unlock the dashboard.' : 'This workspace needs an active live dashboard entitlement.', 'warning');
        navigate(`/institutions/create?${params.toString()}`);
        return;
      }
      const liveSlug = workspaceNameSlug(data?.workspace_name || activeInst?.workspace_name || activeInst?.name);
      if (!data?.enabled || !liveSlug) {
        notify('Publish the live dashboard first.', 'warning');
        focusLiveDashboardSetup();
        return;
      }
      navigate(`/institutions/${liveSlug}`, {
        state: { institutionReturn: { workspaceId: workspaceId || activeInst?.workspace_id || '', instituteId: activeInst?.id || '' } },
      });
    } catch (err) {
      notify(err?.message || 'Could not open the live dashboard.', 'error');
      focusLiveDashboardSetup();
    } finally {
      setOpeningLiveDashboard(false);
    }
  }, [activeInst?.accessLocked, activeInst?.billing_status, activeInst?.brand_settings, activeInst?.id, activeInst?.name, activeInst?.plan_tier, activeInst?.workspace_archived, activeInst?.workspace_id, activeInst?.workspace_name, focusLiveDashboardSetup, navigate, notify, openingLiveDashboard, repairInstitutionWorkspace]);

  const resetAppLeaderboardBranding = () => {
    setAppLeaderboardForm(DEFAULT_APP_LEADERBOARD_SETTINGS);
  };

  const saveAppLeaderboardBranding = async () => {
    if (!activeInst?.id || savingAppLeaderboardBranding) return;
    const nextSettings = {
      ...(activeInst.brand_settings || {}),
      ...DEFAULT_APP_LEADERBOARD_SETTINGS,
      ...appLeaderboardForm,
      primaryColor: normalizeHexInput(appLeaderboardForm.primaryColor, DEFAULT_APP_LEADERBOARD_SETTINGS.primaryColor),
      accentColor: normalizeHexInput(appLeaderboardForm.accentColor, DEFAULT_APP_LEADERBOARD_SETTINGS.accentColor),
      supportColor: normalizeHexInput(appLeaderboardForm.supportColor, DEFAULT_APP_LEADERBOARD_SETTINGS.supportColor),
      highlightColor: normalizeHexInput(appLeaderboardForm.highlightColor, DEFAULT_APP_LEADERBOARD_SETTINGS.highlightColor),
      backgroundColor: normalizeHexInput(appLeaderboardForm.backgroundColor, DEFAULT_APP_LEADERBOARD_SETTINGS.backgroundColor),
      applyBranding: appLeaderboardForm.applyBranding !== false,
    };
    setSavingAppLeaderboardBranding(true);
    try {
      const { error } = await supabase
        .from('institutes')
        .update({
          brand_settings: nextSettings,
        })
        .eq('id', activeInst.id);
      if (error) throw error;
      setInstitutes(prev => prev.map(inst => (
        inst.id === activeInst.id
          ? { ...inst, brand_settings: nextSettings }
          : inst
      )));
      setAppLeaderboardForm(nextSettings);
      notify('Leaderboard app branding saved.', 'success');
    } catch (err) {
      notify(err?.message || 'Could not save leaderboard app branding.', 'error');
    } finally {
      setSavingAppLeaderboardBranding(false);
    }
  };

  const openWorkspaceRename = () => {
    if (!activeInst?.workspace_id) return;
    setWorkspaceRenameValue(activeInst.workspace_name || activeInst.name || '');
    setWorkspaceLogoFile(null);
    setWorkspaceLogoPreview(activeInst.workspace_logo_url || null);
    setShowWorkspaceRename(true);
  };

  const closeWorkspaceSettings = () => {
    if (savingWorkspaceRename) return;
    setShowWorkspaceRename(false);
    setWorkspaceRenameValue('');
    setWorkspaceLogoFile(null);
    setWorkspaceLogoPreview(null);
  };

  const saveWorkspaceRename = async () => {
    const nextName = workspaceRenameValue.trim();
    const workspaceId = activeInst?.workspace_id;
    if (!workspaceId || !nextName || savingWorkspaceRename) return;
    setSavingWorkspaceRename(true);
    try {
      let logoUrl = activeInst.workspace_logo_url || null;
      if (workspaceLogoFile) {
        const ext = workspaceLogoFile.name.split('.').pop() || 'png';
        const path = `workspaces/${workspaceId}/logo-${Date.now()}.${ext}`;
        logoUrl = await uploadFileToR2({ bucket: 'media', folder: 'institutes-workspaces', filename: path.replace(/[/\\]+/g, '-'), file: workspaceLogoFile });
      }
      const { error } = await supabase
        .from('institution_workspaces')
        .update({ name: nextName, logo_url: logoUrl, updated_at: new Date().toISOString() })
        .eq('id', workspaceId)
        .select('id')
        .single();
      if (error) throw error;
      setInstitutes(prev => prev.map(inst => inst.workspace_id === workspaceId ? { ...inst, workspace_name: nextName, workspace_logo_url: logoUrl } : inst));
      setShowWorkspaceRename(false);
      setWorkspaceRenameValue('');
      setWorkspaceLogoFile(null);
      setWorkspaceLogoPreview(null);
      notify('Workspace settings saved.', 'success');
    } catch (err) {
      notify(err?.message || 'Could not save workspace settings.', 'error');
    } finally {
      setSavingWorkspaceRename(false);
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const askConfirm = useCallback((config) => {
    setConfirmDialog({
      title: config.title || 'Confirm action',
      message: config.message || '',
      confirmLabel: config.confirmLabel || 'Confirm',
      tone: config.tone || 'default',
      onConfirm: config.onConfirm,
    });
  }, []);

  const removeInstitutionAdmin = useCallback((adminRow) => {
    if (!activeInst?.id || activeInst.access_role !== 'owner' || !adminRow?.profile_id || adminRow.role === 'owner') return;
    const displayName = institutionAdminDisplayName(adminRow.profiles);
    askConfirm({
      title: 'Remove institute admin',
      message: `Remove ${displayName}'s admin access to ${activeInst.name}? They will remain an institute member.`,
      confirmLabel: 'Remove admin',
      tone: 'danger',
      onConfirm: async () => {
        setChangingInstitutionAdminId(adminRow.profile_id);
        try {
          const { error } = await supabase
            .from('institute_admins')
            .delete()
            .eq('institute_id', activeInst.id)
            .eq('profile_id', adminRow.profile_id);
          if (error) throw error;
          await loadInstitutionAdmins(activeInst);
          notify(`${displayName}'s admin access was removed.`, 'success');
        } catch (error) {
          notify(error?.message || 'Could not remove institution admin.', 'error');
        } finally {
          setChangingInstitutionAdminId(null);
        }
      },
    });
  }, [activeInst, askConfirm, loadInstitutionAdmins, notify]);

  const confirmResetInstitutionSteps = useCallback(() => {
    if (!activeInst?.id || resettingInstitutionSteps) return;
    const targetInst = activeInst;
    const memberLabel = targetInst.type === 'Challenge' ? 'participant' : 'member';
    askConfirm({
      title: 'Reset steps',
      message: `Reset every current ${memberLabel} step value in ${targetInst.name} to 0? Lifetime steps, members, departments and history stay saved. This cannot be undone.`,
      confirmLabel: resettingInstitutionSteps ? 'Resetting...' : 'Reset steps',
      tone: 'danger',
      onConfirm: () => resetInstituteSteps(targetInst),
    });
  }, [activeInst, askConfirm, resetInstituteSteps, resettingInstitutionSteps]);

  const copyToClipboard = (text, label = 'Code') => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => notify(`${label} copied.`)).catch(() => {
        // fallback for iOS
        const el = document.createElement('textarea');
        el.value = text; el.style.position = 'fixed'; el.style.opacity = '0';
        document.body.appendChild(el); el.focus(); el.select();
        try { document.execCommand('copy'); notify(`${label} copied.`); } catch {}
        document.body.removeChild(el);
      });
    }
  };

  const reloadTransactionData = useCallback(async () => {
    const userId = profileData?.id;
    if (!userId) return;
    setTransactionsLoading(true);
    setTransactionsError('');
    try {
      const [txResult, orderResult, billingResult] = await Promise.all([
        supabase.from('transactions').select('*').eq('profile_id', userId).order('created_at', { ascending: false }),
        supabase.from('orders').select('*, order_items(*)').eq('profile_id', userId).order('created_at', { ascending: false }).limit(100),
        loadBillingOverview(userId).catch((error) => ({ __error: error })),
      ]);
      if (txResult.error) throw txResult.error;
      setTransactions(txResult.data || []);
      if (orderResult.error) {
        console.warn('reload store orders:', orderResult.error?.message || orderResult.error);
        setStoreOrders([]);
      } else {
        setStoreOrders(orderResult.data || []);
      }
      if (billingResult?.__error) {
        console.warn('reload billing overview:', billingResult.__error?.message || billingResult.__error);
        setBillingOverview(getEmptyBillingOverview());
        setBillingOverviewError('Could not load plans and coin activity. Refresh the page or try again later.');
      } else {
        setBillingOverview(billingResult || getEmptyBillingOverview());
        setBillingOverviewError('');
      }
    } catch (err) {
      console.warn('reload transactions:', err?.message || err);
      setTransactionsError('Transactions could not load');
    } finally {
      setTransactionsLoading(false);
    }
  }, [profileData?.id]);

  const refreshPayoutSetupStatus = useCallback(async (action = 'status') => {
    if (!profileData?.id) return;
    setPayoutSetupLoading(true);
    setPayoutSetupError('');
    try {
      const { data, error } = await supabase.functions.invoke('creator_payout_connect', {
        body: { action },
      });
      if (error) {
        const parsed = await readSupabaseFunctionError(error, 'Could not check payout setup.');
        setPayoutSetupError(parsed.message);
        return;
      }
      if (data?.error || data?.ok === false) {
        console.warn('Stripe Connect setup status response:', data);
        setPayoutSetupError('Could not check payout setup.');
        return;
      }
      setPayoutAccount(data?.account || null);
    } catch (err) {
      console.warn('Stripe Connect setup status failed:', err);
      setPayoutSetupError('Could not check payout setup.');
    } finally {
      setPayoutSetupLoading(false);
    }
  }, [profileData?.id]);

  const startStripeConnectSetup = useCallback(async () => {
    setPayoutSetupLoading(true);
    setPayoutSetupError('');
    try {
      const returnPath = '/profile?tab=transactions&payout_setup=stripe_connect&connect_return=1';
      const { data, error } = await supabase.functions.invoke('creator_payout_connect', {
        body: {
          action: 'create_onboarding_link',
          country: profileData?.current_country || profileData?.country,
          return_url: `${window.location.origin}${returnPath}`,
          refresh_url: `${window.location.origin}${returnPath}`,
        },
      });
      if (error) {
        const parsed = await readSupabaseFunctionError(error, 'Could not start Stripe Connect setup.');
        setPayoutSetupError(parsed.message);
        setPayoutSetupLoading(false);
        return;
      }
      if (data?.error || !data?.url) {
        console.warn('Stripe Connect setup link response:', data);
        setPayoutSetupError('Could not start Stripe Connect setup.');
        setPayoutSetupLoading(false);
        return;
      }
      window.location.assign(data.url);
    } catch (err) {
      console.warn('Stripe Connect setup link failed:', err);
      setPayoutSetupError('Could not start Stripe Connect setup.');
      setPayoutSetupLoading(false);
    }
  }, [profileData?.country, profileData?.current_country]);

  useEffect(() => {
    if (!profileData?.id) return;
    if (searchParams.get('payout_setup') !== 'stripe_connect') return;
    setPayoutSetupVisible(true);
    setActiveTab('transactions');
    refreshPayoutSetupStatus(searchParams.get('connect_return') === '1' ? 'refresh_status' : 'status');
  }, [profileData?.id, refreshPayoutSetupStatus, searchParams]);
  /* ── Load profile + bookings + transactions ── */
  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setTransactionsLoading(false);
        return;
      }
      const profileRequest = supabase.from('profiles').select('*').eq('id', user.id).single();
      const otherDataPromise = Promise.all([
        supabase.from('bookings').select('*,locations(name),profiles(name,surname)').eq('profile_id', user.id),
        supabase.from('sport_profiles').select('*').eq('profile_id', user.id),
        supabase.from('user_teams').select('team_id').eq('profile_id', user.id),
        supabase.from('transactions').select('*').eq('profile_id', user.id).order('created_at', { ascending: false }),
        supabase.from('orders').select('*, order_items(*)').eq('profile_id', user.id).order('created_at', { ascending: false }).limit(100),
        supabase.from('achievements').select('*').order('sport_id').order('target_value'),
        supabase.from('user_achievements').select('achievement_id,completed_at').eq('profile_id', user.id),
        supabase.from('matches').select('*').or(`profile1_id.eq.${user.id},profile2_id.eq.${user.id}`).order('match_finished_at', { ascending: false }),
        supabase.from('sports').select('id,icon_url').eq('active', true),
        loadBillingOverview(user.id).catch((error) => ({ __error: error })),
      ]);
      const { data: profile } = await profileRequest;
      if (profile) setProfileData(profile);
      const [
        { data: bk },
        { data: sports },
        { data: userTeams },
        txResult,
        orderResult,
        { data: achievements },
        { data: userAch },
        { data: matches7 },
        { data: sportRows },
        billingResult,
      ] = await otherDataPromise;
      const sportIconMap = Object.fromEntries((sportRows || []).map(s => [s.id, s]));
      let teamData = [];
      if (userTeams?.length) {
        const { data: teams } = await supabase.from('teams').select('*,creator:creator_id(username),admin_profile:admin(username),user_teams(profiles(id,username,name,surname,profile_img))').in('id', userTeams.map(t => t.team_id));
        teamData = teams || [];
      }
      setProfileData(current => ({
        ...current,
        sports: (sports || []).filter(s => sportIconMap[s.sport_id]).map(s => ({ ...s, sport_icon_url: sportIconMap[s.sport_id]?.icon_url || null })),
        teams: teamData.filter(t => sportIconMap[t.sport_id]).map(t => ({ ...t, sport_icon_url: sportIconMap[t.sport_id]?.icon_url || null, team_members: (t.user_teams || []).map(row => row.profiles).filter(Boolean) })),
        sportIconMap,
      }));
      setBookings(bk || []);
      if (txResult?.error) {
        console.warn('load transactions:', txResult.error?.message || txResult.error);
        setTransactions([]);
        setTransactionsError('Transactions could not load');
      } else {
        setTransactions(txResult?.data || []);
        setTransactionsError('');
      }
      if (orderResult?.error) {
        console.warn('load store orders:', orderResult.error?.message || orderResult.error);
        setStoreOrders([]);
      } else {
        setStoreOrders(orderResult?.data || []);
      }
      setTransactionsLoading(false);
      if (billingResult?.__error) {
        console.warn('load billing overview:', billingResult.__error?.message || billingResult.__error);
        setBillingOverview(getEmptyBillingOverview());
        setBillingOverviewError('Could not load plans and coin activity. Refresh the page or try again later.');
      } else {
        setBillingOverview(billingResult || getEmptyBillingOverview());
        setBillingOverviewError('');
      }
      setAllAchievements(achievements || []);
      setUserAchievements(userAch || []);
      setMatches(matches7 || []);

      let teamMatchData = [];
      if (teamData.length) {
        const teamIds = teamData.map(t => t.id);
        const { data: tm } = await supabase.from('team_matches').select('*').or(teamIds.map(id => `team1_id.eq.${id},team2_id.eq.${id}`).join(',')).order('match_finished_at', { ascending: false });
        teamMatchData = tm || [];
      }
      setTeamMatches(teamMatchData);

      const allLocationIds = [...new Set([...(matches7 || []).map(m => m.location_id), ...teamMatchData.map(m => m.location_id)].filter(Boolean))];
      if (allLocationIds.length) {
        const { data: locs } = await supabase.from('locations').select('id,name,image_url,address').in('id', allLocationIds);
        const locMap = {};
        for (const l of locs || []) locMap[l.id] = l;
        setMatchLocations(locMap);
      }
      if (profile?.country) {
        const profileCountry = findCountry(profile.country);
        setRankCountry(profileCountry?.name || profile.country);
        setRankCountryCode(profileCountry?.isoCode || '');
      }
      if (profile?.region) setRankRegion(profile.region);
    };
    load();
  }, []);

  /* ── Load institutes ── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!profileData?.id) return;
      setInstitutesLoading(true);
      setInstitutesError('');
      const instituteSelect = 'id,is_active,name,institute_type,access_code,teams,country,state_province,image_url,address,brand_settings,brand_logo_url,workspace_id,auto_join_institute_id,starts_at,ends_at,challenge_member_limit,goal_steps,plan_tier,billing_status,profile_id';
      const [{ data: ownedRows, error: ownedError }, { data: archivedRows }, { data: adminAssignments, error: adminError }] = await Promise.all([
        supabase.from('institutes').select(instituteSelect).eq('profile_id', profileData.id).is('archived_at', null).order('name', { ascending: true }),
        supabase.from('institutes').select('id,name,institute_type,teams,archived_at').eq('profile_id', profileData.id).not('archived_at', 'is', null).order('archived_at', { ascending: false }),
        supabase.from('institute_admins').select('institute_id,role').eq('profile_id', profileData.id),
      ]);
      if (cancelled) return;
      setArchivedInstitutes(archivedRows || []);
      if (ownedError || adminError) {
        console.warn('Could not load managed institutes', ownedError?.message || adminError?.message);
        setInstitutesError('Could not load institutes. Check your connection and try again.');
        setInstitutesLoading(false);
        return;
      }
      const ownedIds = new Set((ownedRows || []).map((row) => row.id));
      const administeredIds = (adminAssignments || [])
        .map((row) => row.institute_id)
        .filter((id) => id && !ownedIds.has(id));
      let administeredRows = [];
      if (administeredIds.length) {
        const { data, error } = await supabase
          .from('institutes')
          .select(instituteSelect)
          .in('id', administeredIds)
          .is('archived_at', null)
          .order('name', { ascending: true });
        if (error) {
          console.warn('Could not load institutes managed as admin', error.message);
        } else {
          administeredRows = data || [];
        }
      }
      const instRows = mergeManagedInstituteRows({
        ownedRows: ownedRows || [],
        adminAssignments: adminAssignments || [],
        administeredRows,
      });
      const instituteIds = instRows.map(row => row.id).filter(Boolean);
      const workspaceIds = Array.from(new Set(instRows.map(row => row.workspace_id).filter(Boolean)));
      const [accessResult, workspaceResult] = await Promise.all([
        instituteIds.length ? supabase
          .from('institutes_with_steps')
          .select('*')
          .in('id', instituteIds) : Promise.resolve({ data: [], error: null }),
        workspaceIds.length ? supabase
          .from('institution_workspaces')
          .select('id,name,logo_url,archived_at')
          .in('id', workspaceIds) : Promise.resolve({ data: [], error: null }),
      ]);
      if (cancelled) return;
      if (accessResult.error) console.warn('Could not load institution access state', accessResult.error.message);
      const accessById = new Map((accessResult.data || []).map(row => [row.id, row]));
      const workspaceById = new Map((workspaceResult.data || []).map(row => [row.id, row]));
      const base = instRows.map(r => {
        const workspace = r.workspace_id ? workspaceById.get(r.workspace_id) : null;
        const accessRow = accessById.get(r.id) || {};
        const billingStatus = accessRow.billing_status ?? r.billing_status;
        const displayLogoUrl = r.brand_logo_url || r.image_url || null;
        return { id: r.id, is_active: r.is_active !== false, name: r.name, type: r.institute_type, code: r.access_code, teams: !!r.teams, country: r.country, region: r.state_province, address: r.address, image_url: r.image_url, display_logo_url: displayLogoUrl, brand_settings: r.brand_settings || {}, brand_logo_url: r.brand_logo_url || null, plan_tier: r.plan_tier || accessRow.plan_tier || r.brand_settings?.planTier || null, billing_status: billingStatus, access_active: accessRow.access_active, accessLocked: accessRow.access_active === false || isInstitutionInactive(billingStatus), workspace_id: r.workspace_id || null, workspace_name: workspace?.name || '', workspace_logo_url: workspace?.logo_url || null, workspace_archived: !!workspace?.archived_at, auto_join_institute_id: r.auto_join_institute_id || null, starts_at: r.starts_at || null, ends_at: r.ends_at || null, challenge_member_limit: r.challenge_member_limit ?? null, goal_steps: r.goal_steps ?? r.brand_settings?.goalSteps ?? null, owner_profile_id: r.profile_id || null, access_role: r.access_role || (r.profile_id === profileData.id ? 'owner' : 'admin'), members: [], departments: [] };
      });
      base.sort(instituteCardOrder);
      await publishInstitutesAndLoadRosters({
        institutes: base,
        publish: listedInstitutes => {
          setInstitutes(previous => listedInstitutes.map(inst => ({
            ...inst,
            members: previous.find(item => item.id === inst.id)?.members || [],
          })));
          setInstitutesLoading(false);
          setLoadingInstituteMemberIds(listedInstitutes.map(inst => inst.id));
          if (listedInstitutes.length) {
            setActiveInstIdx(() => {
              const currentId = activeInstituteIdRef.current;
              const preservedIdx = currentId ? listedInstitutes.findIndex(inst => inst.id === currentId) : -1;
              if (preservedIdx >= 0) return preservedIdx;
              const membersIdx = listedInstitutes.findIndex(inst => !inst.teams && inst.type !== 'Challenge');
              if (membersIdx >= 0) return membersIdx;
              const nonChallengeIdx = listedInstitutes.findIndex(inst => inst.type !== 'Challenge');
              return nonChallengeIdx >= 0 ? nonChallengeIdx : 0;
            });
          }
        },
        loadRoster: async inst => {
          const { data: rows } = await supabase.from('user_institutes').select('id,steps,lifetime_steps,team_id,association_type,status,profiles:profiles(id,name,surname,username,profile_img)').eq('institute_id', inst.id).neq('status', 'Former');
          const demoRows = await fetchDemoInstituteMemberRows(supabase, { instituteId: inst.id });
          return mergeDemoInstituteMembers(rows || [], demoRows).map(r => ({ id: r.profiles?.id ?? r.demo_profile_id ?? r.id, demo_profile_id: r.demo_profile_id || null, is_demo_profile: !!r.is_demo_profile, name: [r.profiles?.name, r.profiles?.surname].filter(Boolean).join(' ') || r.profiles?.username || 'Unknown', username: r.profiles?.username || null, profile_img: r.profiles?.profile_img || null, steps: r.steps ?? 0, team_id: r.team_id || null, association_type: r.association_type, status: r.status }));
        },
        publishRoster: (id, members) => {
          setInstitutes(previous => previous.map(item => item.id === id ? { ...item, members } : item));
          setLoadingInstituteMemberIds(previous => previous.filter(itemId => itemId !== id));
        },
        isCancelled: () => cancelled,
      });
    };
    load().catch(error => {
      if (cancelled) return;
      console.warn('Could not load managed institutes', error);
      setInstitutesError('Could not load institutes. Check your connection and try again.');
      setInstitutesLoading(false);
      setLoadingInstituteMemberIds([]);
    });
    return () => { cancelled = true; };
  }, [instituteRefresh, profileData?.id]);

  useEffect(() => {
    if (!profileData?.id) return undefined;

    let refreshTimer = null;
    const scheduleRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        setInstituteRefresh(value => value + 1);
      }, 300);
    };

    const channel = supabase
      .channel(`institution-admin-web-${profileData.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'institutes',
        filter: `profile_id=eq.${profileData.id}`,
      }, scheduleRefresh)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'institute_admins',
        filter: `profile_id=eq.${profileData.id}`,
      }, scheduleRefresh);

    realtimeInstituteIds.forEach(instituteId => {
      channel
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'institutes',
          filter: `id=eq.${instituteId}`,
        }, scheduleRefresh)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_institutes',
          filter: `institute_id=eq.${instituteId}`,
        }, scheduleRefresh)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'institute_teams',
          filter: `institute_id=eq.${instituteId}`,
        }, scheduleRefresh)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'institute_admins',
          filter: `institute_id=eq.${instituteId}`,
        }, scheduleRefresh);
    });

    realtimeWorkspaceIds.forEach(workspaceId => {
      channel.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'institution_workspaces',
        filter: `id=eq.${workspaceId}`,
      }, scheduleRefresh);
    });

    channel.subscribe();

    return () => {
      window.clearTimeout(refreshTimer);
      supabase.removeChannel(channel);
    };
  }, [
    profileData?.id,
    realtimeInstituteIds,
    realtimeWorkspaceIds,
  ]);

  /* ── Load workout groups ── */
  const loadWG = useCallback(async () => {
    if (!profileData?.id) return;
    setLoadingWG(true);
    try {
      const selectWithAppSettings = 'id,name,coach_id,created_by,created_at,weekly_xp_target,weekly_session_target,session_target_goal_key,session_target_tracking_key,session_target_intensity_key,member_analytics_visibility,daily_calorie_target,allowed_workout_names,allowed_workout_pairs,join_code,club_id,season_id,coach:profiles!workout_groups_coach_id_fkey(id,username,name,surname,profile_img),workout_clubs(id,owner_profile_id,name,sport_id,logo_url,primary_color,secondary_color,accent_color,brand_settings),workout_club_seasons(id,name,status,starts_on,ends_on)';
      const selectWithoutVisibility = selectWithAppSettings.replace(',member_analytics_visibility', '');
      const selectLegacy = selectWithAppSettings.replace(',session_target_goal_key,session_target_tracking_key,session_target_intensity_key,member_analytics_visibility', '');
      const selectVariants = [selectWithAppSettings, selectWithoutVisibility, selectLegacy];
      const fetchRowsForSelect = async (select) => {
        const [ownedResult, memberResult, staffResult] = await Promise.all([
          supabase.from('workout_groups').select(select).or(`coach_id.eq.${profileData.id},created_by.eq.${profileData.id}`).order('created_at', { ascending: false }),
          supabase.from('workout_group_members').select(`workout_groups(${select})`).eq('athlete_id', profileData.id),
          supabase.from('workout_club_coaches').select('club_id,status').eq('profile_id', profileData.id).eq('status', 'active'),
        ]);
        if (ownedResult.error) throw ownedResult.error;
        if (memberResult.error) throw memberResult.error;
        const staffClubIds = [...new Set((staffResult.data || []).map(row => row.club_id).filter(Boolean))];
        let staffClubGroups = [];
        if (staffClubIds.length) {
          const staffGroupsResult = await supabase.from('workout_groups').select(select).in('club_id', staffClubIds);
          if (staffGroupsResult.error) throw staffGroupsResult.error;
          staffClubGroups = staffGroupsResult.data || [];
        }
        return {
          ownedRows: ownedResult.data || [],
          memberRows: memberResult.data || [],
          staffClubGroups,
        };
      };
      let loadedRows = null;
      let lastLoadError = null;
      for (const select of selectVariants) {
        try {
          loadedRows = await fetchRowsForSelect(select);
          break;
        } catch (err) {
          lastLoadError = err;
          if (!isMissingWorkoutGroupColumnError(err)) throw err;
          console.warn('loadWG falling back after workout_groups schema mismatch:', err);
        }
      }
      if (!loadedRows && lastLoadError) throw lastLoadError;
      const { ownedRows = [], memberRows = [], staffClubGroups = [] } = loadedRows || {};
      const byId = new Map();
      const mergeGroup = (g, patch) => {
        const existing = byId.get(g.id) || {};
        byId.set(g.id, { ...existing, ...g, ...patch, __isClubStaff: existing.__isClubStaff || patch.__isClubStaff, __isMember: existing.__isMember || patch.__isMember });
      };
      (ownedRows || []).forEach(g => mergeGroup(g, { memberRole: g.coach_id === profileData.id ? 'coach' : 'creator', __isGroupCoach: true }));
      staffClubGroups.forEach(g => mergeGroup(g, { memberRole: 'club_staff', __isClubStaff: true }));
      (memberRows || []).forEach(row => {
        const g = Array.isArray(row.workout_groups) ? row.workout_groups[0] : row.workout_groups;
        if (g?.id) mergeGroup(g, { memberRole: byId.get(g.id)?.memberRole || 'athlete', __isMember: true });
      });
      const rows = Array.from(byId.values()).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      const groupIds = rows.map(g => g.id).filter(Boolean);
      let groupMemberCounts = {};
      if (groupIds.length) {
        const [{ data: memberCountRows, error: memberCountError }, { data: demoMemberCountRows, error: demoMemberCountError }] = await Promise.all([
          supabase.from('workout_group_members').select('group_id').in('group_id', groupIds),
          supabase.from('demo_workout_group_members').select('group_id,squad_role,counts_as_athlete').in('group_id', groupIds),
        ]);
        if (memberCountError) throw memberCountError;
        if (demoMemberCountError) console.warn('loadWG demo member count error:', demoMemberCountError);
        groupMemberCounts = [
          ...(memberCountRows || []),
          ...(demoMemberCountRows || []).filter(isCountingWorkoutAthlete),
        ].reduce((acc, row) => {
          if (!row?.group_id) return acc;
          acc[row.group_id] = (acc[row.group_id] || 0) + 1;
          return acc;
        }, {});
      }
      setWorkoutGroups(rows.map(g => ({ id: g.id, name: g.name, coach_id: g.coach_id, created_by: g.created_by, memberRole: g.memberRole, __isClubStaff: !!g.__isClubStaff, __isMember: !!g.__isMember, __isGroupCoach: !!g.__isGroupCoach, memberCount: groupMemberCounts[g.id] || 0, weekly_xp_target: g.weekly_xp_target ?? 0, weekly_session_target: g.weekly_session_target ?? 0, session_target_goal_key: g.session_target_goal_key || null, session_target_tracking_key: g.session_target_tracking_key || null, session_target_intensity_key: g.session_target_intensity_key || null, member_analytics_visibility: getWorkoutGroupMemberAnalyticsVisibility(g), daily_calorie_target: g.daily_calorie_target ?? 2500, allowed_workout_names: g.allowed_workout_names || [], allowed_workout_pairs: g.allowed_workout_pairs || [], join_code: g.join_code ?? null, club_id: g.club_id || null, season_id: g.season_id || null, workout_clubs: g.workout_clubs || null, workout_club_seasons: g.workout_club_seasons || null, coachName: [g.coach?.name, g.coach?.surname].filter(Boolean).join(' ') || (g.coach?.username ? `@${g.coach.username}` : 'Unknown'), coachUsername: g.coach?.username || null, coachProfileImg: g.coach?.profile_img || null })));
    } finally { setLoadingWG(false); }
  }, [profileData?.id]);

  useEffect(() => { if (profileData?.id) loadWG(); }, [profileData?.id, loadWG]);

  const loadWorkoutGroupThemePrefs = useCallback(async () => {
    if (!profileData?.id) return;
    setWorkoutGroupThemePrefsLoaded(false);
    try {
      const { data, error } = await supabase
        .from('workout_group_theme_preferences')
        .select('theme_workout_groups_enabled,theme_workout_groups_club_id,theme_workouts_enabled,theme_workouts_club_id,theme_nutrition_enabled,theme_nutrition_club_id,theme_sessions_enabled,theme_sessions_club_id,updated_at')
        .eq('profile_id', profileData.id)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      const next = data ? parseWorkoutGroupThemePreferences(data) : null;
      setWorkoutGroupThemePreferences(next);
      setWorkoutGroupThemePrefsDraft(next ? { ...next } : { ...DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES });
    } finally {
      setWorkoutGroupThemePrefsLoaded(true);
    }
  }, [profileData?.id]);

  useEffect(() => { loadWorkoutGroupThemePrefs(); }, [loadWorkoutGroupThemePrefs]);

  const availableThemeClubs = useMemo(() => {
    const map = new Map();
    workoutGroups.forEach((group) => {
      const club = group?.workout_clubs || null;
      if (!club?.id) return;
      map.set(club.id, club);
    });
    return Array.from(map.values()).sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base', numeric: true }));
  }, [workoutGroups]);

  const getThemeClubOptionsForSurface = useCallback((surface) => {
    const toggleKey = WORKOUT_GROUP_THEME_SURFACES.find((item) => item.key === surface)?.clubToggleKey;
    return availableThemeClubs.filter((club) => {
      if (!club?.id) return false;
      if (surface === 'workout_groups') return club?.brand_settings?.[toggleKey] === true;
      return true;
    });
  }, [availableThemeClubs]);

  useEffect(() => {
    let cancelled = false;
    const loadVisibleOwnerAccess = async () => {
      if (!profileData?.id || workoutGroups.length === 0) {
        setWorkoutGroupOwnerAccessMap({});
        return;
      }
      const entries = await Promise.all(workoutGroups.map(async (group) => {
        const isPlanOwner = getWorkoutGroupPlanOwnerId(group) === profileData.id;
        if (isPlanOwner) return [group.id, workoutAccess || getDefaultWorkoutGroupAccess()];
        const cacheKey = `group:${group.id}`;
        const cached = wgOwnerAccessCacheRef.current.get(cacheKey);
        if (cached) return [group.id, cached];
        try {
          const access = await loadWorkoutGroupOwnerAccessForGroup(group.id);
          wgOwnerAccessCacheRef.current.set(cacheKey, access);
          return [group.id, access];
        } catch (err) {
          console.warn('load visible workout group owner access error:', err);
          return [group.id, null];
        }
      }));
      if (!cancelled) setWorkoutGroupOwnerAccessMap(Object.fromEntries(entries));
    };
    loadVisibleOwnerAccess();
    return () => {
      cancelled = true;
    };
  }, [profileData?.id, workoutGroups, workoutAccess]);

  const workoutGroupOwnerIndexMap = useMemo(() => {
    const ownerGroups = new Map();
    workoutGroups.forEach((group) => {
      const ownerId = getWorkoutGroupPlanOwnerId(group);
      if (!ownerId) return;
      const rows = ownerGroups.get(ownerId) || [];
      rows.push(group);
      ownerGroups.set(ownerId, rows);
    });
    const indexMap = {};
    ownerGroups.forEach((rows) => {
      rows
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base', numeric: true }))
        .forEach((group, index) => { indexMap[group.id] = index; });
    });
    return indexMap;
  }, [workoutGroups]);

  const getWorkoutGroupLockState = useCallback((group) => {
    if (!group || !profileData?.id) return { locked: false, canOpenLocked: false, reason: '', assignedCount: 0, memberCount: 0 };
    const isOwnGroup = group.coach_id === profileData.id || group.created_by === profileData.id;
    const isPlanOwner = getWorkoutGroupPlanOwnerId(group) === profileData.id;
    const memberCount = Number(group.memberCount || 0);
    const assignedCount = getWorkoutGroupAssignedWorkoutCount(group);
    const ownerAccess = isPlanOwner ? workoutAccess : workoutGroupOwnerAccessMap[group.id];
    if (!ownerAccess) {
      return {
        locked: true,
        canOpenLocked: false,
        reason: 'This group is locked while we check the owner\'s Workout Groups plan. Try again in a moment.',
        assignedCount,
        memberCount,
      };
    }
    const athleteLimit = ownerAccess?.limits?.athletes_per_group;
    const groupLimit = ownerAccess?.limits?.groups;
    const workoutLimit = ownerAccess?.limits?.assigned_workouts_per_group;
    const groupIndex = workoutGroupOwnerIndexMap[group.id] ?? 0;

    if (typeof athleteLimit === 'number' && memberCount > athleteLimit) {
      return {
        locked: true,
        canOpenLocked: isOwnGroup,
        reason: isPlanOwner
          ? `Your current plan allows ${athleteLimit} athlete${athleteLimit === 1 ? '' : 's'} per group. This group has ${memberCount}. Open it to remove athletes or upgrade.`
          : `This group is locked because the owner plan allows ${athleteLimit} athlete${athleteLimit === 1 ? '' : 's'} per group, but this group has ${memberCount}. The owner needs to remove athletes or upgrade.`,
        assignedCount,
        memberCount,
      };
    }
    if (typeof groupLimit === 'number' && groupIndex >= groupLimit) {
      return {
        locked: true,
        canOpenLocked: isOwnGroup,
        reason: isPlanOwner
          ? `Your current plan includes ${groupLimit} workout group${groupLimit === 1 ? '' : 's'}. Open it to delete a group or upgrade.`
          : `This group is locked because it is outside the owner plan limit of ${groupLimit} workout group${groupLimit === 1 ? '' : 's'}. The owner needs to delete a group or upgrade.`,
        assignedCount,
        memberCount,
      };
    }
    if (typeof workoutLimit === 'number' && assignedCount > workoutLimit) {
      return {
        locked: true,
        canOpenLocked: isOwnGroup,
        reason: isPlanOwner
          ? `Your current plan allows ${workoutLimit} assigned workout${workoutLimit === 1 ? '' : 's'} per group. This group has ${assignedCount}. Open it to remove assigned workouts or upgrade.`
          : `This group is locked because the owner plan allows ${workoutLimit} assigned workout${workoutLimit === 1 ? '' : 's'} per group, but this group has ${assignedCount}. The owner needs to remove assigned workouts or upgrade.`,
        assignedCount,
        memberCount,
      };
    }
    return { locked: false, canOpenLocked: false, reason: '', assignedCount, memberCount };
  }, [profileData?.id, workoutAccess, workoutGroupOwnerAccessMap, workoutGroupOwnerIndexMap]);

  useEffect(() => {
    const requestedGroupId = searchParams.get('group_id');
    const requestedTab = searchParams.get('tab');
    if (requestedTab !== 'workouts' || !requestedGroupId) return;
    const requestedGroup = workoutGroups.find(group => group.id === requestedGroupId);
    if (!requestedGroup) return;
    const lockState = getWorkoutGroupLockState(requestedGroup);
    if (lockState.locked && !lockState.canOpenLocked) {
      notify(lockState.reason, 'warning');
      return;
    }
    setActiveWGId(requestedGroupId);
  }, [searchParams, workoutGroups, getWorkoutGroupLockState, notify]);

  useEffect(() => {
    if (!workoutGroups.length) {
      if (activeWGId) {
        setActiveWGId(null);
        setActiveWG(null);
      }
      return;
    }
    if (activeWGId && workoutGroups.some(group => group.id === activeWGId)) return;
    const firstAvailableGroup = workoutGroups.find(group => {
      const lockState = getWorkoutGroupLockState(group);
      return !lockState.locked || lockState.canOpenLocked;
    }) || workoutGroups[0];
    if (firstAvailableGroup?.id) {
      setActiveWGId(firstAvailableGroup.id);
      setActiveWG(firstAvailableGroup);
    }
  }, [activeWGId, getWorkoutGroupLockState, workoutGroups]);

  const refreshWorkoutAccess = useCallback(async () => {
    if (!profileData?.id) return null;
    try {
      const nextAccess = await loadWorkoutGroupAccess(profileData.id);
      setWorkoutAccess(nextAccess);
      return nextAccess;
    } catch (err) {
      console.error('loadWorkoutGroupAccess error:', err);
      return null;
    }
  }, [profileData?.id]);

  const hasPaidWorkoutGroupAccess = useCallback((access) => {
    if (!access) return false;
    return (WORKOUT_GROUP_PLAN_ORDER[access.tier] ?? 0) > WORKOUT_GROUP_PLAN_ORDER.free ||
      (access.activeEntitlements || []).some(row => row?.source === 'stripe');
  }, []);
  const workoutGroupStripeSubscription = useMemo(() => {
    const subscription = workoutAccess?.activeSubscription;
    const source = String(workoutAccess?.source || subscription?.provider || '').toLowerCase();
    return source === 'stripe' ? subscription || { provider: 'stripe' } : null;
  }, [workoutAccess?.activeSubscription, workoutAccess?.source]);
  const canManageWorkoutGroupBilling = !!workoutGroupStripeSubscription;
  const currentWorkoutPlanRank = WORKOUT_GROUP_PLAN_ORDER[workoutAccess?.tier || 'free'] ?? 0;
  const workoutUpgradePlans = useMemo(
    () => WORKOUT_GROUP_WEB_PLANS.filter(plan => (WORKOUT_GROUP_PLAN_ORDER[plan.tier] ?? 0) > currentWorkoutPlanRank),
    [currentWorkoutPlanRank],
  );

  const refreshWorkoutAccessUntilSynced = useCallback(async () => {
    const waits = [0, 1200, 2400, 4200, 6500, 9000];
    let lastAccess = null;
    for (const waitMs of waits) {
      if (waitMs > 0) await new Promise(resolve => setTimeout(resolve, waitMs));
      lastAccess = await refreshWorkoutAccess();
      if (hasPaidWorkoutGroupAccess(lastAccess)) {
        return { accessReady: true, access: lastAccess };
      }
    }
    return { accessReady: false, access: lastAccess };
  }, [hasPaidWorkoutGroupAccess, refreshWorkoutAccess]);

  useEffect(() => {
    let mounted = true;
    if (!profileData?.id) return;
    loadWorkoutGroupAccess(profileData.id)
      .then(access => { if (mounted) setWorkoutAccess(access); })
      .catch(err => console.error('loadWorkoutGroupAccess error:', err));
    return () => { mounted = false; };
  }, [profileData?.id]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!['workout-groups-success', 'workout-groups-cancelled'].includes(checkout || '')) return;
    const returnKey = `${checkout}`;
    if (handledWorkoutCheckoutReturnRef.current === returnKey) return;
    handledWorkoutCheckoutReturnRef.current = returnKey;

    setActiveTab('workouts');
    setShowWorkoutPlans(true);

    if (checkout === 'workout-groups-cancelled') {
      setCheckoutMessage('Checkout was cancelled. No card payment was taken.');
      updateProfileSearch((next) => {
        next.set('tab', 'workouts');
        next.delete('checkout');
        next.delete('session_id');
        next.delete('checkout_session_id');
      }, { replace: true });
      return;
    }

    let cancelled = false;
    setCheckoutMessage('Payment returned from Stripe. Refreshing your Workout Groups access...');
    refreshWorkoutAccessUntilSynced()
      .then(({ accessReady, access }) => {
        if (cancelled) return;
        setCheckoutMessage(accessReady
          ? `${access?.planName || 'Your Workout Groups plan'} is active on this account.`
          : 'Checkout returned from Stripe, but access is still syncing. Use Refresh Access or wait a moment.');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('refreshWorkoutGroupAccessAfterCheckout error:', err);
        setCheckoutMessage(err?.message || 'Payment returned from Stripe, but access could not be refreshed yet. Use Refresh Access.');
        refreshWorkoutAccess();
      });

    updateProfileSearch((next) => {
      next.set('tab', 'workouts');
      next.delete('checkout');
      next.delete('session_id');
      next.delete('checkout_session_id');
    }, { replace: true });

    return () => {
      cancelled = true;
    };
  }, [refreshWorkoutAccess, refreshWorkoutAccessUntilSynced, searchParams, updateProfileSearch]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!['institutions-success', 'institutions-cancelled'].includes(checkout || '')) return;
    const returnKey = `${checkout}`;
    if (handledInstitutionCheckoutReturnRef.current === returnKey) return;
    handledInstitutionCheckoutReturnRef.current = returnKey;

    setActiveTab('institutes');
    setShowInstitutionPlans(true);

    if (checkout === 'institutions-cancelled') {
      setInstitutionCheckoutMessage('Checkout was cancelled. Membership was not renewed.');
    } else {
      setInstitutionCheckoutMessage('Payment returned from Stripe. Membership access is refreshing now.');
      reloadTransactionData();
      setInstituteRefresh(value => value + 1);
    }

    updateProfileSearch((next) => {
      next.set('tab', 'institutes');
      next.delete('checkout');
      next.delete('session_id');
      next.delete('checkout_session_id');
    }, { replace: true });
  }, [reloadTransactionData, searchParams, updateProfileSearch]);

  const activeWGCanManage = !!(activeWG?.id && profileData?.id && (activeWG.coach_id === profileData.id || activeWG.created_by === profileData.id || activeWG.__isClubStaff));
  const activeWGCurrentMember = wgMembers.find(member => member?.id === profileData?.id);
  const activeWGCanLeave = !!(activeWG?.id && activeWG.__isMember && activeWGCurrentMember && !activeWGCanManage);
  const activeWGOwnerId = activeWG ? getWorkoutGroupPlanOwnerId(activeWG) : null;
  const activeWGIsPlanOwner = !!(activeWGOwnerId && activeWGOwnerId === profileData?.id);
  const hasTeamClubAccess = !!(
    hasWorkoutGroupTier(workoutAccess, 'team_club') ||
    workoutAccess?.entitlements?.has?.('workout_groups.club_analysis') ||
    workoutAccess?.entitlements?.has?.('workout_groups.branding') ||
    workoutAccess?.entitlements?.has?.('workout_groups.multiple_coaches') ||
    workoutAccess?.entitlements?.has?.('workout_groups.season_management') ||
    workoutAccess?.entitlements?.has?.('workout_groups.calendar_sessions')
  );
  const canOpenAnyClubDashboard = !!(
    hasTeamClubAccess ||
    workoutGroups.some(group => group.__isClubStaff || group.workout_clubs?.owner_profile_id === profileData?.id)
  );
  const workoutGroupSummary = {
    total: workoutGroups.length,
    coaching: workoutGroups.filter(group => group.__isGroupCoach || group.coach_id === profileData?.id || group.created_by === profileData?.id).length,
    joined: workoutGroups.filter(group => group.__isMember && !(group.__isGroupCoach || group.__isClubStaff)).length,
    club: workoutGroups.filter(group => group.club_id || group.__isClubStaff || group.workout_clubs?.owner_profile_id === profileData?.id).length,
    athletes: workoutGroups.reduce((sum, group) => sum + Number(group.memberCount || 0), 0),
  };
  const activeWGCanOpenClubCoaching = !!(
    activeWG?.id &&
    (
      hasTeamClubAccess ||
      activeWG.__isClubStaff ||
      activeWG.__isMember ||
      activeWG.workout_clubs?.owner_profile_id === profileData?.id
    )
  );
  const activeWGCanUseNutritionStats = !!(
    activeWGCanManage &&
    (
      ['pro_coach', 'team_club'].includes(activeWGOwnerAccess?.tier) ||
      activeWGOwnerAccess?.entitlements?.has?.('workout_groups.club_analysis')
    )
  );
  const activeWGHasCalendarAccess = hasWorkoutGroupCalendarAccess(activeWGOwnerAccess);
  const canUseWGExtendedRanges = !!(
    activeWGOwnerAccess?.entitlements?.has?.('workout_groups.range_2w_4w') ||
    ['coach_starter', 'coach', 'pro_coach', 'team_club'].includes(activeWGOwnerAccess?.tier)
  );
  const wgFeatureAccess = activeWGOwnerAccess || (activeWGIsPlanOwner ? workoutAccess : getDefaultWorkoutGroupAccess());
  const activeWGCanUseTrainingReminders = !!(
    activeWGCanManage &&
    (hasWorkoutGroupTier(wgFeatureAccess, 'coach') || activeWG?.__isClubStaff)
  );
  const canUseWorkoutShareCodesEntitlement = !!(
    hasWorkoutGroupTier(wgFeatureAccess, 'coach_starter') ||
    wgFeatureAccess?.entitlements?.has?.('workout_groups.workout_share_codes')
  );
  const canUseWGRestTimeAnalysis = !!(
    hasWorkoutGroupTier(wgFeatureAccess, 'coach') ||
    wgFeatureAccess?.entitlements?.has?.('workout_groups.rest_time_analysis')
  );
  const canUseWGAthleteNotes = !!(
    hasWorkoutGroupTier(wgFeatureAccess, 'coach')
  );
  const canUseWGAthleteSessionFeedback = !!(
    hasWorkoutGroupTier(wgFeatureAccess, 'coach') ||
    wgFeatureAccess?.entitlements?.has?.('workout_groups.athlete_session_feedback')
  );
  const workoutGroupFullMessage = 'This workout group is at its athlete limit. Ask the group owner to upgrade their plan before more athletes can join.';

  useEffect(() => {
    if (timeRange !== 7 && activeWGOwnerId && activeWGOwnerAccess && !canUseWGExtendedRanges) {
      setWgRangeMode('last_7');
      setTimeRange(7);
    }
  }, [timeRange, activeWGOwnerId, activeWGOwnerAccess, canUseWGExtendedRanges]);

  useEffect(() => {
    let cancelled = false;
    const loadOwnerAccess = async () => {
      if (!activeWGOwnerId) {
        setActiveWGOwnerAccess(null);
        return;
      }
      if (activeWGOwnerId === profileData?.id) {
        setActiveWGOwnerAccess(workoutAccess);
        return;
      }
      const cached = wgOwnerAccessCacheRef.current.get(activeWGOwnerId);
      if (cached) {
        setActiveWGOwnerAccess(cached);
        return;
      }
      try {
        const ownerAccess = activeWG?.id
          ? await loadWorkoutGroupOwnerAccessForGroup(activeWG.id)
          : await loadWorkoutGroupAccess(activeWGOwnerId);
        wgOwnerAccessCacheRef.current.set(activeWGOwnerId, ownerAccess);
        if (!cancelled) setActiveWGOwnerAccess(ownerAccess);
      } catch (err) {
        console.error('load active workout group owner access error:', err);
        if (!cancelled) setActiveWGOwnerAccess(null);
      }
    };
    loadOwnerAccess();
    return () => {
      cancelled = true;
    };
  }, [activeWGOwnerId, activeWG?.id, profileData?.id, workoutAccess]);

  const getCheckoutErrorMessage = async (err) => {
    const fallback = err?.message || 'Could not start checkout.';
    const response = err?.context;
    if (!response || typeof response.json !== 'function') return fallback;
    try {
      const payload = await response.json();
      return payload?.error || payload?.message || fallback;
    } catch {
      return fallback;
    }
  };

  const getWorkoutPlanCheckout = (plan) => {
    const yearly = workoutBillingCycle === 'yearly';
    return {
      price: yearly ? plan.yearlyPrice : plan.monthlyPrice,
      alternatePrice: yearly ? plan.monthlyPrice : plan.yearlyPrice,
      alternateLabel: yearly ? 'Monthly option' : 'Yearly option',
      priceKey: yearly ? plan.yearlyPriceKey : plan.monthlyPriceKey,
      saving: yearly ? plan.yearlySaving : null,
      cta: yearly && plan.yearlyPriceKey ? 'Subscribe Yearly' : plan.cta,
    };
  };

  const startWorkoutCheckout = async (plan) => {
    if ((WORKOUT_GROUP_PLAN_ORDER[plan?.tier] ?? 0) <= currentWorkoutPlanRank) {
      setCheckoutMessage('Only higher Workout Groups plans can be selected here. Use Manage or cancel billing for card details, invoices, or cancellation.');
      return;
    }
    const checkout = getWorkoutPlanCheckout(plan);
    if (!checkout.priceKey) return;
    setCheckoutLoading(checkout.priceKey);
    setCheckoutMessage('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) throw new Error('Sign in on the website with the same ProAm account before subscribing.');

      const { data, error } = await supabase.functions.invoke('workout_groups_create_checkout', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: withRequestedStripeMode({
          price_key: checkout.priceKey,
          success_url: `${window.location.origin}/profile?checkout=workout-groups-success`,
          cancel_url: `${window.location.origin}/profile?checkout=workout-groups-cancelled`,
        }),
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      setCheckoutMessage(await getCheckoutErrorMessage(err));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const openWorkoutGroupBillingPortal = async () => {
    if (!canManageWorkoutGroupBilling || openingWorkoutBillingPortal) return;
    setOpeningWorkoutBillingPortal(true);
    setCheckoutMessage('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) throw new Error('Sign in on the website with the same ProAm account before managing billing.');

      const { data, error } = await supabase.functions.invoke('workout_groups_create_billing_portal', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: withRequestedStripeMode({
          return_url: `${window.location.origin}/profile?tab=workouts&billing=workout-groups`,
        }),
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('Billing portal URL was not returned.');
      window.location.href = data.url;
    } catch (err) {
      setCheckoutMessage(await getCheckoutErrorMessage(err));
    } finally {
      setOpeningWorkoutBillingPortal(false);
    }
  };

  const startInstitutionCheckout = async (plan) => {
    if (!plan?.tier) return;
    if (institutionBusinessDemoMode && isBusinessInstitutionPlan(plan)) {
      openInstitutionDemoRequest(plan);
      return;
    }
    if (institutionAccessUnlocked && activeInstitutionTierRank > 0 && institutionPlanRank(plan.tier) <= activeInstitutionTierRank) {
      const message = 'Only higher membership plans can be selected here. Use Manage or cancel billing if you need to change or cancel the current subscription.';
      setInstitutionCheckoutMessage(message);
      notify(message, 'warning');
      return;
    }
    if (institutionAccessUnlocked && plan.memberLimit && totals.memberCount > Number(plan.memberLimit || 0)) {
      const message = `${totals.memberCount} current members exceed the ${institutionPlanDisplayName(plan)} limit. Choose a larger plan or remove members first.`;
      setInstitutionCheckoutMessage(message);
      notify(message, 'warning');
      return;
    }
    if (
      institutionRestartMode &&
      (!institutionRestartLockedPlan || (
        plan.tier !== institutionRestartLockedPlan.tier &&
        institutionPlanRank(plan.tier) <= institutionPlanRank(institutionRestartLockedPlan.tier)
      ))
    ) {
      const message = 'Renewal is locked to the previous membership plan unless you choose a higher plan.';
      setInstitutionCheckoutMessage(message);
      notify(message, 'warning');
      return;
    }

    const priceKey = institutionBillingCycle === 'yearly' ? plan.yearlyProductKey : plan.monthlyProductKey;
    if (!priceKey) return;

    const targetInstitute = activeInst?.id && activeInst?.type !== 'Challenge'
      ? activeInst
      : activeWorkspacePlanInstitute?.id
      ? activeWorkspacePlanInstitute
      : institutes.find(inst => inst?.id && inst?.type !== 'Challenge') || institutes[0];

    if (!targetInstitute?.id) {
      openInstitutionCreatePage({ planTier: plan.tier, billingCycle: institutionBillingCycle });
      return;
    }

    const loadingKey = `institution:${priceKey}`;
    setInstitutionCheckoutLoading(loadingKey);
    setInstitutionCheckoutMessage('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) throw new Error('Sign in on the website with the same ProAm account before renewing membership.');

      const { data, error } = await supabase.functions.invoke('institutions_create_checkout', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: withRequestedStripeMode({
          price_key: priceKey,
          institute_id: targetInstitute.id,
          workspace_name: targetInstitute.workspace_name || targetInstitute.name || undefined,
          create_new_workspace: false,
          institute_name: targetInstitute.name || undefined,
          institute_type: targetInstitute.type || plan.typeHint || 'Collective Group',
          country: targetInstitute.country || undefined,
          region: targetInstitute.region || undefined,
          success_url: `${window.location.origin}/profile?tab=institutes&checkout=institutions-success`,
          cancel_url: `${window.location.origin}/profile?tab=institutes&checkout=institutions-cancelled`,
          brand_settings: {
            planTier: plan.tier,
            billingCycle: institutionBillingCycle,
            segment: String(plan.segment || '').toLowerCase(),
            renewedExistingInstitution: institutionRestartMode || institutionStartExistingMode,
            upgradedExistingInstitution: institutionPlanRank(plan.tier) > institutionPlanRank(institutionRestartLockedPlan?.tier || activeInstitutionStoredTier),
          },
        }),
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('Checkout URL was not returned.');
      window.location.href = data.url;
    } catch (err) {
      const message = await getCheckoutErrorMessage(err);
      setInstitutionCheckoutMessage(message);
      notify(message, 'error');
    } finally {
      setInstitutionCheckoutLoading(null);
    }
  };

  /* ── Rankings ── */
  useEffect(() => {
    const loadRanks = async () => {
      if (activeInst?.type === 'Challenge') {
        setPeerBoard([]);
        setLoadingRanks(false);
        return;
      }
      setLoadingRanks(true);
      try {
        const t = activeInst?.type || capitaliseType(profileData?.institute_type) || instType || 'School';
        if (isDemoCompanyLeaderboard(profileData?.email, t)) {
          setPeerBoard(buildDemoCompanyLeaderboard());
          return;
        }
        if (isDemoClubLeaderboard(profileData?.email, t)) {
          const { data: clubRows, error: clubError } = await supabase.rpc('get_demo_club_leaderboard', {
            p_scope: scope, p_country: rankCountry, p_state_province: rankRegion,
          });
          if (clubError) throw clubError;
          setPeerBoard(buildDemoClubLeaderboard({ realRows: clubRows || [] }));
          return;
        }
        if (isDemoSchoolLeaderboard(profileData?.email, t)) {
          const { data: schoolRows, error: schoolError } = await supabase.rpc('get_demo_school_leaderboard', {
            p_scope: scope, p_country: rankCountry, p_state_province: rankRegion,
          });
          if (schoolError) throw schoolError;
          setPeerBoard(buildDemoSchoolLeaderboard({ realRows: schoolRows || [] }));
          return;
        }
        if (isDemoCollegeLeaderboard(profileData?.email, t)) {
          const { data: collegeRows, error: collegeError } = await supabase.rpc('get_demo_college_leaderboard', {
            p_scope: scope, p_country: rankCountry, p_state_province: rankRegion,
          });
          if (collegeError) throw collegeError;
          setPeerBoard(buildDemoCollegeLeaderboard({ realRows: collegeRows || [] }));
          return;
        }
        const { data: insts, error: instErr } = await supabase.rpc('get_public_institute_leaderboard', {
          p_institute_type: t, p_scope: scope, p_country: rankCountry, p_state_province: rankRegion,
        });
        if (instErr) throw instErr;
        const visibleInsts = (insts || []).filter(i => !isDemoCompanyInstituteRow(i) && !isDemoClubInstituteRow(i));
        if (!visibleInsts.length) { setPeerBoard([]); return; }
	        const displayInstitutes = visibleInsts;
	        const rows = displayInstitutes
	          .map(i => {
	            const accessLocked = isInstitutionAccessLocked(i);
	            return { id: i.id, name: i.name?.replace(/\s+(Students|Members)$/i, '').trim() || i.name, type: t, institute_type: t, region: i.state_province, country: i.country, image_url: i.image_url, totalSteps: Number(i.total_steps) || 0, billing_status: i.billing_status, access_active: i.access_active, accessLocked };
	          })
	          .sort((a, b) => {
	            return b.totalSteps - a.totalSteps || a.name.localeCompare(b.name);
	          })
          .map((r, i) => ({ ...r, rank: i + 1 }));
        setPeerBoard(rows);
      } finally { setLoadingRanks(false); }
    };
    if (profileData?.id) loadRanks();
  }, [activeInst?.id, activeInst?.image_url, activeInst?.type, profileData?.email, profileData?.id, profileData?.institute_type, instType, scope, rankCountry, rankRegion]);

  useEffect(() => {
    if (profileData) setSettingsForm({ website: profileData.website || '', phone_number: profileData.phone_number || '', email: profileData.email || '', address: profileData.address || '', country: profileData.country || '', region: profileData.region || '' });
  }, [profileData]);

  const fetchDepts = async (instId) => {
    const { data: teamRows } = await supabase.from('institute_teams').select('id,name,access_code,image_url,institute_id,type').eq('institute_id', instId).is('archived_at', null);
    const extractYO = (n = '') => { const m = String(n).toLowerCase().match(/(\d{1,2})/); return m ? parseInt(m[1]) : Number.POSITIVE_INFINITY; };
    const depts = await Promise.all((teamRows || []).map(async t => {
      const { data: mRows } = await supabase.from('user_institutes').select('steps,lifetime_steps,association_type,status,profiles:profiles(id,name,surname,username,profile_img)').eq('institute_id', instId).eq('team_id', t.id).neq('status', 'Former');
      const demoRows = await fetchDemoInstituteMemberRows(supabase, { instituteId: instId, teamId: t.id });
      const members = mergeDemoInstituteMembers(mRows || [], demoRows).map(r => ({ id: r.profiles?.id ?? r.demo_profile_id, demo_profile_id: r.demo_profile_id || null, is_demo_profile: !!r.is_demo_profile, name: [r.profiles?.name, r.profiles?.surname].filter(Boolean).join(' ') || r.profiles?.username || 'Unknown', username: r.profiles?.username || null, profile_img: r.profiles?.profile_img || null, steps: r.steps ?? 0, association_type: r.association_type, status: r.status }));
      return { id: t.id, name: t.name, type: t.type || '', code: t.access_code, image_url: t.image_url, institute_id: t.institute_id, members };
    }));
    const sorted = [...depts].sort((a, b) => {
      if ((a.type || '').toLowerCase() === 'students' && (b.type || '').toLowerCase() === 'students') {
        const ay = extractYO(a.name), by = extractYO(b.name);
        if (ay !== by) return ay - by;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    setInstitutes(prev => { const copy = [...prev]; const idx = copy.findIndex(i => i.id === instId); if (idx !== -1) copy[idx] = { ...copy[idx], departments: sorted }; return copy; });
  };

  const workspacePickerGroups = useMemo(() => buildInstitutionWorkspaceGroups(institutes), [institutes]);
  const workspacePickerOptions = useMemo(
    () => workspacePickerGroups.flatMap((group) => group.options),
    [workspacePickerGroups],
  );
  const hasUnassignedInstitutes = useMemo(() => institutes.some((inst) => !inst?.workspace_id), [institutes]);
  const selectedWorkspaceId = workspaceFilterId || activeInst?.workspace_id || workspacePickerOptions[0]?.id || (hasUnassignedInstitutes ? '__unassigned__' : '');
  const visibleInstituteCards = useMemo(() => institutes
    .map((inst, idx) => ({ inst, idx }))
    .filter(({ inst }) => selectedWorkspaceId === '__unassigned__' ? !inst.workspace_id : inst.workspace_id === selectedWorkspaceId), [institutes, selectedWorkspaceId]);
  const selectedWorkspaceName = selectedWorkspaceId === '__unassigned__'
    ? 'Other institutes'
    : workspacePickerOptions.find((workspace) => workspace.id === selectedWorkspaceId)?.name || activeInst?.workspace_name || activeInst?.name || 'Workspace';

  useEffect(() => {
    const activeWorkspaceId = activeInst?.workspace_id || (activeInst ? '__unassigned__' : '');
    if (activeWorkspaceId && activeWorkspaceId !== workspaceFilterId) setWorkspaceFilterId(activeWorkspaceId);
  }, [activeInst?.id, activeInst?.workspace_id, workspaceFilterId]);

  const changeWorkspaceFilter = (nextWorkspaceId) => {
    setWorkspaceFilterId(nextWorkspaceId);
    const nextIndex = institutes.findIndex((inst) => (
      nextWorkspaceId === '__unassigned__' ? !inst.workspace_id : inst.workspace_id === nextWorkspaceId
    ));
    if (nextIndex < 0) return;
    const nextInstitute = institutes[nextIndex];
    setActiveInstIdx(nextIndex);
    applyInstituteRankLocation(nextInstitute);
    setExpandedDept(null);
    if (nextInstitute.teams) fetchDepts(nextInstitute.id);
  };

  useEffect(() => { if (activeInst?.teams && !(activeInst?.departments?.length)) fetchDepts(activeInst.id); }, [activeInst?.id, activeInst?.teams]);

  const challengeDepartmentSource = useMemo(() => {
    if (!activeInst || activeInst.type === 'Challenge') return null;
    if (activeInst.teams) return activeInst;
    if (!activeInst.workspace_id) return null;
    return institutes.find(inst => (
      inst.id !== activeInst.id &&
      inst.workspace_id === activeInst.workspace_id &&
      inst.type !== 'Challenge' &&
      inst.teams
    )) || null;
  }, [activeInst, institutes]);
  const availableChallengeDepartments = useMemo(
    () => (challengeDepartmentSource?.departments || []).filter(dept => dept?.id),
    [challengeDepartmentSource?.departments],
  );

  useEffect(() => {
    let cancelled = false;
    const loadPlatformChallenges = async () => {
      if (!activeInst?.id || activeInst.type === 'Challenge') {
        setPlatformChallengeOffers([]);
        setPlatformChallengeOffersError('');
        return;
      }
      setLoadingPlatformChallengeOffers(true);
      setPlatformChallengeOffersError('');
      try {
        const { data, error } = await supabase.rpc('get_available_platform_challenges', { p_institute_id: activeInst.id });
        if (error) throw error;
        if (!cancelled) {
          setPlatformChallengeOffers(data || []);
          setPlatformChallengeOffersError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPlatformChallengeOffers([]);
          setPlatformChallengeOffersError(err?.message || 'Could not check platform challenges. Refresh and try again.');
          console.warn('Could not load platform challenge offers', err?.message || err);
        }
      } finally {
        if (!cancelled) setLoadingPlatformChallengeOffers(false);
      }
    };
    loadPlatformChallenges();
    return () => { cancelled = true; };
  }, [activeInst?.id, activeInst?.type, instituteRefresh]);

  const openPlatformChallengeOffer = (offer) => {
    if (!offer?.id || offer.opted_in) return;
    if (platformChallengeOfferFinished(offer)) {
      notify('This challenge has already finished.', 'error');
      return;
    }
    openAddInstituteModal('Challenge', offer);
  };

  useEffect(() => {
    if (!showAddInstitute || instNewType !== 'Challenge' || !challengeDepartmentSource?.id || !challengeDepartmentSource.teams) return;
    if ((challengeDepartmentSource.departments || []).length) return;
    fetchDepts(challengeDepartmentSource.id);
  }, [challengeDepartmentSource?.departments, challengeDepartmentSource?.id, challengeDepartmentSource?.teams, instNewType, showAddInstitute]);

  const toggleInstituteChallengeDepartment = (deptId) => {
    setInstChallengeDepartmentIds(current => {
      const next = new Set(current || []);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return Array.from(next);
    });
  };
  const selectAllInstituteChallengeDepartments = () => {
    setInstChallengeDepartmentIds(availableChallengeDepartments.map(dept => dept.id).filter(Boolean));
  };
  const clearInstituteChallengeDepartments = () => setInstChallengeDepartmentIds([]);
  const toggleEditChallengeDepartment = (deptId) => {
    setEditInstForm(current => {
      const next = new Set(current?.audience_department_ids || []);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return { ...current, audience_department_ids: Array.from(next) };
    });
  };
  const selectAllEditChallengeDepartments = () => {
    setEditInstForm(current => ({ ...current, audience_department_ids: editChallengeDepartments.map(dept => dept.id).filter(Boolean) }));
  };
  const clearEditChallengeDepartments = () => {
    setEditInstForm(current => ({ ...current, audience_department_ids: [] }));
  };

  const getChallengeParentInstitute = useCallback((challenge = activeInst) => {
    if (!challenge || challenge.type !== 'Challenge') return null;
    const parentId = challenge.brand_settings?.parentInstituteId || challenge.auto_join_institute_id;
    if (parentId) {
      const parent = institutes.find(inst => inst.id === parentId);
      if (parent) return parent;
      return { id: parentId, name: challenge.brand_settings?.parentInstituteName || 'parent institute' };
    }
    return institutes.find(inst =>
      inst.id !== challenge.id &&
      inst.type !== 'Challenge' &&
      inst.workspace_id &&
      inst.workspace_id === challenge.workspace_id
    ) || null;
  }, [activeInst, institutes]);

  const loadChallengeMemberCandidates = useCallback(async () => {
    if (!showAddMembers || activeInst?.type !== 'Challenge') return;
    const parent = getChallengeParentInstitute(activeInst);
    if (!parent?.id) {
      setChallengeMemberCandidates([]);
      return;
    }
    setLoadingChallengeMemberCandidates(true);
    try {
      const { data, error } = await supabase
        .from('user_institutes')
        .select('profile_id,association_type,status,profiles:profiles(id,name,surname,username,profile_img)')
        .eq('institute_id', parent.id)
        .neq('status', 'Former');
      if (error) throw error;
      const unique = new Map();
      (data || []).forEach(row => {
        if (row.profile_id && !unique.has(row.profile_id)) unique.set(row.profile_id, row);
      });
      setChallengeMemberCandidates(Array.from(unique.values()));
    } catch (err) {
      setChallengeMemberCandidates([]);
      notify(`Could not load parent institute members: ${err.message}`, 'error');
    } finally {
      setLoadingChallengeMemberCandidates(false);
    }
  }, [activeInst, getChallengeParentInstitute, showAddMembers]);

  useEffect(() => {
    if (!showAddMembers) {
      setChallengeMemberSearch('');
      setChallengeMemberCandidates([]);
      setAddingChallengeMemberId(null);
      return;
    }
    if (activeInst?.type === 'Challenge') loadChallengeMemberCandidates();
  }, [activeInst?.id, activeInst?.type, loadChallengeMemberCandidates, showAddMembers]);

  const getMemberSourceInstitute = useCallback((inst = activeInst) => {
    if (!inst) return null;
    const sourceId = inst.brand_settings?.parentInstituteId || inst.auto_join_institute_id;
    if (sourceId) {
      const source = institutes.find(item => item.id === sourceId);
      if (source) return source;
    }
    if (inst.teams && inst.workspace_id) {
      return institutes.find(item => item.id !== inst.id && item.workspace_id === inst.workspace_id && item.type !== 'Challenge' && !item.teams) || inst;
    }
    return inst;
  }, [activeInst, institutes]);

  const loadDepartmentMemberCandidates = useCallback(async () => {
    if (!showAddDeptMembers.open || !activeInst?.id) return;
    const source = getMemberSourceInstitute(activeInst);
    if (!source?.id) {
      setDepartmentMemberCandidates([]);
      return;
    }
    setLoadingDepartmentMemberCandidates(true);
    try {
      const { data, error } = await supabase
        .from('user_institutes')
        .select('profile_id,association_type,status,profiles:profiles(id,name,surname,username,profile_img)')
        .eq('institute_id', source.id)
        .neq('status', 'Former');
      if (error) throw error;
      const unique = new Map();
      (data || []).forEach(row => {
        if (row.profile_id && !unique.has(row.profile_id)) unique.set(row.profile_id, row);
      });
      setDepartmentMemberCandidates(Array.from(unique.values()));
    } catch (err) {
      setDepartmentMemberCandidates([]);
      notify(`Could not load institution members: ${err.message}`, 'error');
    } finally {
      setLoadingDepartmentMemberCandidates(false);
    }
  }, [activeInst, getMemberSourceInstitute, showAddDeptMembers.open]);

  useEffect(() => {
    if (!showAddDeptMembers.open) {
      setDepartmentMemberSearch('');
      setDepartmentMemberCandidates([]);
      setAddingDepartmentMemberId(null);
      return;
    }
    loadDepartmentMemberCandidates();
  }, [loadDepartmentMemberCandidates, showAddDeptMembers.open, showAddDeptMembers.dept?.id]);

  const handleSaveField = async (field, value) => {
    const { data: { user } } = await supabase.auth.getUser();
    const nextValue = field === 'date_of_birth' ? (value || null) : value;
    const { error } = await supabase.from('profiles').update({ [field]: nextValue }).eq('id', user.id);
    if (!error) setProfileData(p => ({ ...p, [field]: nextValue }));
  };

  const handleUpdateProfileImg = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setLoadingImg(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const ts = Date.now(), ext = file.name.split('.').pop(), folder = user.id, path = `${folder}/profile-image-${ts}.${ext}`;
      const publicUrl = await uploadFileToR2({ bucket: 'media', folder: 'profile-images', filename: path.replace(/[/\\]+/g, '-'), file });
      await supabase.from('profiles').update({ profile_img: publicUrl }).eq('id', user.id);
      setProfileData(p => ({ ...p, profile_img: publicUrl }));
    } catch { notify('Failed to upload image.', 'error'); } finally { setLoadingImg(false); }
  };

  const parseUOTP = (raw = '') => {
    const c = raw.trim().replace(/^@/, '');
    // Check for bypass code at end
    if (c.endsWith(BYPASS_CODE)) {
      return { username: c.slice(0, -BYPASS_CODE.length), otp: BYPASS_CODE };
    }
    const m = c.match(/^(.+?)(?:[\s:#-]+)?(\d{4})$/);
    if (!m) return { username: c, otp: null };
    return { username: m[1].trim(), otp: m[2] };
  };

  const isValidOtp = (profile, otp) => {
    if (!otp) return false;
    // Allow bypass code for account_type 'user'
    if (otp === BYPASS_CODE && profile.account_type === 'user') return true;
    return String(profile.one_time_code) === String(otp);
  };

  const getAssocType = (inst) => defaultAssociationTypeForInstitute(inst);

  /* ── Add institute members (with bypass) ── */
  const addMembersToInstitute = async () => {
    const inst = institutes[activeInstIdx]; if (!inst?.id) return;
    const raw = Array.from(new Set(newMembers.map(r => (r.username || '').trim().replace(/^@/, '')).filter(Boolean)));
    if (!raw.length) return;
    const parsed = raw.map(parseUOTP);
    const { data: profs } = await supabase.from('profiles').select('id,name,surname,username,account_type,one_time_code').in('username', parsed.map(p => p.username));
    const valid = [];
    for (const { username, otp } of parsed) {
      const p = (profs || []).find(x => x.username === username);
      if (!p || !isValidOtp(p, otp)) continue;
      valid.push(p);
    }
    if (!valid.length) { notify('No valid members found. Check usernames and passcodes.', 'warning'); return; }
    const { data: ex } = await supabase.from('user_institutes').select('profile_id,status').eq('institute_id', inst.id).in('profile_id', valid.map(p => p.id));
    const exSet = new Set((ex || []).filter(r => r.status !== 'Former').map(r => r.profile_id));
    const fresh = valid.filter(p => !exSet.has(p.id));
    const defaultAssocType = getAssocType(inst);
    if (fresh.length) await supabase.from('user_institutes').insert(fresh.map(p => ({ profile_id: p.id, institute_id: inst.id, association_type: defaultAssocType, steps: 0, lifetime_steps: 0, team_id: null })));
    const { data: rows } = await supabase.from('user_institutes').select('id,steps,association_type,status,profiles:profiles(id,name,surname,username,profile_img)').eq('institute_id', inst.id).neq('status', 'Former');
    const members = (rows || []).map(r => ({ id: r.profiles?.id ?? r.id, name: [r.profiles?.name, r.profiles?.surname].filter(Boolean).join(' ') || r.profiles?.username || 'Unknown', username: r.profiles?.username || null, profile_img: r.profiles?.profile_img || null, steps: r.steps ?? 0, association_type: r.association_type, status: r.status }));
    setInstitutes(prev => { const copy = [...prev]; const i = copy.findIndex(x => x.id === inst.id); if (i !== -1) copy[i] = { ...copy[i], members }; return copy; });
    setShowAddMembers(false); setNewMembers([{ id: uid('m'), username: '' }]);
    notify('Members added.', 'success');
  };

  const addChallengeMember = async (member) => {
    const inst = institutes[activeInstIdx];
    const profileId = member?.profile_id;
    recordChallengeMemberDebug('Profile', 'add requested', {
      challengeId: inst?.id,
      challengeName: inst?.name,
      challengeType: inst?.type,
      profileId,
      candidate: member,
      currentMembers: inst?.members,
      departments: inst?.departments,
    });
    if (!inst?.id || inst.type !== 'Challenge' || !profileId) {
      recordChallengeMemberDebug('Profile', 'add blocked: missing challenge/profile data', { inst, profileId, member }, 'warn');
      return;
    }
    setAddingChallengeMemberId(profileId);
    try {
      const { data: existing, error: existingErr } = await supabase
        .from('user_institutes')
        .select('id,status')
        .eq('institute_id', inst.id)
        .eq('profile_id', profileId)
        .maybeSingle();
      recordChallengeMemberDebug('Profile', 'existing row check', { existing, existingErr });
      if (existingErr) throw existingErr;
      if (existing?.status && existing.status !== 'Former') {
        recordChallengeMemberDebug('Profile', 'already active, no insert/update needed', existing);
        return;
      }

      if (existing?.id) {
        const { error } = await supabase
          .from('user_institutes')
          .update({ status: 'Active', left_date: null, team_id: null, association_type: 'Member' })
          .eq('id', existing.id);
        recordChallengeMemberDebug('Profile', 'reactivated former row', { rowId: existing.id, error });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('user_institutes').insert({
          profile_id: profileId,
          institute_id: inst.id,
          association_type: 'Member',
          status: 'Active',
          joined_date: new Date().toISOString(),
          team_id: null,
        });
        recordChallengeMemberDebug('Profile', 'inserted row', { challengeId: inst.id, profileId, error });
        if (error) throw error;
      }

      const { data: rows, error: rowsErr } = await supabase
        .from('user_institutes')
        .select('id,steps,lifetime_steps,team_id,association_type,status,profiles:profiles(id,name,surname,username,profile_img)')
        .eq('institute_id', inst.id)
        .neq('status', 'Former');
      recordChallengeMemberDebug('Profile', 'reload rows', { rows, rowsErr });
      if (rowsErr) throw rowsErr;
      const members = (rows || []).map(r => ({ id: r.profiles?.id ?? r.id, name: [r.profiles?.name, r.profiles?.surname].filter(Boolean).join(' ') || r.profiles?.username || 'Unknown', username: r.profiles?.username || null, profile_img: r.profiles?.profile_img || null, steps: r.steps ?? 0, lifetime_steps: r.lifetime_steps ?? 0, team_id: r.team_id || null, association_type: r.association_type, status: r.status }));
      recordChallengeMemberDebug('Profile', 'mapped members', {
        count: members.length,
        unassignedCount: members.filter(m => !m.team_id).length,
        members,
      });
      setInstitutes(prev => prev.map(item => item.id === inst.id ? { ...item, members } : item));
      recordChallengeMemberDebug('Profile', 'state update queued', { challengeId: inst.id, memberCount: members.length });
      notify('Participant added.', 'success');
    } catch (err) {
      const message = String(err?.message || '');
      recordChallengeMemberDebug('Profile', 'add failed', err, 'error');
      notify(message.includes('Challenge participant limit reached') ? 'Challenge full. Increase the participant limit before adding more members.' : `Could not add participant: ${message || 'Unknown error'}`, 'error');
    } finally {
      setAddingChallengeMemberId(null);
    }
  };

  const addDepartmentMemberFromList = async (member) => {
    const inst = institutes[activeInstIdx];
    const dept = showAddDeptMembers.dept;
    const profileId = member?.profile_id;
    if (!inst?.id || !dept?.id || !profileId) return;
    setAddingDepartmentMemberId(profileId);
    try {
      const defaultAssocType = defaultAssociationTypeForInstitute(inst);
      const { data: existing, error: existingErr } = await supabase
        .from('user_institutes')
        .select('id,status')
        .eq('institute_id', inst.id)
        .eq('profile_id', profileId)
        .maybeSingle();
      if (existingErr) throw existingErr;
      if (existing?.id) {
        const { error } = await supabase
          .from('user_institutes')
          .update({ status: 'Active', left_date: null, team_id: dept.id, association_type: member.association_type || defaultAssocType })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('user_institutes').insert({
          profile_id: profileId,
          institute_id: inst.id,
          association_type: member.association_type || defaultAssocType,
          status: 'Active',
          joined_date: new Date().toISOString(),
          steps: 0,
          lifetime_steps: 0,
          team_id: dept.id,
        });
        if (error) throw error;
      }
      await fetchDepts(inst.id);
      const { data: rows } = await supabase.from('user_institutes').select('id,steps,lifetime_steps,team_id,association_type,status,profiles:profiles(id,name,surname,username,profile_img)').eq('institute_id', inst.id).neq('status', 'Former');
      const members = (rows || []).map(r => ({ id: r.profiles?.id ?? r.id, name: [r.profiles?.name, r.profiles?.surname].filter(Boolean).join(' ') || r.profiles?.username || 'Unknown', username: r.profiles?.username || null, profile_img: r.profiles?.profile_img || null, steps: r.steps ?? 0, lifetime_steps: r.lifetime_steps ?? 0, team_id: r.team_id || null, association_type: r.association_type, status: r.status }));
      setInstitutes(prev => prev.map(item => item.id === inst.id ? { ...item, members } : item));
      notify('Member added to department.', 'success');
    } catch (err) {
      notify(`Could not add member: ${err.message}`, 'error');
    } finally {
      setAddingDepartmentMemberId(null);
    }
  };

  const moveMemberToDept = async () => {
    if (!activeInst?.id || !moveDeptMember.member?.id || !moveDeptMember.targetDeptId) return;
    const isDemoMember = !!moveDeptMember.member.is_demo_profile;
    const tableName = isDemoMember ? 'demo_user_institutes' : 'user_institutes';
    const idColumn = isDemoMember ? 'demo_profile_id' : 'profile_id';
    const { error } = await supabase.from(tableName).update({ team_id: moveDeptMember.targetDeptId }).eq(idColumn, moveDeptMember.member.id).eq('institute_id', activeInst.id);
    if (error) { notify('Could not move member.', 'error'); return; }
    await fetchDepts(activeInst.id);
    setInstitutes(prev => prev.map(inst => inst.id === activeInst.id ? { ...inst, members: (inst.members || []).map(member => member.id === moveDeptMember.member.id ? { ...member, team_id: moveDeptMember.targetDeptId } : member) } : inst));
    setMoveDeptMember({ open: false, member: null, fromDeptId: null, targetDeptId: '' });
    notify('Member moved.', 'success');
  };

  const openInstitutionMemberRole = (member) => {
    if (!member?.id || activeInst?.type === 'Challenge') return;
    setInstitutionCustomRole('');
    setEditingInstitutionMemberRole(member);
  };

  const closeInstitutionMemberRole = () => {
    if (updatingInstitutionMemberRoleId) return;
    setInstitutionCustomRole('');
    setEditingInstitutionMemberRole(null);
  };

  const updateInstitutionMemberRole = async (member, nextRole) => {
    const profileId = member?.id || member?.profile_id;
    const cleanedRole = String(nextRole || '').trim();
    if (!activeInst?.id || !profileId || !cleanedRole || updatingInstitutionMemberRoleId) return;
    if (cleanedRole.length > 40) {
      notify('Keep the member type under 40 characters.', 'warning');
      return;
    }
    setUpdatingInstitutionMemberRoleId(profileId);
    try {
      const workspaceIds = activeInst.workspace_id
        ? institutes
          .filter(inst => inst.workspace_id === activeInst.workspace_id && inst.type !== 'Challenge')
          .map(inst => inst.id)
          .filter(Boolean)
        : [activeInst.id];
      const targetIds = Array.from(new Set([activeInst.id, ...workspaceIds].filter(Boolean)));
      const isDemoMember = !!member?.is_demo_profile;
      const tableName = isDemoMember ? 'demo_user_institutes' : 'user_institutes';
      const idColumn = isDemoMember ? 'demo_profile_id' : 'profile_id';
      const { error } = await supabase
        .from(tableName)
        .update({ association_type: cleanedRole })
        .eq(idColumn, profileId)
        .in('institute_id', targetIds);
      if (error) throw error;
      setInstitutes(prev => prev.map(inst => targetIds.includes(inst.id)
        ? {
          ...inst,
          members: (inst.members || []).map(row => (row.id || row.profile_id) === profileId ? { ...row, association_type: cleanedRole } : row),
          departments: (inst.departments || []).map(dept => ({
            ...dept,
            members: (dept.members || []).map(row => (row.id || row.profile_id) === profileId ? { ...row, association_type: cleanedRole } : row),
          })),
        }
        : inst));
      setInstitutionCustomRole('');
      setEditingInstitutionMemberRole(null);
      notify('Member type updated.', 'success');
    } catch (err) {
      notify(err?.message || 'Could not update member type.', 'error');
    } finally {
      setUpdatingInstitutionMemberRoleId(null);
    }
  };

  const addDepartment = async () => {
    const name = newDeptName.trim(); if (!name || !activeInst?.id) return;
    let code; try { code = await generateUniqueCode(8, null, null, name); } catch { notify('Could not generate code.', 'error'); return; }
    let imageUrl = null;
    if (deptImageFile) {
      const ext = deptImageFile.name.split('.').pop() || 'png';
      const path = `departments/${activeInst.id}/logo-${Date.now()}.${ext}`;
      imageUrl = await uploadFileToR2({ bucket: 'media', folder: 'institutes-departments', filename: path.replace(/[/\\]+/g, '-'), file: deptImageFile });
    }
    const defaultDeptType = defaultAssociationTypeForInstitute(activeInst);
    const memberType = (newDeptType || defaultDeptType).trim();
    const { data, error } = await supabase.from('institute_teams').insert([{ institute_id: activeInst.id, name, image_url: imageUrl || null, access_code: code, type: memberType }]).select().single();
    if (error) { notify(`Failed to add ${deptLabel.toLowerCase()}.`, 'error'); return; }
    const { data: instData } = await supabase.from('institutes').select('team_options').eq('id', activeInst.id).single();
    await supabase.from('institutes').update({ team_options: [...(instData?.team_options || []), { name: data.name, image_url: data.image_url }] }).eq('id', activeInst.id);
    setInstitutes(prev => { const list = [...prev]; const inst = { ...list[activeInstIdx] }; inst.departments = [...(inst.departments || []), { id: data.id, name: data.name, code: data.access_code, image_url: data.image_url, institute_id: activeInst.id, type: memberType, members: [] }]; list[activeInstIdx] = inst; return list; });
    setNewDeptName(''); setNewDeptType(''); setDeptImageFile(null); setDeptImagePreview(null); setShowAddDept(false);
    notify(`${deptLabel} added.`, 'success');
  };

  const addInstitute = async (name, teams, type) => {
    name = (name || '').trim(); if (!name || !profileData?.id) return null;
    const isChallenge = type === 'Challenge';
    const selectedChallengeDepartments = isChallenge && teams
      ? availableChallengeDepartments.filter(dept => instChallengeDepartmentIds.includes(dept.id))
      : [];
    const challengeLimit = Math.floor(Number(instChallengeMemberLimit));
    const templateGoal = selectedPlatformChallengeOffer?.goal_steps ? Number(selectedPlatformChallengeOffer.goal_steps) : null;
    const challengeGoal = selectedPlatformChallengeOffer ? templateGoal : Math.floor(Number(instChallengeGoalSteps));
    const templateStartsOn = selectedPlatformChallengeOffer ? platformChallengeOfferStartsOn(selectedPlatformChallengeOffer) : '';
    const templateEndsOn = selectedPlatformChallengeOffer ? platformChallengeOfferEndsOn(selectedPlatformChallengeOffer) : '';
    const startsAt = isChallenge ? utcDateBoundary(templateStartsOn || instChallengeStartsOn) : null;
    const endsAt = isChallenge ? utcDateBoundary(templateEndsOn || instChallengeEndsOn, true) : null;
    const today = utcDateBoundary(dateInputValue(new Date()));
    if (isChallenge) {
      if (!instChallengeStartsOn || !Number.isFinite(startsAt.getTime())) { notify('Choose when this challenge starts.', 'error'); return null; }
      if (!instChallengeEndsOn || !Number.isFinite(endsAt.getTime())) { notify('Choose when this challenge finishes.', 'error'); return null; }
      if (!selectedPlatformChallengeOffer && startsAt < today) { notify('Challenge start date must be today or later.', 'error'); return null; }
      if (!(endsAt > startsAt)) { notify('Challenge finish date must be after the start date.', 'error'); return null; }
      if (endsAt < today) { notify('Challenge finish date has already passed.', 'error'); return null; }
      if (!Number.isFinite(challengeLimit) || challengeLimit <= 0) { notify('Enter how many people can join this challenge.', 'error'); return null; }
      if (challengeLimit > 32767) { notify('Challenge participant limit must be 32,767 or fewer.', 'error'); return null; }
      if (!Number.isFinite(challengeGoal) || challengeGoal <= 0) { notify('Enter a valid step goal for this challenge.', 'error'); return null; }
      if (teams && !selectedChallengeDepartments.length) { notify(`Select at least one ${deptLabel.toLowerCase()} for this team challenge.`, 'error'); return null; }
    }
    if (isChallenge && selectedPlatformChallengeOffer?.id) {
      const platformChallengeInstituteId = teams && challengeDepartmentSource?.id ? challengeDepartmentSource.id : activeInst.id;
      setOptingIntoPlatformChallengeId(selectedPlatformChallengeOffer.id);
      try {
        const { error } = await supabase.rpc('opt_in_institution_platform_challenge', {
          p_platform_challenge_id: selectedPlatformChallengeOffer.id,
          p_institute_id: platformChallengeInstituteId,
          p_challenge_mode: teams ? 'departments' : 'members',
          p_starts_at: startsAt.toISOString(),
          p_ends_at: endsAt.toISOString(),
          p_participant_limit: challengeLimit,
          p_description: null,
          p_department_ids: selectedChallengeDepartments.map(dept => dept.id),
        });
        if (error) throw error;
        setShowAddInstitute(false);
        setInstName('');
        setInstNewType('Collective Group');
        setInstTeams(false);
        setInstInitialDepartments('');
        setInstChallengeStartsOn(dateInputValue(new Date()));
        setInstChallengeEndsOn(dateInputValue(addDays(new Date(), 1)));
        setInstChallengeMemberLimit('');
        setInstChallengeDescription('');
        setInstChallengeGoalSteps('50000');
        setSelectedPlatformChallengeOffer(null);
        setInstChallengeDepartmentIds([]);
        setInstImageFile(null);
        setInstImagePreview(null);
        setInstituteRefresh(value => value + 1);
        notify('Challenge created from platform template.', 'success');
        return true;
      } catch (err) {
        const message = err?.message || 'Could not create this challenge.';
        notify(message.includes('select_at_least_one_department') ? `Select at least one ${deptLabel.toLowerCase()} for this team challenge.` : message, 'error');
        return null;
      } finally {
        setOptingIntoPlatformChallengeId(null);
      }
    }
    let accessCode; try { accessCode = await generateUniqueCode(8, null, null, name); } catch { notify('Could not generate code.', 'error'); return null; }
    const defaultImg = null;
    const { data: created, error } = await supabase.from('institutes').insert({
      name,
      institute_type: type,
      access_code: accessCode,
      teams,
      profile_id: profileData.id,
      email: profileData.email ?? null,
      country: profileData.country ?? null,
      state_province: profileData.region ?? null,
      address: profileData.address ?? null,
      image_url: instImageFile ? null : defaultImg,
      brand_logo_url: instImageFile ? null : defaultImg,
      workspace_id: activeInst?.workspace_id || null,
      limit: isChallenge ? challengeLimit : null,
      starts_at: isChallenge ? startsAt.toISOString() : null,
      ends_at: isChallenge ? endsAt.toISOString() : null,
      challenge_member_limit: isChallenge ? challengeLimit : null,
      goal_steps: isChallenge ? challengeGoal : null,
      brand_settings: isChallenge ? {
        ...(activeInst?.brand_settings || {}),
        parentInstituteId: activeInst?.id || null,
        parentInstituteName: activeInst?.name || null,
        departmentSourceInstituteId: challengeDepartmentSource?.id || null,
        departmentSourceInstituteName: challengeDepartmentSource?.name || null,
        departmentIds: selectedChallengeDepartments.map(dept => dept.id),
        departmentNames: selectedChallengeDepartments.map(dept => dept.name),
        challengeMode: teams ? 'departments' : 'members',
        challengeModeLabel: teams ? `${deptLabel}s` : 'Members',
        description: instChallengeDescription.trim() || null,
        goalSteps: challengeGoal,
        startsOn: startsAt.toISOString(),
        endsOn: endsAt.toISOString(),
      } : (activeInst?.workspace_id ? { ...(activeInst?.brand_settings || {}) } : {}),
    }).select('id,name,institute_type,access_code,teams,country,state_province,image_url,brand_logo_url,address,workspace_id,starts_at,ends_at,challenge_member_limit,limit,goal_steps,brand_settings').single();
    if (error || !created) { notify(error?.message ? `Failed to create institute: ${error.message}` : 'Failed to create institute.', 'error'); return null; }
    let imageUrl = null;
    if (instImageFile) {
      const ext = instImageFile.name.split('.').pop() || 'png';
      const path = `institutes/${created.id}/logo-${Date.now()}.${ext}`;
      imageUrl = await uploadFileToR2({ bucket: 'media', folder: 'institutes-logos', filename: path.replace(/[/\\]+/g, '-'), file: instImageFile });
      await supabase.from('institutes').update({ image_url: imageUrl, brand_logo_url: imageUrl }).eq('id', created.id);
    }
    const displayLogoUrl = imageUrl || created.brand_logo_url || created.image_url || defaultImg;
    const inst = { id: created.id, name: created.name, type: created.institute_type, code: created.access_code, teams: created.teams, country: created.country, region: created.state_province, address: created.address ?? null, image_url: displayLogoUrl, brand_logo_url: displayLogoUrl, display_logo_url: displayLogoUrl, workspace_id: created.workspace_id || null, starts_at: created.starts_at || null, ends_at: created.ends_at || null, challenge_member_limit: created.challenge_member_limit ?? created.limit ?? null, limit: created.limit ?? null, goal_steps: created.goal_steps ?? created.brand_settings?.goalSteps ?? null, brand_settings: created.brand_settings || {}, members: [], departments: [] };
    if (teams) {
      const seeds = isChallenge
        ? selectedChallengeDepartments.map(dept => ({ name: dept.name, image_url: dept.image_url || null, type: dept.type || 'Member' }))
        : Array.from(new Set((instInitialDepartments || '').split(/\n|,/).map(v => v.trim()).filter(Boolean).map(v => v.slice(0, 80)))).slice(0, 24).map(deptName => ({ name: deptName, image_url: null, type: ['School','College'].includes(type) ? 'Student' : 'Member' }));
      const createdDepartments = [];
      for (const seed of seeds) {
        let code = null;
        try { code = await generateUniqueCode(8, null, null, seed.name); } catch { code = null; }
        if (!code) continue;
        const { data: dept, error: deptErr } = await supabase.from('institute_teams').insert([{ institute_id: inst.id, name: seed.name, image_url: seed.image_url, access_code: code, type: seed.type }]).select().single();
        if (!deptErr && dept) createdDepartments.push({ id: dept.id, name: dept.name, code: dept.access_code, image_url: dept.image_url, institute_id: inst.id, members: [] });
      }
      if (createdDepartments.length) {
        await supabase.from('institutes').update({ team_options: createdDepartments.map(d => ({ name: d.name, image_url: d.image_url })) }).eq('id', inst.id);
        inst.departments = createdDepartments;
      }
    }
    await supabase.from('user_institutes').insert({ profile_id: profileData.id, institute_id: inst.id, association_type: 'Owner', status: 'Active', joined_date: new Date().toISOString(), steps: 0, lifetime_steps: 0, team_id: null });
    let newIdx = 0;
    setInstitutes(prev => { const next = [...prev, inst]; next.sort(instituteCardOrder); newIdx = next.findIndex(x => x.id === inst.id); return next; });
    setActiveInstIdx(newIdx); setShowAddInstitute(false);
    setInstName(''); setInstNewType('Collective Group'); setInstTeams(false); setInstInitialDepartments(''); setInstChallengeStartsOn(dateInputValue(new Date())); setInstChallengeEndsOn(dateInputValue(addDays(new Date(), 1))); setInstChallengeMemberLimit(''); setInstChallengeDescription(''); setInstChallengeGoalSteps('50000'); setSelectedPlatformChallengeOffer(null); setInstChallengeDepartmentIds([]); setInstImageFile(null); setInstImagePreview(null); setInstInitMembers([{ id: uid('m'), username: '' }]);
    notify(isChallenge ? 'Challenge created.' : 'Institute created.', 'success'); return created;
  };

  const deleteInstitute = async (id) => {
    askConfirm({ title: 'Archive institute', message: 'Archive this institute? It will be hidden from users while its data stays saved.', confirmLabel: 'Archive', tone: 'danger', onConfirm: async () => {
    await supabase.from('institutes').update({ archived_at: new Date().toISOString() }).eq('id', id);
    setInstitutes(prev => { const next = prev.filter(x => x.id !== id); setActiveInstIdx(Math.min(activeInstIdx, next.length - 1)); return next; });
    setExpandedDept(null);
    notify('Institute archived.', 'success');
    setInstituteRefresh(value => value + 1);
    } });
  };

  const restoreInstitute = async (inst) => {
    if (!inst?.id || !inst.archived_at || restoringInstituteId) return;
    setRestoringInstituteId(inst.id);
    try {
      const { error } = await supabase.rpc('restore_owned_institute', { p_institute_id: inst.id });
      if (error) throw error;
      notify('Institute restored. Reassign members to departments if needed.', 'success');
      setInstituteRefresh(value => value + 1);
    } catch (error) {
      notify(error?.message || 'Could not restore institute.', 'error');
    } finally {
      setRestoringInstituteId(null);
    }
  };

  const deleteDept = async (deptId) => {
    askConfirm({ title: 'Archive department', message: 'Archive this department? Members stay in the institute but will no longer be assigned to it.', confirmLabel: 'Archive', tone: 'danger', onConfirm: async () => {
    await supabase.from('user_institutes').update({ team_id: null }).eq('team_id', deptId);
    await supabase.from('institute_teams').update({ archived_at: new Date().toISOString() }).eq('id', deptId);
    setInstitutes(prev => { const list = [...prev]; const i = list.findIndex(x => x.id === activeInst.id); if (i !== -1) list[i] = { ...list[i], departments: (list[i].departments || []).filter(d => d.id !== deptId) }; return list; });
    if (expandedDept === deptId) setExpandedDept(null);
    notify('Department archived.', 'success');
    } });
  };

  const removeMember = async (memberId, memberName) => {
    if (!activeInst?.id) return;
    askConfirm({ title: 'Remove member', message: `Remove ${memberName}?`, confirmLabel: 'Remove', tone: 'danger', onConfirm: async () => {
    await supabase.from('user_institutes').update({ status: 'Former', left_date: new Date().toISOString() }).eq('profile_id', memberId).eq('institute_id', activeInst.id);
    setInstitutes(prev => prev.map(i => i.id === activeInst.id ? { ...i, members: i.members.filter(m => m.id !== memberId) } : i));
    notify('Member removed.', 'success');
    } });
  };

  const removeFromDept = async (deptId, profileId, memberName) => {
    if (!activeInst?.id) return;
    askConfirm({ title: `Remove from ${deptLabel.toLowerCase()}`, message: `Remove ${memberName} from this ${deptLabel.toLowerCase()}? They will remain a member of ${activeInst.name}.`, confirmLabel: 'Remove', tone: 'danger', onConfirm: async () => {
    const { error } = await supabase.from('user_institutes').update({ team_id: null }).eq('profile_id', profileId).eq('institute_id', activeInst.id).eq('team_id', deptId);
    if (error) { notify(`Could not remove member from ${deptLabel.toLowerCase()}.`, 'error'); return; }
    const { data: rows } = await supabase.from('user_institutes').select('steps,association_type,status,profiles:profiles!inner(id,name,surname,username,profile_img)').eq('institute_id', activeInst.id).eq('team_id', deptId).neq('status', 'Former');
    const mems = (rows || []).map(r => ({ id: r.profiles?.id, name: [r.profiles?.name, r.profiles?.surname].filter(Boolean).join(' ') || r.profiles?.username || 'Unknown', username: r.profiles?.username || null, profile_img: r.profiles?.profile_img || null, steps: r.steps ?? 0, association_type: r.association_type, status: r.status }));
    setInstitutes(prev => { const list = [...prev]; const ii = list.findIndex(i => i.id === activeInst.id); if (ii !== -1) { const ic = { ...list[ii] }; ic.members = (ic.members || []).map(m => m.id === profileId ? { ...m, team_id: null } : m); ic.departments = (ic.departments || []).map(d => d.id === deptId ? { ...d, members: mems } : d); list[ii] = ic; } return list; });
    notify(`Member removed from ${deptLabel.toLowerCase()}.`, 'success');
    } });
  };

  const saveInstitute = async () => {
    if (!editInstForm) return;
    const isChallenge = editInstForm.type === 'Challenge';
    const challengeAdminLocked = Boolean(isChallenge && (editInstForm.brand_settings?.platformChallengeOptIn || editInstForm.brand_settings?.platformChallengeId));
    let challengeUpdates = {};
    if (isChallenge) {
      const startsAt = utcDateBoundary(editInstForm.starts_on);
      const endsAt = utcDateBoundary(editInstForm.ends_on, true);
      const challengeLimit = Math.floor(Number(editInstForm.challenge_member_limit));
      if (!challengeAdminLocked && (!editInstForm.starts_on || !Number.isFinite(startsAt.getTime()))) { notify('Choose when this challenge starts.', 'error'); return; }
      if (!challengeAdminLocked && (!editInstForm.ends_on || !Number.isFinite(endsAt.getTime()))) { notify('Choose when this challenge finishes.', 'error'); return; }
      if (!challengeAdminLocked && !(endsAt > startsAt)) { notify('Challenge finish date must be after the start date.', 'error'); return; }
      if (!Number.isFinite(challengeLimit) || challengeLimit <= 0) { notify('Enter a valid participant limit.', 'error'); return; }
      const challengeGoal = Math.floor(Number(editInstForm.goal_steps));
      if (!challengeAdminLocked && (!Number.isFinite(challengeGoal) || challengeGoal <= 0)) { notify('Enter a valid step goal.', 'error'); return; }
      const challengeUsesGroups = editInstForm.audience_mode === 'groups';
      const selectedAudienceDepartments = challengeUsesGroups
        ? editChallengeDepartments.filter(dept => (editInstForm.audience_department_ids || []).includes(dept.id))
        : [];
      if (!challengeAdminLocked && challengeUsesGroups && !editInstForm.audience_source_id) {
        notify(`Choose a source institute before assigning ${deptLabel.toLowerCase()}s.`, 'error');
        return;
      }
      if (!challengeAdminLocked && challengeUsesGroups && !selectedAudienceDepartments.length) {
        notify(`Select at least one ${deptLabel.toLowerCase()} for this challenge.`, 'error');
        return;
      }
      const { count, error: countError } = await supabase
        .from('user_institutes')
        .select('id', { count: 'exact', head: true })
        .eq('institute_id', editInstForm.id)
        .eq('status', 'Active');
      if (countError) { notify('Could not verify current participants.', 'error'); return; }
      if (challengeLimit < (count || 0)) {
        notify(`Participant limit cannot be below the ${count || 0} people already active.`, 'error');
        return;
      }
      challengeUpdates = challengeAdminLocked
        ? {
          challenge_member_limit: challengeLimit,
        }
        : {
          teams: challengeUsesGroups,
          team_options: challengeUsesGroups ? selectedAudienceDepartments.map(dept => ({ name: dept.name, image_url: dept.image_url || null })) : null,
          starts_at: startsAt.toISOString(),
          ends_at: endsAt.toISOString(),
          challenge_member_limit: challengeLimit,
          goal_steps: challengeGoal,
          brand_settings: {
            ...(editInstForm.brand_settings || {}),
            description: (editInstForm.description || '').trim() || null,
            challengeDescription: (editInstForm.description || '').trim() || null,
            goalSteps: challengeGoal,
            startsOn: editInstForm.starts_on,
            endsOn: editInstForm.ends_on,
            parentInstituteId: editInstForm.audience_source_id || editInstForm.brand_settings?.parentInstituteId || null,
            parentInstituteName: editInstForm.audience_source_name || editInstForm.brand_settings?.parentInstituteName || null,
            departmentIds: selectedAudienceDepartments.map(dept => dept.id),
            departmentNames: selectedAudienceDepartments.map(dept => dept.name),
            challengeMode: challengeUsesGroups ? 'departments' : 'members',
            challengeModeLabel: challengeUsesGroups ? `${deptLabel}s` : 'Members',
            subgroupLabel: challengeUsesGroups ? deptLabel.toLowerCase() + 's' : null,
            subgroups: challengeUsesGroups,
          },
        };
    }
    let imageUrl = editInstForm.remove_image ? null : (editInstForm.image_url || null);
    if (!challengeAdminLocked && editInstImageFile) {
      const ext = editInstImageFile.name.split('.').pop() || 'png';
      const path = `institutes/${editInstForm.id}/logo-${Date.now()}.${ext}`;
      imageUrl = await uploadFileToR2({ bucket: 'media', folder: 'institutes-logos', filename: path.replace(/[/\\]+/g, '-'), file: editInstImageFile });
    }
    const instituteUpdate = challengeAdminLocked
      ? { ...challengeUpdates }
      : { name: editInstForm.name, image_url: imageUrl, brand_logo_url: imageUrl, ...challengeUpdates };
    const { data: updated, error } = await supabase.from('institutes').update(instituteUpdate).eq('id', editInstForm.id).select('id,name,institute_type,image_url,brand_logo_url,teams,starts_at,ends_at,challenge_member_limit,goal_steps,brand_settings').single();
    if (error) { notify('Failed to save.', 'error'); return; }
    if (isChallenge && !challengeAdminLocked) {
      const selectedAudienceDepartments = editInstForm.audience_mode === 'groups'
        ? editChallengeDepartments.filter(dept => (editInstForm.audience_department_ids || []).includes(dept.id))
        : [];
      const { data: existingChallengeDepartments, error: existingDeptError } = await supabase
        .from('institute_teams')
        .select('id,name')
        .eq('institute_id', editInstForm.id)
        .is('archived_at', null);
      if (existingDeptError) { notify(`Saved challenge, but could not update ${deptLabel.toLowerCase()}s.`, 'error'); return; }
      const now = new Date().toISOString();
      if (editInstForm.audience_mode !== 'groups') {
        await supabase.from('user_institutes').update({ team_id: null }).eq('institute_id', editInstForm.id);
        if ((existingChallengeDepartments || []).length) {
          await supabase.from('institute_teams').update({ archived_at: now }).eq('institute_id', editInstForm.id).is('archived_at', null);
        }
      } else {
        const selectedNames = new Set(selectedAudienceDepartments.map(dept => dept.name));
        const existingByName = new Map((existingChallengeDepartments || []).map(dept => [dept.name, dept]));
        const toArchive = (existingChallengeDepartments || []).filter(dept => !selectedNames.has(dept.name)).map(dept => dept.id);
        if (toArchive.length) {
          await supabase.from('user_institutes').update({ team_id: null }).eq('institute_id', editInstForm.id).in('team_id', toArchive);
          await supabase.from('institute_teams').update({ archived_at: now }).in('id', toArchive);
        }
        for (const dept of selectedAudienceDepartments) {
          if (existingByName.has(dept.name)) continue;
          let code = null;
          try { code = await generateUniqueCode(8, null, null, dept.name); } catch { code = null; }
          await supabase.from('institute_teams').insert([{ institute_id: editInstForm.id, name: dept.name, image_url: dept.image_url || null, access_code: code, type: dept.type || 'Member' }]);
        }
      }
    }
    setInstitutes(prev => prev.map(i => i.id === updated.id ? { ...i, name: updated.name, type: updated.institute_type, teams: !!updated.teams, image_url: updated.image_url, brand_logo_url: updated.brand_logo_url, display_logo_url: updated.brand_logo_url || updated.image_url || null, starts_at: updated.starts_at, ends_at: updated.ends_at, challenge_member_limit: updated.challenge_member_limit, goal_steps: updated.goal_steps, brand_settings: updated.brand_settings || {}, departments: [] } : i));
    setEditingInstitute(null); setEditInstForm(null); setEditInstImageFile(null); setEditInstImagePreview(null); notify('Saved.', 'success');
  };

  const saveDept = async () => {
    if (!editDeptForm) return;
    let imageUrl = editDeptForm.remove_image ? null : (editDeptForm.image_url || null);
    if (editDeptImageFile) {
      const ext = editDeptImageFile.name.split('.').pop() || 'png';
      const path = `departments/${editDeptForm.id}/logo-${Date.now()}.${ext}`;
      imageUrl = await uploadFileToR2({ bucket: 'media', folder: 'institutes-departments', filename: path.replace(/[/\\]+/g, '-'), file: editDeptImageFile });
    }
    const { error } = await supabase.from('institute_teams').update({ name: editDeptForm.name, image_url: imageUrl }).eq('id', editDeptForm.id);
    if (error) { notify('Failed to save.', 'error'); return; }
    const { data: instData } = await supabase.from('institutes').select('team_options').eq('id', editDeptForm.institute_id).single();
    const updatedOptions = (instData?.team_options || []).map(opt => opt?.name === editDeptForm.previous_name || opt?.name === editDeptForm.name ? { ...opt, name: editDeptForm.name, image_url: imageUrl } : opt);
    await supabase.from('institutes').update({ team_options: updatedOptions }).eq('id', editDeptForm.institute_id);
    setInstitutes(prev => prev.map(inst => ({ ...inst, departments: (inst.departments || []).map(d => d.id === editDeptForm.id ? { ...d, name: editDeptForm.name, image_url: imageUrl } : d) })));
    setEditingDepartment(null); setEditDeptForm(null); setEditDeptImageFile(null); setEditDeptImagePreview(null); notify('Saved.', 'success');
  };

  const createWG = async () => {
    const name = wgName.trim();
    if (!name || !profileData?.id) return;
    const ownedGroupCount = workoutGroups.filter(g => getWorkoutGroupPlanOwnerId(g) === profileData.id).length;
    if (workoutAccess?.limits?.groups !== null && ownedGroupCount >= workoutAccess?.limits?.groups) {
      notify(`Your current plan allows ${workoutAccess.limits.groups} workout group${workoutAccess.limits.groups === 1 ? '' : 's'}. Upgrade to add more.`, 'warning');
      setShowCreateWG(false);
      setShowWorkoutPlans(true);
      window.requestAnimationFrame(() => {
        workoutPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    setCreatingWG(true);
    try {
      const { error } = await supabase.from('workout_groups').insert({ coach_id: profileData.id, created_by: profileData.id, name });
      if (error) throw error;
      setWgName(''); setShowCreateWG(false);
      await loadWG(); notify('Workout group created.', 'success');
    } catch { notify('Failed to create workout group.', 'error'); } finally { setCreatingWG(false); }
  };

  const loadGroupData = async (groupId) => {
    try {
      const detailSelectWithAppSettings = 'id,name,coach_id,created_by,join_code,weekly_xp_target,weekly_session_target,session_target_goal_key,session_target_tracking_key,session_target_intensity_key,member_analytics_visibility,daily_calorie_target,allowed_workout_names,allowed_workout_pairs,club_id,season_id,coach:profiles!workout_groups_coach_id_fkey(id,username,name,surname,profile_img),workout_clubs(id,name,sport_id,logo_url,primary_color,secondary_color,accent_color,brand_settings),workout_club_seasons(id,name,status,starts_on,ends_on),workout_group_members(athlete_id,profiles(id,username,name,surname,profile_img,xp,level,push_token))';
      const detailSelectWithoutVisibility = detailSelectWithAppSettings.replace(',member_analytics_visibility', '');
      const detailSelectLegacy = detailSelectWithAppSettings.replace(',session_target_goal_key,session_target_tracking_key,session_target_intensity_key,member_analytics_visibility', '');
      let g = null;
      let lastGroupLoadError = null;
      for (const select of [detailSelectWithAppSettings, detailSelectWithoutVisibility, detailSelectLegacy]) {
        const result = await supabase.from('workout_groups').select(select).eq('id', groupId).single();
        if (!result.error) {
          g = result.data;
          break;
        }
        lastGroupLoadError = result.error;
        if (!isMissingWorkoutGroupColumnError(result.error)) throw result.error;
        console.warn('loadGroupData falling back after workout_groups schema mismatch:', result.error);
      }
      if (!g && lastGroupLoadError) throw lastGroupLoadError;
      const [{ data: clubSeasons }, { data: demoMemberRows, error: demoMemberError }] = await Promise.all([
        g?.club_id
          ? supabase.from('workout_club_seasons').select('id,name,status,starts_on,ends_on').eq('club_id', g.club_id).order('created_at', { ascending: false })
          : Promise.resolve({ data: [] }),
        supabase.from('demo_workout_group_members').select(DEMO_WORKOUT_GROUP_MEMBERS_SELECT).eq('group_id', groupId),
      ]);
      if (demoMemberError) console.warn('loadGroupData demo members error:', demoMemberError);
      setWgClubSeasons(clubSeasons || []);
      setActiveWG(prev => ({ ...(prev || {}), ...(g || {}) }));
      const mRows = g?.workout_group_members || [];
      const demoRows = (demoMemberRows || []).map(mapDemoWorkoutMemberRow).filter(isCountingWorkoutAthlete);
      const realAthleteIds = mRows.map(m => m.athlete_id).filter(Boolean);
      const clubSportId = String(firstRelationRow(g?.workout_clubs)?.sport_id || '').trim();
      let sportPositionByProfileId = new Map();
      if (realAthleteIds.length && clubSportId) {
        const { data: sportProfileRows, error: sportProfileError } = await supabase
          .from('sport_profiles')
          .select('profile_id,position')
          .in('profile_id', realAthleteIds)
          .eq('sport_id', clubSportId);
        if (sportProfileError) {
          console.warn('loadGroupData sport profile positions error:', sportProfileError);
        } else {
          sportPositionByProfileId = new Map((sportProfileRows || []).map(row => [row.profile_id, String(row.position || '').trim()]).filter(([, position]) => position));
        }
      }
      const realMembers = mRows.map(m => {
        const profile = firstRelationRow(m.profiles);
        if (!profile) return null;
        const position = sportPositionByProfileId.get(profile.id);
        return position ? { ...profile, position, sport_position: position } : profile;
      }).filter(Boolean);
      const demoMembers = demoRows.map(m => m.profiles).filter(Boolean);
      setWgMembers([...realMembers, ...demoMembers].sort((a, b) => ((`${a.name || ''} ${a.surname || ''}`).trim() || a.username || '').localeCompare(((`${b.name || ''} ${b.surname || ''}`).trim() || b.username || ''))));
      const demoAthleteIds = demoRows.map(m => m.demo_profile_id || m.athlete_id).filter(Boolean);
      const athleteIds = [...realAthleteIds, ...demoAthleteIds];
      if (!athleteIds.length) {
        setGroupStats({ activeThisWeek: 0, totalSessions: 0, totalXp: 0, avgXp: 0, totalSets: 0, totalReps: 0, totalVolume: 0, activeDays: 0, totalDurationSeconds: 0, totalRestSeconds: 0 });
        setPerfMap({});
        setWgAnalyticsSessions([]);
        setWgNutritionDays(1);
        setWgNutritionSummary(null);
        setWgNutritionRows([]);
        setWgNutritionByAthlete({});
        setWgNutritionProfilesByAthlete({});
        setWgNutritionError('');
        return;
      }
      const selectedSeasonId = String(wgRangeMode || '').startsWith('season:') ? String(wgRangeMode).replace('season:', '') : null;
      const season = selectedSeasonId
        ? (clubSeasons || []).find(row => row.id === selectedSeasonId) || g?.workout_club_seasons || null
        : null;
      const dateWindow = wgRangeMode === 'date' ? getWGSelectedDateWindow() : null;
      let startAt = dateWindow?.start || getWGCalendarStart(timeRange);
      const todayEndAt = getWorkoutGroupEndOfToday();
      let endAt = dateWindow?.end || todayEndAt;
      if (season?.starts_on) {
        const seasonStart = new Date(`${season.starts_on}T00:00:00`);
        if (selectedSeasonId || seasonStart > startAt) startAt = seasonStart;
      }
      if (season?.ends_on) {
        endAt = minWorkoutGroupDate(new Date(`${season.ends_on}T23:59:59.999`), todayEndAt) || todayEndAt;
      }
      const sessionSelectWithExercises = 'id,profile_id,total_xp,started_at,local_date,duration_seconds,total_sets,total_reps,total_volume,total_rest_seconds,session_effort,workout_plan_id,preset_plan_id,user_workouts(id,name,share_code,source_workout_plan_id),workouts(id,name),workout_exercise_summary(total_reps,avg_weight,total_xp,exercises(id,name,image_url,gif_url))';
      const sessionSelectTotalsOnly = 'id,profile_id,total_xp,started_at,local_date,duration_seconds,total_sets,total_reps,total_volume,total_rest_seconds,session_effort,workout_plan_id,preset_plan_id,user_workouts(id,name,share_code,source_workout_plan_id),workouts(id,name)';
      const demoSessionSelect = 'id,demo_profile_id,group_id,total_xp,started_at,local_date,duration_seconds,total_sets,total_reps,total_volume,total_rest_seconds,session_effort,workout_plan_id,user_workouts(id,name,share_code,source_workout_plan_id),demo_user_workout_feedback(rating,difficulty,focus_rating,mood,notes),demo_exercise_progress(id,set,set_number,logged_as,reps,weight,volume,xp_gained,performed_at,exercises(id,name,image_url,gif_url))';
      const loadRealSessions = async () => {
        if (!realAthleteIds.length) return [];
        let sessionQuery = supabase.from('workout_sessions').select(sessionSelectWithExercises).in('profile_id', realAthleteIds).gte('started_at', startAt.toISOString());
        if (endAt) sessionQuery = sessionQuery.lte('started_at', endAt.toISOString());
        let { data: sessions, error: sessionsError } = await sessionQuery;
        if (sessionsError) {
          console.warn('loadGroupData falling back without exercise summaries:', sessionsError);
          let fallbackSessionQuery = supabase.from('workout_sessions').select(sessionSelectTotalsOnly).in('profile_id', realAthleteIds).gte('started_at', startAt.toISOString());
          if (endAt) fallbackSessionQuery = fallbackSessionQuery.lte('started_at', endAt.toISOString());
          const fallbackResult = await fallbackSessionQuery;
          if (fallbackResult.error) throw fallbackResult.error;
          sessions = fallbackResult.data || [];
        }
        return sessions || [];
      };
      const loadDemoSessions = async () => {
        if (!demoAthleteIds.length) return [];
        let demoSessionQuery = supabase
          .from('demo_workout_sessions')
          .select(demoSessionSelect)
          .eq('group_id', groupId)
          .in('demo_profile_id', demoAthleteIds)
          .gte('started_at', startAt.toISOString());
        if (endAt) demoSessionQuery = demoSessionQuery.lte('started_at', endAt.toISOString());
        const { data, error } = await demoSessionQuery;
        if (error) {
          console.warn('loadGroupData demo sessions error:', error);
          return [];
        }
        return (data || []).map(normalizeDemoWorkoutSession);
      };
      const [realSessions, demoSessions] = await Promise.all([loadRealSessions(), loadDemoSessions()]);
      const sessions = [...realSessions, ...demoSessions];
      const all = (sessions || []).filter(s => sessionMatchesWGAllowedWorkout(s, g));
      const totalXp = all.reduce((s, x) => s + (x.total_xp || 0), 0);
      const active = new Set(all.map(s => s.profile_id)).size;
      const activeDays = new Set(all.map(s => getWorkoutSessionDateKey(s)).filter(Boolean)).size;
      const sessionTotals = all.reduce((acc, session) => {
        const totals = getWorkoutSessionTotals(session);
        acc.totalSets += totals.totalSets;
        acc.totalReps += totals.totalReps;
        acc.totalVolume += totals.totalVolume;
        acc.totalDurationSeconds += totals.durationSeconds;
        acc.totalRestSeconds += totals.totalRestSeconds;
        acc.effortSum += totals.effort;
        acc.effortCount += totals.effort > 0 ? 1 : 0;
        return acc;
      }, { totalSets: 0, totalReps: 0, totalVolume: 0, totalDurationSeconds: 0, totalRestSeconds: 0, effortSum: 0, effortCount: 0 });
      setWgAnalyticsSessions(all);
      setGroupStats({
        activeThisWeek: active,
        totalSessions: all.length,
        totalXp,
        avgXp: active > 0 ? Math.round(totalXp / active) : 0,
        totalSets: sessionTotals.totalSets,
        totalReps: sessionTotals.totalReps,
        totalVolume: sessionTotals.totalVolume,
        activeDays,
        totalDurationSeconds: sessionTotals.totalDurationSeconds,
        totalRestSeconds: sessionTotals.totalRestSeconds,
        averageEffort: sessionTotals.effortCount > 0 ? sessionTotals.effortSum / sessionTotals.effortCount : 0,
      });
      const nutritionWindow = getWGNutritionWindow(season);
      const groupCanManageNutrition = !!(
        profileData?.id &&
        (g?.coach_id === profileData.id || g?.created_by === profileData.id || g?.__isClubStaff || activeWG?.__isClubStaff) &&
        (
          ['pro_coach', 'team_club'].includes(activeWGOwnerAccess?.tier) ||
          activeWGOwnerAccess?.entitlements?.has?.('workout_groups.club_analysis')
        )
      );
      let foodRows = [];
      let nutritionProfileRows = [];
      let nutritionLoadError = null;
      if (groupCanManageNutrition) {
        try {
          let foodQuery = realAthleteIds.length
            ? supabase
              .from('food_log')
              .select('user_id,log_date,meal_section,food_name,calories,protein_g,carbs_g,fat_g,fiber_g,sugar_g,sodium_mg,created_at')
              .in('user_id', realAthleteIds)
            : null;
          let demoFoodQuery = demoAthleteIds.length
            ? supabase
              .from('demo_food_log')
              .select('demo_profile_id,log_date,meal_section,food_name,calories,protein_g,carbs_g,fat_g,fiber_g,sugar_g,sodium_mg,created_at')
              .eq('group_id', groupId)
              .in('demo_profile_id', demoAthleteIds)
            : null;
          if (nutritionWindow.start) {
            const nutritionStartDate = nutritionWindow.startDate || localDateInputValue(nutritionWindow.start);
            if (foodQuery) foodQuery = foodQuery.gte('log_date', nutritionStartDate);
            if (demoFoodQuery) demoFoodQuery = demoFoodQuery.gte('log_date', nutritionStartDate);
          }
          else {
            const nutritionFetchStart = new Date();
            nutritionFetchStart.setDate(nutritionFetchStart.getDate() - 1095);
            if (foodQuery) foodQuery = foodQuery.gte('log_date', nutritionFetchStart.toISOString().slice(0, 10));
            if (demoFoodQuery) demoFoodQuery = demoFoodQuery.gte('log_date', nutritionFetchStart.toISOString().slice(0, 10));
          }
          if (nutritionWindow.end) {
            const nutritionEndDate = nutritionWindow.endDate || localDateInputValue(nutritionWindow.end);
            if (foodQuery) foodQuery = foodQuery.lte('log_date', nutritionEndDate);
            if (demoFoodQuery) demoFoodQuery = demoFoodQuery.lte('log_date', nutritionEndDate);
          }
          const [foodResult, demoFoodResult, profileResult] = await Promise.all([
            foodQuery || Promise.resolve({ data: [], error: null }),
            demoFoodQuery || Promise.resolve({ data: [], error: null }),
            realAthleteIds.length
              ? supabase
                .from('nutrition_profiles')
                .select('user_id,calorie_target,protein_g,carbs_g,fat_g,fiber_g,sugar_g,sodium_mg,goal_type,weight_kg')
                .in('user_id', realAthleteIds)
              : Promise.resolve({ data: [], error: null }),
          ]);
          if (foodResult.error) throw foodResult.error;
          if (demoFoodResult.error) console.warn('Workout group demo nutrition load error:', demoFoodResult.error);
          if (profileResult.error) console.warn('Workout group nutrition profile load error:', profileResult.error);
          foodRows = [...(foodResult.data || []), ...(demoFoodResult.error ? [] : (demoFoodResult.data || []).map(mapDemoFoodLogRow))];
          nutritionProfileRows = profileResult.error ? [] : (profileResult.data || []);
          setWgNutritionError('');
        } catch (nutritionError) {
          console.warn('Workout group nutrition load error:', nutritionError);
          nutritionLoadError = nutritionError;
          setWgNutritionError('Nutrition data is private or not available for this group.');
        }
      } else {
        setWgNutritionError('');
      }
      const nutritionRows = (foodRows || []).filter(row => {
        const time = dateOnlyTime(row.log_date);
        if (!time) return false;
        if (nutritionWindow.start && time < dateOnlyTime(nutritionWindow.startDate || localDateInputValue(nutritionWindow.start))) return false;
        if (nutritionWindow.end && time > dateOnlyTime(nutritionWindow.endDate || localDateInputValue(nutritionWindow.end))) return false;
        return true;
      });
      const nutritionDays = getWGNutritionWindowDays(nutritionRows, nutritionWindow, season);
      const nutritionProfileMap = {};
      (nutritionProfileRows || []).forEach(row => {
        if (row?.user_id) nutritionProfileMap[row.user_id] = row;
      });
      setWgNutritionDays(nutritionDays);
      setWgNutritionRows(groupCanManageNutrition && !nutritionLoadError ? nutritionRows : []);
      setWgNutritionProfilesByAthlete(nutritionProfileMap);
      if (groupCanManageNutrition && !nutritionLoadError) {
        const dailyCalorieTarget = safeWorkoutGroupNutritionNumber(g?.daily_calorie_target, 2500);
        const groupProteinTargets = athleteIds
          .map(id => getWorkoutGroupAthleteProteinTarget(g, nutritionProfileMap[id]))
          .filter(value => Number.isFinite(value) && value > 0);
        const avgGroupProteinTarget = groupProteinTargets.length
          ? Math.round(groupProteinTargets.reduce((sum, value) => sum + value, 0) / groupProteinTargets.length)
          : null;
        setWgNutritionSummary(buildWorkoutGroupNutritionSummary(nutritionRows, {
          expectedDays: nutritionDays,
          athleteCount: athleteIds.length,
          dailyCalorieTarget,
          dailyProteinTarget: avgGroupProteinTarget,
        }));
        const byAthlete = {};
        athleteIds.forEach(id => {
          byAthlete[id] = buildWorkoutGroupNutritionSummary(nutritionRows.filter(row => row.user_id === id), {
            expectedDays: nutritionDays,
            athleteCount: 1,
            dailyCalorieTarget,
            dailyProteinTarget: getWorkoutGroupAthleteProteinTarget(g, nutritionProfileMap[id]),
          });
        });
        setWgNutritionByAthlete(byAthlete);
      } else {
        setWgNutritionSummary(null);
        setWgNutritionRows([]);
        setWgNutritionByAthlete({});
        setWgNutritionProfilesByAthlete(nutritionProfileMap);
      }
      const pm = {}; athleteIds.forEach(id => { pm[id] = { rangeXp: 0, sessionsInRange: 0, lastWorkout: null, caloriesInRange: 0 }; });
      for (const s of all) { if (!pm[s.profile_id]) pm[s.profile_id] = { rangeXp: 0, sessionsInRange: 0, lastWorkout: null, caloriesInRange: 0 }; pm[s.profile_id].rangeXp += (s.total_xp || 0); pm[s.profile_id].sessionsInRange += 1; if (!pm[s.profile_id].lastWorkout || new Date(s.started_at) > new Date(pm[s.profile_id].lastWorkout)) pm[s.profile_id].lastWorkout = s.started_at; }
      for (const row of nutritionRows) {
        if (!pm[row.user_id]) pm[row.user_id] = { rangeXp: 0, sessionsInRange: 0, lastWorkout: null, caloriesInRange: 0 };
        pm[row.user_id].caloriesInRange += Number(row.calories || 0);
      }
      setPerfMap(pm);
    } catch (e) { console.error(e); }
  };

  const hydrateWGCalendarSessions = async (sessionRows = []) => {
    const sessionIds = sessionRows.map(row => row.id).filter(Boolean);
    if (!sessionIds.length) return [];

    const [{ data: athleteRows, error: athleteError }, { data: coachRows, error: coachError }] = await Promise.all([
      supabase
        .from('workout_group_calendar_session_athletes')
        .select('id,session_id,athlete_id,status,responded_at,created_at')
        .in('session_id', sessionIds),
      supabase
        .from('workout_group_calendar_session_coaches')
        .select('id,session_id,coach_id,role,created_at')
        .in('session_id', sessionIds),
    ]);
    if (athleteError) throw athleteError;
    if (coachError) throw coachError;

    const profileIds = uniqValues([
      ...(athleteRows || []).map(row => row.athlete_id),
      ...(coachRows || []).map(row => row.coach_id),
      ...sessionRows.map(row => row.created_by),
    ]);
    let profilesById = new Map();
    if (profileIds.length) {
      const { data: profileRows, error: profileError } = await supabase
        .from('profiles')
        .select(WG_CALENDAR_PROFILE_SELECT)
        .in('id', profileIds);
      if (profileError) throw profileError;
      profilesById = new Map((profileRows || []).map(profile => [profile.id, profile]));
    }

    const demoProfileIds = uniqValues(sessionRows.flatMap(session => (
      Array.isArray(session?.metadata?.demo_athlete_ids) ? session.metadata.demo_athlete_ids : []
    )));
    let demoProfilesById = new Map();
    if (demoProfileIds.length) {
      const { data: demoProfileRows, error: demoProfileError } = await supabase
        .from('demo_profiles')
        .select('id,username,name,surname,profile_img,level,metadata')
        .in('id', demoProfileIds);
      if (demoProfileError) throw demoProfileError;
      demoProfilesById = new Map((demoProfileRows || []).map(profile => [profile.id, profile]));
    }

    const athletesBySessionId = (athleteRows || []).reduce((acc, row) => {
      if (!row.session_id) return acc;
      acc[row.session_id] = acc[row.session_id] || [];
      acc[row.session_id].push({ ...row, profile: profilesById.get(row.athlete_id) || null });
      return acc;
    }, {});
    const coachesBySessionId = (coachRows || []).reduce((acc, row) => {
      if (!row.session_id) return acc;
      acc[row.session_id] = acc[row.session_id] || [];
      acc[row.session_id].push({ ...row, profile: profilesById.get(row.coach_id) || null });
      return acc;
    }, {});

    return sessionRows.map(session => {
      const demoIds = Array.isArray(session?.metadata?.demo_athlete_ids) ? session.metadata.demo_athlete_ids.filter(Boolean) : [];
      const demoAthletes = demoIds.map((demoId) => {
        const member = wgMembers.find(row => (row.demo_profile_id || row.id) === demoId);
        const profile = member || mapDemoProfileToWorkoutGroupCalendarProfile(demoProfilesById.get(demoId), demoId);
        return {
          id: `demo-${session.id}-${demoId}`,
          session_id: session.id,
          athlete_id: demoId,
          demo_profile_id: demoId,
          is_demo_profile: true,
          status: 'pending',
          profile,
        };
      });
      return {
        ...session,
        athletes: [...(athletesBySessionId[session.id] || []), ...demoAthletes],
        coaches: coachesBySessionId[session.id] || [],
        creatorProfile: profilesById.get(session.created_by) || null,
      };
    });
  };

  const loadWGCalendarSessions = useCallback(async (silent = false) => {
    if (!activeWG?.id) {
      setWgCalendarSessions([]);
      setWgCalendarSelectedSessionId(null);
      return;
    }
    if (!silent) setWgCalendarLoading(true);
    setWgCalendarError('');
    try {
      const { data, error } = await supabase
        .from('workout_group_calendar_sessions')
        .select('id,group_id,club_id,created_by,title,starts_at,ends_at,location,notes,status,metadata,created_at,updated_at')
        .eq('group_id', activeWG.id)
        .order('starts_at', { ascending: true });
      if (error) throw error;
      const rows = await hydrateWGCalendarSessions(data || []);
      setWgCalendarSessions(rows);
      setWgCalendarSelectedSessionId(current => current && rows.some(session => session.id === current) ? current : null);
    } catch (err) {
      console.error('loadWGCalendarSessions error:', err);
      setWgCalendarError('Could not load calendar sessions. Refresh or try again in a moment.');
    } finally {
      if (!silent) setWgCalendarLoading(false);
    }
  }, [activeWG?.id]);

  const loadWGCalendarCoachOptions = useCallback(async () => {
    if (!activeWG?.id) {
      setWgCalendarCoachOptions([]);
      return;
    }
    setWgCalendarCoachesLoading(true);
    try {
      let profileIds = uniqValues([activeWG.coach_id, activeWG.created_by, profileData?.id]);
      if (activeWG.club_id) {
        const [{ data: club }, { data: staffRows }] = await Promise.all([
          supabase.from('workout_clubs').select('owner_profile_id').eq('id', activeWG.club_id).maybeSingle(),
          supabase.from('workout_club_coaches').select('profile_id,status').eq('club_id', activeWG.club_id).eq('status', 'active'),
        ]);
        profileIds = uniqValues([
          ...profileIds,
          club?.owner_profile_id,
          ...(staffRows || []).map(row => row.profile_id),
        ]);
      }
      if (!profileIds.length) {
        setWgCalendarCoachOptions([]);
        return;
      }
      const { data, error } = await supabase
        .from('profiles')
        .select(WG_CALENDAR_PROFILE_SELECT)
        .in('id', profileIds);
      if (error) throw error;
      const profilesById = new Map((data || []).map(profile => [profile.id, profile]));
      setWgCalendarCoachOptions(profileIds
        .map(id => profilesById.get(id) || { id })
        .sort((a, b) => getWorkoutGroupCalendarProfileName(a).localeCompare(getWorkoutGroupCalendarProfileName(b), undefined, { sensitivity: 'base' })));
    } catch (err) {
      console.error('loadWGCalendarCoachOptions error:', err);
      setWgCalendarCoachOptions(profileData?.id ? [profileData] : []);
    } finally {
      setWgCalendarCoachesLoading(false);
    }
  }, [activeWG?.id, activeWG?.coach_id, activeWG?.created_by, activeWG?.club_id, profileData]);

  useEffect(() => {
    setWgCalendarFilter('upcoming');
    setWgCalendarSelectedDateKey(null);
    setWgCalendarSelectedSessionId(null);
    setWgCalendarFormOpen(false);
    setWgCalendarEditingSession(null);
    setWgCalendarForm(getWorkoutGroupCalendarDefaultForm(profileData?.id || ''));
    if (!activeWG?.id) {
      setWgCalendarSessions([]);
      setWgCalendarCoachOptions([]);
      return;
    }
    loadWGCalendarSessions();
    loadWGCalendarCoachOptions();
  }, [activeWG?.id, loadWGCalendarCoachOptions, loadWGCalendarSessions, profileData?.id]);

  useEffect(() => {
    if (!activeWG?.id) return undefined;
    let refreshTimer = null;
    const refreshCalendar = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => loadWGCalendarSessions(true), 250);
    };
    const refreshCalendarAndCoaches = () => {
      loadWGCalendarCoachOptions();
      refreshCalendar();
    };
    const channel = supabase
      .channel(`workout-group-calendar-profile-web-${activeWG.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_calendar_sessions', filter: `group_id=eq.${activeWG.id}` }, refreshCalendar)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_calendar_session_athletes' }, refreshCalendar)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_calendar_session_coaches' }, refreshCalendarAndCoaches)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_club_coaches' }, refreshCalendarAndCoaches)
      .subscribe();
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      supabase.removeChannel(channel);
    };
  }, [activeWG?.id, loadWGCalendarCoachOptions, loadWGCalendarSessions]);

  useEffect(() => { if (activeWGId) loadGroupData(activeWGId); }, [activeWGId, timeRange, wgRangeMode, wgCustomDays, wgSelectedDate, activeWGOwnerAccess]);

  const selectWGRange = (mode) => {
    const days = getWGRangeDays(mode);
    if (days !== 7 && activeWGOwnerId && !canUseWGExtendedRanges) {
      notify('Date, 14D, 30D, 90D, All, and custom ranges are included with Coach Starter and higher plans.', 'warning');
      return;
    }
    setWgRangeMode(mode);
    setTimeRange(days);
  };

  const canManageWG = (group = activeWG) => !!(group?.id && profileData?.id && (group.coach_id === profileData.id || group.created_by === profileData.id || group.__isClubStaff));

  const mergeWGLocal = (groupId, patch) => {
    setWorkoutGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...patch } : g));
    setActiveWG(prev => (prev?.id === groupId ? { ...prev, ...patch } : prev));
  };

  const updateWGNumber = async (field, label) => {
    if (!canManageWG()) return;
    setTargetModal({ field, label, value: String(activeWG?.[field] ?? 0) });
  };

  const hydrateWGSessionTargetDraft = (group = activeWG) => {
    const weeklySessionTarget = getWorkoutGroupWeeklySessionTarget(group);
    setWorkoutGroupSessionTargetDraft({
      weeklySessionTarget: String(weeklySessionTarget),
      weeklyXpTarget: String(normalizePositiveTarget(group?.weekly_xp_target, 1000)),
      goalKey: getWorkoutGroupSessionGoalOption(group).key,
      trackingKey: getWorkoutGroupSessionTrackingOption(group).key,
      intensityKey: getWorkoutGroupSessionIntensityOption(group).key,
    });
    setWorkoutGroupSessionTargetPicker(null);
  };

  const openWGSessionTargetModal = (group = activeWG) => {
    if (!canManageWG(group)) return;
    hydrateWGSessionTargetDraft(group);
    setWorkoutGroupManageOpen(false);
    setWorkoutGroupSessionTargetOpen(true);
  };

  const saveWGSessionTarget = async () => {
    if (!canManageWG() || !activeWG?.id || workoutGroupSessionTargetSaving) return;
    const sessionValue = Number(workoutGroupSessionTargetDraft.weeklySessionTarget);
    const xpValue = Number(workoutGroupSessionTargetDraft.weeklyXpTarget);
    if (!Number.isFinite(sessionValue) || sessionValue <= 0) {
      notify('Training days must be at least 1.', 'warning');
      return;
    }
    if (!Number.isFinite(xpValue) || xpValue <= 0) {
      notify('Weekly XP target must be at least 1.', 'warning');
      return;
    }

    const nextSessionTarget = Math.min(7, Math.max(1, Math.round(sessionValue)));
    const nextXpTarget = Math.round(xpValue);
    const goalOption = findWorkoutGroupSessionGoalOption(workoutGroupSessionTargetDraft.goalKey) ||
      findWorkoutGroupSessionGoalOption(getWorkoutGroupSessionGoalForTarget(nextSessionTarget)) ||
      WORKOUT_GROUP_SESSION_GOAL_OPTIONS[0];
    const trackingOption = findWorkoutGroupSessionTrackingOption(workoutGroupSessionTargetDraft.trackingKey) ||
      findWorkoutGroupSessionTrackingOption(getWorkoutGroupSessionTrackingForTarget(nextSessionTarget)) ||
      WORKOUT_GROUP_SESSION_TRACKING_OPTIONS[0];
    const intensityOption = findWorkoutGroupSessionIntensityOption(workoutGroupSessionTargetDraft.intensityKey) ||
      findWorkoutGroupSessionIntensityOption(getWorkoutGroupSessionIntensityForTarget(nextSessionTarget)) ||
      WORKOUT_GROUP_SESSION_INTENSITY_OPTIONS[1];
    const patch = {
      weekly_session_target: nextSessionTarget,
      weekly_xp_target: nextXpTarget,
      session_target_goal_key: goalOption.key,
      session_target_tracking_key: trackingOption.key,
      session_target_intensity_key: intensityOption.key,
    };

    setWorkoutGroupSessionTargetSaving(true);
    try {
      const { error } = await supabase.from('workout_groups').update(patch).eq('id', activeWG.id);
      if (error) {
        if (!isMissingWorkoutGroupColumnError(error)) throw error;
        const legacyPatch = {
          weekly_session_target: nextSessionTarget,
          weekly_xp_target: nextXpTarget,
        };
        const legacyResult = await supabase.from('workout_groups').update(legacyPatch).eq('id', activeWG.id);
        if (legacyResult.error) throw legacyResult.error;
        mergeWGLocal(activeWG.id, legacyPatch);
      } else {
        mergeWGLocal(activeWG.id, patch);
      }
      setWorkoutGroupSessionTargetOpen(false);
      setWorkoutGroupSessionTargetPicker(null);
      notify(`${nextSessionTarget} day${nextSessionTarget === 1 ? '' : 's'}/week and ${nextXpTarget.toLocaleString()} XP target saved.`, 'success');
    } catch (err) {
      console.error('saveWGSessionTarget error:', err);
      notify(err?.message || 'Could not save session target.', 'error');
    } finally {
      setWorkoutGroupSessionTargetSaving(false);
    }
  };

  const saveWGMemberAnalyticsVisibility = async (nextVisibility) => {
    if (!canManageWG() || !activeWG?.id || workoutGroupAnalyticsVisibilitySaving) return;
    const normalizedVisibility = nextVisibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
      ? WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
      : WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.ownOnly;
    if (getWorkoutGroupMemberAnalyticsVisibility(activeWG) === normalizedVisibility) return;
    setWorkoutGroupAnalyticsVisibilitySaving(true);
    try {
      const { error } = await supabase
        .from('workout_groups')
        .update({ member_analytics_visibility: normalizedVisibility })
        .eq('id', activeWG.id);
      if (error) throw error;
      mergeWGLocal(activeWG.id, { member_analytics_visibility: normalizedVisibility });
      notify(normalizedVisibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group ? 'Athletes can see everyone.' : 'Athletes can see only themselves.', 'success');
    } catch (err) {
      console.error('saveWGMemberAnalyticsVisibility error:', err);
      notify(isMissingWorkoutGroupColumnError(err) ? 'Athlete visibility needs the latest workout group database update.' : (err?.message || 'Could not save athlete visibility.'), 'error');
    } finally {
      setWorkoutGroupAnalyticsVisibilitySaving(false);
    }
  };

  const getPreferredThemeClubId = useCallback((surface) => {
    const options = getThemeClubOptionsForSurface(surface);
    if (!options.length) return null;
    return options[0]?.id || null;
  }, [getThemeClubOptionsForSurface]);

  const setThemeSurfaceEnabled = useCallback((surface, enabled) => {
    const config = WORKOUT_GROUP_THEME_SURFACES.find((item) => item.key === surface);
    if (!config) return;
    setWorkoutGroupThemePrefsDraft((prev) => {
      const next = { ...prev, [config.enabledKey]: !!enabled };
      if (enabled) {
        const hasAvailable = getThemeClubOptionsForSurface(surface).length > 0;
        if (!hasAvailable) {
          next[config.enabledKey] = false;
          return next;
        }
        next[config.clubIdKey] = parseThemeClubId(prev[config.clubIdKey]) || getPreferredThemeClubId(surface);
      } else {
        next[config.clubIdKey] = null;
      }
      return next;
    });
  }, [getPreferredThemeClubId, getThemeClubOptionsForSurface]);

  const setThemeSurfaceClub = useCallback((surface, clubId) => {
    const config = WORKOUT_GROUP_THEME_SURFACES.find((item) => item.key === surface);
    if (!config) return;
    setWorkoutGroupThemePrefsDraft((prev) => ({
      ...prev,
      [config.clubIdKey]: parseThemeClubId(clubId),
    }));
  }, []);

  const saveWorkoutGroupThemePrefs = useCallback(async () => {
    if (!profileData?.id || workoutGroupThemePrefsSaving) return;
    const next = {};
    let hasIssue = false;
    for (const surface of WORKOUT_GROUP_THEME_SURFACES) {
      const enabled = workoutGroupThemePrefsDraft[surface.enabledKey] === true;
      const options = getThemeClubOptionsForSurface(surface.key);
      if (!enabled) {
        next[surface.enabledKey] = false;
        next[surface.clubIdKey] = null;
        continue;
      }
      const availableIds = new Set(options.map((club) => String(club.id)));
      const pickedId = parseThemeClubId(workoutGroupThemePrefsDraft[surface.clubIdKey]);
      if (!pickedId || !availableIds.has(pickedId)) {
        notify(`Pick a club for ${surface.label} colours before saving.`, 'warning');
        hasIssue = true;
        return;
      }
      next[surface.enabledKey] = true;
      next[surface.clubIdKey] = pickedId;
    }
    if (hasIssue) return;
    if (!Object.keys(next).length) return;
    setWorkoutGroupThemePrefsSaving(true);
    try {
      const payload = {
        profile_id: profileData.id,
        ...next,
      };
      const { error } = await supabase.from('workout_group_theme_preferences').upsert(payload, { onConflict: 'profile_id' });
      if (error) throw error;
      const parsed = parseWorkoutGroupThemePreferences(payload);
      setWorkoutGroupThemePreferences(parsed);
      setWorkoutGroupThemePrefsDraft({ ...parsed });
      notify('Workout group colours saved.', 'success');
    } catch (err) {
      console.error('saveWorkoutGroupThemePrefs error:', err);
      notify(err?.message || 'Could not save colour preferences.', 'error');
    } finally {
      setWorkoutGroupThemePrefsSaving(false);
    }
  }, [
    getThemeClubOptionsForSurface,
    notify,
    profileData?.id,
    setWorkoutGroupThemePrefsDraft,
    workoutGroupThemePrefsDraft,
    workoutGroupThemePrefsSaving,
  ]);

  const saveWorkoutGroupThemeSurface = useCallback(async (surfaceKey, club, enabled) => {
    if (!profileData?.id || workoutGroupThemePrefsSaving) return;
    const config = WORKOUT_GROUP_THEME_SURFACES.find((item) => item.key === surfaceKey);
    if (!config) return;
    if (enabled && !club?.id) {
      notify('This group is not attached to a club yet.', 'warning');
      return;
    }
    const options = getThemeClubOptionsForSurface(surfaceKey);
    const canUseClub = !enabled || options.some((option) => String(option.id) === String(club.id));
    if (!canUseClub) {
      notify(surfaceKey === 'workout_groups' ? 'This club has not enabled colours for Workout Groups.' : 'This club is not available for this screen.', 'warning');
      return;
    }
    const next = {
      ...DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES,
      ...(workoutGroupThemePreferences || {}),
      ...(workoutGroupThemePrefsDraft || {}),
      [config.enabledKey]: enabled === true,
      [config.clubIdKey]: enabled === true ? club.id : null,
    };
    setWorkoutGroupThemePrefsSaving(true);
    setWorkoutGroupThemePrefsDraft(next);
    try {
      const payload = {
        profile_id: profileData.id,
        ...next,
      };
      const { error } = await supabase.from('workout_group_theme_preferences').upsert(payload, { onConflict: 'profile_id' });
      if (error) throw error;
      const parsed = parseWorkoutGroupThemePreferences(payload);
      setWorkoutGroupThemePreferences(parsed);
      setWorkoutGroupThemePrefsDraft({ ...parsed });
      notify(enabled ? `${config.label} will use ${club.name || 'this club'} colours.` : `${config.label} colours turned off.`, 'success');
    } catch (err) {
      console.error('saveWorkoutGroupThemeSurface error:', err);
      notify(err?.message || 'Could not save colour preference.', 'error');
      setWorkoutGroupThemePrefsDraft(workoutGroupThemePreferences ? { ...workoutGroupThemePreferences } : { ...DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES });
    } finally {
      setWorkoutGroupThemePrefsSaving(false);
    }
  }, [
    getThemeClubOptionsForSurface,
    notify,
    profileData?.id,
    workoutGroupThemePreferences,
    workoutGroupThemePrefsDraft,
    workoutGroupThemePrefsSaving,
  ]);

  const saveWGTarget = async () => {
    if (!canManageWG() || !targetModal?.field) return;
    const value = Number.parseInt(String(targetModal.value || '').replace(/[^\d]/g, ''), 10);
    if (!Number.isFinite(value) || value < 0) { notify(`${targetModal.label} must be 0 or higher.`, 'warning'); return; }
    const field = targetModal.field;
    if (field === 'daily_calorie_target' && (value < 800 || value > 8000)) { notify('Daily calorie target must be between 800 and 8000 kcal.', 'warning'); return; }
    const { data, error } = await supabase.from('workout_groups').update({ [field]: value }).eq('id', activeWG.id).select(`id,${field}`).single();
    if (error) { notify(`Failed to update ${targetModal.label.toLowerCase()}.`, 'error'); return; }
    mergeWGLocal(activeWG.id, { [targetModal.field]: data?.[targetModal.field] ?? value });
    setTargetModal(null);
    notify(`${targetModal.label} updated.`, 'success');
  };

  const sendExpoPushMessages = async (messages) => {
    if (!messages.length) return { attempted: 0, failed: 0, errors: [] };
    const { data, error } = await supabase.functions.invoke('send_expo_push', { body: { messages } });
    if (error) {
      return { attempted: messages.length, failed: messages.length, errors: [error.message || String(error)] };
    }
    return {
      attempted: Number(data?.attempted || 0),
      failed: Number(data?.failed || 0),
      errors: Array.isArray(data?.errors) ? data.errors : [],
    };
  };

  const visibleWGCalendarSessions = useMemo(() => (
    wgCalendarSessions.filter(session => {
      if (activeWGCanManage) return true;
      if (!profileData?.id) return false;
      return session.created_by === profileData.id ||
        (session.athletes || []).some(athlete => athlete.athlete_id === profileData.id) ||
        (session.coaches || []).some(coach => coach.coach_id === profileData.id);
    })
  ), [activeWGCanManage, profileData?.id, wgCalendarSessions]);

  const selectedWGCalendarSession = useMemo(() => (
    visibleWGCalendarSessions.find(session => session.id === wgCalendarSelectedSessionId) || null
  ), [visibleWGCalendarSessions, wgCalendarSelectedSessionId]);

  const wgCalendarSelectedAthleteIds = useMemo(() => {
    const memberIds = wgMembers.map(member => member.id).filter(Boolean);
    if (wgCalendarForm.inviteAll) return memberIds;
    const memberIdSet = new Set(memberIds);
    return uniqValues(wgCalendarForm.selectedAthleteIds).filter(id => memberIdSet.has(id));
  }, [wgCalendarForm.inviteAll, wgCalendarForm.selectedAthleteIds, wgMembers]);

  const wgCalendarSelectedAthleteRows = useMemo(() => {
    const selectedIds = new Set(wgCalendarSelectedAthleteIds);
    return wgMembers.filter(member => selectedIds.has(member.id));
  }, [wgCalendarSelectedAthleteIds, wgMembers]);

  const wgCalendarRealAthleteIds = useMemo(() => (
    uniqValues(wgCalendarSelectedAthleteRows.filter(member => !member.is_demo_profile).map(member => member.id))
  ), [wgCalendarSelectedAthleteRows]);

  const wgCalendarDemoAthleteRows = useMemo(() => (
    wgCalendarSelectedAthleteRows
      .filter(member => member.is_demo_profile)
      .map(member => ({
        id: member.demo_profile_id || member.id,
        profile: member,
      }))
      .filter(row => row.id)
  ), [wgCalendarSelectedAthleteRows]);

  const wgCalendarSelectedCoachIds = useMemo(() => uniqValues(wgCalendarForm.selectedCoachIds), [wgCalendarForm.selectedCoachIds]);
  const wgCalendarStartsAt = useMemo(() => buildWorkoutGroupCalendarDateTime(wgCalendarForm.date, wgCalendarForm.startTime), [wgCalendarForm.date, wgCalendarForm.startTime]);
  const wgCalendarEndsAt = useMemo(() => (
    wgCalendarForm.hasEndTime ? buildWorkoutGroupCalendarDateTime(wgCalendarForm.date, wgCalendarForm.endTime) : null
  ), [wgCalendarForm.date, wgCalendarForm.endTime, wgCalendarForm.hasEndTime]);
  const wgCalendarFormError = useMemo(() => {
    if (!wgCalendarForm.title.trim()) return 'Enter a session title.';
    if (!wgCalendarStartsAt) return 'Choose a valid date and start time.';
    if (wgCalendarForm.hasEndTime && !wgCalendarEndsAt) return 'Choose a valid end time.';
    if (wgCalendarEndsAt && wgCalendarEndsAt <= wgCalendarStartsAt) return 'End time must be after the start time.';
    if (!wgCalendarSelectedAthleteIds.length) return 'Invite at least one athlete.';
    if (!wgCalendarSelectedCoachIds.length) return 'Assign at least one coach.';
    return '';
  }, [wgCalendarEndsAt, wgCalendarForm.hasEndTime, wgCalendarForm.title, wgCalendarSelectedAthleteIds.length, wgCalendarSelectedCoachIds.length, wgCalendarStartsAt]);

  const displayedWGCalendarSessions = useMemo(() => {
    const now = Date.now();
    return visibleWGCalendarSessions
      .filter(session => {
        const startMs = new Date(session.starts_at).getTime();
        if (wgCalendarFilter === 'cancelled') return session.status === 'cancelled';
        if (session.status === 'cancelled') return false;
        if (wgCalendarFilter === 'past') return startMs < now;
        if (startMs < now) return false;
        if (wgCalendarSelectedDateKey && getWorkoutGroupCalendarDateKey(session.starts_at) !== wgCalendarSelectedDateKey) return false;
        return true;
      })
      .sort((a, b) => {
        const aTime = new Date(a.starts_at).getTime();
        const bTime = new Date(b.starts_at).getTime();
        return wgCalendarFilter === 'upcoming' ? aTime - bTime : bTime - aTime;
      });
  }, [visibleWGCalendarSessions, wgCalendarFilter, wgCalendarSelectedDateKey]);

  const wgCalendarSessionCounts = useMemo(() => {
    const now = Date.now();
    return visibleWGCalendarSessions.reduce((acc, session) => {
      const startMs = new Date(session.starts_at).getTime();
      if (session.status === 'cancelled') acc.cancelled += 1;
      else if (startMs < now) acc.past += 1;
      else acc.upcoming += 1;
      return acc;
    }, { upcoming: 0, past: 0, cancelled: 0 });
  }, [visibleWGCalendarSessions]);

  const wgCalendarUpcomingDayChips = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const countsByDate = visibleWGCalendarSessions.reduce((acc, session) => {
      const startMs = new Date(session.starts_at).getTime();
      if (session.status === 'cancelled' || startMs < Date.now()) return acc;
      const key = getWorkoutGroupCalendarDateKey(session.starts_at);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Array.from({ length: 14 }).map((_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() + index);
      const key = getWorkoutGroupCalendarDateKey(date);
      return {
        key,
        day: index === 0 ? 'Today' : date.toLocaleDateString([], { weekday: 'short' }),
        date: date.toLocaleDateString([], { day: 'numeric' }),
        label: date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' }),
        count: countsByDate[key] || 0,
      };
    });
  }, [visibleWGCalendarSessions]);

  const groupedWGCalendarSessions = useMemo(() => (
    displayedWGCalendarSessions.reduce((acc, session) => {
      const key = getWorkoutGroupCalendarDateKey(session.starts_at);
      const existing = acc.find(group => group.key === key);
      if (existing) existing.sessions.push(session);
      else acc.push({ key, title: formatWorkoutGroupCalendarSectionLabel(key), sessions: [session] });
      return acc;
    }, [])
  ), [displayedWGCalendarSessions]);

  const currentWGCalendarInvite = useMemo(() => (
    selectedWGCalendarSession?.athletes?.find(athlete => athlete.athlete_id === profileData?.id) || null
  ), [profileData?.id, selectedWGCalendarSession]);

  const currentWGCalendarCoachAssignment = useMemo(() => (
    selectedWGCalendarSession?.coaches?.find(coach => coach.coach_id === profileData?.id) || null
  ), [profileData?.id, selectedWGCalendarSession]);

  const getWGCalendarRecipients = (session) => uniqValues([
    ...(session?.athletes || []).filter(athlete => !athlete.is_demo_profile).map(athlete => athlete.athlete_id),
    ...(session?.coaches || []).map(coach => coach.coach_id),
  ]);

  const getWGCalendarCoachRecipients = (session) => uniqValues([
    session?.created_by,
    ...(session?.coaches || []).map(coach => coach.coach_id),
  ]);

  const sendWGCalendarNotification = async ({ recipientIds, session, message, notificationType = 'calendar_session_updated', metadata = {}, targetId = null, excludeSender = true }) => {
    const recipients = uniqValues(recipientIds).filter(id => !excludeSender || id !== profileData?.id);
    if (!profileData?.id || !activeWG?.id || !session?.id || !recipients.length) return { notificationCount: 0, pushCount: 0 };
    const safeMessage = String(message || '').trim().slice(0, 240);
    const { error } = await supabase.from('notifications').insert(recipients.map(receiverId => ({
      sender_id: profileData.id,
      receiver_id: receiverId,
      type: 'Workout Group',
      target_id: targetId ?? null,
      message: safeMessage,
      metadata: {
        notification_type: notificationType,
        session_id: session.id,
        group_id: activeWG.id,
        group_name: activeWG.name,
        starts_at: session.starts_at,
        ...metadata,
      },
    })));
    if (error) throw error;

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id,push_token')
      .in('id', recipients);
    const pushMessages = (profiles || [])
      .map(profile => profile.push_token)
      .filter(token => token?.startsWith('ExponentPushToken[') || token?.startsWith('ExpoPushToken['))
      .map(token => ({
        to: token,
        title: 'Workout Group Calendar',
        body: safeMessage,
        sound: 'default',
        data: {
          screen: 'WorkoutGroupCalendar',
          type: notificationType,
          session_id: session.id,
          group_id: activeWG.id,
          from_profile_id: profileData.id,
        },
      }));
    const pushResult = await sendExpoPushMessages(pushMessages);
    return { notificationCount: recipients.length, pushCount: pushResult.attempted, pushFailed: pushResult.failed };
  };

  const syncWGCalendarAssignments = async (sessionId, table, idField, nextIds, extra = () => ({})) => {
    const { data: existingRows, error: existingError } = await supabase.from(table).select(idField).eq('session_id', sessionId);
    if (existingError) throw existingError;
    const existingIds = uniqValues((existingRows || []).map(row => row[idField]));
    const uniqueNextIds = uniqValues(nextIds);
    const nextSet = new Set(uniqueNextIds);
    const existingSet = new Set(existingIds);
    const removedIds = existingIds.filter(id => !nextSet.has(id));
    const addedIds = uniqueNextIds.filter(id => !existingSet.has(id));
    if (removedIds.length) {
      const { error } = await supabase.from(table).delete().eq('session_id', sessionId).in(idField, removedIds);
      if (error) throw error;
    }
    if (addedIds.length) {
      const { error } = await supabase.from(table).insert(addedIds.map(id => ({ session_id: sessionId, [idField]: id, ...extra(id) })));
      if (error) throw error;
    }
  };

  const openWGCalendarCreate = () => {
    if (!activeWGCanManage) return;
    if (!activeWGHasCalendarAccess) {
      openWorkoutGroupUpgradePrompt('Calendar sessions are included with Pro Coach and Club Coaching.');
      return;
    }
    if (!wgMembers.length) {
      notify('Add athletes to this group before scheduling a session.', 'warning');
      return;
    }
    const coachIds = getPreferredWorkoutGroupCalendarCoachIds(activeWG, wgCalendarCoachOptions, profileData?.id || '');
    const nextForm = getWorkoutGroupCalendarDefaultForm('');
    setWgCalendarEditingSession(null);
    setWgCalendarForm({
      ...nextForm,
      date: wgCalendarSelectedDateKey || nextForm.date,
      selectedAthleteIds: wgMembers.map(member => member.id).filter(Boolean),
      selectedCoachIds: coachIds,
    });
    setWgCalendarFormOpen(true);
    loadWGCalendarCoachOptions();
  };

  const openWGCalendarEdit = (session) => {
    if (!activeWGCanManage || !session?.id || session.status === 'cancelled') return;
    if (!activeWGHasCalendarAccess) {
      openWorkoutGroupUpgradePrompt('Calendar sessions are included with Pro Coach and Club Coaching.');
      return;
    }
    setWgCalendarEditingSession(session);
    setWgCalendarForm(getWorkoutGroupCalendarEditForm(session));
    setWgCalendarFormOpen(true);
    loadWGCalendarCoachOptions();
  };

  const saveWGCalendarSession = async () => {
    if (!activeWG?.id || !profileData?.id || wgCalendarSaving) return;
    if (!activeWGHasCalendarAccess) {
      openWorkoutGroupUpgradePrompt('Calendar sessions are included with Pro Coach and Club Coaching.');
      return;
    }
    if (wgCalendarFormError || !wgCalendarStartsAt) {
      notify(wgCalendarFormError || 'Complete the session details before saving.', 'warning');
      return;
    }
    const startsAtISO = wgCalendarStartsAt.toISOString();
    const endsAtISO = wgCalendarEndsAt ? wgCalendarEndsAt.toISOString() : null;
    const title = wgCalendarForm.title.trim();
    const location = wgCalendarForm.location.trim() || null;
    const notes = wgCalendarForm.notes.trim() || null;
    const athleteIds = wgCalendarSelectedAthleteIds;
    const realAthleteIds = wgCalendarRealAthleteIds;
    const demoAthleteRows = wgCalendarDemoAthleteRows;
    const demoAthleteIds = demoAthleteRows.map(row => row.id);
    const coachIds = wgCalendarSelectedCoachIds;
    const previousSession = wgCalendarEditingSession;
    setWgCalendarSaving(true);
    try {
      let sessionId = previousSession?.id || null;
      const nextMetadata = {
        ...(previousSession?.metadata || {}),
        demo_athlete_ids: demoAthleteIds,
        demo_invited_athlete_count: demoAthleteIds.length,
        invited_athlete_count: athleteIds.length,
      };
      if (sessionId) {
        const { error } = await supabase
          .from('workout_group_calendar_sessions')
          .update({ title, starts_at: startsAtISO, ends_at: endsAtISO, location, notes, metadata: nextMetadata, updated_at: new Date().toISOString() })
          .eq('id', sessionId);
        if (error) throw error;
        await syncWGCalendarAssignments(sessionId, 'workout_group_calendar_session_athletes', 'athlete_id', realAthleteIds, () => ({ status: 'pending' }));
        await syncWGCalendarAssignments(sessionId, 'workout_group_calendar_session_coaches', 'coach_id', coachIds, coachId => ({ role: coachId === profileData.id ? 'lead' : 'coach' }));
      } else if (demoAthleteIds.length) {
        const { data: insertedSession, error } = await supabase
          .from('workout_group_calendar_sessions')
          .insert({
            group_id: activeWG.id,
            club_id: activeWG.club_id || null,
            created_by: profileData.id,
            title,
            starts_at: startsAtISO,
            ends_at: endsAtISO,
            location,
            notes,
            status: 'scheduled',
            metadata: {
              ...nextMetadata,
              created_from: 'workout_groups_calendar_demo_metadata',
              assigned_coach_count: coachIds.length,
            },
          })
          .select('id')
          .single();
        if (error) throw error;
        sessionId = insertedSession.id;
        await syncWGCalendarAssignments(sessionId, 'workout_group_calendar_session_athletes', 'athlete_id', realAthleteIds, () => ({ status: 'pending' }));
        await syncWGCalendarAssignments(sessionId, 'workout_group_calendar_session_coaches', 'coach_id', coachIds, coachId => ({ role: coachId === profileData.id ? 'lead' : 'coach' }));
      } else {
        const { data, error } = await supabase.rpc('create_workout_group_calendar_session', {
          p_group_id: activeWG.id,
          p_title: title,
          p_starts_at: startsAtISO,
          p_ends_at: endsAtISO,
          p_location: location,
          p_notes: notes,
          p_athlete_ids: realAthleteIds,
          p_coach_ids: coachIds,
        });
        if (error) throw error;
        sessionId = data;
      }

      const nextSession = {
        ...(previousSession || {}),
        id: sessionId,
        group_id: activeWG.id,
        created_by: previousSession?.created_by || profileData.id,
        title,
        starts_at: startsAtISO,
        ends_at: endsAtISO,
        location,
        notes,
        metadata: nextMetadata,
        status: previousSession?.status || 'scheduled',
        athletes: [
          ...realAthleteIds.map(athlete_id => ({ athlete_id })),
          ...demoAthleteRows.map(row => ({
            athlete_id: row.id,
            demo_profile_id: row.id,
            is_demo_profile: true,
            status: 'pending',
            profile: row.profile,
          })),
        ],
        coaches: coachIds.map(coach_id => ({ coach_id })),
      };
      const notifyRecipients = previousSession
        ? uniqValues([...getWGCalendarRecipients(previousSession), ...realAthleteIds, ...coachIds])
        : uniqValues([...realAthleteIds, ...coachIds]);
      try {
        await sendWGCalendarNotification({
          recipientIds: notifyRecipients,
          session: nextSession,
          message: `${previousSession ? 'Session updated' : 'New session'}: ${title} for ${activeWG.name || 'your workout group'} ${formatWorkoutGroupCalendarRange(startsAtISO, endsAtISO)}.`,
          notificationType: previousSession ? 'calendar_session_updated' : 'calendar_session_created',
          metadata: { title },
        });
      } catch (notifyError) {
        console.error('workout group calendar notification error:', notifyError);
        notify(previousSession ? 'Session updated, but notifications could not be sent.' : 'Session created, but notifications could not be sent.', 'warning');
      }
      await loadWGCalendarSessions(true);
      setWgCalendarSelectedSessionId(sessionId);
      setWgCalendarSelectedDateKey(getWorkoutGroupCalendarDateKey(startsAtISO));
      setWgCalendarFilter(new Date(startsAtISO).getTime() < Date.now() ? 'past' : 'upcoming');
      setWgCalendarFormOpen(false);
      setWgCalendarEditingSession(null);
      notify(previousSession ? 'Session updated.' : 'Session created.', 'success');
    } catch (err) {
      console.error('saveWGCalendarSession error:', err);
      notify(err?.message || 'Could not save session.', 'error');
    } finally {
      setWgCalendarSaving(false);
    }
  };

  const setWGCalendarSessionStatus = (session, status) => {
    if (!session?.id || !activeWGCanManage) return;
    const restoring = status === 'scheduled';
    if (restoring && !activeWGHasCalendarAccess) {
      openWorkoutGroupUpgradePrompt('Calendar sessions are included with Pro Coach and Club Coaching.');
      return;
    }
    askConfirm({
      title: restoring ? 'Make session live again?' : 'Cancel session?',
      message: restoring ? 'This restores the session and notifies invited athletes and assigned coaches.' : 'This keeps the session in history and notifies invited athletes and assigned coaches.',
      confirmLabel: restoring ? 'Make Live' : 'Cancel Session',
      tone: restoring ? 'default' : 'danger',
      onConfirm: async () => {
        const { error } = await supabase
          .from('workout_group_calendar_sessions')
          .update({ status, updated_at: new Date().toISOString() })
          .eq('id', session.id);
        if (error) {
          notify(restoring ? 'Could not restore session.' : 'Could not cancel session.', 'error');
          return;
        }
        const nextSession = { ...session, status };
        setWgCalendarSessions(prev => prev.map(row => row.id === session.id ? nextSession : row));
        await sendWGCalendarNotification({
          recipientIds: getWGCalendarRecipients(session),
          session: nextSession,
          message: restoring
            ? `Session live again: ${session.title} for ${activeWG.name || 'your workout group'}.`
            : `Session cancelled: ${session.title} for ${activeWG.name || 'your workout group'}.`,
          notificationType: restoring ? 'calendar_session_updated' : 'calendar_session_cancelled',
          metadata: { title: session.title, status },
        }).catch(err => console.error('calendar status notification error:', err));
        notify(restoring ? 'Session is live again.' : 'Session cancelled.', 'success');
      },
    });
  };

  const respondWGCalendarAttendance = async (invite, status) => {
    if (!invite?.id || !selectedWGCalendarSession?.id || wgCalendarResponseSaving) return;
    setWgCalendarResponseSaving(status);
    try {
      const { error } = await supabase
        .from('workout_group_calendar_session_athletes')
        .update({ status })
        .eq('id', invite.id);
      if (error) throw error;
      const nextSession = {
        ...selectedWGCalendarSession,
        athletes: selectedWGCalendarSession.athletes.map(row => row.id === invite.id ? { ...row, status, responded_at: new Date().toISOString() } : row),
      };
      setWgCalendarSessions(prev => prev.map(row => row.id === nextSession.id ? nextSession : row));
      await sendWGCalendarNotification({
        recipientIds: getWGCalendarCoachRecipients(selectedWGCalendarSession),
        session: selectedWGCalendarSession,
        message: `${getWorkoutGroupCalendarProfileName(profileData)} is ${status === 'accepted' ? 'available' : 'unavailable'} for ${selectedWGCalendarSession.title}.`,
        notificationType: 'calendar_session_response',
        metadata: { athlete_id: profileData.id, response_status: status },
        targetId: null,
      }).catch(err => console.error('calendar response notification error:', err));
      notify(status === 'accepted' ? 'Marked available.' : 'Marked unavailable.', 'success');
    } catch (err) {
      console.error('respondWGCalendarAttendance error:', err);
      notify(err?.message || 'Could not update attendance.', 'error');
    } finally {
      setWgCalendarResponseSaving(null);
    }
  };

  const removeWGCalendarCoachAssignment = () => {
    if (!currentWGCalendarCoachAssignment?.id || !selectedWGCalendarSession?.id || wgCalendarResponseSaving) return;
    askConfirm({
      title: 'Mark unavailable?',
      message: 'This removes you from the assigned coaches for this session.',
      confirmLabel: 'Mark Unavailable',
      tone: 'danger',
      onConfirm: async () => {
        setWgCalendarResponseSaving('declined');
        try {
          const { error } = await supabase
            .from('workout_group_calendar_session_coaches')
            .delete()
            .eq('id', currentWGCalendarCoachAssignment.id);
          if (error) throw error;
          const nextSession = {
            ...selectedWGCalendarSession,
            coaches: selectedWGCalendarSession.coaches.filter(coach => coach.id !== currentWGCalendarCoachAssignment.id),
          };
          setWgCalendarSessions(prev => prev.map(row => row.id === nextSession.id ? nextSession : row));
          await sendWGCalendarNotification({
            recipientIds: getWGCalendarCoachRecipients(nextSession),
            session: nextSession,
            message: `${getWorkoutGroupCalendarProfileName(profileData)} is unavailable and was removed from ${nextSession.title}.`,
            notificationType: 'calendar_session_response',
            metadata: { coach_id: profileData.id, response_status: 'declined', membership_change: 'coach_removed_self' },
            targetId: null,
          }).catch(err => console.error('calendar coach unavailable notification error:', err));
          notify('Coach assignment removed.', 'success');
        } catch (err) {
          console.error('removeWGCalendarCoachAssignment error:', err);
          notify(err?.message || 'Could not update coach assignment.', 'error');
        } finally {
          setWgCalendarResponseSaving(null);
        }
      },
    });
  };

  const sendWGCalendarAudienceNotification = async (session, audience, recipientIds) => {
    if (!session?.id || wgCalendarNotifySaving) return;
    setWgCalendarNotifySaving(true);
    try {
      await sendWGCalendarNotification({
        recipientIds,
        session,
        message: `Reminder: ${session.title} for ${activeWG?.name || 'your workout group'} ${formatWorkoutGroupCalendarRange(session.starts_at, session.ends_at)}.`,
        notificationType: 'calendar_session_updated',
        metadata: { title: session.title, resent_from: 'profile_workout_calendar' },
        targetId: null,
        excludeSender: false,
      });
      notify(audience === 'athletes' ? 'Session reminder sent to athletes.' : audience === 'coaches' ? 'Session reminder sent to coaches.' : 'Session reminder sent to everyone.', 'success');
    } catch (err) {
      console.error('notifyWGCalendarAudience error:', err);
      notify(err?.message || 'Could not send session alert.', 'error');
    } finally {
      setWgCalendarNotifySaving(false);
    }
  };

  const notifyWGCalendarAudience = (session, audience) => {
    if (!session?.id || wgCalendarNotifySaving) return;
    const athleteIds = (session.athletes || []).map(athlete => athlete.athlete_id);
    const coachIds = getWGCalendarCoachRecipients(session);
    const recipientIds = audience === 'athletes' ? athleteIds : audience === 'coaches' ? coachIds : [...athleteIds, ...coachIds];
    const uniqueRecipients = uniqValues(recipientIds);
    if (!uniqueRecipients.length) {
      notify('No recipients for this session.', 'warning');
      return;
    }
    const audienceLabel = audience === 'athletes' ? 'athletes' : audience === 'coaches' ? 'coaches' : 'everyone';
    askConfirm({
      title: `Notify ${audienceLabel === 'everyone' ? 'Everyone' : audienceLabel.charAt(0).toUpperCase() + audienceLabel.slice(1)}?`,
      message: `Send a session reminder for "${session.title}" to ${uniqueRecipients.length} ${uniqueRecipients.length === 1 ? 'person' : 'people'}?`,
      confirmLabel: audience === 'all' ? 'Notify Everyone' : audience === 'athletes' ? 'Notify Athletes' : 'Notify Coaches',
      tone: 'default',
      onConfirm: async () => sendWGCalendarAudienceNotification(session, audience, uniqueRecipients),
    });
  };

  const sendWGNotification = async ({ athleteId = null, message, type = 'GROUP_REMINDER' }) => {
    if (!canManageWG() || !activeWG?.id || !message?.trim()) return;
    const receivers = athleteId ? [athleteId] : wgMembers.map(member => member.id).filter(id => id && id !== profileData.id);
    if (!receivers.length) {
      notify('No members to notify.', 'warning');
      return;
    }
    const teamName = activeWG?.workout_clubs?.name || 'Your team';
    const groupName = activeWG?.name || 'Workout group';
    const notificationMessage = `${teamName} - ${groupName}: ${message.trim()}`.slice(0, 255);
    const receiverProfiles = wgMembers.filter(member => receivers.includes(member.id));
    const rows = receivers
      .filter(Boolean)
      .map(id => ({
        sender_id: profileData.id,
        receiver_id: id,
        type,
        message: notificationMessage,
        target_id: null,
        metadata: {
          group_id: activeWG.id,
          group_name: activeWG.name,
          team_name: teamName,
          daily_calorie_target: Number(activeWG.daily_calorie_target || 2500),
        },
      }));
    if (!rows.length) {
      notify(athleteId ? 'No member to notify.' : 'No other members to notify.', 'warning');
      return;
    }
    const validPushProfiles = receiverProfiles.filter(member => member.push_token?.startsWith('ExponentPushToken['));
    const pushMessages = validPushProfiles.map(member => ({
      to: member.push_token,
      title: 'Snows ProAm',
      body: notificationMessage,
      sound: 'default',
      data: {
        screen: 'WorkoutGroups',
        type,
        group_id: activeWG.id,
        from_profile_id: profileData.id,
      },
    }));
    const baseDebug = {
      group: activeWG.name,
      type,
      receivers: receiverProfiles.length,
      notificationRows: rows.length,
      pushTokens: validPushProfiles.length,
      missingPushTokens: Math.max(0, receiverProfiles.length - validPushProfiles.length),
      notificationInserted: 0,
      pushAttempted: 0,
      pushFailed: 0,
      errors: [],
    };
    console.groupCollapsed('[Workout Group Reminder Debug]');
    console.log({ activeWG, type, receivers: receiverProfiles, rows, pushMessages: pushMessages.map(item => ({ ...item, to: item.to?.slice(0, 28) + '...' })) });
    const { data: inserted, error } = await supabase.from('notifications').insert(rows).select('id,receiver_id,type,created_at');
    if (error) {
      console.error('sendWGNotification error:', error);
      const debug = { ...baseDebug, errors: [`Supabase insert: ${error.message || JSON.stringify(error)}`] };
      console.log(debug);
      console.groupEnd();
      notify(`Reminder failed: ${error.message || 'notification insert error'}`, 'error');
      return;
    }
    const pushResult = await sendExpoPushMessages(pushMessages);
    const debug = {
      ...baseDebug,
      notificationInserted: inserted?.length || rows.length,
      pushAttempted: pushResult.attempted,
      pushFailed: pushResult.failed,
      errors: pushResult.errors,
    };
    console.log(debug);
    console.groupEnd();
    if (!pushResult.attempted) {
      notify(`Reminder saved for ${rows.length} member${rows.length === 1 ? '' : 's'}, but no push tokens were found.`, 'warning');
      return;
    }
    if (pushResult.failed) {
      notify(`Reminder saved, but ${pushResult.failed} push alert${pushResult.failed === 1 ? '' : 's'} failed. Open debug for details.`, 'warning');
      return;
    }
    notify(`Reminder sent: ${rows.length} notification${rows.length === 1 ? '' : 's'}, ${pushResult.attempted} push alert${pushResult.attempted === 1 ? '' : 's'}.`, 'success');
  };

  const confirmWGNotification = (options) => {
    const count = options.athleteId ? 1 : wgMembers.filter(member => member.id !== profileData.id).length;
    if (!count) {
      notify(options.athleteId ? 'No member to notify.' : 'No other members to notify.', 'warning');
      return;
    }
    const isCalorie = options.type === 'GROUP_CALORIE_REMINDER';
    const label = isCalorie ? 'calorie reminder' : 'training reminder';
    askConfirm({
      title: `Send ${label}?`,
      message: `Send this ${label} to ${count} member${count === 1 ? '' : 's'} in ${activeWG?.name || 'this group'}?`,
      confirmLabel: 'Send Reminder',
      tone: 'default',
      onConfirm: async () => sendWGNotification(options),
    });
  };

  const normalizeWorkoutName = (name = '') => String(name || '').trim().toLowerCase();
  const generateWorkoutShareCode = (len = 6) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const getAllowedWorkoutPairsList = (group = activeWG) => {
    const seen = new Set();
    const pairs = [];
    const pushPair = (name, code = null, workoutId = null, scheduledDays = []) => {
      const trimmed = String(name || '').trim();
      const key = normalizeWorkoutName(trimmed);
      if (!key || seen.has(key)) return;
      seen.add(key);
      pairs.push({ name: trimmed, code: code || null, workout_id: workoutId || null, scheduled_days: normalizeAssignedWorkoutDays(scheduledDays) });
    };
    (Array.isArray(group?.allowed_workout_pairs) ? group.allowed_workout_pairs : []).forEach(pair => {
      pushPair(pair?.name, pair?.code, pair?.workout_id || pair?.id, pair?.scheduled_days || pair?.days);
    });
    (Array.isArray(group?.allowed_workout_names) ? group.allowed_workout_names : []).forEach(name => {
      pushPair(name);
    });
    return pairs;
  };
  const getAllowedWorkoutNamesList = (group = activeWG) => getAllowedWorkoutPairsList(group).map(pair => pair.name);

  const loadCoachWorkouts = async () => {
    if (!profileData?.id) return;
    setCoachWorkoutsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_workouts')
        .select('id,name,share_code,created_at')
        .eq('profile_id', profileData.id)
        .eq('deleted', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setCoachWorkouts(data || []);
    } catch (err) {
      console.error('loadCoachWorkouts error:', err);
      setCoachWorkouts([]);
      notify('Could not load your workouts.', 'error');
    } finally {
      setCoachWorkoutsLoading(false);
    }
  };

  const updateWGAllowedWorkouts = async () => {
    if (!canManageWG()) return;
    setAllowedWorkoutSearch('');
    setAllowedWorkoutsModal(true);
    loadCoachWorkouts();
  };

  const saveAllowedWorkoutsForGroup = async (pairs, names, message = 'Allowed workouts updated.') => {
    if (!canManageWG() || !activeWG?.id) return;
    const limit = workoutAccess?.limits?.assigned_workouts_per_group;
    const assignedCount = getAllowedWorkoutNamesList({ allowed_workout_pairs: pairs, allowed_workout_names: names }).length;
    if (limit !== null && typeof limit === 'number' && assignedCount > limit) {
      notify(`Your current plan lets you assign up to ${limit} workout${limit === 1 ? '' : 's'} per group. Upgrade to attach more.`, 'warning');
      navigate('/coaching#pricing');
      return;
    }
    setAllowedWorkoutsSaving(true);
    try {
      const { data, error } = await supabase
        .from('workout_groups')
        .update({ allowed_workout_pairs: pairs, allowed_workout_names: names })
        .eq('id', activeWG.id)
        .select('id,allowed_workout_pairs,allowed_workout_names')
        .single();
      if (error) throw error;
      const patch = {
        allowed_workout_pairs: data?.allowed_workout_pairs || pairs,
        allowed_workout_names: data?.allowed_workout_names || names,
      };
      mergeWGLocal(activeWG.id, patch);
      await loadGroupData(activeWG.id);
      notify(message, 'success');
    } catch (err) {
      console.error('saveAllowedWorkoutsForGroup error:', err);
      notify('Failed to update allowed workouts.', 'error');
    } finally {
      setAllowedWorkoutsSaving(false);
    }
  };

  const ensureWorkoutShareCode = async (workout) => {
    if (workout?.share_code) return workout.share_code;
    if (!canUseWorkoutShareCodesEntitlement) return null;
    const code = generateWorkoutShareCode();
    const { error } = await supabase
      .from('user_workouts')
      .update({ share_code: code, code_expiration: null, deleted: false })
      .eq('id', workout.id);
    if (error) throw error;
    setCoachWorkouts(prev => prev.map(item => item.id === workout.id ? { ...item, share_code: code } : item));
    return code;
  };

  const addAllowedWorkoutFromPlan = async (workout) => {
    if (!workout?.id || !workout?.name || !activeWG?.id || allowedWorkoutsSaving) return;
    const currentNames = getAllowedWorkoutNamesList();
    const currentPairs = getAllowedWorkoutPairsList();
    const nameKey = normalizeWorkoutName(workout.name);
    if (currentNames.some(name => normalizeWorkoutName(name) === nameKey)) {
      notify('Workout already assigned to this group.', 'warning');
      return;
    }
    setAllowedWorkoutsSaving(true);
    try {
      const code = await ensureWorkoutShareCode(workout);
      const nextPairs = [...currentPairs, { name: workout.name, code: code || null, workout_id: workout.id, scheduled_days: [] }];
      const nextNames = [...currentNames, workout.name];
      await saveAllowedWorkoutsForGroup(
        nextPairs,
        nextNames,
        `${workout.name} added.`
      );
    } catch (err) {
      console.error('addAllowedWorkoutFromPlan error:', err);
      notify('Could not add workout.', 'error');
      setAllowedWorkoutsSaving(false);
    }
  };

  const removeAllowedWorkout = async (workoutName) => {
    if (!activeWG?.id || !workoutName || allowedWorkoutsSaving) return;
    const key = normalizeWorkoutName(workoutName);
    const names = getAllowedWorkoutNamesList().filter(name => normalizeWorkoutName(name) !== key);
    const pairs = getAllowedWorkoutPairsList().filter(pair => normalizeWorkoutName(pair?.name) !== key);
    await saveAllowedWorkoutsForGroup(pairs, names, `${workoutName} removed.`);
  };

  const updateAllowedWorkoutScheduleForGroup = async (workoutName, dayIndex) => {
    if (!activeWG?.id || !workoutName || allowedWorkoutsSaving) return;
    const key = normalizeWorkoutName(workoutName);
    const pairs = getAllowedWorkoutPairsList().map(pair => {
      if (normalizeWorkoutName(pair?.name) !== key) return pair;
      const days = normalizeAssignedWorkoutDays(pair?.scheduled_days || pair?.days);
      const nextDays = days.includes(dayIndex)
        ? days.filter(day => day !== dayIndex)
        : [...days, dayIndex].sort((a, b) => a - b);
      return { ...pair, scheduled_days: nextDays };
    });
    await saveAllowedWorkoutsForGroup(pairs, getAllowedWorkoutNamesList(), 'Workout schedule updated.');
  };

  const confirmRemoveAllowedWorkout = (workoutName) => {
    askConfirm({
      title: 'Remove workout?',
      message: `Remove ${workoutName} from this group?`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: async () => removeAllowedWorkout(workoutName),
    });
  };

  const openEditWG = (group = activeWG) => {
    if (!canManageWG(group)) return;
    setEditingWG(group);
    setEditingWGName(group?.name || '');
  };

  const saveWGName = async () => {
    const name = editingWGName.trim();
    if (!editingWG?.id || !name) return;
    const { data, error } = await supabase.from('workout_groups').update({ name }).eq('id', editingWG.id).select('id,name').single();
    if (error) { notify('Could not update group name.', 'error'); return; }
    mergeWGLocal(editingWG.id, { name: data?.name || name });
    setEditingWG(null);
    setEditingWGName('');
    notify('Workout group updated.', 'success');
  };

  const deleteWG = async (group = activeWG) => {
    if (!canManageWG(group)) return;
    askConfirm({
      title: 'Delete workout group',
      message: `Delete "${group.name}"? This removes the group and its memberships.`,
      confirmLabel: 'Delete Group',
      tone: 'danger',
      onConfirm: async () => {
    const { error: memberError } = await supabase.from('workout_group_members').delete().eq('group_id', group.id);
    if (memberError) { notify('Could not delete group members.', 'error'); return; }
    const { error } = await supabase.from('workout_groups').delete().eq('id', group.id);
    if (error) { notify('Could not delete workout group.', 'error'); return; }
    setWorkoutGroups(prev => prev.filter(g => g.id !== group.id));
    if (activeWGId === group.id) {
      setActiveWGId(null);
      setActiveWG(null);
      setWgMembers([]);
      setGroupStats(null);
      setPerfMap({});
      setWgAnalyticsSessions([]);
    }
    notify('Workout group deleted.', 'success');
      },
    });
  };

  const removeWGMember = async (member) => {
    if (!activeWG?.id || !member?.id) return;
    const isSelf = member.id === profileData.id;
    if (!canManageWG() && !isSelf) return;
    askConfirm({
      title: isSelf ? 'Leave workout group' : 'Remove athlete',
      message: isSelf ? 'Leave this workout group?' : `Remove ${member.username ? `@${member.username}` : 'this member'} from the group?`,
      confirmLabel: isSelf ? 'Leave Group' : 'Remove Athlete',
      tone: 'danger',
      onConfirm: async () => {
    const { error } = await supabase.from('workout_group_members').delete().eq('group_id', activeWG.id).eq('athlete_id', member.id);
    if (error) { notify('Could not update group membership.', 'error'); return; }
    if (isSelf) {
      setActiveWGId(null);
      setActiveWG(null);
      await loadWG();
      notify('You left the workout group.', 'success');
      return;
    }
    await loadGroupData(activeWG.id);
    notify('Athlete removed.', 'success');
      },
    });
  };

  const getWGAttentionStatus = (xp, xpTarget, sessions, sessionTarget) => {
    return getWorkoutGroupSessionProgressStatus(sessions, sessionTarget);
  };

  const formatWGDuration = (seconds) => {
    const s = Math.max(0, Math.floor(Number(seconds || 0)));
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
    return m > 0 ? `${m}m ${r}s` : `${r}s`;
  };

  const getWGRangeDays = (mode = wgRangeMode) => {
    if (mode === 'date') return 1;
    if (mode === 'custom') {
      const days = Number(wgCustomDays);
      return Number.isFinite(days) && days > 0 ? Math.min(Math.round(days), 1095) : 90;
    }
    if (String(mode).startsWith('season:')) {
      const seasonId = String(mode).replace('season:', '');
      const season = wgClubSeasons.find(s => s.id === seasonId) || activeWG?.workout_club_seasons || null;
      if (season?.starts_on && season?.ends_on) {
        const start = new Date(`${season.starts_on}T00:00:00`).getTime();
        const end = new Date(`${season.ends_on}T00:00:00`).getTime();
        return Math.max(1, Math.ceil((end - start) / 86400000) + 1);
      }
      return 365;
    }
    return WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS.find(option => option.value === mode)?.days ??
      WORKOUT_GROUP_PROFILE_RANGE_OPTIONS.find(option => option.value === mode)?.days ??
      7;
  };

  const getWGSelectedDateWindow = () => {
    const dateValue = wgSelectedDate || localDateInputValue(new Date());
    const start = new Date(`${dateValue}T00:00:00`);
    const end = new Date(`${dateValue}T23:59:59.999`);
    return {
      date: dateValue,
      start: Number.isFinite(start.getTime()) ? start : new Date(),
      end: Number.isFinite(end.getTime()) ? end : new Date(),
    };
  };

  const getWGCalendarStart = (rangeDays = timeRange) => {
    const now = new Date();
    if (rangeDays === 0) {
      return new Date('2000-01-01T00:00:00');
    }
    const start = new Date(now);
    start.setDate(start.getDate() - Math.max(1, Number(rangeDays) || 7) + 1);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getWGAllowedNames = (group = activeWG) => {
    const names = Array.isArray(group?.allowed_workout_names) ? group.allowed_workout_names : [];
    const pairNames = Array.isArray(group?.allowed_workout_pairs) ? group.allowed_workout_pairs.map(pair => pair?.name) : [];
    return Array.from(new Set([...names, ...pairNames].map(x => String(x || '').trim().toLowerCase()).filter(Boolean)));
  };

  const sessionMatchesWGAllowedWorkout = (session, group = activeWG) => {
    const allowedNames = getWGAllowedNames(group);
    const allowedPairs = Array.isArray(group?.allowed_workout_pairs) ? group.allowed_workout_pairs : [];
    if (!allowedNames.length && !allowedPairs.length) return false;
    const customWorkout = Array.isArray(session?.user_workouts) ? session.user_workouts[0] : session?.user_workouts;
    const presetWorkout = Array.isArray(session?.workouts) ? session.workouts[0] : session?.workouts;
    const sessionNames = [
      session?.workout_name,
      customWorkout?.name,
      presetWorkout?.name,
    ].map(value => String(value || '').trim().toLowerCase()).filter(Boolean);
    if (sessionNames.some(name => allowedNames.includes(name))) return true;
    const sessionIds = new Set([
      session?.workout_plan_id,
      customWorkout?.id,
      customWorkout?.source_workout_plan_id,
      session?.preset_plan_id,
      presetWorkout?.id,
    ].map(value => String(value || '').trim()).filter(Boolean));
    const sessionCodes = new Set([
      customWorkout?.share_code,
      customWorkout?.code,
      session?.workout_code,
    ].map(value => String(value || '').trim()).filter(Boolean));
    return allowedPairs.some(pair => {
      const pairWorkoutId = String(pair?.workout_id || pair?.id || '').trim();
      const pairCode = String(pair?.code || '').trim();
      return (!!pairWorkoutId && sessionIds.has(pairWorkoutId)) || (!!pairCode && sessionCodes.has(pairCode));
    });
  };

  const getWGWindow = (group = activeWG) => {
    if (wgRangeMode === 'date') {
      const dateWindow = getWGSelectedDateWindow();
      return { shouldCount: true, startISO: dateWindow.start.toISOString(), endISO: dateWindow.end.toISOString() };
    }
    const startAt = getWGCalendarStart(timeRange);
    const selectedSeasonId = String(wgRangeMode || '').startsWith('season:') ? String(wgRangeMode).replace('season:', '') : null;
    const season = selectedSeasonId
      ? wgClubSeasons.find(s => s.id === selectedSeasonId) || group?.workout_club_seasons || null
      : null;
    let start = startAt;
    if (season?.starts_on) {
      const seasonStart = new Date(`${season.starts_on}T00:00:00`);
      if (selectedSeasonId || seasonStart > start) start = seasonStart;
    }
    const todayEndAt = getWorkoutGroupEndOfToday();
    const seasonEndAt = season?.ends_on ? new Date(`${season.ends_on}T23:59:59.999`) : null;
    const endAt = minWorkoutGroupDate(seasonEndAt, todayEndAt) || todayEndAt;
    const endISO = endAt.toISOString();
    return { shouldCount: true, startISO: start.toISOString(), endISO };
  };

  const getWGNutritionWindow = (season = null) => {
    if (wgRangeMode === 'date') {
      const dateWindow = getWGSelectedDateWindow();
      return {
        start: dateWindow.start,
        end: dateWindow.end,
        days: 1,
        startDate: dateWindow.date,
        endDate: dateWindow.date,
      };
    }
    if (season) {
      return {
        start: season.starts_on ? new Date(`${season.starts_on}T00:00:00`) : null,
        end: season.ends_on ? new Date(`${season.ends_on}T23:59:59.999`) : null,
        days: null,
        startDate: season.starts_on || null,
        endDate: season.ends_on || null,
      };
    }
    if (timeRange === 0) return { start: null, end: null, days: null };
    const days = Number.isFinite(Number(timeRange)) && Number(timeRange) > 0 ? Math.min(Math.round(Number(timeRange)), 1095) : 7;
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    start.setHours(0, 0, 0, 0);
    return { start, end: null, days, startDate: localDateInputValue(start), endDate: null };
  };

  const getWGNutritionWindowDays = (rows = [], window, season = null) => {
    const today = new Date();
    if (season) {
      const times = rows.map(row => dateOnlyTime(row.log_date)).filter(Boolean);
      const start = season.starts_on ? new Date(`${season.starts_on}T00:00:00`) : new Date(times.length ? Math.min(...times) : today.getTime());
      const end = season.ends_on ? new Date(`${season.ends_on}T00:00:00`) : today;
      return inclusiveDays(start, end);
    }
    if (window?.days) return window.days;
    const times = rows.map(row => dateOnlyTime(row.log_date)).filter(Boolean);
    if (!times.length) return 1;
    return inclusiveDays(new Date(Math.min(...times)), today);
  };

  const filterWGAllowedSessions = (sessions, group = activeWG) => {
    return (sessions || []).filter(session => sessionMatchesWGAllowedWorkout(session, group));
  };

  const openWGAthlete = async (athlete) => {
    if (!athlete?.id || !activeWG?.id) return;
    setSelectedWGAthlete(athlete);
    setAthleteSessions([]);
    setAthleteNote(null);
    setAthleteNoteText('');
    setExpandedAthleteSessionId(null);
    setAthleteLoading(true);
    try {
      const windowInfo = getWGWindow(activeWG);
      if (!windowInfo.shouldCount) {
        setAthleteSessions([]);
        return;
      }
      let sessionsQuery = supabase
        .from('workout_sessions')
        .select('id,started_at,local_date,duration_seconds,total_xp,total_sets,total_reps,total_volume,total_rest_seconds,session_effort,workout_plan_id,preset_plan_id,user_workouts(id,name,share_code,source_workout_plan_id),workouts(id,name),workout_context(device,environment,time_of_day,rest_timer_usage),user_workout_feedback(rating,difficulty,focus_rating,mood,notes),workout_exercise_summary(total_reps,avg_weight,total_xp,exercises(id,name,image_url,gif_url))')
        .eq('profile_id', athlete.id)
        .gte('started_at', windowInfo.startISO)
        .order('started_at', { ascending: false });
      if (windowInfo.endISO) sessionsQuery = sessionsQuery.lte('started_at', windowInfo.endISO);
      const [{ data: sessions, error: sessionsError }, { data: notes, error: notesError }] = await Promise.all([
        sessionsQuery,
        supabase.from('coach_notes').select('id,note,created_at,coach_id,group_id,athlete_id').eq('group_id', activeWG.id).eq('athlete_id', athlete.id).order('created_at', { ascending: false }).limit(1),
      ]);
      if (sessionsError) throw sessionsError;
      if (notesError) console.error('coach note load error:', notesError);
      const latestNote = notes?.[0] || null;
      setAthleteSessions(filterWGAllowedSessions(sessions || [], activeWG));
      setAthleteNote(latestNote);
      setAthleteNoteText(latestNote?.note || '');
    } catch (err) {
      console.error('openWGAthlete error:', err);
      notify('Could not load athlete analysis.', 'error');
    } finally {
      setAthleteLoading(false);
    }
  };

  useEffect(() => {
    if (!profileData?.id) return undefined;
    let refreshTimer = null;
    const refreshWorkoutGroups = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(async () => {
        await loadWG();
        if (activeWGId) await loadGroupData(activeWGId);
        if (selectedWGAthlete?.id && activeWG?.id) await openWGAthlete(selectedWGAthlete);
      }, 300);
    };

    const channel = supabase
      .channel(`workout-groups-profile-web-${profileData.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_groups' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_members' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_clubs' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_club_seasons' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_sessions' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_calendar_sessions' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_calendar_session_athletes' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_group_calendar_session_coaches' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_context' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workout_exercise_summary' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_workout_feedback' }, refreshWorkoutGroups)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'food_log' }, refreshWorkoutGroups)
      .subscribe();

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      supabase.removeChannel(channel);
    };
    // Realtime refresh intentionally calls the latest in-scope loaders after a debounced event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWG?.id, activeWGId, loadWG, profileData?.id, selectedWGAthlete?.id, timeRange, wgCustomDays, wgRangeMode]);

  const saveWGAthleteNote = async () => {
    if (!canManageWG() || !activeWG?.id || !selectedWGAthlete?.id || !athleteNoteText.trim()) return;
    setAthleteNoteSaving(true);
    try {
      const { data, error } = await supabase.from('coach_notes').insert({
        group_id: activeWG.id,
        athlete_id: selectedWGAthlete.id,
        coach_id: profileData.id,
        note: athleteNoteText.trim(),
      }).select('id,note,created_at,coach_id,group_id,athlete_id').single();
      if (error) throw error;
      setAthleteNote(data);
    } catch (err) {
      console.error('saveWGAthleteNote error:', err);
      notify('Could not save coach note.', 'error');
    } finally {
      setAthleteNoteSaving(false);
    }
  };

  const getWorkoutGroupJoinErrorMessage = async (error, data) => {
    const fallback = 'Could not join workout group.';
    let rawMessage = data?.error || error?.message || fallback;

    if (error?.context && /non-?2xx/i.test(rawMessage)) {
      try {
        const payload = await error.context.clone().json();
        rawMessage = payload?.error || payload?.message || rawMessage;
      } catch {
        try {
          const text = await error.context.clone().text();
          rawMessage = text || rawMessage;
        } catch {}
      }
    }

    return /plan athlete limit|athlete limit|group full|group limit|at its athlete limit|upgrade their plan/i.test(rawMessage)
      ? workoutGroupFullMessage
      : rawMessage;
  };

  const joinWG = async () => {
    const code = joinWGCode.trim().toUpperCase();
    if (!code) return;
    const { data, error } = await supabase.functions.invoke('workout_groups_join', { body: { join_code: code } });
    if (error || data?.error) {
      const message = await getWorkoutGroupJoinErrorMessage(error, data);
      notify(message, 'error');
      return;
    }
    setJoinWGCode('');
    setShowJoinWG(false);
    await loadWG();
    notify('You joined the workout group.', 'success');
  };

  const saveSettings = async () => {
    if (!profileData?.id) return;
    const nextEmail = settingsForm.email.trim();
    if (!nextEmail || !EMAIL_RE.test(nextEmail)) {
      notify('Enter a valid email address.', 'error');
      return;
    }

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      notify('Please sign in again before changing settings.', 'error');
      return;
    }

    const profileUpdates = {
      website: settingsForm.website || null,
      phone_number: settingsForm.phone_number || null,
      address: settingsForm.address || null,
      country: settingsForm.country || null,
      region: settingsForm.region || null,
    };
    const emailChanged = nextEmail.toLowerCase() !== String(user.email || '').toLowerCase();

    const { error } = await supabase.from('profiles').update(profileUpdates).eq('id', profileData.id);
    if (error) {
      notify('Could not save settings. Please try again.', 'error');
      return;
    }

    if (emailChanged) {
      const { error: emailError } = await supabase.auth.updateUser(
        { email: nextEmail },
        { emailRedirectTo: EMAIL_CHANGE_REDIRECT_URL }
      );
      if (emailError) {
        notify(emailError.code === 'email_exists' ? 'That email is already in use.' : 'Could not start the email change. Please try again.', 'error');
        return;
      }
    }

    setProfileData(p => ({ ...p, ...profileUpdates }));
    setShowSettings(false);
    notify(emailChanged ? 'Check your new email to confirm the change.' : 'Settings saved.', 'success');
  };

  const toggleBooking = async (bId) => {
    if (expandedBooking === bId) return setExpandedBooking(null);
    setExpandedBooking(bId);
    if (!bookingDetails[bId]) {
      const { data } = await supabase.from('bookings').select('*,profiles!bookings_profile_id_fkey(name,surname),locations!bookings_location_id_fkey(name)').eq('id', bId).single();
      if (data) setBookingDetails(p => ({ ...p, [bId]: data }));
    }
  };

  const getJoinCode = async (group) => {
    if (!group?.id) return;
    const athleteLimit = group.id === activeWG?.id
      ? activeWGOwnerAccess?.limits?.athletes_per_group
      : workoutAccess?.limits?.athletes_per_group;
    const isAtAthleteLimit = typeof athleteLimit === 'number' && group.id === activeWG?.id && wgMembers.length >= athleteLimit;
    const openJoinCodeModal = (code) => setJoinCodeShare({
      code,
      group,
      warning: isAtAthleteLimit
        ? `This group is at your current plan limit of ${athleteLimit} athlete${athleteLimit === 1 ? '' : 's'}. You can copy the code, but athletes will not be able to join until you upgrade.`
        : '',
    });
    if (group.join_code) { openJoinCodeModal(group.join_code); return; }
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = ''; for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    const { data: updated } = await supabase.from('workout_groups').update({ join_code: code }).eq('id', group.id).select('id,join_code').single();
    if (updated?.join_code) {
      setWorkoutGroups(prev => prev.map(g => g.id === group.id ? { ...g, join_code: updated.join_code } : g));
      setActiveWG(prev => prev?.id === group.id ? { ...prev, join_code: updated.join_code } : prev);
      openJoinCodeModal(updated.join_code);
      notify('Invite code generated.', 'success');
    }
  };

  const selectWorkoutGroup = (group, isActive) => {
    if (isActive) {
      window.requestAnimationFrame(() => {
        workoutDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    setActiveWGId(group.id);
    setActiveWG(group);
    window.requestAnimationFrame(() => {
      workoutDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const canCreateWorkoutGroup = () => {
    if (!profileData?.id || !workoutAccess) return true;
    const limit = workoutAccess?.limits?.groups;
    if (limit === null || typeof limit !== 'number') return true;
    const ownedGroupCount = workoutGroups.filter(g => getWorkoutGroupPlanOwnerId(g) === profileData.id).length;
    return ownedGroupCount < limit;
  };

  const openCreateWorkoutGroup = () => {
    if (canCreateWorkoutGroup()) {
      setShowCreateWG(true);
      return;
    }
    const limit = workoutAccess?.limits?.groups || 0;
    notify(`Your current plan allows ${limit} workout group${limit === 1 ? '' : 's'}. Pick a plan below to add more.`, 'warning');
    setShowWorkoutPlans(true);
    window.requestAnimationFrame(() => {
      workoutPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const openWorkoutGroupUpgradePrompt = (message) => {
    setShowWorkoutPlans(true);
    notify(message, 'warning');
    window.requestAnimationFrame(() => {
      workoutPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const toggleWorkoutClub = (clubId) => {
    setExpandedWorkoutClubIds(prev => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId);
      else next.add(clubId);
      return next;
    });
  };

  const workoutGroupSections = useMemo(() => {
    const clubMap = new Map();
    const standalone = [];
    workoutGroups.forEach(group => {
      const club = group.workout_clubs;
      if (!group.club_id || !club?.id) {
        standalone.push(group);
        return;
      }
      const existing = clubMap.get(club.id) || { type: 'club', club, groups: [], latest: 0 };
      const createdAt = new Date(group.created_at || 0).getTime() || 0;
      existing.groups.push(group);
      existing.latest = Math.max(existing.latest, createdAt);
      clubMap.set(club.id, existing);
    });
    return [
      ...Array.from(clubMap.values()).sort((a, b) => b.latest - a.latest),
      ...standalone.map(group => ({ type: 'group', group, latest: new Date(group.created_at || 0).getTime() || 0 })),
    ];
  }, [workoutGroups]);

  useEffect(() => {
    if (!activeWG?.club_id) return;
    setExpandedWorkoutClubIds(prev => {
      if (prev.has(activeWG.club_id)) return prev;
      const next = new Set(prev);
      next.add(activeWG.club_id);
      return next;
    });
  }, [activeWG?.club_id]);

  const earnedMap = useMemo(() => { const m = new Map(); for (const ua of userAchievements) m.set(ua.achievement_id, ua.completed_at); return m; }, [userAchievements]);
  const achievementSports = useMemo(() => { const s = new Set(allAchievements.map(a => a.sport_id).filter(Boolean)); return ['all', ...Array.from(s).sort()]; }, [allAchievements]);
  const filteredAchievements = useMemo(() => allAchievements.filter(a => { if (achivFilter === 'earned' && !earnedMap.has(a.id)) return false; if (achivFilter === 'locked' && earnedMap.has(a.id)) return false; if (achivSport !== 'all' && a.sport_id !== achivSport) return false; return true; }), [allAchievements, achivFilter, achivSport, earnedMap]);
  const matchSports = useMemo(() => { const s = new Set(matches.map(m => m.sport_id).filter(Boolean)); return ['all', ...Array.from(s).sort()]; }, [matches]);
  const matchStats = useMemo(() => { if (!profileData?.id) return { wins: 0, losses: 0, draws: 0, total: 0 }; let wins = 0, losses = 0, draws = 0; for (const m of matches) { const isP1 = m.profile1_id === profileData.id; const my = isP1 ? m.profile1_points : m.profile2_points; const opp = isP1 ? m.profile2_points : m.profile1_points; if (my > opp) wins++; else if (my < opp) losses++; else draws++; } return { wins, losses, draws, total: matches.length }; }, [matches, profileData?.id]);
  const filteredMatches = useMemo(() => { if (!profileData?.id) return []; return matches.filter(m => { if (matchSport !== 'all' && m.sport_id !== matchSport) return false; if (matchMode !== 'all' && m.mode2 !== matchMode) return false; if (matchFilter !== 'all') { const isP1 = m.profile1_id === profileData.id; const my = isP1 ? m.profile1_points : m.profile2_points; const opp = isP1 ? m.profile2_points : m.profile1_points; const result = my > opp ? 'win' : my < opp ? 'loss' : 'draw'; if (result !== matchFilter) return false; } return true; }); }, [matches, matchFilter, matchSport, matchMode, profileData?.id]);
  const myTeamIds = useMemo(() => new Set((profileData?.teams || []).map(t => t.id)), [profileData?.teams]);
  const teamMatchSports = useMemo(() => { const s = new Set(teamMatches.map(m => m.sport_id).filter(Boolean)); return ['all', ...Array.from(s).sort()]; }, [teamMatches]);
  const teamMatchStats = useMemo(() => { let wins = 0, losses = 0, draws = 0; for (const m of teamMatches) { const myTeam = myTeamIds.has(m.team1_id) ? 1 : 2; const my = myTeam === 1 ? m.team1_points : m.team2_points; const opp = myTeam === 1 ? m.team2_points : m.team1_points; if (my > opp) wins++; else if (my < opp) losses++; else draws++; } return { wins, losses, draws, total: teamMatches.length }; }, [teamMatches, myTeamIds]);
  const filteredTeamMatches = useMemo(() => teamMatches.filter(m => { if (matchSport !== 'all' && m.sport_id !== matchSport) return false; if (matchMode !== 'all' && m.mode2 !== matchMode) return false; if (matchFilter !== 'all') { const myTeam = myTeamIds.has(m.team1_id) ? 1 : 2; const my = myTeam === 1 ? m.team1_points : m.team2_points; const opp = myTeam === 1 ? m.team2_points : m.team1_points; const result = my > opp ? 'win' : my < opp ? 'loss' : 'draw'; if (result !== matchFilter) return false; } return true; }), [teamMatches, matchFilter, matchSport, matchMode, myTeamIds]);
  const unassignedChallengeMembers = useMemo(() => {
    if (!activeInst?.teams || activeInst?.type !== 'Challenge') return [];
    const assignedIds = new Set((activeInst?.departments ?? []).flatMap(d => d?.members ?? []).map(m => m.id).filter(Boolean));
    return (activeInst?.members ?? []).filter(m => m?.id && !m.team_id && !assignedIds.has(m.id));
  }, [activeInst]);
  const allMembers = useMemo(() => {
    if (!activeInst) return [];
    if (activeInst?.teams) return [...(activeInst?.departments ?? []).flatMap(d => d?.members ?? []), ...unassignedChallengeMembers];
    return activeInst?.members ?? [];
  }, [activeInst, unassignedChallengeMembers]);
  const institutionAccessUnlocked = useMemo(() => {
    const now = Date.now();
    const subscriptionUnlocked = (billingOverview?.subscriptions || []).some(row =>
      row?.billing_products?.product_line === 'institutions' &&
      ['active', 'trialing'].includes(String(row?.status || '').toLowerCase()) &&
      (!row?.current_period_end || new Date(row.current_period_end).getTime() > now));
    const entitlementUnlocked = (billingOverview?.entitlements || []).some(row =>
      row?.billing_products?.product_line === 'institutions' &&
      ['active', 'trialing'].includes(String(row?.status || '').toLowerCase()) &&
      (!row?.starts_at || new Date(row.starts_at).getTime() <= now) &&
      (!row?.expires_at || new Date(row.expires_at).getTime() > now));
    return subscriptionUnlocked || entitlementUnlocked;
  }, [billingOverview]);
  const activeInstitutionStepsLocked = isInstitutionAccessLocked(activeInst) || !institutionAccessUnlocked;
  const sortInstitutionMembers = useCallback((membersToSort = []) =>
    [...membersToSort].sort((a, b) =>
      activeInstitutionStepsLocked
        ? String(a?.name || a?.username || '').localeCompare(String(b?.name || b?.username || ''), undefined, { sensitivity: 'base', numeric: true })
        : (b?.steps || 0) - (a?.steps || 0) || String(a?.name || a?.username || '').localeCompare(String(b?.name || b?.username || ''), undefined, { sensitivity: 'base', numeric: true })),
  [activeInstitutionStepsLocked]);
  const sortInstitutionMembersBySteps = useCallback((membersToSort = []) =>
    [...membersToSort].sort((a, b) => (b?.steps || 0) - (a?.steps || 0) || String(a?.name || a?.username || '').localeCompare(String(b?.name || b?.username || ''), undefined, { sensitivity: 'base', numeric: true })),
  []);
  const institutionMemberMatchesSearch = useCallback((member, query) => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    return `${member?.name || ''} ${member?.username || ''} ${member?.association_type || ''}`.toLowerCase().includes(q);
  }, []);
  const totals = useMemo(() => { const ts = allMembers.reduce((a, m) => a + (m?.steps || 0), 0); const mc = allMembers.length; const dc = activeInst?.teams ? ((activeInst?.departments?.length) || 0) : 0; const top = sortInstitutionMembersBySteps(allMembers).slice(0, 5); return { totalSteps: ts, memberCount: mc, deptCount: dc, avgSteps: mc ? Math.round(ts / mc) : 0, top }; }, [allMembers, activeInst, sortInstitutionMembersBySteps]);
  const departmentMemberCountLabel = useCallback((dept = {}) => {
    const count = (dept.members || []).length;
    return `${numberFmt(count)} ${count === 1 ? 'person' : 'people'} in this group`;
  }, []);
  const activeChallengeLimit = activeInst?.type === 'Challenge' ? activeInst.challenge_member_limit : null;
  const activeChallengeGoalSteps = activeInst?.type === 'Challenge' ? (activeInst.goal_steps ?? activeInst.brand_settings?.goalSteps ?? null) : null;
  const activeChallengeDescription = activeInst?.type === 'Challenge' ? (activeInst.brand_settings?.description || activeInst.brand_settings?.challengeDescription || '') : '';
  const activeChallengeDateText = activeInst?.type === 'Challenge'
    ? [activeInst.starts_at ? new Date(activeInst.starts_at).toLocaleDateString() : '', activeInst.ends_at ? new Date(activeInst.ends_at).toLocaleDateString() : ''].filter(Boolean).join(' - ')
    : '';
  const activeChallengeLifecycle = useMemo(() => {
    if (activeInst?.type !== 'Challenge') return '';
    const now = Date.now();
    const start = activeInst.starts_at ? new Date(activeInst.starts_at).getTime() : 0;
    const end = activeInst.ends_at ? new Date(activeInst.ends_at).getTime() : 0;
    if (end && now > end) return 'Finished';
    if (start && now < start) return 'Upcoming';
    return 'Live';
  }, [activeInst?.ends_at, activeInst?.starts_at, activeInst?.type]);
  useEffect(() => {
    let cancelled = false;
    const loadResults = async () => {
      if (activeInst?.type !== 'Challenge' || !activeInst?.id) {
        setChallengeResults([]);
        setChallengeResultsError('');
        return;
      }
      setLoadingChallengeResults(true);
      setChallengeResults([]);
      setChallengeResultsError('');
      try {
        const { data, error } = await supabase
          .from('institution_challenge_results')
          .select('id,place,steps_at_win,goal_steps,locked_at,source,profile_id,team_id,profiles(id,name,surname,username,profile_img),institute_teams(id,name,image_url)')
          .eq('challenge_institute_id', activeInst.id)
          .order('place', { ascending: true });
        if (error) throw error;
        if (!cancelled) setChallengeResults(data || []);
      } catch (err) {
        if (!cancelled) {
          setChallengeResults([]);
          setChallengeResultsError('Could not load goal completions. Try again.');
          console.warn('Could not load challenge results', err?.message || err);
        }
      } finally {
        if (!cancelled) setLoadingChallengeResults(false);
      }
    };
    loadResults();
    return () => { cancelled = true; };
  }, [activeInst?.id, activeInst?.type, instituteRefresh]);
  const activeChallengeGoalNumber = useMemo(() => {
    const value = Number(activeChallengeGoalSteps || 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [activeChallengeGoalSteps]);
  const challengeResultByTeamId = useMemo(() => {
    const resultMap = new Map();
    challengeResults.forEach(result => {
      if (result.team_id) resultMap.set(result.team_id, result);
    });
    return resultMap;
  }, [challengeResults]);
  const challengeResultByProfileId = useMemo(() => {
    const resultMap = new Map();
    challengeResults.forEach(result => {
      if (result.profile_id) resultMap.set(result.profile_id, result);
    });
    return resultMap;
  }, [challengeResults]);
  const challengeProgressPct = useCallback((steps) => {
    if (!activeChallengeGoalNumber) return null;
    const value = Math.round((Number(steps || 0) / activeChallengeGoalNumber) * 100);
    return Math.max(0, Math.min(100, value));
  }, [activeChallengeGoalNumber]);
  const challengePlaceLabel = useCallback((place) => {
    const value = Number(place || 0);
    if (value === 1) return '1st';
    if (value === 2) return '2nd';
    if (value === 3) return '3rd';
    return value ? `${value}th` : '';
  }, []);
  const challengeProgressLabel = useCallback((steps, result) => {
    if (result?.place) return `${challengePlaceLabel(result.place)} reached`;
    const value = challengeProgressPct(steps);
    return value == null ? '' : `${value}% complete`;
  }, [challengePlaceLabel, challengeProgressPct]);
  const filteredDepts = useMemo(() => {
    if (!activeInst?.teams) return [];
    let b = activeInst?.departments ?? [];
    if (instQuery.trim()) b = b.filter(d => (d.name || '').toLowerCase().includes(instQuery.toLowerCase()));
    if (institutionDepartmentMemberSearch.trim()) b = b.filter(d => (d.members || []).some(member => institutionMemberMatchesSearch(member, institutionDepartmentMemberSearch)));
    const mapped = b.map(d => ({ ...d, total: (d.members || []).reduce((a, m) => a + (m.steps || 0), 0) })).sort((a, b) => activeInstitutionStepsLocked ? String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }) : (b.total || 0) - (a.total || 0) || String(a.name || '').localeCompare(String(b.name || '')));
    if (activeInst.type === 'Challenge' && unassignedChallengeMembers.length && (!instQuery.trim() || 'unassigned participants'.includes(instQuery.trim().toLowerCase()))) {
      mapped.push({
        id: '__unassigned_challenge_members',
        name: 'Unassigned participants',
        type: 'Participants',
        code: null,
        image_url: null,
        members: unassignedChallengeMembers,
        total: unassignedChallengeMembers.reduce((a, m) => a + (m.steps || 0), 0),
        isUnassignedChallengeMembers: true,
      });
    }
    return mapped;
  }, [activeInst, activeInstitutionStepsLocked, instQuery, institutionDepartmentMemberSearch, institutionMemberMatchesSearch, unassignedChallengeMembers]);
  const maxDeptSteps = useMemo(() => activeInstitutionStepsLocked ? 0 : filteredDepts.length ? Math.max(...filteredDepts.map(d => d.total || 0)) : 0, [activeInstitutionStepsLocked, filteredDepts]);
  const showAllGroupsByDefault = shouldShowAllInstituteGroups(activeInst);
  const visibleDepts = useMemo(() => visibleInstituteGroups({
    institute: activeInst,
    groups: filteredDepts,
    expanded: showAllDepts,
    hasSearch: Boolean(instQuery.trim() || institutionDepartmentMemberSearch.trim()),
    desktopLimit: 6,
    mobileLimit: 6,
  }), [activeInst, filteredDepts, showAllDepts, instQuery, institutionDepartmentMemberSearch]);
	  const maxPeerSteps = useMemo(() => {
	    const steps = peerBoard.map(i => i.totalSteps);
	    return steps.length ? Math.max(...steps) : 0;
	  }, [peerBoard]);
  const effectiveType = activeInst?.type || capitaliseType(profileData?.institute_type) || 'Institute';
  const activeInstituteHeaderUsesLogo = instituteImageIsLogo(activeInst);
  const activeLeaderboardBranding = activeInst?.brand_settings || DEFAULT_APP_LEADERBOARD_SETTINGS;
  const showInstituteLeaderboard = !activeInst?.teams && activeInst?.type !== 'Challenge';
  const deptLabel = institutionSubgroupLabel(activeInst);
  const memberLabel = ['School', 'College'].includes(activeInst?.type) ? 'Student' : 'Member';
  const isInstitutionDemoAccount = String(profileData?.email || '').trim().toLowerCase() === DEMO_COMPANY_LEADERBOARD_EMAIL;
  const showDemoAppSessionPreview = activeTab === 'institutes' && activeInst?.type !== 'Challenge' && (
    isInstitutionDemoAccount ||
    activeInst?.brand_settings?.demo_seed === 'snowspire-demo-institute-v1'
  );
  const canManageInstitutionAdmins = activeInst?.access_role === 'owner';
  const institutionAdminProfileIds = useMemo(
    () => institutionAdmins.map(row => row.profile_id).filter(Boolean),
    [institutionAdmins],
  );
  const institutionAdminCandidates = useMemo(() => buildInstitutionAdminCandidates({
    members: activeInst?.members || [],
    adminProfileIds: institutionAdminProfileIds,
    ownerProfileId: activeInst?.owner_profile_id,
    search: institutionAdminSearch,
  }), [activeInst?.members, activeInst?.owner_profile_id, institutionAdminProfileIds, institutionAdminSearch]);
  const activeInstituteViews = useMemo(() => ([
    { id: 'people', label: activeInst?.teams ? `${deptLabel}s` : `${memberLabel}s`, icon: activeInst?.teams ? faBuilding : faUsers },
    ...(showInstituteLeaderboard ? [{ id: 'leaderboard', label: 'Leaderboard', icon: faTrophy }] : []),
    { id: 'activities', label: 'Activities', icon: faCalendar },
    ...(activeInst?.type !== 'Challenge' ? [{ id: 'challenges', label: 'Challenges', icon: faTrophy }] : []),
    { id: 'branding', label: 'Settings', icon: faGear },
    ...(!isInstitutionDemoAccount && !institutionPublicPricingHidden ? [{ id: 'plan', label: 'Plan', icon: faCreditCard }] : []),
  ]), [activeInst?.teams, activeInst?.type, deptLabel, institutionPublicPricingHidden, isInstitutionDemoAccount, memberLabel, showInstituteLeaderboard]);
  const managedInstitutionChallenges = useMemo(() => {
    if (!activeInst?.id || activeInst.type === 'Challenge') return [];
    return institutes
      .filter(inst => inst.type === 'Challenge')
      .filter(inst => {
        const settings = inst.brand_settings || {};
        if (settings.parentInstituteId === activeInst.id) return true;
        if (inst.auto_join_institute_id === activeInst.id) return true;
        return Boolean(activeInst.workspace_id && inst.workspace_id === activeInst.workspace_id && settings.parentInstituteId);
      })
      .sort((a, b) => new Date(a.starts_at || a.brand_settings?.startsOn || 0) - new Date(b.starts_at || b.brand_settings?.startsOn || 0));
  }, [activeInst, institutes]);
  const institutionWebPlans = useMemo(
    () => mergeInstitutionPlansWithBillingRows(INSTITUTION_WEB_PLANS, institutionBillingRows),
    [institutionBillingRows],
  );
  const activeWorkspacePlanInstitute = useMemo(() => {
    if (!activeInst?.workspace_id) return null;
    return institutes.find(inst =>
      inst.workspace_id === activeInst.workspace_id &&
      inst.type !== 'Challenge' &&
      (inst.plan_tier || inst.brand_settings?.planTier)
    ) || null;
  }, [activeInst?.workspace_id, institutes]);
  const activeInstitutionStoredTier = String(
    activeInst?.plan_tier ||
    activeInst?.brand_settings?.planTier ||
    activeWorkspacePlanInstitute?.plan_tier ||
    activeWorkspacePlanInstitute?.brand_settings?.planTier ||
    ''
  ).toLowerCase();
  const institutionRestartLockedPlan = useMemo(
    () => activeInstitutionStoredTier ? institutionWebPlans.find(plan => plan.tier === activeInstitutionStoredTier) || null : null,
    [activeInstitutionStoredTier, institutionWebPlans],
  );
  const institutionExistingAccessInactive = institutes.length > 0 && !institutionAccessUnlocked;
  const institutionRestartMode = institutionExistingAccessInactive && !!institutionRestartLockedPlan;
  const institutionStartExistingMode = institutionExistingAccessInactive && !institutionRestartLockedPlan;
  const activeInstitutionTierRank = institutionPlanRank(activeInstitutionStoredTier);
  const institutionRenewUpgradePlans = useMemo(
    () => activeInstitutionTierRank > 0
      ? institutionWebPlans.filter(plan => institutionPlanRank(plan.tier) > activeInstitutionTierRank)
      : [],
    [activeInstitutionTierRank, institutionWebPlans],
  );
  const visibleInstitutionWebPlans = useMemo(
    () => institutionRestartMode
      ? (showInstitutionPlans ? institutionRenewUpgradePlans : [institutionRestartLockedPlan])
      : institutionWebPlans.filter(plan => plan.segment === institutionPlanSegment),
    [institutionRestartLockedPlan, institutionRestartMode, institutionRenewUpgradePlans, institutionWebPlans, institutionPlanSegment, showInstitutionPlans],
  );
  const displayedInstitutionWebPlans = useMemo(
    () => collapseBusinessDemoInstitutionPlans(visibleInstitutionWebPlans, institutionBusinessDemoMode),
    [institutionBusinessDemoMode, visibleInstitutionWebPlans],
  );
  const activeInstitutionPlanKeys = useMemo(() => {
    const keys = new Set();
    const current = (row) => ['active', 'trialing', 'past_due', 'pending'].includes(row?.status) && (!row?.expires_at || new Date(row.expires_at).getTime() > Date.now());
    (billingOverview?.subscriptions || []).forEach(row => {
      if (row?.billing_products?.product_line === 'institutions' && ['active', 'trialing', 'past_due'].includes(row?.status)) {
        keys.add(row.billing_products.product_key);
      }
    });
    (billingOverview?.entitlements || []).forEach(row => {
      if (row?.billing_products?.product_line === 'institutions' && current(row)) {
        keys.add(row.billing_products.product_key);
      }
    });
    return keys;
  }, [billingOverview]);
	  const activeInstitutionSummary = useMemo(() => {
	    const rows = [
	      ...(billingOverview?.subscriptions || []).filter(row => row?.billing_products?.product_line === 'institutions' && ['active', 'trialing', 'past_due'].includes(row?.status)),
	      ...(billingOverview?.entitlements || []).filter(row => row?.billing_products?.product_line === 'institutions' && ['active', 'trialing', 'pending'].includes(row?.status)),
    ];
    const activeId = activeInst?.id || null;
    const workspaceInstituteIds = new Set((institutes || [])
      .filter(inst => activeInst?.workspace_id && inst?.workspace_id === activeInst.workspace_id)
      .map(inst => inst.id)
      .filter(Boolean));
    const scopedRows = rows.filter(row => row?.target_id && (row.target_id === activeId || workspaceInstituteIds.has(row.target_id)));
    const row = activeId ? scopedRows[0] || null : rows[0];
    const tier = row?.billing_products?.metadata?.tier || null;
    const plan = institutionWebPlans.find(item => item.tier === tier);
    return row ? {
      name: plan ? institutionPlanDisplayName(plan) : row?.billing_products?.name || tier || 'Institution Access',
      status: row?.status || 'active',
      renewsAt: row?.current_period_end || null,
      expiresAt: row?.expires_at || null,
	      tier,
	    } : null;
	  }, [activeInst?.id, activeInst?.workspace_id, billingOverview, institutes, institutionWebPlans]);
  const institutionStripeSubscription = useMemo(() => {
    const activeStatuses = new Set(['active', 'trialing', 'past_due', 'incomplete']);
    const rows = (billingOverview?.subscriptions || []).filter(row =>
      row?.billing_products?.product_line === 'institutions' &&
      String(row?.provider || '').toLowerCase() === 'stripe' &&
      activeStatuses.has(String(row?.status || '').toLowerCase()) &&
      (row?.provider_customer_id || row?.provider_subscription_id));
    if (!rows.length) return null;
    const activeId = activeInst?.id || null;
    const workspaceInstituteIds = new Set((institutes || [])
      .filter(inst => activeInst?.workspace_id && inst?.workspace_id === activeInst.workspace_id)
      .map(inst => inst.id)
      .filter(Boolean));
    return rows.find(row => row?.target_id && row.target_id === activeId) ||
      rows.find(row => row?.target_id && workspaceInstituteIds.has(row.target_id)) ||
      (!activeId ? rows[0] : null);
  }, [activeInst?.id, activeInst?.workspace_id, billingOverview, institutes]);
  const canManageInstitutionBilling = !!institutionStripeSubscription;
  const institutionPlanFitsCurrentMembers = useCallback((plan) => (
    !plan?.memberLimit || totals.memberCount <= Number(plan.memberLimit || 0)
  ), [totals.memberCount]);
  const institutionManagementPlans = useMemo(
    () => activeInstitutionTierRank > 0
      ? institutionWebPlans.filter(plan => institutionPlanRank(plan.tier) > activeInstitutionTierRank && institutionPlanFitsCurrentMembers(plan))
      : institutionWebPlans.filter(plan => plan.segment === institutionPlanSegment),
    [activeInstitutionTierRank, institutionWebPlans, institutionPlanSegment, institutionPlanFitsCurrentMembers],
  );
  const displayedInstitutionManagementPlans = useMemo(
    () => collapseBusinessDemoInstitutionPlans(institutionManagementPlans, institutionBusinessDemoMode),
    [institutionBusinessDemoMode, institutionManagementPlans],
  );
	  const institutionAccessName = institutionAccessUnlocked
	    ? activeInstitutionSummary?.name
	    : String(activeInstitutionSummary?.status || '').toLowerCase() === 'pending'
	      ? 'Activating'
	      : 'Inactive';
	  const institutionAccessAction = institutionPublicPricingHidden && !institutionAccessUnlocked
	    ? 'Contact Snows ProAm for setup'
	    : institutionAccessUnlocked
	    ? activeInstitutionSummary?.renewsAt
	      ? fmtDate(activeInstitutionSummary.renewsAt)
	      : activeInstitutionSummary?.expiresAt
	        ? fmtDate(activeInstitutionSummary.expiresAt)
	        : 'Access is active'
	    : String(activeInstitutionSummary?.status || '').toLowerCase() === 'past_due'
	      ? 'Update payment to unlock steps'
	      : 'Start access to unlock steps';

	  const currentAccessPlans = useMemo(() => sortAccessPlans(getCurrentBillingPlans(billingOverview)), [billingOverview]);
  const accountSummaryStats = useMemo(() => {
    const stats = [];
    const activeAccess = currentAccessPlans.length ? `${currentAccessPlans.length} active` : '';
    const coinBalance = Number.isFinite(Number(profileData?.proam_coins)) ? formatTxCoins(Number(profileData?.proam_coins || 0)) : '';
    const lastPayment = (billingOverview?.payments || []).find(payment => typeof payment?.amount_cents === 'number' || typeof payment?.coins_amount === 'number');
    const upcomingPlan = currentAccessPlans.find(plan => plan.renewsAt || plan.expiresAt);
    if (activeAccess) stats.push({ label: 'Active access', value: activeAccess, icon: faLock });
    if (coinBalance) stats.push({ label: 'Coin balance', value: coinBalance, icon: faCoins });
    if (lastPayment) stats.push({ label: 'Last payment', value: paymentAmountLabel(lastPayment), meta: fmtDate(lastPayment.created_at), icon: faCreditCard });
    if (upcomingPlan) stats.push({ label: upcomingPlan.renewsAt ? 'Next renewal' : 'Access ends', value: fmtDate(upcomingPlan.renewsAt || upcomingPlan.expiresAt), icon: faCalendar });
    return stats.slice(0, 4);
  }, [billingOverview, currentAccessPlans, profileData?.proam_coins]);
  const transactionFeed = useMemo(
    () => mergeTransactionsWithStoreOrders(
      mergeTransactionsWithBillingPayments(transactions, billingOverview?.payments || [], profileData?.id),
      storeOrders,
      profileData?.id,
    ),
    [billingOverview?.payments, profileData?.id, storeOrders, transactions],
  );
  const filteredTransactions = useMemo(() => {
    const query = transactionSearch.trim().toLowerCase();
    const matchesFilter = (tx) => {
      const type = String(tx.transaction_type || '').toLowerCase();
      const source = String(tx.source || '').toLowerCase();
      if (transactionFilter === 'all') return true;
      if (transactionFilter === 'purchases') return source.includes('purchase') || source.includes('stripe');
      if (transactionFilter === 'refunds') return source.includes('refund') || String(tx.status || '').toLowerCase().includes('refund');
      if (transactionFilter === 'coins') return Number(tx.proam_coins || 0) !== 0 || source.includes('coin') || source.includes('redemption');
      if (transactionFilter === 'access') return type.includes('tournament') || type.includes('workout') || type.includes('event') || type.includes('challenge');
      if (transactionFilter === 'issues') return isTransactionIssue(tx);
      return true;
    };
    return [...transactionFeed]
      .filter((tx) => {
        if (!matchesFilter(tx)) return false;
        if (!query) return true;
        const item = getStoreOrderPrimaryItem(tx);
        const discount = getTransactionDiscount(tx);
        const haystack = [
          formatTransactionTitle(tx),
          formatTransactionStatus(tx),
          formatTransactionDiscountSummary(tx),
          item?.product_name,
          discount?.code,
          tx.transaction_type,
          tx.source,
          tx.id,
          tx.payment_intent_id,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
      })
      .sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime() || 0;
        const bTime = new Date(b.created_at || 0).getTime() || 0;
        return transactionSort === 'oldest' ? aTime - bTime : bTime - aTime;
      });
  }, [transactionFeed, transactionFilter, transactionSearch, transactionSort]);
  const visibleTransactions = filteredTransactions.slice(0, visibleTransactionCount);
  useEffect(() => {
    setVisibleTransactionCount(20);
  }, [transactionFilter, transactionSearch, transactionSort]);

  const transactionPayment = useCallback((tx = {}) => (
    (billingOverview?.payments || []).find(payment =>
      (tx.billing_payment_id && payment.id === tx.billing_payment_id) ||
      (tx.id && payment.transaction_id === tx.id)
    ) || null
  ), [billingOverview?.payments]);

  const isActivityRefundTransaction = useCallback((tx = {}) => {
    const payment = transactionPayment(tx);
    const txType = String(tx.transaction_type || '').toLowerCase();
    const targetType = String(payment?.target_type || payment?.metadata?.target_type || '').toLowerCase();
    const text = `${txType} ${targetType}`;
    if (txType.includes('event listing') || targetType.includes('event_listing')) return false;
    if (txType.includes('tournament creation') || targetType.includes('tournament_creation')) return false;
    return (
      text.includes('event registration') ||
      text.includes('tournament enrollment') ||
      text.includes('tournament_entry') ||
      text.includes('scheduled session') ||
      text.includes('scheduled_session')
    );
  }, [transactionPayment]);

  const transactionSubscription = useCallback((tx = {}) => {
    const payment = transactionPayment(tx);
    return (billingOverview?.subscriptions || []).find(subscription =>
      canCancelBillingSubscription(subscription) &&
      (
        subscriptionMatchesPayment(subscription, payment, tx) ||
        (subscription.product_id && tx.target_id && subscription.product_id === tx.target_id)
      )
    ) || null;
  }, [billingOverview?.subscriptions, transactionPayment]);

  const requestTransactionRefund = useCallback(async (tx = {}, details = {}) => {
    if (isActivityRefundTransaction(tx)) {
      notify('Use Report problem for event, tournament, and scheduled session payments. This will not create a refund request.', 'info');
      return;
    }
    const payment = transactionPayment(tx);
    const method = payment?.provider === 'coins' || (!tx.payment_intent_id && Number(tx.proam_coins || 0) > 0) ? 'coins' : 'stripe';
    const reason = String(details.reason || '').trim();
    const message = String(details.message || '').trim();
    if (!reason) {
      notify('Choose a refund reason before submitting.', 'error');
      return;
    }
    try {
      const { data, error } = await supabase.from('refund_requests').insert({
        profile_id: profileData?.id,
        transaction_id: tx.synthetic ? null : tx.id,
        billing_payment_id: payment?.id || null,
        refund_method: method,
        status: 'pending',
        requested_amount_cents: method === 'stripe' ? Math.max(0, Math.round(Number(tx.amount || payment?.amount_cents / 100 || 0) * 100)) : null,
        requested_coins: method === 'coins' ? Math.max(0, Number(tx.proam_coins || payment?.coins_amount || 0)) : null,
        currency: 'eur',
        reason,
        message: message || null,
        target_type: tx.transaction_type || null,
        target_id: tx.target_id || null,
        metadata: {
          source: tx.source || null,
          payment_intent_id: tx.payment_intent_id || null,
          provider: payment?.provider || null,
          web_created: true,
        },
      }).select('id').single();
      if (error) throw error;
      if (data?.id) {
        supabase.functions.invoke('refund_request_email', {
          body: { refund_request_id: data.id },
        }).then(({ data: emailData, error: emailError }) => {
          if (emailError || emailData?.error) {
            console.error('[Profile] refund request email failed', {
              refund_request_id: data.id,
              error: emailError?.message || emailData?.error || null,
            });
          }
        });
      }
      notify('Refund request submitted.', 'success');
    } catch (err) {
      notify(err?.message || 'Could not submit refund request.', 'error');
    }
  }, [isActivityRefundTransaction, notify, profileData?.id, transactionPayment]);

  const reportTransactionProblem = useCallback((tx = null) => {
    const supportBody = tx
      ? `Username: @${profileData?.username || ''}\nEmail: ${profileData?.email || ''}\nDate: ${tx.created_at ? new Date(tx.created_at).toLocaleString('en-IE') : 'Not available'}\nIssue:`
      : `Username: @${profileData?.username || ''}\nEmail: ${profileData?.email || ''}\nIssue:`;
    const supportHref = `mailto:support@snowsproam.com?subject=${encodeURIComponent('Transaction support request')}&body=${encodeURIComponent(supportBody)}`;
    notify('Opening email support. This will not create a refund request.', 'info');
    window.location.href = supportHref;
  }, [notify, profileData?.email, profileData?.username]);

  const confirmTransactionRefund = useCallback((tx = {}) => {
    setRefundDialog({
      tx,
      reason: 'Technical issue',
      message: '',
    });
  }, []);

  const cancelTransactionSubscription = useCallback(async (subscription) => {
    try {
      const { data, error } = await supabase.functions.invoke('cancel_billing_subscription', {
        body: { subscription_id: subscription.id },
      });
      if (error || data?.error || !data?.ok) throw new Error(data?.error || error?.message || 'Could not cancel subscription.');
      setBillingOverview(prev => ({
        ...prev,
        subscriptions: (prev?.subscriptions || []).map(row =>
          row.id === subscription.id
            ? { ...row, cancel_at_period_end: true, current_period_end: data.ends_at || row.current_period_end }
            : row
        ),
      }));
      notify('Subscription cancellation scheduled.', 'success');
    } catch (err) {
      notify(err?.message || 'Could not cancel subscription.', 'error');
    }
  }, [notify]);

  const confirmCancelTransactionSubscription = useCallback((subscription) => {
    setConfirmDialog({
      title: 'Cancel subscription?',
      message: 'This stops future renewals and cannot be reverted from this app. Access remains active until the current period ends.',
      confirmLabel: 'Cancel subscription',
      tone: 'danger',
      onConfirm: () => cancelTransactionSubscription(subscription),
    });
  }, [cancelTransactionSubscription]);

  const renderWorkoutGroupCard = (g, options = {}) => {
    const isActive = g.id === activeWGId;
    const isOwnGroup = g.coach_id === profileData.id || g.created_by === profileData.id;
    const isPlanOwner = getWorkoutGroupPlanOwnerId(g) === profileData.id;
    const lockState = getWorkoutGroupLockState(g);
    const memberCount = lockState.memberCount;
    const assignedCount = lockState.assignedCount;
    const ownerAccess = isPlanOwner ? workoutAccess : workoutGroupOwnerAccessMap[g.id];
    const athleteLimit = ownerAccess?.limits?.athletes_per_group;
    const workoutLimit = ownerAccess?.limits?.assigned_workouts_per_group;
    const isLockedGroup = lockState.locked;
    const t = clubTheme(g, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
    const themed = t.enabled;
    const displayClub = t.club || firstRelationRow(g.workout_clubs) || null;
    const cardText = themed ? t.onPrimary : C.text;
    const cardMuted = themed ? t.mutedOnPrimary : C.textMuted;
    const cardBorder = isActive || isLockedGroup ? t.accent : (themed ? 'rgba(255,255,255,0.22)' : 'rgba(12,105,200,0.16)');
    const cardBackground = themed
      ? `linear-gradient(135deg, ${t.primary} 0%, ${t.surfaceSecondary || t.accent} 100%)`
      : 'linear-gradient(135deg,#FFFFFF 0%,#F4F8FF 56%,#FFF8E1 100%)';
    const softPanelBg = themed ? t.subtleOnPrimary : 'rgba(255,255,255,0.82)';
    const softPanelBorder = themed ? t.borderOnPrimary : 'rgba(12,105,200,0.12)';
    const roleMeta = isOwnGroup
      ? { label: 'Coach Access', detail: 'You manage this group', icon: faCheck, tone: C.accent }
      : g.__isClubStaff
        ? { label: 'Club Staff', detail: 'Staff dashboard access', icon: faBuilding, tone: '#D5A419' }
        : { label: 'Athlete Member', detail: 'Joined as an athlete', icon: faUser, tone: '#16A34A' };
    return (
      <div
        key={g.id}
        className="card-lift wg-card"
        onClick={() => {
          if (isLockedGroup && !lockState.canOpenLocked) {
            notify(lockState.reason, 'warning');
            return;
          }
          selectWorkoutGroup(g, isActive);
        }}
        style={card(C, { padding: 0, overflow: 'hidden', marginTop: isMobile ? 8 : 0, minWidth: options.compact ? (isMobile ? 214 : 230) : (isMobile ? 224 : 250), maxWidth: options.compact ? (isMobile ? 236 : 256) : (isMobile ? 248 : 286), flexShrink: 0, cursor: 'pointer', borderColor: cardBorder, background: cardBackground, boxShadow: themed ? `0 14px 38px ${t.primary}33` : '0 14px 34px rgba(12,105,200,0.13)', opacity: isLockedGroup ? 0.78 : 1 })}
      >
        <div style={{ position: 'relative', overflow: 'hidden', padding: '12px 13px', borderBottom: `1px solid ${softPanelBorder}`, background: themed ? t.accent : 'linear-gradient(135deg,rgba(12,105,200,0.10),rgba(213,164,25,0.10))' }}>
          <FontAwesomeIcon aria-hidden="true" icon={faDumbbell} style={{ position: 'absolute', right: 12, bottom: -18, color: themed ? '#FFFFFF' : t.primary, opacity: themed ? 0.08 : 0.07, fontSize: 58 }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <WorkoutGroupLogo C={C} group={g} club={displayClub} themed={themed} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${themed ? 'rgba(255,255,255,0.24)' : `${roleMeta.tone}33`}`, background: themed ? 'rgba(255,255,255,0.12)' : '#FFFFFF', color: themed ? '#fff' : roleMeta.tone, borderRadius: 999, padding: '6px 8px', fontSize: 10, lineHeight: 1, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.35, whiteSpace: 'nowrap' }}>
              <FontAwesomeIcon icon={roleMeta.icon} style={{ fontSize: 9 }} />
              {roleMeta.label}
            </span>
          </div>
          {displayClub?.name && <div style={{ position: 'relative', fontSize: 10, color: themed ? 'rgba(255,255,255,0.76)' : cardMuted, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 9 }}>{displayClub.name}</div>}
        </div>
        <div style={{ padding: '13px 13px 14px' }}>
          <div className="heading" style={{ fontSize: 18, fontWeight: 950, color: cardText, marginBottom: 5, lineHeight: 1.08, letterSpacing: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 39 }}>{g.name}</div>
          <div style={{ color: cardMuted, fontSize: 12, fontWeight: 850, lineHeight: 1.35, marginBottom: 11 }}>{roleMeta.detail}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '30px minmax(0,1fr)', gap: 8, alignItems: 'center', marginBottom: 11, padding: '8px 9px', borderRadius: 13, border: `1px solid ${softPanelBorder}`, background: softPanelBg }}>
            {g.coachProfileImg ? (
              <img src={g.coachProfileImg} alt="" style={{ width: 30, height: 30, borderRadius: 11, objectFit: 'cover', border: `1px solid ${softPanelBorder}`, background: themed ? t.secondary : C.bgAlt }} loading="lazy" />
            ) : (
              <span style={{ width: 30, height: 30, borderRadius: 11, display: 'grid', placeItems: 'center', background: themed ? t.secondary : 'rgba(12,105,200,0.10)', color: themed ? t.primary : t.primary, border: `1px solid ${softPanelBorder}` }}>
                <FontAwesomeIcon icon={faUser} style={{ fontSize: 11 }} />
              </span>
            )}
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', color: cardMuted, fontSize: 9, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.45 }}>Coach</span>
              <span style={{ display: 'block', color: cardText, fontSize: 12, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{g.coachUsername ? `@${g.coachUsername}` : g.coachName}</span>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7 }}>
            {[
              { icon: faBullseye, label: 'XP / wk', value: Number(g.weekly_xp_target || 0).toLocaleString(), color: t.primary || C.accent },
              { icon: faCalendar, label: 'Sessions / wk', value: Number(g.weekly_session_target || 0).toLocaleString(), color: '#16A34A' },
            ].map(item => (
              <span key={item.label} style={{ minHeight: 58, border: `1px solid ${softPanelBorder}`, background: softPanelBg, borderRadius: 13, padding: '9px 9px 8px', display: 'grid', alignContent: 'space-between', gap: 5 }}>
                <span style={{ color: themed ? '#fff' : item.color, fontSize: 12, lineHeight: 1 }}>
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <span>
                  <span style={{ display: 'block', color: cardText, fontSize: 15, lineHeight: 1, fontWeight: 950, whiteSpace: 'nowrap' }}>{item.value}</span>
                  <span style={{ display: 'block', color: cardMuted, fontSize: 9, lineHeight: 1.2, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.35, marginTop: 4 }}>{item.label}</span>
                </span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
          {isLockedGroup && typeof athleteLimit === 'number' && memberCount > athleteLimit && (
            <span style={{ fontSize: 11, color: cardMuted, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 850 }}>
              <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} /> {memberCount}/{athleteLimit} athletes
            </span>
          )}
          {isLockedGroup && typeof workoutLimit === 'number' && assignedCount > workoutLimit && (
            <span style={{ fontSize: 11, color: cardMuted, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 850 }}>
              <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} /> {assignedCount}/{workoutLimit} workouts
            </span>
          )}
          {activeWGCanUseNutritionStats && <span style={{ fontSize: 11, color: cardMuted, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 850 }}>{Number(g.daily_calorie_target || 2500).toLocaleString()} kcal/day</span>}
          </div>
        </div>
      </div>
    );
  };

  if (!profileData) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
      <Circles height="60" width="60" color={C.accent} ariaLabel="loading" />
    </div>
  );

  /* ─── Responsive layout values ─── */
  const px = isMobile ? '14px' : '32px';
  const pageMax = isMobile ? '100%' : 'min(1560px, calc(100vw - 64px))';
  const sectionPY = isMobile ? '20px 14px 60px' : '28px 32px 80px';
  const profileAge = getAgeFromDateOfBirth(profileData.date_of_birth);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text, fontFamily: "'DM Sans',sans-serif", transition: 'background 0.3s ease,color 0.3s ease', overflowX: 'hidden' }}>
      <style>{makeCSS(C)}</style>

      {/* ── HEADER ── */}
      <header className="profile-page-header" style={{ position: 'sticky', top: 0, zIndex: 1000, height: 58, padding: `0 ${px}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.headerBg, backdropFilter: 'blur(20px) saturate(1.2)', WebkitBackdropFilter: 'blur(20px) saturate(1.2)', borderBottom: `1px solid ${C.border}` }}>
        <Link to="/"><img src={logo} alt="Snows ProAm" style={{ height: isMobile ? 26 : 32 }} /></Link>
        <div className="heading" style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          <span style={{ color: C.text }}>My Profile</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={toggleTheme} aria-label={isDark ? "Use light theme" : "Use dark theme"} style={{ width: 38, height: 38, borderRadius: 10, background: C.bgElevated, border: `1px solid ${C.border}`, color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </button>
          <button onClick={() => setShowSettings(true)} aria-label="Open settings" style={{ width: 38, height: 38, borderRadius: 10, background: C.bgElevated, border: `1px solid ${C.border}`, color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faGear} style={{ fontSize: 13 }} />
          </button>
          <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/'); }} style={{ fontSize: 12, fontWeight: 500, color: C.textSecondary, padding: '5px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
            ← Back
          </button>
        </div>
      </header>

      {/* ── PROFILE HERO ── */}
      <div style={{ background: C.bgCard, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '20px 14px 0' : '32px 24px 0' }}>
	        <div style={{ width: pageMax, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-end', gap: isMobile ? 14 : 20, marginBottom: isMobile ? 16 : 24, flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <img src={profileData.profile_img || noImage} alt="Profile"
                style={{ width: isMobile ? 68 : 88, height: isMobile ? 68 : 88, borderRadius: 18, objectFit: 'cover', border: `3px solid ${C.accent}`, boxShadow: `0 0 0 4px ${C.accentBgLight}` }} />
              {loadingImg && (
                <div style={{ position: 'absolute', inset: 0, borderRadius: 18, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Circles height="28" width="28" color="#fff" />
                </div>
              )}
              <label htmlFor="profileImgInput" aria-label="Change profile photo" style={{ position: 'absolute', bottom: -5, right: -5, width: 26, height: 26, borderRadius: 7, background: C.accentGrad, border: `2px solid ${C.bgCard}`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faPen} style={{ fontSize: 10 }} />
              </label>
              <input type="file" id="profileImgInput" accept="image/*" style={{ display: 'none' }} onChange={handleUpdateProfileImg} />
            </div>

            {/* Name + stats */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 className="heading" style={{ fontSize: isMobile ? 18 : 'clamp(22px,4vw,32px)', fontWeight: 800, color: C.text, letterSpacing: -0.5, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profileData.name} {profileData.surname}</h1>
              <div style={{ fontSize: isMobile ? 13 : 15, color: C.textMuted, fontWeight: 500, marginBottom: isMobile ? 8 : 10 }}>@{profileData.username}</div>
              <div className="hero-stats" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { icon: faBolt, label: 'Level', val: profileData.level },
                  { icon: faChartLine, label: 'Experience', val: Number(profileData.xp || 0).toLocaleString() },
                  { icon: faReceipt, label: 'Coins', val: Number(profileData.proam_coins || 0).toLocaleString() },
                  { icon: faTrophy, label: 'Achievements', val: userAchievements.length },
                  { icon: faTableTennis, label: 'Matches', val: matches.length },
                ].map(s => (
                  <div key={s.label} style={{ ...tagSt(C), gap: 4, padding: isMobile ? '4px 9px' : '5px 12px', fontSize: isMobile ? 11 : 12 }}>
                    <FontAwesomeIcon icon={s.icon} style={{ fontSize: 11 }} />
                    <span style={{ color: C.textSecondary, fontWeight: 500 }}>{s.label}:</span>
                    <span className="heading" style={{ color: C.text, fontWeight: 700 }}>{s.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TABS — scrollable on mobile */}
          <div role="tablist" aria-label="Profile sections" style={{ display: 'flex', gap: 0, overflowX: 'auto', borderTop: `1px solid ${C.border}`, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            {TABS.map(t => (
              <button key={t.id} type="button" role="tab" aria-selected={activeTab === t.id} className="tab-btn" onClick={() => selectProfileTab(t.id)}
                style={{ padding: isMobile ? '12px 14px' : '14px 20px', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === t.id ? C.accent : 'transparent'}`, color: activeTab === t.id ? C.accent : C.textMuted, fontWeight: 600, fontSize: isMobile ? 12 : 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 7, whiteSpace: 'nowrap', fontFamily: "'DM Sans',sans-serif", transition: 'all 0.15s ease', flexShrink: 0 }}>
                <FontAwesomeIcon icon={t.icon} style={{ fontSize: isMobile ? 14 : 13 }} />
                <span className="tab-label">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
	      <div style={{ width: pageMax, margin: '0 auto', padding: sectionPY }}>

        {/* ══ TAB: PROFILE ══ */}
        {activeTab === 'profile' && (
          <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(300px,1fr))', gap: 16, alignItems: 'start' }}>
              {[
                { title: 'Personal Information', icon: faUser, fields: [{ f: 'date_of_birth', l: 'Age', value: profileData.date_of_birth ? String(profileData.date_of_birth).split('T')[0] : '', displayValue: profileAge, inputType: 'date' }, { f: 'gender', l: 'Gender' }, { f: 'country', l: 'Country' }, { f: 'region', l: 'Region' }, { f: 'address', l: 'Address' }] },
                { title: 'Contact Information',  icon: faEnvelope, fields: [{ f: 'email', l: 'Email' }, { f: 'phoneNumber', l: 'Phone Number' }] },
                { title: 'Physical Attributes',  icon: faDumbbell, fields: [{ f: 'height', l: 'Height (cm)' }, { f: 'weight', l: 'Weight (kg)' }] },
              ].map(sec => (
                <div key={sec.title} style={card(C, { padding: '18px 18px' })}>
                  <div className="heading" style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><FontAwesomeIcon icon={sec.icon} style={{ color: C.accent, fontSize: 13 }} /> {sec.title}</div>
                  {sec.fields.map(f => (<EditField key={f.f} C={C} label={f.l} field={f.f} value={f.value ?? profileData[f.f]} displayValue={f.displayValue} inputType={f.inputType} onSave={handleSaveField} readOnly={f.readOnly} />))}
                </div>
              ))}
              <div style={card(C, { padding: '18px 18px' })}>
                <div className="heading" style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><FontAwesomeIcon icon={faBolt} style={{ color: C.accent, fontSize: 13 }} /> ProAm Stats</div>
                {[{ label: 'Level', val: profileData.level }, { label: 'XP', val: Number(profileData.xp || 0).toLocaleString() }, { label: 'ProAm Coins', val: Number(profileData.proam_coins || 0).toLocaleString() }].map(s => (
                  <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 14, color: C.textMuted, fontWeight: 500 }}>{s.label}</span>
                    <span className="heading" style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{s.val}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={card(C, { padding: '18px 18px' })}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <div className="heading" style={{ fontSize: 15, fontWeight: 800, color: C.text }}>Workout Groups Access</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>Source of truth: subscriptions and entitlements</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                  {canManageWorkoutGroupBilling && (
                    <button
                      type="button"
                      onClick={openWorkoutGroupBillingPortal}
                      disabled={openingWorkoutBillingPortal}
                      style={{ background: C.accentGrad, border: 'none', color: '#fff', padding: '9px 13px', borderRadius: 10, cursor: openingWorkoutBillingPortal ? 'wait' : 'pointer', opacity: openingWorkoutBillingPortal ? 0.72 : 1, fontSize: 12, fontWeight: 900, minHeight: 40, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                    >
                      {openingWorkoutBillingPortal ? 'Opening billing...' : 'Manage or cancel billing'}
                    </button>
                  )}
	                  <button
	                    type="button"
	                    onClick={() => setShowWorkoutPlans(v => !v)}
	                    style={{ background: canManageWorkoutGroupBilling ? C.bgCard : C.accentGrad, border: canManageWorkoutGroupBilling ? `1px solid ${C.accentBorder}` : 'none', color: canManageWorkoutGroupBilling ? C.accent : '#fff', padding: '9px 13px', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 900, minHeight: 40, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
	                  >
	                    {showWorkoutPlans ? 'Hide upgrades' : 'Upgrade'}
	                  </button>
                </div>
              </div>
              {workoutAccess ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,1fr)', gap: 10 }}>
                    {[
                      ['Plan', workoutAccess.planName],
                      ['Status', workoutAccess.status],
                      ['Source', workoutAccess.source],
                      [workoutAccessTimingSummary(workoutAccess).label, workoutAccessTimingSummary(workoutAccess).value],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                        <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', fontWeight: 800, letterSpacing: .4 }}>{label}</div>
                        <div style={{ marginTop: 5, fontSize: 13, color: C.text, fontWeight: 800 }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {checkoutMessage && (
                    <div style={{ marginTop: 12, background: C.mode === 'dark' ? 'rgba(239,68,68,0.10)' : 'rgba(220,38,38,0.08)', border: `1px solid ${C.mode === 'dark' ? 'rgba(239,68,68,0.25)' : 'rgba(220,38,38,0.16)'}`, color: C.danger, borderRadius: 12, padding: 12, fontSize: 12, fontWeight: 700 }}>
                      {checkoutMessage}
                    </div>
                  )}

                  {showWorkoutPlans && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
	                        <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 700 }}>Only higher web subscription plans are shown. Mobile coin unlocks still read the same Supabase entitlements.</div>
                        <div style={{ display: 'inline-flex', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 3 }}>
                          {[
                            ['monthly', 'Monthly'],
                            ['yearly', 'Yearly · save 15%'],
                          ].map(([value, label]) => (
                            <button key={value} type="button" onClick={() => setWorkoutBillingCycle(value)} style={{ border: 'none', borderRadius: 9, padding: '7px 10px', background: workoutBillingCycle === value ? C.accentGrad : 'transparent', color: workoutBillingCycle === value ? '#fff' : C.textSecondary, fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>{label}</button>
                          ))}
                        </div>
                        <button onClick={refreshWorkoutAccess} style={{ ...tagSt(C), cursor: 'pointer', fontSize: 11, padding: '7px 10px' }}>Refresh Access</button>
                      </div>
	                      {workoutUpgradePlans.length === 0 && (
	                        <div role="status" style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt, padding: 14, color: C.textSecondary, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
	                          No higher Workout Groups web plan is available. Manage billing to update card details, view invoices, or cancel.
	                        </div>
	                      )}
		                      {workoutUpgradePlans.length > 0 && (
	                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(210px,1fr))', gap: 10 }}>
	                        {workoutUpgradePlans.map(plan => {
	                          const featured = plan.tier === 'coach';
	                          const checkout = getWorkoutPlanCheckout(plan);
	                          const priceConfigured = !checkout.priceKey || workoutAccess.prices?.some(p => p.price_key === checkout.priceKey && p.stripe_price_id);
	                          return (
	                            <div key={plan.tier} style={{ background: featured ? C.accentBgSubtle : C.bgAlt, border: `1px solid ${featured ? C.accentBorder : C.border}`, borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                                <div>
                                  <div className="heading" style={{ fontSize: 14, fontWeight: 900, color: C.text }}>{plan.title}</div>
                                  <div style={{ color: featured ? C.accent : C.textSecondary, fontSize: 18, fontWeight: 900, marginTop: 3 }}>{checkout.price}</div>
                                  {checkout.alternatePrice && checkout.alternatePrice !== checkout.price && <div style={{ color: checkout.alternateLabel === 'Monthly option' ? '#FFFFFF' : C.textMuted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>{checkout.alternateLabel}: {checkout.alternatePrice}</div>}
                                  {checkout.saving && <div style={{ color: C.success, fontSize: 11, fontWeight: 900, marginTop: 3 }}>{checkout.saving}</div>}
                                </div>
	                                <span style={{ borderRadius: 999, padding: '4px 8px', background: C.bgCard, color: C.textMuted, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>
	                                  {plan.badge || 'UPGRADE'}
                                </span>
                              </div>
                              <div style={{ display: 'grid', gap: 6 }}>
                                {plan.bullets.map(b => (
                                  <div key={b} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', color: C.textSecondary, fontSize: 12, fontWeight: 650, lineHeight: 1.35 }}>
	                                    <FontAwesomeIcon icon={faCheck} style={{ color: C.accent, fontSize: 11, marginTop: 2, flexShrink: 0 }} />
                                    <span>{b}</span>
                                  </div>
                                ))}
                                {plan.note && <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>{plan.note}</div>}
                              </div>
                              <button
	                                onClick={() => !checkout.priceKey ? null : startWorkoutCheckout(plan)}
	                                disabled={!checkout.priceKey || !priceConfigured || checkoutLoading === checkout.priceKey}
	                                style={{ marginTop: 'auto', border: 'none', borderRadius: 11, padding: '10px 12px', background: C.accentGrad, color: '#fff', cursor: !checkout.priceKey || !priceConfigured ? 'default' : 'pointer', opacity: !priceConfigured ? 0.55 : 1, fontSize: 12, fontWeight: 900 }}
	                              >
	                                {!priceConfigured ? 'Missing Stripe Price' : checkoutLoading === checkout.priceKey ? 'Opening Checkout...' : 'Upgrade'}
	                              </button>
                            </div>
                          );
	                        })}
	                      </div>
	                      )}
		                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: C.textMuted, fontSize: 13 }}>Loading access...</div>
              )}
            </div>

            {(profileData.sports || []).length > 0 && (
              <div>
                <div className="heading" style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 14 }}>🏅 Sport Profiles</div>
                <div className="sport-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
                  {(profileData.sports || []).map((sport, i) => <SportProfileCard key={i} C={C} sport={sport} />)}
                </div>
              </div>
            )}

            {(profileData.teams || []).length > 0 && (
              <div>
                <div className="heading" style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 14 }}>My Teams</div>
                <div className="sport-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
                  {(profileData.teams || []).map((team, i) => <TeamCard key={i} C={C} team={team} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ TAB: BOOKINGS ══ */}
        {activeTab === 'bookings' && (
          <div className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>History</div>
                <h2 className="heading" style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.text }}>My Bookings</h2>
              </div>
              <span style={{ fontSize: 12, color: C.textMuted }}>{bookings.length} total</span>
            </div>
            {bookings.length === 0 ? (
              <div style={card(C, { padding: '48px 24px', textAlign: 'center' })}><div style={{ fontSize: 36, marginBottom: 12 }}>📅</div><p style={{ color: C.textMuted }}>No bookings yet.</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {bookings.map((b) => {
                  const rawStatus = (b.status || 'upcoming').toLowerCase();
                  const st = statusConfig[rawStatus] || statusConfig.upcoming;
                  const details = bookingDetails[b.id];
                  const isOpen = expandedBooking === b.id;
                  return (
                    <div key={b.id} className="card-lift" style={card(C, { padding: 0, overflow: 'hidden', borderColor: isOpen ? C.accent : C.border })}>
                      <div onClick={() => toggleBooking(b.id)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>📍</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: C.text, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? '55vw' : 'none' }}>{b?.locations?.name || 'Location'}</div>
                            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{b.sport_id || 'Sport'} · {fmtDate(b.created_at)}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {b.total_price && <span className="heading" style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{fmtAmt(b.total_price)}</span>}
                          <span style={{ ...tagSt(C), background: st.bg, border: `1px solid ${st.border}`, color: st.color, padding: '4px 10px', fontSize: 11 }}>{st.label}</span>
                          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} style={{ fontSize: 11, color: C.textMuted }} />
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${C.border}` }}>
                          {details ? (
                            <div style={{ paddingTop: 12, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(200px,1fr))', gap: '6px 16px' }}>
                              {details.profiles && <div style={{ fontSize: 13, color: C.textSecondary }}><strong style={{ color: C.text }}>Booked by:</strong> {details.profiles.name} {details.profiles.surname}</div>}
                              {details.locations?.name && <div style={{ fontSize: 13, color: C.textSecondary }}><strong style={{ color: C.text }}>Location:</strong> {details.locations.name}</div>}
                              {Object.entries(details).filter(([k]) => !['id', 'profile_id', 'location_id', 'booking_partner_id', 'profiles', 'locations'].includes(k)).map(([k, v], j) => {
                                let disp = v; if (k === 'created_at') disp = fmtDate(v);
                                const lbl = k === 'sport_id' ? 'Sport' : k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                return (<div key={j} style={{ fontSize: 13, color: C.textSecondary }}><strong style={{ color: C.text }}>{lbl}:</strong> {String(disp)}</div>);
                              })}
                            </div>
                          ) : (<div style={{ paddingTop: 12, fontSize: 13, color: C.textMuted }}>Loading…</div>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ TAB: TRANSACTIONS ══ */}
        {activeTab === 'transactions' && (
          <div className="fade-in" style={{ display: 'grid', gap: 16 }}>
            {(() => {
              const supportBody = `Username: @${profileData.username || ''}\nEmail: ${profileData.email || ''}\nIssue:`;
              return (
                <>
                  <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 14, flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={{ minWidth: 0 }}>
                      <h2 className="heading" style={{ fontSize: isMobile ? 24 : 30, fontWeight: 950, color: C.text, margin: 0, letterSpacing: 0 }}>Transactions</h2>
                      <p style={{ color: C.textMuted, fontSize: isMobile ? 13 : 14, fontWeight: 750, marginTop: 5 }}>Manage your access, billing, and coin activity.</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                      <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 850 }}>{transactionFeed.length} total</span>
                      <button type="button" onClick={() => reportTransactionProblem()} style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, border: 'none', background: C.accentGrad, color: '#fff', fontWeight: 900, fontSize: 13, cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 12 }} /> Get support
                      </button>
                    </div>
                  </div>

                  {accountSummaryStats.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,minmax(0,1fr))', gap: 10 }} aria-label="Account summary">
                      {accountSummaryStats.map((stat) => (
                        <div key={stat.label} style={card(C, { padding: 14, boxShadow: C.shadowSm, display: 'grid', gridTemplateColumns: '34px 1fr', gap: 10, alignItems: 'center' })}>
                          <TxIcon C={C} iconKey={stat.icon === faCoins ? 'coins' : stat.icon === faCreditCard ? 'card' : stat.icon === faCalendar ? 'calendar' : 'receipt'} tone="info" size={34} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</div>
                            <div style={{ color: C.text, fontSize: 15, fontWeight: 950, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stat.value}</div>
                            {stat.meta && <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 750, marginTop: 1 }}>{stat.meta}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ padding: 16, borderRadius: 14, background: C.bgCard, border: `1px solid ${C.border}`, boxShadow: C.shadowSm, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '40px 1fr auto', gap: 12, alignItems: 'center' }}>
                    <TxIcon C={C} iconKey="issue" tone="warning" size={40} />
                    <div style={{ minWidth: 0 }}>
                      <div className="heading" style={{ color: C.text, fontSize: 16, fontWeight: 900 }}>Need help with a transaction?</div>
                      <div style={{ color: C.textSecondary, fontSize: 13, fontWeight: 750, lineHeight: 1.45, marginTop: 3 }}>Contact support with your username, email, approximate date, and what happened.</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                      <button type="button" onClick={() => reportTransactionProblem()} style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.accent, padding: '9px 12px', fontSize: 13, fontWeight: 900, cursor: 'pointer' }}>Email support</button>
                      <button type="button" onClick={() => copyToClipboard(supportBody, 'Support details')} style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 10, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textSecondary, padding: '9px 12px', fontSize: 13, fontWeight: 900, cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} /> Copy support details
                      </button>
                    </div>
                  </div>

                  <BillingOverviewPanel
                    C={C}
                    overview={billingOverview}
                    error={billingOverviewError || transactionsError}
                    isMobile={isMobile}
                    loading={transactionsLoading}
                    profileData={profileData}
                    onRetry={reloadTransactionData}
                    onCancelSubscription={confirmCancelTransactionSubscription}
                  />

                  <SectionFrame C={C} id="all-transactions-heading" title="All transactions" subtitle="Search, filter, and copy IDs from your full history.">
                    <div style={{ padding: 16, display: 'grid', gap: 12, borderBottom: `1px solid ${C.border}` }}>
                      <label style={{ position: 'relative', display: 'block' }}>
                        <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>Search transactions</span>
                        <FontAwesomeIcon icon={faMagnifyingGlass} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: C.textMuted }} />
                        <input value={transactionSearch} onChange={e => setTransactionSearch(e.target.value)} placeholder="Search transactions" type="search" style={{ ...inputSt(C), minHeight: 44, paddingLeft: 36 }} />
                      </label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : 'visible', paddingBottom: isMobile ? 4 : 0 }}>
                        {[
                          ['all', 'All'],
                          ['purchases', 'Purchases'],
                          ['refunds', 'Refunds'],
                          ['coins', 'Coins'],
                          ['access', 'Access'],
                          ['issues', 'Issues'],
                        ].map(([value, label]) => (
                          <button key={value} type="button" onClick={() => setTransactionFilter(value)} style={{ ...tagSt(C, transactionFilter === value), minHeight: 40, cursor: 'pointer', flex: '0 0 auto' }}>{label}</button>
                        ))}
                        <div style={{ marginLeft: isMobile ? 0 : 'auto', flex: isMobile ? '0 0 150px' : '0 0 170px' }}>
                          <SelectShell C={C}>
                            <select aria-label="Sort transactions" value={transactionSort} onChange={e => setTransactionSort(e.target.value)} style={{ minHeight: 44 }}>
                              <option value="newest">Newest first</option>
                              <option value="oldest">Oldest first</option>
                            </select>
                          </SelectShell>
                        </div>
                      </div>
                    </div>

                    {transactionsLoading ? (
                      <div aria-busy="true"><TransactionSkeletons C={C} rows={6} /></div>
                    ) : transactionsError ? (
                      <EmptyState C={C} icon={faTriangleExclamation} title="Transactions could not load" body="Refresh the page or contact support if this keeps happening." action={<button type="button" onClick={reloadTransactionData} style={{ marginTop: 6, minHeight: 44, border: 'none', borderRadius: 10, background: C.accentGrad, color: '#fff', padding: '11px 16px', fontWeight: 900, cursor: 'pointer' }}>Try again</button>} />
                    ) : transactionFeed.length === 0 ? (
                      <EmptyState C={C} icon={faReceipt} title="No transactions found" body="Try changing your filters or search." />
                    ) : filteredTransactions.length === 0 ? (
                      <EmptyState C={C} icon={faReceipt} title="No transactions found" body="Try changing your filters or search." />
                    ) : (
                      <>
                        <div style={{ display: 'grid' }}>
                          {visibleTransactions.map((tx, index) => {
                            const tone = getTransactionTone(tx);
                            const issue = isTransactionIssue(tx);
                            const shortId = shortenTransactionId(tx.id);
                            const amount = formatTransactionAmount(tx);
                            const primaryItem = getStoreOrderPrimaryItem(tx);
                            const discount = getTransactionDiscount(tx);
                            const discountSummary = formatTransactionDiscountSummary(tx);
                            const linkedSubscription = transactionSubscription(tx);
                            const canRequestRefund = ['purchase', 'deposit'].includes(String(tx.source || '').toLowerCase()) && String(tx.status || '').toLowerCase() !== 'refunded' && !isActivityRefundTransaction(tx);
                            const canCancelSubscription = !!linkedSubscription;
                            const coins = Number(tx.proam_coins || 0);
                            const moneyAmount = Number(tx.amount || 0);
                            const amountTone = issue || tone === 'danger' ? C.danger : (String(tx.source || '').toLowerCase() === 'refund' ? C.success : C.text);
                            const thumb = primaryItem?.image_url;
                            const title = primaryItem?.product_name || formatTransactionTitle(tx);
                            return (
                              <article key={tx.id || index} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '40px minmax(0,1fr) auto', gap: isMobile ? 10 : 12, alignItems: 'center', padding: isMobile ? '14px 16px' : '14px 18px', borderTop: index ? `1px solid ${C.border}` : 'none', background: C.bgCard }}>
                                {isMobile ? (
                                  <div style={{ display: 'grid', gap: 9 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) auto', gap: 10, alignItems: 'center' }}>
                                      {thumb ? (
                                        <img src={thumb} alt="" loading="lazy" style={{ width: 36, height: 36, borderRadius: 9, objectFit: 'contain', background: C.bgAlt, border: `1px solid ${C.border}` }} />
                                      ) : (
                                        <TxIcon C={C} iconKey={getTransactionIcon(tx)} tone={tone} size={36} />
                                      )}
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ color: C.text, fontSize: 14, fontWeight: 950, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{title}</div>
                                        <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, marginTop: 3 }}>{humanizeToken(tx.source, 'Transaction')} · {formatTransactionDate(tx.created_at)}</div>
                                        {discountSummary && <div style={{ color: C.success, fontSize: 11, fontWeight: 850, marginTop: 3 }}>{discountSummary}</div>}
                                      </div>
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={{ color: amountTone, fontSize: 14, fontWeight: 950, whiteSpace: 'nowrap' }}>{amount}</div>
                                        {discount?.originalLabel && <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 800, textDecoration: 'line-through', whiteSpace: 'nowrap' }}>{discount.originalLabel}</div>}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                                      <StatusPill C={C} tone={issue ? 'issue' : tone}>{formatTransactionStatus(tx)}</StatusPill>
                                      <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 850 }}>ID <strong style={{ color: C.textSecondary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{shortId}</strong></span>
                                      <button type="button" onClick={() => copyToClipboard(tx.id || '', 'Transaction ID')} aria-label={`Copy transaction ID ${shortId}`} disabled={!tx.id} style={{ minHeight: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, padding: '6px 9px', fontSize: 12, fontWeight: 900, cursor: tx.id ? 'pointer' : 'not-allowed' }}>Copy ID</button>
                                      {canRequestRefund && <button type="button" onClick={() => confirmTransactionRefund(tx)} style={{ minHeight: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, padding: '6px 9px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>Refund</button>}
                                      {canCancelSubscription && <button type="button" onClick={() => confirmCancelTransactionSubscription(linkedSubscription)} style={{ minHeight: 34, borderRadius: 8, border: '1px solid rgba(220,38,38,0.24)', background: 'rgba(220,38,38,0.08)', color: C.danger, padding: '6px 9px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>Cancel</button>}
                                      <button type="button" onClick={() => reportTransactionProblem(tx)} style={{ minHeight: 34, borderRadius: 8, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '6px 9px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>Report problem</button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {thumb ? (
                                      <img src={thumb} alt="" loading="lazy" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'contain', background: C.bgAlt, border: `1px solid ${C.border}` }} />
                                    ) : (
                                      <TxIcon C={C} iconKey={getTransactionIcon(tx)} tone={tone} size={40} />
                                    )}
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <h4 style={{ color: C.text, fontSize: 14, fontWeight: 950, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h4>
                                        <StatusPill C={C} tone={issue ? 'issue' : tone}>{formatTransactionStatus(tx)}</StatusPill>
                                        {discount?.code && <StatusPill C={C} tone="success">Discount {discount.code}</StatusPill>}
                                      </div>
                                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', color: C.textMuted, fontSize: 12, fontWeight: 750, marginTop: 5 }}>
                                        <span>{formatTransactionDate(tx.created_at)}</span>
                                        <span>ID <strong style={{ color: C.textSecondary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{shortId}</strong></span>
                                        {primaryItem?.quantity && <span>{primaryItem.quantity} item{Number(primaryItem.quantity) === 1 ? '' : 's'}</span>}
                                        {discountSummary && <span style={{ color: C.success }}>{discountSummary}</span>}
                                        {coins !== 0 && <span>{coins > 0 ? '+' : ''}{formatTxCoins(coins)}</span>}
                                        {moneyAmount !== 0 && <span>{formatMoney(moneyAmount)}</span>}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                                      <div style={{ minWidth: 92, textAlign: 'right' }}>
                                        <div style={{ color: amountTone, fontSize: 15, fontWeight: 950 }}>{amount}</div>
                                        {discount?.originalLabel && <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 800, textDecoration: 'line-through' }}>{discount.originalLabel}</div>}
                                      </div>
                                      <button type="button" onClick={() => copyToClipboard(tx.id || '', 'Transaction ID')} aria-label={`Copy transaction ID ${shortId}`} disabled={!tx.id} style={{ width: 36, minWidth: 36, height: 36, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, cursor: tx.id ? 'pointer' : 'not-allowed' }}>
                                        <FontAwesomeIcon icon={faCopy} style={{ fontSize: 12 }} />
                                      </button>
                                      {canRequestRefund && <button type="button" onClick={() => confirmTransactionRefund(tx)} style={{ minHeight: 36, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, padding: '8px 10px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>Refund</button>}
                                      {canCancelSubscription && <button type="button" onClick={() => confirmCancelTransactionSubscription(linkedSubscription)} style={{ minHeight: 36, borderRadius: 9, border: '1px solid rgba(220,38,38,0.24)', background: 'rgba(220,38,38,0.08)', color: C.danger, padding: '8px 10px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>Cancel</button>}
                                      <button type="button" onClick={() => reportTransactionProblem(tx)} style={{ minHeight: 36, borderRadius: 9, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '8px 10px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>Report problem</button>
                                      {!canRequestRefund && !canCancelSubscription && <button type="button" style={{ minHeight: 36, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, padding: '8px 10px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>{getPrimaryTransactionAction(tx)}</button>}
                                    </div>
                                  </>
                                )}
                              </article>
                            );
                          })}
                        </div>
                        {visibleTransactions.length < filteredTransactions.length && (
                          <div style={{ padding: 16, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'center' }}>
                            <button type="button" onClick={() => setVisibleTransactionCount(count => count + 20)} style={{ minHeight: 44, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, padding: '10px 16px', fontWeight: 900, cursor: 'pointer' }}>
                              Show more
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </SectionFrame>
                </>
              );
            })()}
          </div>
        )}

        {/* ══ TAB: INSTITUTES ══ */}
        {activeTab === 'institutes' && (
          <div className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>Management</div>
                <h2 className="heading" style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.text }}>My Institutes</h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {activeInst?.workspace_id && (
                  <button
                    type="button"
                    onClick={openWorkspaceRename}
                    aria-label={`Open settings for ${activeInst.workspace_name || 'workspace'}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textSecondary, padding: isMobile ? '10px 12px' : '10px 16px', borderRadius: 10, cursor: 'pointer', fontSize: isMobile ? 12 : 13.5, fontWeight: 800, fontFamily: "'DM Sans',sans-serif", minHeight: 44 }}
                  >
                    <FontAwesomeIcon icon={faPen} style={{ fontSize: 11, color: C.accent }} /> {isMobile ? 'Workspace' : 'Workspace Settings'}
                  </button>
                )}
                <button onClick={() => openAddInstituteModal('Challenge')} style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.bgAlt, border: `1px solid ${C.accentBorder}`, color: C.accent, padding: isMobile ? '10px 12px' : '10px 16px', borderRadius: 10, cursor: 'pointer', fontSize: isMobile ? 12 : 13.5, fontWeight: 800, fontFamily: "'DM Sans',sans-serif", minHeight: 44 }}>
                  <FontAwesomeIcon icon={faTrophy} style={{ fontSize: 11 }} /> {isMobile ? 'Challenge' : 'Create Challenge'}
                </button>
                <button onClick={() => openInstitutionCreatePage()} style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.accentGrad, border: 'none', color: '#fff', padding: isMobile ? '10px 14px' : '10px 18px', borderRadius: 10, cursor: 'pointer', fontSize: isMobile ? 12 : 13.5, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", minHeight: 44 }}>
                  <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} /> {isMobile ? 'New' : 'New Institute'}
                </button>
              </div>
            </div>

            {!isInstitutionDemoAccount && <div style={card(C, { padding: isMobile ? 14 : 18, marginBottom: 16, borderRadius: 18, borderColor: C.border })}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.35fr) minmax(260px,0.65fr)', gap: 14, alignItems: 'stretch' }}>
                <div style={{ borderRadius: 16, padding: isMobile ? 14 : 16, background: institutionAccessUnlocked ? C.bgAlt : 'rgba(217,119,6,0.10)', border: `1px solid ${institutionAccessUnlocked ? C.border : 'rgba(217,119,6,0.32)'}` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 28, padding: '5px 9px', borderRadius: 999, background: institutionAccessUnlocked ? 'rgba(34,197,94,0.12)' : 'rgba(217,119,6,0.14)', color: institutionAccessUnlocked ? '#15803D' : (C.warning || '#B45309'), fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        <FontAwesomeIcon icon={institutionAccessUnlocked ? faCheck : faLock} style={{ fontSize: 10 }} />
                        {isInstitutionDemoAccount ? 'Demo access' : institutionAccessUnlocked ? 'Access active' : 'Access inactive'}
                      </div>
                      <h3 className="heading" style={{ margin: '11px 0 6px', color: C.text, fontSize: isMobile ? 21 : 26, lineHeight: 1.08, fontWeight: 950, letterSpacing: 0 }}>
                        {isInstitutionDemoAccount ? 'Institution tools enabled' : institutionAccessUnlocked ? institutionAccessName || 'Institution access' : 'Renew membership to unlock institution steps'}
                      </h3>
                      <p style={{ margin: 0, maxWidth: 680, color: C.textSecondary, fontSize: 13, lineHeight: 1.5, fontWeight: 750 }}>
                        {isInstitutionDemoAccount
                          ? 'This demonstration includes institution dashboards, member steps, departments, activities, challenges and branded pages.'
                          : institutionAccessUnlocked
                          ? 'Institution dashboards, step values, departments and branded pages are available while access is active.'
                          : 'Steps continue saving for members. Step values stay hidden until membership is active again.'}
                      </p>
                    </div>
                    {!isInstitutionDemoAccount && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
	                      {canManageInstitutionBilling && (
	                        <button
	                          type="button"
	                          onClick={openInstitutionBillingPortal}
                          disabled={openingInstitutionBilling}
                          style={{ minHeight: 44, border: 'none', borderRadius: 11, padding: '10px 14px', background: C.accentGrad, color: '#fff', cursor: openingInstitutionBilling ? 'wait' : 'pointer', opacity: openingInstitutionBilling ? 0.72 : 1, fontSize: 13, fontWeight: 950, whiteSpace: 'nowrap', flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                        >
	                          {openingInstitutionBilling ? 'Opening billing...' : 'Manage or cancel billing'}
	                        </button>
	                      )}
	                      {institutionAccessUnlocked && !institutionPublicPricingHidden && (
	                        <button
	                          type="button"
	                          onClick={() => {
	                            setActiveInstituteView('plan');
	                            window.setTimeout(() => {
	                              document.getElementById('institution-plan-upgrade-options')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	                            }, 0);
	                          }}
	                          disabled={!institutionManagementPlans.length}
	                          style={{ minHeight: 44, border: 'none', borderRadius: 11, padding: '10px 14px', background: institutionManagementPlans.length ? C.accentGrad : C.bgAlt, color: institutionManagementPlans.length ? '#fff' : C.textMuted, cursor: institutionManagementPlans.length ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 950, whiteSpace: 'nowrap', flex: isMobile ? '1 1 100%' : '0 0 auto' }}
	                        >
	                          Upgrade
	                        </button>
	                      )}
	                      {!institutionRestartMode && !institutionAccessUnlocked && !institutionPublicPricingHidden && (
	                        <button
                          type="button"
                          onClick={() => {
                            if (showInstitutionPlans) toggleInstitutionPlans();
                            else openInstitutionPaymentOptions();
                          }}
                          style={{ minHeight: 44, border: `1px solid ${C.accentBorder}`, borderRadius: 11, padding: '10px 14px', background: canManageInstitutionBilling ? C.bgCard : C.accentGrad, color: canManageInstitutionBilling ? C.accent : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 950, whiteSpace: 'nowrap', flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                        >
                          {showInstitutionPlans ? 'Hide billing' : 'Choose billing'}
                        </button>
                      )}
                    </div>}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {(isInstitutionDemoAccount ? [
                    ['Step values', 'Available'],
                    ['Institutions', institutes.length ? `${institutes.length} active` : institutesLoading ? 'Loading…' : institutesError ? 'Unavailable' : 'None yet'],
                    ['Demo status', 'Ready'],
                  ] : [
                    ['Step values', institutionAccessUnlocked ? 'Unlocked' : 'Locked'],
                    ['Institutions', institutes.length ? `${institutes.length} active` : institutesLoading ? 'Loading…' : institutesError ? 'Unavailable' : 'None yet'],
                    [institutionAccessUnlocked && activeInstitutionSummary?.renewsAt ? 'Next renewal' : institutionAccessUnlocked && activeInstitutionSummary?.expiresAt ? 'Access until' : 'Next step', institutionAccessAction],
                  ]).map(([label, value]) => (
                    <div key={label} style={{ borderRadius: 13, border: `1px solid ${C.border}`, background: C.bgAlt, padding: '11px 12px' }}>
                      <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 950, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</div>
                      <div style={{ color: C.text, fontSize: 14, fontWeight: 950, lineHeight: 1.2, marginTop: 4 }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {institutionPublicPricingHidden && !isInstitutionDemoAccount && (
                <div role="note" style={{ marginTop: 16, padding: 16, borderRadius: 14, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.textSecondary, lineHeight: 1.5 }}>
                  For institution setup or plan changes, <a href="/support" style={{ color: C.accent, fontWeight: 900 }}>contact the Snows ProAm team</a>. Your current institution access remains available here.
                </div>
              )}

              {!institutionPublicPricingHidden && !isInstitutionDemoAccount && institutionRestartMode && institutionRestartLockedPlan && (() => {
                const restartProductKey = institutionBillingCycle === 'yearly' ? institutionRestartLockedPlan.yearlyProductKey : institutionRestartLockedPlan.monthlyProductKey;
                const restartPrice = institutionBillingCycle === 'yearly' ? institutionRestartLockedPlan.yearlyPrice : institutionRestartLockedPlan.monthlyPrice;
                const alternatePrice = institutionBillingCycle === 'yearly' ? institutionRestartLockedPlan.monthlyPrice : institutionRestartLockedPlan.yearlyPrice;
                const restartLoading = institutionCheckoutLoading === `institution:${restartProductKey}`;
                const restartHighlights = [
                  institutionRestartLockedPlan.size,
                  ...(institutionRestartLockedPlan.bullets || []).filter(item => /dashboard|department|team|branding|analytics|live/i.test(item)).slice(0, 4),
                ].filter(Boolean);
                return (
                  <div id="institution-access-payment-options" style={{ marginTop: 16, padding: 14, borderRadius: 16, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) auto', gap: 14, alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>Saved plan</div>
                      <div className="heading" style={{ color: C.text, fontSize: isMobile ? 22 : 26, fontWeight: 950, lineHeight: 1.08, letterSpacing: 0 }}>{institutionPlanDisplayName(institutionRestartLockedPlan)}</div>
                      <div style={{ color: C.textSecondary, fontSize: 13, fontWeight: 750, lineHeight: 1.4, marginTop: 7 }}>
                        Renew the existing membership. No new institute will be created.
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                        <span style={{ color: C.text, fontSize: isMobile ? 24 : 30, fontWeight: 950, lineHeight: 1 }}>{restartPrice}</span>
                        {alternatePrice && <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 850 }}>{institutionBillingCycle === 'yearly' ? 'Monthly' : 'Yearly'}: {alternatePrice}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 11 }}>
                        {restartHighlights.map(item => (
                          <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '6px 9px', background: C.bgCard, border: `1px solid ${C.border}`, color: C.textSecondary, fontSize: 12, fontWeight: 850 }}>
                            <FontAwesomeIcon icon={faCheck} style={{ color: C.accent, fontSize: 10 }} />
                            {planItemState(item).label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gap: 9, minWidth: isMobile ? 0 : 220 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 13, padding: 3 }}>
                        {[
                          ['monthly', 'Monthly'],
                          ['yearly', 'Yearly'],
                        ].map(([value, label]) => (
                          <button key={value} type="button" onClick={() => setInstitutionBillingCycle(value)} aria-pressed={institutionBillingCycle === value} style={{ border: 'none', borderRadius: 10, minHeight: 40, padding: '8px 10px', background: institutionBillingCycle === value ? C.accentGrad : 'transparent', color: institutionBillingCycle === value ? '#fff' : C.textSecondary, fontSize: 13, fontWeight: 950, cursor: 'pointer' }}>{label}</button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => startInstitutionCheckout(institutionRestartLockedPlan)}
                        disabled={restartLoading}
                        style={{ minHeight: 44, border: 'none', borderRadius: 11, padding: '11px 14px', background: C.accentGrad, color: '#fff', cursor: restartLoading ? 'wait' : 'pointer', opacity: restartLoading ? 0.72 : 1, fontSize: 13, fontWeight: 950 }}
                      >
                        {restartLoading ? 'Opening checkout...' : 'Renew membership'}
                      </button>
                      {institutionRenewUpgradePlans.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowInstitutionPlans(open => !open)}
                          style={{ minHeight: 42, border: `1px solid ${C.accentBorder}`, borderRadius: 11, padding: '10px 14px', background: C.bgCard, color: C.accent, cursor: 'pointer', fontSize: 13, fontWeight: 950 }}
                        >
                          {showInstitutionPlans ? 'Hide upgrades' : 'Upgrade'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {!institutionPublicPricingHidden && !isInstitutionDemoAccount && showInstitutionPlans && !institutionAccessUnlocked && (!institutionRestartMode || institutionRenewUpgradePlans.length > 0) && (
                <div id="institution-access-payment-options" style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) auto', gap: 12, alignItems: 'start', marginBottom: 12 }}>
                    <div>
                      <div className="heading" style={{ color: C.text, fontSize: isMobile ? 18 : 20, fontWeight: 950, lineHeight: 1.15 }}>{institutionRestartMode ? 'Upgrade membership' : 'Choose membership'}</div>
                      <div style={{ color: C.textSecondary, fontSize: 13, fontWeight: 750, lineHeight: 1.45, marginTop: 4 }}>
                        {institutionRestartMode
                          ? 'Choose a higher plan for the saved institution. Members, invite codes and history stay in place.'
                          : institutionStartExistingMode
                            ? 'Choose a plan for the existing institution. No new workspace will be created.'
                            : 'Pick a plan first. The next page follows the full institute setup structure.'}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gap: 8, justifyItems: isMobile ? 'stretch' : 'end' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 13, padding: 3, minWidth: isMobile ? '100%' : 238 }}>
                      {[
                        ['monthly', 'Monthly'],
                        ['yearly', 'Yearly'],
                      ].map(([value, label]) => (
                        <button key={value} type="button" onClick={() => setInstitutionBillingCycle(value)} aria-pressed={institutionBillingCycle === value} style={{ border: 'none', borderRadius: 10, minHeight: 40, padding: '8px 12px', background: institutionBillingCycle === value ? C.accentGrad : 'transparent', color: institutionBillingCycle === value ? '#fff' : C.textSecondary, fontSize: 13, fontWeight: 950, cursor: 'pointer' }}>{label}</button>
                      ))}
                      </div>
                      {institutes.length > 0 && (
                        <button
                          type="button"
                          onClick={openInstitutionBillingPortal}
                          disabled={openingInstitutionBilling}
                          style={{ justifySelf: isMobile ? 'stretch' : 'end', background: C.bgAlt, border: `1px solid ${C.border}`, color: C.accent, minHeight: 38, padding: '8px 12px', borderRadius: 10, cursor: openingInstitutionBilling ? 'wait' : 'pointer', fontSize: 12, fontWeight: 900 }}
                        >
                          {openingInstitutionBilling ? 'Opening billing...' : 'Manage or cancel billing'}
                        </button>
                      )}
                    </div>
                  </div>
                  {institutionStartExistingMode ? (
                    <div role="note" style={{ marginBottom: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.text, borderRadius: 12, padding: '10px 12px', fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                      No previous paid plan is saved for this institution. Pick a plan here and it will attach to the existing institution.
                    </div>
                  ) : institutionRestartMode ? (
                    <div role="note" style={{ marginBottom: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.text, borderRadius: 12, padding: '10px 12px', fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                      Upgrade keeps the existing institution and renews it on a higher membership plan.
                    </div>
                  ) : (
                    <div style={{ display: 'inline-flex', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 3, marginBottom: 10, maxWidth: '100%', overflowX: 'auto' }}>
                      {['Social', 'Club', 'Business'].map(value => (
                        <button key={value} type="button" onClick={() => setInstitutionPlanSegment(value)} style={{ border: 'none', borderRadius: 9, padding: '8px 12px', background: institutionPlanSegment === value ? C.accentGrad : 'transparent', color: institutionPlanSegment === value ? '#fff' : C.textSecondary, fontSize: 11, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}>{value}</button>
                      ))}
                    </div>
                  )}
                  {institutionCheckoutMessage && (
                    <div role="status" style={{ marginBottom: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.text, borderRadius: 12, padding: '10px 12px', fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                      {institutionCheckoutMessage}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : displayedInstitutionWebPlans.length === 1 ? 'minmax(230px,420px)' : 'repeat(auto-fit,minmax(230px,1fr))', justifyContent: displayedInstitutionWebPlans.length === 1 ? 'center' : 'stretch', gap: 10 }}>
                    {displayedInstitutionWebPlans.map(plan => {
                      const sourcePlan = plan.sourcePlan || plan;
                      const productKey = institutionBillingCycle === 'yearly' ? sourcePlan.yearlyProductKey : sourcePlan.monthlyProductKey;
                      const active = institutionAccessUnlocked && (activeInstitutionPlanKeys.has(productKey) || activeInstitutionSummary?.tier === sourcePlan.tier);
                      const featured = plan.featured;
                      const businessDemoPlan = institutionBusinessDemoMode && (plan.isBusinessDemoCard || isBusinessInstitutionPlan(plan));
                      const planTone = plan.segment === 'Business' ? C.success : C.accent;
                      const price = institutionBillingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
                      const altPrice = institutionBillingCycle === 'yearly' ? plan.monthlyPrice : plan.yearlyPrice;
                      const usesExistingInstituteCheckout = !active && (institutionRestartMode || institutionStartExistingMode);
                      const loadingKey = `institution:${productKey}`;
                      const buttonDisabled = active || (!businessDemoPlan && institutionCheckoutLoading === loadingKey);
                      const buttonLabel = active
                        ? 'Current Plan'
                        : businessDemoPlan
                          ? 'Book a Demo'
                        : institutionCheckoutLoading === loadingKey
                          ? 'Opening Checkout...'
                          : institutionRestartMode
                            ? 'Upgrade Membership'
                            : institutionStartExistingMode
                              ? 'Start Membership'
                            : plan.cta;
                      const displayPlanName = institutionPlanDisplayName(plan);
                      return (
                        <div key={plan.tier} style={{ background: featured ? C.accentBgSubtle : C.bgAlt, border: `1px solid ${active || featured ? C.accentBorder : C.border}`, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
                          <div style={{ display: 'grid', justifyItems: 'center', gap: 5, width: '100%', paddingBottom: 12, borderBottom: `1px solid ${C.border}`, textAlign: 'center' }}>
                            <div style={{ color: planTone, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{plan.segment}</div>
                            <div className="heading" style={{ fontSize: 20, lineHeight: 1.14, fontWeight: 950, color: C.text, letterSpacing: 0, overflowWrap: 'anywhere' }}>{displayPlanName}</div>
                            <div style={{ color: featured ? C.accent : C.text, fontSize: 25, lineHeight: 1.1, fontWeight: 950, marginTop: 2 }}>{price}</div>
                            {altPrice && !businessDemoPlan && <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 850 }}>{institutionBillingCycle === 'yearly' ? 'Monthly option' : 'Yearly option'}: {altPrice}</div>}
                            <div style={{ color: planTone, fontSize: 13, fontWeight: 950, marginTop: 2 }}>{plan.size}</div>
                            {plan.perMember && !businessDemoPlan && <div style={{ color: planTone, fontSize: 12, fontWeight: 900 }}>{plan.perMember}</div>}
                            {!businessDemoPlan && (
                              <span style={{ borderRadius: 999, padding: '5px 9px', background: active ? 'rgba(52,211,153,0.12)' : C.bgCard, color: active ? C.success : C.textMuted, fontSize: 11, fontWeight: 950, whiteSpace: 'nowrap', marginTop: 2 }}>
                                {active ? 'ACTIVE' : plan.badge || 'PLAN'}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'grid', gap: 7, width: '100%', maxWidth: 260, justifySelf: 'center', alignSelf: 'center', textAlign: 'left' }}>
                            {plan.bullets.map(b => {
                              const state = planItemState(b);
                              return (
                                <div key={b} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: C.textSecondary, fontSize: 12.5, fontWeight: 750, lineHeight: 1.35 }}>
                                  <FontAwesomeIcon icon={state.unavailable ? faTimes : state.addon ? faPlus : faCheck} style={{ color: state.unavailable ? C.danger : state.addon ? C.warn : C.accent, fontSize: 11, width: 12, marginTop: 2, flexShrink: 0 }} />
                                  <span>{state.label}</span>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (active) return;
                              if (businessDemoPlan) {
                                openInstitutionDemoRequest(sourcePlan);
                                return;
                              }
                              if (usesExistingInstituteCheckout) startInstitutionCheckout(sourcePlan);
                              else openInstitutionCreatePage({ planTier: sourcePlan.tier, billingCycle: institutionBillingCycle });
                            }}
                            disabled={buttonDisabled}
                            style={{ marginTop: 'auto', width: '100%', border: 'none', borderRadius: 11, minHeight: 42, padding: '10px 12px', background: active ? C.bgCard : C.accentGrad, color: active ? C.success : '#fff', cursor: buttonDisabled ? 'default' : 'pointer', opacity: institutionCheckoutLoading === loadingKey ? 0.72 : 1, fontSize: 13, fontWeight: 950 }}
                          >
                            {buttonLabel}
                          </button>
                        </div>
                      );
	                        })}
	                      </div>
	                      )}
	                    </div>
              )}
            </div>}

            {archivedInstitutes.length > 0 && (
              <section aria-label="Archived institutes" style={{ ...card(C, { padding: '14px 16px', marginBottom: 16 }), borderColor: C.borderMedium }}>
                <button type="button" onClick={() => setArchivedInstitutesOpen(open => !open)} aria-expanded={archivedInstitutesOpen} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: 0, padding: 0, background: 'transparent', color: C.text, cursor: 'pointer', textAlign: 'left' }}>
                  <span className="heading" style={{ fontSize: 14, fontWeight: 900 }}>Archived institutes ({archivedInstitutes.length})</span>
                  <FontAwesomeIcon icon={archivedInstitutesOpen ? faChevronUp : faChevronDown} style={{ color: C.textMuted, fontSize: 12 }} />
                </button>
                {archivedInstitutesOpen && (
                  <>
                    <p style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5, margin: '8px 0 10px' }}>Restore an institute to bring it back on web. Members may need to be reassigned to departments.</p>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {archivedInstitutes.map(inst => (
                        <div key={inst.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 10, borderRadius: 10, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: C.textSecondary, fontSize: 13, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.name}</div>
                            <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{inst.institute_type === 'Challenge' ? 'Challenge' : inst.teams ? `${institutionSubgroupLabel({ ...inst, type: inst.institute_type })}s` : 'Members'}</div>
                          </div>
                          <button type="button" onClick={() => restoreInstitute(inst)} disabled={restoringInstituteId === inst.id} style={{ minHeight: 36, flexShrink: 0, padding: '8px 11px', borderRadius: 9, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, cursor: restoringInstituteId === inst.id ? 'wait' : 'pointer', opacity: restoringInstituteId === inst.id ? 0.7 : 1, fontSize: 12, fontWeight: 900 }}>{restoringInstituteId === inst.id ? 'Restoring...' : 'Restore'}</button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>
            )}

            {institutesLoading && institutes.length === 0 ? (
              <div role="status" style={card(C, { padding: '48px 24px', textAlign: 'center', color: C.textSecondary })}>Loading your institutes…</div>
            ) : institutesError && institutes.length === 0 ? (
              <div role="alert" style={card(C, { padding: '32px 24px', textAlign: 'center' })}>
                <p style={{ color: C.textSecondary, margin: '0 0 14px' }}>{institutesError}</p>
                <button type="button" onClick={() => setInstituteRefresh(value => value + 1)} style={{ minHeight: 44, border: 0, borderRadius: 10, padding: '10px 16px', background: C.accentGrad, color: '#fff', fontWeight: 800, cursor: 'pointer' }}>Retry loading</button>
              </div>
            ) : institutes.length === 0 ? (
              <div style={card(C, { padding: '48px 24px', textAlign: 'center' })}>
                <FontAwesomeIcon icon={faBuilding} style={{ fontSize: 34, color: C.accent, marginBottom: 12 }} />
                <h3 className="heading" style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: C.text }}>No institutes yet</h3>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => openAddInstituteModal('Challenge')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.bgAlt, border: `1px solid ${C.accentBorder}`, color: C.accent, padding: '12px 18px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 800, minHeight: 44 }}>
                    <FontAwesomeIcon icon={faTrophy} style={{ fontSize: 12 }} /> Create Challenge
                  </button>
                  <button onClick={() => openInstitutionCreatePage()} style={{ background: C.accentGrad, border: 'none', color: '#fff', padding: '12px 22px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, minHeight: 44 }}>+ Create Institute</button>
                </div>
              </div>
            ) : (
              <>
                {institutesError && <div role="alert" style={{ ...card(C, { padding: 12, marginBottom: 12 }), color: C.textSecondary, fontSize: 13 }}>
                  {institutesError} <button type="button" onClick={() => setInstituteRefresh(value => value + 1)} style={{ border: 0, background: 'transparent', color: C.accent, fontWeight: 900, cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
                </div>}
                {instituteMembersLoading && <div role="status" style={{ color: C.textSecondary, fontSize: 13, marginBottom: 12 }}>Loading institute members…</div>}
                {/* Institute picker */}
                {(workspacePickerOptions.length > 1 || hasUnassignedInstitutes) && (
                  <section aria-label="Workspace filter" style={{ ...card(C, { padding: isMobile ? 12 : '12px 14px', marginBottom: 12, borderRadius: 14 }), display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="heading" style={{ color: C.text, fontSize: 13, fontWeight: 900 }}>Workspace</div>
                      <p style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.4, margin: '3px 0 0' }}>
                        Showing institutes in {selectedWorkspaceName}. Switch workspace to keep each organisation separate.
                      </p>
                    </div>
                    <label style={{ display: 'grid', gap: 5, minWidth: isMobile ? '100%' : 250, color: C.textMuted, fontSize: 11, fontWeight: 900, letterSpacing: 0.25, textTransform: 'uppercase' }}>
                      Select workspace
                      <select
                        value={selectedWorkspaceId}
                        onChange={(event) => changeWorkspaceFilter(event.target.value)}
                        aria-label="Select institution workspace"
                        style={{ width: '100%', minHeight: 42, borderRadius: 10, border: `1px solid ${C.accentBorder}`, background: C.bgAlt, color: C.text, padding: '8px 10px', fontSize: 13, fontWeight: 850, cursor: 'pointer' }}
                      >
                        {workspacePickerGroups.map((group) => (
                          <optgroup key={group.type} label={group.label}>
                            {group.options.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}
                          </optgroup>
                        ))}
                        {hasUnassignedInstitutes && <option value="__unassigned__">Other institutes</option>}
                      </select>
                    </label>
                  </section>
                )}
                <div style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '2px 2px 10px', marginBottom: 16, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                  {visibleInstituteCards.map(({ inst, idx }) => {
                    const selected = idx === activeInstIdx;
                    return (
                      <div key={inst.id} className="card-lift inst-card" onClick={() => { setActiveInstIdx(idx); applyInstituteRankLocation(inst); setExpandedDept(null); if (inst.teams) fetchDepts(inst.id); }}
                        style={card(C, { padding: 0, minWidth: isMobile ? 220 : 260, maxWidth: isMobile ? 230 : 288, flexShrink: 0, cursor: 'pointer', overflow: 'hidden', borderRadius: 18, borderColor: selected ? C.accent : C.border, boxShadow: selected ? '0 18px 42px rgba(12,105,200,0.18)' : C.shadowMd, background: selected ? `linear-gradient(180deg, ${C.bgCard}, ${C.accentBgSubtle})` : C.bgCard })}>
                        <div style={{ position: 'relative' }}>
                          <InstituteImage C={C} institute={inst} iconSize={34} style={{ width: '100%', height: isMobile ? 142 : 158 }} />
                          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 35%, rgba(0,0,0,0.28) 100%)', pointerEvents: 'none' }} />
                          {instituteLogoUrl(inst) && instituteLogoUrl(inst) !== instituteImageUrl(inst) && (
                            <InstituteBrandLogo src={instituteLogoUrl(inst)} alt={`${inst.name} logo`} style={{ position: 'absolute', right: 9, bottom: 9, width: 45, height: 45, padding: 4, borderRadius: 9, background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }} />
                          )}
                          <div style={{ position: 'absolute', left: 9, bottom: 9, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 'calc(100% - 18px)', padding: '5px 9px', borderRadius: 999, background: C.mode === 'dark' ? 'rgba(10,29,68,0.88)' : 'rgba(255,255,255,0.92)', color: selected ? (C.mode === 'dark' ? '#FFFFFF' : C.accent) : (C.mode === 'dark' ? C.textSecondary : C.textSecondary), border: `1px solid ${selected ? C.accentBorder : C.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.5)'}`, fontSize: 10.5, lineHeight: 1.1, fontWeight: 900, backdropFilter: 'blur(10px)' }}>
                            <FontAwesomeIcon icon={inst.teams ? faBuilding : faUsers} style={{ fontSize: 10, flexShrink: 0 }} />
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{instituteStructureLabel(inst)}</span>
                          </div>
                          {selected && (
                            <div style={{ position: 'absolute', top: 9, left: 9, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 999, background: C.accent, color: '#fff', fontSize: 10, fontWeight: 900 }}>
                              <FontAwesomeIcon icon={faCheck} style={{ fontSize: 9 }} />
                              Active
                            </div>
                          )}
                          <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 5, padding: 4, borderRadius: 12, background: 'rgba(10,29,68,0.46)', backdropFilter: 'blur(10px)' }}>
                            <button onClick={e => { e.stopPropagation(); openEditInstituteModal(inst); }}
                              aria-label={`Edit ${inst.name}`}
                              style={imageOverlayIconButtonSt(C)}>
                              <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                            </button>
                            <button onClick={e => { e.stopPropagation(); deleteInstitute(inst.id); }}
                              aria-label={`Archive ${inst.name}`}
                              style={imageOverlayIconButtonSt(C, 'danger')}>
                              <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                            </button>
                          </div>
                        </div>
                        <div style={{ padding: '13px 14px 14px' }}>
                          <div className="heading" style={{ fontSize: 14.5, lineHeight: 1.25, fontWeight: 900, color: C.text, marginBottom: 7, overflowWrap: 'anywhere' }}>{inst.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: C.textMuted, fontSize: 12, lineHeight: 1.2, fontWeight: 800, minWidth: 0 }}>
                            <TypeIcon type={inst.type} style={{ fontSize: 11, marginRight: 0 }} />
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.type}</span>
                          </div>
                          {inst.code && (
                            <button type="button" onClick={e => { e.stopPropagation(); copyToClipboard(inst.code, 'Institute invite code'); }} aria-label={`Copy invite code ${inst.code} for ${inst.name}`} style={{ marginTop: 9, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', minHeight: 30, borderRadius: 999, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '5px 9px', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faCopy} style={{ fontSize: 10, flexShrink: 0 }} />
                              <span style={{ color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Code</span>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.code}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {activeInst && (
                  <>
                    {showDemoAppSessionPreview && (
                      <button
                        type="button"
                        onClick={() => setAppLeaderboardPreviewMode(activeInst.teams ? 'departments' : 'members')}
                        aria-label="Open demo app leaderboard preview"
                        title="Open demo app leaderboard preview"
                        style={{
                          position: 'fixed',
                          right: isMobile ? 16 : 24,
                          bottom: isMobile ? 148 : 94,
                          zIndex: 1400,
                          width: 58,
                          height: 58,
                          borderRadius: 18,
                          border: '1px solid rgba(255,255,255,0.28)',
                          background: 'linear-gradient(135deg, #D4AF37 0%, #0A1D44 100%)',
                          color: '#fff',
                          boxShadow: '0 14px 36px rgba(10,29,68,0.32)',
                          cursor: 'pointer',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        <FontAwesomeIcon icon={faTrophy} style={{ fontSize: 22 }} />
                      </button>
                    )}
                    {appLeaderboardPreviewMode && (
                      <LeaderboardAppPreview
                        C={C}
                        mode={appLeaderboardPreviewMode}
                        activeInst={activeInst}
                        members={allMembers}
                        departments={filteredDepts}
                        overallRows={peerBoard}
                        branding={activeLeaderboardBranding}
                        locked={activeInstitutionStepsLocked}
                        deptLabel={deptLabel}
                        memberLabel={memberLabel}
                        onClose={() => setAppLeaderboardPreviewMode(null)}
                      />
                    )}
                    {/* Active institute header */}
                    <div style={card(C, { padding: isMobile ? '12px' : '14px 16px', marginBottom: 14 })}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <InstituteImage C={C} institute={activeInst} fit={activeInstituteHeaderUsesLogo ? 'contain' : 'cover'} style={activeInstituteHeaderUsesLogo ? { width: isMobile ? 76 : 86, height: isMobile ? 76 : 86, borderRadius: 14, border: `1px solid ${C.border}`, padding: 6, flexShrink: 0 } : { width: isMobile ? 104 : 132, height: isMobile ? 66 : 78, borderRadius: 12, border: `1px solid ${C.border}`, flexShrink: 0 }} />
                        {instituteLogoUrl(activeInst) && instituteLogoUrl(activeInst) !== instituteImageUrl(activeInst) && (
                          <InstituteBrandLogo src={instituteLogoUrl(activeInst)} alt={`${activeInst.name} logo`} style={{ width: isMobile ? 42 : 54, height: isMobile ? 42 : 54, padding: 4, borderRadius: 10, background: '#FFFFFF', border: `1px solid ${C.border}`, flexShrink: 0 }} />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h3 className="heading" style={{ fontSize: isMobile ? 14 : 16, lineHeight: 1.2, fontWeight: 800, color: C.text, overflowWrap: 'anywhere' }}>{activeInst.name}</h3>
                          <div style={{ display: 'flex', gap: 7, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ ...tagSt(C, true), pointerEvents: 'none', fontSize: 10.5, maxWidth: '100%', lineHeight: 1.2 }}><TypeIcon type={activeInst.type} style={{ fontSize: 10 }} />{activeInst.type}</span>
                            <span style={{ ...tagSt(C), pointerEvents: 'none', fontSize: 10.5, maxWidth: '100%', lineHeight: 1.2 }}>
                              <FontAwesomeIcon icon={activeInst.teams ? faBuilding : faUsers} style={{ fontSize: 10, marginRight: 5 }} />
                              {instituteStructureLabel(activeInst)}
                            </span>
                            {activeInst.code && (
                              <button type="button" onClick={() => copyToClipboard(activeInst.code, 'Institute invite code')} aria-label={`Copy invite code ${activeInst.code} for ${activeInst.name}`} style={{ ...tagSt(C), cursor: 'pointer', fontSize: 10.5, maxWidth: '100%', lineHeight: 1.2, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <FontAwesomeIcon icon={faCopy} style={{ fontSize: 10 }} />
                                <span>Invite {activeInst.code}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile || activeInst.type === 'Challenge' ? '1fr' : 'auto minmax(0, 1fr)', alignItems: 'center', gap: 8, minHeight: isMobile ? 0 : 48, marginTop: 12, padding: 8, borderRadius: 13, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                          <button
                            type="button"
                            onClick={openLiveDashboard}
                            disabled={openingLiveDashboard}
                            style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: C.accentGrad, border: 'none', color: '#fff', padding: '9px 13px', borderRadius: 10, cursor: openingLiveDashboard ? 'wait' : 'pointer', opacity: openingLiveDashboard ? 0.72 : 1, fontSize: 12, fontWeight: 900, fontFamily: "'DM Sans',sans-serif", width: isMobile ? '100%' : 'auto', whiteSpace: 'nowrap' }}
                          >
                            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 12 }} /> {openingLiveDashboard ? 'Opening...' : 'Live Dashboard'}
                          </button>
                          <button
                            type="button"
                            onClick={confirmResetInstitutionSteps}
                            disabled={resettingInstitutionSteps || !activeInst?.id}
                            aria-label={`Reset steps for ${activeInst.name}`}
                            style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.24)', color: C.danger, padding: '9px 13px', borderRadius: 10, cursor: resettingInstitutionSteps ? 'wait' : 'pointer', opacity: resettingInstitutionSteps ? 0.72 : 1, fontSize: 12, fontWeight: 900, fontFamily: "'DM Sans',sans-serif", width: isMobile ? '100%' : 'auto', whiteSpace: 'nowrap' }}
                          >
                            <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 12 }} /> {resettingInstitutionSteps ? 'Resetting...' : 'Reset steps'}
                          </button>
                        </div>
                        {activeInst.type !== 'Challenge' && <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0, width: '100%', justifySelf: 'stretch' }}>
                          {scope !== 'global' && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                              <SelectShell C={C} style={{ width: isMobile ? '100%' : 190, fontSize: 13, background: C.bgCard }}>
                                <select
                                  value={rankCountryCode}
                                  onChange={e => chooseRankCountry(e.target.value)}
                                  autoComplete="country-name"
                                  aria-label="Leaderboard country"
                                >
                                  <option value="">Select country</option>
                                  {COUNTRIES.map(item => <option key={item.isoCode} value={item.isoCode}>{item.name}</option>)}
                                </select>
                              </SelectShell>
                              {scope === 'regional' && (
                                rankRegionOptions.length > 0 ? (
                                  <SelectShell C={C} style={{ width: isMobile ? '100%' : 190, fontSize: 13, background: C.bgCard }}>
                                    <select
                                      value={rankRegion}
                                      onChange={e => setRankRegion(e.target.value)}
                                      autoComplete="address-level1"
                                      aria-label="Leaderboard region"
                                    >
                                      <option value="">Select region</option>
                                      {rankRegionOptions.map(item => <option key={item.isoCode} value={item.name}>{item.name}</option>)}
                                    </select>
                                  </SelectShell>
                                ) : (
                                  <input value={rankRegion} onChange={e => setRankRegion(e.target.value)} placeholder="Region / state" style={{ ...inputSt(C), width: isMobile ? '100%' : 190, fontSize: 13, background: C.bgCard }} autoComplete="address-level1" aria-label="Leaderboard region" />
                                )
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 3, background: C.bgCard, border: `1px solid ${C.border}`, padding: 3, borderRadius: 99, width: isMobile ? '100%' : 'auto' }}>
                            {['regional', 'national', 'global'].map(s => (
                              <button key={s} onClick={() => setScope(s)} style={{ flex: isMobile ? 1 : '0 0 auto', border: 'none', background: scope === s ? C.accentGrad : 'transparent', color: scope === s ? '#fff' : C.textSecondary, fontWeight: 700, fontSize: 11, padding: '7px 10px', borderRadius: 99, cursor: 'pointer', textTransform: 'capitalize', transition: 'all 0.15s ease' }}>{s}</button>
                            ))}
                          </div>
                        </div>}
                      </div>
                      {activeInst.teams && (
                        <div style={{ marginTop: 10 }}>
                          <input value={instQuery} onChange={e => setInstQuery(e.target.value)} placeholder={`Search ${deptLabel}s…`} style={{ ...inputSt(C), fontSize: 13 }} />
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: 4, marginBottom: 14, borderRadius: 14, background: C.bgAlt, border: `1px solid ${C.border}`, scrollbarWidth: 'none' }}>
                      {activeInstituteViews.map(view => {
                        const selected = activeInstituteView === view.id;
                        return (
                          <button
                            key={view.id}
                            type="button"
                            onClick={() => setActiveInstituteView(view.id)}
                            aria-pressed={selected}
                            style={{ minHeight: 40, flex: isMobile ? '0 0 auto' : 1, border: 'none', borderRadius: 11, padding: '9px 12px', background: selected ? C.accentGrad : 'transparent', color: selected ? '#fff' : C.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, whiteSpace: 'nowrap' }}
                          >
                            <FontAwesomeIcon icon={view.icon} style={{ fontSize: 11 }} />
                            {view.label}
                          </button>
                        );
                      })}
                    </div>
                    {showDemoAppSessionPreview && (
                      <div aria-label="Demo app leaderboard previews" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center', justifyContent: isMobile ? 'stretch' : 'flex-end', marginBottom: 14 }}>
                        {[
                          { id: 'members', label: `${memberLabel}s app`, icon: faUsers },
                          ...(activeInst.teams ? [{ id: 'departments', label: `${deptLabel}s app`, icon: faBuilding }] : []),
                          { id: 'overall', label: 'Overall app', icon: faTrophy },
                        ].map(preview => (
                          <button key={preview.id} type="button" onClick={() => setAppLeaderboardPreviewMode(preview.id)} style={{ minHeight: 36, flex: isMobile ? '1 1 100%' : '0 0 auto', borderRadius: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '8px 11px', cursor: 'pointer', fontSize: 12, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                            <FontAwesomeIcon icon={preview.icon} style={{ fontSize: 11 }} />
                            {preview.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Stats 4-grid */}
                    {(() => {
                      const metricColumns = isMobile ? 2 : 3;
                      const metrics = [
                        { icon: faBolt, label: 'Total Steps', value: institutionStepText(activeInstitutionStepsLocked, totals.totalSteps) },
                        { icon: faChartLine, label: 'Avg Steps', value: institutionStepText(activeInstitutionStepsLocked, totals.avgSteps) },
                        { icon: faUsers, label: activeInst.type === 'Challenge' ? 'Participants' : activeInst.teams ? 'People assigned' : `${memberLabel}s`, value: activeInst.type === 'Challenge' && activeChallengeLimit != null ? `${numberFmt(totals.memberCount)} / ${numberFmt(activeChallengeLimit)}` : totals.memberCount },
                        ...(activeInst.type === 'Challenge' && activeChallengeLimit != null ? [{ icon: faTrophy, label: 'Spots Left', value: numberFmt(Math.max(0, Number(activeChallengeLimit) - totals.memberCount)) }] : []),
                      ];
                      return (
                        <div className="stats-4" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                          {metrics.map((m, index) => {
                            const isSingleInLastRow = index === metrics.length - 1 && metrics.length % metricColumns === 1;
                            return (
                              <div key={m.label} style={card(C, { padding: '14px 14px', textAlign: 'center', gridColumn: isSingleInLastRow ? '1 / -1' : undefined })}>
                                <FontAwesomeIcon icon={m.icon} style={{ fontSize: 18, marginBottom: 8, color: C.accent }} />
                                <div style={{ fontSize: 10, lineHeight: 1.2, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3, overflowWrap: 'anywhere' }}>{m.label}</div>
                                <div className="heading" style={{ fontSize: isMobile ? 18 : 20, lineHeight: 1.15, fontWeight: 800, color: C.text, letterSpacing: 0, overflowWrap: 'anywhere' }}>{m.value}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {activeInst.type === 'Challenge' && (
                      <section style={card(C, { padding: isMobile ? 14 : 16, borderRadius: 16, marginBottom: 16 })} aria-label="Challenge details">
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) auto', gap: 12, alignItems: 'start' }}>
                          <div>
                            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Challenge</div>
                            <h3 className="heading" style={{ margin: '4px 0 0', color: C.text, fontSize: isMobile ? 17 : 19, lineHeight: 1.15, fontWeight: 950 }}>{activeInst.name}</h3>
                            {activeChallengeDescription && <p style={{ margin: '7px 0 0', color: C.textSecondary, fontSize: 13, lineHeight: 1.5 }}>{activeChallengeDescription}</p>}
                          </div>
                          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                            <span style={{ ...tagSt(C, true), pointerEvents: 'none', fontSize: 11 }}>{activeChallengeLifecycle}</span>
                            {activeChallengeGoalSteps ? <span style={{ ...tagSt(C), pointerEvents: 'none', fontSize: 11 }}>{Number(activeChallengeGoalSteps).toLocaleString()} steps</span> : null}
                            <span style={{ ...tagSt(C), pointerEvents: 'none', fontSize: 11 }}>{activeInst.teams ? `${deptLabel} challenge` : 'Member challenge'}</span>
                          </div>
                        </div>
                        {activeChallengeGoalNumber ? (
                          <div style={{ marginTop: 12, border: `1px solid ${C.accentBorder}`, borderRadius: 14, background: C.accentBgSubtle, padding: isMobile ? '10px 12px' : '11px 14px', textAlign: 'center' }}>
                            <div style={{ color: C.textMuted, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.3 }}>Challenge goal</div>
                            <div className="heading" style={{ color: C.accent, fontSize: isMobile ? 20 : 22, lineHeight: 1.15, fontWeight: 950, marginTop: 2 }}>{Number(activeChallengeGoalNumber).toLocaleString()} steps</div>
                          </div>
                        ) : null}
	                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(300px,0.8fr)', gap: 12, marginTop: 12 }}>
	                          <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgAlt, padding: isMobile ? 12 : 14 }}>
	                            <div style={{ color: C.text, fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Challenge details</div>
	                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 10 }}>
	                              {[
	                                ['Goal', activeChallengeGoalNumber ? `${Number(activeChallengeGoalNumber).toLocaleString()} steps` : 'Not set'],
	                                ['Dates', activeChallengeDateText || 'Not set'],
	                                ['Participants', activeChallengeLimit != null ? `${numberFmt(totals.memberCount)} / ${numberFmt(activeChallengeLimit)}` : numberFmt(totals.memberCount)],
	                                ['Audience', activeInst.teams ? `${deptLabel}s compete as groups` : 'Members compete individually'],
	                              ].map(([label, value]) => (
	                                <div key={label} style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.bgCard, padding: '9px 10px', minWidth: 0 }}>
	                                  <div style={{ color: C.textMuted, fontSize: 10.5, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
	                                  <div style={{ color: C.text, fontSize: 13.5, lineHeight: 1.35, fontWeight: 900, marginTop: 3, overflowWrap: 'anywhere' }}>{value}</div>
	                                </div>
	                              ))}
	                            </div>
	                            <div style={{ display: 'grid', gap: 8, color: C.textSecondary, fontSize: 13.5, lineHeight: 1.45 }}>
	                              <div><strong style={{ color: C.text }}>How it works:</strong> {activeInst.teams ? `${deptLabel}s progress toward ${activeChallengeGoalNumber ? `${Number(activeChallengeGoalNumber).toLocaleString()} steps` : 'the challenge goal'}. When a ${deptLabel.toLowerCase()} reaches the goal, its place is recorded.` : `Members progress toward ${activeChallengeGoalNumber ? `${Number(activeChallengeGoalNumber).toLocaleString()} steps` : 'the challenge goal'}. When a member reaches the goal, their place is recorded.`}</div>
	                              <div><strong style={{ color: C.text }}>Counting:</strong> Steps count during the challenge window only.</div>
	                            </div>
	                          </div>
	                          <div style={{ display: 'grid', gap: 12 }}>
	                            <div style={{ border: `1px solid ${C.accentBorder}`, borderRadius: 14, background: C.accentBgSubtle, padding: isMobile ? 12 : 14 }}>
	                              <div style={{ color: C.text, fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Join code</div>
	                              {activeInst.code ? (
	                                <button type="button" onClick={() => copyToClipboard(activeInst.code, 'Challenge join code')} aria-label={`Copy challenge join code ${activeInst.code}`} style={{ width: '100%', border: `1px solid ${C.accentBorder}`, borderRadius: 13, background: C.bgCard, color: C.text, minHeight: 50, padding: '9px 11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
	                                  <span style={{ minWidth: 0, color: C.accent, fontSize: isMobile ? 18 : 19, fontWeight: 950, letterSpacing: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeInst.code}</span>
	                                  <span style={{ color: C.accent, fontSize: 12.5, fontWeight: 950, display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}><FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} />Copy</span>
	                                </button>
	                              ) : <div style={{ color: C.textMuted, fontSize: 13 }}>No join code is available.</div>}
	                            </div>
	                            <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgCard, padding: isMobile ? 12 : 14 }}>
	                              <div style={{ color: C.text, fontSize: 14, fontWeight: 950, marginBottom: 8 }}>Goal reached</div>
	                              {loadingChallengeResults ? (
	                                <div role="status" style={{ color: C.textMuted, fontSize: 13 }}>Loading goal completions...</div>
	                              ) : challengeResultsError ? (
	                                <div role="alert" style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.45 }}>
	                                  <p style={{ margin: '0 0 8px' }}>{challengeResultsError}</p>
	                                  <button type="button" onClick={() => setInstituteRefresh(value => value + 1)} style={{ minHeight: 40, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgCard, color: C.accent, padding: '8px 12px', cursor: 'pointer', fontWeight: 800 }}>Retry goal completions</button>
	                                </div>
	                              ) : challengeResults.length === 0 ? (
	                                <div role="status" style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.45 }}>No {activeInst.teams ? `${deptLabel.toLowerCase()}s have` : 'participants have'} reached the goal yet. View progress in the {activeInst.teams ? `${deptLabel}s` : `${memberLabel}s`} tab.</div>
	                              ) : (
	                                <div style={{ display: 'grid', gap: 8 }}>
	                                  {challengeResults.map(result => {
	                                    const subject = result.institute_teams || result.profiles || {};
	                                    const label = result.institute_teams?.name || [subject.name, subject.surname].filter(Boolean).join(' ') || subject.username || 'Participant';
	                                    return (
	                                      <div key={result.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
	                                        <RankBadge C={C} rank={result.place} branding={activeLeaderboardBranding} />
	                                        {result.profiles?.profile_img ? <img src={result.profiles.profile_img} alt="" style={{ width: 30, height: 30, borderRadius: 999, objectFit: 'cover', border: `1px solid ${C.border}` }} /> : null}
	                                        <span style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 13, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
	                                        <span style={{ color: leaderboardRankColor(activeLeaderboardBranding, result.place), fontSize: 12.5, fontWeight: 900 }}>{Number(result.steps_at_win || 0).toLocaleString()}</span>
	                                      </div>
	                                    );
	                                  })}
	                                </div>
	                              )}
	                            </div>
	                          </div>
	                        </div>
                      </section>
                    )}

                    <div style={{ display: activeInstituteView === 'activities' ? 'block' : 'none' }} aria-hidden={activeInstituteView !== 'activities'}>
                      <InstitutionContentPanel C={C} activeInst={activeInst} institutes={institutes} isMobile={isMobile} profileData={profileData} showAppDemoPreview={showDemoAppSessionPreview} />
                    </div>

                    {activeInstituteView === 'challenges' && activeInst.type !== 'Challenge' && (
                      <div style={card(C, { padding: isMobile ? 14 : 18, borderRadius: 18, marginBottom: 16 })}>
                        <div style={{ marginBottom: 16 }}>
                          <div>
                            <h3 className="heading" style={{ margin: 0, fontSize: isMobile ? 15 : 17, fontWeight: 900, color: C.text }}>Challenges</h3>
                            <p style={{ margin: '4px 0 0', color: C.textMuted, fontSize: 12.5, lineHeight: 1.45 }}>Use Snows ProAm challenge templates or manage challenges created for {activeInst.name}.</p>
                          </div>
                        </div>
                        <section aria-labelledby="snows-proam-challenge-templates" style={{ display: 'grid', gap: 10, marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${C.border}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <div>
                              <div id="snows-proam-challenge-templates" style={{ color: C.text, fontSize: 13, fontWeight: 900 }}>Snows ProAm challenges</div>
                              <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>Templates created by Snows ProAm. Use one, then choose members or departments for this institute.</div>
                            </div>
                            {loadingPlatformChallengeOffers && <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 800 }}>Checking...</span>}
                          </div>
                          {!loadingPlatformChallengeOffers && platformChallengeOffersError ? (
                            <div role="alert" style={{ padding: 12, borderRadius: 12, border: `1px solid ${C.dangerBorder || C.border}`, background: C.dangerBg || C.bgAlt, color: C.danger || C.textSecondary, fontSize: 12.5, lineHeight: 1.45 }}>
                              Could not load platform challenges. Refresh this page and try again.
                            </div>
                          ) : !loadingPlatformChallengeOffers && platformChallengeOffers.length === 0 ? (
                            <div role="status" style={{ padding: 12, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, fontSize: 12.5 }}>
                              No platform challenges are available for this institute right now.
                            </div>
                          ) : platformChallengeOffers.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                              {platformChallengeOffers.map(offer => {
                                const busy = optingIntoPlatformChallengeId === offer.id;
                                return (
                                  <article key={offer.id} style={{ display: 'grid', gap: 10, alignContent: 'start', border: `1px solid ${offer.opted_in ? C.accentBorder : C.border}`, borderRadius: 14, background: offer.opted_in ? C.accentBgSubtle : C.bgAlt, padding: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                      <div style={{ width: 144, height: 92, borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bgCard, flexShrink: 0 }}>
                                        {offer.image_url ? (
                                          <img src={offer.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        ) : (
                                          <span style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent, background: C.accentBgSubtle }}>
                                            <FontAwesomeIcon icon={faTrophy} style={{ fontSize: 20 }} />
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="heading" style={{ color: C.text, fontSize: 14, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{offer.name}</div>
                                        <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{Number(offer.goal_steps || 0).toLocaleString()} step goal</div>
                                      </div>
                                    </div>
                                    {offer.description && <p style={{ margin: 0, color: C.textSecondary, fontSize: 12, lineHeight: 1.45 }}>{offer.description}</p>}
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                      <span style={{ ...tagSt(C, true), pointerEvents: 'none', fontSize: 11 }}>Admin template</span>
                                    </div>
                                    <button type="button" onClick={() => openPlatformChallengeOffer(offer)} disabled={offer.opted_in || busy} style={{ justifySelf: 'start', minHeight: 32, borderRadius: 9, border: `1px solid ${offer.opted_in ? C.border : C.accentBorder}`, background: offer.opted_in ? C.bgCard : C.accentGrad, color: offer.opted_in ? C.textMuted : '#fff', padding: '6px 10px', cursor: offer.opted_in || busy ? 'default' : 'pointer', fontSize: 11.5, fontWeight: 900 }}>
                                      {offer.opted_in ? 'Added' : busy ? 'Creating...' : 'Use template'}
                                    </button>
                                  </article>
                                );
                              })}
                            </div>
                          ) : null}
                        </section>
                        <section aria-labelledby="institute-admin-challenges" style={{ display: 'grid', gap: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div id="institute-admin-challenges" style={{ color: C.text, fontSize: 13, fontWeight: 900 }}>{activeInst.name} challenges</div>
                              <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>Challenges created and managed by this institute admin account.</div>
                            </div>
                            <button type="button" onClick={() => openAddInstituteModal('Challenge')} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 36, borderRadius: 10, border: 0, background: C.accentGrad, color: '#fff', padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}>
                              <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} />
                              Create Challenge
                            </button>
                          </div>
                        {managedInstitutionChallenges.length === 0 ? (
                          <div style={{ padding: 18, borderRadius: 14, border: `1px dashed ${C.borderMedium}`, background: C.bgAlt, color: C.textSecondary, textAlign: 'center' }}>
                            <FontAwesomeIcon icon={faTrophy} style={{ color: C.accent, fontSize: 22, marginBottom: 8 }} />
                            <div style={{ fontWeight: 900, color: C.text, marginBottom: 4 }}>No challenges yet</div>
                            <div style={{ fontSize: 13 }}>Create one here so members can join it from the app and website.</div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                            {managedInstitutionChallenges.map(challenge => {
                              const settings = challenge.brand_settings || {};
                              const departmentNames = Array.isArray(settings.departmentNames) ? settings.departmentNames.filter(Boolean) : [];
                              const audience = challenge.teams
                                ? departmentNames.length
                                  ? departmentNames.length <= 2 ? departmentNames.join(', ') : `${departmentNames.slice(0, 2).join(', ')} +${departmentNames.length - 2}`
                                  : `${deptLabel}s`
                                : 'All members';
                              const starts = challenge.starts_at || settings.startsOn || settings.startsAt;
                              const ends = challenge.ends_at || settings.endsOn || settings.endsAt;
                              const description = settings.description || settings.challengeDescription || '';
                              const goalSteps = challenge.goal_steps ?? settings.goalSteps ?? null;
                              const dateText = [starts ? new Date(starts).toLocaleDateString() : '', ends ? new Date(ends).toLocaleDateString() : ''].filter(Boolean).join(' - ');
                              return (
                                <article key={challenge.id} style={{ display: 'grid', gap: 10, border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgAlt, padding: 12 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                    <InstituteImage C={C} institute={challenge} fit="cover" style={{ width: 144, height: 92, borderRadius: 10, border: `1px solid ${C.border}`, flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div className="heading" style={{ color: C.text, fontSize: 14, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{challenge.name}</div>
                                      <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{dateText || 'Dates not set'}</div>
                                    </div>
                                  </div>
                                  {description && <p style={{ margin: 0, color: C.textSecondary, fontSize: 12, lineHeight: 1.45 }}>{description}</p>}
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ ...tagSt(C, true), pointerEvents: 'none', fontSize: 11 }}>{challenge.teams ? `${deptLabel} challenge` : 'Member challenge'}</span>
                                    <span style={{ ...tagSt(C), pointerEvents: 'none', fontSize: 11 }}>{audience}</span>
                                    {goalSteps ? <span style={{ ...tagSt(C), pointerEvents: 'none', fontSize: 11 }}>{Number(goalSteps).toLocaleString()} goal</span> : null}
                                    <span style={{ ...tagSt(C), pointerEvents: 'none', fontSize: 11 }}>{challenge.challenge_member_limit ?? challenge.limit ? `${challenge.challenge_member_limit ?? challenge.limit} limit` : 'No limit'}</span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button type="button" onClick={() => { const idx = institutes.findIndex(inst => inst.id === challenge.id); if (idx >= 0) { setActiveInstIdx(idx); if (challenge.teams) fetchDepts(challenge.id); setActiveInstituteView('people'); } }} style={{ minHeight: 34, borderRadius: 9, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}>Open</button>
                                    <button type="button" onClick={() => openEditInstituteModal(challenge)} style={{ minHeight: 34, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}>
                                      <FontAwesomeIcon icon={faPen} style={{ fontSize: 10, marginRight: 5 }} />
                                      Manage
                                    </button>
                                    {challenge.code && (
                                      <button type="button" onClick={() => copyToClipboard(challenge.code, 'Challenge code')} style={{ minHeight: 34, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}>
                                        <FontAwesomeIcon icon={faCopy} style={{ fontSize: 10, marginRight: 5 }} />
                                        Code
                                      </button>
                                    )}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        )}
                        </section>
                      </div>
                    )}

                    {!institutionPublicPricingHidden && !isInstitutionDemoAccount && activeInstituteView === 'plan' && (
                      <section aria-label="Institute plan management" style={card(C, { padding: isMobile ? 14 : 16, borderRadius: 18, marginBottom: 16 })}>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) auto', gap: 12, alignItems: 'start', marginBottom: 14 }}>
                          <div>
                            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Membership</div>
                            <h3 className="heading" style={{ color: C.text, fontSize: isMobile ? 18 : 21, lineHeight: 1.15, fontWeight: 950, marginTop: 4 }}>Manage plan</h3>
                            <p style={{ color: C.textSecondary, fontSize: 13, lineHeight: 1.5, fontWeight: 750, margin: '6px 0 0' }}>
                              Change the plan attached to {activeInst.name}. Members, invite codes, departments and step history stay in place.
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                            {canManageInstitutionBilling && (
                              <button
                                type="button"
                                onClick={openInstitutionBillingPortal}
                                disabled={openingInstitutionBilling}
                                style={{ minHeight: 40, border: `1px solid ${C.accentBorder}`, borderRadius: 10, padding: '8px 12px', background: C.bgCard, color: C.accent, cursor: openingInstitutionBilling ? 'wait' : 'pointer', opacity: openingInstitutionBilling ? 0.72 : 1, fontSize: 12, fontWeight: 900, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                              >
                                {openingInstitutionBilling ? 'Opening billing...' : 'Manage or cancel billing'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => document.getElementById('institution-plan-upgrade-options')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                              disabled={!institutionManagementPlans.length}
                              style={{ minHeight: 40, border: 'none', borderRadius: 10, padding: '8px 12px', background: institutionManagementPlans.length ? C.accentGrad : C.bgAlt, color: institutionManagementPlans.length ? '#fff' : C.textMuted, cursor: institutionManagementPlans.length ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 900, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                            >
                              Upgrade
                            </button>
                            <button
                              type="button"
                              onClick={reloadTransactionData}
                              style={{ minHeight: 40, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px', background: C.bgAlt, color: C.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 900, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                            >
                              Refresh access
                            </button>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(220px,0.7fr) minmax(0,1.3fr)', gap: 12, alignItems: 'start' }}>
                          <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt, padding: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 40, height: 40, borderRadius: 13, background: C.accentBgSubtle, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: 16 }} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Current access</div>
                                <div className="heading" style={{ color: C.text, fontSize: 15, lineHeight: 1.2, fontWeight: 950, marginTop: 3, overflowWrap: 'anywhere' }}>
                                  {activeInstitutionSummary?.name || (activeInstitutionStoredTier ? institutionPlanDisplayName(institutionWebPlans.find(plan => plan.tier === activeInstitutionStoredTier)) : 'No active plan found')}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                              {[
                                ['Status', institutionAccessUnlocked ? activeInstitutionSummary?.status || activeInst.billing_status || 'active' : activeInst.billing_status || 'inactive'],
                                ['Renewal', activeInstitutionSummary?.renewsAt ? fmtDate(activeInstitutionSummary.renewsAt) : activeInstitutionSummary?.expiresAt ? fmtDate(activeInstitutionSummary.expiresAt) : 'Not scheduled'],
                                ['Plan source', institutionStripeSubscription ? 'Stripe card' : activeInstitutionSummary ? 'Entitlement' : 'Not connected'],
                              ].map(([label, value]) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>
                                  <span style={{ color: C.textMuted }}>{label}</span>
                                  <span style={{ color: C.text, textAlign: 'right', overflowWrap: 'anywhere' }}>{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div id="institution-plan-upgrade-options">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                              <div>
                                <div style={{ color: C.text, fontSize: 13, fontWeight: 950 }}>Upgrade options</div>
                                <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, marginTop: 2 }}>Only higher membership plans that fit the current member count are shown.</div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 3, minWidth: isMobile ? '100%' : 220 }}>
                                {[
                                  ['monthly', 'Monthly'],
                                  ['yearly', 'Yearly'],
                                ].map(([value, label]) => (
                                  <button key={value} type="button" onClick={() => setInstitutionBillingCycle(value)} aria-pressed={institutionBillingCycle === value} style={{ border: 'none', borderRadius: 9, minHeight: 36, padding: '7px 10px', background: institutionBillingCycle === value ? C.accentGrad : 'transparent', color: institutionBillingCycle === value ? '#fff' : C.textSecondary, fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>{label}</button>
                                ))}
                              </div>
                            </div>
                            {institutionCheckoutMessage && (
                              <div role="status" style={{ marginBottom: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.text, borderRadius: 12, padding: '10px 12px', fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                                {institutionCheckoutMessage}
                              </div>
                            )}
                            {displayedInstitutionManagementPlans.length ? (
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : displayedInstitutionManagementPlans.length === 1 ? 'minmax(210px,380px)' : 'repeat(auto-fit,minmax(210px,1fr))', justifyContent: displayedInstitutionManagementPlans.length === 1 ? 'center' : 'stretch', gap: 10 }}>
                                {displayedInstitutionManagementPlans.map(plan => {
                                const sourcePlan = plan.sourcePlan || plan;
                                const productKey = institutionBillingCycle === 'yearly' ? sourcePlan.yearlyProductKey : sourcePlan.monthlyProductKey;
                                const businessDemoPlan = institutionBusinessDemoMode && (plan.isBusinessDemoCard || isBusinessInstitutionPlan(plan));
                                const price = institutionBillingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
                                const loadingKey = `institution:${productKey}`;
                                const loading = institutionCheckoutLoading === loadingKey;
                                const disabled = !businessDemoPlan && loading;
                                return (
                                  <div key={`manage-${plan.tier}`} style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt, padding: 14, display: 'grid', gap: 10 }}>
                                    <div>
                                      <div style={{ color: plan.segment === 'Business' ? C.success : C.accent, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{plan.segment}</div>
                                      <div className="heading" style={{ color: C.text, fontSize: 16, lineHeight: 1.15, fontWeight: 950, marginTop: 4 }}>{institutionPlanDisplayName(plan)}</div>
                                      <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 850, marginTop: 5 }}>{plan.size}</div>
                                      <div style={{ color: C.text, fontSize: 19, lineHeight: 1.1, fontWeight: 950, marginTop: 9 }}>{price}</div>
                                    </div>
                                    <div style={{ display: 'grid', gap: 6 }}>
                                      {plan.bullets.slice(0, 4).map(item => {
                                        const state = planItemState(item);
                                        return (
                                          <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, color: C.textSecondary, fontSize: 12, fontWeight: 750, lineHeight: 1.35 }}>
                                            <FontAwesomeIcon icon={state.unavailable ? faTimes : state.addon ? faPlus : faCheck} style={{ color: state.unavailable ? C.danger : state.addon ? C.warn : C.accent, fontSize: 10, width: 11, marginTop: 2 }} />
                                            <span>{state.label}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (disabled) return;
                                        if (businessDemoPlan) {
                                          openInstitutionDemoRequest(sourcePlan);
                                          return;
                                        }
                                        startInstitutionCheckout(sourcePlan);
                                      }}
                                      disabled={disabled}
                                      style={{ minHeight: 40, border: 'none', borderRadius: 10, padding: '9px 12px', background: C.accentGrad, color: '#fff', cursor: disabled ? 'default' : 'pointer', opacity: loading ? 0.72 : 1, fontSize: 12, fontWeight: 950 }}
                                    >
                                      {businessDemoPlan ? 'Book a Demo' : loading ? 'Opening Stripe...' : 'Upgrade'}
                                    </button>
                                  </div>
                                );
                                })}
                              </div>
                            ) : (
                              <div role="status" style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt, padding: 14, color: C.textSecondary, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                                No higher self-serve plan is available for the current membership size. Manage billing to cancel or contact Snows ProAm for a custom rollout.
                              </div>
                            )}
                          </div>
                        </div>
                      </section>
                    )}

                    {activeInstituteView === 'overview' && (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr .9fr', gap: 14, marginBottom: 16, alignItems: 'stretch' }}>
                        <div style={card(C, { padding: 16, borderRadius: 18 })}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <h3 className="heading" style={{ fontSize: 15, fontWeight: 900, color: C.text, marginBottom: 5 }}>Next actions</h3>
                              <p style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5, margin: 0 }}>Manage people, compare rankings, or tune branding without leaving this institute.</p>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 8, marginTop: 14 }}>
                            {[
                              { id: 'people', title: activeInst.teams ? `Manage ${deptLabel.toLowerCase()}s` : `Manage ${memberLabel.toLowerCase()}s`, text: activeInst.teams ? `${filteredDepts.length} ${deptLabel.toLowerCase()}s` : `${totals.memberCount} ${memberLabel.toLowerCase()}s`, icon: activeInst.teams ? faBuilding : faUsers },
                              ...(showInstituteLeaderboard ? [{ id: 'leaderboard', title: 'Check ranking', text: `${scope === 'global' ? 'Global' : scope === 'national' ? rankCountry : rankRegion || 'Regional'} view`, icon: faTrophy }] : []),
                              { id: 'branding', title: 'Branding', text: 'App and live colours', icon: faGear },
                            ].map(action => (
                              <button key={action.id} type="button" onClick={() => setActiveInstituteView(action.id)} style={{ minHeight: 84, textAlign: 'left', border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgAlt, color: C.text, padding: 12, cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={action.icon} style={{ color: C.accent, fontSize: 15, marginBottom: 9 }} />
                                <div style={{ fontSize: 12, fontWeight: 900 }}>{action.title}</div>
                                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, marginTop: 3 }}>{action.text}</div>
                              </button>
                            ))}
                          </div>
                          {showDemoAppSessionPreview && (
                            <div style={{ marginTop: 12, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, borderRadius: 14, padding: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                                <div>
                                  <div style={{ color: C.text, fontSize: 12, fontWeight: 950 }}>App leaderboard mockups</div>
                                  <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 750, marginTop: 2 }}>Demo-only previews for the member app.</div>
                                </div>
                                <FontAwesomeIcon icon={faTrophy} style={{ color: C.accent }} />
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 8 }}>
                                {[
                                  { id: 'members', label: `${memberLabel}s`, icon: faUsers },
                                  ...(activeInst.teams ? [{ id: 'departments', label: `${deptLabel}s`, icon: faBuilding }] : []),
                                  { id: 'overall', label: 'Overall', icon: faTrophy },
                                ].map(preview => (
                                  <button key={preview.id} type="button" onClick={() => setAppLeaderboardPreviewMode(preview.id)} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.accentBorder}`, background: C.bgCard, color: C.accent, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                                    <FontAwesomeIcon icon={preview.icon} style={{ fontSize: 11 }} />
                                    {preview.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <aside style={card(C, { padding: 16, borderRadius: 18 })}>
                          <h3 className="heading" style={{ fontSize: 15, fontWeight: 900, color: C.text, marginBottom: 10 }}>Top performers</h3>
                          {totals.top.length === 0 ? (
                            <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>No members yet.</p>
                          ) : (
                            <div style={{ display: 'grid', gap: 8 }}>
                              {totals.top.slice(0, 5).map((m, i) => (
                                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                  <RankBadge C={C} rank={i + 1} branding={activeLeaderboardBranding} />
                                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 800, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                                  <span style={{ fontSize: 12, fontWeight: 900, color: leaderboardRankColor(activeLeaderboardBranding, i + 1) }}>{institutionStepText(activeInstitutionStepsLocked, m.steps)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </aside>
                      </div>
                    )}

                    {activeInstituteView === 'branding' && (
                      <div id="institution-live-dashboard-profile-panel" tabIndex={-1} style={{ marginBottom: 16, scrollMarginTop: 90, outline: 'none' }}>
                        <InstituteVisibilityControl key={activeInst.id} institute={activeInst} C={C} onChanged={(ids, is_active) => setInstitutes(prev => prev.map(inst => ids.includes(inst.id) ? { ...inst, is_active } : inst))} />
                        <section aria-labelledby="institution-admin-access-title" style={{ marginBottom: 12, padding: isMobile ? 13 : 14, borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <strong id="institution-admin-access-title" style={{ color: C.text, fontSize: 14 }}>Admin access</strong>
                              <p style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5, margin: '4px 0 0' }}>
                                Admins can manage members, departments, activities, challenges, and institute settings. Only the primary owner can add or remove admins.
                              </p>
                            </div>
                            <span style={{ ...tagSt(C, canManageInstitutionAdmins), pointerEvents: 'none', fontSize: 11 }}>
                              {canManageInstitutionAdmins ? 'Primary owner' : 'Admin access'}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                            <div style={{ color: C.textSecondary, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Current admins</div>
                            {institutionAdminsLoading ? (
                              <div role="status" style={{ color: C.textMuted, fontSize: 12, padding: '10px 0' }}>Loading admins...</div>
                            ) : institutionAdminsError ? (
                              <div role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 11, borderRadius: 11, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, fontSize: 12 }}>
                                <span>{institutionAdminsError}</span>
                                <button type="button" onClick={() => loadInstitutionAdmins(activeInst)} style={{ minHeight: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.accent, padding: '6px 9px', cursor: 'pointer', fontSize: 11, fontWeight: 900 }}>Retry</button>
                              </div>
                            ) : institutionAdmins.length === 0 ? (
                              <div style={{ color: C.textMuted, fontSize: 12, padding: '10px 0' }}>No administrator records were found.</div>
                            ) : institutionAdmins.map(row => {
                              const adminName = institutionAdminDisplayName(row.profiles, row.profile_id === profileData?.id ? 'You' : 'Admin');
                              const isOwnerRow = row.role === 'owner' || row.profile_id === activeInst?.owner_profile_id;
                              const adminInitial = adminName.charAt(0).toUpperCase() || 'A';
                              return (
                                <div key={`${row.profile_id}-${row.role}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, padding: '10px 11px', borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgCard }}>
                                  {row.profiles?.profile_img ? (
                                    <img src={row.profiles.profile_img} alt="" style={{ width: 38, height: 38, borderRadius: 999, objectFit: 'cover', border: `1px solid ${C.border}`, flexShrink: 0 }} />
                                  ) : (
                                    <span aria-hidden="true" style={{ width: 38, height: 38, borderRadius: 999, display: 'grid', placeItems: 'center', background: C.accentBgSubtle, color: C.accent, border: `1px solid ${C.accentBorder}`, fontSize: 13, fontWeight: 950, flexShrink: 0 }}>{adminInitial}</span>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: C.text, fontSize: 13, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminName}</div>
                                    <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 750, marginTop: 2 }}>{isOwnerRow ? 'Primary owner' : 'Institute admin'}{row.profiles?.username ? ` · @${row.profiles.username}` : ''}</div>
                                  </div>
                                  {canManageInstitutionAdmins && !isOwnerRow ? (
                                    <button
                                      type="button"
                                      onClick={() => removeInstitutionAdmin(row)}
                                      disabled={changingInstitutionAdminId === row.profile_id}
                                      aria-label={`Remove ${adminName} as institute admin`}
                                      style={{ minHeight: 34, borderRadius: 9, border: '1px solid rgba(220,38,38,0.24)', background: 'rgba(220,38,38,0.08)', color: C.danger, padding: '7px 10px', cursor: changingInstitutionAdminId === row.profile_id ? 'wait' : 'pointer', opacity: changingInstitutionAdminId === row.profile_id ? 0.65 : 1, fontSize: 11.5, fontWeight: 900, flexShrink: 0 }}
                                    >
                                      {changingInstitutionAdminId === row.profile_id ? 'Removing...' : 'Remove'}
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>

                          {canManageInstitutionAdmins ? (
                            <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                              <div style={{ color: C.textSecondary, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Add an admin</div>
                              <input
                                type="search"
                                value={institutionAdminSearch}
                                onChange={event => setInstitutionAdminSearch(event.target.value)}
                                placeholder="Search institute members..."
                                aria-label="Search members to make institute admin"
                                style={{ ...inputSt(C), marginBottom: 10, fontSize: 13, background: C.bgCard }}
                              />
                              <div style={{ display: 'grid', gap: 8 }}>
                                {institutionAdminCandidates.length === 0 ? (
                                  <div style={{ color: C.textMuted, fontSize: 12, padding: '8px 0' }}>
                                    {institutionAdminSearch.trim() ? 'No matching members are available.' : 'Every eligible member already has admin access.'}
                                  </div>
                                ) : institutionAdminCandidates.map(member => {
                                  const memberName = member.name || member.username || 'Member';
                                  return (
                                    <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, padding: '9px 10px', borderRadius: 11, border: `1px solid ${C.border}`, background: C.bgCard }}>
                                      {member.profile_img ? (
                                        <img src={member.profile_img} alt="" style={{ width: 34, height: 34, borderRadius: 999, objectFit: 'cover', border: `1px solid ${C.border}`, flexShrink: 0 }} />
                                      ) : (
                                        <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 999, display: 'grid', placeItems: 'center', background: C.bgAlt, color: C.accent, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 950, flexShrink: 0 }}>{memberName.charAt(0).toUpperCase() || 'M'}</span>
                                      )}
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ color: C.text, fontSize: 12.5, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memberName}</div>
                                        <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{member.username ? `@${member.username}` : member.association_type || 'Member'}</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => grantInstitutionAdmin(member)}
                                        disabled={changingInstitutionAdminId === member.id}
                                        aria-label={`Make ${memberName} an institute admin`}
                                        style={{ minHeight: 34, borderRadius: 9, border: 'none', background: C.accentGrad, color: '#fff', padding: '7px 10px', cursor: changingInstitutionAdminId === member.id ? 'wait' : 'pointer', opacity: changingInstitutionAdminId === member.id ? 0.7 : 1, fontSize: 11.5, fontWeight: 900, flexShrink: 0 }}
                                      >
                                        {changingInstitutionAdminId === member.id ? 'Adding...' : 'Make admin'}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}
                        </section>
                        <section style={{ marginBottom: 12, padding: 14, borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: appLeaderboardOpen ? 12 : 0, flexWrap: 'wrap' }}>
                            <div>
                              <strong style={{ color: C.text, fontSize: 14 }}>Leaderboard app branding</strong>
                              <p style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5, margin: '4px 0 0' }}>Colours members see in the app institute leaderboard.</p>
                            </div>
                            <button type="button" onClick={() => setAppLeaderboardOpen(open => !open)} aria-expanded={appLeaderboardOpen} style={{ minHeight: 34, borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgCard, color: C.accent, padding: '7px 11px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>{appLeaderboardOpen ? 'Close' : 'Edit colours'}</button>
                          </div>
                          {appLeaderboardOpen && (
                            <>
                              <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: C.textSecondary, fontSize: 12, fontWeight: 800, marginBottom: 12 }}>
                                <input type="checkbox" checked={appLeaderboardForm.applyBranding !== false} onChange={e => setAppLeaderboardForm(v => ({ ...v, applyBranding: e.target.checked }))} />
                                Apply branding
                              </label>
                              <div style={{ minHeight: 106, borderRadius: 12, padding: 14, marginBottom: 12, background: `linear-gradient(180deg, ${tintHex(appLeaderboardForm.primaryColor || DEFAULT_APP_LEADERBOARD_SETTINGS.primaryColor, 0.94)}, ${appLeaderboardForm.backgroundColor || DEFAULT_APP_LEADERBOARD_SETTINGS.backgroundColor})`, border: `1px solid ${rgba(appLeaderboardForm.primaryColor || DEFAULT_APP_LEADERBOARD_SETTINGS.primaryColor, 0.20)}`, color: C.textPrimary }}>
                                <div style={{ fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: .6, color: C.textSecondary }}>App leaderboard</div>
                                <div style={{ marginTop: 7, fontSize: 18, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.textPrimary }}>{activeInst?.name || 'Institute'}</div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16 }}>
                                  <div style={{ width: 92, height: 8, borderRadius: 999, background: appLeaderboardForm.accentColor || DEFAULT_APP_LEADERBOARD_SETTINGS.accentColor }} />
                                  <div style={{ width: 30, height: 8, borderRadius: 999, background: appLeaderboardForm.supportColor || DEFAULT_APP_LEADERBOARD_SETTINGS.supportColor }} />
                                  <div style={{ width: 42, height: 8, borderRadius: 999, background: appLeaderboardForm.highlightColor || DEFAULT_APP_LEADERBOARD_SETTINGS.highlightColor }} />
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 8, marginBottom: 12 }}>
                                {[
                                  ['primaryColor', 'Primary'],
                                  ['accentColor', 'Accent'],
                                  ['supportColor', 'Support'],
                                  ['highlightColor', 'Highlight'],
                                  ['backgroundColor', 'Background'],
                                ].map(([key, label]) => (
                                  <label key={key} style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>
                                    {label}
                                    <div style={{ display: 'grid', gridTemplateColumns: '42px minmax(0,1fr)', gap: 7, alignItems: 'center', marginTop: 5 }}>
                                      <input type="color" value={appLeaderboardForm[key] || DEFAULT_APP_LEADERBOARD_SETTINGS[key]} onChange={e => setAppLeaderboardForm(v => ({ ...v, [key]: e.target.value.toUpperCase() }))} style={{ width: 42, height: 36 }} aria-label={`App leaderboard ${label.toLowerCase()} colour`} />
                                      <input value={appLeaderboardForm[key] || DEFAULT_APP_LEADERBOARD_SETTINGS[key]} onChange={e => setAppLeaderboardForm(v => ({ ...v, [key]: e.target.value }))} onBlur={e => setAppLeaderboardForm(v => ({ ...v, [key]: normalizeHexInput(e.target.value, DEFAULT_APP_LEADERBOARD_SETTINGS[key]) }))} style={{ ...inputSt(C), padding: '8px 9px', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} />
                                    </div>
                                  </label>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" onClick={resetAppLeaderboardBranding} disabled={savingAppLeaderboardBranding} style={{ minHeight: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, padding: '8px 12px', fontSize: 12, fontWeight: 900, cursor: savingAppLeaderboardBranding ? 'wait' : 'pointer' }}>Reset app defaults</button>
                                <button type="button" onClick={saveAppLeaderboardBranding} disabled={savingAppLeaderboardBranding} style={{ minHeight: 38, borderRadius: 10, border: 'none', background: C.accentGrad, color: '#fff', padding: '8px 12px', fontSize: 12, fontWeight: 900, cursor: savingAppLeaderboardBranding ? 'wait' : 'pointer' }}>{savingAppLeaderboardBranding ? 'Saving…' : 'Save app branding'}</button>
                              </div>
                            </>
                          )}
                        </section>
                        {activeInst.workspace_archived ? (
                          <section style={{ padding: 14, borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt }}>
                            <strong style={{ color: C.text }}>Live dashboard workspace needs restoring</strong>
                            <p style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.55, margin: '6px 0 10px' }}>Restore it to publish or open the live workspace page.</p>
                            <button type="button" onClick={openLiveDashboard} disabled={openingLiveDashboard} style={{ background: C.accentGrad, border: 'none', color: '#fff', padding: '10px 13px', borderRadius: 10, cursor: openingLiveDashboard ? 'wait' : 'pointer', fontSize: 12, fontWeight: 900 }}>{openingLiveDashboard ? 'Restoring...' : 'Restore workspace'}</button>
                          </section>
                        ) : activeInst.workspace_id ? (
                          <InstitutionLiveDashboardSettings workspaceId={activeInst.workspace_id} C={C} appLeaderboardSettings={{ ...(activeInst.brand_settings || {}) }} />
                        ) : null}
                      </div>
                    )}

                    {/* Members / Depts */}
                    {activeInstituteView === 'people' && <div style={{ display: 'grid', gridTemplateColumns: activeInst.teams || isMobile ? '1fr' : '1fr 280px', gap: 14, alignItems: 'start', marginBottom: 16 }}>
                      <div style={card(C, { borderRadius: 16 })}>
                        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                          <h3 className="heading" style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{activeInst.teams ? `${deptLabel}s` : `${memberLabel}s`}</h3>
                          <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                            {activeInst.teams ? (
                              <>
                                <button onClick={() => setShowAddDept(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: C.accentGrad, border: 'none', color: '#fff', padding: '8px 13px', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 700, minHeight: 36 }}>
                                  <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} /> Add {deptLabel}
                                </button>
                                {activeInst.type === 'Challenge' && (
                                  <button onClick={() => setShowAddMembers(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textSecondary, padding: '8px 13px', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 800, minHeight: 36 }}>
                                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: 10 }} /> Find members
                                  </button>
                                )}
                                {!showAllGroupsByDefault && filteredDepts.length > 6 && <button onClick={() => setShowAllDepts(v => !v)} style={{ ...tagSt(C), cursor: 'pointer', padding: '5px 10px', minHeight: 36 }}>{showAllDepts ? 'Less ▲' : `All (${filteredDepts.length}) ▼`}</button>}
                              </>
                            ) : (
                              <button onClick={() => setShowAddMembers(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: C.accentGrad, border: 'none', color: '#fff', padding: '8px 13px', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 700, minHeight: 36 }}>
                                <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} /> Add {memberLabel}s
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ padding: '12px 14px' }}>
                          <input
                            type="search"
                            value={activeInst.teams ? institutionDepartmentMemberSearch : institutionMemberSearch}
                            onChange={e => activeInst.teams ? setInstitutionDepartmentMemberSearch(e.target.value) : setInstitutionMemberSearch(e.target.value)}
                            placeholder="Search members by name, username, or type..."
                            aria-label="Search institute members"
                            style={{ ...inputSt(C), marginBottom: 12, fontSize: 13 }}
                          />
                          {activeInst.teams ? (
                            filteredDepts.length === 0
                              ? <div style={{ textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 14 }}>{institutionDepartmentMemberSearch.trim() ? 'No matching members.' : `No ${deptLabel.toLowerCase()}s yet.`}</div>
                              : (
                                <div className="dept-grid" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  {visibleDepts.map((dept, deptIndex) => {
                                    const isExp = expandedDept === dept.id;
                                    const sortedMembers = sortInstitutionMembers(dept.members || []).filter(member => institutionMemberMatchesSearch(member, institutionDepartmentMemberSearch));
                                    const topSteps = activeInstitutionStepsLocked ? 0 : Math.max(1, ...sortedMembers.map(member => member.steps || 0));
                                    const departmentRankColor = leaderboardRankColor(activeLeaderboardBranding, deptIndex + 1);
                                    const challengeTeamResult = activeInst.type === 'Challenge' ? challengeResultByTeamId.get(dept.id) : null;
                                    const challengeTeamPct = activeInst.type === 'Challenge' ? challengeProgressPct(dept.total) : null;
                                    return (
                                      <div key={dept.id} className="card-lift" style={card(C, { padding: 0, overflow: 'hidden', borderColor: isExp ? C.accent : C.border })}>
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          aria-expanded={isExp}
                                          onClick={() => setExpandedDept(isExp ? null : dept.id)}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              setExpandedDept(isExp ? null : dept.id);
                                            }
                                          }}
                                          style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '168px minmax(0,1fr)', gap: 0, cursor: 'pointer' }}
                                        >
                                          <div style={{ position: 'relative', minHeight: isMobile ? 148 : 116 }}>
                                            <GroupImage C={C} group={dept} label={deptLabel} iconSize={isMobile ? 34 : 28} style={{ width: '100%', height: '100%', minHeight: isMobile ? 148 : 116, aspectRatio: '16 / 9', background: C.bgAlt }} />
                                          </div>
                                          <div style={{ padding: '12px 14px', display: 'grid', gap: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                                              <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                  <RankBadge C={C} rank={deptIndex + 1} branding={activeLeaderboardBranding} />
                                                  <div className="heading" style={{ fontSize: 14, lineHeight: 1.25, fontWeight: 800, color: C.text, overflowWrap: 'anywhere' }}>{dept.name}</div>
                                                </div>
                                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 5 }}>
                                                  <span style={{ fontSize: 12, color: departmentRankColor, fontWeight: 800 }}>{institutionStepText(activeInstitutionStepsLocked, dept.total)} steps</span>
                                                  <span style={{ fontSize: 11.5, color: C.textMuted }}>{departmentMemberCountLabel(dept)}</span>
                                                  {activeInst.type === 'Challenge' && activeChallengeGoalNumber ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 24, borderRadius: 999, border: `1px solid ${challengeTeamResult ? C.accentBorder : C.border}`, background: challengeTeamResult ? C.accentBgSubtle : C.bgCard, color: challengeTeamResult ? C.accent : C.textSecondary, padding: '3px 8px', fontSize: 11, fontWeight: 900 }}>
                                                      {challengeProgressLabel(dept.total, challengeTeamResult)}
                                                    </span>
                                                  ) : null}
                                                  {dept.code && (
                                                    <button type="button" onClick={e => { e.stopPropagation(); copyToClipboard(dept.code, `${dept.name} invite code`); }} aria-label={`Copy invite code ${dept.code} for ${dept.name}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 26, borderRadius: 999, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, padding: '3px 8px', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
                                                      <FontAwesomeIcon icon={faCopy} style={{ fontSize: 9 }} />
                                                      {dept.code}
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                              <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                                                {!dept.isUnassignedChallengeMembers && (
                                                  <>
                                                    <button type="button" aria-label={`Edit ${dept.name}`} onClick={e => { e.stopPropagation(); setEditingDepartment(dept); setEditDeptForm({ id: dept.id, name: dept.name, code: dept.code || '', previous_name: dept.name, image_url: dept.image_url || null, remove_image: false, institute_id: dept.institute_id }); setEditDeptImageFile(null); setEditDeptImagePreview(dept.image_url || null); }}
                                                      style={{ width: 34, height: 34, borderRadius: 8, background: C.bgAlt, border: `1px solid ${C.border}`, color: C.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                                                    </button>
                                                    <button type="button" aria-label={`Archive ${dept.name}`} onClick={e => { e.stopPropagation(); deleteDept(dept.id); }}
                                                      style={dangerIconButtonSt(C, 34)}>
                                                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                                                    </button>
                                                  </>
                                                )}
                                                <button type="button" aria-expanded={isExp} aria-label={`${isExp ? 'Hide' : 'Show'} ${dept.name} members`} onClick={e => { e.stopPropagation(); setExpandedDept(isExp ? null : dept.id); }}
                                                  style={{ width: 34, height: 34, borderRadius: 8, background: isExp ? C.accentBgLight : C.bgAlt, border: `1px solid ${isExp ? C.accentBorder : C.border}`, color: isExp ? C.accent : C.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  <FontAwesomeIcon icon={isExp ? faChevronUp : faChevronDown} style={{ fontSize: 11 }} />
                                                </button>
                                              </div>
                                            </div>
                                            <Bar C={C} p={activeInst.type === 'Challenge' && activeChallengeGoalNumber ? challengeTeamPct : activeInstitutionStepsLocked ? 0 : pct(dept.total, maxDeptSteps)} fill={departmentRankColor} />
                                          </div>
                                        </div>
                                          {isExp && (
                                            <div style={{ padding: '12px 14px 14px', borderTop: `1px solid ${C.border}` }}>
                                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary }}>{dept.type ? capitalise(dept.type) : 'Members'}</span>
                                                {!dept.isUnassignedChallengeMembers && (
                                                  <button onClick={() => setShowAddDeptMembers({ open: true, dept })}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: C.accentBgLight, border: `1px solid ${C.accentBorder}`, color: C.accent, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700, minHeight: 34 }}>
                                                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: 9 }} /> Add
                                                  </button>
                                                )}
                                              </div>
                                              {sortedMembers.length === 0
                                                ? <p style={{ fontSize: 12, color: C.textMuted, textAlign: 'center' }}>{institutionDepartmentMemberSearch.trim() ? 'No matching members.' : 'No members yet.'}</p>
                                                : (
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                                    {sortedMembers.map((m, i) => (
                                                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <RankBadge C={C} rank={i + 1} branding={activeLeaderboardBranding} />
                                                        <img src={m.profile_img || noImage} alt="" style={{ width: 32, height: 32, borderRadius: 999, objectFit: 'cover', flexShrink: 0, border: `1px solid ${C.border}` }} loading="lazy" />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                                                            {activeInst.type !== 'Challenge' && <span style={{ ...tagSt(C), padding: '2px 7px', fontSize: 10.5 }}>{m.association_type || 'Member'}</span>}
                                                          </div>
                                                          <Bar C={C} p={activeInstitutionStepsLocked ? 0 : pct(m.steps || 0, topSteps)} fill={leaderboardRankColor(activeLeaderboardBranding, i + 1)} />
                                                        </div>
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: leaderboardRankColor(activeLeaderboardBranding, i + 1), flexShrink: 0 }}>{institutionStepText(activeInstitutionStepsLocked, m.steps)}</span>
                                                        <button onClick={() => setMoveDeptMember({ open: true, member: m, fromDeptId: dept.isUnassignedChallengeMembers ? null : dept.id, targetDeptId: '' })}
                                                          style={{ minHeight: 30, padding: '0 8px', borderRadius: 7, border: `1px solid ${C.accentBorder}`, background: C.accentBgLight, color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                                          Move
                                                        </button>
                                                        {activeInst.type !== 'Challenge' && (
                                                          <button type="button" onClick={() => openInstitutionMemberRole(m)} disabled={updatingInstitutionMemberRoleId === m.id} aria-label={`Edit member type for ${m.name}`}
                                                            style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, cursor: updatingInstitutionMemberRoleId === m.id ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                                                          </button>
                                                        )}
                                                        <button onClick={() => dept.isUnassignedChallengeMembers ? removeMember(m.id, m.name) : removeFromDept(dept.id, m.id, m.name)}
                                                          style={dangerIconButtonSt(C, 30)}>
                                                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 11 }} />
                                                        </button>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                            </div>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              {(activeInst?.members || []).length === 0
                                ? <div role={instituteMembersLoading ? 'status' : undefined} style={{ textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 14 }}>{instituteMembersLoading ? `Loading ${memberLabel.toLowerCase()}s…` : `No ${memberLabel.toLowerCase()}s yet.`}</div>
                                : (() => {
                                  const visibleMembers = sortInstitutionMembers(activeInst?.members || []).filter(member => institutionMemberMatchesSearch(member, institutionMemberSearch));
                                  const topMemberSteps = activeInstitutionStepsLocked ? 0 : Math.max(1, ...visibleMembers.map(member => member.steps || 0));
                                  return visibleMembers.length === 0
                                    ? <div style={{ textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 14 }}>No matching {memberLabel.toLowerCase()}s.</div>
                                    : visibleMembers.map((m, i, arr) => {
                                      const memberChallengeResult = activeInst.type === 'Challenge' ? challengeResultByProfileId.get(m.profile_id || m.id) : null;
                                      const memberChallengePct = activeInst.type === 'Challenge' ? challengeProgressPct(m.steps || 0) : null;
                                      return (
                                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 11, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                                    <RankBadge C={C} rank={i + 1} branding={activeLeaderboardBranding} />
                                    <img src={m.profile_img || noImage} alt="" style={{ width: 34, height: 34, borderRadius: 999, objectFit: 'cover', flexShrink: 0, border: `1px solid ${C.border}` }} loading="lazy" />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.name}</span>
                                        {activeInst.type !== 'Challenge' && <span style={{ ...tagSt(C), padding: '2px 7px', fontSize: 11 }}>{m.association_type || 'Member'}</span>}
                                      </div>
                                      <Bar C={C} p={activeInst.type === 'Challenge' && activeChallengeGoalNumber ? memberChallengePct : activeInstitutionStepsLocked ? 0 : pct(m.steps || 0, topMemberSteps)} fill={leaderboardRankColor(activeLeaderboardBranding, i + 1)} />
                                    </div>
                                    <div style={{ display: 'grid', gap: 3, justifyItems: 'end', flexShrink: 0 }}>
                                      <span style={{ fontSize: 13, fontWeight: 700, color: leaderboardRankColor(activeLeaderboardBranding, i + 1) }}>{institutionStepText(activeInstitutionStepsLocked, m.steps)}</span>
                                      {activeInst.type === 'Challenge' && activeChallengeGoalNumber ? (
                                        <span style={{ color: memberChallengeResult ? C.accent : C.textMuted, fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap' }}>{challengeProgressLabel(m.steps || 0, memberChallengeResult)}</span>
                                      ) : null}
                                    </div>
                                    {activeInst.type !== 'Challenge' && (
                                      <button type="button" onClick={() => openInstitutionMemberRole(m)} disabled={updatingInstitutionMemberRoleId === m.id} aria-label={`Edit member type for ${m.name}`}
                                        style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, cursor: updatingInstitutionMemberRoleId === m.id ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                                      </button>
                                    )}
                                    <button onClick={() => removeMember(m.id, m.name)} style={dangerIconButtonSt(C, 30)}>
                                      <FontAwesomeIcon icon={faTimes} style={{ fontSize: 11 }} />
                                    </button>
                                  </div>
                                      );
                                    });
                                })()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Top performers sidebar (desktop only) */}
                      {!activeInst.teams && !isMobile && (
                        <aside style={card(C, { borderRadius: 16 })}>
                          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
                            <h3 className="heading" style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Top Performers</h3>
                          </div>
                          <div style={{ padding: '12px 16px' }}>
                            {totals.top.length === 0 ? <p style={{ fontSize: 13, color: C.textMuted, textAlign: 'center', padding: '10px 0' }}>No members yet</p> : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                {totals.top.map((m, i) => (
                                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <RankBadge C={C} rank={i + 1} branding={activeLeaderboardBranding} />
                                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: leaderboardRankColor(activeLeaderboardBranding, i + 1) }}>{institutionStepText(activeInstitutionStepsLocked, m.steps)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </aside>
                      )}
                    </div>}

                    {/* Leaderboard */}
                    {activeInstituteView === 'leaderboard' && showInstituteLeaderboard && (
                      <div style={card(C, { padding: '18px 16px', borderRadius: 18 })}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                          <h3 className="heading" style={{ fontSize: isMobile ? 15 : 16, fontWeight: 800, color: C.text }}>{effectiveType} <span className="grad-text">Leaderboard</span></h3>
                          <span style={{ fontSize: 12, color: C.textMuted }}>{scope === 'global' ? '🌍 Global' : scope === 'national' ? `${countryFlagEmoji(rankCountryCode)} ${rankCountry}` : `📍 ${rankRegion}`}</span>
                        </div>
                        {loadingRanks ? <div style={{ textAlign: 'center', padding: '24px 0', color: C.textMuted }}>⏳ Loading…</div> : peerBoard.length ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {peerBoard.map(inst => {
                              const useSquareLogo = ['company', 'club', 'school', 'college'].includes(String(inst.institute_type || inst.type || effectiveType).toLowerCase());
                              return (
                              <div key={inst.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                                <RankBadge C={C} rank={inst.rank} />
                                <InstituteImage C={C} institute={inst} iconSize={18} fit={useSquareLogo ? 'contain' : 'cover'} style={useSquareLogo ? { width: isMobile ? 56 : 58, height: isMobile ? 56 : 58, borderRadius: 10, border: `1px solid ${C.border}`, padding: 5, flexShrink: 0 } : { width: isMobile ? 84 : 124, height: isMobile ? 46 : 58, borderRadius: 10, border: `1px solid ${C.border}`, flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, color: C.text, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.name}</div>
                                  {!isMobile && <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 3 }}>{inst.region ? `${inst.region}, ` : ''}{inst.country}</div>}
	                                  <Bar C={C} p={maxPeerSteps ? Math.round((inst.totalSteps / maxPeerSteps) * 100) : 0} />
                                </div>
                                <div className="heading" style={{ fontSize: 13, fontWeight: 700, color: C.accent, flexShrink: 0, textAlign: 'right' }}>
	                                  {institutionStepText(false, inst.totalSteps)}
	                                  <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 400 }}>steps</div>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <div style={{ fontSize: 28, marginBottom: 8 }}>🏆</div>
                            <p style={{ color: C.textMuted, fontSize: 14 }}>No {typePluralLower(effectiveType)} found.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ TAB: WORKOUT GROUPS ══ */}
        {activeTab === 'workouts' && (
          <div className="fade-in">
            <div ref={workoutPlansRef} style={card(C, { padding: '18px 18px', marginBottom: 16, scrollMarginTop: 76 })}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <div className="heading" style={{ fontSize: 15, fontWeight: 800, color: C.text }}>Workout Groups Membership</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>Manage your web Stripe subscription. The app unlocks from the same Supabase entitlements.</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                  {canManageWorkoutGroupBilling && (
                    <button
                      type="button"
                      onClick={openWorkoutGroupBillingPortal}
                      disabled={openingWorkoutBillingPortal}
                      style={{ background: C.accentGrad, border: 'none', color: '#fff', padding: '9px 13px', borderRadius: 10, cursor: openingWorkoutBillingPortal ? 'wait' : 'pointer', opacity: openingWorkoutBillingPortal ? 0.72 : 1, fontSize: 12, fontWeight: 900, minHeight: 40, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
                    >
                      {openingWorkoutBillingPortal ? 'Opening billing...' : 'Manage or cancel billing'}
                    </button>
                  )}
	                  <button
	                    type="button"
	                    onClick={() => setShowWorkoutPlans(v => !v)}
	                    style={{ background: canManageWorkoutGroupBilling ? C.bgCard : C.accentGrad, border: canManageWorkoutGroupBilling ? `1px solid ${C.accentBorder}` : 'none', color: canManageWorkoutGroupBilling ? C.accent : '#fff', padding: '9px 13px', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 900, minHeight: 40, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
	                  >
	                    {showWorkoutPlans ? 'Hide Upgrades' : 'Upgrade'}
	                  </button>
                </div>
              </div>
              {workoutAccess ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,1fr)', gap: 10 }}>
                    {[
                      ['Plan', workoutAccess.planName],
                      ['Status', workoutAccess.status],
                      ['Source', workoutAccess.source],
                      [workoutAccessTimingSummary(workoutAccess).label, workoutAccessTimingSummary(workoutAccess).value],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                        <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', fontWeight: 800, letterSpacing: .4 }}>{label}</div>
                        <div style={{ marginTop: 5, fontSize: 13, color: C.text, fontWeight: 800 }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {checkoutMessage && (
                    <div style={{ marginTop: 12, background: C.mode === 'dark' ? 'rgba(239,68,68,0.10)' : 'rgba(220,38,38,0.08)', border: `1px solid ${C.mode === 'dark' ? 'rgba(239,68,68,0.25)' : 'rgba(220,38,38,0.16)'}`, color: C.danger, borderRadius: 12, padding: 12, fontSize: 12, fontWeight: 700 }}>
                      {checkoutMessage}
                    </div>
                  )}

                  {showWorkoutPlans && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
	                        <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 700 }}>Only higher web subscription plans are shown. Yearly saves 15%; mobile coin unlocks still work separately in the app.</div>
                        <div style={{ display: 'inline-flex', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 3 }}>
                          {[
                            ['monthly', 'Monthly'],
                            ['yearly', 'Yearly · save 15%'],
                          ].map(([value, label]) => (
                            <button key={value} type="button" onClick={() => setWorkoutBillingCycle(value)} style={{ border: 'none', borderRadius: 9, padding: '7px 10px', background: workoutBillingCycle === value ? C.accentGrad : 'transparent', color: workoutBillingCycle === value ? '#fff' : C.textSecondary, fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>{label}</button>
                          ))}
                        </div>
                        <button onClick={refreshWorkoutAccess} style={{ ...tagSt(C), cursor: 'pointer', fontSize: 11, padding: '7px 10px' }}>Refresh Access</button>
                      </div>
	                      {workoutUpgradePlans.length === 0 && (
	                        <div role="status" style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.bgAlt, padding: 14, color: C.textSecondary, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
	                          No higher Workout Groups web plan is available. Manage billing to update card details, view invoices, or cancel.
	                        </div>
	                      )}
		                      {workoutUpgradePlans.length > 0 && (
	                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(210px,1fr))', gap: 10 }}>
	                        {workoutUpgradePlans.map(plan => {
	                          const featured = plan.tier === 'coach';
	                          const checkout = getWorkoutPlanCheckout(plan);
	                          const priceConfigured = !checkout.priceKey || workoutAccess.prices?.some(p => p.price_key === checkout.priceKey && p.stripe_price_id);
	                          return (
	                            <div key={plan.tier} style={{ background: featured ? C.accentBgSubtle : C.bgAlt, border: `1px solid ${featured ? C.accentBorder : C.border}`, borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                                <div>
                                  <div className="heading" style={{ fontSize: 14, fontWeight: 900, color: C.text }}>{plan.title}</div>
                                  <div style={{ color: featured ? C.accent : C.textSecondary, fontSize: 18, fontWeight: 900, marginTop: 3 }}>{checkout.price}</div>
                                  {checkout.alternatePrice && checkout.alternatePrice !== checkout.price && <div style={{ color: checkout.alternateLabel === 'Monthly option' ? '#FFFFFF' : C.textMuted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>{checkout.alternateLabel}: {checkout.alternatePrice}</div>}
                                  {checkout.saving && <div style={{ color: C.success, fontSize: 11, fontWeight: 900, marginTop: 3 }}>{checkout.saving}</div>}
                                </div>
	                                <span style={{ borderRadius: 999, padding: '4px 8px', background: C.bgCard, color: C.textMuted, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>
	                                  {plan.badge || 'UPGRADE'}
                                </span>
                              </div>
                              <div style={{ display: 'grid', gap: 6 }}>
                                {plan.bullets.map(b => (
                                  <div key={b} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', color: C.textSecondary, fontSize: 12, fontWeight: 650, lineHeight: 1.35 }}>
                                    <span style={{ color: C.accent, fontWeight: 900 }}>✓</span>
                                    <span>{b}</span>
                                  </div>
                                ))}
                                {plan.note && <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>{plan.note}</div>}
                              </div>
                              <button
	                                onClick={() => !checkout.priceKey ? null : startWorkoutCheckout(plan)}
	                                disabled={!checkout.priceKey || !priceConfigured || checkoutLoading === checkout.priceKey}
	                                style={{ marginTop: 'auto', border: 'none', borderRadius: 11, padding: '10px 12px', background: C.accentGrad, color: '#fff', cursor: !checkout.priceKey || !priceConfigured ? 'default' : 'pointer', opacity: !priceConfigured ? 0.55 : 1, fontSize: 12, fontWeight: 900 }}
	                              >
	                                {!priceConfigured ? 'Missing Stripe Price' : checkoutLoading === checkout.priceKey ? 'Opening Checkout...' : 'Upgrade'}
	                              </button>
                            </div>
                          );
	                        })}
	                      </div>
	                      )}
	                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: C.textMuted, fontSize: 13 }}>Loading access...</div>
              )}
            </div>

            <div className="proam-modern-surface proam-motion-card" style={card(C, { padding: 0, overflow: 'hidden', marginBottom: 18, borderColor: C.accentBorder })}>
              <div className="proam-command-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.35fr) minmax(280px,.8fr)' }}>
                <div style={{ padding: isMobile ? 18 : 22, background: C.mode === 'dark' ? 'radial-gradient(circle at top left, rgba(12,105,200,0.30), transparent 42%), linear-gradient(135deg,#101B2E,#0A0A0A)' : 'radial-gradient(circle at top left, rgba(12,105,200,0.16), transparent 42%), linear-gradient(135deg,#FFFFFF,#F4F8FF)', color: C.text }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Workout Groups Command Center</div>
                  <h2 className="heading" style={{ fontSize: isMobile ? 24 : 32, lineHeight: 1, fontWeight: 900, color: C.text, margin: 0 }}>Coach, invite, and manage from one place.</h2>
                  <p style={{ color: C.textSecondary, fontSize: 13, fontWeight: 750, lineHeight: 1.55, margin: '12px 0 0', maxWidth: 620 }}>
                    Start with a simple group, bring athletes in with a code, then move into Club Coaching when you need seasons, staff access, branded dashboards, and multi-group operations.
                  </p>
                  <div className="proam-command-actions" style={{ marginTop: 16 }}>
                    <button type="button" className="proam-action" onClick={openCreateWorkoutGroup} style={{ background: C.accentGrad, border: 'none', color: '#fff', padding: '12px 15px', borderRadius: 12, cursor: 'pointer', fontSize: 13, fontWeight: 900, fontFamily: "'DM Sans',sans-serif", minHeight: 44 }}>
                      <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11, flexShrink: 0 }} /> <span>Create Group</span>
                    </button>
                    <button type="button" className="proam-action" onClick={() => setShowJoinWG(true)} style={{ ...joinWorkoutGroupButtonSt(C), cursor: 'pointer', padding: '11px 15px', fontSize: 13, minHeight: 44 }}>
                      <span>Join With Code</span>
                    </button>
                    {canOpenAnyClubDashboard && (
                      <button type="button" className="proam-action" onClick={() => navigate('/coaching/dashboard')} style={{ ...tagSt(C), cursor: 'pointer', padding: '11px 15px', fontSize: 13, minHeight: 44, background: C.accentBgSubtle, borderColor: C.accentBorder, color: C.accent }}>
                        <span>Open Club Dashboard</span>
                      </button>
                    )}
                    {!canOpenAnyClubDashboard && (
                      <button type="button" className="proam-action" onClick={() => navigate('/coaching#pricing')} style={{ ...tagSt(C), cursor: 'pointer', padding: '11px 15px', fontSize: 13, minHeight: 44, background: C.accentBgSubtle, borderColor: C.accentBorder, color: C.accent }}>
                        <span>See Coaching Plans</span>
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ padding: isMobile ? 16 : 18, borderLeft: isMobile ? 'none' : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : 'none', background: C.bgCard }}>
                  <div className="proam-kpi-grid proam-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 }}>
                    {[
                      { icon: faDumbbell, label: 'Groups', value: workoutGroupSummary.total },
                      { icon: faBullseye, label: 'Coaching', value: workoutGroupSummary.coaching },
                      { icon: faUser, label: 'Joined', value: workoutGroupSummary.joined },
                      { icon: faBuilding, label: 'Club linked', value: workoutGroupSummary.club },
                    ].map(item => (
                      <div key={item.label} className="proam-kpi-card proam-motion-card" style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 14, padding: 12 }}>
                        <FontAwesomeIcon icon={item.icon} style={{ color: C.accent, fontSize: 13, marginBottom: 8 }} />
                        <div data-kpi-value style={{ color: C.text, fontSize: 22, fontWeight: 950, lineHeight: 1 }}>{item.value}</div>
                        <div data-kpi-label style={{ color: C.textMuted, fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 4 }}>{item.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, borderRadius: 14, padding: 12 }}>
                    <div style={{ color: C.accent, fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Current access</div>
                    <div style={{ color: C.text, fontSize: 14, fontWeight: 900, marginTop: 4 }}>{workoutAccess?.planName || workoutAccess?.tier || 'Free group access'}</div>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, marginTop: 4 }}>{workoutGroupSummary.athletes} athlete{workoutGroupSummary.athletes === 1 ? '' : 's'} tracked across owned groups.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="proam-modern-surface" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>Fitness</div>
	                <h2 className="heading" style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.text, margin: '0 0 10px' }}>Workout Groups</h2>
                <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, lineHeight: 1.4 }}>Create a coached group, join with a code, or open Club Coaching for seasons and staff.</div>
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                <button onClick={() => setShowJoinWG(true)} style={{ ...joinWorkoutGroupButtonSt(C), cursor: 'pointer', padding: '8px 12px', fontSize: 12, minHeight: 40 }}>Join</button>
                <button onClick={loadWG} disabled={loadingWG} style={{ ...tagSt(C), cursor: 'pointer', padding: '8px 12px', fontSize: 12, minHeight: 40 }}>{loadingWG ? '…' : 'Refresh'}</button>
                {canOpenAnyClubDashboard && (
                  <button onClick={() => navigate('/coaching/dashboard')} style={{ ...tagSt(C), cursor: 'pointer', padding: '8px 12px', fontSize: 12, minHeight: 40, background: C.accentBgSubtle, borderColor: C.accentBorder, color: C.accent }}>Club Dashboard</button>
                )}
                <button onClick={openCreateWorkoutGroup} style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.accentGrad, border: 'none', color: '#fff', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontSize: isMobile ? 12 : 13.5, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", minHeight: 44 }}>
                  <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} /> {isMobile ? 'New' : 'New Group'}
                </button>
              </div>
            </div>

            {loadingWG ? <div style={card(C, { padding: '48px', textAlign: 'center', color: C.textMuted, fontWeight: 800 })}>Loading workout groups...</div>
	              : workoutGroups.length === 0 ? (
	                <div style={card(C, { padding: '48px 24px', textAlign: 'center' })}>
	                  <FontAwesomeIcon icon={faDumbbell} style={{ fontSize: 34, marginBottom: 14, color: C.accent }} />
	                  <h3 className="heading" style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: C.text }}>No workout groups yet</h3>
                    <p style={{ color: C.textMuted, fontSize: 13, fontWeight: 700, lineHeight: 1.5, margin: '0 auto 16px', maxWidth: 360 }}>Create a group if you coach athletes, or join one with the code your coach shared.</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
	                    <button onClick={openCreateWorkoutGroup} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: C.accentGrad, border: 'none', color: '#fff', padding: '12px 22px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, minHeight: 44 }}><FontAwesomeIcon icon={faPlus} style={{ fontSize: 12 }} /> Create Workout Group</button>
                      <button onClick={() => setShowJoinWG(true)} style={{ ...joinWorkoutGroupButtonSt(C), cursor: 'pointer', padding: '11px 18px', fontSize: 13, minHeight: 44 }}>Join With Code</button>
                    </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', overflowY: 'visible', paddingTop: isMobile ? 14 : 10, paddingBottom: 8, marginBottom: 16, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                    {workoutGroupSections.map(section => {
                      if (section.type === 'group') return renderWorkoutGroupCard(section.group);
                      const club = section.club;
                      const expanded = expandedWorkoutClubIds.has(club.id);
                      const activeInside = section.groups.some(group => group.id === activeWGId);
                      const sampleGroup = section.groups[0] || {};
                      const t = clubTheme({ ...sampleGroup, workout_clubs: club }, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
                      const themed = t.enabled;
                      return (
                        <div key={club.id} style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexShrink: 0 }}>
                          <button type="button" className="card-lift wg-card" onClick={() => toggleWorkoutClub(club.id)}
                            style={card(C, { position: 'relative', padding: 14, marginTop: isMobile ? 8 : 0, minWidth: isMobile ? 210 : 250, maxWidth: isMobile ? 230 : 280, flexShrink: 0, cursor: 'pointer', textAlign: 'center', borderColor: activeInside ? t.accent : (themed ? t.borderOnPrimary : C.border), background: themed ? `linear-gradient(135deg, ${t.primary} 0%, ${t.surfaceSecondary || t.accent} 100%)` : C.bgCard, boxShadow: themed ? `0 12px 34px ${t.primary}33` : C.shadowMd })}>
                            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ position: 'absolute', top: 14, right: 14, color: themed ? t.onPrimary : C.textMuted, fontSize: 12 }} />
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 11 }}>
                              {club.logo_url ? <img src={club.logo_url} alt={club.name || 'Club'} style={{ width: isMobile ? 78 : 88, height: isMobile ? 78 : 88, borderRadius: 18, objectFit: 'cover', objectPosition: 'center', border: `1px solid ${themed ? t.borderOnPrimary : C.border}`, background: '#FFFFFF' }} /> : <div style={{ width: isMobile ? 78 : 88, height: isMobile ? 78 : 88, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: themed ? t.accent : C.accentBgSubtle, color: themed ? '#fff' : C.accent }}><FontAwesomeIcon icon={faBuilding} style={{ fontSize: 24 }} /></div>}
                            </div>
                            <div style={{ color: themed ? t.mutedOnPrimary : C.textMuted, fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Club</div>
                            <div className="heading" style={{ color: themed ? t.onPrimary : C.text, fontSize: 15, fontWeight: 850, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.12, minHeight: 34 }}>{club.name || 'Club'}</div>
                            <div style={{ color: themed ? t.mutedOnPrimary : C.textMuted, fontSize: 12, fontWeight: 800, marginTop: 6 }}>{section.groups.length} workout group{section.groups.length === 1 ? '' : 's'}</div>
                            <div style={{ marginTop: 10, ...tagSt(C), justifyContent: 'center', background: themed ? t.accent : C.accentBgSubtle, borderColor: themed ? t.borderOnPrimary : C.accentBorder, color: themed ? '#fff' : C.accent }}>
                              {expanded ? 'Hide Groups' : 'Open Groups'}
                            </div>
                          </button>
                          {expanded && section.groups.map(group => renderWorkoutGroupCard(group, { compact: true }))}
                        </div>
                      );
                    })}
                  </div>

	                  {activeWGId && (() => {
				                    const t = clubTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
			                    const themed = t.enabled;
			                    const displayClub = t.club || firstRelationRow(activeWG?.workout_clubs) || null;
			                    const panelText = t.onPrimary || '#fff';
			                    const panelMuted = t.mutedOnPrimary || 'rgba(255,255,255,0.74)';
			                    const wgDetachedSectionStyle = {
			                      background: `linear-gradient(145deg, ${t.primary} 0%, ${t.surfaceSecondary || t.secondary} 100%)`,
			                      border: `1px solid ${t.accent}66`,
			                      borderRadius: 24,
			                      padding: 20,
			                      marginBottom: 18,
			                      boxShadow: `0 18px 54px ${t.primary}1F`,
			                      color: '#fff',
			                      overflow: 'hidden',
			                    };
			                    const wgCalendarInnerPanelStyle = {
			                      border: '1px solid rgba(255,255,255,0.16)',
			                      background: 'rgba(255,255,255,0.08)',
			                      borderRadius: 16,
			                      padding: 14,
			                    };
			                    const wgDashboardSubsectionStyle = {
			                      border: '1px solid rgba(255,255,255,0.14)',
			                      background: 'rgba(255,255,255,0.07)',
			                      borderRadius: 18,
			                      padding: 14,
			                      marginTop: 14,
			                    };
		                    const wgCalendarEyebrowStyle = {
		                      color: 'rgba(255,255,255,0.68)',
		                      fontSize: 10,
		                      fontWeight: 950,
		                      textTransform: 'uppercase',
		                      letterSpacing: 0.5,
		                    };
	                    const calorieDaysFactor = Math.max(1, Number(wgNutritionDays || 1));
	                    const sessionTarget = Number(activeWG?.weekly_session_target || 3);
	                    const groupTarget = Number(activeWG?.weekly_xp_target || 1000);
	                    const calorieTarget = Number(activeWG?.daily_calorie_target || 2500) * calorieDaysFactor;
	                    const athleteTarget = wgMembers.length ? Math.round(groupTarget / wgMembers.length) : groupTarget;
	                    const athleteRows = wgMembers.map(m => {
	                      const p = perfMap?.[m.id] || {};
	                      const xp = Number(p.rangeXp || 0);
	                      const sessions = Number(p.sessionsInRange || 0);
	                      const calories = Number(p.caloriesInRange || 0);
		                      return { athlete: m, xp, sessions, calories, calorieTarget, xpTarget: athleteTarget, status: getWGAttentionStatus(xp, athleteTarget, sessions, sessionTarget) };
	                    });
	                    const attentionSessionCutoff = Math.ceil(sessionTarget * 0.75);
	                    const needAttentionCount = athleteRows.filter(row => row.sessions < attentionSessionCutoff).length;
	                    const onPlanCount = athleteRows.filter(row => row.sessions >= sessionTarget).length;
	                    const topPerformer = athleteRows.slice().sort((a, b) => (b.sessions - a.sessions) || (b.xp - a.xp))[0];
	                    const completion = groupTarget > 0 ? Math.min(100, Math.round(((groupStats?.totalXp || 0) / groupTarget) * 100)) : 0;
	                    const completedSessionTotal = Number(groupStats?.totalSessions || 0);
	                    const expectedSessionTotal = sessionTarget * wgMembers.length;
	                    const sessionCompletion = expectedSessionTotal > 0
	                      ? Math.min(100, Math.round((completedSessionTotal / expectedSessionTotal) * 100))
	                      : 0;
				                    const currentCycleHeadline = wgMembers.length === 0
		                      ? 'No athletes yet'
		                      : needAttentionCount > 0
		                        ? `${needAttentionCount} athlete${needAttentionCount === 1 ? ' needs' : 's need'} attention`
		                        : 'No attention needed';
		                    const currentCycleStatusTone = wgMembers.length === 0
		                      ? { bg: 'rgba(255,255,255,0.12)', border: 'rgba(255,255,255,0.22)', text: '#FFFFFF', icon: faUsers }
		                      : needAttentionCount > 0
		                        ? { bg: 'rgba(253,230,138,0.14)', border: 'rgba(253,230,138,0.42)', text: '#FDE68A', icon: faTriangleExclamation }
		                        : { bg: 'rgba(187,247,208,0.14)', border: 'rgba(187,247,208,0.42)', text: '#BBF7D0', icon: faCheck };
		                    const currentCycleSubtitle = expectedSessionTotal > 0
		                      ? needAttentionCount > 0
		                        ? 'Several athletes are behind the weekly session target. Open analytics to see who needs a follow-up.'
		                        : 'The squad is tracking well against the weekly session target.'
		                      : 'Add athletes to start tracking session completion.';
	                    const calendarLocked = activeWGCanManage && !activeWGHasCalendarAccess;
	                    const scrollToWorkoutGroupSection = (ref) => {
	                      window.requestAnimationFrame(() => {
	                        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	                      });
	                    };
		                    return (
                    <>
                    <div ref={workoutDashboardRef} className="fade-in proam-modern-surface" style={{ ...wgDetachedSectionStyle, scrollMarginTop: 76 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0, flex: '1 1 320px' }}>
                          <WorkoutGroupLogo C={C} group={activeWG} club={displayClub} themed={themed} size={44} radius={14} iconSize={17} />
                          <div style={{ minWidth: 0, flex: 1 }}>
	                            <div style={{ color: 'rgba(255,255,255,0.66)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.7 }}>Workout Group Dashboard</div>
	                            <h2 style={{ margin: '6px 0 0', fontSize: isMobile ? 28 : 'clamp(30px,3vw,42px)', lineHeight: 1.05, color: panelText, fontWeight: 950, letterSpacing: 0, whiteSpace: 'normal', overflowWrap: 'break-word' }}>{activeWG?.name || 'Group Overview'}</h2>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.84)', fontSize: 12, fontWeight: 900, marginTop: 8, maxWidth: '100%' }}>
                                <img src={activeWG?.coachProfileImg || noImage} alt="" loading="lazy" style={{ width: 32, height: 32, borderRadius: 999, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.24)', flexShrink: 0 }} />
                                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Lead coach: {activeWG?.coachName || 'Unassigned'}</span>
                              </div>
                          </div>
                        </div>
	                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', flex: '1 1 520px' }}>
	                          {canManageWG() && (
	                            <button
	                              type="button"
	                              onClick={() => setWorkoutGroupManageOpen(true)}
	                              style={{ border: '1px solid rgba(255,255,255,0.28)', background: '#fff', color: t.primary, borderRadius: 12, padding: '10px 13px', fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
	                            >
	                              <FontAwesomeIcon icon={faGear} style={{ fontSize: 12 }} />
	                              Manage Group
	                            </button>
	                          )}
                          {activeWGCanLeave && (
                            <button onClick={() => removeWGMember(activeWGCurrentMember)} style={{ border: '1px solid rgba(248,113,113,0.38)', background: '#FFFFFF', color: C.danger, borderRadius: 12, padding: '9px 11px', fontWeight: 950, cursor: 'pointer' }}>
                              Leave Group
                            </button>
                          )}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select value={wgRangeMode} onChange={e => selectWGRange(e.target.value)} style={{ border: `1px solid ${themed ? 'rgba(255,255,255,0.22)' : C.border}`, background: themed ? 'rgba(255,255,255,0.12)' : C.bgAlt, color: themed ? '#fff' : C.textSecondary, borderRadius: 10, padding: '7px 9px', fontWeight: 900, fontSize: 11, outline: 'none' }}>
                            {WORKOUT_GROUP_PROFILE_RANGE_OPTIONS.map(option => <option key={option.value} value={option.value} style={{ color: C.text }}>{option.label}</option>)}
                            {wgClubSeasons.length > 0 && (
                              <optgroup label="Seasons">
                                {wgClubSeasons.map(season => <option key={season.id} value={`season:${season.id}`} style={{ color: C.text }}>{season.name}</option>)}
                              </optgroup>
                            )}
                          </select>
                          {wgRangeMode === 'date' && (
                            <>
                              <label htmlFor="wg-dashboard-date-range" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Review date</label>
                              <input
                                id="wg-dashboard-date-range"
                                type="date"
                                value={wgSelectedDate}
                                onChange={e => setWgSelectedDate(e.target.value || localDateInputValue(new Date()))}
                                style={{ border: `1px solid ${themed ? 'rgba(255,255,255,0.22)' : C.border}`, background: themed ? 'rgba(255,255,255,0.12)' : C.bgAlt, color: themed ? '#fff' : C.textSecondary, borderRadius: 10, padding: '7px 9px', fontWeight: 900, fontSize: 11, outline: 'none', minHeight: 34 }}
                              />
                            </>
                          )}
                          {wgRangeMode === 'custom' && (
                            <input type="number" min="1" max="1095" value={wgCustomDays} onChange={e => {
                              setWgCustomDays(e.target.value);
                              const days = Math.min(Math.max(Number(e.target.value) || 1, 1), 1095);
                              setTimeRange(days);
                            }} style={{ width: 76, border: `1px solid ${themed ? 'rgba(255,255,255,0.22)' : C.border}`, background: themed ? 'rgba(255,255,255,0.12)' : C.bgAlt, color: themed ? '#fff' : C.textSecondary, borderRadius: 10, padding: '7px 8px', fontWeight: 900, fontSize: 11, outline: 'none' }} />
                          )}
	                        </div>
	                        </div>
	                      </div>
		                      <div style={{ position: 'relative', overflow: 'hidden', border: 'none', background: 'linear-gradient(135deg,#0C69C8 0%,#0D275B 100%)', borderRadius: 24, padding: isMobile ? 16 : 18, marginBottom: 14 }}>
		                        <span aria-hidden="true" style={{ position: 'absolute', right: -28, top: 14, width: 180, height: 92, borderRadius: 36, background: 'rgba(255,255,255,0.08)', transform: 'rotate(-18deg)' }} />
			                        <FontAwesomeIcon aria-hidden="true" icon={faChartLine} style={{ position: 'absolute', right: 26, bottom: 18, color: '#22D3EE', opacity: 0.16, fontSize: isMobile ? 54 : 76 }} />
			                        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.35fr) minmax(220px, .65fr)', gap: 16, alignItems: 'center' }}>
		                          <div>
			                            <div style={{ color: '#22D3EE', fontSize: 13, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Team story</div>
		                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginTop: 10, border: `1px solid ${currentCycleStatusTone.border}`, background: currentCycleStatusTone.bg, color: currentCycleStatusTone.text, borderRadius: 999, padding: '9px 12px', fontSize: 14, lineHeight: 1.1, fontWeight: 950 }}>
		                              <FontAwesomeIcon icon={currentCycleStatusTone.icon} style={{ fontSize: 12 }} />
		                              {currentCycleHeadline}
		                            </div>
		                            <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: 15, fontWeight: 850, lineHeight: 1.45, marginTop: 10, maxWidth: 620 }}>{currentCycleSubtitle}</div>
	                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
	                              <button type="button" onClick={() => setWorkoutGroupAnalyticsOpen(true)} style={{ border: '1px solid rgba(255,255,255,0.20)', background: '#fff', color: '#0D275B', borderRadius: 999, padding: '10px 13px', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 950, cursor: 'pointer' }}>
	                                <FontAwesomeIcon icon={faCalendar} style={{ color: t.primary }} />
	                                Open group analytics
	                              </button>
	                              {canManageWG() && wgMembers.length > 0 && (
	                                <button type="button" onClick={() => confirmWGNotification({ message: 'Quick reminder to get your workout session done this week.', type: 'GROUP_REMINDER' })} style={{ border: '1px solid rgba(253,230,138,0.40)', background: 'rgba(253,230,138,0.14)', color: '#FDE68A', borderRadius: 999, padding: '10px 13px', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 950, cursor: 'pointer' }}>
	                                  <FontAwesomeIcon icon={faEnvelope} />
	                                  Remind
	                                </button>
	                              )}
	                            </div>
	                          </div>
			                          <div style={{ border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.10)', borderRadius: 22, padding: 16 }}>
			                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
			                              <div style={{ color: 'rgba(255,255,255,0.80)', fontSize: 13, fontWeight: 950, textTransform: 'uppercase' }}>Session completion</div>
			                              <div style={{ color: '#fff', fontSize: 15, fontWeight: 950 }}>{sessionCompletion}%</div>
			                            </div>
			                            <div style={{ height: 10, background: 'rgba(255,255,255,0.20)', borderRadius: 999, overflow: 'hidden', marginTop: 11 }}>
				                              <div style={{ height: '100%', width: `${sessionCompletion}%`, background: '#22D3EE', borderRadius: 999 }} />
			                            </div>
			                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
			                              <div style={{ color: '#fff', fontSize: 30, fontWeight: 950, lineHeight: 1 }}>{completedSessionTotal.toLocaleString()}</div>
			                              <div style={{ color: '#fff', fontSize: 30, fontWeight: 950, lineHeight: 1, textAlign: 'right' }}>{expectedSessionTotal.toLocaleString()}</div>
			                              <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, fontWeight: 900 }}>completed</div>
			                              <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, fontWeight: 900, textAlign: 'right' }}>expected</div>
			                            </div>
			                            <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: 13, fontWeight: 850, lineHeight: 1.35, marginTop: 11 }}>
			                              {sessionTarget} each x {wgMembers.length} athletes
			                            </div>
			                          </div>
	                        </div>
	                      </div>
			                      <div style={wgDashboardSubsectionStyle}>
                        <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 9 }}>Shortcuts</div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                          {[
                            {
                              key: 'calendar',
                              icon: calendarLocked ? faLock : faCalendar,
                              title: 'Calendar',
                              sub: calendarLocked ? 'Preview sessions' : 'Sessions & attendance',
                              onClick: () => scrollToWorkoutGroupSection(workoutGroupCalendarRef),
                            },
	                            {
	                              key: 'groupAnalytics',
			                              icon: faChartLine,
			                              title: 'Group analytics',
			                              sub: 'Performance trends',
			                              onClick: () => setWorkoutGroupAnalyticsOpen(true),
	                            },
                            {
                              key: 'athleteAnalytics',
                              icon: faUsers,
                              title: 'Athlete analytics',
                              sub: 'Tap a member to open',
                              onClick: () => scrollToWorkoutGroupSection(workoutGroupMembersRef),
                            },
                            canManageWG() && {
                              key: 'assignedWorkouts',
                              icon: faDumbbell,
                              title: `Allowed Workouts (${getAllowedWorkoutNamesList(activeWG).length})`,
                              sub: 'Assigned plans',
                              onClick: updateWGAllowedWorkouts,
                            },
                            canManageWG() && {
                              key: 'inviteCode',
                              icon: activeWG?.join_code ? faCopy : faBolt,
                              title: activeWG?.join_code ? 'Invite code' : 'Create invite',
                              sub: activeWG?.join_code || 'Generate code',
                              onClick: () => getJoinCode(activeWG),
                            },
                            activeWGCanOpenClubCoaching && {
                              key: 'clubDashboard',
                              icon: faBuilding,
                              title: activeWG?.club_id ? 'Club dashboard' : 'Create club',
                              sub: 'Coaches & club tools',
                              onClick: () => navigate(`/coaching/dashboard?group_id=${encodeURIComponent(activeWG.id)}&return_tab=workouts`),
                            },
                          ].filter(Boolean).map(action => (
                            <button
                              key={action.key}
                              type="button"
                              onClick={action.onClick}
                              style={{ minHeight: 68, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', borderRadius: 15, padding: 10, cursor: 'pointer', display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 9, alignItems: 'center', textAlign: 'left' }}
                            >
                              <span style={{ width: 34, height: 34, borderRadius: 13, display: 'grid', placeItems: 'center', background: action.key === 'inviteCode' ? '#FFFFFF' : 'rgba(255,255,255,0.12)', color: action.key === 'inviteCode' ? t.primary : '#fff', border: '1px solid rgba(255,255,255,0.20)' }}>
                                <FontAwesomeIcon icon={action.icon} style={{ fontSize: 14, color: action.key === 'inviteCode' ? t.primary : '#fff' }} />
                              </span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: 'block', color: '#fff', fontSize: 12, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{action.title}</span>
                                <span style={{ display: 'block', color: panelMuted, fontSize: 10, fontWeight: 800, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{action.sub}</span>
                              </span>
                            </button>
                          ))}
                        </div>
	                      </div>
	                      <div style={wgDashboardSubsectionStyle}>
                        <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 9 }}>Group summary</div>
		                      <div className="stats-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
	                        {[
	                          { icon: faCalendar, label: 'Completed sessions', val: numberFmt(groupStats?.totalSessions ?? 0), color: '#16A34A', bg: '#EAFBF0' },
		                          { icon: faBolt, label: 'Total XP', val: numberFmt(groupStats?.totalXp ?? 0), color: t.primary || '#0C69C8', bg: rgba(t.primary || '#0C69C8', 0.10) },
		                          { icon: faRepeat, label: 'Completed reps', val: compactWorkoutGroupNumber(groupStats?.totalReps ?? 0), color: t.surfaceSecondary || t.accent || '#111827', bg: rgba(t.surfaceSecondary || t.accent || '#111827', 0.10) },
		                          { icon: faDumbbell, label: 'Completed volume', val: formatWorkoutGroupVolume(groupStats?.totalVolume ?? 0), color: t.accent || '#D5A419', bg: rgba(t.accent || '#D5A419', 0.10) },
	                        ].map(m => <WGAppMetricCard key={m.label} icon={m.icon} label={m.label} value={m.val} color={m.color} bg={m.bg} />)}
		                      </div>
                      </div>
			                      <div ref={workoutGroupAnalyticsRef} style={{ ...wgDashboardSubsectionStyle, scrollMarginTop: 76 }}>
	                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
	                          <div>
	                            <h3 style={{ margin: 0, fontSize: 18 }}>Performance Analysis</h3>
	                            <div style={{ color: '#fff', fontSize: 12, fontWeight: 850, marginTop: 3 }}>{sessionTarget} sessions per athlete</div>
	                          </div>
	                          <div style={{ color: '#fff', fontSize: 13, fontWeight: 950 }}>{sessionCompletion}% of session target</div>
	                        </div>
	                        <div style={{ height: 9, background: 'rgba(255,255,255,0.24)', borderRadius: 999, overflow: 'hidden', marginBottom: 12 }}>
		                          <div style={{ height: '100%', width: `${sessionCompletion}%`, background: '#fff', borderRadius: 999 }} />
	                        </div>
                            <div style={{ color: 'rgba(255,255,255,0.74)', fontSize: 11, fontWeight: 850, marginBottom: 12 }}>
                              {completedSessionTotal.toLocaleString()} / {expectedSessionTotal.toLocaleString()} sessions completed
                            </div>
		                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
		                          {[
		                            { label: 'On Plan', value: `${onPlanCount}/${wgMembers.length}`, detail: 'athletes meeting targets' },
		                            { label: 'XP output', value: `${completion}%`, detail: `${Number(groupStats?.totalXp || 0).toLocaleString()} / ${groupTarget.toLocaleString()} XP` },
		                            { label: needAttentionCount > 0 ? 'Needs Activity' : 'No attention needed', value: needAttentionCount, detail: needAttentionCount > 0 ? '' : 'all visible athletes on track', avatars: athleteRows.filter(row => row.sessions < attentionSessionCutoff).map(row => row.athlete?.profile_img || noImage) },
		                            { label: 'Top Athlete', value: topPerformer ? ((`${topPerformer.athlete.name || ''} ${topPerformer.athlete.surname || ''}`).trim() || topPerformer.athlete.username || 'Unknown') : 'None', detail: `${Number(topPerformer?.sessions || 0).toLocaleString()}/${sessionTarget} sessions · ${Number(topPerformer?.xp || 0).toLocaleString()} XP`, image: topPerformer?.athlete?.profile_img || noImage },
	                          ].map(item => (
	                            <div key={item.label} style={{ border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.09)', borderRadius: 12, padding: '11px 12px', display: item.image ? 'grid' : 'block', gridTemplateColumns: item.image ? 'minmax(0, 1fr) 44px' : undefined, gap: item.image ? 10 : undefined, alignItems: 'center' }}>
                                <div style={{ minWidth: 0 }}>
	                                <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{item.label}</div>
	                                <div style={{ color: '#fff', fontSize: 17, fontWeight: 950, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</div>
	                                {item.detail ? <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 11, fontWeight: 800, marginTop: 2 }}>{item.detail}</div> : null}
                                  {item.avatars?.length ? (
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 8, overflowX: 'auto', paddingBottom: 1 }}>
                                      {item.avatars.map((src, index) => (
                                        <img key={`${item.label}_${index}`} src={src} alt="" style={{ width: 28, height: 28, borderRadius: 999, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.28)', marginLeft: index === 0 ? 0 : -7, background: '#FFFFFF', flexShrink: 0 }} loading="lazy" />
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                                {item.image ? <img src={item.image} alt="" style={{ width: 44, height: 44, borderRadius: 14, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.24)' }} loading="lazy" /> : null}
		                            </div>
		                          ))}
			                        </div>
				                      </div>
						                      <div ref={workoutGroupMembersRef} style={{ ...wgDashboardSubsectionStyle, scrollMarginTop: 76 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
                          <h3 style={{ margin: 0, fontSize: 18 }}>Members</h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {canManageWG() && wgMembers.length > 0 && (
                              <>
                                <button title="Send training reminder to all members" onClick={() => confirmWGNotification({ message: 'Quick reminder to get your workout session done this week.', type: 'GROUP_REMINDER' })} style={{ border: `1px solid ${themed ? 'rgba(255,255,255,0.24)' : C.border}`, background: themed ? 'rgba(255,255,255,0.10)' : C.bgAlt, color: themed ? '#fff' : C.textSecondary, borderRadius: 999, padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, fontWeight: 900 }}>
                                  <FontAwesomeIcon icon={faEnvelope} />
                                  <span>Training Reminder</span>
                                </button>
                                {activeWGCanUseNutritionStats && (
                                  <button title="Send calorie reminder to all members" onClick={() => confirmWGNotification({ message: `Reminder: ${activeWG?.name || 'your group'} has a ${Number(activeWG?.daily_calorie_target || 2500).toLocaleString()} kcal daily target. Log your meals in ProAm Nutrition.`, type: 'GROUP_CALORIE_REMINDER' })} style={{ border: `1px solid ${themed ? 'rgba(253,230,138,0.45)' : '#FED7AA'}`, background: themed ? 'rgba(253,230,138,0.16)' : '#FFF7ED', color: themed ? '#FDE68A' : '#9A3412', borderRadius: 999, padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, fontWeight: 900 }}>
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    <span>Calorie Reminder</span>
                                  </button>
                                )}
                              </>
	                            )}
	                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.70)', fontWeight: 850 }}>{wgMembers.length} total</span>
	                      </div>
                      </div>
		                        {!wgMembers.length ? <div style={{ border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.10)', borderRadius: 14, padding: 16, color: 'rgba(255,255,255,0.70)', fontWeight: 850 }}>No members yet.</div> : (
                          <div style={{ borderRadius: 14, overflow: 'hidden', border: themed ? 'none' : '1px solid rgba(255,255,255,0.18)' }}>
	                            <div className="wg-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '40px minmax(0, 1fr) auto' : '48px minmax(260px, 2.2fr) minmax(130px, .95fr) minmax(128px, .9fr) minmax(120px, .9fr) 74px', gap: 10, padding: '9px 12px', background: 'rgba(255,255,255,0.12)', fontSize: 10, fontWeight: 950, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
	                              <div>Rank</div><div>Athlete</div><div style={{ textAlign: 'right' }}>Sessions</div>
	                              {!isMobile && <><div style={{ textAlign: 'right' }}>Nutrition</div><div style={{ textAlign: 'right' }}>Status</div><div style={{ textAlign: 'right' }}>Action</div></>}
	                            </div>
	                            {wgMembers.map(m => {
	                              const p = perfMap?.[m.id] || {};
	                              const dn = `${m.name || ''} ${m.surname || ''}`.trim() || (m.username ? `@${m.username}` : 'Unknown');
	                              const xp = Number(p.rangeXp ?? 0);
	                              const sess = Number(p.sessionsInRange ?? 0);
	                              const calories = Number(p.caloriesInRange ?? 0);
		                              return { member: m, id: m.id, dn, username: m.username || null, img: m.profile_img || null, xp, sess, calories, xpTarget: athleteTarget, status: getWGAttentionStatus(xp, athleteTarget, sess, sessionTarget) };
	                            }).sort((a, b) => (b.sess - a.sess) || (b.xp - a.xp)).map((row, i) => {
		                              const rowXpTarget = Number(row.xpTarget || 0);
		                              const pp = rowXpTarget > 0 ? Math.min(100, Math.round((row.xp / rowXpTarget) * 100)) : 0;
                                const sessionPct = sessionTarget > 0 ? Math.min(100, Math.round((row.sess / sessionTarget) * 100)) : 0;
                                const rankLabel = challengePlaceLabel(i + 1);
                                const rankMeta = i === 0
                                  ? { bg: 'rgba(212,160,23,0.96)', text: '#FFFFFF', border: 'rgba(255,255,255,0.34)' }
                                  : i === 1
                                    ? { bg: 'rgba(203,213,225,0.96)', text: '#0A1D44', border: 'rgba(255,255,255,0.34)' }
                                    : i === 2
                                      ? { bg: 'rgba(180,83,9,0.96)', text: '#FFFFFF', border: 'rgba(255,255,255,0.34)' }
                                      : { bg: 'rgba(255,255,255,0.10)', text: '#FFFFFF', border: 'rgba(255,255,255,0.14)' };
	                              const caloriePct = calorieTarget > 0 ? Math.min(100, Math.round((row.calories / calorieTarget) * 100)) : 0;
	                              const hasCalorieProgress = caloriePct > 0;
	                              return (
	                                <div key={row.id} onClick={() => openWGAthlete(row.member)} className="wg-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '40px minmax(0, 1fr) auto' : '48px minmax(280px, 2.2fr) minmax(130px, .95fr) minmax(128px, .9fr) minmax(120px, .9fr) 74px', gap: 12, padding: '13px 12px', borderTop: '1px solid rgba(255,255,255,0.12)', alignItems: 'center', background: 'rgba(255,255,255,0.055)', cursor: 'pointer' }}>
	                                  <div aria-label={`Rank ${rankLabel}`} style={{ width: isMobile ? 38 : 42, minHeight: 30, borderRadius: 999, background: rankMeta.bg, color: rankMeta.text, border: `1px solid ${rankMeta.border}`, display: 'inline-grid', placeItems: 'center', fontWeight: 950, fontSize: 12, letterSpacing: 0, boxShadow: i < 3 ? '0 8px 18px rgba(0,0,0,0.16)' : 'none' }}>{rankLabel}</div>
	                                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
	                                    <img src={row.img || noImage} alt="" style={{ width: isMobile ? 46 : 52, height: isMobile ? 46 : 52, borderRadius: 14, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(255,255,255,0.24)' }} loading="lazy" />
	                                    <div style={{ minWidth: 0, flex: 1 }}>
	                                      <div style={{ fontWeight: 950, color: '#fff', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.dn}</div>
	                                      {row.username && !isMobile && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.62)', fontWeight: 800 }}>@{row.username}</div>}
		                                      <div style={{ display: 'grid', gap: 5, marginTop: 7, maxWidth: 220 }}>
                                          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(72px, 132px) auto', gap: 8, alignItems: 'center' }}>
	                                            <Bar C={C} p={pp} fill="#FFFFFF" track="rgba(255,255,255,0.16)" border="none" height={5} />
	                                            <span style={{ color: '#FFFFFF', fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>{row.xp.toLocaleString()} XP</span>
                                          </div>
                                          {activeWGCanUseNutritionStats && (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(72px, 132px) auto', gap: 8, alignItems: 'center' }}>
                                              <Bar C={C} p={caloriePct} fill={hasCalorieProgress ? '#FDE68A' : 'transparent'} track="rgba(253,230,138,0.16)" border="none" height={5} />
                                              <span style={{ color: hasCalorieProgress ? '#FDE68A' : panelMuted, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>{row.calories.toLocaleString()} / {calorieTarget.toLocaleString()} kcal</span>
                                            </div>
                                          )}
                                        </div>
	                                    </div>
	                                  </div>
	                                  <div style={{ display: 'grid', justifyItems: 'end', gap: 7 }}>
                                      <span style={{ color: '#fff', fontSize: isMobile ? 13 : 14, lineHeight: 1, fontWeight: 950, whiteSpace: 'nowrap' }}>
                                        {row.sess}/{sessionTarget} sessions
                                      </span>
                                      {!isMobile && (
                                        <div style={{ width: '100%', maxWidth: 118 }}>
	                                          <Bar C={C} p={sessionPct} fill="#FFFFFF" track="rgba(255,255,255,0.18)" border="none" height={6} />
                                        </div>
                                      )}
                                    </div>
                                    {!isMobile && (
                                      <div style={{ display: 'grid', justifyItems: 'end', gap: 6 }}>
                                        <div style={{ color: hasCalorieProgress ? '#FDE68A' : panelMuted, fontSize: 13, fontWeight: 950 }}>{caloriePct}%</div>
                                        <div style={{ color: panelMuted, fontSize: 10, fontWeight: 850 }}>{row.calories.toLocaleString()} kcal</div>
                                      </div>
                                    )}
	                                  {!isMobile && <div style={{ textAlign: 'right' }}><span style={{ display: 'inline-flex', borderRadius: 99, padding: '4px 8px', background: row.status.bg, border: `1px solid ${row.status.border}`, color: row.status.text, fontSize: 10, fontWeight: 900 }}>{row.status.label}</span></div>}
	                                  {(canManageWG() || row.id === profileData.id) && (
	                                    <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 6, alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
	                                      <div style={{ display: 'flex', gap: 6, justifyContent: isMobile ? 'flex-start' : 'flex-end', flexWrap: 'wrap' }}>
	                                        <button type="button" aria-label={`Send training reminder to ${row.dn}`} title="Send training reminder" onClick={(e) => { e.stopPropagation(); confirmWGNotification({ athleteId: row.id, message: 'Quick reminder to get your workout session done this week.', type: 'GROUP_REMINDER' }); }} style={{ border: `1px solid ${themed ? 'rgba(255,255,255,0.24)' : C.border}`, background: themed ? 'rgba(255,255,255,0.10)' : C.bgAlt, color: themed ? '#fff' : C.textSecondary, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 900 }}>
	                                          <FontAwesomeIcon icon={faEnvelope} />
	                                        </button>
	                                        {activeWGCanUseNutritionStats && (
	                                          <button type="button" aria-label={`Send calorie reminder to ${row.dn}`} title="Send calorie reminder" onClick={(e) => { e.stopPropagation(); confirmWGNotification({ athleteId: row.id, message: `Reminder: ${activeWG?.name || 'your group'} has a ${Number(activeWG?.daily_calorie_target || 2500).toLocaleString()} kcal daily target. Log your meals in ProAm Nutrition.`, type: 'GROUP_CALORIE_REMINDER' }); }} style={{ border: `1px solid ${themed ? 'rgba(253,230,138,0.45)' : '#FED7AA'}`, background: themed ? 'rgba(253,230,138,0.16)' : '#FFF7ED', color: themed ? '#FDE68A' : '#9A3412', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 900 }}>
	                                            <FontAwesomeIcon icon={faEnvelope} />
	                                          </button>
	                                        )}
	                                      </div>
	                                      <button type="button" onClick={(e) => { e.stopPropagation(); removeWGMember(row.member); }} style={{ border: `1px solid ${row.id === profileData.id ? 'rgba(255,255,255,0.46)' : (themed ? 'rgba(255,255,255,0.24)' : C.border)}`, background: row.id === profileData.id ? '#FFFFFF' : (themed ? 'rgba(255,255,255,0.10)' : C.bgAlt), color: row.id === profileData.id ? C.danger : (themed ? '#fff' : C.danger), borderRadius: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 900 }}>
	                                        {row.id === profileData.id ? 'Leave' : 'Remove'}
	                                      </button>
	                                    </div>
	                                  )}
	                                  {!(canManageWG() || row.id === profileData.id) && !isMobile && <div />}
                                </div>
                              );
	                            })}
	                          </div>
	                        )}
	                      </div>
	                    </div>
				                          <section ref={workoutGroupCalendarRef} style={{ ...wgDetachedSectionStyle, scrollMarginTop: 76 }}>
				                          <div style={{ ...wgCalendarInnerPanelStyle, position: 'relative', overflow: 'hidden' }}>
			                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) auto', gap: 12, alignItems: 'start' }}>
	                            <div style={{ minWidth: 0 }}>
	                              <div style={wgCalendarEyebrowStyle}>Schedule</div>
	                              <h3 style={{ margin: '4px 0 0', fontSize: 18, color: '#fff' }}>Session controls</h3>
	                              <div style={{ color: panelMuted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>
	                                Manage training dates, athlete responses, coaches, and alerts.
	                      </div>
	                    </div>
	                            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
	                              <button type="button" onClick={() => loadWGCalendarSessions()} disabled={wgCalendarLoading} style={{ border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 999, padding: '8px 11px', fontSize: 11, fontWeight: 950, cursor: wgCalendarLoading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
	                                <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 10 }} />
	                                {wgCalendarLoading ? 'Refreshing...' : 'Refresh'}
	                              </button>
	                              {activeWGCanManage && (
	                                <button type="button" onClick={openWGCalendarCreate} style={{ border: 'none', background: '#fff', color: t.primary, borderRadius: 999, padding: '8px 12px', fontSize: 11, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
	                                  <FontAwesomeIcon icon={calendarLocked ? faLock : faPlus} style={{ fontSize: 10 }} />
	                                  Create Session
	                                </button>
	                              )}
	                            </div>
	                          </div>
	                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(3, minmax(120px, 1fr))', gap: 8, marginTop: 13 }}>
	                            {WORKOUT_GROUP_CALENDAR_FILTERS.map(option => {
	                              const active = wgCalendarFilter === option.value;
	                              return (
	                                <button
	                                  key={option.value}
	                                  type="button"
	                                  onClick={() => {
	                                    setWgCalendarFilter(option.value);
	                                    if (option.value !== 'upcoming') setWgCalendarSelectedDateKey(null);
	                                  }}
	                                  aria-pressed={active}
	                                  style={{ minHeight: 70, textAlign: 'left', border: `1px solid ${active ? '#fff' : 'rgba(255,255,255,0.18)'}`, background: active ? '#fff' : 'rgba(255,255,255,0.08)', color: active ? t.primary : '#fff', borderRadius: 15, padding: '10px 11px', cursor: 'pointer' }}
	                                >
	                                  <div style={{ fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{option.label}</div>
	                                  <div style={{ fontSize: 22, fontWeight: 950, lineHeight: 1, marginTop: 8 }}>{wgCalendarSessionCounts[option.value] || 0}</div>
	                                </button>
	                              );
	                            })}
	                          </div>
		                        </div>

	                        {calendarLocked && (
		                          <div style={{ ...wgCalendarInnerPanelStyle, borderColor: 'rgba(253,230,138,0.45)', background: 'rgba(253,230,138,0.14)', color: '#FDE68A', marginTop: 12 }}>
	                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
	                              <FontAwesomeIcon icon={faLock} style={{ marginTop: 2, fontSize: 12 }} />
	                              <div style={{ fontSize: 12, fontWeight: 850, lineHeight: 1.45 }}>
	                                Calendar sessions are included with Pro Coach and Club Coaching. Existing sessions remain visible; creating or restoring sessions needs the calendar entitlement.
	                              </div>
	                            </div>
		                          </div>
	                        )}

	                        {wgCalendarFilter === 'upcoming' && (
		                          <div style={{ ...wgCalendarInnerPanelStyle, marginTop: 12 }}>
	                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 11 }}>
	                              <div>
	                                <div style={wgCalendarEyebrowStyle}>Date selector</div>
	                                <h3 style={{ margin: '4px 0 0', fontSize: 18, color: '#fff' }}>Next 14 days</h3>
	                              </div>
	                              {wgCalendarSelectedDateKey && (
	                                <button type="button" onClick={() => setWgCalendarSelectedDateKey(null)} style={{ border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 999, padding: '7px 10px', fontSize: 11, fontWeight: 950, cursor: 'pointer' }}>
	                                  Clear date
	                                </button>
	                              )}
	                            </div>
		                            <div style={{ overflowX: 'auto', padding: '2px 0 1px', WebkitOverflowScrolling: 'touch' }}>
		                              <div style={{ display: 'flex', gap: 8, width: 'max-content', margin: '0 auto' }}>
		                              {wgCalendarUpcomingDayChips.map(day => {
	                                const selected = wgCalendarSelectedDateKey === day.key;
	                                return (
	                                  <button
	                                    key={day.key}
	                                    type="button"
	                                    onClick={() => setWgCalendarSelectedDateKey(selected ? null : day.key)}
	                                    aria-pressed={selected}
	                                    title={day.label}
	                                    style={{ minWidth: 58, border: `1px solid ${selected ? '#fff' : 'rgba(255,255,255,0.22)'}`, background: selected ? '#fff' : day.count ? 'rgba(12,105,200,0.42)' : 'rgba(255,255,255,0.08)', color: selected ? t.primary : '#fff', borderRadius: 15, padding: '9px 8px', cursor: 'pointer', textAlign: 'center', flexShrink: 0 }}
	                                  >
	                                    <div style={{ fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{day.day}</div>
	                                    <div style={{ fontSize: 18, fontWeight: 950, lineHeight: 1.1, marginTop: 2 }}>{day.date}</div>
	                                    <div style={{ marginTop: 5, minHeight: 18, display: 'grid', placeItems: 'center' }}>
	                                      <span style={{ minWidth: 18, height: 18, borderRadius: 999, display: 'inline-grid', placeItems: 'center', background: selected ? t.primary : (day.count ? '#fff' : 'rgba(255,255,255,0.14)'), color: selected ? '#fff' : (day.count ? t.primary : 'rgba(255,255,255,0.58)'), fontSize: 10, fontWeight: 950 }}>
	                                        {day.count}
	                                      </span>
	                                    </div>
	                                  </button>
	                                );
		                              })}
		                              </div>
		                            </div>
		                          </div>
	                        )}

		                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.05fr) minmax(300px,.95fr)', gap: 12, alignItems: 'start', marginTop: 12 }}>
		                          <section style={wgCalendarInnerPanelStyle}>
	                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 11 }}>
	                              <div>
	                                <div style={wgCalendarEyebrowStyle}>Sessions</div>
	                                <h3 style={{ margin: '4px 0 0', fontSize: 18, color: '#fff' }}>{WORKOUT_GROUP_CALENDAR_FILTERS.find(option => option.value === wgCalendarFilter)?.label || 'Sessions'}</h3>
	                              </div>
	                              <span style={{ border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: panelMuted, borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 950 }}>
	                                {displayedWGCalendarSessions.length} shown
	                              </span>
	                            </div>
	                            <div style={{ display: 'grid', gap: 10 }}>
	                              {wgCalendarLoading ? (
	                                <div style={{ border: '1px dashed rgba(255,255,255,0.22)', borderRadius: 15, padding: 16, color: panelMuted, fontWeight: 850 }}>Loading calendar sessions...</div>
	                              ) : wgCalendarError ? (
	                                <div style={{ border: '1px solid rgba(248,113,113,0.35)', background: 'rgba(248,113,113,0.12)', borderRadius: 15, padding: 16, color: '#FCA5A5', fontWeight: 850 }}>{wgCalendarError}</div>
	                              ) : groupedWGCalendarSessions.length === 0 ? (
	                                <div style={{ border: '1px dashed rgba(255,255,255,0.22)', borderRadius: 15, padding: 16, color: panelMuted, fontWeight: 850, lineHeight: 1.45 }}>
	                                  {wgCalendarFilter === 'upcoming'
	                                    ? 'No upcoming sessions in this view.'
	                                    : wgCalendarFilter === 'past'
	                                      ? 'No past sessions yet.'
	                                      : 'No cancelled sessions.'}
	                                </div>
	                              ) : groupedWGCalendarSessions.map(group => (
	                                <div key={group.key}>
	                                  <div style={{ color: 'rgba(255,255,255,0.66)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 7px 2px' }}>{group.title}</div>
	                                  <div style={{ display: 'grid', gap: 8 }}>
	                                    {group.sessions.map(session => {
	                                      const summary = getWorkoutGroupCalendarAttendanceSummary(session);
	                                      const selected = selectedWGCalendarSession?.id === session.id;
	                                      const sessionTime = formatWorkoutGroupCalendarParts(session.starts_at, session.ends_at);
	                                      const ownInvite = session.athletes?.find(athlete => athlete.athlete_id === profileData?.id);
	                                      const statusMeta = session.status === 'cancelled'
	                                        ? WORKOUT_GROUP_CALENDAR_STATUS.cancelled
	                                        : ownInvite
	                                          ? WORKOUT_GROUP_CALENDAR_STATUS[ownInvite.status || 'pending']
	                                          : null;
	                                      return (
	                                        <button
	                                          key={session.id}
	                                          type="button"
	                                          onClick={() => setWgCalendarSelectedSessionId(selected ? null : session.id)}
	                                          style={{ width: '100%', textAlign: 'left', border: `1px solid ${selected ? '#22D3EE' : '#E0E7EF'}`, background: '#FFFFFF', borderRadius: 15, padding: 12, cursor: 'pointer', color: '#0A1D44', boxShadow: selected ? '0 0 0 2px rgba(34,211,238,0.16)' : '0 8px 20px rgba(15,23,42,0.16)' }}
	                                        >
	                                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
	                                            <div style={{ minWidth: 0 }}>
	                                              <div style={{ color: '#0A1D44', fontSize: 14, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.title || 'Workout group session'}</div>
	                                              <div style={{ color: '#64748B', fontSize: 11, fontWeight: 850, marginTop: 3 }}>{activeWG?.name || 'Workout group'}</div>
	                                            </div>
	                                            {statusMeta && <span style={{ border: `1px solid ${statusMeta.border}`, background: statusMeta.bg, color: statusMeta.text, borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 950, flexShrink: 0 }}>{statusMeta.label}</span>}
	                                          </div>
	                                          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center', marginTop: 12 }}>
	                                            <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
	                                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #E0E7EF', background: '#F8FAFC', color: '#0A1D44', borderRadius: 10, padding: '7px 9px', fontSize: 11, fontWeight: 950 }}>
	                                                <FontAwesomeIcon icon={faCalendar} style={{ color: '#0C69C8' }} />
	                                                {sessionTime.date}
	                                              </span>
	                                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #E0E7EF', background: '#F8FAFC', color: '#0A1D44', borderRadius: 10, padding: '7px 9px', fontSize: 11, fontWeight: 950 }}>
	                                                <FontAwesomeIcon icon={faClock} style={{ color: '#0C69C8' }} />
	                                                {sessionTime.time}
	                                              </span>
		                                              {session.location && <span style={{ color: '#64748B', fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 5, overflowWrap: 'anywhere' }}><FontAwesomeIcon icon={faLocationDot} style={{ fontSize: 10 }} />{session.location}</span>}
	                                            </div>
	                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(58px, 1fr))', gap: 6, minWidth: 210 }}>
	                                              {[
	                                                ['Available', summary.accepted, '#166534', '#ECFDF3', '#BBF7D0'],
	                                                ['Pending', summary.pending, '#92400E', '#FFFBEB', '#FDE68A'],
	                                                ['Unavailable', summary.declined, '#991B1B', '#FEF2F2', '#FECACA'],
	                                              ].map(([label, value, color, bg, border]) => (
	                                                <span key={label} style={{ border: `1px solid ${border}`, background: bg, color, borderRadius: 10, padding: '6px 7px', textAlign: 'center', lineHeight: 1.05 }}>
	                                                  <span style={{ display: 'block', fontSize: 15, fontWeight: 950 }}>{Number(value || 0).toLocaleString()}</span>
	                                                  <span style={{ display: 'block', fontSize: 9, fontWeight: 950, textTransform: 'uppercase', marginTop: 3 }}>{label}</span>
	                                                </span>
	                                              ))}
	                                            </div>
	                                          </div>
	                                        </button>
	                                      );
	                                    })}
	                                  </div>
	                                </div>
	                              ))}
	                            </div>
	                          </section>

	                          <div style={{ display: 'grid', gap: 12 }}>
	                            {!selectedWGCalendarSession ? (
		                              <section style={{ ...wgCalendarInnerPanelStyle, minHeight: 240 }}>
	                                <div style={wgCalendarEyebrowStyle}>Session details</div>
	                                <div style={{ display: 'grid', placeItems: 'center', minHeight: 185, textAlign: 'center', color: panelMuted, fontWeight: 850, lineHeight: 1.45 }}>
	                                  Choose a session to view attendance, details, and coach actions.
	                                </div>
	                              </section>
	                            ) : (() => {
	                              const session = selectedWGCalendarSession;
	                              const isCancelled = session.status === 'cancelled';
	                              const summary = getWorkoutGroupCalendarAttendanceSummary(session);
	                              const sessionTime = formatWorkoutGroupCalendarParts(session.starts_at, session.ends_at);
	                              const canShowSessionActions = !isCancelled && (currentWGCalendarInvite || currentWGCalendarCoachAssignment || activeWGCanManage);
	                              return (
	                                <>
		                                  <section style={wgCalendarInnerPanelStyle}>
	                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
	                                      <div style={{ minWidth: 0 }}>
	                                        <div style={wgCalendarEyebrowStyle}>Session details</div>
	                                        <h4 style={{ margin: '4px 0 3px', color: '#fff', fontSize: 18, lineHeight: 1.15 }}>{session.title}</h4>
	                                        <div style={{ color: panelMuted, fontSize: 12, fontWeight: 850 }}>{activeWG?.name || 'Workout group'}</div>
	                                      </div>
	                                      <span style={{ border: `1px solid ${isCancelled ? WORKOUT_GROUP_CALENDAR_STATUS.cancelled.border : 'rgba(255,255,255,0.24)'}`, background: isCancelled ? WORKOUT_GROUP_CALENDAR_STATUS.cancelled.bg : 'rgba(255,255,255,0.12)', color: isCancelled ? WORKOUT_GROUP_CALENDAR_STATUS.cancelled.text : '#fff', borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 950, flexShrink: 0 }}>
	                                        {isCancelled ? 'Cancelled' : 'Scheduled'}
	                                      </span>
	                                    </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 8, marginTop: 12 }}>
                                        <div style={{ border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.08)', borderRadius: 13, padding: 10 }}>
                                          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Date</div>
                                          <div style={{ color: '#fff', fontSize: 16, fontWeight: 950, marginTop: 4 }}>{sessionTime.date}</div>
                                        </div>
                                        <div style={{ border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.08)', borderRadius: 13, padding: 10 }}>
                                          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Time</div>
                                          <div style={{ color: '#fff', fontSize: 16, fontWeight: 950, marginTop: 4 }}>{sessionTime.time}</div>
                                        </div>
                                        {session.location && (
                                          <div style={{ border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.08)', borderRadius: 13, padding: 10 }}>
                                            <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Location</div>
                                            <div style={{ color: '#fff', fontSize: 14, fontWeight: 900, marginTop: 4, overflowWrap: 'anywhere' }}>{session.location}</div>
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: 8, marginTop: 10 }}>
                                        {[
                                          ['Invited', (session.athletes || []).length, '#475569', '#F1F5F9', '#CBD5E1'],
                                          ['Available', summary.accepted, '#166534', '#ECFDF3', '#BBF7D0'],
                                          ['Pending', summary.pending, '#92400E', '#FFFBEB', '#FDE68A'],
                                          ['Unavailable', summary.declined, '#991B1B', '#FEF2F2', '#FECACA'],
                                        ].map(([label, value, color, bg, border]) => (
                                          <div key={label} style={{ border: `1px solid ${border}`, background: bg, color, borderRadius: 13, padding: 10 }}>
                                            <div style={{ fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
                                            <div style={{ fontSize: 22, fontWeight: 950, marginTop: 3 }}>{Number(value || 0).toLocaleString()}</div>
                                          </div>
                                        ))}
                                      </div>
	                                    {session.notes && <div style={{ marginTop: 10, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: 10, color: panelMuted, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>{session.notes}</div>}
	                                    <button type="button" onClick={() => setWgCalendarSelectedSessionId(null)} style={{ marginTop: 10, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 10, padding: '8px 10px', fontSize: 11, fontWeight: 950, cursor: 'pointer' }}>
	                                      Close details
	                                    </button>
	                                  </section>

		                                  <section style={wgCalendarInnerPanelStyle}>
	                                    <div style={wgCalendarEyebrowStyle}>Assigned coaches</div>
	                                    {(session.coaches || []).length === 0 ? <div style={{ color: panelMuted, fontSize: 12, fontWeight: 800, marginTop: 8 }}>No coaches assigned.</div> : (
	                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
	                                        {session.coaches.map(coach => (
	                                          <span key={coach.id || coach.coach_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', borderRadius: 999, padding: '5px 10px 5px 5px', color: '#fff', fontSize: 12, fontWeight: 900 }}>
	                                            <img src={coach.profile?.profile_img || noImage} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: 'cover', flexShrink: 0 }} loading="lazy" />
	                                            {coach.coach_id === profileData?.id ? 'You' : getWorkoutGroupCalendarProfileName(coach.profile)}
	                                          </span>
	                                        ))}
	                                      </div>
	                                    )}
	                                  </section>

	                                  {canShowSessionActions && (
		                                    <section style={wgCalendarInnerPanelStyle}>
	                                      <div style={wgCalendarEyebrowStyle}>Session actions</div>
	                                      {!isCancelled && currentWGCalendarInvite && (
	                                        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
	                                          <div style={{ color: '#fff', fontSize: 12, fontWeight: 950 }}>Your response</div>
	                                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
	                                            <button type="button" disabled={!!wgCalendarResponseSaving} onClick={() => respondWGCalendarAttendance(currentWGCalendarInvite, 'accepted')} style={{ border: '1px solid #BBF7D0', background: currentWGCalendarInvite.status === 'accepted' ? '#ECFDF3' : 'rgba(22,163,74,0.16)', color: currentWGCalendarInvite.status === 'accepted' ? '#166534' : '#BBF7D0', borderRadius: 12, padding: '10px 12px', fontWeight: 950, cursor: wgCalendarResponseSaving ? 'wait' : 'pointer' }}>Available</button>
	                                            <button type="button" disabled={!!wgCalendarResponseSaving} onClick={() => respondWGCalendarAttendance(currentWGCalendarInvite, 'declined')} style={{ border: '1px solid #FECACA', background: currentWGCalendarInvite.status === 'declined' ? '#FEF2F2' : 'rgba(239,68,68,0.15)', color: currentWGCalendarInvite.status === 'declined' ? '#991B1B' : '#FECACA', borderRadius: 12, padding: '10px 12px', fontWeight: 950, cursor: wgCalendarResponseSaving ? 'wait' : 'pointer' }}>Unavailable</button>
	                                          </div>
	                                        </div>
	                                      )}

	                                      {!isCancelled && currentWGCalendarCoachAssignment && (
	                                        <button type="button" disabled={!!wgCalendarResponseSaving} onClick={removeWGCalendarCoachAssignment} style={{ marginTop: 10, width: '100%', border: '1px solid #FECACA', background: 'rgba(239,68,68,0.15)', color: '#FECACA', borderRadius: 12, padding: '10px 12px', fontWeight: 950, cursor: wgCalendarResponseSaving ? 'wait' : 'pointer' }}>
	                                          Mark coach unavailable
	                                        </button>
	                                      )}

	                                      {activeWGCanManage && (
	                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
	                                          {!isCancelled && <button type="button" onClick={() => openWGCalendarEdit(session)} style={{ border: '1px solid rgba(255,255,255,0.24)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 10, padding: '8px 10px', fontSize: 11, fontWeight: 950, cursor: 'pointer' }}>Edit</button>}
	                                          <button type="button" onClick={() => setWGCalendarSessionStatus(session, isCancelled ? 'scheduled' : 'cancelled')} style={{ border: `1px solid ${isCancelled ? '#BBF7D0' : '#FECACA'}`, background: isCancelled ? 'rgba(22,163,74,0.16)' : 'rgba(239,68,68,0.15)', color: isCancelled ? '#BBF7D0' : '#FECACA', borderRadius: 10, padding: '8px 10px', fontSize: 11, fontWeight: 950, cursor: 'pointer' }}>{isCancelled ? 'Make Live' : 'Cancel'}</button>
	                                          {!isCancelled && (
	                                            <>
	                                              <button type="button" disabled={wgCalendarNotifySaving} onClick={() => notifyWGCalendarAudience(session, 'athletes')} style={{ border: '1px solid rgba(255,255,255,0.24)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 10, padding: '8px 10px', fontSize: 11, fontWeight: 950, cursor: wgCalendarNotifySaving ? 'wait' : 'pointer' }}>Notify Athletes</button>
	                                              <button type="button" disabled={wgCalendarNotifySaving} onClick={() => notifyWGCalendarAudience(session, 'coaches')} style={{ border: '1px solid rgba(255,255,255,0.24)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 10, padding: '8px 10px', fontSize: 11, fontWeight: 950, cursor: wgCalendarNotifySaving ? 'wait' : 'pointer' }}>Notify Coaches</button>
	                                              <button type="button" disabled={wgCalendarNotifySaving} onClick={() => notifyWGCalendarAudience(session, 'all')} style={{ border: '1px solid rgba(255,255,255,0.24)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 10, padding: '8px 10px', fontSize: 11, fontWeight: 950, cursor: wgCalendarNotifySaving ? 'wait' : 'pointer' }}>Notify Everyone</button>
	                                            </>
	                                          )}
	                                        </div>
	                                      )}
	                                    </section>
	                                  )}

		                                  <section style={wgCalendarInnerPanelStyle}>
	                                    <div style={wgCalendarEyebrowStyle}>Attendance</div>
	                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7, marginTop: 10 }}>
	                                      {[
	                                        ['Available', summary.accepted, WORKOUT_GROUP_CALENDAR_STATUS.accepted],
	                                        ['Pending', summary.pending, WORKOUT_GROUP_CALENDAR_STATUS.pending],
	                                        ['Unavailable', summary.declined, WORKOUT_GROUP_CALENDAR_STATUS.declined],
	                                      ].map(([label, value, meta]) => (
	                                        <div key={label} style={{ border: `1px solid ${meta.border}`, background: meta.bg, color: meta.text, borderRadius: 12, padding: 9, textAlign: 'center' }}>
	                                          <div style={{ fontSize: 18, fontWeight: 950, lineHeight: 1 }}>{value}</div>
	                                          <div style={{ fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 3 }}>{label}</div>
	                                        </div>
	                                      ))}
	                                    </div>

	                                    <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
	                                      {['accepted', 'pending', 'declined'].map(status => {
	                                        const rows = (session.athletes || []).filter(athlete => (athlete.status || 'pending') === status);
	                                        const meta = WORKOUT_GROUP_CALENDAR_STATUS[status];
	                                        return (
	                                          <div key={status}>
	                                            <div style={{ color: meta.text === '#92400E' ? '#FDE68A' : meta.border, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{meta.label}</div>
	                                            {rows.length === 0 ? (
	                                              <div style={{ color: panelMuted, fontSize: 12, fontWeight: 800 }}>No athletes.</div>
	                                            ) : (
	                                              <div style={{ display: 'grid', gap: 6 }}>
	                                                {rows.map(row => (
	                                                  <div key={row.id || row.athlete_id} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: 8 }}>
	                                                    <img src={row.profile?.profile_img || noImage} alt="" style={{ width: 42, height: 42, borderRadius: 13, objectFit: 'cover', flexShrink: 0 }} loading="lazy" />
	                                                    <div style={{ minWidth: 0, flex: 1 }}>
	                                                      <div style={{ color: '#fff', fontSize: 12, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getWorkoutGroupCalendarProfileName(row.profile)}</div>
	                                                      {row.responded_at && <div style={{ color: panelMuted, fontSize: 10, fontWeight: 800, marginTop: 1 }}>{new Date(row.responded_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>}
	                                                    </div>
	                                                  </div>
	                                                ))}
	                                              </div>
	                                            )}
	                                          </div>
	                                        );
	                                      })}
	                                    </div>
	                                  </section>

	                                </>
	                              );
	                            })()}
	                          </div>
	                        </div>
		                      </section>
	                    </>
                    );
                  })()}
                </>
              )}
          </div>
        )}

        {/* ══ TAB: ACHIEVEMENTS ══ */}
        {activeTab === 'achievements' && (
          <div className="fade-in">
            <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>Progress</div>
                <h2 className="heading" style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.text }}>Achievements</h2>
              </div>
              <div style={card(C, { padding: '10px 16px', display: 'flex', gap: 14, borderRadius: 12 })}>
                <div style={{ textAlign: 'center' }}>
                  <div className="heading" style={{ fontSize: 18, fontWeight: 800, color: C.gold || '#D4A017' }}>{userAchievements.length}</div>
                  <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Earned</div>
                </div>
                <div style={{ width: 1, background: C.border }} />
                <div style={{ textAlign: 'center' }}>
                  <div className="heading" style={{ fontSize: 18, fontWeight: 800, color: C.textMuted }}>{allAchievements.length - userAchievements.length}</div>
                  <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Locked</div>
                </div>
              </div>
            </div>

            <div style={card(C, { padding: '14px 16px', marginBottom: 16, borderRadius: 14 })}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary }}>Overall Progress</span>
                <span className="heading" style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>{allAchievements.length > 0 ? Math.round((userAchievements.length / allAchievements.length) * 100) : 0}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 99, background: C.bgAlt, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${allAchievements.length > 0 ? (userAchievements.length / allAchievements.length) * 100 : 0}%`, background: 'linear-gradient(90deg,#D4A017,#f0c040)', borderRadius: 99, transition: 'width 0.8s ease' }} />
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 5 }}>{userAchievements.length} of {allAchievements.length} achievements unlocked</div>
            </div>

            {/* Filters — scrollable on mobile */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 3, background: C.bgAlt, border: `1px solid ${C.border}`, padding: 3, borderRadius: 99, alignSelf: 'flex-start' }}>
                {[{ v: 'all', l: 'All' }, { v: 'earned', l: '✨ Earned' }, { v: 'locked', l: '🔒 Locked' }].map(opt => (
                  <button key={opt.v} onClick={() => setAchivFilter(opt.v)} style={{ border: 'none', background: achivFilter === opt.v ? C.accentGrad : 'transparent', color: achivFilter === opt.v ? '#fff' : C.textSecondary, fontWeight: 700, fontSize: 12, padding: '7px 12px', borderRadius: 99, cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap' }}>{opt.l}</button>
                ))}
              </div>
              <div className="filter-row" style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
                {achievementSports.map(s => (
                  <button key={s} onClick={() => setAchivSport(s)} style={{ ...tagSt(C, achivSport === s), cursor: 'pointer', padding: '6px 12px', fontSize: 12, flexShrink: 0, gap: 6 }}>
                    {s === 'all' ? 'All' : <><SportIcon C={C} iconUrl={profileData.sportIconMap?.[s]?.icon_url} sportId={s} size={18} style={{ background: C.bgCard }} /> {s}</>}
                  </button>
                ))}
              </div>
            </div>

            {filteredAchievements.length === 0 ? (
              <div style={card(C, { padding: '48px 24px', textAlign: 'center' })}><div style={{ fontSize: 40, marginBottom: 12 }}>🏆</div><p style={{ color: C.textMuted }}>No achievements match your filter.</p></div>
            ) : (
              <div className="achiev-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(160px,1fr))', gap: 12 }}>
                {filteredAchievements.map(achievement => (
                  <AchievementCard key={achievement.id} C={C} achievement={achievement} earned={earnedMap.has(achievement.id)} completedAt={earnedMap.get(achievement.id)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ TAB: MATCHES ══ */}
        {activeTab === 'matches' && (
          <div className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>History</div>
                <h2 className="heading" style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.text }}>My Matches</h2>
              </div>
              <div style={{ display: 'flex', gap: 3, background: C.bgAlt, border: `1px solid ${C.border}`, padding: 3, borderRadius: 99 }}>
                {[{ v: 'solo', l: 'Solo', count: matches.length }, { v: 'team', l: 'Team', count: teamMatches.length }].map(opt => (
                  <button key={opt.v} onClick={() => { setMatchTab(opt.v); setMatchFilter('all'); setMatchSport('all'); setMatchMode('all'); }}
                    style={{ border: 'none', background: matchTab === opt.v ? C.accentGrad : 'transparent', color: matchTab === opt.v ? '#fff' : C.textSecondary, fontWeight: 700, fontSize: 13, padding: '7px 14px', borderRadius: 99, cursor: 'pointer', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {opt.l}
                    <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, background: matchTab === opt.v ? 'rgba(255,255,255,0.2)' : C.bgCard, color: matchTab === opt.v ? '#fff' : C.textMuted, fontWeight: 700 }}>{opt.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Stats row */}
            {(() => {
              const stats = matchTab === 'solo' ? matchStats : teamMatchStats;
              return (
                <>
                  <div className="stats-4" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'Played', val: stats.total,  icon: faGamepad,     color: C.text,    iconColor: C.accent, bg: C.bgCard, iconBg: rgba(C.accent, 0.1) },
                      { label: 'Wins',   val: stats.wins,   icon: faCircleCheck, color: C.success, iconColor: C.success, bg: rgba(C.success, 0.06), iconBg: rgba(C.success, 0.1) },
                      { label: 'Losses', val: stats.losses, icon: faCircleXmark, color: C.danger,  iconColor: C.danger,  bg: rgba(C.danger, 0.06),  iconBg: rgba(C.danger, 0.1) },
                      { label: 'Draws',  val: stats.draws,  icon: faHandshake,   color: C.warn,    iconColor: C.warn,    bg: rgba(C.warn, 0.06),    iconBg: rgba(C.warn, 0.1) },
                    ].map(s => (
                      <div key={s.label} style={{ background: s.bg, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px', textAlign: 'center' }}>
                        <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 10, margin: '0 auto 6px', display: 'grid', placeItems: 'center', color: s.iconColor, background: s.iconBg }}>
                          <FontAwesomeIcon icon={s.icon} style={{ fontSize: 15 }} />
                        </span>
                        <div className="heading" style={{ fontSize: isMobile ? 22 : 26, fontWeight: 900, color: s.color, letterSpacing: -1 }}>{s.val}</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 3 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {stats.total > 0 && (
                    <div style={card(C, { padding: '14px 16px', marginBottom: 16, borderRadius: 14 })}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary }}>Win Rate</span>
                        <span className="heading" style={{ fontSize: 13, fontWeight: 700, color: '#34D399' }}>{Math.round((stats.wins / stats.total) * 100)}%</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 99, background: C.bgAlt, border: `1px solid ${C.border}`, overflow: 'hidden', display: 'flex' }}>
                        <div style={{ height: '100%', width: `${(stats.wins / stats.total) * 100}%`, background: '#34D399', transition: 'width 0.8s ease' }} />
                        <div style={{ height: '100%', width: `${(stats.draws / stats.total) * 100}%`, background: '#F59E0B' }} />
                        <div style={{ height: '100%', width: `${(stats.losses / stats.total) * 100}%`, background: '#EF4444' }} />
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 7 }}>
                        {[['#34D399', 'Wins'], ['#F59E0B', 'Draws'], ['#EF4444', 'Losses']].map(([color, label]) => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 99, background: color }} />
                            <span style={{ fontSize: 11, color: C.textMuted }}>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              <div className="filter-row" style={{ display: 'flex', gap: 4, overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ display: 'flex', gap: 3, background: C.bgAlt, border: `1px solid ${C.border}`, padding: 3, borderRadius: 99, flexShrink: 0 }}>
                  {[{ v: 'all', l: 'All' }, { v: 'win', l: 'Win', icon: faCircleCheck }, { v: 'loss', l: 'Loss', icon: faCircleXmark }, { v: 'draw', l: 'Draw', icon: faHandshake }].map(opt => (
                    <button key={opt.v} aria-pressed={matchFilter === opt.v} onClick={() => setMatchFilter(opt.v)} style={{ border: 'none', background: matchFilter === opt.v ? C.accentGrad : 'transparent', color: matchFilter === opt.v ? '#fff' : C.textSecondary, fontWeight: 700, fontSize: 12, padding: '6px 10px', borderRadius: 99, cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {opt.icon && <FontAwesomeIcon icon={opt.icon} aria-hidden="true" style={{ fontSize: 11 }} />}
                      {opt.l}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 3, background: C.bgAlt, border: `1px solid ${C.border}`, padding: 3, borderRadius: 99, flexShrink: 0 }}>
                  {[{ v: 'all', l: 'All Modes' }, { v: 'competitive', l: 'Comp', icon: faTrophy }, { v: 'casual', l: 'Casual' }].map(opt => (
                    <button key={opt.v} aria-pressed={matchMode === opt.v} onClick={() => setMatchMode(opt.v)} style={{ border: 'none', background: matchMode === opt.v ? C.accentGrad : 'transparent', color: matchMode === opt.v ? '#fff' : C.textSecondary, fontWeight: 700, fontSize: 12, padding: '6px 10px', borderRadius: 99, cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {opt.icon && <FontAwesomeIcon icon={opt.icon} aria-hidden="true" style={{ fontSize: 11 }} />}
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-row" style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
                {(matchTab === 'solo' ? matchSports : teamMatchSports).map(s => (
                  <button key={s} onClick={() => setMatchSport(s)} style={{ ...tagSt(C, matchSport === s), cursor: 'pointer', padding: '6px 12px', fontSize: 12, flexShrink: 0, gap: 6 }}>
                    {s === 'all' ? 'All' : <><SportIcon C={C} iconUrl={profileData.sportIconMap?.[s]?.icon_url} sportId={s} size={18} style={{ background: C.bgCard }} /> {s}</>}
                  </button>
                ))}
              </div>
            </div>

            {/* Match grid */}
            {matchTab === 'solo' ? (
              filteredMatches.length === 0 ? (
                <div style={card(C, { padding: '48px 24px', textAlign: 'center' })}><FontAwesomeIcon icon={faGamepad} aria-hidden="true" style={{ display: 'block', fontSize: 36, color: C.textMuted, margin: '0 auto 14px' }} /><p style={{ color: C.textMuted }}>No solo matches found.</p></div>
              ) : (
                <div className="match-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(320px,1fr))', gap: 14 }}>
                  {filteredMatches.map(match => <MatchCard key={match.id} C={C} match={match} myId={profileData.id} location={matchLocations[match.location_id]} sportIconMap={profileData.sportIconMap} />)}
                </div>
              )
            ) : (
              filteredTeamMatches.length === 0 ? (
                <div style={card(C, { padding: '48px 24px', textAlign: 'center' })}><FontAwesomeIcon icon={faUsers} aria-hidden="true" style={{ display: 'block', fontSize: 36, color: C.textMuted, margin: '0 auto 14px' }} /><p style={{ color: C.textMuted }}>No team matches found.</p></div>
              ) : (
                <div className="match-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(320px,1fr))', gap: 14 }}>
                  {filteredTeamMatches.map(match => <TeamMatchCard key={match.id} C={C} match={match} myTeamIds={myTeamIds} location={matchLocations[match.location_id]} sportIconMap={profileData.sportIconMap} />)}
                </div>
              )
            )}
          </div>
        )}

      </div>{/* end main content */}

      {/* ══════════════ MODALS ══════════════ */}

      {showWorkspaceRename && (
        <Modal C={C} onClose={closeWorkspaceSettings}>
          <MHead C={C} title="Workspace Settings" onClose={closeWorkspaceSettings} />
          <MBody>
            <p style={{ margin: '0 0 14px', color: C.textSecondary, fontSize: 13, lineHeight: 1.55 }}>
              The workspace name and logo appear when switching dashboards. They can be different from individual institute branding.
            </p>
            <FormRow C={C} label="Workspace logo">
              <div style={{ display: 'grid', gap: 9, justifyItems: 'start' }}>
                <label style={{ position: 'relative', width: 132, height: 132, borderRadius: 14, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bgAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: savingWorkspaceRename ? 'wait' : 'pointer' }}>
                  {workspaceLogoPreview ? (
                    <img src={workspaceLogoPreview} alt="Workspace logo preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, textAlign: 'center', padding: 12 }}>Choose square logo</span>
                  )}
                  <span aria-hidden="true" style={{ position: 'absolute', right: 7, bottom: 7, width: 28, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: C.accent, border: '2px solid #fff', boxShadow: '0 2px 7px rgba(15,23,42,0.24)' }}>
                    <FontAwesomeIcon icon={faCamera} style={{ fontSize: 12 }} />
                  </span>
                  <input type="file" accept="image/*" disabled={savingWorkspaceRename} style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }} onChange={e => { const file = e.target.files?.[0]; if (file) { setWorkspaceLogoFile(file); setWorkspaceLogoPreview(URL.createObjectURL(file)); } }} />
                </label>
                <span style={{ color: C.textMuted, fontSize: 11 }}>Use a square image for the clearest result.</span>
              </div>
            </FormRow>
            <FormRow C={C} label="Workspace name">
              <input
                autoFocus
                value={workspaceRenameValue}
                onChange={e => setWorkspaceRenameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveWorkspaceRename(); }}
                placeholder="e.g. Snows ProAm"
                autoComplete="organization"
                style={inputSt(C)}
              />
            </FormRow>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={closeWorkspaceSettings}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={saveWorkspaceRename} disabled={savingWorkspaceRename || !workspaceRenameValue.trim()}>
              {savingWorkspaceRename ? 'Saving...' : 'Save settings'}
            </BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {showAddMembers && activeInst && (!activeInst.teams || activeInst.type === 'Challenge') && (
        <Modal C={C} onClose={() => setShowAddMembers(false)}>
          <MHead C={C} title={activeInst.type === 'Challenge' ? 'Find and add members' : `Add ${memberLabel}s`} onClose={() => setShowAddMembers(false)} />
          <MBody>
            {activeInst.type === 'Challenge' ? (
              <ChallengeMemberPicker
                C={C}
                candidates={challengeMemberCandidates}
                currentMembers={activeInst.members}
                loading={loadingChallengeMemberCandidates}
                addingId={addingChallengeMemberId}
                search={challengeMemberSearch}
                setSearch={setChallengeMemberSearch}
                onAdd={addChallengeMember}
                parentName={getChallengeParentInstitute(activeInst)?.name}
              />
            ) : (
              <MemberBuilder C={C} rows={newMembers} setRows={setNewMembers} />
            )}
          </MBody>
          <MFoot C={C}>
            {activeInst.type === 'Challenge' ? (
              <>
                <BtnSec C={C} onClick={async () => {
                  try {
                    const count = await copyChallengeMemberDebugLogs();
                    notify(`${count} debug log${count === 1 ? '' : 's'} copied.`, 'success');
                  } catch (err) {
                    notify('Could not copy debug logs. Check browser clipboard permissions.', 'error');
                  }
                }}>Copy debug logs</BtnSec>
                <BtnPrimary C={C} onClick={() => setShowAddMembers(false)}>Done</BtnPrimary>
              </>
            ) : (
              <>
                <BtnSec C={C} onClick={() => { setShowAddMembers(false); setNewMembers([{ id: uid('m'), username: '' }]); }}>Cancel</BtnSec>
                <BtnPrimary C={C} onClick={addMembersToInstitute}>Add {memberLabel}s</BtnPrimary>
              </>
            )}
          </MFoot>
        </Modal>
      )}

      {showAddDeptMembers.open && showAddDeptMembers.dept && (
        <Modal C={C} onClose={() => setShowAddDeptMembers({ open: false, dept: null })}>
          <MHead C={C} title={`Add members to ${showAddDeptMembers.dept.name}`} onClose={() => setShowAddDeptMembers({ open: false, dept: null })} />
          <MBody>
            <ExistingMemberPicker
              C={C}
              candidates={departmentMemberCandidates}
              currentMembers={showAddDeptMembers.dept.members}
              loading={loadingDepartmentMemberCandidates}
              addingId={addingDepartmentMemberId}
              search={departmentMemberSearch}
              setSearch={setDepartmentMemberSearch}
              onAdd={addDepartmentMemberFromList}
              sourceName={getMemberSourceInstitute(activeInst)?.name}
            />
          </MBody>
          <MFoot C={C}>
            <BtnPrimary C={C} onClick={() => setShowAddDeptMembers({ open: false, dept: null })}>Done</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {moveDeptMember.open && moveDeptMember.member && (
        <Modal C={C} onClose={() => setMoveDeptMember({ open: false, member: null, fromDeptId: null, targetDeptId: '' })}>
          <MHead C={C} title={`Move ${moveDeptMember.member.name}`} onClose={() => setMoveDeptMember({ open: false, member: null, fromDeptId: null, targetDeptId: '' })} />
          <MBody>
            <FormRow C={C} label={`Move to ${deptLabel.toLowerCase()}`}>
              <select value={moveDeptMember.targetDeptId} onChange={e => setMoveDeptMember(v => ({ ...v, targetDeptId: e.target.value }))} style={selectSt(C)}>
                <option value="">Select {deptLabel.toLowerCase()}</option>
                {(activeInst?.departments || []).filter(dept => dept.id !== moveDeptMember.fromDeptId).map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
              </select>
            </FormRow>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setMoveDeptMember({ open: false, member: null, fromDeptId: null, targetDeptId: '' })}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={moveMemberToDept} disabled={!moveDeptMember.targetDeptId}>Move member</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {editingInstitutionMemberRole && (
        <Modal C={C} onClose={closeInstitutionMemberRole}>
          <MHead C={C} title={`Change member type - ${editingInstitutionMemberRole.name || 'Member'}`} onClose={closeInstitutionMemberRole} />
          <MBody>
            <p style={{ margin: '0 0 12px', color: C.textMuted, fontSize: 13, lineHeight: 1.45 }}>
              Choose a preset type or write a custom label for this member.
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {roleOptionsForMember(activeInst?.type, editingInstitutionMemberRole.association_type).map(role => {
                const selected = role === (editingInstitutionMemberRole.association_type || 'Member');
                const saving = updatingInstitutionMemberRoleId === (editingInstitutionMemberRole.id || editingInstitutionMemberRole.profile_id);
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => updateInstitutionMemberRole(editingInstitutionMemberRole, role)}
                    disabled={saving || selected}
                    aria-pressed={selected}
                    style={{ minHeight: 44, borderRadius: 10, border: `1px solid ${selected ? C.accentBorder : C.border}`, background: selected ? C.accentBgSubtle : C.bgAlt, color: selected ? readableAccentText(C) : C.text, cursor: saving || selected ? 'default' : 'pointer', padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 850, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
                  >
                    <span>{role}</span>
                    {selected && <span style={{ color: C.accent, fontSize: 11, fontWeight: 900 }}>Current</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
              <label htmlFor="institution-custom-member-type" style={{ display: 'block', color: C.textMuted, fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Custom member type</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="institution-custom-member-type"
                  value={institutionCustomRole}
                  onChange={e => setInstitutionCustomRole(e.target.value)}
                  placeholder="e.g. Volunteer"
                  maxLength={40}
                  style={{ ...inputSt(C), flex: 1 }}
                />
                <BtnPrimary C={C} onClick={() => updateInstitutionMemberRole(editingInstitutionMemberRole, institutionCustomRole)} disabled={!institutionCustomRole.trim() || !!updatingInstitutionMemberRoleId}>Save</BtnPrimary>
              </div>
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={closeInstitutionMemberRole} disabled={!!updatingInstitutionMemberRoleId}>Cancel</BtnSec>
          </MFoot>
        </Modal>
      )}

      {showAddDept && activeInst?.teams && (
        <Modal C={C} onClose={() => setShowAddDept(false)}>
          <MHead C={C} title={`Add ${deptLabel}`} onClose={() => setShowAddDept(false)} />
          <MBody>
            <div style={{ display: 'grid', gap: 12 }}>
              <FormRow C={C} label={`${deptLabel} Name *`}><input value={newDeptName} onChange={e => setNewDeptName(e.target.value)} placeholder="e.g., Year 1 / Finance" style={inputSt(C)} /></FormRow>
              <FormRow C={C} label="Default member type"><input value={newDeptType} onChange={e => setNewDeptType(e.target.value)} placeholder={`Default: ${defaultAssociationTypeForInstitute(activeInst)}`} style={inputSt(C)} /></FormRow>
              <FormRow C={C} label="Image (optional)">
                <input type="file" accept="image/*" style={{ fontSize: 13, color: C.textSecondary }} onChange={e => { const f = e.target.files?.[0]; if (f) { setDeptImageFile(f); setDeptImagePreview(URL.createObjectURL(f)); } }} />
              </FormRow>
              {deptImagePreview && <img src={deptImagePreview} alt="Preview" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8 }} />}
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => { setShowAddDept(false); setDeptImageFile(null); setDeptImagePreview(null); }}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={addDepartment}>Create {deptLabel}</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {showAddInstitute && (
        <Modal C={C} onClose={() => setShowAddInstitute(false)} wide>
          <MHead C={C} title={instNewType === 'Challenge' ? 'Create Challenge' : 'Create Institute'} onClose={() => { setShowAddInstitute(false); setSelectedPlatformChallengeOffer(null); }} />
          <MBody>
            <div style={{ display: 'grid', gap: 14 }}>
              <FormRow C={C} label={instNewType === 'Challenge' ? 'Challenge Name *' : 'Institute Name *'}>
                {selectedPlatformChallengeOffer ? (
                  <div style={{ ...inputSt(C), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: C.bgAlt }}>
                    <span style={{ color: C.text, fontWeight: 900, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{instName || selectedPlatformChallengeOffer.name}</span>
                    <span style={{ color: C.textMuted, fontSize: 11, fontWeight: 900, flexShrink: 0 }}>Admin template</span>
                  </div>
                ) : (
                  <input value={instName} onChange={e => setInstName(e.target.value)} placeholder={instNewType === 'Challenge' ? 'e.g., June Steps Challenge' : "e.g., St. Patrick's College"} style={inputSt(C)} />
                )}
              </FormRow>
              {instNewType === 'Challenge' ? (
                <FormRow C={C} label="Institute Type">
                  <div
                    aria-label="Institute type: Challenge"
                    style={{ ...inputSt(C), display: 'flex', alignItems: 'center', gap: 8, background: C.bgAlt, color: C.textSecondary, fontWeight: 850 }}
                  >
                    <FontAwesomeIcon icon={faTrophy} style={{ color: C.accent, fontSize: 12 }} />
                    Challenge
                  </div>
                </FormRow>
              ) : (
                <FormRow C={C} label="Institute Type">
                  <select value={instNewType} onChange={e => setInstNewType(e.target.value)} style={selectSt(C)}>
                    {ALL_INSTITUTE_TYPES.filter(t => t.value !== 'Challenge').map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </FormRow>
              )}
              <FormRow C={C} label={instNewType === 'Challenge' ? 'Challenge Mode' : `Has ${instNewType === 'College' ? 'academic groups' : ['School', 'Company'].includes(instNewType) ? 'departments' : 'teams'}?`}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ v: false, l: instNewType === 'Challenge' ? 'Member challenge' : 'Members' }, { v: true, l: instNewType === 'Challenge' ? 'Team challenge' : `With ${instNewType === 'College' ? 'academic groups' : ['School', 'Company'].includes(instNewType) ? 'departments' : 'teams'}` }].map(opt => (
                    <button key={String(opt.v)} onClick={() => setInstTeams(opt.v)}
                      style={{ flex: 1, padding: '12px 10px', borderRadius: 10, border: `1px solid ${instTeams === opt.v ? C.accent : C.border}`, background: instTeams === opt.v ? C.accentBgLight : C.bgAlt, color: instTeams === opt.v ? C.accent : C.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer', minHeight: 44 }}>
                      {opt.l}
                    </button>
                  ))}
                </div>
              </FormRow>
              {instNewType === 'Challenge' && (
                <div style={{ display: 'grid', gap: 12, border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 12, padding: 12 }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 900 }}>Challenge rules</div>
                    <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>{selectedPlatformChallengeOffer ? 'Use the admin challenge details, then choose the audience, available dates, and participant cap.' : 'Choose the goal, time window, and maximum number of people who can join.'}</div>
                  </div>
                  <FormRow C={C} label="Description" hint={selectedPlatformChallengeOffer ? 'Locked by Snows ProAm admin for this template.' : undefined}>
                    <textarea
                      value={instChallengeDescription}
                      onChange={e => setInstChallengeDescription(e.target.value)}
                      placeholder="What should participants know about this challenge?"
                      disabled={!!selectedPlatformChallengeOffer}
                      style={{ ...inputSt(C), minHeight: 82, resize: 'vertical', lineHeight: 1.5, paddingTop: 10, background: selectedPlatformChallengeOffer ? C.bgCard : inputSt(C).background, color: selectedPlatformChallengeOffer ? C.textMuted : C.text }}
                    />
                  </FormRow>
                  <FormRow C={C} label="Goal steps *" hint={selectedPlatformChallengeOffer ? 'Locked by Snows ProAm admin for this template.' : 'The first three participants or teams to reach this goal are locked as winners.'}>
                    <input
                      type="number"
                      min="1"
                      inputMode="numeric"
                      value={selectedPlatformChallengeOffer?.goal_steps ? String(selectedPlatformChallengeOffer.goal_steps) : instChallengeGoalSteps}
                      onChange={e => setInstChallengeGoalSteps(e.target.value)}
                      disabled={!!selectedPlatformChallengeOffer}
                      style={{ ...inputSt(C), background: selectedPlatformChallengeOffer ? C.bgCard : inputSt(C).background, color: selectedPlatformChallengeOffer ? C.textMuted : C.text }}
                    />
                  </FormRow>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                    {(() => {
                      const templateStartsOn = selectedPlatformChallengeOffer ? platformChallengeOfferStartsOn(selectedPlatformChallengeOffer) : '';
                      const templateEndsOn = selectedPlatformChallengeOffer ? platformChallengeOfferEndsOn(selectedPlatformChallengeOffer) : '';
                      return <>
                        <FormRow C={C} label="Start Date *" hint={templateStartsOn ? 'Locked by Snows ProAm admin.' : undefined}>
                          <input type="date" min={dateInputValue(new Date())} value={templateStartsOn || instChallengeStartsOn} disabled={!!templateStartsOn} onChange={e => {
                            const startsOn = e.target.value;
                            const minEnd = dateInputValue(addDays(new Date(`${startsOn}T00:00:00`), 1));
                            setInstChallengeStartsOn(startsOn);
                            setInstChallengeEndsOn(current => !current || current < minEnd ? minEnd : current);
                          }} style={{ ...inputSt(C), background: templateStartsOn ? C.bgCard : inputSt(C).background, color: templateStartsOn ? C.textMuted : C.text }} />
                        </FormRow>
                        <FormRow C={C} label="Finish Date *" hint={templateEndsOn ? 'Locked by Snows ProAm admin.' : undefined}>
                          <input type="date" min={(templateStartsOn || instChallengeStartsOn) ? dateInputValue(addDays(new Date(`${templateStartsOn || instChallengeStartsOn}T00:00:00`), 1)) : dateInputValue(new Date())} value={templateEndsOn || instChallengeEndsOn} disabled={!!templateEndsOn} onChange={e => setInstChallengeEndsOn(e.target.value)} style={{ ...inputSt(C), background: templateEndsOn ? C.bgCard : inputSt(C).background, color: templateEndsOn ? C.textMuted : C.text }} />
                        </FormRow>
                        <div style={{ gridColumn: '1 / -1', color: C.textMuted, fontSize: 12, fontWeight: 850 }}>
                          {challengeDurationLabel(templateStartsOn || instChallengeStartsOn, templateEndsOn || instChallengeEndsOn)}
                        </div>
                      </>;
                    })()}
                  </div>
                  <FormRow C={C} label="Allowed Participants *">
                    <input type="number" min="1" max="32767" inputMode="numeric" value={instChallengeMemberLimit} onChange={e => setInstChallengeMemberLimit(e.target.value)} placeholder="e.g., 80" style={inputSt(C)} />
                  </FormRow>
                </div>
              )}
              {instNewType === 'Challenge' && instTeams ? (
                <FormRow C={C} label={`${deptLabel}s included *`} hint={challengeDepartmentSource ? `Using ${challengeDepartmentSource.name}.` : `Create a ${deptLabel.toLowerCase()} dashboard first, then return to select who can access this challenge.`}>
                  {availableChallengeDepartments.length === 0 ? (
                    <div role="status" style={{ padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, fontSize: 13, lineHeight: 1.45 }}>
                      No {deptLabel.toLowerCase()}s are available yet. Create {deptLabel.toLowerCase()}s in the linked dashboard before starting a team challenge.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>
                          {instChallengeDepartmentIds.length} of {availableChallengeDepartments.length} selected
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" onClick={selectAllInstituteChallengeDepartments} style={{ border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                            Select all
                          </button>
                          <button type="button" onClick={clearInstituteChallengeDepartments} style={{ border: `1px solid ${C.border}`, background: C.bgElevated, color: C.textSecondary, borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                            Clear
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, maxHeight: 240, overflowY: 'auto', paddingRight: 2 }}>
                        {availableChallengeDepartments.map(dept => {
                          const checked = instChallengeDepartmentIds.includes(dept.id);
                          return (
                            <label key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '9px 10px', borderRadius: 10, border: `1px solid ${checked ? C.accent : C.border}`, background: checked ? C.accentBgLight : C.bgAlt, cursor: 'pointer', color: C.text }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleInstituteChallengeDepartment(dept.id)}
                                style={{ width: 16, height: 16, accentColor: C.accent, flexShrink: 0 }}
                              />
                              {dept.image_url && <img src={dept.image_url} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', border: `1px solid ${C.border}`, flexShrink: 0 }} />}
                              <span style={{ minWidth: 0, fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dept.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </FormRow>
              ) : instTeams && (
                <FormRow C={C} label={`Starting ${instNewType === 'College' ? 'academic groups' : ['School', 'Company'].includes(instNewType) ? 'departments' : 'teams'} (optional)`}>
                  <textarea
                    value={instInitialDepartments}
                    onChange={e => setInstInitialDepartments(e.target.value)}
                    placeholder={instNewType === 'Company' ? 'Accounting, Finance, Operations' : 'First Team, Juniors, Coaches'}
                    style={{ ...inputSt(C), minHeight: 82, resize: 'vertical', lineHeight: 1.5, paddingTop: 10 }}
                  />
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>Separate names with commas or new lines. Each one gets an invite code automatically.</div>
                </FormRow>
              )}
              {!selectedPlatformChallengeOffer && <FormRow C={C} label={instNewType === 'Company' ? 'Company logo (optional)' : 'Image (optional)'}>
                <input type="file" accept="image/*" style={{ fontSize: 13, color: C.textSecondary }} onChange={e => { const f = e.target.files?.[0]; if (f) { setInstImageFile(f); setInstImagePreview(URL.createObjectURL(f)); } }} />
                <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 5, lineHeight: 1.45 }}>
                  {instNewType === 'Company'
                    ? 'Use a standalone square logo for company leaderboard cards. Avoid wide office photos or team photos.'
                    : 'Use a clear image that represents this institute.'}
                </div>
              </FormRow>}
              {instImagePreview && <img src={instImagePreview} alt="Preview" style={{ width: instNewType === 'Company' ? 150 : '100%', height: instNewType === 'Company' ? 150 : isMobile ? 240 : 320, maxHeight: instNewType === 'Company' ? 150 : '44vh', objectFit: instNewType === 'Company' ? 'contain' : 'cover', borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }} />}
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => { setShowAddInstitute(false); setSelectedPlatformChallengeOffer(null); setInstInitialDepartments(''); setInstChallengeDescription(''); setInstChallengeGoalSteps('50000'); setInstChallengeDepartmentIds([]); setInstImageFile(null); setInstImagePreview(null); }}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={() => addInstitute(instName, instTeams, instNewType)} disabled={!instName.trim() || !!optingIntoPlatformChallengeId}>{optingIntoPlatformChallengeId ? 'Creating...' : instNewType === 'Challenge' ? 'Create Challenge' : 'Create Institute'}</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {editingInstitute && editInstForm && (
        <Modal C={C} wide={editInstForm.type === 'Challenge'} onClose={() => { setEditingInstitute(null); setEditInstForm(null); setEditInstImageFile(null); setEditInstImagePreview(null); }}>
          <MHead C={C} title={editInstForm.type === 'Challenge' ? 'Edit Challenge' : 'Edit Institute'} onClose={() => { setEditingInstitute(null); setEditInstForm(null); setEditInstImageFile(null); setEditInstImagePreview(null); }} />
          <MBody>
            <div style={{ display: 'grid', gap: 12 }}>
              <FormRow C={C} label={editInstForm.type === 'Company' ? 'Company logo' : 'Institute image'}>
                <div style={{ display: 'grid', gap: 9 }}>
                  <div style={{ width: editInstForm.type === 'Company' ? 150 : '100%', height: editInstForm.type === 'Company' ? 150 : 180, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bgAlt }}>
                    {editInstImagePreview ? (
                      <img src={editInstImagePreview} alt={`${editInstForm.name || 'Institute'} preview`} style={{ width: '100%', height: '100%', objectFit: editInstForm.type === 'Company' ? 'contain' : 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: 13, fontWeight: 800 }}>No image selected</div>
                    )}
                  </div>
                  <input type="file" accept="image/*" style={{ fontSize: 13, color: C.textSecondary }} onChange={e => { const f = e.target.files?.[0]; if (f) { setEditInstImageFile(f); setEditInstImagePreview(URL.createObjectURL(f)); setEditInstForm(current => ({ ...current, remove_image: false })); } }} />
                  <div style={{ color: C.textMuted, fontSize: 11.5, lineHeight: 1.45 }}>
                    {editInstForm.type === 'Company'
                      ? 'Use a standalone square logo for company leaderboard cards. Avoid wide office photos or team photos.'
                      : 'Use a clear image that represents this institute.'}
                  </div>
                  {(editInstImagePreview || editInstForm.image_url) && (
                    <button type="button" onClick={() => { setEditInstImageFile(null); setEditInstImagePreview(null); setEditInstForm(current => ({ ...current, image_url: null, remove_image: true })); }} style={dangerActionButtonSt(C, { justifySelf: 'start', padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 })}>
                      Remove image
                    </button>
                  )}
                </div>
              </FormRow>
              <FormRow C={C} label={editInstForm.type === 'Challenge' ? 'Challenge name' : 'Name'}><input value={editInstForm.name} onChange={e => setEditInstForm(f => ({ ...f, name: e.target.value }))} style={inputSt(C)} /></FormRow>
              <FormRow C={C} label="Institute invite code">
                <button type="button" onClick={() => editInstForm.code && copyToClipboard(editInstForm.code, 'Institute invite code')} disabled={!editInstForm.code} aria-label={`Copy institute invite code ${editInstForm.code || ''}`} style={{ ...inputSt(C), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left', background: C.bgAlt, cursor: editInstForm.code ? 'pointer' : 'default' }}>
                  <span className="heading" style={{ color: C.accent, fontSize: 15, fontWeight: 900, letterSpacing: 0.9 }}>{editInstForm.code || 'Not generated'}</span>
                  <span style={{ color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 900 }}><FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} /> Copy</span>
                </button>
                <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 5, lineHeight: 1.45 }}>Generated on creation and kept fixed for existing members.</div>
              </FormRow>
              <FormRow C={C} label="Type">
                <div style={{ ...inputSt(C), display: 'flex', alignItems: 'center', background: C.bgAlt, color: C.textSecondary }}>
                  <TypeIcon type={editInstForm.type} /> {editInstForm.type || 'Institute'}
                </div>
              </FormRow>
              {editInstForm.type === 'Challenge' && (
                <div style={{ display: 'grid', gap: 12, border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 12, padding: 12 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 900 }}>Challenge rules</div>
                  <FormRow C={C} label="Challenge description">
                    <textarea
                      value={editInstForm.description || ''}
                      onChange={e => setEditInstForm(f => ({ ...f, description: e.target.value }))}
                      rows={3}
                      style={{ ...inputSt(C), minHeight: 88, resize: 'vertical', lineHeight: 1.45 }}
                    />
                  </FormRow>
                  <FormRow C={C} label="Audience">
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                        {[
                          { value: 'members', label: 'All members' },
                          { value: 'groups', label: `${deptLabel}s` },
                        ].map(option => {
                          const selected = editInstForm.audience_mode === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setEditInstForm(f => ({ ...f, audience_mode: option.value }))}
                              aria-pressed={selected}
                              style={{ minHeight: 42, borderRadius: 10, border: `1px solid ${selected ? C.accent : C.border}`, background: selected ? C.accentBgLight : C.bgCard, color: selected ? C.accent : C.textSecondary, padding: '9px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 900 }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      {editInstForm.audience_mode === 'groups' && (
                        <div style={{ display: 'grid', gap: 9 }}>
                          <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.45 }}>
                            {editInstForm.audience_source_name ? `Using ${editInstForm.audience_source_name}.` : `Choose which ${deptLabel.toLowerCase()}s can use this challenge.`}
                          </div>
                          {loadingEditChallengeAudience ? (
                            <div style={{ color: C.textMuted, fontSize: 12 }}>Loading {deptLabel.toLowerCase()}s...</div>
                          ) : editChallengeDepartments.length === 0 ? (
                            <div role="status" style={{ padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textSecondary, fontSize: 13, lineHeight: 1.45 }}>
                              No {deptLabel.toLowerCase()}s are available from the source institute.
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div style={{ color: C.textSecondary, fontSize: 12, fontWeight: 850 }}>
                                  {(editInstForm.audience_department_ids || []).length} of {editChallengeDepartments.length} selected
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button type="button" onClick={selectAllEditChallengeDepartments} style={{ border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Select all</button>
                                  <button type="button" onClick={clearEditChallengeDepartments} style={{ border: `1px solid ${C.border}`, background: C.bgElevated, color: C.textSecondary, borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Clear</button>
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, maxHeight: 220, overflowY: 'auto', paddingRight: 2 }}>
                                {editChallengeDepartments.map(dept => {
                                  const checked = (editInstForm.audience_department_ids || []).includes(dept.id);
                                  return (
                                    <label key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '8px 10px', borderRadius: 10, border: `1px solid ${checked ? C.accent : C.border}`, background: checked ? C.accentBgLight : C.bgCard, cursor: 'pointer', color: C.text }}>
                                      <input type="checkbox" checked={checked} onChange={() => toggleEditChallengeDepartment(dept.id)} style={{ width: 16, height: 16, accentColor: C.accent, flexShrink: 0 }} />
                                      {dept.image_url && <img src={dept.image_url} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', border: `1px solid ${C.border}`, flexShrink: 0 }} />}
                                      <span style={{ minWidth: 0, fontSize: 13, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dept.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </FormRow>
                  <FormRow C={C} label="Goal steps">
                    <input
                      type="number"
                      min="1"
                      inputMode="numeric"
                      value={editInstForm.goal_steps || ''}
                      disabled={!!(editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn)}
                      onChange={e => setEditInstForm(f => ({ ...f, goal_steps: e.target.value }))}
                      style={{ ...inputSt(C), background: (editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? C.bgCard : inputSt(C).background, color: (editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? C.textMuted : C.text }}
                    />
                    {(editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) && (
                      <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 5, lineHeight: 1.45 }}>Goal is locked because this challenge was created from a Snows ProAm template.</div>
                    )}
                  </FormRow>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                    <FormRow C={C} label="Start Date" hint={(editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? 'Locked by Snows ProAm admin.' : undefined}>
                      <input type="date" value={editInstForm.starts_on} disabled={!!(editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn)} onChange={e => setEditInstForm(f => ({ ...f, starts_on: e.target.value }))} style={{ ...inputSt(C), background: (editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? C.bgCard : inputSt(C).background, color: (editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? C.textMuted : C.text }} />
                    </FormRow>
                    <FormRow C={C} label="Finish Date" hint={(editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? 'Locked by Snows ProAm admin.' : undefined}>
                      <input type="date" min={editInstForm.starts_on || undefined} value={editInstForm.ends_on} disabled={!!(editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn)} onChange={e => setEditInstForm(f => ({ ...f, ends_on: e.target.value }))} style={{ ...inputSt(C), background: (editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? C.bgCard : inputSt(C).background, color: (editInstForm.brand_settings?.platformChallengeId || editInstForm.brand_settings?.platformChallengeOptIn) ? C.textMuted : C.text }} />
                    </FormRow>
                    <div style={{ gridColumn: '1 / -1', color: C.textMuted, fontSize: 12, fontWeight: 850 }}>
                      {challengeDurationLabel(editInstForm.starts_on, editInstForm.ends_on)}
                    </div>
                  </div>
                  <FormRow C={C} label="Allowed Participants">
                    <input type="number" min="1" inputMode="numeric" value={editInstForm.challenge_member_limit} onChange={e => setEditInstForm(f => ({ ...f, challenge_member_limit: e.target.value }))} style={inputSt(C)} />
                  </FormRow>
                </div>
              )}
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => { setEditingInstitute(null); setEditInstForm(null); setEditInstImageFile(null); setEditInstImagePreview(null); }}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={saveInstitute}>Save Changes</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {editingDepartment && editDeptForm && (
        <Modal C={C} onClose={() => { setEditingDepartment(null); setEditDeptForm(null); setEditDeptImageFile(null); setEditDeptImagePreview(null); }}>
          <MHead C={C} title={`Edit ${deptLabel}`} onClose={() => { setEditingDepartment(null); setEditDeptForm(null); setEditDeptImageFile(null); setEditDeptImagePreview(null); }} />
          <MBody>
            <div style={{ display: 'grid', gap: 12 }}>
              <FormRow C={C} label={`${deptLabel} image`}>
                <div style={{ display: 'grid', gap: 9 }}>
                  <div style={{ width: '100%', height: 160, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bgAlt }}>
                    {editDeptImagePreview ? (
                      <img src={editDeptImagePreview} alt={`${editDeptForm.name || deptLabel} preview`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: 13, fontWeight: 800 }}>No image selected</div>
                    )}
                  </div>
                  <input type="file" accept="image/*" style={{ fontSize: 13, color: C.textSecondary }} onChange={e => { const f = e.target.files?.[0]; if (f) { setEditDeptImageFile(f); setEditDeptImagePreview(URL.createObjectURL(f)); setEditDeptForm(current => ({ ...current, remove_image: false })); } }} />
                  {(editDeptImagePreview || editDeptForm.image_url) && (
                    <button type="button" onClick={() => { setEditDeptImageFile(null); setEditDeptImagePreview(null); setEditDeptForm(current => ({ ...current, image_url: null, remove_image: true })); }} style={dangerActionButtonSt(C, { justifySelf: 'start', padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 })}>
                      Remove image
                    </button>
                  )}
                </div>
              </FormRow>
              <FormRow C={C} label="Name"><input value={editDeptForm.name} onChange={e => setEditDeptForm(f => ({ ...f, name: e.target.value }))} style={inputSt(C)} /></FormRow>
              <FormRow C={C} label={`${deptLabel} invite code`}>
                <button type="button" onClick={() => editDeptForm.code && copyToClipboard(editDeptForm.code, `${deptLabel} invite code`)} disabled={!editDeptForm.code} aria-label={`Copy ${deptLabel.toLowerCase()} invite code ${editDeptForm.code || ''}`} style={{ ...inputSt(C), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left', background: C.bgAlt, cursor: editDeptForm.code ? 'pointer' : 'default' }}>
                  <span className="heading" style={{ color: C.accent, fontSize: 15, fontWeight: 900, letterSpacing: 0.9 }}>{editDeptForm.code || 'Not generated'}</span>
                  <span style={{ color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 900 }}><FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} /> Copy</span>
                </button>
                <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 5, lineHeight: 1.45 }}>Generated when this {deptLabel.toLowerCase()} is created and used by members joining it.</div>
              </FormRow>
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => { setEditingDepartment(null); setEditDeptForm(null); setEditDeptImageFile(null); setEditDeptImagePreview(null); }}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={saveDept}>Save Changes</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {showCreateWG && (
        <Modal C={C} onClose={() => setShowCreateWG(false)}>
          <MHead C={C} title="Create Workout Group" onClose={() => setShowCreateWG(false)} />
          <MBody>
            <div style={{ display: 'grid', gap: 12 }}>
              <FormRow C={C} label="Group Name *"><input autoFocus value={wgName} onChange={e => setWgName(e.target.value)} placeholder="e.g., U16 Strength Squad" onKeyDown={e => { if (e.key === 'Enter') createWG(); }} style={inputSt(C)} /></FormRow>
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setShowCreateWG(false)}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={createWG} disabled={creatingWG || !wgName.trim()}>{creatingWG ? 'Creating…' : 'Create Group'}</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {showJoinWG && (
        <Modal C={C} onClose={() => setShowJoinWG(false)}>
          <MHead C={C} title="Join Workout Group" onClose={() => setShowJoinWG(false)} />
          <MBody>
            <FormRow C={C} label="Join Code">
              <input autoFocus value={joinWGCode} onChange={e => setJoinWGCode(e.target.value.toUpperCase())} placeholder="Enter group code" onKeyDown={e => { if (e.key === 'Enter') joinWG(); }} style={{ ...inputSt(C), textTransform: 'uppercase', letterSpacing: 1.5 }} />
            </FormRow>
            <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, lineHeight: 1.5 }}>
              Ask your coach for the group code. Joined groups appear here and in the mobile app.
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setShowJoinWG(false)}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={joinWG} disabled={!joinWGCode.trim()}>Join Group</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {workoutGroupManageOpen && activeWG && (() => {
        const modalC = workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        return (
        <Modal C={modalC} wide onClose={() => setWorkoutGroupManageOpen(false)}>
          {(() => {
            const t = clubTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
            const visibility = getWorkoutGroupMemberAnalyticsVisibility(activeWG);
            const visibilityShared = visibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group;
            const manageCalendarLocked = activeWGCanManage && !activeWGHasCalendarAccess;
            const activeThemeClub = activeWG?.workout_clubs || null;
            const themePrefs = workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES;
            const runManageAction = (action) => {
              setWorkoutGroupManageOpen(false);
              window.requestAnimationFrame(() => action?.());
            };
            const scrollToSection = (ref) => {
              runManageAction(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
            };
            const sendTrainingReminder = () => {
              if (!activeWGCanUseTrainingReminders) {
                openWorkoutGroupUpgradePrompt('Training reminders are included with Coach and higher plans.');
                return;
              }
              confirmWGNotification({ message: 'Quick reminder to get your workout session done this week.', type: 'GROUP_REMINDER' });
            };
            const sendCalorieReminder = () => {
              if (!activeWGCanUseNutritionStats) {
                openWorkoutGroupUpgradePrompt('Nutrition targets, nutrition stats, and calorie reminders are included with Pro Coach and Club Coaching.');
                return;
              }
              confirmWGNotification({ message: `Reminder: ${activeWG?.name || 'your group'} has a ${Number(activeWG?.daily_calorie_target || 2500).toLocaleString()} kcal daily target. Log your meals in ProAm Nutrition.`, type: 'GROUP_CALORIE_REMINDER' });
            };
            return (
              <>
                <MHead C={modalC} title="Manage Group" onClose={() => setWorkoutGroupManageOpen(false)} />
                <MBody className="profile-workout-modal">
                  <div style={{ display: 'grid', gap: 0 }}>
                    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 24, padding: 18, color: t.onPrimary || '#fff', background: `linear-gradient(135deg, ${t.primary}, ${t.surfaceSecondary || t.secondary})` }}>
                      <div style={{ position: 'absolute', right: -18, top: 14, width: 148, height: 76, borderRadius: 32, background: t.subtleOnPrimary || 'rgba(255,255,255,0.12)', transform: 'rotate(-18deg)' }} />
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: t.mutedOnPrimary || 'rgba(255,255,255,0.76)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.8 }}>Coach Tools</div>
                          <h2 style={{ margin: '5px 0 4px', color: t.onPrimary || '#fff', fontSize: 26, lineHeight: 1.05 }}>Manage group</h2>
                          <div style={{ color: t.mutedOnPrimary || 'rgba(255,255,255,0.76)', fontSize: 13, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeWG.name || 'Workout group'}</div>
                        </div>
                        <div style={{ border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.13)', borderRadius: 18, padding: '8px 12px', minWidth: 94, textAlign: 'center' }}>
                          <div style={{ color: '#fff', fontSize: 20, fontWeight: 950, lineHeight: 1 }}>{getWorkoutGroupWeeklySessionTarget(activeWG)}</div>
                          <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 10, fontWeight: 950, marginTop: 2 }}>days/week</div>
                        </div>
                      </div>
                    </div>

                    <WGManageLabel C={modalC}>Next actions</WGManageLabel>
                    <div style={{ display: 'grid', gap: 10 }}>
                      <WGManageOption C={modalC} icon={activeWG?.join_code ? faCopy : faBolt} title="Invite code" subtitle="Copy or create the athlete join code" onClick={() => runManageAction(() => getJoinCode(activeWG))} />
                      <WGManageOption C={modalC} icon={manageCalendarLocked ? faLock : faCalendar} title="Calendar" subtitle={manageCalendarLocked ? 'Preview calendar and upgrade when creating' : 'Plan sessions and attendance'} onClick={() => scrollToSection(workoutGroupCalendarRef)} muted={manageCalendarLocked} />
                    </div>

                    <WGManageLabel C={modalC}>Reminders</WGManageLabel>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                      <WGManageTileButton C={modalC} icon={activeWGCanUseTrainingReminders ? faEnvelope : faLock} title="Workout reminder" onClick={() => runManageAction(sendTrainingReminder)} muted={!activeWGCanUseTrainingReminders} />
                      <WGManageTileButton C={modalC} icon={activeWGCanUseNutritionStats ? faBullseye : faLock} title="Calorie reminder" onClick={() => runManageAction(sendCalorieReminder)} muted={!activeWGCanUseNutritionStats} />
                    </div>

                    <WGManageLabel C={modalC}>Tools</WGManageLabel>
                    <div style={{ display: 'grid', gap: 10 }}>
                      <WGManageOption C={modalC} icon={faDumbbell} title={`Assigned workouts (${getAllowedWorkoutNamesList(activeWG).length})`} subtitle="Choose the plans that count here" onClick={() => runManageAction(updateWGAllowedWorkouts)} />
	                      <WGManageOption C={modalC} icon={faChartLine} title="Group analytics" subtitle="Open the deeper performance view" onClick={() => runManageAction(() => setWorkoutGroupAnalyticsOpen(true))} />
                    </div>

	                    <WGManageLabel C={modalC}>Club colours</WGManageLabel>
                      <div style={{ border: '1px solid #DCE6F3', background: '#FFFFFF', borderRadius: 18, padding: 14, marginBottom: 10 }}>
                        {activeThemeClub?.id ? (
                          <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                            <div style={{ width: 42, height: 42, borderRadius: 16, background: activeThemeClub.primary_color || '#0C69C8', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                              <FontAwesomeIcon icon={faGear} />
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ color: '#0D275B', fontSize: 15, fontWeight: 950 }}>Club colours</div>
                              <div style={{ color: '#64748B', fontSize: 12, fontWeight: 850, marginTop: 2 }}>Choose where {activeThemeClub.name || 'this club'} colours apply for you.</div>
                            </div>
                            {workoutGroupThemePrefsSaving && <span style={{ color: '#64748B', fontSize: 11, fontWeight: 900 }}>Saving...</span>}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 13 }}>
                            {USER_WORKOUT_GROUP_THEME_SURFACES.map(surface => {
                              const selected = themePrefs[surface.enabledKey] === true && String(themePrefs[surface.clubIdKey] || '') === String(activeThemeClub.id);
                              const adminBlocked = surface.key === 'workout_groups' && activeThemeClub?.brand_settings?.theme_workout_groups !== true;
                              const disabled = workoutGroupThemePrefsSaving || !workoutGroupThemePrefsLoaded || adminBlocked;
                              const nextEnabled = !selected;
                              return (
                                <button
                                  key={surface.key}
                                  type="button"
                                  disabled={disabled}
                                  aria-pressed={selected}
                                  title={adminBlocked ? 'The club admin has not enabled colours for the main Workout Groups screen.' : undefined}
                                  onClick={() => saveWorkoutGroupThemeSurface(surface.key, activeThemeClub, nextEnabled)}
                                  style={{ minHeight: 78, border: `1px solid ${selected ? activeThemeClub.accent_color || '#D5A419' : adminBlocked ? '#FDE68A' : '#E2E8F0'}`, background: selected ? activeThemeClub.primary_color || '#0C69C8' : adminBlocked ? '#FFFBEB' : '#F8FAFC', color: selected ? '#FFFFFF' : adminBlocked ? '#92400E' : '#475569', borderRadius: 16, padding: 10, cursor: disabled ? 'not-allowed' : 'pointer', display: 'grid', alignContent: 'center', gap: 5, textAlign: 'left', opacity: disabled && !adminBlocked ? 0.7 : 1 }}
                                >
                                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13, fontWeight: 950 }}>
                                    <span>{surface.label}</span>
                                    <FontAwesomeIcon icon={selected ? faCheck : adminBlocked ? faLock : faPlus} style={{ fontSize: 11 }} />
                                  </span>
                                  <span style={{ color: selected ? 'rgba(255,255,255,0.76)' : adminBlocked ? '#92400E' : '#64748B', fontSize: 11, fontWeight: 850 }}>
                                    {adminBlocked ? 'Admin controls this screen' : selected ? 'Using this club' : `Use ${activeThemeClub.name || 'club'} colours`}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ color: '#64748B', fontSize: 11, fontWeight: 800, lineHeight: 1.45, marginTop: 10 }}>
                            Choosing this club for a screen replaces any other club you previously picked for that same screen.
                          </div>
                          </>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                            <div style={{ width: 42, height: 42, borderRadius: 16, background: '#E2E8F0', color: '#64748B', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                              <FontAwesomeIcon icon={faBuilding} />
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ color: '#0D275B', fontSize: 15, fontWeight: 950 }}>No club colours available</div>
                              <div style={{ color: '#64748B', fontSize: 12, fontWeight: 850, marginTop: 2 }}>This group needs to be attached to a club before you can use club colours on your screens.</div>
                            </div>
                          </div>
                        )}
                      </div>

	                    <WGManageLabel C={modalC}>Settings</WGManageLabel>
	                    <div style={{ border: '1px solid #DCE6F3', background: '#FFFFFF', borderRadius: 18, padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 16, background: '#0D275B', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          <FontAwesomeIcon icon={visibilityShared ? faUsers : faLock} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: '#0D275B', fontSize: 15, fontWeight: 950 }}>Athletes see</div>
                          <div style={{ color: '#64748B', fontSize: 12, fontWeight: 850, marginTop: 2 }}>Coach: all</div>
                        </div>
                        {workoutGroupAnalyticsVisibilitySaving && <span style={{ color: '#64748B', fontSize: 11, fontWeight: 900 }}>Saving...</span>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 13 }}>
                        {[
                          { key: WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.ownOnly, icon: faUser, title: 'Private', sub: 'Their own' },
                          { key: WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group, icon: faUsers, title: 'Shared', sub: 'Everyone' },
                        ].map(option => {
                          const selected = visibility === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              disabled={workoutGroupAnalyticsVisibilitySaving}
                              onClick={() => saveWGMemberAnalyticsVisibility(option.key)}
                              style={{ minHeight: 78, border: `1px solid ${selected ? '#0C69C8' : '#E2E8F0'}`, background: selected ? '#E6F0FF' : '#F8FAFC', color: selected ? '#0D275B' : '#475569', borderRadius: 16, padding: 10, cursor: workoutGroupAnalyticsVisibilitySaving ? 'wait' : 'pointer', display: 'grid', placeItems: 'center', gap: 4, textAlign: 'center' }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: selected ? '#0C69C8' : '#64748B', fontSize: 15, fontWeight: 950 }}>
                                <FontAwesomeIcon icon={option.icon} />
                                {option.title}
                              </span>
                              <span style={{ color: selected ? '#0C69C8' : '#64748B', fontSize: 11, fontWeight: 850 }}>{option.sub}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 10 }}>
                      <WGManageTileButton C={modalC} icon={faClipboardList} title="Session target" onClick={() => openWGSessionTargetModal(activeWG)} />
                      <WGManageTileButton C={modalC} icon={activeWGCanUseNutritionStats ? faBullseye : faLock} title="Nutrition target" onClick={() => {
                        if (!activeWGCanUseNutritionStats) {
                          runManageAction(() => openWorkoutGroupUpgradePrompt('Nutrition targets, nutrition stats, and calorie reminders are included with Pro Coach and Club Coaching.'));
                          return;
                        }
                        runManageAction(() => updateWGNumber('daily_calorie_target', 'Daily calorie target'));
                      }} muted={!activeWGCanUseNutritionStats} />
                    </div>

                    {activeWGCanOpenClubCoaching && (
                      <WGManageOption C={modalC} icon={faBuilding} title={activeWG?.club_id ? 'Club dashboard' : 'Create club'} subtitle="Coaches, club tools, seasons, and club analytics" onClick={() => runManageAction(() => navigate(`/coaching/dashboard?group_id=${encodeURIComponent(activeWG.id)}&return_tab=workouts`))} />
                    )}

                    <div style={{ marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 14 }}>
                      <button
                        type="button"
                        onClick={() => runManageAction(() => openEditWG())}
                        style={{ width: '100%', minHeight: 44, border: '1px solid #DCE6F3', background: '#FFFFFF', color: '#0D275B', borderRadius: 14, padding: '10px 12px', fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                      >
                        <FontAwesomeIcon icon={faPen} />
                        Edit name
                      </button>
                      <button
                        type="button"
                        onClick={() => runManageAction(() => deleteWG())}
                        style={{ width: '100%', minHeight: 44, border: '1px solid #FECACA', background: '#FEF2F2', color: '#991B1B', borderRadius: 14, padding: '10px 12px', fontWeight: 950, cursor: 'pointer', marginTop: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Delete group
                      </button>
                    </div>
                  </div>
                </MBody>
                <MFoot C={modalC}>
                  <BtnSec C={modalC} onClick={() => setWorkoutGroupManageOpen(false)}>Close</BtnSec>
                </MFoot>
              </>
            );
          })()}
        </Modal>
        );
      })()}

      {joinCodeShare && (() => {
        const modalC = workoutGroupModalTheme(joinCodeShare.group || activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        return (
        <Modal C={modalC} onClose={() => setJoinCodeShare(null)}>
          <MHead C={modalC} title="Group Join Code" onClose={() => setJoinCodeShare(null)} />
          <MBody className="profile-workout-modal">
            <div style={{ display: 'grid', gap: 14, justifyItems: 'center', textAlign: 'center' }}>
              <div style={{ background: modalC.bgElevated, border: `1px solid ${modalC.border}`, borderRadius: 16, padding: '16px 24px', color: modalC.text, fontSize: 28, fontWeight: 950, letterSpacing: 6 }}>
                {joinCodeShare.code}
              </div>
              {joinCodeShare.warning && (
                <div style={{ background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', borderRadius: 14, padding: 12, fontSize: 13, fontWeight: 850, lineHeight: 1.45 }}>
                  {joinCodeShare.warning}
                </div>
              )}
            </div>
          </MBody>
          <MFoot C={modalC}>
            <BtnSec C={modalC} onClick={() => setJoinCodeShare(null)}>Close</BtnSec>
            <BtnPrimary C={modalC} onClick={() => copyToClipboard(joinCodeShare.code, 'Invite code')}>Copy Code</BtnPrimary>
          </MFoot>
        </Modal>
        );
      })()}

      {editingWG && (() => {
        const modalC = workoutGroupModalTheme(editingWG || activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        return (
        <Modal C={modalC} onClose={() => setEditingWG(null)}>
          <MHead C={modalC} title="Edit Workout Group" onClose={() => setEditingWG(null)} />
          <MBody>
            <FormRow C={modalC} label="Group Name">
              <input autoFocus value={editingWGName} onChange={e => setEditingWGName(e.target.value)} placeholder="Workout group name" onKeyDown={e => { if (e.key === 'Enter') saveWGName(); }} style={inputSt(modalC)} />
            </FormRow>
          </MBody>
          <MFoot C={modalC}>
            <BtnSec C={modalC} onClick={() => setEditingWG(null)}>Cancel</BtnSec>
            <BtnPrimary C={modalC} onClick={saveWGName} disabled={!editingWGName.trim()}>Save Group</BtnPrimary>
          </MFoot>
        </Modal>
        );
      })()}

      {workoutGroupSessionTargetOpen && activeWG && (() => {
        const modalC = workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        return (
        <Modal C={modalC} wide onClose={() => { if (!workoutGroupSessionTargetSaving) setWorkoutGroupSessionTargetOpen(false); }}>
          {(() => {
            const t = clubTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
            const sessionValue = Number(workoutGroupSessionTargetDraft.weeklySessionTarget);
            const xpValue = Number(workoutGroupSessionTargetDraft.weeklyXpTarget);
            const selectedSessionValue = Number.isFinite(sessionValue) && sessionValue > 0 ? Math.min(7, Math.max(1, Math.round(sessionValue))) : getWorkoutGroupWeeklySessionTarget(activeWG);
            const selectedGoal = findWorkoutGroupSessionGoalOption(workoutGroupSessionTargetDraft.goalKey) ||
              findWorkoutGroupSessionGoalOption(getWorkoutGroupSessionGoalForTarget(selectedSessionValue)) ||
              WORKOUT_GROUP_SESSION_GOAL_OPTIONS[0];
            const selectedTracking = findWorkoutGroupSessionTrackingOption(workoutGroupSessionTargetDraft.trackingKey) ||
              findWorkoutGroupSessionTrackingOption(getWorkoutGroupSessionTrackingForTarget(selectedSessionValue)) ||
              WORKOUT_GROUP_SESSION_TRACKING_OPTIONS[0];
            const selectedIntensity = findWorkoutGroupSessionIntensityOption(workoutGroupSessionTargetDraft.intensityKey) ||
              findWorkoutGroupSessionIntensityOption(getWorkoutGroupSessionIntensityForTarget(selectedSessionValue)) ||
              WORKOUT_GROUP_SESSION_INTENSITY_OPTIONS[1];
            const canSaveSessionTarget = !workoutGroupSessionTargetSaving &&
              Number.isFinite(sessionValue) && sessionValue > 0 &&
              Number.isFinite(xpValue) && xpValue > 0;
            const setDraft = (patch) => setWorkoutGroupSessionTargetDraft(prev => ({ ...prev, ...patch }));
            const renderOptionButton = ({ key, title, detail, icon, selected, onClick }) => (
              <button
                key={key}
                type="button"
                onClick={onClick}
                disabled={workoutGroupSessionTargetSaving}
                style={{ minHeight: 54, border: `1px solid ${selected ? '#0C69C8' : '#E0E7EF'}`, background: selected ? '#E6F0FF' : '#FFFFFF', borderRadius: 15, padding: '10px 12px', cursor: workoutGroupSessionTargetSaving ? 'wait' : 'pointer', display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 10, alignItems: 'center', textAlign: 'left' }}
              >
                <span style={{ width: 34, height: 34, borderRadius: 17, background: selected ? '#0C69C8' : '#F4F8FF', color: selected ? '#FFFFFF' : '#0C69C8', display: 'grid', placeItems: 'center' }}>
                  <FontAwesomeIcon icon={selected ? faCheck : icon} style={{ fontSize: 13 }} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', color: '#0D275B', fontSize: 14, fontWeight: 950 }}>{title}</span>
                  {detail && <span style={{ display: 'block', color: '#64748B', fontSize: 11, fontWeight: 800, marginTop: 2 }}>{detail}</span>}
                </span>
              </button>
            );
            return (
              <>
                <MHead C={modalC} title="Session Target" onClose={() => { if (!workoutGroupSessionTargetSaving) setWorkoutGroupSessionTargetOpen(false); }} />
                <MBody>
                  <div style={{ display: 'grid', gap: 14 }}>
                    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 24, padding: 18, color: t.onPrimary || '#fff', background: `linear-gradient(135deg, ${t.primary}, ${t.surfaceSecondary || t.secondary})` }}>
                      <div style={{ position: 'absolute', right: -24, top: 14, width: 150, height: 78, borderRadius: 32, background: t.subtleOnPrimary || 'rgba(255,255,255,0.12)', transform: 'rotate(-18deg)' }} />
                      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: t.mutedOnPrimary || 'rgba(255,255,255,0.76)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.8 }}>Training Target</div>
                          <h2 style={{ margin: '5px 0 4px', color: t.onPrimary || '#fff', fontSize: 26, lineHeight: 1.05 }}>Session target</h2>
                          <div style={{ color: t.mutedOnPrimary || 'rgba(255,255,255,0.76)', fontSize: 13, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeWG.name || 'Workout group'}</div>
                        </div>
                        <div style={{ border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.13)', borderRadius: 18, padding: '8px 12px', minWidth: 94, textAlign: 'center' }}>
                          <div style={{ color: '#fff', fontSize: 22, fontWeight: 950, lineHeight: 1 }}>{selectedSessionValue}</div>
                          <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 10, fontWeight: 950, marginTop: 2 }}>days/week</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ color: '#64748B', fontSize: 13, fontWeight: 850, lineHeight: 1.5 }}>
                      Choose a realistic weekly target your group can understand at a glance.
                    </div>

                    <WGSessionTargetField
                      C={modalC}
                      label="Goal preset"
                      title={selectedGoal.label}
                      detail={selectedGoal.detail}
                      open={workoutGroupSessionTargetPicker === 'goal'}
                      onToggle={() => setWorkoutGroupSessionTargetPicker(prev => prev === 'goal' ? null : 'goal')}
                    >
                      {WORKOUT_GROUP_SESSION_GOAL_OPTIONS.map(option => renderOptionButton({
                        key: option.key,
                        title: option.label,
                        detail: `${option.detail} - suggests ${option.suggestedTarget} day${option.suggestedTarget === 1 ? '' : 's'}`,
                        icon: faDumbbell,
                        selected: workoutGroupSessionTargetDraft.goalKey === option.key,
                        onClick: () => {
                          setDraft({ goalKey: option.key, weeklySessionTarget: String(option.suggestedTarget) });
                          setWorkoutGroupSessionTargetPicker(null);
                        },
                      }))}
                    </WGSessionTargetField>

                    <WGSessionTargetField
                      C={modalC}
                      label="Weekly intensity"
                      title={selectedIntensity.label}
                      detail={selectedIntensity.detail}
                      open={workoutGroupSessionTargetPicker === 'intensity'}
                      onToggle={() => setWorkoutGroupSessionTargetPicker(prev => prev === 'intensity' ? null : 'intensity')}
                    >
                      {WORKOUT_GROUP_SESSION_INTENSITY_OPTIONS.map(option => renderOptionButton({
                        key: option.key,
                        title: option.label,
                        detail: option.detail,
                        icon: faBolt,
                        selected: workoutGroupSessionTargetDraft.intensityKey === option.key,
                        onClick: () => {
                          setDraft({ intensityKey: option.key });
                          setWorkoutGroupSessionTargetPicker(null);
                        },
                      }))}
                    </WGSessionTargetField>

                    <WGSessionTargetField
                      C={modalC}
                      label="Training days"
                      title={`${selectedSessionValue} days/week`}
                      open={workoutGroupSessionTargetPicker === 'days'}
                      onToggle={() => setWorkoutGroupSessionTargetPicker(prev => prev === 'days' ? null : 'days')}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(74px, 1fr))', gap: 8 }}>
                        {WORKOUT_GROUP_SESSION_TARGET_OPTIONS.map(option => {
                          const selected = selectedSessionValue === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={workoutGroupSessionTargetSaving}
                              onClick={() => {
                                setDraft({ weeklySessionTarget: String(option.value) });
                                setWorkoutGroupSessionTargetPicker(null);
                              }}
                              style={{ minHeight: 56, border: `1px solid ${selected ? '#0C69C8' : '#E0E7EF'}`, background: selected ? '#E6F0FF' : '#FFFFFF', color: selected ? '#0C69C8' : '#0D275B', borderRadius: 15, padding: '8px 10px', cursor: workoutGroupSessionTargetSaving ? 'wait' : 'pointer', textAlign: 'center' }}
                            >
                              <div style={{ fontSize: 19, fontWeight: 950, lineHeight: 1 }}>{option.label}</div>
                              <div style={{ color: '#64748B', fontSize: 10, fontWeight: 950, marginTop: 3 }}>days</div>
                            </button>
                          );
                        })}
                      </div>
                    </WGSessionTargetField>

                    <WGSessionTargetField
                      C={modalC}
                      label="Tracking focus"
                      title={selectedTracking.label}
                      detail={selectedTracking.detail}
                      open={workoutGroupSessionTargetPicker === 'tracking'}
                      onToggle={() => setWorkoutGroupSessionTargetPicker(prev => prev === 'tracking' ? null : 'tracking')}
                    >
                      {WORKOUT_GROUP_SESSION_TRACKING_OPTIONS.map(option => renderOptionButton({
                        key: option.key,
                        title: option.label,
                        detail: option.detail,
                        icon: faChartLine,
                        selected: workoutGroupSessionTargetDraft.trackingKey === option.key,
                        onClick: () => {
                          setDraft({ trackingKey: option.key });
                          setWorkoutGroupSessionTargetPicker(null);
                        },
                      }))}
                    </WGSessionTargetField>

                    <div style={{ border: '1px solid #DCE6F3', background: '#FFFFFF', borderRadius: 16, padding: '12px 13px' }}>
                      <label style={{ display: 'block', color: '#64748B', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>XP target</label>
                      <input
                        type="number"
                        min="1"
                        value={workoutGroupSessionTargetDraft.weeklyXpTarget}
                        onChange={e => setDraft({ weeklyXpTarget: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') saveWGSessionTarget(); }}
                        disabled={workoutGroupSessionTargetSaving}
                        style={{ width: '100%', border: 0, outline: 'none', background: 'transparent', color: '#0D275B', fontSize: 20, fontWeight: 950, padding: '6px 0' }}
                      />
                      <div style={{ color: Number.isFinite(xpValue) && xpValue > 0 ? '#64748B' : '#B91C1C', fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                        {Number.isFinite(xpValue) && xpValue > 0 ? 'Weekly XP target for the whole group.' : 'Enter a weekly XP target to save.'}
                      </div>
                    </div>
                  </div>
                </MBody>
                <MFoot C={modalC}>
                  <BtnSec C={modalC} onClick={() => setWorkoutGroupSessionTargetOpen(false)}>Cancel</BtnSec>
                  <BtnPrimary C={modalC} onClick={saveWGSessionTarget} disabled={!canSaveSessionTarget}>
                    {workoutGroupSessionTargetSaving ? 'Saving...' : 'Save'}
                  </BtnPrimary>
                </MFoot>
              </>
            );
          })()}
        </Modal>
        );
      })()}

      {targetModal && (() => {
        const modalC = workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        return (
        <Modal C={modalC} onClose={() => setTargetModal(null)}>
          <MHead C={modalC} title={targetModal.label} onClose={() => setTargetModal(null)} />
          <MBody>
            <FormRow C={modalC} label={targetModal.label}>
              <input autoFocus type="number" min="0" value={targetModal.value} onChange={e => setTargetModal(m => ({ ...m, value: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveWGTarget(); }} style={inputSt(modalC)} />
            </FormRow>
          </MBody>
          <MFoot C={modalC}>
            <BtnSec C={modalC} onClick={() => setTargetModal(null)}>Cancel</BtnSec>
            <BtnPrimary C={modalC} onClick={saveWGTarget}>Save Target</BtnPrimary>
          </MFoot>
        </Modal>
        );
      })()}

      {wgCalendarFormOpen && ((C) => (
        <Modal C={C} wide onClose={() => { if (!wgCalendarSaving) setWgCalendarFormOpen(false); }}>
          <MHead C={C} title={wgCalendarEditingSession ? 'Edit Session' : 'Create Session'} onClose={() => { if (!wgCalendarSaving) setWgCalendarFormOpen(false); }} />
          <MBody>
            <div style={{ display: 'grid', gap: 14 }}>
              {!activeWGHasCalendarAccess && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', borderRadius: 14, padding: 12, fontSize: 13, fontWeight: 850, lineHeight: 1.45 }}>
                  Calendar sessions are included with Pro Coach and Club Coaching.
                </div>
              )}
              <FormRow C={C} label="Session Title *">
                <input
                  autoFocus
                  value={wgCalendarForm.title}
                  onChange={e => setWgCalendarForm(form => ({ ...form, title: e.target.value }))}
                  placeholder="e.g., Group training"
                  style={inputSt(C)}
                />
              </FormRow>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10 }}>
                <FormRow C={C} label="Date *">
                  <input type="date" value={wgCalendarForm.date} onChange={e => setWgCalendarForm(form => ({ ...form, date: e.target.value }))} style={inputSt(C)} />
                </FormRow>
                <FormRow C={C} label="Start Time *">
                  <input type="time" value={wgCalendarForm.startTime} onChange={e => setWgCalendarForm(form => ({ ...form, startTime: e.target.value }))} style={inputSt(C)} />
                </FormRow>
                <FormRow C={C} label="End Time">
                  <input type="time" value={wgCalendarForm.endTime} onChange={e => setWgCalendarForm(form => ({ ...form, endTime: e.target.value, hasEndTime: true }))} disabled={!wgCalendarForm.hasEndTime} style={{ ...inputSt(C), opacity: wgCalendarForm.hasEndTime ? 1 : 0.56 }} />
                </FormRow>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.textSecondary, fontSize: 13, fontWeight: 800 }}>
                <input type="checkbox" checked={wgCalendarForm.hasEndTime} onChange={e => setWgCalendarForm(form => ({ ...form, hasEndTime: e.target.checked }))} />
                Set an end time
              </label>
              <FormRow C={C} label="Location">
                <input value={wgCalendarForm.location} onChange={e => setWgCalendarForm(form => ({ ...form, location: e.target.value }))} placeholder="Court, gym, field, or online link" style={inputSt(C)} />
              </FormRow>
              <FormRow C={C} label="Notes">
                <textarea value={wgCalendarForm.notes} onChange={e => setWgCalendarForm(form => ({ ...form, notes: e.target.value }))} placeholder="Details athletes and coaches need before the session" style={{ ...inputSt(C), minHeight: 82, resize: 'vertical', lineHeight: 1.45 }} />
              </FormRow>

              <div style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 14, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 14, fontWeight: 950 }}>Invited athletes</div>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>{wgCalendarSelectedAthleteIds.length} selected</div>
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: C.textSecondary, fontSize: 12, fontWeight: 900 }}>
                    <input type="checkbox" checked={wgCalendarForm.inviteAll} onChange={e => setWgCalendarForm(form => ({ ...form, inviteAll: e.target.checked }))} />
                    Invite all
                  </label>
                </div>
                {wgCalendarForm.inviteAll ? (
                  <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>All current group athletes will be invited. New athletes added later are not automatically added to this session.</div>
                ) : wgMembers.length === 0 ? (
                  <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800 }}>No athletes are in this group yet.</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
                    {wgMembers.map(member => {
                      const selected = wgCalendarSelectedAthleteIds.includes(member.id);
                      const name = getWorkoutGroupCalendarProfileName(member);
                      return (
                        <label key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${selected ? C.accentBorder : C.border}`, background: selected ? C.accentBgSubtle : C.bgCard, borderRadius: 12, padding: 9, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={e => setWgCalendarForm(form => ({
                              ...form,
                              selectedAthleteIds: e.target.checked
                                ? uniqValues([...form.selectedAthleteIds, member.id])
                                : form.selectedAthleteIds.filter(id => id !== member.id),
                            }))}
                          />
                          <img src={member.profile_img || noImage} alt="" style={{ width: 28, height: 28, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }} loading="lazy" />
                          <span style={{ minWidth: 0, color: C.text, fontSize: 12, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 14, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 14, fontWeight: 950 }}>Assigned coaches</div>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>{wgCalendarSelectedCoachIds.length} selected</div>
                  </div>
                  <BtnSec C={C} onClick={loadWGCalendarCoachOptions}>{wgCalendarCoachesLoading ? 'Loading...' : 'Refresh Coaches'}</BtnSec>
                </div>
                {wgCalendarCoachOptions.length === 0 ? (
                  <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800 }}>No coaches are available for this group.</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
                    {wgCalendarCoachOptions.map(coach => {
                      const selected = wgCalendarSelectedCoachIds.includes(coach.id);
                      return (
                        <label key={coach.id} style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${selected ? C.accentBorder : C.border}`, background: selected ? C.accentBgSubtle : C.bgCard, borderRadius: 12, padding: 9, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={e => setWgCalendarForm(form => ({
                              ...form,
                              selectedCoachIds: e.target.checked
                                ? uniqValues([...form.selectedCoachIds, coach.id])
                                : form.selectedCoachIds.filter(id => id !== coach.id),
                            }))}
                          />
                          <img src={coach.profile_img || noImage} alt="" style={{ width: 28, height: 28, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }} loading="lazy" />
                          <span style={{ minWidth: 0, color: C.text, fontSize: 12, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{coach.id === profileData?.id ? 'You' : getWorkoutGroupCalendarProfileName(coach)}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {wgCalendarFormError && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 12, padding: 11, fontSize: 12, fontWeight: 850 }}>
                  {wgCalendarFormError}
                </div>
              )}
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setWgCalendarFormOpen(false)}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={saveWGCalendarSession} disabled={wgCalendarSaving || !!wgCalendarFormError || !activeWGHasCalendarAccess}>
              {wgCalendarSaving ? 'Saving...' : wgCalendarEditingSession ? 'Save Session' : 'Create Session'}
            </BtnPrimary>
          </MFoot>
        </Modal>
      ))(workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES))}

      {allowedWorkoutsModal && ((C) => (
        <Modal C={C} wide onClose={() => setAllowedWorkoutsModal(false)}>
          <MHead C={C} title="Allowed Workouts" onClose={() => setAllowedWorkoutsModal(false)} />
          <MBody>
            {(() => {
              const allowedPairs = getAllowedWorkoutPairsList();
              const filteredWorkouts = coachWorkouts.filter(workout => normalizeWorkoutName(workout?.name).includes(normalizeWorkoutName(allowedWorkoutSearch)));
              const limit = workoutAccess?.limits?.assigned_workouts_per_group;
              const limitLabel = typeof limit === 'number' ? `${allowedPairs.length} / ${limit}` : `${allowedPairs.length}`;
              return (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 14, padding: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: C.text, fontSize: 14, fontWeight: 950 }}>Assigned workout plans</div>
                      <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>Choose the workouts athletes can use for this group.</div>
                    </div>
                    <div style={{ display: 'grid', placeItems: 'center', minWidth: 78, borderRadius: 12, background: C.bgCard, border: `1px solid ${C.border}`, padding: '8px 10px' }}>
                      <div style={{ color: C.accent, fontSize: 18, fontWeight: 950, lineHeight: 1 }}>{limitLabel}</div>
                      <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 900, marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>assigned</div>
                    </div>
                  </div>

                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgCard, padding: 12 }}>
                    <div style={{ color: C.textSecondary, fontSize: 12, fontWeight: 950, marginBottom: 10 }}>Selected for this group</div>
                    {allowedPairs.length > 0 ? (
                      <>
                        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                          {allowedPairs.map(pair => (
                            <div
                              key={`${pair?.code || pair?.name}_allowed`}
                              style={{ display: 'grid', gap: 9, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.text, borderRadius: 12, padding: 10, minWidth: 0 }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, minWidth: 0 }}>
                                <span style={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', fontSize: 12, fontWeight: 950, lineHeight: 1.25 }}>{pair?.name}</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                  {pair?.code && (
                                    <span style={{ border: `1px solid ${canUseWorkoutShareCodesEntitlement ? C.accentBorder : C.border}`, background: C.bgCard, color: canUseWorkoutShareCodesEntitlement ? C.accent : C.textSecondary, borderRadius: 999, padding: '4px 7px', fontSize: 10, fontWeight: 950, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                      Code {pair.code}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => confirmRemoveAllowedWorkout(pair?.name)}
                                    aria-label={`Remove ${pair?.name} from group`}
                                    title="Remove from group"
                                    style={{ width: 30, height: 30, border: `1px solid ${C.border}`, background: C.bgCard, color: C.textMuted, cursor: allowedWorkoutsSaving ? 'wait' : 'pointer', borderRadius: 10, display: 'grid', placeItems: 'center', padding: 0 }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                                  </button>
                                </span>
                              </div>
                              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 850 }}>
                                Schedule: {formatAssignedWorkoutDays(pair)}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {ASSIGNED_WORKOUT_DAYS.map(day => {
                                  const selected = normalizeAssignedWorkoutDays(pair?.scheduled_days || pair?.days).includes(day.index);
                                  return (
                                    <button
                                      key={`${pair?.name}_${day.short}`}
                                      type="button"
                                      onClick={() => updateAllowedWorkoutScheduleForGroup(pair?.name, day.index)}
                                      disabled={allowedWorkoutsSaving}
                                      aria-pressed={selected}
                                      aria-label={`${selected ? 'Remove' : 'Assign'} ${pair?.name} on ${day.label}`}
                                      style={{ minHeight: 32, border: `1px solid ${selected ? C.accent : C.border}`, background: selected ? C.accent : C.bgCard, color: selected ? '#fff' : C.textSecondary, borderRadius: 999, padding: '6px 9px', fontSize: 11, fontWeight: 950, cursor: allowedWorkoutsSaving ? 'wait' : 'pointer' }}
                                    >
                                      {day.short}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        {!canUseWorkoutShareCodesEntitlement && (
                          <div style={{ marginTop: 8, color: C.textMuted, fontSize: 11, fontWeight: 800, lineHeight: 1.45 }}>
                            Workout share codes are included with Coach Starter and higher plans.
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>No workout plans assigned yet.</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      autoFocus
                      value={allowedWorkoutSearch}
                      onChange={e => setAllowedWorkoutSearch(e.target.value)}
                      placeholder="Search your workout plans"
                      style={{ ...inputSt(C), flex: '1 1 240px' }}
                    />
                    <BtnSec C={C} onClick={loadCoachWorkouts}>Refresh</BtnSec>
                  </div>

                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, background: C.bgAlt, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 900 }}>Your Workout Plans</div>
                      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 800 }}>{filteredWorkouts.length} shown</div>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: 'auto', padding: 10 }}>
                      {coachWorkoutsLoading ? (
                        <div style={{ padding: 18, textAlign: 'center', color: C.textMuted, fontWeight: 800 }}>Loading workout plans...</div>
                      ) : filteredWorkouts.length === 0 ? (
                        <div style={{ padding: 16, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                          <div style={{ color: C.text, fontSize: 14, fontWeight: 900 }}>No workout plans found</div>
                          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, lineHeight: 1.5, marginTop: 5 }}>Create workouts first, then assign them to this group here.</div>
                        </div>
                      ) : filteredWorkouts.map(workout => {
                        const assignedPair = allowedPairs.find(pair => normalizeWorkoutName(pair?.name) === normalizeWorkoutName(workout.name));
                        const assigned = !!assignedPair;
                        return (
                          <button
                            key={workout.id}
                            onClick={() => assigned ? notify('Workout already assigned to this group.', 'warning') : addAllowedWorkoutFromPlan(workout)}
                            disabled={allowedWorkoutsSaving}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', border: `1px solid ${assigned ? C.accent : C.border}`, background: C.bgCard, borderRadius: 14, padding: 12, marginBottom: 8, cursor: allowedWorkoutsSaving ? 'wait' : 'pointer', opacity: allowedWorkoutsSaving ? 0.72 : 1 }}
                          >
                            <div style={{ width: 38, height: 38, borderRadius: 19, background: assigned ? C.accent : C.accentBgSubtle, color: assigned ? '#fff' : C.accent, display: 'grid', placeItems: 'center', flexShrink: 0, fontWeight: 950 }}>
                              {assigned ? '✓' : <FontAwesomeIcon icon={faDumbbell} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: C.text, fontSize: 14, fontWeight: 900, lineHeight: 1.25, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{workout.name}</div>
                              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>
                                {assigned ? `Assigned · ${formatAssignedWorkoutDays(assignedPair)}` : 'Tap to assign'}
                              </div>
                            </div>
                            {workout.share_code && (
                              <span style={{ border: `1px solid ${canUseWorkoutShareCodesEntitlement ? C.accentBorder : C.border}`, background: canUseWorkoutShareCodesEntitlement ? C.accentBgSubtle : C.bgCard, color: canUseWorkoutShareCodesEntitlement ? C.accent : C.textSecondary, borderRadius: 999, padding: '6px 8px', fontSize: 10, fontWeight: 950, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', flexShrink: 0 }}>
                                Share {workout.share_code}
                              </span>
                            )}
                            <span style={{ background: assigned ? 'rgba(22,101,52,0.10)' : C.text, color: assigned ? '#166534' : '#fff', borderRadius: 12, padding: '7px 10px', fontSize: 11, fontWeight: 900 }}>
                              {assigned ? 'Added' : 'Add'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {!canUseWorkoutShareCodesEntitlement && (
                    <div style={{ marginTop: 8, color: C.textMuted, fontSize: 11, fontWeight: 800, lineHeight: 1.45 }}>
                      Workout share codes are included with Coach Starter and higher plans.
                    </div>
                  )}
                </div>
              );
            })()}
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setAllowedWorkoutsModal(false)}>Done</BtnSec>
          </MFoot>
        </Modal>
      ))(workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES))}

      {workoutGroupAnalyticsOpen && activeWG && (() => {
        const t = clubTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        const modalC = workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        const base = modalC.mode === 'dark'
          ? { text: '#F3F7FF', muted: '#B7C3D6', border: '#334155' }
          : { text: '#0A1D44', muted: '#64748B', border: '#E0E7EF' };
        const sessionTarget = Number(activeWG?.weekly_session_target || 3);
        const groupTarget = Number(activeWG?.weekly_xp_target || 1000);
        const athleteTarget = wgMembers.length ? Math.round(groupTarget / wgMembers.length) : groupTarget;
        const selectedReviewSeasonId = String(wgRangeMode || '').startsWith('season:') ? String(wgRangeMode).replace('season:', '') : null;
        const reviewSeason = selectedReviewSeasonId
          ? (wgClubSeasons || []).find(row => row.id === selectedReviewSeasonId) || activeWG?.workout_club_seasons || null
          : activeWG?.workout_club_seasons || null;
        const reviewRangeOption = WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS.find(option => option.value === wgRangeMode) ||
          WORKOUT_GROUP_PROFILE_RANGE_OPTIONS.find(option => option.value === wgRangeMode);
        const measurementLabel = selectedReviewSeasonId
          ? 'Full season'
          : wgRangeMode === 'date'
            ? fmtDate(wgSelectedDate)
            : wgRangeMode === 'custom'
              ? `${Math.max(1, Math.min(365, Math.round(Number(wgCustomDays || 7) || 7)))} days`
              : reviewRangeOption?.label === '7D'
                ? 'Last 7 days'
                : reviewRangeOption?.label || (timeRange === 0 ? 'All time' : `${timeRange} days`);
        const seasonStartLabel = reviewSeason?.starts_on ? fmtDate(reviewSeason.starts_on) : 'Open start';
        const seasonEndLabel = reviewSeason?.ends_on ? fmtDate(reviewSeason.ends_on) : 'Open end';
        const rangeLabel = wgRangeMode === 'date'
          ? fmtDate(wgSelectedDate)
          : reviewSeason?.name
          ? `${reviewSeason.name}: ${reviewSeason.starts_on || 'open'} to ${reviewSeason.ends_on || 'open'}`
          : timeRange === 0 ? 'All' : `${timeRange}D`;
        const athleteRows = wgMembers.map(member => {
          const perf = perfMap?.[member.id] || {};
          const xp = Number(perf.rangeXp || 0);
          const sessions = Number(perf.sessionsInRange || 0);
          const calories = Number(perf.caloriesInRange || 0);
          return { member, xp, sessions, calories, status: getWGAttentionStatus(xp, athleteTarget, sessions, sessionTarget) };
        });
        const attentionSessionCutoff = Math.ceil(sessionTarget * 0.75);
        const onPlanRows = athleteRows.filter(row => row.sessions >= sessionTarget).sort((a, b) => b.sessions - a.sessions || b.xp - a.xp);
        const closeRows = athleteRows.filter(row => row.sessions >= attentionSessionCutoff && row.sessions < sessionTarget).sort((a, b) => b.sessions - a.sessions || b.xp - a.xp);
        const activityRows = athleteRows.filter(row => row.sessions < attentionSessionCutoff).sort((a, b) => a.sessions - b.sessions || a.xp - b.xp);
        const selectedAnalyticsAthlete = wgAnalyticsAthleteId === 'all'
          ? null
          : athleteRows.find(row => row.member.id === wgAnalyticsAthleteId) || null;
        const selectedAnalyticsAthleteName = selectedAnalyticsAthlete
          ? (`${selectedAnalyticsAthlete.member.name || ''} ${selectedAnalyticsAthlete.member.surname || ''}`).trim() || selectedAnalyticsAthlete.member.username || 'Athlete'
          : '';
        const analyticsSessions = selectedAnalyticsAthlete
          ? wgAnalyticsSessions.filter(session => String(session.profile_id || '') === String(selectedAnalyticsAthlete.member.id || ''))
          : wgAnalyticsSessions;
        const analyticsStats = {
          ...getWorkoutSessionsTotals(analyticsSessions),
          totalSessions: analyticsSessions.length,
        };
        const sortedAttentionRows = [...activityRows, ...closeRows, ...onPlanRows];
        const attentionPreviewLimit = 6;
        const visibleAttentionCount = showAllWGAnalyticsAthletes ? sortedAttentionRows.length : Math.min(sortedAttentionRows.length, attentionPreviewLimit);
        const hiddenAttentionCount = Math.max(0, sortedAttentionRows.length - visibleAttentionCount);
        const needAttentionCount = activityRows.length;
        const closeCount = closeRows.length;
        const onPlanCount = onPlanRows.length;
        const sessionCompletion = sessionTarget > 0 && wgMembers.length
          ? Math.min(100, Math.round((Number(analyticsStats?.totalSessions || 0) / (sessionTarget * (selectedAnalyticsAthlete ? 1 : wgMembers.length))) * 100))
          : 0;
        const groupDurationSeconds = Number(analyticsStats?.totalDurationSeconds || 0);
        const groupRestSeconds = Number(analyticsStats?.totalRestSeconds || 0);
        const groupActiveMinutes = Math.max(1, Math.round(Math.max(0, groupDurationSeconds - groupRestSeconds) / 60));
        const groupWorkDensity = analyticsStats?.totalVolume > 0 ? Number(analyticsStats.totalVolume || 0) / groupActiveMinutes : 0;
        const groupRestRatio = groupDurationSeconds > 0 ? Math.round((groupRestSeconds / groupDurationSeconds) * 100) : 0;
        const groupTrendRows = buildWorkoutGroupTrendRows(analyticsSessions, timeRange);
        const groupTrendOptions = [
          { key: 'sessions', label: 'Sessions', icon: faCalendar, metric: 'sessions', color: '#16A34A', value: Number(analyticsStats?.totalSessions || 0).toLocaleString(), detail: `${rangeLabel} completed sessions` },
          { key: 'totalSets', label: 'Sets', icon: faDumbbell, metric: 'totalSets', color: '#D5A419', value: compactWorkoutGroupNumber(analyticsStats?.totalSets || 0), detail: `${rangeLabel} completed sets` },
          { key: 'totalReps', label: 'Reps', icon: faRepeat, metric: 'totalReps', color: t.enabled ? (t.surfaceSecondary || t.accent || '#111827') : '#22D3EE', value: compactWorkoutGroupNumber(analyticsStats?.totalReps || 0), detail: `${rangeLabel} completed reps` },
          { key: 'totalVolume', label: 'Volume', icon: faDumbbell, metric: 'totalVolume', color: t.accent || '#0C69C8', value: formatWorkoutGroupVolume(analyticsStats?.totalVolume || 0), detail: `${rangeLabel} completed volume` },
        ];
        const selectedGroupTrend = groupTrendOptions.find(option => option.key === wgTrendMetric) || groupTrendOptions[0];
        const workoutTrendTargets = buildWorkoutGroupWorkoutTargets(analyticsSessions);
        const activeWorkoutTarget = workoutTrendTargets.find(workout => workout.key === selectedWGWorkoutKey) || workoutTrendTargets[0] || null;
        const activeExerciseTarget = activeWorkoutTarget?.exercises?.find(exercise => exercise.id === selectedWGExerciseId) || null;
        const activeWorkoutTrendTarget = activeExerciseTarget || activeWorkoutTarget;
        const workoutTrendOptions = [
          { key: 'totalSets', label: 'Sets', icon: faDumbbell, metric: 'totalSets', color: '#D5A419', value: compactWorkoutGroupNumber(activeWorkoutTrendTarget?.total_sets || 0) },
          { key: 'totalReps', label: 'Reps', icon: faRepeat, metric: 'totalReps', color: t.enabled ? (t.surfaceSecondary || t.accent || '#111827') : '#22D3EE', value: compactWorkoutGroupNumber(activeWorkoutTrendTarget?.total_reps || 0) },
          { key: 'totalVolume', label: 'Volume', icon: faDumbbell, metric: 'totalVolume', color: t.accent || '#0C69C8', value: formatWorkoutGroupVolume(activeWorkoutTrendTarget?.total_volume || 0) },
          { key: 'sessions', label: 'Sessions', icon: faCalendar, metric: 'sessions', color: '#16A34A', value: Number(activeWorkoutTrendTarget?.sessions_count || 0).toLocaleString() },
        ];
        const selectedWorkoutTrend = workoutTrendOptions.find(option => option.key === wgWorkoutTrendMetric) || workoutTrendOptions[0];
        const activeWorkoutTrendRows = buildWorkoutGroupTrendRows(activeWorkoutTrendTarget?.trendSessions || [], timeRange);
        const activeWorkoutImageUrl = activeExerciseTarget
          ? (activeExerciseTarget.image_url || activeExerciseTarget.gif_url || '')
          : (activeWorkoutTarget?.exercises || []).find(exercise => exercise.image_url || exercise.gif_url)?.image_url ||
            (activeWorkoutTarget?.exercises || []).find(exercise => exercise.image_url || exercise.gif_url)?.gif_url ||
            '';
        const activeWorkoutTrendLabel = activeWorkoutTrendTarget?.name || activeWorkoutTrendTarget?.label || 'Workout';
        const groupTrendHasBreakdown = groupTrendRows.some(row => Number(row?.[selectedGroupTrend.metric] || 0) > 0);
        const workoutTrendHasBreakdown = activeWorkoutTrendRows.some(row => Number(row?.[selectedWorkoutTrend.metric] || 0) > 0);
        const topExercises = workoutTrendTargets
          .flatMap(workout => (workout.exercises || []).map(exercise => ({ ...exercise, workoutLabel: workout.label })))
          .sort((a, b) => (b.total_xp - a.total_xp) || (b.total_reps - a.total_reps))
          .slice(0, 4);
        const nutritionMembers = selectedAnalyticsAthlete ? [selectedAnalyticsAthlete.member] : wgMembers;
        const nutritionRowsForSelection = selectedAnalyticsAthlete
          ? wgNutritionRows.filter(row => String(row?.user_id || '') === String(selectedAnalyticsAthlete.member.id || ''))
          : wgNutritionRows;
        const groupNutrition = selectedAnalyticsAthlete
          ? buildWorkoutGroupNutritionSummary(nutritionRowsForSelection, {
              expectedDays: wgNutritionDays,
              athleteCount: 1,
              dailyCalorieTarget: safeWorkoutGroupNutritionNumber(activeWG?.daily_calorie_target, 2500),
            })
          : wgNutritionSummary;
        const groupDailyCalorieTarget = safeWorkoutGroupNutritionNumber(activeWG?.daily_calorie_target, 2500);
        const selectedNutritionProfiles = selectedAnalyticsAthlete
          ? [wgNutritionProfilesByAthlete?.[selectedAnalyticsAthlete.member.id]].filter(Boolean)
          : Object.values(wgNutritionProfilesByAthlete || {});
        const groupProteinTargets = selectedNutritionProfiles
          .map(profile => getWorkoutGroupAthleteProteinTarget(activeWG, profile))
          .filter(value => Number.isFinite(value) && value > 0);
        const groupProteinTarget = groupProteinTargets.length
          ? Math.round(groupProteinTargets.reduce((sum, value) => sum + value, 0) / groupProteinTargets.length)
          : null;
        const groupCarbsTarget = safeWorkoutGroupNutritionNumber(activeWG?.daily_carbs_target_g, NaN);
        const groupFatTarget = safeWorkoutGroupNutritionNumber(activeWG?.daily_fat_target_g, NaN);
        const groupFibreTargets = selectedNutritionProfiles
          .map(profile => safeWorkoutGroupNutritionNumber(profile?.fiber_g, NaN))
          .filter(value => Number.isFinite(value) && value > 0);
        const groupFibreTarget = groupFibreTargets.length
          ? Math.round(groupFibreTargets.reduce((sum, value) => sum + value, 0) / groupFibreTargets.length)
          : null;
        const groupNutritionHasData = activeWGCanUseNutritionStats && safeWorkoutGroupNutritionNumber(groupNutrition?.logged_days) > 0;
        const groupNutritionInsight = buildWorkoutGroupNutritionInsight(groupNutrition, groupDailyCalorieTarget, rangeLabel, groupProteinTarget, selectedAnalyticsAthlete ? 'athlete' : 'group');
        const groupNutritionStory = buildWorkoutGroupNutritionStory(nutritionRowsForSelection, nutritionMembers, groupNutrition, {
          expectedDays: wgNutritionDays,
          dailyCalorieTarget: groupDailyCalorieTarget,
          dailyProteinTarget: groupProteinTarget,
          dailyCarbsTarget: groupCarbsTarget,
          dailyFatTarget: groupFatTarget,
          dailyFibreTarget: groupFibreTarget,
        });
        const groupAvgCalories = safeWorkoutGroupNutritionNumber(groupNutrition?.average_calories);
        const groupCalorieProgress = groupDailyCalorieTarget > 0
          ? Math.min(100, Math.round((groupAvgCalories / groupDailyCalorieTarget) * 100))
          : 0;
        const customRangeActive = wgRangeMode === 'custom' || !WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS.some(option => option.value === wgRangeMode);
        const openLockedRange = () => openWorkoutGroupUpgradePrompt('Date, 14D, 30D, 90D, All, and custom analytics ranges are included with Coach Starter and higher plans.');
        const applyCustomRange = () => {
          if (!canUseWGExtendedRanges) {
            openLockedRange();
            return;
          }
          const days = Math.max(1, Math.min(365, Math.round(Number(wgCustomDays || 7) || 7)));
          setWgCustomDays(String(days));
          setWgRangeMode('custom');
          setTimeRange(days);
        };
        const renderAthleteDecisionRow = (row) => {
          const name = (`${row.member.name || ''} ${row.member.surname || ''}`).trim() || row.member.username || 'Athlete';
          const role = getWorkoutGroupMemberPosition(row.member);
          const sessionPct = sessionTarget > 0 ? Math.min(100, Math.round((row.sessions / sessionTarget) * 100)) : 0;
          const xpPct = athleteTarget > 0 ? Math.min(100, Math.round((row.xp / athleteTarget) * 100)) : 0;
          const calorieWindowTarget = groupDailyCalorieTarget * Math.max(1, Number(wgNutritionDays || 1));
          const caloriePct = calorieWindowTarget > 0 ? Math.min(100, Math.round((row.calories / calorieWindowTarget) * 100)) : 0;
          return (
            <button key={row.member.id} type="button" onClick={() => { setWorkoutGroupAnalyticsOpen(false); openWGAthlete(row.member); }} style={{ width: '100%', border: `1px solid ${base.border}`, background: '#fff', borderRadius: 16, padding: 12, cursor: 'pointer', display: 'grid', gridTemplateColumns: isMobile ? '48px minmax(0,1fr)' : '54px minmax(0,1fr) 132px', alignItems: 'center', gap: 12, textAlign: 'left' }}>
              <img src={row.member.profile_img || noImage} alt="" style={{ width: isMobile ? 48 : 54, height: isMobile ? 48 : 54, borderRadius: 16, objectFit: 'cover' }} loading="lazy" />
              <span style={{ minWidth: 0, display: 'grid', gap: 7 }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', color: base.text, fontSize: 14, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    {role && <span style={{ display: 'block', color: base.muted, fontSize: 11, fontWeight: 850, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{role}</span>}
                  </span>
                  {isMobile && <span style={{ border: `1px solid ${row.status.border}`, background: row.status.bg, color: row.status.text, borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 950, flexShrink: 0 }}>{row.status.label}</span>}
                </span>
                <span style={{ display: 'grid', gridTemplateColumns: activeWGCanUseNutritionStats ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: 7 }}>
                  {[
                    { label: `${row.sessions}/${sessionTarget}`, sub: 'sessions', pct: sessionPct, color: '#16A34A' },
                    { label: `${row.xp.toLocaleString()}/${athleteTarget.toLocaleString()}`, sub: 'XP', pct: xpPct, color: '#0C69C8' },
                    ...(activeWGCanUseNutritionStats ? [{ label: `${row.calories.toLocaleString()}/${calorieWindowTarget.toLocaleString()}`, sub: 'kcal', pct: caloriePct, color: '#D97706' }] : []),
                  ].map(item => (
                    <span key={item.sub} style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', color: base.text, fontSize: 11, fontWeight: 950, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                      <span style={{ display: 'block', color: base.muted, fontSize: 9, fontWeight: 950, textTransform: 'uppercase', marginTop: 2 }}>{item.sub}</span>
                      <span style={{ display: 'block', height: 5, background: '#E2E8F0', borderRadius: 999, overflow: 'hidden', marginTop: 5 }}>
                        <span style={{ display: 'block', width: `${item.pct}%`, height: '100%', background: item.color, borderRadius: 999 }} />
                      </span>
                    </span>
                  ))}
                </span>
              </span>
              {!isMobile && <span style={{ justifySelf: 'end', border: `1px solid ${row.status.border}`, background: row.status.bg, color: row.status.text, borderRadius: 999, padding: '7px 10px', fontSize: 11, fontWeight: 950 }}>{row.status.label}</span>}
            </button>
          );
        };
        const renderAthleteBucket = ({ title, rows, tone, empty }) => (
          <section key={title} style={{ border: `1px solid ${tone.border}`, background: tone.bg, borderRadius: 18, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: rows.length ? 10 : 0 }}>
              <div>
                <h4 style={{ margin: 0, color: tone.text, fontSize: 14, fontWeight: 950 }}>{title}</h4>
                <div style={{ color: tone.muted, fontSize: 11, fontWeight: 850, marginTop: 2 }}>{rows.length} athlete{rows.length === 1 ? '' : 's'}</div>
              </div>
              <span style={{ width: 34, height: 34, borderRadius: 13, display: 'grid', placeItems: 'center', background: '#fff', color: tone.text, fontSize: 14, fontWeight: 950 }}>{rows.length}</span>
            </div>
            {rows.length ? <div style={{ display: 'grid', gap: 8 }}>{rows.map(renderAthleteDecisionRow)}</div> : <div style={{ color: tone.muted, fontSize: 12, fontWeight: 850 }}>{empty}</div>}
          </section>
        );
        return (
          <Modal C={modalC} wide extraWide onClose={() => setWorkoutGroupAnalyticsOpen(false)}>
            <MHead C={modalC} title="Group Analytics" onClose={() => setWorkoutGroupAnalyticsOpen(false)} />
            <MBody className="profile-workout-analytics-modal">
              <div style={{ display: 'grid', gap: 12 }}>
                <WorkoutGroupAnalyticsAthleteSelector
                  athletes={athleteRows.map(row => ({
                    ...row.member,
                    name: (`${row.member.name || ''} ${row.member.surname || ''}`).trim() || row.member.username || 'Athlete',
                  }))}
                  selectedId={wgAnalyticsAthleteId}
                  accent={t.primary || '#0C69C8'}
                  noImage={noImage}
                  onSelectAll={() => {
                    setWgAnalyticsAthleteId('all');
                    setSelectedWGWorkoutKey('');
                    setSelectedWGExerciseId('');
                  }}
                  onSelectAthlete={(athlete) => {
                    setWgAnalyticsAthleteId(athlete.id);
                    setSelectedWGWorkoutKey('');
                    setSelectedWGExerciseId('');
                  }}
                />
                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: isMobile ? 13 : 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) minmax(260px, 0.65fr)', gap: 14, alignItems: 'stretch', marginBottom: 13 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: t.primary || '#0C69C8', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>Performance review</div>
                      <h3 style={{ margin: '5px 0 0', color: base.text, fontSize: isMobile ? 22 : 26, lineHeight: 1.05, fontWeight: 950 }}>
                        {reviewSeason?.name || 'Open training window'}
                      </h3>
                      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 10 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${base.border}`, background: '#F8FAFF', color: base.text, borderRadius: 999, padding: '7px 10px', fontSize: 11, fontWeight: 950 }}>
                          <FontAwesomeIcon icon={faCalendar} style={{ color: t.primary || '#0C69C8', fontSize: 10 }} />
                          {reviewSeason?.status ? capitalise(reviewSeason.status) : 'Season'}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${base.border}`, background: '#F8FAFF', color: base.text, borderRadius: 999, padding: '7px 10px', fontSize: 11, fontWeight: 950 }}>
                          <FontAwesomeIcon icon={faClock} style={{ color: t.primary || '#0C69C8', fontSize: 10 }} />
                          {measurementLabel}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                      {[
                        { label: 'Starts', value: seasonStartLabel },
                        { label: 'Ends', value: seasonEndLabel },
                      ].map(item => (
                        <div key={item.label} style={{ border: `1px solid ${base.border}`, background: '#F8FAFF', borderRadius: 15, padding: '11px 12px', minWidth: 0 }}>
                          <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>{item.label}</div>
                          <div style={{ color: base.text, fontSize: 14, fontWeight: 950, marginTop: 5, lineHeight: 1.15 }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap', borderTop: `1px solid ${base.border}`, paddingTop: 12 }}>
                    <div style={{ color: base.muted, fontSize: 12, fontWeight: 900 }}>Measurement window</div>
                    <WGAppIcon icon={faCalendar} color={t.primary || '#0C69C8'} bg="#E6F0FF" size={34} />
                  </div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    {WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS.map(option => {
                      const selected = wgRangeMode === option.value && !customRangeActive;
                      const locked = !canUseWGExtendedRanges && option.days !== 7;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={selected}
                          disabled={false}
                          onClick={() => locked ? openLockedRange() : selectWGRange(option.value)}
                          style={{ minHeight: 38, flex: '1 1 76px', border: `1px solid ${selected ? t.primary : locked ? '#CBD5E1' : '#E2E8F0'}`, background: selected ? t.primary : locked ? '#F1F5F9' : '#F8FAFF', color: selected ? '#fff' : locked ? '#94A3B8' : base.muted, borderRadius: 13, padding: '8px 10px', fontSize: 12, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: locked ? 0.62 : 1 }}
                        >
                          {locked && <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} />}
                          {option.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      aria-pressed={customRangeActive}
                      onClick={() => canUseWGExtendedRanges ? setWgRangeMode('custom') : openLockedRange()}
                      style={{ minHeight: 38, flex: '1 1 92px', border: `1px solid ${customRangeActive ? t.primary : canUseWGExtendedRanges ? '#E2E8F0' : '#CBD5E1'}`, background: customRangeActive ? t.primary : canUseWGExtendedRanges ? '#F8FAFF' : '#F1F5F9', color: customRangeActive ? '#fff' : canUseWGExtendedRanges ? base.muted : '#94A3B8', borderRadius: 13, padding: '8px 10px', fontSize: 12, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: canUseWGExtendedRanges ? 1 : 0.62 }}
                    >
                      {!canUseWGExtendedRanges && <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} />}
                      Custom
                    </button>
                  </div>
                  {wgRangeMode === 'date' && canUseWGExtendedRanges && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label htmlFor="wg-group-analytics-review-date" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Analytics review date</label>
                      <input
                        id="wg-group-analytics-review-date"
                        type="date"
                        value={wgSelectedDate}
                        onChange={e => setWgSelectedDate(e.target.value || localDateInputValue(new Date()))}
                        style={{ ...inputSt(C), flex: '1 1 180px', minHeight: 44, background: '#F8FAFF' }}
                      />
                    </div>
                  )}
                  {customRangeActive && canUseWGExtendedRanges && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label htmlFor="wg-group-analytics-custom-days" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Custom review days</label>
                      <input
                        id="wg-group-analytics-custom-days"
                        type="number"
                        min="1"
                        max="365"
                        value={wgCustomDays}
                        onChange={e => setWgCustomDays(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                        placeholder="Days"
                        style={{ ...inputSt(C), flex: '1 1 160px', minHeight: 44, background: '#F8FAFF' }}
                      />
                      <button type="button" onClick={applyCustomRange} style={{ minHeight: 44, border: 0, background: t.primary, color: '#fff', borderRadius: 14, padding: '10px 16px', fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>Apply</button>
                    </div>
                  )}
                </section>

                <div style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${t.accent || '#0D47B7'}`, background: `linear-gradient(135deg, ${t.primary || '#061B4F'}, ${t.surfaceSecondary || t.secondary || '#0D47B7'})`, borderRadius: 22, padding: isMobile ? 15 : 18, color: t.onPrimary || '#fff' }}>
                  <span aria-hidden="true" style={{ position: 'absolute', right: -24, top: 14, width: 160, height: 88, borderRadius: 32, background: t.subtleOnPrimary || 'rgba(255,255,255,0.16)', transform: 'rotate(-16deg)' }} />
                  <FontAwesomeIcon aria-hidden="true" icon={faChartLine} style={{ position: 'absolute', right: 28, bottom: 16, color: t.onPrimary || '#fff', opacity: 0.14, fontSize: isMobile ? 54 : 76 }} />
                  <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 210px', gap: 16, alignItems: 'center' }}>
                    <div>
	                      <div style={{ color: t.mutedOnPrimary || 'rgba(255,255,255,0.76)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>{selectedAnalyticsAthlete ? 'Athlete status' : 'Team status'}</div>
	                      <h3 style={{ color: t.onPrimary || '#fff', margin: '7px 0 0', fontSize: isMobile ? 24 : 31, lineHeight: 1.1, fontWeight: 950 }}>
	                        {selectedAnalyticsAthlete ? `${selectedAnalyticsAthlete.sessions}/${sessionTarget} sessions` : `${onPlanCount}/${wgMembers.length} on plan`}
	                      </h3>
	                      <div style={{ color: t.mutedOnPrimary || 'rgba(255,255,255,0.76)', fontSize: 13, fontWeight: 850, lineHeight: 1.45, marginTop: 8 }}>
	                        {selectedAnalyticsAthlete
	                          ? `${selectedAnalyticsAthlete.xp.toLocaleString()} XP · ${selectedAnalyticsAthlete.calories.toLocaleString()} kcal · ${selectedAnalyticsAthleteName}`
	                          : `${needAttentionCount} need activity · ${closeCount} close · ${Number(analyticsStats?.totalSessions || 0).toLocaleString()} completed sessions`}
	                      </div>
	                      {!selectedAnalyticsAthlete && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
	                        <button type="button" onClick={() => confirmWGNotification({ message: 'Quick reminder to get your workout session done this week.', type: 'GROUP_REMINDER' })} style={{ minHeight: 38, border: '1px solid rgba(255,255,255,0.28)', background: '#fff', color: t.primary || '#0C69C8', borderRadius: 12, padding: '8px 12px', fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>
	                          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 7 }} />
	                          Remind athletes
	                        </button>
                        <button type="button" onClick={() => setShowAllWGAnalyticsAthletes(true)} style={{ minHeight: 38, border: '1px solid rgba(255,255,255,0.24)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 12, padding: '8px 12px', fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>
	                          Review athletes
	                        </button>
	                      </div>}
                    </div>
                    <div style={{ border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.10)', borderRadius: 20, padding: 14, display: 'grid', gap: 10 }}>
                      {[
	                        ['On plan', `${onPlanCount}/${wgMembers.length}`],
	                        ['Close', closeCount.toLocaleString()],
	                        ['Needs activity', needAttentionCount.toLocaleString()],
                        ['Completion', `${sessionCompletion}%`],
                      ].map(([label, value]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                          <span style={{ color: 'rgba(255,255,255,0.72)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase' }}>{label}</span>
                          <span style={{ color: '#fff', fontSize: 15, fontWeight: 950 }}>{value}</span>
                        </div>
                      ))}
                      <div style={{ height: 9, background: 'rgba(255,255,255,0.20)', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${sessionCompletion}%`, background: t.enabled ? '#FFFFFF' : '#22D3EE', borderRadius: 999 }} />
                      </div>
                    </div>
                  </div>
                </div>

	                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
	                  {[
	                    { icon: faBolt, label: 'Total XP', value: numberFmt(analyticsStats?.totalXp ?? 0), color: t.primary || '#0C69C8', bg: t.enabled ? rgba(t.primary || '#0C69C8', 0.10) : '#E6F0FF' },
	                    { icon: faCalendar, label: 'Sessions', value: numberFmt(analyticsStats?.totalSessions ?? 0), color: '#16A34A', bg: '#EAFBF0' },
	                    { icon: faRepeat, label: 'Reps', value: compactWorkoutGroupNumber(analyticsStats?.totalReps ?? 0), color: t.surfaceSecondary || t.accent || '#111827', bg: t.enabled ? rgba(t.surfaceSecondary || t.accent || '#111827', 0.10) : '#E7FAFF' },
	                    { icon: faDumbbell, label: 'Volume', value: formatWorkoutGroupVolume(analyticsStats?.totalVolume ?? 0), color: t.accent || '#D5A419', bg: t.enabled ? rgba(t.accent || '#D5A419', 0.10) : '#FFF8E1' },
	                  ].map(item => <WGAppMetricCard key={item.label} icon={item.icon} label={item.label} value={item.value} color={item.color} bg={item.bg} />)}
	                </div>

	                {!selectedAnalyticsAthlete && <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ margin: 0, color: base.text, fontSize: 18 }}>Athlete Review</h3>
                      <div style={{ color: base.muted, fontSize: 13, fontWeight: 800, lineHeight: 1.4, marginTop: 4 }}>
                        Grouped by session target so the next coaching action is visible first.
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {[
                        ['On plan', `${onPlanCount}/${wgMembers.length}`],
                        ['Close', closeCount],
                        ['Needs Activity', needAttentionCount],
                      ].map(([label, value]) => (
                        <span key={label} style={{ border: `1px solid ${base.border}`, background: '#F8FAFC', color: base.text, borderRadius: 999, padding: '7px 10px', fontSize: 11, fontWeight: 950 }}>
                          {value} {label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                    {renderAthleteBucket({
                      title: 'Needs Activity',
                      rows: showAllWGAnalyticsAthletes ? activityRows : activityRows.slice(0, attentionPreviewLimit),
                      empty: 'No athletes below the activity threshold.',
                      tone: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', muted: '#B45309' },
                    })}
                    {(showAllWGAnalyticsAthletes || closeRows.length > 0) && renderAthleteBucket({
                      title: 'Close',
                      rows: showAllWGAnalyticsAthletes ? closeRows : closeRows.slice(0, Math.max(0, attentionPreviewLimit - Math.min(activityRows.length, attentionPreviewLimit))),
                      empty: 'No athletes are one step away from target.',
                      tone: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', muted: '#A16207' },
                    })}
                    {showAllWGAnalyticsAthletes && renderAthleteBucket({
                      title: 'On plan',
                      rows: onPlanRows,
                      empty: 'No athletes have hit the target yet.',
                      tone: { bg: '#ECFDF3', border: '#BBF7D0', text: '#166534', muted: '#15803D' },
                    })}
                  </div>
                  {sortedAttentionRows.length > attentionPreviewLimit && (
                    <button
                      type="button"
                      aria-expanded={showAllWGAnalyticsAthletes}
                      onClick={() => setShowAllWGAnalyticsAthletes(open => !open)}
                      style={{ marginTop: 10, width: '100%', minHeight: 42, border: `1px solid ${base.border}`, background: '#F8FAFC', color: base.text, borderRadius: 14, padding: '10px 12px', fontSize: 12, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                      {showAllWGAnalyticsAthletes ? 'Show fewer athletes' : `Show all ${sortedAttentionRows.length} athletes`}
                      <FontAwesomeIcon icon={showAllWGAnalyticsAthletes ? faChevronUp : faChevronDown} style={{ fontSize: 10 }} />
                    </button>
                  )}
                  {!showAllWGAnalyticsAthletes && hiddenAttentionCount > 0 && (
                    <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 8, textAlign: 'center' }}>
                      {hiddenAttentionCount} more athlete{hiddenAttentionCount === 1 ? '' : 's'} hidden to keep this review focused.
                    </div>
                  )}
	                </section>}

                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: isMobile ? 12 : 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto', gap: 12, alignItems: 'start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: t.primary || '#0C69C8', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Team trends</div>
                      <h3 style={{ margin: '4px 0 0', color: base.text, fontSize: 19, lineHeight: 1.15 }}>Completed volume, reps, and sessions</h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 96px)', gap: 8 }}>
                      {groupTrendOptions.map(option => {
                        const active = selectedGroupTrend.key === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setWgTrendMetric(option.key)}
                            style={{ minHeight: 46, border: `1px solid ${active ? option.color : '#E0E7EF'}`, background: active ? option.color : '#F8FAFF', color: active ? '#fff' : base.text, borderRadius: 15, padding: '8px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
                          >
                            <FontAwesomeIcon icon={option.icon} style={{ color: active ? '#fff' : option.color, fontSize: 13 }} />
                            <span style={{ color: active ? '#fff' : base.text, fontSize: 11, fontWeight: 950 }}>{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginTop: 12, border: `1px solid ${base.border}`, background: '#F8FAFC', borderRadius: 18, padding: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Selected signal</div>
                        <div style={{ color: base.text, fontSize: 24, lineHeight: 1.05, fontWeight: 950, marginTop: 4 }}>{selectedGroupTrend.value}</div>
                      </div>
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, textAlign: isMobile ? 'left' : 'right' }}>
                        {groupTrendHasBreakdown ? measurementLabel : `${selectedGroupTrend.value} logged in this window.`}
                      </div>
                    </div>
                    <WGTrendBars rows={groupTrendRows} metric={selectedGroupTrend.metric} color={selectedGroupTrend.color} height={136} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 10 }}>
                    {[
                      { icon: faClock, label: 'Duration', value: formatWGDuration(groupDurationSeconds) },
                      { icon: faRepeat, label: 'Rest', value: formatWGDuration(groupRestSeconds) },
                      { icon: faChartLine, label: 'Rest ratio', value: `${groupRestRatio}%` },
                      { icon: faBolt, label: 'Density', value: formatWorkoutGroupVolume(groupWorkDensity).replace(' kg', '') },
                    ].map(item => (
                      <div key={item.label} style={{ border: `1px solid ${base.border}`, background: '#FFFFFF', borderRadius: 14, padding: 10, display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr)', gap: 8, alignItems: 'center', minWidth: 0 }}>
                        <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 12, background: '#E6F0FF', color: t.primary || '#0C69C8', display: 'grid', placeItems: 'center' }}>
                          <FontAwesomeIcon icon={item.icon} style={{ fontSize: 12 }} />
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', color: base.text, fontSize: 15, lineHeight: 1, fontWeight: 950, overflowWrap: 'anywhere' }}>{item.value}</span>
                          <span style={{ display: 'block', color: base.muted, fontSize: 9, fontWeight: 950, textTransform: 'uppercase', marginTop: 4 }}>{item.label}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: isMobile ? 12 : 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: t.primary || '#0C69C8', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Workout and exercise trends</div>
                      <h3 style={{ margin: '4px 0 0', color: base.text, fontSize: 19, lineHeight: 1.15 }}>Pick a workout, then choose an exercise</h3>
                    </div>
                    <WGAppIcon icon={faDumbbell} color={t.primary || '#0C69C8'} bg="#E6F0FF" size={38} />
                  </div>
                  {workoutTrendTargets.length === 0 ? (
                    <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 14, color: base.muted, fontSize: 12, fontWeight: 850 }}>No exercise detail in this range yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                      {workoutTrendTargets.length > 1 && (
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                          {workoutTrendTargets.map(workout => {
                            const selected = workout.key === activeWorkoutTarget?.key;
                            const workoutImageUrl = (workout.exercises || []).find(exercise => exercise.image_url || exercise.gif_url)?.image_url ||
                              (workout.exercises || []).find(exercise => exercise.image_url || exercise.gif_url)?.gif_url ||
                              '';
                            return (
                              <button
                                key={workout.key}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => {
                                  setSelectedWGWorkoutKey(workout.key);
                                  setSelectedWGExerciseId('');
                                }}
                                style={{ minHeight: 86, border: `1px solid ${selected ? t.primary : '#E0E7EF'}`, background: selected ? '#EAF3FF' : '#FFFFFF', borderRadius: 16, padding: 10, cursor: 'pointer', textAlign: 'left', display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', gap: 10, alignItems: 'center' }}
                              >
                                <span style={{ width: 44, height: 44, borderRadius: 14, background: selected ? '#FFFFFF' : '#EAF3FF', color: t.primary, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                                  {workoutImageUrl ? <img src={workoutImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4, boxSizing: 'border-box' }} loading="lazy" /> : <FontAwesomeIcon icon={faDumbbell} />}
                                </span>
                                <span style={{ minWidth: 0 }}>
                                  <span style={{ display: 'block', color: base.text, fontSize: 12, lineHeight: 1.18, fontWeight: 950, overflowWrap: 'anywhere' }}>{workout.label}</span>
                                  <span style={{ display: 'block', color: selected ? t.primary : base.muted, fontSize: 10, fontWeight: 950, marginTop: 5 }}>{workout.exercises.length} exercise{workout.exercises.length === 1 ? '' : 's'} · {compactWorkoutGroupNumber(workout.sessions_count || 0)} sessions</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {activeWorkoutTarget?.exercises?.length > 1 && (
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(128px, 1fr))', gap: 8 }}>
                          {activeWorkoutTarget.exercises.slice(0, 8).map(exercise => {
                            const selected = exercise.id === activeExerciseTarget?.id;
                            const imageUrl = exercise.image_url || exercise.gif_url || '';
                            return (
                              <button
                                key={`${activeWorkoutTarget.key}_${exercise.id}`}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => setSelectedWGExerciseId(current => current === exercise.id ? '' : exercise.id)}
                                style={{ minHeight: 128, border: `1px solid ${selected ? t.primary : '#E0E7EF'}`, background: selected ? '#EAF3FF' : '#FFFFFF', borderRadius: 16, padding: 9, cursor: 'pointer', textAlign: 'left', display: 'grid', gap: 8 }}
                              >
                                {imageUrl ? (
                                  <img src={imageUrl} alt="" style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', borderRadius: 12, background: '#EAF3FF', padding: 6, boxSizing: 'border-box' }} loading="lazy" />
                                ) : (
                                  <span aria-hidden="true" style={{ width: '100%', height: 68, borderRadius: 12, background: '#EAF3FF', color: t.primary, display: 'grid', placeItems: 'center' }}><FontAwesomeIcon icon={faDumbbell} /></span>
                                )}
                                <span style={{ color: base.text, fontSize: 12, lineHeight: 1.2, fontWeight: 950, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{exercise.name}</span>
                                <span style={{ color: selected ? t.primary : base.muted, fontSize: 10, fontWeight: 950 }}>{compactWorkoutGroupNumber(exercise.total_sets || 0)} sets · {compactWorkoutGroupNumber(exercise.total_reps)} reps</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {activeWorkoutTrendTarget && (
                        <div style={{ border: `1px solid ${base.border}`, background: '#F8FAFF', borderRadius: 18, padding: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '76px minmax(0, 1fr)' : '94px minmax(0, 1fr) auto', gap: 13, alignItems: 'center' }}>
                            <div style={{ width: isMobile ? 76 : 94, height: isMobile ? 76 : 94, borderRadius: 18, background: '#EAF3FF', color: t.primary, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                              {activeWorkoutImageUrl ? (
                                <img src={activeWorkoutImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6, boxSizing: 'border-box' }} loading="lazy" />
                              ) : (
                                <FontAwesomeIcon icon={faDumbbell} style={{ fontSize: 24 }} />
                              )}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                {activeExerciseTarget ? 'Selected exercise' : 'Selected workout'}
                              </div>
                              <div style={{ color: base.text, fontSize: isMobile ? 16 : 18, lineHeight: 1.16, fontWeight: 950, marginTop: 4, overflowWrap: 'anywhere' }}>{activeWorkoutTrendLabel}</div>
                              {activeExerciseTarget && activeWorkoutTarget?.label && (
                                <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, marginTop: 4, overflowWrap: 'anywhere' }}>{activeWorkoutTarget.label}</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                              {workoutTrendOptions.map(option => {
                                const active = selectedWorkoutTrend.key === option.key;
                                return (
                                  <button key={option.key} type="button" aria-pressed={active} onClick={() => setWgWorkoutTrendMetric(option.key)} style={{ minHeight: 36, border: `1px solid ${active ? option.color : '#DCE6F3'}`, background: active ? option.color : '#FFFFFF', color: active ? '#fff' : base.text, borderRadius: 12, padding: '7px 10px', fontSize: 11, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <FontAwesomeIcon icon={option.icon} />
                                    {option.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 12 }}>
                            {[
                              { label: 'Sessions', value: compactWorkoutGroupNumber(activeWorkoutTrendTarget.sessions_count || 0) },
                              { label: 'Sets', value: compactWorkoutGroupNumber(activeWorkoutTrendTarget.total_sets || 0) },
                              { label: 'Reps', value: compactWorkoutGroupNumber(activeWorkoutTrendTarget.total_reps || 0) },
                              { label: 'Volume', value: formatWorkoutGroupVolume(activeWorkoutTrendTarget.total_volume || 0) },
                            ].map(item => (
                              <div key={item.label} style={{ border: `1px solid ${base.border}`, background: '#FFFFFF', borderRadius: 14, padding: 10 }}>
                                <div style={{ color: base.text, fontSize: 16, lineHeight: 1, fontWeight: 950 }}>{item.value}</div>
                                <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', marginTop: 5 }}>{item.label}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop: 12, borderTop: `1px solid ${base.border}`, paddingTop: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                              <span style={{ color: base.muted, fontSize: 11, fontWeight: 950, textTransform: 'uppercase' }}>{selectedWorkoutTrend.label}</span>
                              <span style={{ color: selectedWorkoutTrend.color, fontSize: 22, lineHeight: 1, fontWeight: 950 }}>{selectedWorkoutTrend.value}</span>
                            </div>
                            {!workoutTrendHasBreakdown && (
                              <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, marginTop: 8 }}>
                                {selectedWorkoutTrend.value} logged in this window.
                              </div>
                            )}
                            <WGTrendBars rows={activeWorkoutTrendRows} metric={selectedWorkoutTrend.metric} color={selectedWorkoutTrend.color} height={128} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </section>

                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: isMobile ? 12 : 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div>
                      <div style={{ color: '#D97706', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Fueling summary</div>
                      <h3 style={{ margin: '4px 0 0', color: base.text, fontSize: 19, lineHeight: 1.15, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <FontAwesomeIcon icon={activeWGCanUseNutritionStats ? faFire : faLock} style={{ color: '#D97706', fontSize: 14 }} />
                        Nutrition readiness
                      </h3>
                    </div>
                    <span style={{ border: '1px solid #FED7AA', background: '#FFF7ED', color: '#9A3412', borderRadius: 12, padding: '7px 10px', fontSize: 11, fontWeight: 950 }}>
                      {formatWorkoutGroupNutritionNumber(groupDailyCalorieTarget)} kcal/day target
                    </span>
                  </div>

                  {!activeWGCanUseNutritionStats ? (
                    <div style={{ border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 16, padding: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <WGAppIcon icon={faLock} color="#D97706" bg="#FFF7ED" size={36} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: '#92400E', fontSize: 13, fontWeight: 950 }}>Nutrition analytics locked</div>
                          <div style={{ color: '#92400E', fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>
                            Group nutrition details stay hidden until this group has Pro Coach or Club Coaching nutrition access.
                          </div>
                        </div>
                      </div>
                      <button type="button" onClick={() => openWorkoutGroupUpgradePrompt('Nutrition stats, macro trends, meal logs, and calorie reminders unlock with Pro Coach or Club Coaching.')} style={{ marginTop: 12, minHeight: 40, border: 0, background: t.primary, color: '#fff', borderRadius: 13, padding: '9px 12px', fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>Review plans</button>
                    </div>
                  ) : wgNutritionError ? (
                    <div style={{ border: '1px dashed #CBD5E1', background: '#F8FAFC', borderRadius: 16, padding: 12, color: base.muted, fontSize: 12, fontWeight: 850 }}>
                      {wgNutritionError}
                    </div>
                  ) : !groupNutritionHasData ? (
                    <div style={{ border: '1px dashed #FDE68A', background: '#FFFBEB', borderRadius: 16, padding: 12 }}>
                      <div style={{ color: '#92400E', fontSize: 13, fontWeight: 950 }}>No nutrition logs yet</div>
                      <div style={{ color: '#92400E', fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>
                        Nutrition signals will appear after athletes log meals in this review window.
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.08fr) minmax(260px, .92fr)', gap: 12 }}>
                        <div style={{ border: '1px solid #FED7AA', background: '#FFFBEB', borderRadius: 18, padding: 14, display: 'grid', gap: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 128px', gap: 12, alignItems: 'start' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#92400E', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>Average intake</div>
                              <div style={{ color: '#0D275B', fontSize: isMobile ? 28 : 34, lineHeight: 1, fontWeight: 950, marginTop: 6 }}>
                                {formatWorkoutGroupNutritionNumber(groupAvgCalories)}
                                <span style={{ color: '#64748B', fontSize: 14, fontWeight: 900 }}> / {formatWorkoutGroupNutritionNumber(groupDailyCalorieTarget)} kcal</span>
                              </div>
                              <div style={{ height: 10, background: '#FDE68A', borderRadius: 999, overflow: 'hidden', marginTop: 11 }}>
                                <div style={{ height: '100%', width: `${groupCalorieProgress}%`, background: '#D97706', borderRadius: 999 }} />
                              </div>
                              <div style={{ color: '#64748B', fontSize: 12, fontWeight: 850, marginTop: 7 }}>{groupCalorieProgress}% of calorie target</div>
                            </div>
                            <div style={{ border: '1px solid #FED7AA', background: '#FFFFFF', borderRadius: 15, padding: 11 }}>
                              <div style={{ color: '#92400E', fontSize: 10, fontWeight: 950, textTransform: 'uppercase' }}>Logging</div>
                              <div style={{ color: '#0D275B', fontSize: 23, lineHeight: 1, fontWeight: 950, marginTop: 6 }}>
                                {formatWorkoutGroupNutritionNumber(groupNutritionStory.loggedDays)} / {formatWorkoutGroupNutritionNumber(groupNutritionStory.expectedDays)}
                              </div>
                              <div style={{ color: '#64748B', fontSize: 10, fontWeight: 850, marginTop: 5 }}>athlete-days</div>
                              <span style={{ display: 'inline-flex', marginTop: 9, color: groupNutritionStory.consistency >= 75 ? '#166534' : groupNutritionStory.consistency >= 40 ? '#92400E' : '#991B1B', background: groupNutritionStory.consistency >= 75 ? '#DCFCE7' : groupNutritionStory.consistency >= 40 ? '#FEF3C7' : '#FEE2E2', borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 950 }}>
                                {groupNutritionStory.coverageStatus}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))', gap: 8 }}>
                            {[
                              { label: 'Calories', value: `${formatWorkoutGroupNutritionNumber(groupNutrition?.average_calories)} kcal`, sub: `${formatWorkoutGroupNutritionNumber(groupDailyCalorieTarget)} kcal target` },
                              { label: 'Protein', value: `${formatWorkoutGroupNutritionNumber(groupNutrition?.average_protein)}g`, sub: groupProteinTarget ? `${formatWorkoutGroupNutritionNumber(groupProteinTarget)}g target` : 'average' },
                              { label: 'Carbs', value: `${formatWorkoutGroupNutritionNumber(groupNutrition?.average_carbs)}g`, sub: Number.isFinite(groupCarbsTarget) && groupCarbsTarget > 0 ? `${formatWorkoutGroupNutritionNumber(groupCarbsTarget)}g target` : 'average' },
                              { label: 'Fat', value: `${formatWorkoutGroupNutritionNumber(groupNutrition?.average_fat)}g`, sub: Number.isFinite(groupFatTarget) && groupFatTarget > 0 ? `${formatWorkoutGroupNutritionNumber(groupFatTarget)}g target` : 'average' },
                              { label: 'Fibre', value: `${formatWorkoutGroupNutritionNumber(groupNutrition?.average_fiber)}g`, sub: groupFibreTarget ? `${formatWorkoutGroupNutritionNumber(groupFibreTarget)}g target` : 'average' },
                            ].map(item => (
                              <div key={item.label} style={{ border: '1px solid #FED7AA', background: '#FFFFFF', borderRadius: 13, padding: 10, minWidth: 0 }}>
                                <div style={{ color: '#0D275B', fontSize: 16, lineHeight: 1, fontWeight: 950, overflowWrap: 'anywhere' }}>{item.value}</div>
                                <div style={{ color: '#64748B', fontSize: 9, fontWeight: 950, textTransform: 'uppercase', marginTop: 5 }}>{item.label}</div>
                                <div style={{ color: '#64748B', fontSize: 10, fontWeight: 800, marginTop: 3 }}>{item.sub}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div style={{ border: '1px solid #E0E7EF', background: '#FFFFFF', borderRadius: 18, padding: 14 }}>
                          <div style={{ color: '#0D275B', fontSize: 15, fontWeight: 950 }}>Coach next action</div>
                          <div style={{ color: '#475569', fontSize: 13, fontWeight: 850, lineHeight: 1.45, marginTop: 6 }}>{groupNutritionInsight}</div>
                          <div style={{ display: 'grid', gap: 7, marginTop: 12 }}>
                            {groupNutritionStory.athleteRows.slice(0, 4).map(athlete => (
                              <div key={athlete.id || athlete.name} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center', border: '1px solid #E0E7EF', background: '#F8FAFC', borderRadius: 12, padding: '9px 10px' }}>
                                <span style={{ minWidth: 0 }}>
                                  <span style={{ display: 'block', color: '#0D275B', fontSize: 12, lineHeight: 1.18, fontWeight: 950, overflowWrap: 'anywhere' }}>{athlete.name}</span>
                                  <span style={{ display: 'block', color: '#64748B', fontSize: 10, fontWeight: 800, marginTop: 2 }}>{athlete.loggedDays} logged day{athlete.loggedDays === 1 ? '' : 's'} · {formatWorkoutGroupNutritionNumber(athlete.avgCalories)} avg kcal</span>
                                </span>
                                <span style={{ color: athlete.status.color, background: athlete.status.bg, borderRadius: 999, padding: '4px 7px', fontSize: 10, fontWeight: 950 }}>{athlete.status.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                        <section style={{ border: '1px solid #E0E7EF', background: '#F8FAFC', borderRadius: 18, padding: 14 }}>
                          <div style={{ color: '#0D275B', fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Macro targets</div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            {groupNutritionStory.macroRows.map(row => <WGNutritionMacroRow key={row.key} row={row} />)}
                          </div>
                        </section>

                        <section style={{ border: '1px solid #E0E7EF', background: '#F8FAFC', borderRadius: 18, padding: 14 }}>
                          <div style={{ color: '#0D275B', fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Meal pattern</div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            {groupNutritionStory.mealRows.map(meal => <WGNutritionMealRow key={meal.meal} meal={meal} />)}
                          </div>
                        </section>
                      </div>

                      <section style={{ border: '1px solid #E0E7EF', background: '#FFFFFF', borderRadius: 18, padding: 14 }}>
                        <div style={{ color: '#0D275B', fontSize: 14, fontWeight: 950 }}>Repeated foods</div>
                        <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                          {groupNutritionStory.topFoods.length ? groupNutritionStory.topFoods.map((food, index) => (
                            <div key={food.name} style={{ display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr) auto', gap: 9, alignItems: 'center', border: '1px solid #E0E7EF', background: '#F8FAFC', borderRadius: 13, padding: '9px 10px' }}>
                              <span style={{ width: 28, height: 28, borderRadius: 10, background: index === 0 ? '#0C69C8' : '#E6F0FF', color: index === 0 ? '#fff' : '#0C69C8', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 950 }}>{index + 1}</span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: 'block', color: '#0D275B', fontSize: 13, lineHeight: 1.18, fontWeight: 950, overflowWrap: 'anywhere' }}>{food.name}</span>
                                <span style={{ display: 'block', color: '#64748B', fontSize: 10, fontWeight: 800, marginTop: 2 }}>{food.topMeal} · {food.athletes} athlete{food.athletes === 1 ? '' : 's'} · avg {formatWorkoutGroupNutritionNumber(food.avgCalories)} kcal</span>
                              </span>
                              <span style={{ color: '#0F766E', background: '#CCFBF1', borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 950 }}>{food.count} log{food.count === 1 ? '' : 's'}</span>
                            </div>
                          )) : (
                            <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 12, color: base.muted, fontSize: 12, fontWeight: 850 }}>No food names available in this range.</div>
                          )}
                        </div>
                      </section>
                    </div>
                  )}
                </section>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, alignItems: 'start' }}>
                  <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: isMobile ? 12 : 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div>
                        <div style={{ color: t.primary || '#0C69C8', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Exercise leaderboard</div>
                        <h3 style={{ margin: '4px 0 0', color: base.text, fontSize: 19, lineHeight: 1.15 }}>Highest training load</h3>
                      </div>
                      <WGAppIcon icon={faTrophy} color={t.primary || '#0C69C8'} bg="#E6F0FF" size={38} />
                    </div>
                    {topExercises.length === 0 ? (
                      <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 14, color: base.muted, fontSize: 12, fontWeight: 850 }}>No exercise detail in this range.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {topExercises.map((exercise, index) => (
                          <div key={`${exercise.name}_${index}`} style={{ border: `1px solid ${index === 0 ? '#CFE2FF' : base.border}`, background: index === 0 ? '#F8FAFF' : '#FFFFFF', borderRadius: 16, padding: 10, display: 'grid', gridTemplateColumns: isMobile ? '46px minmax(0,1fr)' : '54px minmax(0,1fr) 106px', gap: 10, alignItems: 'center' }}>
                            <div style={{ position: 'relative', width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: 15, background: '#E6F0FF', overflow: 'hidden', display: 'grid', placeItems: 'center', color: t.primary }}>
                              {exercise.image_url || exercise.gif_url ? (
                                <img src={exercise.image_url || exercise.gif_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4, boxSizing: 'border-box' }} loading="lazy" />
                              ) : (
                                <FontAwesomeIcon icon={faDumbbell} />
                              )}
                              <span style={{ position: 'absolute', left: 4, top: 4, minWidth: 18, height: 18, borderRadius: 7, background: index === 0 ? t.primary : '#FFFFFF', color: index === 0 ? '#fff' : t.primary, display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 950, boxShadow: '0 4px 10px rgba(12,105,200,0.18)' }}>{index + 1}</span>
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: base.text, fontSize: 13, lineHeight: 1.18, fontWeight: 950, overflowWrap: 'anywhere' }}>{exercise.name}</div>
                              <div style={{ color: base.muted, fontSize: 11, fontWeight: 800, marginTop: 3, overflowWrap: 'anywhere' }}>{exercise.workoutLabel}</div>
                              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 7 }}>
                                <span style={{ color: '#0D275B', background: '#F1F5F9', borderRadius: 999, padding: '4px 7px', fontSize: 10, fontWeight: 950 }}>{Number(exercise.total_sets || 0).toLocaleString()} sets · {Number(exercise.total_reps || 0).toLocaleString()} reps</span>
                                <span style={{ color: '#0D275B', background: '#F1F5F9', borderRadius: 999, padding: '4px 7px', fontSize: 10, fontWeight: 950 }}>{formatWorkoutGroupVolume(exercise.total_volume || 0)}</span>
                                <span style={{ color: '#0D275B', background: '#F1F5F9', borderRadius: 999, padding: '4px 7px', fontSize: 10, fontWeight: 950 }}>{Number(exercise.sessions_count || 0).toLocaleString()} sessions</span>
                              </div>
                            </div>
                            <div style={{ color: t.primary, fontSize: 15, lineHeight: 1, fontWeight: 950, textAlign: isMobile ? 'left' : 'right', gridColumn: isMobile ? '2 / -1' : 'auto' }}>
                              {Number(exercise.total_xp || 0).toLocaleString()} XP
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </MBody>
          </Modal>
        );
      })()}

      {selectedWGAthlete && (() => {
        const t = clubTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        const modalC = workoutGroupModalTheme(activeWG, C, workoutGroupThemePreferences || DEFAULT_WORKOUT_GROUP_THEME_PREFERENCES);
        const base = { text: '#0A1D44', muted: '#64748B', border: '#E0E7EF', blue: modalC.accent };
        const athleteName = (`${selectedWGAthlete.name || ''} ${selectedWGAthlete.surname || ''}`).trim() || selectedWGAthlete.username || 'Athlete';
        const canWriteNote = canManageWG();
        const athleteSessionCount = athleteSessions.length;
        const athleteTotalXp = athleteSessions.reduce((sum, session) => sum + Number(session.total_xp || 0), 0);
        const athleteTotals = getWorkoutSessionsTotals(athleteSessions);
        const athleteTotalDuration = Number(athleteTotals.totalDurationSeconds || 0);
        const athleteAvgXp = athleteSessionCount ? Math.round(athleteTotalXp / athleteSessionCount) : 0;
        const athleteActiveDays = new Set(athleteSessions.map(getWorkoutSessionDateKey).filter(Boolean)).size;
        const athleteEfforts = athleteSessions.map(session => Number(session.session_effort || 0)).filter(value => value > 0);
        const athleteAvgEffort = athleteEfforts.length ? (athleteEfforts.reduce((sum, value) => sum + value, 0) / athleteEfforts.length).toFixed(1) : '0';
        const athleteRangeLabel = wgRangeMode === 'date'
          ? fmtDate(wgSelectedDate)
          : activeWG?.workout_club_seasons?.name
          ? `${activeWG.workout_club_seasons.name}: ${activeWG.workout_club_seasons.starts_on || 'open'} to ${activeWG.workout_club_seasons.ends_on || 'open'}`
          : timeRange === 0 ? 'All' : `Last ${timeRange} days`;
        const athleteWeeksFactor = timeRange === 0 ? 1 : Math.max(1, Math.ceil(timeRange / 7));
        const athleteSessionTarget = Number(activeWG?.weekly_session_target || 3) * athleteWeeksFactor;
        const athleteCompletion = athleteSessionTarget > 0 ? Math.min(100, Math.round((athleteSessionCount / athleteSessionTarget) * 100)) : 0;
        const athleteStatus = getWorkoutGroupSessionProgressStatus(athleteSessionCount, athleteSessionTarget);
        const athleteNutrition = wgNutritionByAthlete?.[selectedWGAthlete.id] || null;
        const athleteNutritionProfile = wgNutritionProfilesByAthlete?.[selectedWGAthlete.id] || null;
        const athleteDailyCalorieTarget = safeWorkoutGroupNutritionNumber(activeWG?.daily_calorie_target || athleteNutritionProfile?.calorie_target, 2500);
        const athleteProteinTarget = getWorkoutGroupAthleteProteinTarget(activeWG, athleteNutritionProfile);
        const athleteCarbsTarget = safeWorkoutGroupNutritionNumber(activeWG?.daily_carbs_target_g || athleteNutritionProfile?.carbs_g, NaN);
        const athleteFatTarget = safeWorkoutGroupNutritionNumber(activeWG?.daily_fat_target_g || athleteNutritionProfile?.fat_g, NaN);
        const athleteFibreTarget = safeWorkoutGroupNutritionNumber(athleteNutritionProfile?.fiber_g, NaN);
        const athleteNutritionTargetTotal = safeWorkoutGroupNutritionNumber(
          athleteNutrition?.calorie_target_total,
          athleteDailyCalorieTarget * Math.max(1, Number(wgNutritionDays || 1))
        );
        const athleteNutritionHasData = activeWGCanUseNutritionStats && safeWorkoutGroupNutritionNumber(athleteNutrition?.logged_days) > 0;
        const athleteNutritionInsight = buildWorkoutGroupNutritionInsight(athleteNutrition, athleteDailyCalorieTarget, athleteRangeLabel, athleteProteinTarget, 'athlete');
        const athleteTrendRows = buildWorkoutGroupTrendRows(athleteSessions, timeRange);
        const athleteTrendOptions = [
          { key: 'totalVolume', label: 'Volume', metric: 'totalVolume', icon: faDumbbell, color: t.accent || '#0C69C8', value: formatWorkoutGroupVolume(athleteTotals.totalVolume || 0), detail: 'Completed load' },
          { key: 'totalReps', label: 'Reps', metric: 'totalReps', icon: faRepeat, color: t.enabled ? (t.surfaceSecondary || t.accent || '#111827') : '#22D3EE', value: compactWorkoutGroupNumber(athleteTotals.totalReps || 0), detail: 'Completed reps' },
          { key: 'sessions', label: 'Sessions', metric: 'sessions', icon: faCalendar, color: '#16A34A', value: athleteSessionCount.toLocaleString(), detail: 'Completed sessions' },
        ];
        const selectedAthleteTrend = athleteTrendOptions.find(option => option.key === athleteTrendMetric) || athleteTrendOptions[0];
        const exerciseMap = new Map();
        athleteSessions.forEach(session => {
          (Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : []).forEach((row, index) => {
            const exercise = Array.isArray(row.exercises) ? row.exercises[0] : row.exercises;
            const exerciseName = exercise?.name || `Exercise ${index + 1}`;
            const key = exercise?.id || exerciseName;
            const current = exerciseMap.get(key) || { name: exerciseName, reps: 0, volume: 0, xp: 0, sessions: 0, topWeight: 0 };
            const reps = Number(row.total_reps || 0);
            const avgWeight = Number(row.avg_weight || 0);
            current.reps += reps;
            current.volume += Number(row.total_volume || 0) || (avgWeight * reps);
            current.xp += Number(row.total_xp || 0);
            current.sessions += 1;
            current.topWeight = Math.max(current.topWeight, avgWeight);
            exerciseMap.set(key, current);
          });
        });
        const topExercises = Array.from(exerciseMap.values())
          .sort((a, b) => (b.xp - a.xp) || (b.reps - a.reps))
          .slice(0, 3);
        const athleteCustomRangeActive = wgRangeMode === 'custom' || !WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS.some(option => option.value === wgRangeMode);
        const openAthleteLockedRange = () => openWorkoutGroupUpgradePrompt('Date, 14D, 30D, 90D, All, and custom athlete analytics ranges are included with Coach Starter and higher plans.');
        const applyAthleteCustomRange = () => {
          if (!canUseWGExtendedRanges) {
            openAthleteLockedRange();
            return;
          }
          const days = Math.max(1, Math.min(365, Math.round(Number(wgCustomDays || 7) || 7)));
          setWgCustomDays(String(days));
          setWgRangeMode('custom');
          setTimeRange(days);
        };
        const closeAthleteModal = () => {
          setSelectedWGAthlete(null);
          setExpandedAthleteSessionId(null);
        };
        const athletePrompt = athleteSessionCount <= 0
          ? 'No completed assigned-workout sessions are logged in this window.'
          : athleteSessionCount >= athleteSessionTarget
            ? `${athleteSessionCount}/${athleteSessionTarget} sessions logged. Target reached for this window.`
            : `${athleteSessionCount}/${athleteSessionTarget} sessions logged. Keep the next session clear and close.`;
        return (
          <div
            role="presentation"
            onClick={e => {
              if (e.target === e.currentTarget) closeAthleteModal();
            }}
            style={{ position: 'fixed', inset: 0, zIndex: 2050, background: 'rgba(10,29,68,0.46)', display: 'grid', placeItems: 'center', padding: isMobile ? 10 : 18, backdropFilter: 'blur(8px)' }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="wg-athlete-title"
              onClick={e => e.stopPropagation()}
              style={{ width: 'min(920px, 100%)', maxHeight: '90svh', overflow: 'hidden', background: '#F5F8FF', borderRadius: 24, border: `1px solid ${base.border}`, boxShadow: '0 24px 70px rgba(10,29,68,0.30)', display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ position: 'relative', overflow: 'hidden', background: `linear-gradient(135deg, ${t.primary}, ${t.surfaceSecondary || t.secondary})`, color: t.onPrimary || '#fff', padding: isMobile ? '18px 16px 24px' : '24px 24px 30px', minHeight: isMobile ? 132 : 150 }}>
                <span aria-hidden="true" style={{ position: 'absolute', right: -34, top: -32, width: 170, height: 122, borderRadius: 42, background: t.subtleOnPrimary || 'rgba(255,255,255,0.17)', transform: 'rotate(-18deg)' }} />
                <FontAwesomeIcon aria-hidden="true" icon={faChartLine} style={{ position: 'absolute', right: 32, bottom: 16, color: t.onPrimary || '#fff', opacity: 0.14, fontSize: isMobile ? 56 : 82 }} />
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 12, minWidth: 0, alignItems: 'center' }}>
                    <img src={selectedWGAthlete.profile_img || noImage} alt="" style={{ width: isMobile ? 52 : 58, height: isMobile ? 52 : 58, borderRadius: 18, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.30)', flexShrink: 0 }} loading="lazy" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.6 }}>Athlete Analytics</div>
                      <h2 id="wg-athlete-title" style={{ margin: '5px 0 4px', fontSize: isMobile ? 22 : 30, lineHeight: 1.12, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? 'calc(100vw - 136px)' : 560 }}>{athleteName}</h2>
                      <div style={{ color: 'rgba(255,255,255,0.76)', fontSize: 13, lineHeight: 1.25, fontWeight: 850 }}>{athleteRangeLabel}</div>
                    </div>
                  </div>
                  <button type="button" aria-label="Close athlete analytics" onClick={closeAthleteModal} style={{ border: 0, background: '#fff', color: base.text, width: 38, height: 38, borderRadius: 19, fontWeight: 950, cursor: 'pointer', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
                  </button>
                </div>
              </div>
              <div style={{ padding: isMobile ? 12 : 16, display: 'grid', gap: 12, overflowY: 'auto', flex: 1 }}>
                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 11 }}>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Review window</h3>
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>{athleteRangeLabel}</div>
                    </div>
                    <WGAppIcon icon={faCalendar} color={modalC.accent} bg={modalC.accentBgSubtle} size={38} />
                  </div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    {WORKOUT_GROUP_ANALYTICS_RANGE_OPTIONS.map(option => {
                      const selected = wgRangeMode === option.value && !athleteCustomRangeActive;
                      const locked = !canUseWGExtendedRanges && option.days !== 7;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => locked ? openAthleteLockedRange() : selectWGRange(option.value)}
                          style={{ minHeight: 38, flex: '1 1 76px', border: `1px solid ${selected ? t.primary : locked ? '#CBD5E1' : '#E2E8F0'}`, background: selected ? t.primary : locked ? '#F1F5F9' : '#F8FAFF', color: selected ? '#fff' : locked ? '#94A3B8' : base.muted, borderRadius: 13, padding: '8px 10px', fontSize: 12, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: locked ? 0.62 : 1 }}
                        >
                          {locked && <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} />}
                          {option.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      aria-pressed={athleteCustomRangeActive}
                      onClick={() => canUseWGExtendedRanges ? setWgRangeMode('custom') : openAthleteLockedRange()}
                      style={{ minHeight: 38, flex: '1 1 92px', border: `1px solid ${athleteCustomRangeActive ? t.primary : canUseWGExtendedRanges ? '#E2E8F0' : '#CBD5E1'}`, background: athleteCustomRangeActive ? t.primary : canUseWGExtendedRanges ? '#F8FAFF' : '#F1F5F9', color: athleteCustomRangeActive ? '#fff' : canUseWGExtendedRanges ? base.muted : '#94A3B8', borderRadius: 13, padding: '8px 10px', fontSize: 12, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: canUseWGExtendedRanges ? 1 : 0.62 }}
                    >
                      {!canUseWGExtendedRanges && <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} />}
                      Custom
                    </button>
                  </div>
                  {wgRangeMode === 'date' && canUseWGExtendedRanges && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label htmlFor="wg-athlete-analytics-review-date" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Athlete analytics review date</label>
                      <input
                        id="wg-athlete-analytics-review-date"
                        type="date"
                        value={wgSelectedDate}
                        onChange={e => setWgSelectedDate(e.target.value || localDateInputValue(new Date()))}
                        style={{ ...inputSt(modalC), flex: '1 1 180px', minHeight: 44, background: '#F8FAFF' }}
                      />
                    </div>
                  )}
                  {athleteCustomRangeActive && canUseWGExtendedRanges && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label htmlFor="wg-athlete-analytics-custom-days" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Custom review days</label>
                      <input
                        id="wg-athlete-analytics-custom-days"
                        type="number"
                        min="1"
                        max="365"
                        value={wgCustomDays}
                        onChange={e => setWgCustomDays(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                        placeholder="Days"
                        style={{ ...inputSt(modalC), flex: '1 1 160px', minHeight: 44, background: '#F8FAFF' }}
                      />
                      <button type="button" onClick={applyAthleteCustomRange} style={{ minHeight: 44, border: 0, background: t.primary, color: '#fff', borderRadius: 14, padding: '10px 16px', fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>Apply</button>
                    </div>
                  )}
                </section>

                <div style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${t.enabled ? rgba(t.accent || t.primary, 0.35) : '#0D47B7'}`, background: t.enabled ? `linear-gradient(135deg, ${t.primary || '#0C69C8'} 0%, ${t.surfaceSecondary || t.secondary || '#111827'} 100%)` : 'linear-gradient(135deg,#061B4F 0%,#0D47B7 100%)', borderRadius: 22, padding: isMobile ? 16 : 20, color: '#fff', minHeight: isMobile ? 260 : 196 }}>
                  <span aria-hidden="true" style={{ position: 'absolute', right: -18, top: 20, width: 156, height: 82, borderRadius: 28, background: t.enabled ? rgba(t.primary, 0.16) : 'rgba(255,255,255,0.10)', transform: 'rotate(-16deg)' }} />
                  <div style={{ position: 'relative', minHeight: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 230px', gap: 16, alignItems: 'stretch' }}>
                    <div style={{ display: 'grid', alignContent: 'center', justifyItems: 'center', textAlign: 'center', minWidth: 0 }}>
                      <div style={{ color: t.enabled ? '#FFFFFF' : '#22D3EE', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>Coach priority</div>
                      <h3 style={{ margin: '8px 0 0', color: '#fff', fontSize: isMobile ? 24 : 30, lineHeight: 1.12, fontWeight: 950 }}>{athleteStatus.label}</h3>
                      <div style={{ color: 'rgba(255,255,255,0.76)', fontSize: 13, fontWeight: 850, lineHeight: 1.45, marginTop: 9, maxWidth: 520 }}>{athletePrompt}</div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.10)', color: '#fff', borderRadius: 999, padding: '8px 11px', fontSize: 12, fontWeight: 950, marginTop: 14 }}>
                        <FontAwesomeIcon icon={faCalendar} />
                        {athleteSessionCount}/{athleteSessionTarget} sessions
                      </div>
                    </div>
                    <div style={{ border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.10)', borderRadius: 20, padding: 14, alignSelf: 'center', minHeight: 134 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: 'rgba(255,255,255,0.78)', fontSize: 11, fontWeight: 950, textTransform: 'uppercase' }}>
                        <span>Completion</span>
                        <span style={{ color: '#fff' }}>{athleteCompletion}%</span>
                      </div>
                      <div style={{ height: 9, background: 'rgba(255,255,255,0.20)', borderRadius: 999, overflow: 'hidden', marginTop: 10 }}>
                        <div style={{ height: '100%', width: `${athleteCompletion}%`, background: t.enabled ? '#FFFFFF' : '#22D3EE', borderRadius: 999 }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 13 }}>
                        <div style={{ color: '#fff', fontSize: 22, fontWeight: 950 }}>{athleteTotalXp.toLocaleString()}</div>
                        <div style={{ color: '#fff', fontSize: 22, fontWeight: 950, textAlign: 'right' }}>{athleteAvgEffort}</div>
                        <div style={{ color: 'rgba(255,255,255,0.66)', fontSize: 10, fontWeight: 900 }}>total XP</div>
                        <div style={{ color: 'rgba(255,255,255,0.66)', fontSize: 10, fontWeight: 900, textAlign: 'right' }}>avg effort</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
                  {[
                    { icon: faCalendar, label: 'Sessions', value: athleteSessionCount.toLocaleString(), color: '#16A34A', bg: '#EAFBF0' },
                    { icon: faBolt, label: 'Avg XP', value: athleteAvgXp.toLocaleString(), color: modalC.accent, bg: modalC.accentBgSubtle },
                    { icon: faClock, label: 'Workout time', value: formatWGDuration(athleteTotalDuration), color: '#7C3AED', bg: '#F3E8FF' },
                    { icon: faFire, label: 'Active days', value: athleteActiveDays.toLocaleString(), color: '#D5A419', bg: '#FFF8E1' },
                  ].map(item => <WGAppMetricCard key={item.label} icon={item.icon} label={item.label} value={item.value} color={item.color} bg={item.bg} />)}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.1fr) minmax(260px, .9fr)', gap: 12, alignItems: 'start' }}>
                  <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                      <div>
                        <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Performance Signals</h3>
                        <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>{selectedAthleteTrend.detail}</div>
                      </div>
                      {athleteLoading && <span style={{ color: base.muted, fontSize: 11, fontWeight: 900 }}>Loading...</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 6 }}>
                      {athleteTrendOptions.map(option => {
                        const active = selectedAthleteTrend.key === option.key;
                        return (
                          <button key={option.key} type="button" onClick={() => setAthleteTrendMetric(option.key)} style={{ border: `1px solid ${active ? option.color : '#DCE6F3'}`, background: active ? option.color : '#F8FAFC', color: active ? '#fff' : '#0D275B', borderRadius: 999, padding: '7px 10px', fontSize: 11, fontWeight: 950, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <FontAwesomeIcon icon={option.icon} />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ color: base.text, fontSize: 26, fontWeight: 950 }}>{selectedAthleteTrend.value}</div>
                      <div style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>{athleteRangeLabel}</div>
                    </div>
                    <WGTrendBars rows={athleteTrendRows} metric={selectedAthleteTrend.metric} color={selectedAthleteTrend.color} height={136} />
                  </section>

                  <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Top Exercises</h3>
                    <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3, marginBottom: 10 }}>Highest XP from assigned sessions</div>
                    {topExercises.length === 0 ? (
                      <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 14, color: base.muted, fontSize: 12, fontWeight: 850 }}>No exercise detail in this range.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {topExercises.map((exercise, index) => (
                          <div key={`${exercise.name}_${index}`} style={{ border: `1px solid ${base.border}`, background: '#F8FAFC', borderRadius: 14, padding: 11, display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
                            <div style={{ width: 34, height: 34, borderRadius: 13, background: index === 0 ? modalC.accent : modalC.accentBgSubtle, color: index === 0 ? '#fff' : modalC.accent, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 950 }}>{index + 1}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: base.text, fontSize: 13, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exercise.name}</div>
                              <div style={{ color: base.muted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>{exercise.reps.toLocaleString()} reps · {formatWorkoutGroupVolume(exercise.volume)}</div>
                            </div>
                            <div style={{ color: t.primary, fontSize: 12, fontWeight: 950 }}>{exercise.xp.toLocaleString()} XP</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>

                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div>
                      <h3 style={{ margin: 0, color: base.text, fontSize: 17, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <FontAwesomeIcon icon={activeWGCanUseNutritionStats ? faFire : faLock} style={{ color: '#D97706', fontSize: 14 }} />
                        Nutrition Insights
                      </h3>
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>
                        Calories, macro averages, fibre, and food logging for this athlete.
                      </div>
                    </div>
                    <span style={{ border: '1px solid #FED7AA', background: '#FFF7ED', color: '#9A3412', borderRadius: 999, padding: '6px 9px', fontSize: 10, fontWeight: 950 }}>
                      {formatWorkoutGroupNutritionNumber(athleteDailyCalorieTarget)} kcal/day
                    </span>
                  </div>

                  {!activeWGCanUseNutritionStats ? (
                    <div style={{ border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 16, padding: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <WGAppIcon icon={faLock} color="#D97706" bg="#FFF7ED" size={36} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: '#92400E', fontSize: 13, fontWeight: 950 }}>Nutrition analytics locked</div>
                          <div style={{ color: '#92400E', fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>
                            Athlete nutrition details stay hidden until this group has Pro Coach or Club Coaching nutrition access.
                          </div>
                        </div>
                      </div>
                      <button type="button" onClick={() => openWorkoutGroupUpgradePrompt('Athlete nutrition stats, macro trends, meal logs, and calorie reminders unlock with Pro Coach or Club Coaching.')} style={{ marginTop: 12, minHeight: 40, border: 0, background: t.primary, color: '#fff', borderRadius: 13, padding: '9px 12px', fontSize: 12, fontWeight: 950, cursor: 'pointer' }}>Review plans</button>
                    </div>
                  ) : wgNutritionError ? (
                    <div style={{ border: '1px dashed #CBD5E1', background: '#F8FAFC', borderRadius: 16, padding: 12, color: base.muted, fontSize: 12, fontWeight: 850 }}>
                      {wgNutritionError}
                    </div>
                  ) : !athleteNutritionHasData ? (
                    <div style={{ border: '1px dashed #FDE68A', background: '#FFFBEB', borderRadius: 16, padding: 12 }}>
                      <div style={{ color: '#92400E', fontSize: 13, fontWeight: 950 }}>No nutrition logs yet</div>
                      <div style={{ color: '#92400E', fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>
                        Nutrition signals will appear after {athleteName} logs meals in this review window.
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))', gap: 10 }}>
                        <WGNutritionTile
                          emphasis
                          icon={faFire}
                          label="Calories logged"
                          value={formatWorkoutGroupNutritionNumber(athleteNutrition?.total_calories, { compact: true })}
                          sub={`${Math.round(safeWorkoutGroupNutritionNumber(athleteNutrition?.calorie_target_progress_pct))}% of ${formatWorkoutGroupNutritionNumber(athleteNutritionTargetTotal, { compact: true })} kcal target`}
                        />
                        <WGNutritionTile
                          icon={faCalendar}
                          label="Logged days"
                          value={`${formatWorkoutGroupNutritionNumber(athleteNutrition?.logged_days)} / ${formatWorkoutGroupNutritionNumber(athleteNutrition?.expected_days)}`}
                          sub={`${Math.round(safeWorkoutGroupNutritionNumber(athleteNutrition?.logging_consistency_pct))}% logging consistency`}
                          color={modalC.accent}
                          bg={modalC.accentBgSubtle}
                        />
                        <WGNutritionTile
                          icon={faBolt}
                          label="Average kcal"
                          value={formatWorkoutGroupNutritionNumber(athleteNutrition?.average_calories)}
                          sub={`Target ${formatWorkoutGroupNutritionNumber(athleteDailyCalorieTarget)} kcal/day`}
                          color="#7C3AED"
                          bg="#F3E8FF"
                        />
                        <WGNutritionTile
                          icon={faDumbbell}
                          label="Protein"
                          value={athleteProteinTarget ? `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_protein)}g / ${formatWorkoutGroupNutritionNumber(athleteProteinTarget)}g` : `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_protein)}g`}
                          sub={athleteProteinTarget ? 'Average vs daily target' : 'Daily average'}
                          color="#16A34A"
                          bg="#EAFBF0"
                        />
                        <WGNutritionTile
                          icon={faClipboardList}
                          label="Carbs"
                          value={Number.isFinite(athleteCarbsTarget) && athleteCarbsTarget > 0 ? `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_carbs)}g / ${formatWorkoutGroupNutritionNumber(athleteCarbsTarget)}g` : `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_carbs)}g`}
                          sub={Number.isFinite(athleteCarbsTarget) && athleteCarbsTarget > 0 ? 'Average vs daily target' : 'Daily average'}
                          color="#22D3EE"
                          bg="#E7FAFF"
                        />
                        <WGNutritionTile
                          icon={faClipboardList}
                          label="Fat"
                          value={Number.isFinite(athleteFatTarget) && athleteFatTarget > 0 ? `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_fat)}g / ${formatWorkoutGroupNutritionNumber(athleteFatTarget)}g` : `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_fat)}g`}
                          sub={Number.isFinite(athleteFatTarget) && athleteFatTarget > 0 ? 'Average vs daily target' : 'Daily average'}
                          color="#D5A419"
                          bg="#FFF8E1"
                        />
                        <WGNutritionTile
                          icon={faClipboardList}
                          label="Fibre"
                          value={Number.isFinite(athleteFibreTarget) && athleteFibreTarget > 0 ? `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_fiber)}g / ${formatWorkoutGroupNutritionNumber(athleteFibreTarget)}g` : `${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_fiber)}g`}
                          sub={Number.isFinite(athleteFibreTarget) && athleteFibreTarget > 0 ? 'Average vs daily target' : 'Daily average'}
                          color={modalC.accent}
                          bg={modalC.accentBgSubtle}
                        />
                        <WGNutritionTile
                          icon={faClipboardList}
                          label="Sugar"
                          value={`${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_sugar)}g`}
                          sub="Daily average"
                          color="#EC4899"
                          bg="#FCE7F3"
                        />
                        <WGNutritionTile
                          icon={faClipboardList}
                          label="Sodium"
                          value={`${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_sodium)}mg`}
                          sub="Daily average"
                          color="#64748B"
                          bg="#F1F5F9"
                        />
                        <WGNutritionTile
                          icon={faList}
                          label="Food entries"
                          value={formatWorkoutGroupNutritionNumber(athleteNutrition?.total_items)}
                          sub={`${formatWorkoutGroupNutritionNumber(athleteNutrition?.average_items)} per logged day`}
                          color="#0F766E"
                          bg="#CCFBF1"
                        />
                      </div>
                      <div style={{ border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 16, padding: 12 }}>
                        <div style={{ color: '#92400E', fontSize: 12, fontWeight: 950 }}>Coach nutrition insight</div>
                        <div style={{ color: '#92400E', fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>{athleteNutritionInsight}</div>
                      </div>
                    </div>
                  )}
                </section>

                <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                    <div>
                      <h3 style={{ margin: 0, color: base.text, fontSize: 17, display: 'inline-flex', alignItems: 'center', gap: 8 }}><FontAwesomeIcon icon={faList} style={{ color: t.primary, fontSize: 13 }} />Recent Sessions</h3>
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>Open a session to view exercises and feedback.</div>
                    </div>
                    <span style={{ border: `1px solid ${athleteStatus.border}`, background: athleteStatus.bg, color: athleteStatus.text, borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 950 }}>{athleteStatus.label}</span>
                  </div>
                  {athleteLoading ? (
                    <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 14, color: base.muted, fontWeight: 850 }}>Loading athlete sessions...</div>
                  ) : athleteSessions.length === 0 ? (
                    <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 14, color: base.muted, fontWeight: 850 }}>No sessions in this range.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {athleteSessions.map(session => {
                        const feedback = (session.user_workout_feedback || [])[0];
                        const customWorkout = Array.isArray(session.user_workouts) ? session.user_workouts[0] : session.user_workouts;
                        const presetWorkout = Array.isArray(session.workouts) ? session.workouts[0] : session.workouts;
                        const workoutName = customWorkout?.name || presetWorkout?.name || 'Unnamed workout';
                        const durationSeconds = Number(session.duration_seconds || 0);
                        const context = Array.isArray(session.workout_context) ? session.workout_context[0] : session.workout_context;
                        const restSeconds = Number(session.total_rest_seconds || context?.rest_timer_usage || 0);
                        const showRest = durationSeconds >= 25 && restSeconds > 0 && restSeconds <= durationSeconds;
                        const exerciseRows = Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : [];
                        const isExpanded = expandedAthleteSessionId === session.id;
                        const mood = feedback?.mood ? MOOD_OPTIONS.find(item => item.value === feedback.mood) : null;
                        return (
                          <article key={session.id} style={{ border: `1px solid ${isExpanded ? '#CFE2FF' : base.border}`, background: isExpanded ? '#F8FBFF' : '#FFFFFF', borderRadius: 16, overflow: 'hidden' }}>
                            <button type="button" onClick={() => setExpandedAthleteSessionId(isExpanded ? null : session.id)} aria-expanded={isExpanded} style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', padding: 12, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'start' }}>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: 'block', color: base.text, fontSize: 14, fontWeight: 950, lineHeight: 1.25, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{workoutName}</span>
                                <span style={{ display: 'block', color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 4 }}>{new Date(session.started_at).toLocaleString()} · {formatWGDuration(session.duration_seconds)}</span>
                                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
                                  <span style={{ border: `1px solid ${modalC.accentBorder}`, background: modalC.accentBgSubtle, color: modalC.accent, borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 950 }}>{Number(session.total_xp || 0).toLocaleString()} XP</span>
                                  <span style={{ border: '1px solid #DCE6F3', background: '#F8FAFC', color: '#334155', borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 900 }}>{Number(session.total_reps || 0).toLocaleString()} reps</span>
                                  <span style={{ border: '1px solid #DCE6F3', background: '#F8FAFC', color: '#334155', borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 900 }}>{exerciseRows.length} exercises</span>
                                  {feedback && <span style={{ border: '1px solid #BBF7D0', background: '#ECFDF3', color: '#166534', borderRadius: 999, padding: '4px 8px', fontSize: 10, fontWeight: 900 }}>{canUseWGAthleteSessionFeedback ? 'Feedback' : 'Feedback locked'}</span>}
                                </span>
                              </span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: t.primary, fontSize: 12, fontWeight: 950, whiteSpace: 'nowrap' }}>
                                {isExpanded ? 'Hide details' : 'View details'}
                                <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ fontSize: 11 }} />
                              </span>
                            </button>
                            {isExpanded && (
                              <div style={{ borderTop: `1px solid ${base.border}`, padding: 12, display: 'grid', gap: 12 }}>
                                {exerciseRows.length > 0 && (
                                  <div>
                                    <div style={{ color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 8 }}>Exercise detail</div>
                                    <div style={{ display: 'grid', gap: 7 }}>
                                      {exerciseRows.slice(0, 6).map((row, index) => {
                                        const exercise = Array.isArray(row.exercises) ? row.exercises[0] : row.exercises;
                                        const exerciseName = exercise?.name || 'Exercise';
                                        return (
                                          <div key={`${session.id}_exercise_${exercise?.id || exerciseName}_${index}`} style={{ border: `1px solid ${base.border}`, background: '#fff', borderRadius: 12, padding: 10, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
                                            <div style={{ minWidth: 0 }}>
                                              <div style={{ color: base.text, fontSize: 12, fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exerciseName}</div>
                                              <div style={{ color: base.muted, fontSize: 11, fontWeight: 800, marginTop: 2 }}>
                                                {Number(row.total_reps || 0).toLocaleString()} reps{Number(row.avg_weight || 0) > 0 ? ` · avg ${Number(row.avg_weight || 0).toLocaleString()} kg` : ''}
                                              </div>
                                            </div>
                                            <div style={{ color: t.primary, fontSize: 12, fontWeight: 950 }}>{Number(row.total_xp || 0).toLocaleString()} XP</div>
                                          </div>
                                        );
                                      })}
                                      {exerciseRows.length > 6 && <div style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>+{exerciseRows.length - 6} more exercises</div>}
                                    </div>
                                  </div>
                                )}
                                {feedback ? (
                                  canUseWGAthleteSessionFeedback ? (
                                    <div style={{ background: '#F0F6FF', border: '1px solid #CFE2FF', borderRadius: 16, padding: 12 }}>
                                      <div style={{ color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 9 }}>Session feedback</div>
                                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                                        <WGFeedbackSummaryRow label="Enjoyment" value={feedback.rating} options={FEEDBACK_EMOJI_OPTIONS.rating} color={t.primary || base.blue} />
                                        <WGFeedbackSummaryRow label="Difficulty" value={feedback.difficulty} options={FEEDBACK_EMOJI_OPTIONS.difficulty} color="#D5A419" />
                                        <WGFeedbackSummaryRow label="Target Focus" value={feedback.focus_rating} options={FEEDBACK_EMOJI_OPTIONS.focus} color="#16A34A" />
                                      </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 8 }}>
                                        {mood && (
                                          <div style={{ border: `1px solid ${base.border}`, background: '#fff', borderRadius: 14, padding: 11 }}>
                                            <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Mood</div>
                                            <div style={{ color: base.text, fontSize: 13, fontWeight: 950, marginTop: 4 }}>{mood.emoji} {mood.label}</div>
                                          </div>
                                        )}
                                        {canUseWGRestTimeAnalysis && showRest && (
                                          <div style={{ border: '1px solid #FED7AA', background: '#FFF7ED', borderRadius: 14, padding: 11 }}>
                                            <div style={{ color: '#9A3412', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.4 }}>Rest Time</div>
                                            <div style={{ color: '#9A3412', fontSize: 13, fontWeight: 950, marginTop: 4 }}>{formatWGDuration(restSeconds)} · {Math.round((restSeconds / durationSeconds) * 100)}% of session</div>
                                          </div>
                                        )}
                                      </div>
                                      {String(feedback.notes || '').trim().length > 0 && (
                                        <div style={{ marginTop: 8, background: '#FFFFFF', borderRadius: 14, padding: 12, border: `1px solid ${base.border}` }}>
                                          <div style={{ fontSize: 11, fontWeight: 950, color: '#0D275B', marginBottom: 5 }}>Athlete Notes</div>
                                          <div style={{ fontSize: 12, fontWeight: 800, color: '#334155', lineHeight: 1.5 }}>{String(feedback.notes).trim()}</div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div style={{ background: '#F8FAFC', borderRadius: 16, padding: 12, border: `1px solid ${base.border}` }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                        <div style={{ color: base.text, fontSize: 12, fontWeight: 950 }}>Session Feedback</div>
                                        <span style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>Locked</span>
                                      </div>
                                      <div style={{ marginTop: 6, color: base.muted, fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>
                                        Athlete feedback is attached to this session, but you need Coach Starter or higher to view it.
                                      </div>
                                    </div>
                                  )
                                ) : canUseWGAthleteSessionFeedback ? (
                                  <div style={{ border: '1px dashed #CBD5E1', borderRadius: 14, padding: 12, color: base.muted, fontSize: 12, fontWeight: 850 }}>No feedback for this session.</div>
                                ) : null}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>

                {canUseWGAthleteNotes ? (
                  <section style={{ background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                      <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Coach Notes</h3>
                      {athleteNote?.created_at && <span style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>{new Date(athleteNote.created_at).toLocaleDateString()}</span>}
                    </div>
                    <div style={{ color: base.muted, fontWeight: 800, lineHeight: 1.5, marginBottom: canWriteNote ? 10 : 0 }}>{athleteNote?.note?.trim() || 'No athlete notes yet.'}</div>
                    {canWriteNote && (
                      <>
                        <label htmlFor="wg-athlete-note" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Coach note</label>
                        <textarea id="wg-athlete-note" value={athleteNoteText} onChange={e => setAthleteNoteText(e.target.value)} placeholder="Write athlete notes..." style={{ width: '100%', minHeight: 86, resize: 'vertical', border: `1px solid ${base.border}`, borderRadius: 12, padding: 12, fontWeight: 800, color: base.text, boxSizing: 'border-box' }} />
                        <button type="button" onClick={saveWGAthleteNote} disabled={athleteNoteSaving || !athleteNoteText.trim()} style={{ marginTop: 10, border: 0, background: athleteNoteText.trim() ? t.primary : '#A8B4C8', color: '#fff', borderRadius: 12, padding: '10px 14px', fontWeight: 950, cursor: athleteNoteText.trim() ? 'pointer' : 'default' }}>{athleteNoteSaving ? 'Saving...' : 'Save Notes'}</button>
                      </>
                    )}
                  </section>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      closeAthleteModal();
                      openWorkoutGroupUpgradePrompt('Athlete Notes are included with Coach and higher plans.');
                    }}
                    style={{ width: '100%', textAlign: 'left', background: '#fff', border: `1px solid ${base.border}`, borderRadius: 20, padding: 14, cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                      <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Coach Notes</h3>
                      <span style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>Locked</span>
                    </div>
                    <div style={{ color: base.muted, fontWeight: 800, lineHeight: 1.5 }}>
                      Upgrade to Coach to view and edit athlete notes.
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {payoutSetupVisible && (
        <Modal C={C} onClose={closePayoutSetupModal} wide>
          <MHead C={C} title={payoutSetupNeedsVerification ? 'Verify Stripe Connect identity' : payoutSetupReady ? 'Stripe Connect payouts enabled' : 'Set up Stripe Connect payouts'} onClose={closePayoutSetupModal} />
          <MBody>
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '48px minmax(0,1fr)', gap: 14, alignItems: 'start' }}>
                <div style={{ width: 48, height: 48, borderRadius: 24, background: payoutSetupReady ? 'rgba(22,163,74,0.12)' : payoutSetupNeedsVerification ? 'rgba(245,158,11,0.12)' : C.accentBgSubtle, border: `1px solid ${payoutSetupReady ? 'rgba(22,163,74,0.32)' : payoutSetupNeedsVerification ? 'rgba(245,158,11,0.35)' : C.accentBorder}`, color: payoutSetupReady ? '#16A34A' : payoutSetupNeedsVerification ? '#B54708' : C.accent, display: 'grid', placeItems: 'center' }}>
                  <FontAwesomeIcon icon={payoutSetupReady ? faCheck : payoutSetupNeedsVerification ? faTriangleExclamation : faCreditCard} />
                </div>
                <div>
                  <div className="heading" style={{ color: C.text, fontSize: isMobile ? 22 : 26, fontWeight: 950, lineHeight: 1.12, marginBottom: 8 }}>
                    {payoutSetupNeedsVerification ? 'Identity verification needed' : payoutSetupReady ? 'Payout setup complete' : payoutSetupReturned ? 'Finish cash payout setup' : 'Continue cash payout setup'}
                  </div>
                  <p style={{ color: C.textSecondary, fontSize: 14, fontWeight: 750, lineHeight: 1.55, margin: 0 }}>
                    {payoutSetupNeedsVerification
                      ? 'Stripe needs more information before payouts and transfers can continue without interruption. Continue to Stripe Connect to submit the missing identity details.'
                      : payoutSetupReady
                      ? 'Your Stripe Connect account is linked to this Snows ProAm profile. Eligible card proceeds from paid sessions, events, and tournaments can now be sent to your bank account.'
                      : payoutSetupReturned
                      ? 'Stripe sent you back to Snows ProAm. If more information is needed, continue setup and Stripe will show the remaining steps.'
                      : 'Stripe Connect lets Snows ProAm send your card proceeds from paid sessions, events, and tournaments to your bank account. You need to be signed in so the payout account is attached to the correct Snows ProAm profile.'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,minmax(0,1fr))', gap: 10 }}>
                {[
                  ['1', 'Confirm details', 'Stripe verifies your identity and payout country.'],
                  ['2', 'Add bank account', 'Enter where eligible cash payouts should be sent.'],
                  ['3', 'Return to ProAm', 'Come back here and collect ready session earnings.'],
                ].map(([step, title, body]) => (
                  <div key={step} style={{ border: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: 14, padding: 13 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 14, background: C.accent, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 950, marginBottom: 9 }}>{step}</div>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 950 }}>{title}</div>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, lineHeight: 1.45, marginTop: 4 }}>{body}</div>
                  </div>
                ))}
              </div>

              <div style={{ border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, borderRadius: 14, padding: 13, display: 'grid', gap: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.text, fontSize: 13, fontWeight: 950 }}>
                  <FontAwesomeIcon icon={payoutSetupReady ? faCheck : payoutSetupNeedsVerification ? faTriangleExclamation : faLock} style={{ color: payoutSetupReady ? '#16A34A' : payoutSetupNeedsVerification ? '#B54708' : C.accent }} />
                  {payoutSetupNeedsVerification ? 'Stripe Connect needs identity verification' : payoutSetupReady ? 'Stripe Connect payouts are enabled' : 'Stripe Connect setup is required before card payouts can be sent'}
                </div>
                <div style={{ color: C.textSecondary, fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                  {payoutSetupLoading
                    ? 'Checking your payout setup status...'
                    : payoutAccount?.status
                    ? `Current setup status: ${humanizeToken(payoutAccount.status)}.`
                    : 'No active payout account has been found yet.'}
                </div>
              </div>

              {payoutSetupError && (
                <div role="alert" style={{ border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.08)', color: C.mode === 'dark' ? '#FCA5A5' : '#B42318', borderRadius: 12, padding: 12, fontSize: 13, fontWeight: 800, lineHeight: 1.45, display: 'grid', gap: 10 }}>
                  <div>{payoutSetupError}</div>
                </div>
              )}

              <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 750, lineHeight: 1.5 }}>
                Stripe may ask for identity and bank details. Snows ProAm does not store your full bank account details.
              </div>
            </div>
          </MBody>
          <MFoot C={C}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: 10, width: '100%', justifyContent: 'flex-end' }}>
              <BtnSec C={C} onClick={closePayoutSetupModal}>{payoutSetupReady ? 'Close' : 'Not now'}</BtnSec>
              <button
                type="button"
                onClick={startStripeConnectSetup}
                disabled={payoutSetupLoading || payoutSetupReady}
                style={{ minHeight: 44, border: 'none', borderRadius: 10, padding: '11px 16px', background: payoutSetupReady ? '#16A34A' : payoutSetupNeedsVerification ? '#B54708' : C.accentGrad, color: '#fff', fontSize: 13, fontWeight: 950, cursor: payoutSetupLoading || payoutSetupReady ? 'default' : 'pointer', opacity: payoutSetupLoading ? 0.72 : 1, width: isMobile ? '100%' : 'auto' }}
              >
                {payoutSetupLoading ? 'Checking setup...' : payoutSetupReady ? 'Payouts enabled' : payoutSetupNeedsVerification ? 'Verify with Stripe' : payoutSetupReturned ? 'Continue Stripe Connect setup' : 'Start Stripe Connect setup'}
              </button>
            </div>
          </MFoot>
        </Modal>
      )}

      {showSettings && (
        <Modal C={C} onClose={() => setShowSettings(false)} wide>
          <MHead C={C} title="Settings" onClose={() => setShowSettings(false)} />
          <MBody>
            <div style={{ display: 'grid', gap: 12 }}>
              {[
                { f: 'email', l: 'Email', ph: 'your@email.com', type: 'email' },
                { f: 'phone_number', l: 'Phone Number', ph: '+353...', type: 'tel' },
                { f: 'website', l: 'Website', ph: 'https://...', type: 'url' },
                { f: 'address', l: 'Address', ph: '123 Main St' },
                { f: 'country', l: 'Country', ph: 'Ireland' },
                { f: 'region', l: 'Region', ph: 'Dublin' },
              ].map(({ f, l, ph, type }) => (
                <FormRow key={f} C={C} label={l}>
                  <input type={type || 'text'} value={settingsForm[f]} onChange={e => setSettingsForm(s => ({ ...s, [f]: e.target.value }))} placeholder={ph} style={inputSt(C)} autoComplete={f === 'email' ? 'email' : undefined} />
                  {f === 'email' && (
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                      Changing this sends a confirmation email. Your sign-in email changes after confirmation.
                    </div>
                  )}
                </FormRow>
              ))}
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setShowSettings(false)}>Cancel</BtnSec>
            <BtnPrimary C={C} onClick={saveSettings}>Save Settings</BtnPrimary>
          </MFoot>
        </Modal>
      )}

      {confirmDialog && (
        <Modal C={C} onClose={() => setConfirmDialog(null)}>
          <MHead C={C} title={confirmDialog.title} onClose={() => setConfirmDialog(null)} />
          <MBody>
            <div style={{ color: C.textSecondary, fontSize: 14, fontWeight: 800, lineHeight: 1.55 }}>{confirmDialog.message}</div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setConfirmDialog(null)}>Cancel</BtnSec>
            <button
              onClick={async () => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(null);
                await action?.();
              }}
              style={{ border: 'none', borderRadius: 10, padding: '10px 16px', minHeight: 42, background: confirmDialog.tone === 'danger' ? C.danger : C.accent, color: '#fff', cursor: 'pointer', fontWeight: 900 }}
            >
              {confirmDialog.confirmLabel}
            </button>
          </MFoot>
        </Modal>
      )}

      {refundDialog && (() => {
        const isActivityRefund = isActivityRefundTransaction(refundDialog.tx);
        return (
        <Modal C={C} onClose={() => setRefundDialog(null)}>
          <MHead C={C} title="Request a refund" onClose={() => setRefundDialog(null)} />
          <MBody>
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ color: C.textSecondary, fontSize: 14, fontWeight: 800, lineHeight: 1.55 }}>
                {isActivityRefund
                  ? 'Activity payments should use Report problem or email support. This does not create a refund request.'
                  : 'This sends a refund request for review. It does not immediately reverse the payment or remove access.'}
              </div>
              <label style={{ display: 'grid', gap: 7, color: C.text, fontSize: 13, fontWeight: 900 }}>
                Reason
                <select
                  value={refundDialog.reason}
                  onChange={e => setRefundDialog(d => ({ ...d, reason: e.target.value }))}
                  style={{ ...inputSt(C), minHeight: 44 }}
                >
                  {['Accidental purchase', 'Duplicate payment', 'Technical issue', 'Cancelled session or event', 'No longer needed', 'Other'].map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 7, color: C.text, fontSize: 13, fontWeight: 900 }}>
                What happened?
                <textarea
                  value={refundDialog.message}
                  onChange={e => setRefundDialog(d => ({ ...d, message: e.target.value }))}
                  placeholder="Add details that help support review this request."
                  rows={4}
                  style={{ ...inputSt(C), resize: 'vertical', lineHeight: 1.45 }}
                />
              </label>
              <div style={{ border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textSecondary, borderRadius: 12, padding: 12, fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>
                {isActivityRefund
                  ? 'Activity reports go to admin for review. Email support opens support@snowsproam.com.'
                  : 'Approved coin refunds return to your ProAm balance. Approved card refunds go back through Stripe to the original payment method.'}
              </div>
            </div>
          </MBody>
          <MFoot C={C}>
            <BtnSec C={C} onClick={() => setRefundDialog(null)}>Cancel</BtnSec>
            <button
              type="button"
              disabled={!String(refundDialog.reason || '').trim()}
              onClick={async () => {
                const dialog = refundDialog;
                setRefundDialog(null);
                await requestTransactionRefund(dialog.tx, dialog);
              }}
              style={{ border: 'none', borderRadius: 10, padding: '10px 16px', minHeight: 42, background: C.accent, color: '#fff', cursor: String(refundDialog.reason || '').trim() ? 'pointer' : 'not-allowed', fontWeight: 900, opacity: String(refundDialog.reason || '').trim() ? 1 : 0.58 }}
            >
              Submit refund request
            </button>
          </MFoot>
        </Modal>
        );
      })()}

      {toast && (
        <div style={{ position: 'fixed', left: '50%', top: isMobile ? 72 : 76, transform: 'translateX(-50%)', zIndex: 2600, width: 'calc(100vw - 28px)', maxWidth: 380, background: C.bgElevated, border: `1px solid ${toast.tone === 'error' ? 'rgba(239,68,68,0.45)' : toast.tone === 'warning' ? 'rgba(245,158,11,0.45)' : toast.tone === 'success' ? 'rgba(34,197,94,0.38)' : C.border}`, color: C.text, borderRadius: 14, padding: '12px 14px', boxShadow: C.shadowLg, fontSize: 13, fontWeight: 900 }}>
          {toast.message}
        </div>
      )}

    </div>
  );
};

export default Profile;
